import L from "leaflet";
import { reportTaskStatus } from "../../core/task-status.js";
import { deliverableFilename } from "../../core/deliverable-naming.js";

const STATE_KEY = "hydrotools:slip-inventory:v2";
const API_BASE = "/api/slip-inventory/v1/slip-inventory";
const COL_CFG_KEY = "hydrotools:slip-inventory-cols:v1";
const REMOTE_SYNC_INTERVAL_MS = 45000;
const LOCAL_SAVE_GRACE_MS = 2500;

const ALL_COLUMNS = [
  { key: "measurement_id",        label: "Meas ID",          width: 130, numeric: false },
  { key: "marina_id",             label: "Marina ID",         width: 80,  numeric: false },
  { key: "marina_name",           label: "Marina",            width: 140, numeric: false },
  { key: "accepted_adjusted",     label: "Accepted/Adj",      width: 110, numeric: false },
  { key: "dock_name",             label: "Dock",              width: 80,  numeric: false },
  { key: "region",                label: "Region",            width: 100, numeric: false },
  { key: "adjusted_dock",         label: "Adj Dock",          width: 80,  numeric: false },
  { key: "slip_id",               label: "Slip ID",           width: 100, numeric: false },
  { key: "suffix",                label: "Suffix",            width: 70,  numeric: false },
  { key: "adjusted_slip_id",      label: "Adj Slip ID",       width: 100, numeric: false },
  { key: "multislip_yn",          label: "Multi-slip",        width: 80,  numeric: false },
  { key: "multislip_id",          label: "Multi-slip ID",     width: 100, numeric: false },
  { key: "adjusted_multislip_id", label: "Adj Multi-slip ID", width: 125, numeric: false },
  { key: "space_type",            label: "Slip Type",         width: 100, numeric: false },
  { key: "adjusted_space_type",   label: "Adj Slip Type",     width: 115, numeric: false },
  { key: "covered_yn",            label: "Covered",           width: 75,  numeric: false },
  { key: "adjusted_covered",      label: "Adj Covered",       width: 90,  numeric: false },
  { key: "boatlift_yn",           label: "Boatlift",          width: 75,  numeric: false },
  { key: "fueldock_yn",           label: "Fuel Dock",         width: 80,  numeric: false },
  { key: "out_of_service",        label: "Out of Service",    width: 90,  numeric: false },
  { key: "out_of_service_reason", label: "OOS Reason",        width: 130, numeric: false },
  { key: "measured_linear_feet",  label: "Meas L (ft)",       width: 90,  numeric: true  },
  { key: "marketable_linear_feet",label: "Mkt L (ft)",        width: 80,  numeric: true  },
  { key: "measured_width_feet",   label: "Meas W (ft)",       width: 90,  numeric: true  },
  { key: "marketable_width_feet", label: "Mkt W (ft)",        width: 80,  numeric: true  },
  { key: "notes",                 label: "Notes",             width: 180, numeric: false },
  { key: "measured_depth_feet",   label: "Depth (ft)",        width: 80,  numeric: true  },
  { key: "measurement_date",      label: "Meas Date",         width: 100, numeric: false },
  { key: "geometry_wkt",          label: "Geometry",          width: 210, numeric: false },
  { key: "confidence_score",      label: "Confidence",        width: 90,  numeric: false },
  { key: "record_created_timestamp", label: "Created",        width: 140, numeric: false },
];

const DEFAULT_HIDDEN_COLS = new Set([
  "measurement_id", "marina_id", "marina_name", "adjusted_dock", "adjusted_slip_id",
  "multislip_id", "adjusted_multislip_id", "adjusted_space_type", "adjusted_covered",
  "marketable_linear_feet", "marketable_width_feet",
  "notes", "geometry_wkt", "record_created_timestamp",
  // Only needed for the rare marina with multiple same-numbered yards/regions -- hidden by
  // default so it doesn't clutter every other marina's table.
  "region",
]);

const BINARY_COLS = new Set(["multislip_yn", "covered_yn", "boatlift_yn", "fueldock_yn", "out_of_service"]);
const NUMERIC_COLS = new Set(["measured_linear_feet", "marketable_linear_feet", "measured_width_feet", "marketable_width_feet", "measured_depth_feet"]);
// slip_id is derived (Dock + Slip Number, see buildSlipIdFromState) -- never directly editable.
const NON_EDITABLE_COLS = new Set(["measurement_id", "geometry_wkt", "record_created_timestamp", "slip_id"]);

// Canonical dockage_type set per the 2026-09-10 column-rule audit. "Med-Mooring" (hyphenated)
// and "Boat Ramp" are pre-existing values grandfathered on the API side (slip-api's
// DOCKAGE_TYPE_LEGACY_VALUES) rather than migrated -- they're deliberately left out of this
// list so they're not offered for *new* entries, but see spaceTypeOptionsFor() below for how
// an existing record already carrying one of them still round-trips through the edit UI.
const SPACE_TYPE_OPTIONS = ["Finger", "Pilings", "Side Tie", "Travel Lift", "Med Mooring"];
const OOS_REASON_OPTIONS = ["Environmental", "Damage", "Silting", "Other"];

// The two selects that edit an *existing* record's space_type (the data table cell and the
// Selected Slip Attributes panel) read the record's current value back out on every save. If
// that value isn't in SPACE_TYPE_OPTIONS at all (a grandfathered legacy value), the browser
// silently selects the blank option instead -- so any unrelated edit to that row would save it
// with dockage_type blanked out, which the API now rejects as a required field. Appending the
// current value as an extra option keeps it selected and round-tripping until someone
// deliberately changes it.
function spaceTypeOptionsFor(currentValue) {
  const value = String(currentValue || "").trim();
  return value && !SPACE_TYPE_OPTIONS.includes(value) ? [...SPACE_TYPE_OPTIONS, value] : SPACE_TYPE_OPTIONS;
}

// Single source of truth for "Color Slips By" modes -- shared by the sidebar select, the
// table-panel select, the map's own status-tray control, and validity checks on state
// restore. Order here is also the cycle order for the tray's prev/next arrows.
const COLOR_SLIPS_BY_OPTIONS = [
  { value: "none", label: "None" },
  { value: "dimension", label: "Dimension" },
  { value: "confidence", label: "Confidence" },
  { value: "space_type", label: "Slip Type" },
  { value: "multislip", label: "Multislip ID" },
  { value: "orientation_qc", label: "Orientation QC (L vs W)" },
  { value: "region", label: "Region" },
  { value: "marina_name", label: "Marina Name" },
  { value: "dock", label: "Dock" },
  { value: "multislip_integrity", label: "Multislip Integrity" },
  { value: "duplicate_slips", label: "Duplicate/Overlap" },
  { value: "duplicate_slip_id", label: "Duplicate Slip ID" },
  { value: "attribute", label: "Attribute" },
];
const COLOR_SLIPS_BY_VALUES = COLOR_SLIPS_BY_OPTIONS.map((o) => o.value);

function normalizeColorSlipsBy(next) {
  const normalized = String(next || "none").toLowerCase();
  return COLOR_SLIPS_BY_VALUES.includes(normalized) ? normalized : "none";
}

function renderColorSlipsByOptions(current) {
  return COLOR_SLIPS_BY_OPTIONS.map((o) => (
    `<option value="${o.value}"${o.value === current ? " selected" : ""}>${o.label}</option>`
  )).join("");
}

// "Color Slips By: Attribute" -- one view, like Slip Type: each slip is colored by which of
// its attributes are set (the Attributes section of the Slip ID / Attributes panel). A slip
// with several gets its own combined category ("Covered + Boatlift") rather than having one
// attribute win silently; a slip with none is gray.
const SLIP_ATTRIBUTES = [
  { key: "covered_yn",     label: "Covered",        color: "#3b82f6" },
  { key: "boatlift_yn",    label: "Boatlift",       color: "#22c55e" },
  { key: "fueldock_yn",    label: "Fuel Dock",      color: "#eab308" },
  { key: "out_of_service", label: "Out of Service", color: "#ef4444" },
];
const ATTRIBUTE_NONE_KEY = "None";
const ATTRIBUTE_NONE_COLOR = "#64748b";
const ATTRIBUTE_COMBO_COLORS = ["#a855f7", "#ec4899", "#14b8a6", "#f97316", "#84cc16", "#06b6d4", "#e879f9", "#fb7185"];

function isAttributeYes(value) {
  return ["yes", "y", "true", "1"].includes(String(value ?? "").trim().toLowerCase());
}

function slipAttributeKeyForRecord(record) {
  const set = SLIP_ATTRIBUTES.filter((a) => isAttributeYes(record?.[a.key])).map((a) => a.label);
  return set.length ? set.join(" + ") : ATTRIBUTE_NONE_KEY;
}

// Legend/filter order: single attributes in panel order, then combinations (by how many
// attributes, then panel order), then None. Built from the full record set so the map,
// table filter and plot export agree.
function buildAttributeColoring(recordList) {
  const present = new Set(recordList.map(slipAttributeKeyForRecord));
  const order = (key) => SLIP_ATTRIBUTES.map((a) => a.label).filter((l) => key.split(" + ").includes(l));
  const entries = SLIP_ATTRIBUTES.filter((a) => present.has(a.label))
    .map((a) => ({ key: a.label, label: a.label, color: a.color }));
  const combos = [...present].filter((k) => k !== ATTRIBUTE_NONE_KEY && k.includes(" + "))
    .sort((a, b) => order(a).length - order(b).length || a.localeCompare(b));
  combos.forEach((k, i) => entries.push({ key: k, label: k, color: ATTRIBUTE_COMBO_COLORS[i % ATTRIBUTE_COMBO_COLORS.length] }));
  if (present.has(ATTRIBUTE_NONE_KEY)) entries.push({ key: ATTRIBUTE_NONE_KEY, label: ATTRIBUTE_NONE_KEY, color: ATTRIBUTE_NONE_COLOR });
  const byKey = new Map(entries.map((e) => [e.key, e]));
  return {
    entries,
    keyFor: slipAttributeKeyForRecord,
    colorFor: (record) => byKey.get(slipAttributeKeyForRecord(record))?.color ?? ATTRIBUTE_NONE_COLOR,
  };
}

const RECORD_HEADERS = ALL_COLUMNS.map((c) => c.key);

// Fixed column set/order for the CSV deliverable specifically -- distinct from
// RECORD_HEADERS (which also drives the XLSX export and stays untouched). "dockage_type"
// is this deliverable's external name for the space_type field.
const CSV_EXPORT_COLUMNS = [
  { key: "measurement_id",           header: "measurement_id" },
  { key: "marina_id",                header: "marina_id" },
  { key: "marina_name",              header: "marina_name" },
  { key: "dock_name",                header: "dock_name" },
  { key: "region",                   header: "region" },
  { key: "slip_id",                  header: "slip_id" },
  // suffix (added 2026-09-01 as a 3rd Slip ID component) intentionally stays out of this
  // list -- the CSV/XLSX deliverable is a fixed 24-column external contract clients ingest
  // against; it stays visible/editable in the app's own table (ALL_COLUMNS) regardless.
  { key: "multislip_yn",             header: "multislip_yn" },
  { key: "multislip_id",             header: "multislip_id" },
  { key: "space_type",               header: "dockage_type" },
  { key: "covered_yn",               header: "covered_yn" },
  { key: "boatlift_yn",              header: "boatlift_yn" },
  { key: "fueldock_yn",              header: "fueldock_yn" },
  { key: "out_of_service",           header: "out_of_service" },
  { key: "out_of_service_reason",    header: "out_of_service_reason" },
  { key: "measured_linear_feet",     header: "measured_linear_feet" },
  { key: "marketable_linear_feet",   header: "marketable_linear_feet" },
  { key: "measured_width_feet",      header: "measured_width_feet" },
  { key: "marketable_width_feet",    header: "marketable_width_feet" },
  // marina_comments is a historical DB column, unified onto notes and left untouched since
  // (see slip-api's startup backfill) -- the CSV deliverable's external column is still
  // named marina_comments, but its value now has to come from the live field.
  { key: "notes",                    header: "marina_comments" },
  { key: "measured_depth_feet",      header: "measured_depth_feet" },
  { key: "measurement_date",         header: "measurement_date" },
  { key: "geometry_wkt",             header: "geometry_wkt" },
  { key: "confidence_score",         header: "confidence_score" },
  { key: "record_created_timestamp", header: "record_created_timestamp" },
];

function readColCfg() {
  try {
    const raw = localStorage.getItem(COL_CFG_KEY);
    const saved = raw ? JSON.parse(raw) : {};
    const allKeys = ALL_COLUMNS.map((c) => c.key);
    const savedOrder = Array.isArray(saved?.order) ? saved.order.filter((k) => allKeys.includes(k)) : [];
    const order = [...savedOrder, ...allKeys.filter((k) => !savedOrder.includes(k))];
    const hidden = new Set(Array.isArray(saved?.hidden) ? saved.hidden.filter((k) => allKeys.includes(k)) : [...DEFAULT_HIDDEN_COLS]);
    const widths = (saved?.widths && typeof saved.widths === "object") ? { ...saved.widths } : {};
    return { order, hidden, widths };
  } catch {
    return { order: ALL_COLUMNS.map((c) => c.key), hidden: new Set(DEFAULT_HIDDEN_COLS), widths: {} };
  }
}

function writeColCfg(cfg) {
  try {
    localStorage.setItem(COL_CFG_KEY, JSON.stringify({ order: cfg.order, hidden: [...cfg.hidden], widths: cfg.widths }));
  } catch {}
}

function getColWidth(cfg, key) {
  const saved = Number(cfg.widths[key]);
  if (Number.isFinite(saved) && saved >= 30) return saved;
  return ALL_COLUMNS.find((c) => c.key === key)?.width || 100;
}

function visibleCols(cfg) {
  return cfg.order
    .filter((k) => !cfg.hidden.has(k))
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter(Boolean);
}

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function todayDate() {
  return new Date().toISOString().slice(0, 10);
}

function defaultState() {
  return {
    marinaId: "",
    marinaName: "",
    measurementDate: todayDate(),
    confidenceScore: "High",
    selectedMeasurementId: "",
    dockName: "",
    // Disambiguates marinas with multiple same-numbered sub-areas (e.g. North Yard's B-001
    // vs South Yard's B-001) -- purely a separate metadata field, not part of the slip_id
    // text (dock+separator+number, see buildSlipIdFromState) and not written by either lasso
    // tool. Blank by default; most marinas never need it.
    region: "",
    slipIdSeparator: "-",
    slipIdPart3: "",
    slipIdSuffix: "",
    slipIdAutoIncrement: true,
    slipIdIncrementBy: 1,
    slipIdAutoIncrementSuffix: false,
    slipIdIncrementBySuffix: 1,
    multislipId: DEFAULT_MULTISLIP_ID,
    spaceType: "",
    lineSlipCount: 6,
    slipGeometryTypes: [],
    slipGeometryPolygons: [],
    slipGeometrySelectedId: "",
    slipGeometrySelectedIds: [],
    slipGeometryDraftTypeId: "",
    slipGeometryRunBetweenCount: 5,
    showSlipIds: false,
    slipIdLabelSizePx: 13,
    showDepths: false,
    showSlipLength: false,
    showSlipWidth: false,
    showEdgeExtensions: false,
    edgeExtensionFt: 10,
    tableSortKey: "slip_id",
    tableSortDir: "asc",
    colorSlipsBy: "none",
    displayMeasurementToleranceFt: 0.1,
    dimensionColorToleranceFt: 0.5,
    showDimensionLegend: true,
    lengthWidthToleranceFt: 0.5,
    rotationToleranceDeg: 0.5,
    filterHiddenValues: {},
    // Full Dock's default "start of slips" distance from the centerline on each side, used
    // to skip the inner-edge click (Enter accepts it as-is; a click still overrides it for
    // a marina whose walkway is unusually wide/narrow). No universal number is
    // structurally correct here -- it's a per-marina construction detail -- so this is a
    // conservative, editable starting guess rather than something derived from geometry.
    fullDockWalkwayFt: 15,
  };
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const merged = { ...defaultState(), ...(parsed && typeof parsed === "object" ? parsed : {}) };
    merged.measurementDate = todayDate();
    merged.dockName = String(parsed?.dockName ?? parsed?.slipIdPart1 ?? merged.dockName ?? "");
    merged.slipIdSeparator = ["-", "."].includes(String(merged.slipIdSeparator || "")) ? String(merged.slipIdSeparator) : "-";
    merged.slipIdSuffix = String(merged.slipIdSuffix || "");
    merged.spaceType = SPACE_TYPE_OPTIONS.includes(String(merged.spaceType || "")) ? String(merged.spaceType) : "";
    merged.slipIdIncrementBy = Math.max(1, Number(merged.slipIdIncrementBy || 1) || 1);
    merged.slipIdAutoIncrement = merged.slipIdAutoIncrement !== false;
    merged.slipIdIncrementBySuffix = Math.max(1, Number(merged.slipIdIncrementBySuffix || 1) || 1);
    merged.slipIdAutoIncrementSuffix = merged.slipIdAutoIncrementSuffix === true;
    merged.multislipId = String(merged.multislipId || "").trim() || DEFAULT_MULTISLIP_ID;
    merged.lineSlipCount = Math.max(1, Number(merged.lineSlipCount || 6) || 6);
    merged.slipGeometryTypes = Array.isArray(merged.slipGeometryTypes) ? merged.slipGeometryTypes : [];
    merged.slipGeometryPolygons = Array.isArray(merged.slipGeometryPolygons) ? merged.slipGeometryPolygons : [];
    merged.slipGeometrySelectedId = "";
    merged.slipGeometrySelectedIds = [];
    merged.slipGeometryDraftTypeId = "";
    merged.slipGeometryRunBetweenCount = Math.max(1, Number(merged.slipGeometryRunBetweenCount || 5) || 5);
    merged.showSlipIds = merged.showSlipIds === true;
    merged.slipIdLabelSizePx = clampNumber(Number(merged.slipIdLabelSizePx || 13), 8, 28);
    merged.showDepths = merged.showDepths === true;
    merged.showSlipLength = merged.showSlipLength === true;
    merged.showSlipWidth = merged.showSlipWidth === true;
    merged.showEdgeExtensions = merged.showEdgeExtensions === true;
    merged.edgeExtensionFt = Number.isFinite(Number(merged.edgeExtensionFt)) && Number(merged.edgeExtensionFt) >= 0 ? Number(merged.edgeExtensionFt) : 10;
    merged.tableSortKey = String(merged.tableSortKey || "slip_id");
    merged.tableSortDir = String(merged.tableSortDir || "asc").toLowerCase() === "desc" ? "desc" : "asc";
    merged.colorSlipsBy = normalizeColorSlipsBy(merged.colorSlipsBy);
    merged.displayMeasurementToleranceFt = [0.1, 0.25, 0.5, 1].includes(Number(merged.displayMeasurementToleranceFt))
      ? Number(merged.displayMeasurementToleranceFt)
      : 0.1;
    merged.dimensionColorToleranceFt = [0.5, 1].includes(Number(merged.dimensionColorToleranceFt))
      ? Number(merged.dimensionColorToleranceFt)
      : 0.5;
    merged.showDimensionLegend = merged.showDimensionLegend !== false;
    merged.lengthWidthToleranceFt = [0.1, 0.25, 0.5, 1].includes(Number(merged.lengthWidthToleranceFt))
      ? Number(merged.lengthWidthToleranceFt)
      : 0.5;
    merged.rotationToleranceDeg = [0.1, 0.5, 1].includes(Number(merged.rotationToleranceDeg))
      ? Number(merged.rotationToleranceDeg)
      : 0.5;
    merged.filterHiddenValues = (merged.filterHiddenValues && typeof merged.filterHiddenValues === "object" && !Array.isArray(merged.filterHiddenValues)) ? merged.filterHiddenValues : {};
    for (const mk of Object.keys(merged.filterHiddenValues)) {
      merged.filterHiddenValues[mk] = Array.isArray(merged.filterHiddenValues[mk]) ? merged.filterHiddenValues[mk].map(String) : [];
    }
    return merged;
  } catch {
    return defaultState();
  }
}

function persistState(state) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {}
}

// Math.random().toString(36) doesn't guarantee a fixed length -- trailing zeros in the float's
// binary representation drop from the base-36 string, so a single call can yield fewer than 8
// chars after slicing (this produced a 7-char measurement ID in production). Loop until there's
// enough length banked, then take exactly 8. At least one of the 8 is always a letter: an
// all-digit ID (about 1 in 60 draws) fails Safe Harbor's client-side validation.
function makeId(prefix = "MEAS") {
  let s = "";
  while (s.length < 8) s += Math.random().toString(36).slice(2);
  s = s.slice(0, 8).toUpperCase();
  if (!/[A-Z]/.test(s)) {
    const i = Math.floor(Math.random() * 8), letter = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    s = s.slice(0, i) + letter + s.slice(i + 1);
  }
  return `${prefix}-${s}`;
}

function buildSlipIdFromState(state) {
  const dock = String(state?.dockName || "");
  const sep = String(state?.slipIdSeparator || "-");
  const num = String(state?.slipIdPart3 || "");
  const suf = String(state?.slipIdSuffix || "");
  return [dock, num, suf].filter(Boolean).join(sep);
}

function incrementSlipIdNumber(state) {
  if (state?.slipIdAutoIncrement !== true) return;
  const current = String(state?.slipIdPart3 || "").trim();
  const incrementBy = Math.max(1, Number(state?.slipIdIncrementBy || 1) || 1);
  if (!/^\d+$/.test(current)) return;
  const width = current.length;
  const next = String((Number(current) || 0) + incrementBy);
  state.slipIdPart3 = next.length < width ? next.padStart(width, "0") : next;
}

// Spreadsheet-column-style alphabetical increment (A, B, ... Z, AA, AB, ...), mirroring
// incrementSlipIdNumber but for the letters-only Suffix field. Case of the existing suffix
// is preserved; anything that isn't purely letters (blank, "A1", etc.) is left untouched
// since there's no unambiguous "next" value for it.
function nextAlphaSuffix(value) {
  const chars = value.split("");
  let i = chars.length - 1;
  while (i >= 0) {
    if (chars[i] === "Z") {
      chars[i] = "A";
      i -= 1;
    } else {
      chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1);
      return chars.join("");
    }
  }
  return "A" + chars.join("");
}

function incrementSlipIdSuffix(state) {
  if (state?.slipIdAutoIncrementSuffix !== true) return;
  const current = String(state?.slipIdSuffix || "").trim();
  if (!current || !/^[A-Za-z]+$/.test(current)) return;
  const incrementBy = Math.max(1, Number(state?.slipIdIncrementBySuffix || 1) || 1);
  const isLower = current === current.toLowerCase();
  let letters = current.toUpperCase();
  for (let n = 0; n < incrementBy; n++) letters = nextAlphaSuffix(letters);
  state.slipIdSuffix = isLower ? letters.toLowerCase() : letters;
}

const DEFAULT_MULTISLIP_ID = "MS-001";

// Shared label-column width for the Slip ID / Attributes field grids in New Slip Defaults, so
// both panels' labels and inputs line up on the same left edge.
const FIELD_LABEL_COL_WIDTH = 72;

// Bumps the trailing digit run of a multislip ID (e.g. "MS-001" -> "MS-002"),
// preserving zero-padding width. IDs that don't end in digits are left as-is
// (the user typed a custom name — don't mangle it).
function incrementMultislipId(current) {
  const value = String(current || "").trim();
  const match = /^(.*?)(\d+)$/.exec(value);
  if (!match) return value || DEFAULT_MULTISLIP_ID;
  const [, prefix, digits] = match;
  const width = digits.length;
  const next = String(Number(digits) + 1);
  return prefix + (next.length < width ? next.padStart(width, "0") : next);
}

function slipIdForDockNameChange(record, nextDockName, fallbackSeparator = "-") {
  const oldSlipId = String(record?.slip_id || "").trim();
  const oldDockName = String(record?.dock_name || "").trim();
  const nextDock = String(nextDockName || "").trim();
  const sep = String(fallbackSeparator || "-") || "-";
  if (!oldSlipId) return nextDock;

  if (oldDockName) {
    if (oldSlipId === oldDockName) return nextDock;
    if (oldSlipId.startsWith(oldDockName)) {
      const suffix = oldSlipId.slice(oldDockName.length);
      if (!suffix || /^[-.\s_]/.test(suffix)) return nextDock ? nextDock + suffix : suffix.replace(/^[-.\s_]+/, "");
    }
  }

  const separated = /^(.+?)([-.])(.+)$/.exec(oldSlipId);
  if (separated) return nextDock ? nextDock + separated[2] + separated[3] : separated[3];
  if (/^\d+$/.test(oldSlipId)) return nextDock ? nextDock + sep + oldSlipId : oldSlipId;
  return nextDock ? nextDock + sep + oldSlipId : oldSlipId;
}

// Rebuilds slip_id when only the Slip Number (formerly labeled "Next ID") changes, holding
// the record's existing Dock prefix and separator fixed. Mirror image of
// slipIdForDockNameChange, which holds the number fixed and rebuilds around a new Dock.
// (slipIdForNumberChange, the mirror-image "hold Dock fixed, rebuild the number" function,
// was retired once its only caller -- saveSelectedRecord -- moved to a full
// buildSlipIdFromState rebuild that also needs to fold Suffix in.)

// Pulls the editable Slip Number back out of an existing record's slip_id, stripping the
// Dock prefix (and, once one exists, the trailing Suffix) so the attributes panel can show
// just the number for editing.
function slipNumberFromRecord(record) {
  const slipId = String(record?.slip_id || "").trim();
  const dockName = String(record?.dock_name || "").trim();
  let numberPart;
  if (!slipId || !dockName) {
    numberPart = slipId;
  } else if (slipId === dockName) {
    numberPart = "";
  } else if (slipId.startsWith(dockName)) {
    numberPart = slipId.slice(dockName.length).replace(/^[-.\s_]+/, "");
  } else {
    // dock_name doesn't actually match slip_id's own prefix -- it's drifted out of sync (a
    // slip can be "mis-docked" in that stored column while its Slip ID still reads
    // correctly). Trusting dockName here would return the whole slip_id unstripped instead
    // of just its number (and callers that then rebuild dock+sep+number, e.g. the lasso
    // tool's "Dock" action, would duplicate the dock: "K-059" -> "K-K-059"). Parse slip_id's
    // own shape instead, same fallback slipIdForDockNameChange already uses for the
    // mirror-image case.
    const separated = /^(.+?)([-.])(.+)$/.exec(slipId);
    numberPart = separated ? separated[3] : slipId;
  }
  // numberPart may still carry a trailing "<sep>Suffix" segment (e.g. "011-A") once a Suffix
  // is set -- strip it using the stored suffix column (this column can't drift the way
  // dock_name did, since nothing but this feature's write paths ever sets it) so this
  // returns the bare Slip Number, not "Number-Suffix" as one string.
  const suffix = String(record?.suffix || "").trim();
  if (suffix && numberPart.endsWith(suffix)) {
    const before = numberPart.slice(0, numberPart.length - suffix.length);
    if (/[-.\s_]$/.test(before)) numberPart = before.slice(0, -1);
  }
  return numberPart;
}

// Inverse of slipIdForDockNameChange -- pulls a slip's real current Dock back out of its
// slip_id when dock_name doesn't actually match (drifted out of sync, same "mis-docked"
// scenario slipNumberFromRecord guards against), so a caller that needs "this slip's actual
// dock" (e.g. the lasso tool renumbering without touching Dock) doesn't rebuild slip_id
// around a stale dock_name.
function slipDockFromRecord(record) {
  const slipId = String(record?.slip_id || "").trim();
  const dockName = String(record?.dock_name || "").trim();
  if (!slipId) return dockName;
  if (dockName && (slipId === dockName || slipId.startsWith(dockName))) return dockName;
  const separated = /^(.+?)([-.])(.+)$/.exec(slipId);
  return separated ? separated[1] : dockName;
}

// Suffix is a brand-new column with no legacy data to drift, so unlike Dock/Number it can be
// trusted directly -- nothing but this feature's own write paths (which stay disciplined
// about keeping slip_id text and this column in sync) ever sets it.
function slipSuffixFromRecord(record) {
  return String(record?.suffix || "").trim();
}

// Mirror of slipIdForDockNameChange, for the trailing segment instead of the leading one:
// holds Dock+Number fixed and rebuilds only the Suffix segment. Used by the inline
// table-cell edit path, the one place a Suffix change happens without a full pseudo-state
// (see saveSelectedRecord/buildSlipIdFromState for the "change several parts at once" path).
function slipIdForSuffixChange(record, nextSuffix, fallbackSeparator = "-") {
  const oldSlipId = String(record?.slip_id || "").trim();
  const oldSuffix = String(record?.suffix || "").trim();
  const nextSuf = String(nextSuffix || "").trim();
  const sep = String(fallbackSeparator || "-") || "-";
  if (!oldSlipId) return nextSuf;
  let base = oldSlipId;
  if (oldSuffix && base.endsWith(oldSuffix)) {
    const before = base.slice(0, base.length - oldSuffix.length);
    if (/[-.\s_]$/.test(before)) base = before.slice(0, -1);
  }
  if (!base) return nextSuf;
  return nextSuf ? `${base}${sep}${nextSuf}` : base;
}

function previewSlipIds(state, count) {
  const total = Math.max(1, Number(count || 0) || 0);
  const base = {
    dockName: String(state?.dockName || ""),
    slipIdSeparator: String(state?.slipIdSeparator || "-"),
    slipIdPart3: String(state?.slipIdPart3 || ""),
    slipIdSuffix: String(state?.slipIdSuffix || ""),
    slipIdAutoIncrement: state?.slipIdAutoIncrement !== false,
    slipIdIncrementBy: Math.max(1, Number(state?.slipIdIncrementBy || 1) || 1),
    slipIdAutoIncrementSuffix: state?.slipIdAutoIncrementSuffix === true,
    slipIdIncrementBySuffix: Math.max(1, Number(state?.slipIdIncrementBySuffix || 1) || 1),
  };
  const values = [];
  for (let index = 0; index < total; index += 1) {
    values.push(buildSlipIdFromState(base) || `SLIP-${String(index + 1).padStart(4, "0")}`);
    incrementSlipIdNumber(base);
    incrementSlipIdSuffix(base);
  }
  return values;
}

function buildPointWkt(lng, lat) {
  return `POINT(${Number(lng).toFixed(6)} ${Number(lat).toFixed(6)})`;
}

function buildPolygonWkt(latLngRing) {
  const ring = Array.isArray(latLngRing) ? latLngRing : [];
  if (ring.length < 3) return "";
  const coords = ring.map((pair) => {
    const lat = Number(pair?.[0]);
    const lng = Number(pair?.[1]);
    return Number.isFinite(lat) && Number.isFinite(lng) ? `${lng.toFixed(7)} ${lat.toFixed(7)}` : null;
  }).filter(Boolean);
  if (coords.length < 3) return "";
  if (coords[0] !== coords[coords.length - 1]) coords.push(coords[0]);
  return `POLYGON((${coords.join(", ")}))`;
}

function parsePolygonWkt(wkt) {
  const text = String(wkt || "").trim();
  const match = /^POLYGON\s*\(\(\s*(.+?)\s*\)\)$/i.exec(text);
  if (!match) return null;
  const coords = String(match[1] || "")
    .split(",")
    .map((chunk) => String(chunk || "").trim())
    .map((chunk) => {
      const parts = chunk.split(/\s+/);
      if (parts.length < 2) return null;
      const lng = Number(parts[0]);
      const lat = Number(parts[1]);
      return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
    })
    .filter(Boolean);
  if (coords.length < 4) return null;
  const first = coords[0];
  const last = coords[coords.length - 1];
  if (first && last && Number(first[0]) === Number(last[0]) && Number(first[1]) === Number(last[1])) {
    coords.pop();
  }
  return coords.length >= 3 ? coords : null;
}

function parsePointWkt(wkt) {
  const text = String(wkt || "").trim();
  const match = /^POINT\s*\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(text);
  if (!match) return null;
  const lng = Number(match[1]);
  const lat = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function parseLineStringWkt(wkt) {
  const text = String(wkt || "").trim();
  const match = /^LINESTRING\s*\(\s*(.+?)\s*\)$/i.exec(text);
  if (!match) return null;
  const coords = String(match[1] || "")
    .split(",")
    .map((chunk) => String(chunk || "").trim())
    .map((chunk) => {
      const parts = chunk.split(/\s+/);
      if (parts.length < 2) return null;
      const lng = Number(parts[0]);
      const lat = Number(parts[1]);
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    })
    .filter(Boolean);
  return coords.length >= 2 ? coords : null;
}

function geometryFromWkt(wkt) {
  const point = parsePointWkt(wkt);
  if (point) {
    return {
      type: "Point",
      coordinates: [Number(point.lng), Number(point.lat)],
    };
  }
  const line = parseLineStringWkt(wkt);
  if (Array.isArray(line) && line.length >= 2) {
    return {
      type: "LineString",
      coordinates: line.map((entry) => [Number(entry.lng), Number(entry.lat)]),
    };
  }
  const polygon = parsePolygonWkt(wkt);
  if (Array.isArray(polygon) && polygon.length >= 3) {
    const ring = polygon.map((pair) => [Number(pair[1]), Number(pair[0])]);
    const first = ring[0];
    const last = ring[ring.length - 1];
    if (!last || Number(first?.[0]) !== Number(last?.[0]) || Number(first?.[1]) !== Number(last?.[1])) {
      ring.push([Number(first?.[0]), Number(first?.[1])]);
    }
    return {
      type: "Polygon",
      coordinates: [ring],
    };
  }
  return null;
}

function polygonAreaCentroid(ring) {
  const n = ring.length;
  // Translate to origin first — without this, cross products of large geographic
  // coordinates (e.g. lat≈38, lng≈-122) lose all precision for marina-scale polygons.
  const refLat = Number(ring[0][0]);
  const refLng = Number(ring[0][1]);
  let A = 0;
  let cLat = 0;
  let cLng = 0;
  for (let i = 0; i < n; i++) {
    const dLat0 = Number(ring[i][0]) - refLat;
    const dLng0 = Number(ring[i][1]) - refLng;
    const dLat1 = Number(ring[(i + 1) % n][0]) - refLat;
    const dLng1 = Number(ring[(i + 1) % n][1]) - refLng;
    const cross = dLng0 * dLat1 - dLng1 * dLat0;
    A += cross;
    cLat += (dLat0 + dLat1) * cross;
    cLng += (dLng0 + dLng1) * cross;
  }
  A /= 2;
  if (Math.abs(A) < 1e-20) {
    const sum = ring.reduce((acc, p) => [acc[0] + Number(p[0]), acc[1] + Number(p[1])], [0, 0]);
    return { lat: sum[0] / n, lng: sum[1] / n };
  }
  return { lat: refLat + cLat / (6 * A), lng: refLng + cLng / (6 * A) };
}

function pointFromAnyWkt(wkt) {
  const point = parsePointWkt(wkt);
  if (point) return point;
  const line = parseLineStringWkt(wkt);
  if (Array.isArray(line) && line.length >= 2) {
    const mid = line[Math.floor(line.length / 2)];
    if (mid) return { lat: Number(mid.lat), lng: Number(mid.lng) };
  }
  const ring = parsePolygonWkt(wkt);
  if (!Array.isArray(ring) || ring.length < 3) return null;
  return polygonAreaCentroid(ring);
}

function inferPolyMetricsFromRing(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  const ll = ring.map((pair) => L.latLng(Number(pair[0]), Number(pair[1]))).filter((entry) => Number.isFinite(entry.lat) && Number.isFinite(entry.lng));
  if (ll.length < 3) return null;
  const center = {
    lat: ll.reduce((sum, p) => sum + p.lat, 0) / ll.length,
    lng: ll.reduce((sum, p) => sum + p.lng, 0) / ll.length,
  };
  const cosLat = Math.max(0.000001, Math.cos((center.lat * Math.PI) / 180));
  const localMeters = ll.map((p) => ({
    x: (p.lng - center.lng) * (111320 * cosLat),
    y: (p.lat - center.lat) * 111320,
  }));
  const candidateAngles = [];
  for (let i = 0; i < localMeters.length; i += 1) {
    const a = localMeters[i];
    const b = localMeters[(i + 1) % localMeters.length];
    const dx = Number(b.x - a.x);
    const dy = Number(b.y - a.y);
    const segLen = Math.hypot(dx, dy);
    if (segLen < 0.01) continue;
    const angle = Math.atan2(dx, dy);
    const normalized = ((angle % Math.PI) + Math.PI) % Math.PI;
    if (!candidateAngles.some((value) => Math.abs(value - normalized) < 1e-6)) candidateAngles.push(normalized);
  }
  if (!candidateAngles.length) candidateAngles.push(0);
  let bestFit = null;
  for (const angle of candidateAngles) {
    const ux = Math.sin(angle);
    const uy = Math.cos(angle);
    const vx = Math.sin(angle + (Math.PI / 2));
    const vy = Math.cos(angle + (Math.PI / 2));
    const projections = localMeters.map((p) => ({
      along: (p.x * ux) + (p.y * uy),
      across: (p.x * vx) + (p.y * vy),
    }));
    const alongVals = projections.map((entry) => entry.along);
    const acrossVals = projections.map((entry) => entry.across);
    const alongRange = Math.max(...alongVals) - Math.min(...alongVals);
    const acrossRange = Math.max(...acrossVals) - Math.min(...acrossVals);
    const area = alongRange * acrossRange;
    if (!bestFit || area < bestFit.area) {
      bestFit = {
        angle,
        projections,
        alongRange,
        acrossRange,
        area,
      };
    }
  }
  const chosenAngle = Number(bestFit?.angle || 0);
  const alongRange = Number(bestFit?.alongRange || 0);
  const acrossRange = Number(bestFit?.acrossRange || 0);
  const headingRad = alongRange >= acrossRange ? chosenAngle : (chosenAngle + (Math.PI / 2));
  const headingDeg = (((headingRad * (180 / Math.PI)) % 360) + 360) % 360;
  const rad = headingRad;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const local = localMeters.map((p) => ({
    along: (p.x * ux) + (p.y * uy),
    across: (p.x * vx) + (p.y * vy),
  }));
  const alongVals = local.map((entry) => entry.along);
  const acrossVals = local.map((entry) => entry.across);
  const lengthM = Math.max(...alongVals) - Math.min(...alongVals);
  const widthM = Math.max(...acrossVals) - Math.min(...acrossVals);
  return {
    center,
    headingDeg,
    lengthFt: Math.max(1, metersToFeet(lengthM)),
    widthFt: Math.max(1, metersToFeet(widthM)),
  };
}

// Outward push direction + physical length (ft) for edge i of a ring (array of {lat,lng}).
// For a 4-point ring, "outward" means away from the opposite edge's midpoint, so pushing
// this edge grows the true rectangle dimension spanning between the two -- same convention
// renderSlipEditHandles's single-slip edge handles already use. For any other ring size
// there's no "opposite edge" concept, so it falls back to the edge's own perpendicular.
// Returns null for a near-degenerate edge (nothing meaningful to resize/match against).
function msRingEdgeNormal(ring, i) {
  const n = ring.length;
  const R = 111320;
  const a = ring[i];
  const b = ring[(i + 1) % n];
  const mid = { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 };
  const midLatRad = (mid.lat * Math.PI) / 180;
  const edgeDx = (b.lng - a.lng) * Math.cos(midLatRad) * R;
  const edgeDy = (b.lat - a.lat) * R;
  const edgeLenFt = Math.sqrt((edgeDx * edgeDx) + (edgeDy * edgeDy)) * 3.280839895;
  let nx, ny;
  if (n === 4) {
    const oppA = ring[(i + 2) % n];
    const oppB = ring[(i + 3) % n];
    const oppMid = { lat: (oppA.lat + oppB.lat) / 2, lng: (oppA.lng + oppB.lng) / 2 };
    const rvx = (mid.lng - oppMid.lng) * Math.cos(midLatRad) * R;
    const rvy = (mid.lat - oppMid.lat) * R;
    const rLen = Math.sqrt((rvx * rvx) + (rvy * rvy));
    if (rLen < 0.001) return null;
    nx = rvx / rLen; ny = rvy / rLen;
  } else {
    const svx = (b.lng - a.lng) * Math.cos(midLatRad) * R;
    const svy = (b.lat - a.lat) * R;
    const sideLenM = Math.sqrt((svx * svx) + (svy * svy));
    if (sideLenM < 0.001) return null;
    nx = -svy / sideLenM; ny = svx / sideLenM;
  }
  return { mid, nx, ny, edgeLenFt };
}

// Pure: pushes edge i's two vertices along (nx, ny) by snappedM meters. Returns a new ring
// (array of {lat,lng}); doesn't mutate the input.
function msPushRingEdge(ring, i, nx, ny, snappedM) {
  const n = ring.length;
  const R = 111320;
  return ring.map((pt, j) => {
    if (j !== i && j !== (i + 1) % n) return pt;
    const ptLatRad = (pt.lat * Math.PI) / 180;
    return {
      lat: pt.lat + ((snappedM * ny) / R),
      lng: pt.lng + ((snappedM * nx) / (R * Math.cos(ptLatRad))),
    };
  });
}

// For multi-slip resize propagation: given a target outward direction expressed in this
// ring's own (east,north) terms -- e.g. sign * <that slip's own length-axis unit vector> --
// finds whichever of its 4 edges pushes most closely in that direction. Only rectangles
// (4-point rings) get matched; an irregular ring (mitred chain segment, hand-reshaped
// corner) has no principled "corresponding edge" to find, so a drag on one slip never
// propagates onto those -- they still get their own handles, just no cross-slip echo.
function msFindMatchingRingEdge(ring, targetX, targetY) {
  if (!Array.isArray(ring) || ring.length !== 4) return null;
  let best = null;
  for (let i = 0; i < 4; i++) {
    const info = msRingEdgeNormal(ring, i);
    if (!info || info.edgeLenFt < 2) continue;
    const dot = (info.nx * targetX) + (info.ny * targetY);
    if (!best || dot > best.dot) best = { i, dot, ...info };
  }
  return best;
}

function transformRingLatLngsWithMetrics({
  ringLatLngs,
  oldCenter,
  newCenter,
  oldHeadingDeg,
  newHeadingDeg,
  oldLengthFt,
  newLengthFt,
  oldWidthFt,
  newWidthFt,
}) {
  const ring = Array.isArray(ringLatLngs) ? ringLatLngs : [];
  if (!ring.length) return null;
  const oc = { lat: Number(oldCenter?.lat), lng: Number(oldCenter?.lng) };
  const nc = { lat: Number(newCenter?.lat), lng: Number(newCenter?.lng) };
  if (!Number.isFinite(oc.lat) || !Number.isFinite(oc.lng) || !Number.isFinite(nc.lat) || !Number.isFinite(nc.lng)) return null;

  const oldHeadingRad = (Number(oldHeadingDeg || 0) * Math.PI) / 180;
  const newHeadingRad = (Number(newHeadingDeg || 0) * Math.PI) / 180;
  const oldUx = Math.sin(oldHeadingRad);
  const oldUy = Math.cos(oldHeadingRad);
  const oldVx = Math.sin(oldHeadingRad + (Math.PI / 2));
  const oldVy = Math.cos(oldHeadingRad + (Math.PI / 2));
  const newUx = Math.sin(newHeadingRad);
  const newUy = Math.cos(newHeadingRad);
  const newVx = Math.sin(newHeadingRad + (Math.PI / 2));
  const newVy = Math.cos(newHeadingRad + (Math.PI / 2));

  const oldLen = Math.max(0.0001, Number(oldLengthFt || 1));
  const oldWid = Math.max(0.0001, Number(oldWidthFt || 1));
  const newLen = Math.max(0.0001, Number(newLengthFt || oldLen));
  const newWid = Math.max(0.0001, Number(newWidthFt || oldWid));
  const lenScale = newLen / oldLen;
  const widScale = newWid / oldWid;
  const cosLat = Math.max(0.000001, Math.cos((oc.lat * Math.PI) / 180));

  const out = ring.map((pair) => {
    const lat = Number(pair?.[0]);
    const lng = Number(pair?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const dLatM = (lat - oc.lat) * 111320;
    const dLngM = (lng - oc.lng) * (111320 * cosLat);
    const along = ((dLngM * oldUx) + (dLatM * oldUy)) * lenScale;
    const across = ((dLngM * oldVx) + (dLatM * oldVy)) * widScale;
    const nextDlngM = (along * newUx) + (across * newVx);
    const nextDlatM = (along * newUy) + (across * newVy);
    const nextLat = nc.lat + (nextDlatM / 111320);
    const nextLng = nc.lng + (nextDlngM / (111320 * Math.max(0.000001, Math.cos((nc.lat * Math.PI) / 180))));
    return [nextLat, nextLng];
  }).filter(Boolean);
  return out.length >= 3 ? out : null;
}

function translateRingLatLngs(ringLatLngs, fromCenter, toCenter) {
  const ring = Array.isArray(ringLatLngs) ? ringLatLngs : [];
  if (!ring.length) return null;
  const fc = { lat: Number(fromCenter?.lat), lng: Number(fromCenter?.lng) };
  const tc = { lat: Number(toCenter?.lat), lng: Number(toCenter?.lng) };
  if (!Number.isFinite(fc.lat) || !Number.isFinite(fc.lng) || !Number.isFinite(tc.lat) || !Number.isFinite(tc.lng)) return null;
  const dLat = tc.lat - fc.lat;
  const dLng = tc.lng - fc.lng;
  const moved = ring
    .map((pair) => [Number(pair?.[0]) + dLat, Number(pair?.[1]) + dLng])
    .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
  return moved.length >= 3 ? moved : null;
}

// A-B is the fixed first edge; C is the freely-dragged point for the second edge (B->C,
// unconstrained instead of perpendicular to A-B). Ring = A, B, B+(C-B), A+(C-B) -- opposite
// sides equal and parallel by construction, i.e. a parallelogram sheared by however far C
// has moved off the perpendicular.
function buildParallelogramRing(A, B, C) {
  const dv = { lat: Number(C.lat) - Number(B.lat), lng: Number(C.lng) - Number(B.lng) };
  return [
    [Number(A.lat), Number(A.lng)],
    [Number(B.lat), Number(B.lng)],
    [Number(B.lat) + dv.lat, Number(B.lng) + dv.lng],
    [Number(A.lat) + dv.lat, Number(A.lng) + dv.lng],
  ];
}

function roundToHalfFoot(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0.5, Math.round(n * 2) / 2);
}

function roundToStep(value, step, min = 0) {
  const n = Number(value);
  const s = Number(step);
  if (!Number.isFinite(n)) return Number(min) || 0;
  if (!Number.isFinite(s) || s <= 0) return Math.max(Number(min) || 0, n);
  const rounded = Math.round(n / s) * s;
  const places = String(s).includes(".") ? String(s).split(".")[1].length : 0;
  return Math.max(Number(min) || 0, Number(rounded.toFixed(Math.min(6, places))));
}

function toFeetDistance(a, b) {
  const meters = Number(a?.distanceTo?.(b) || 0);
  return meters * 3.280839895;
}

function metersToFeet(meters) {
  return Number(meters || 0) * 3.280839895;
}

function feetToMeters(feet) {
  return Number(feet || 0) / 3.280839895;
}

function offsetPoint(center, dxMeters, dyMeters) {
  const lat = Number(center.lat);
  const lng = Number(center.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return { lat: 0, lng: 0 };
  const dLat = dyMeters / 111320;
  const dLng = dxMeters / (111320 * Math.max(0.000001, Math.cos((lat * Math.PI) / 180)));
  return { lat: lat + dLat, lng: lng + dLng };
}

// ── Multislip geometry helpers ────────────────────────────────────────────────
function msDockvec(p1, p2) {
  // Unit vector (east/north) along dock + raw length in metres
  const cosLat = Math.cos((p1.lat * Math.PI) / 180);
  const ex = (p2.lng - p1.lng) * cosLat * 111320;
  const ny = (p2.lat - p1.lat) * 111320;
  const lenM = Math.sqrt(ex * ex + ny * ny);
  return lenM < 0.001 ? { ux: 1, uy: 0, lenM: 0 } : { ux: ex / lenM, uy: ny / lenM, lenM };
}

function msPerpDistFt(p1, p2, mouse) {
  // Signed perpendicular distance (feet) from dock line to mouse point.
  // Positive = left when facing p1→p2; negative = right.
  const { ux, uy } = msDockvec(p1, p2);
  const cosLat = Math.cos((p1.lat * Math.PI) / 180);
  const ex = (mouse.lng - p1.lng) * cosLat * 111320;
  const ny = (mouse.lat - p1.lat) * 111320;
  return (-uy * ex + ux * ny) * 3.280839895; // dot with left-normal (-uy, ux)
}

// Inverse of msRectCornersSheared's corner offset: given a raw mouse point, recover the
// (shearDeg, widthFt) pair that places the sheared offshore corner (anchored at p2) at
// the mouse -- lets freeform mode set angle AND length together from one drag instead of
// measuring perpendicular distance and shearing after the fact. Anchoring at p2 (not p1)
// matters: it must match msRectCornersSheared's own c3 = p2 + tilt*wM anchor.
// shearDeg is rounded to a whole degree, then widthFt is the mouse's projection onto
// THAT (rounded) tilt direction -- self-consistent with the rounded angle actually used
// for rendering, rather than an inverse of the raw unrounded angle.
// pivot: the dock-side corner the tilted side hangs from (default p2, as the freeform
// length phases use; the Fingers tilt handle passes the run's start corner instead).
// side: slips extruded to the right of p1->p2 (negative width) point the other way, so
// the angle is measured against that side's own perpendicular -- without this every
// right-side freeform/tilt drag snapped to -80 deg.
function msDeriveShearedLength(p1, p2, mouse, pivot = p2) {
  const { ux, uy } = msDockvec(p1, p2);
  const cosLat = Math.cos((p1.lat * Math.PI) / 180);
  const ex = (mouse.lng - pivot.lng) * cosLat * 111320;
  const ny = (mouse.lat - pivot.lat) * 111320;
  const perpFt = (-uy * ex + ux * ny) * 3.280839895;
  const alongFt = (ux * ex + uy * ny) * 3.280839895;
  const side = perpFt < 0 ? -1 : 1;
  const rawDeg = (Math.atan2(-alongFt * side, perpFt * side) * 180) / Math.PI;
  const shearDeg = Math.round(Math.max(-80, Math.min(80, rawDeg)));
  const { tiltX, tiltY } = msTiltVec(p1, p2, shearDeg);
  const widthFt = (tiltX * ex + tiltY * ny) * 3.280839895;
  return { shearDeg, widthFt };
}

function msRectCorners(p1, p2, widthFt) {
  // Rectangle: dock line + perpendicular extrusion. Returns [p1, p2, c3, c4].
  const { ux, uy } = msDockvec(p1, p2);
  const wM = widthFt / 3.280839895;
  const c3 = offsetPoint(p2, -uy * wM, ux * wM);
  const c4 = offsetPoint(p1, -uy * wM, ux * wM);
  return [p1, p2, c3, c4];
}

// slotFracs, when given, is a length-N array of [t1, t2] along-dock fraction pairs (0..1)
// replacing the default even i/N split -- lets Multislip derive each slot's actual span
// from independently-placed finger centers plus a shared finger-width gap (adjacent
// slots need NOT share an edge, unlike the old single-shared-boundary scheme) while Comb
// (which never passes it) keeps its plain even split unchanged.
function msSlotCentroid(p1, p2, widthFt, i, N, slotFracs = null) {
  const [ft1, ft2] = slotFracs ? slotFracs[i] : [i / N, (i + 1) / N];
  const t = (ft1 + ft2) / 2;
  const along = { lat: p1.lat + t * (p2.lat - p1.lat), lng: p1.lng + t * (p2.lng - p1.lng) };
  const { ux, uy } = msDockvec(p1, p2);
  const halfM = widthFt / 2 / 3.280839895;
  return offsetPoint(along, -uy * halfM, ux * halfM);
}

function msSlotCorners(p1, p2, widthFt, i, N) {
  const t1 = i / N, t2 = (i + 1) / N;
  const sp1 = { lat: p1.lat + t1 * (p2.lat - p1.lat), lng: p1.lng + t1 * (p2.lng - p1.lng) };
  const sp2 = { lat: p1.lat + t2 * (p2.lat - p1.lat), lng: p1.lng + t2 * (p2.lng - p1.lng) };
  return msRectCorners(sp1, sp2, widthFt);
}
// Same as msRectCorners, but the offshore edge is tilted shearDeg away from pure
// perpendicular instead of straight out from the dock line -- p1/p2 (the dock-side edge)
// stay exactly where they are; only c3/c4 (offshore) shift sideways, producing a
// parallelogram instead of a rectangle. shearDeg 0 is identical to msRectCorners.
function msTiltVec(p1, p2, shearDeg) {
  const { ux, uy } = msDockvec(p1, p2);
  const shearRad = (Math.max(-80, Math.min(80, Number(shearDeg) || 0)) * Math.PI) / 180;
  const perpX = -uy, perpY = ux;
  const cosS = Math.cos(shearRad), sinS = Math.sin(shearRad);
  return { tiltX: (perpX * cosS) - (perpY * sinS), tiltY: (perpX * sinS) + (perpY * cosS) };
}
function msRectCornersSheared(p1, p2, widthFt, shearDeg) {
  const wM = widthFt / 3.280839895;
  const { tiltX, tiltY } = msTiltVec(p1, p2, shearDeg);
  const c3 = offsetPoint(p2, tiltX * wM, tiltY * wM);
  const c4 = offsetPoint(p1, tiltX * wM, tiltY * wM);
  return [p1, p2, c3, c4];
}
function msSlotCornersPerLenSheared(p1, p2, slotLenFt, i, N, shearDeg, slotFracs = null) {
  const [t1, t2] = slotFracs ? slotFracs[i] : [i / N, (i + 1) / N];
  const sp1 = { lat: p1.lat + t1 * (p2.lat - p1.lat), lng: p1.lng + t1 * (p2.lng - p1.lng) };
  const sp2 = { lat: p1.lat + t2 * (p2.lat - p1.lat), lng: p1.lng + t2 * (p2.lng - p1.lng) };
  return msRectCornersSheared(sp1, sp2, slotLenFt, shearDeg);
}
function msSlotCornersPerLen(p1, p2, slotLenFt, i, N, slotFracs = null) {
  const [t1, t2] = slotFracs ? slotFracs[i] : [i / N, (i + 1) / N];
  const sp1 = { lat: p1.lat + t1 * (p2.lat - p1.lat), lng: p1.lng + t1 * (p2.lng - p1.lng) };
  const sp2 = { lat: p1.lat + t2 * (p2.lat - p1.lat), lng: p1.lng + t2 * (p2.lng - p1.lng) };
  return msRectCorners(sp1, sp2, slotLenFt);
}
function msRotateAroundPivot(point, pivot, fromDir, toDir) {
  // Rotate `point` around `pivot` by the angle delta from (pivot→fromDir) to (pivot→toDir).
  const cosLat = Math.cos(pivot.lat * Math.PI / 180);
  const toVec = (pt) => ({ e: (pt.lng - pivot.lng) * cosLat * 111320, n: (pt.lat - pivot.lat) * 111320 });
  const f = toVec(fromDir), t = toVec(toDir), p = toVec(point);
  const dTheta = Math.atan2(t.n, t.e) - Math.atan2(f.n, f.e);
  const cos = Math.cos(dTheta), sin = Math.sin(dTheta);
  return {
    lat: pivot.lat + (p.e * sin + p.n * cos) / 111320,
    lng: pivot.lng + (p.e * cos - p.n * sin) / (cosLat * 111320),
  };
}
function msSlotOffshorePoint(p1, p2, slotLenFt, i, N, slotFracs = null) {
  const [ft1, ft2] = slotFracs ? slotFracs[i] : [i / N, (i + 1) / N];
  const t = (ft1 + ft2) / 2;
  const along = { lat: p1.lat + t * (p2.lat - p1.lat), lng: p1.lng + t * (p2.lng - p1.lng) };
  const { ux, uy } = msDockvec(p1, p2);
  const wM = slotLenFt / 3.280839895;
  return offsetPoint(along, -uy * wM, ux * wM);
}
// Same as msSlotOffshorePoint, but tilted by shearDeg so the drag handle sits on the
// actual sheared offshore edge (msSlotCornersPerLenSheared) instead of the plain
// perpendicular midpoint. shearDeg 0 is identical to msSlotOffshorePoint.
function msSlotOffshorePointSheared(p1, p2, slotLenFt, i, N, shearDeg, slotFracs = null) {
  const [ft1, ft2] = slotFracs ? slotFracs[i] : [i / N, (i + 1) / N];
  const t = (ft1 + ft2) / 2;
  const along = { lat: p1.lat + t * (p2.lat - p1.lat), lng: p1.lng + t * (p2.lng - p1.lng) };
  const wM = slotLenFt / 3.280839895;
  const { tiltX, tiltY } = msTiltVec(p1, p2, shearDeg);
  return offsetPoint(along, tiltX * wM, tiltY * wM);
}
// Point at fraction t (0..1) along p1→p2, plus the inverse: given an arbitrary point,
// the (unclamped) fraction of its projection onto the p1→p2 line. Used by the Multislip
// finger-center and finger-width handle drags to constrain motion to the dock line and
// read back a t value.
function msPointAtFrac(p1, p2, t) {
  return { lat: p1.lat + t * (p2.lat - p1.lat), lng: p1.lng + t * (p2.lng - p1.lng) };
}
function msProjectFrac(p1, p2, pt) {
  const cosLat = Math.cos((p1.lat * Math.PI) / 180);
  const ex = (p2.lng - p1.lng) * cosLat * 111320;
  const ny = (p2.lat - p1.lat) * 111320;
  const lenSqM = (ex * ex) + (ny * ny);
  if (lenSqM < 1e-9) return 0;
  const px = (pt.lng - p1.lng) * cosLat * 111320;
  const py = (pt.lat - p1.lat) * 111320;
  return ((px * ex) + (py * ny)) / lenSqM;
}
function msEvenFracs(N) {
  return Array.from({ length: N + 1 }, (_, i) => i / N);
}
// Derives each slot's [t1, t2] along-dock span from N+1 finger-center fractions
// (centerFracs[0] and [N] are the run's own start/end, not fingers) plus one shared
// finger-width gap in feet, split evenly on either side of every center -- including the
// two end slots' outer edges. Trimming only the interior side of the end slots (an earlier
// version of this) left them systematically half a finger-width longer than every other
// slot, forcing a manual fix on the two ends after every commit; trimming both sides
// uniformly means every slot gets identical treatment and only genuine outliers need a
// manual drag. Degenerates to touching (old) edges at fingerWidthFt=0.
function msSlotFracsFromCenters(centerFracs, fingerWidthFt, runLenFt) {
  const N = centerFracs.length - 1;
  const halfFrac = runLenFt > 0.01 ? (Math.max(0, Number(fingerWidthFt) || 0) / 2) / runLenFt : 0;
  const out = [];
  for (let i = 0; i < N; i++) {
    const lo = centerFracs[i] + halfFrac;
    const hi = centerFracs[i + 1] - halfFrac;
    out.push(hi >= lo ? [lo, hi] : [(lo + hi) / 2, (lo + hi) / 2]);
  }
  return out;
}

// Offsets a bent polyline (chainPoints) by a single signed width, mitering the
// offshore corner at each interior vertex so adjacent segments share an edge
// instead of gapping/overlapping. Falls back to a bevel (each segment keeps its
// own unmitered corner, leaving a visible seam) when the miter would project
// past `miterLimit` × width — e.g. a sharp reflex bend — which is also the
// self-intersecting-bowtie case. Returns one ring per segment plus the indices
// of vertices that had to bevel, so the caller can flag them for review.
function mitreChainRing(points, widthFt, { miterLimit = 3 } = {}) {
  const n = points.length;
  if (n < 2 || !widthFt) return { rings: [], flaggedVertices: [] };
  const anchor = points[0];
  const cosLat = Math.max(0.000001, Math.cos((anchor.lat * Math.PI) / 180));
  const toXY = (pt) => ({
    x: (pt.lng - anchor.lng) * cosLat * 111320,
    y: (pt.lat - anchor.lat) * 111320,
  });
  const fromXY = (p) => offsetPoint(anchor, p.x, p.y);
  const wM = widthFt / 3.280839895;
  const pts = points.map(toXY);

  const segOffset = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = pts[i + 1].x - pts[i].x, dy = pts[i + 1].y - pts[i].y;
    const len = Math.hypot(dx, dy) || 1e-6;
    const ux = dx / len, uy = dy / len;
    const nx = -uy * wM, ny = ux * wM;
    segOffset.push({
      o1: { x: pts[i].x + nx, y: pts[i].y + ny },
      o2: { x: pts[i + 1].x + nx, y: pts[i + 1].y + ny },
    });
  }

  const lineIntersect = (p1, p2, p3, p4) => {
    const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
    const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
    return { x: p1.x + t * d1x, y: p1.y + t * d1y };
  };

  const nearCorner = new Array(n - 1);
  const farCorner = new Array(n - 1);
  nearCorner[0] = segOffset[0].o1;
  farCorner[n - 2] = segOffset[n - 2].o2;

  const flaggedVertices = [];
  for (let i = 1; i < n - 1; i++) {
    const A = segOffset[i - 1], B = segOffset[i];
    const inter = lineIntersect(A.o1, A.o2, B.o1, B.o2);
    const miterOk = inter && Math.hypot(inter.x - pts[i].x, inter.y - pts[i].y) <= miterLimit * Math.abs(wM);
    if (miterOk) {
      farCorner[i - 1] = inter;
      nearCorner[i] = inter;
    } else {
      farCorner[i - 1] = A.o2;
      nearCorner[i] = B.o1;
      flaggedVertices.push(i);
    }
  }

  const rings = [];
  for (let i = 0; i < n - 1; i++) {
    rings.push([pts[i], pts[i + 1], farCorner[i], nearCorner[i]].map(fromXY));
  }
  return { rings, flaggedVertices };
}

// The largest width (feet, in the given direction: +1 = left-of-travel, -1 = right)
// that keeps every slip a simple, non-overlapping quad. Only concave bends (where
// offsetting toward `sign` pinches the inside of a turn) can force this below
// Infinity — a convex bend just widens its miter wedge and never overlaps anything.
//
// Every offshore corner (whether it's a chain endpoint or a shared miter join
// with a neighbor) moves in exact proportion to width, so each is an affine
// function of W: corner(W) = base + W*slope. For each segment, its own near and
// far corner must stay in order along the segment (near before far) — solving
// for where they'd cross gives the exact width where that slip would invert.
// This is per-SEGMENT rather than per-vertex on purpose: a short segment between
// two concave joins gets pulled inward from both ends at once, and checking each
// join in isolation (against the segment's raw, un-pulled far point) misses that
// combined pinch — the failure this was tightened to catch.
function maxChainWidthFt(points, sign) {
  const n = points.length;
  if (n < 2 || !sign) return Infinity;
  const anchor = points[0];
  const cosLat = Math.max(0.000001, Math.cos((anchor.lat * Math.PI) / 180));
  const toXY = (pt) => ({
    x: (pt.lng - anchor.lng) * cosLat * 111320,
    y: (pt.lat - anchor.lat) * 111320,
  });
  const pts = points.map(toXY);

  const dirs = [];
  for (let i = 0; i < n - 1; i++) {
    const dx = pts[i + 1].x - pts[i].x, dy = pts[i + 1].y - pts[i].y;
    const len = Math.hypot(dx, dy) || 1e-6;
    dirs.push({ x: dx / len, y: dy / len, len });
  }
  const leftNormal = (d) => ({ x: -d.y, y: d.x });

  const lineIntersect = (p1, p2, p3, p4) => {
    const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
    const d2x = p4.x - p3.x, d2y = p4.y - p3.y;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-9) return null;
    const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / denom;
    return { x: p1.x + t * d1x, y: p1.y + t * d1y };
  };

  // k[i] = slope of the shared offshore corner at interior vertex i: the corner
  // sits at pts[i] + W*k[i] for every real W (probing at W=1 fixes the slope,
  // since the construction is affine). A near-parallel join (denom≈0, an almost
  // straight-through vertex) is treated as unjoined — the two slopes are nearly
  // equal there anyway, so it's a harmless approximation.
  const k = new Array(n);
  for (let i = 1; i < n - 1; i++) {
    const dPrev = dirs[i - 1], dNext = dirs[i];
    const nPrev = leftNormal(dPrev), nNext = leftNormal(dNext);
    const inter = lineIntersect(
      { x: pts[i - 1].x + nPrev.x, y: pts[i - 1].y + nPrev.y },
      { x: pts[i].x + nPrev.x, y: pts[i].y + nPrev.y },
      { x: pts[i].x + nNext.x, y: pts[i].y + nNext.y },
      { x: pts[i + 1].x + nNext.x, y: pts[i + 1].y + nNext.y },
    );
    k[i] = inter ? { x: inter.x - pts[i].x, y: inter.y - pts[i].y } : nNext;
  }

  let upperBound = Infinity; // valid range is W <= upperBound
  let lowerBound = -Infinity; // valid range is W >= lowerBound
  for (let i = 0; i < n - 1; i++) {
    const d = dirs[i];
    const nearSlope = i === 0 ? leftNormal(d) : k[i];
    const farSlope = i === n - 2 ? leftNormal(d) : k[i + 1];
    const delta = (nearSlope.x * d.x + nearSlope.y * d.y) - (farSlope.x * d.x + farSlope.y * d.y);
    if (Math.abs(delta) < 1e-9) continue;
    const crit = d.len / delta;
    if (delta > 0) { if (crit < upperBound) upperBound = crit; }
    else if (crit > lowerBound) lowerBound = crit;
  }

  const maxWM = sign > 0 ? upperBound : -lowerBound;
  if (!Number.isFinite(maxWM)) return Infinity;
  return Math.max(0.1, maxWM * 3.280839895);
}

// Caps a candidate chain width at the overlap limit for whichever side it's on.
function clampChainWidthFt(points, widthFt) {
  const sign = widthFt < 0 ? -1 : 1;
  const maxFt = maxChainWidthFt(points, sign);
  return Math.abs(widthFt) > maxFt ? sign * maxFt : widthFt;
}

// ─────────────────────────────────────────────────────────────────────────────

function computeSlipPolygonLatLngs(poly) {
  if (Array.isArray(poly?.ringLatLngs) && poly.ringLatLngs.length >= 3) {
    return poly.ringLatLngs
      .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
      .filter((pair) => Number.isFinite(pair[0]) && Number.isFinite(pair[1]));
  }
  const center = { lat: Number(poly?.center?.lat), lng: Number(poly?.center?.lng) };
  const lengthM = feetToMeters(poly?.lengthFt || 0);
  const widthM = feetToMeters(poly?.widthFt || 0);
  const headingDeg = Number(poly?.headingDeg || 0);
  const rad = (headingDeg * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const hl = lengthM / 2;
  const hw = widthM / 2;
  const localCorners = [
    { x: hl, y: hw },
    { x: hl, y: -hw },
    { x: -hl, y: -hw },
    { x: -hl, y: hw },
  ];
  const corners = localCorners.map((point) => ({
    x: (ux * point.x) + (vx * point.y),
    y: (uy * point.x) + (vy * point.y),
  }));
  return corners.map((corner) => {
    const ll = offsetPoint(center, corner.x, corner.y);
    return [ll.lat, ll.lng];
  });
}

// Cool blue / soft violet -- distinct from the amber selection highlight, the green
// multislip preview, and the magenta line records, and legible on both light and dark tiles.
const SLIP_DIMENSION_LINE_COLORS = { length: "#38bdf8", width: "#a78bfa" };

// Light, neutral construction-line color -- distinct from the semantic length/width/selection
// palette above, reads as a reference/guide line rather than data.
const SLIP_EDGE_EXTENSION_COLOR = "#e2e8f0";

// For a selected slip, one line per polygon edge, each stretched past both of its own corners
// by extensionFt -- lets the drawn edge (and its dotted overhang) be compared visually against
// an adjacent slip's edge or a dock line to check alignment. Reuses msDockvec's local
// east/north meter projection (already used for the same kind of edge-direction math
// elsewhere in this file) and offsetPoint to walk back out to lat/lng.
// Core geometry, split out from computeSlipEdgeExtensionLines below so a live drag can call it
// directly with a not-yet-committed ring -- computeSlipPolygonLatLngs only ever reads
// poly.ringLatLngs, which stays at its pre-drag value until dragend (see liveUpdateRing /
// liveRedrawEdgeExtensions in renderSlipEditHandles).
function computeEdgeExtensionLinesFromRing(ring, extensionFt) {
  const extM = feetToMeters(Number(extensionFt) || 0);
  if (!Array.isArray(ring) || ring.length < 3 || extM <= 0) return [];
  const n = ring.length;
  const lines = [];
  for (let i = 0; i < n; i += 1) {
    const a = { lat: ring[i][0], lng: ring[i][1] };
    const b = { lat: ring[(i + 1) % n][0], lng: ring[(i + 1) % n][1] };
    const { ux, uy, lenM } = msDockvec(a, b);
    if (lenM < 0.01) continue;
    const start = offsetPoint(a, -ux * extM, -uy * extM);
    const end = offsetPoint(b, ux * extM, uy * extM);
    lines.push([[start.lat, start.lng], [end.lat, end.lng]]);
  }
  return lines;
}

function computeSlipEdgeExtensionLines(poly, extensionFt) {
  return computeEdgeExtensionLinesFromRing(computeSlipPolygonLatLngs(poly), extensionFt);
}

// Centerline endpoints for the length axis and width axis of a slip. A 4-vertex ring (chain's
// mitred segments, or any parametric rectangle) draws these directly from the ring's own edge
// midpoints -- geometrically exact for any quadrilateral, rectangle or trapezoid alike, since it
// needs no assumption about which way is "length" beyond matching the two candidate spans
// against the record's own measured_linear_feet (see below). This matters because a mitred
// chain segment at a dock bend is a trapezoid, not a rectangle:
// the parametric fallback below (center/heading/length/width from inferPolyMetricsFromRing's
// bounding-box fit) doesn't coincide with the ring's actual boundary for a shape like that,
// which is what made the overlay look offset from the polygon.
// Minimum-area bounding rectangle fit for a ring of any vertex count, self-consistent:
// center, length, and width all come from the SAME fit. inferPolyMetricsFromRing's
// `center` is the ring's own vertex centroid instead -- fine for placement/dragging, but
// for a non-symmetric ring (a mitred multi-segment alongside wall, boat ramp, travel lift
// bay -- anything that isn't a simple quad) that centroid can land many meters from the
// bounding rectangle's own middle. Mixing the two (as the parametric fallback below does
// via poly.center + poly.lengthFt/widthFt/headingDeg) is what pushed the dimension-line
// overlay visibly off the polygon for those shapes.
function fitRingBoundingRect(ring) {
  const pts = ring.map((pair) => L.latLng(Number(pair?.[0]), Number(pair?.[1])));
  if (pts.length < 3 || !pts.every((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))) return null;
  const refLat = pts.reduce((sum, p) => sum + p.lat, 0) / pts.length;
  const refLng = pts.reduce((sum, p) => sum + p.lng, 0) / pts.length;
  const cosLat = Math.max(0.000001, Math.cos((refLat * Math.PI) / 180));
  const local = pts.map((p) => ({
    x: (p.lng - refLng) * (111320 * cosLat),
    y: (p.lat - refLat) * 111320,
  }));
  const candidateAngles = [];
  for (let i = 0; i < local.length; i += 1) {
    const a = local[i];
    const b = local[(i + 1) % local.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (Math.hypot(dx, dy) < 0.01) continue;
    const angle = ((Math.atan2(dx, dy) % Math.PI) + Math.PI) % Math.PI;
    if (!candidateAngles.some((value) => Math.abs(value - angle) < 1e-6)) candidateAngles.push(angle);
  }
  if (!candidateAngles.length) candidateAngles.push(0);
  let best = null;
  for (const angle of candidateAngles) {
    const ux = Math.sin(angle), uy = Math.cos(angle);
    const vx = Math.sin(angle + (Math.PI / 2)), vy = Math.cos(angle + (Math.PI / 2));
    const along = local.map((p) => (p.x * ux) + (p.y * uy));
    const across = local.map((p) => (p.x * vx) + (p.y * vy));
    const alongMin = Math.min(...along), alongMax = Math.max(...along);
    const acrossMin = Math.min(...across), acrossMax = Math.max(...across);
    const area = (alongMax - alongMin) * (acrossMax - acrossMin);
    if (!best || area < best.area) best = { angle, ux, uy, vx, vy, alongMin, alongMax, acrossMin, acrossMax, area };
  }
  if (!best) return null;
  const alongMid = (best.alongMin + best.alongMax) / 2;
  const acrossMid = (best.acrossMin + best.acrossMax) / 2;
  const centerX = (best.ux * alongMid) + (best.vx * acrossMid);
  const centerY = (best.uy * alongMid) + (best.vy * acrossMid);
  const toLatLng = (x, y) => { const ll = offsetPoint({ lat: refLat, lng: refLng }, x, y); return [ll.lat, ll.lng]; };
  const alongHalf = (best.alongMax - best.alongMin) / 2;
  const acrossHalf = (best.acrossMax - best.acrossMin) / 2;
  return {
    alongM: best.alongMax - best.alongMin,
    acrossM: best.acrossMax - best.acrossMin,
    alongLine: [toLatLng(centerX - (best.ux * alongHalf), centerY - (best.uy * alongHalf)), toLatLng(centerX + (best.ux * alongHalf), centerY + (best.uy * alongHalf))],
    acrossLine: [toLatLng(centerX - (best.vx * acrossHalf), centerY - (best.vy * acrossHalf)), toLatLng(centerX + (best.vx * acrossHalf), centerY + (best.vy * acrossHalf))],
  };
}

function computeSlipDimensionLines(poly, record) {
  const ring = Array.isArray(poly?.ringLatLngs) ? poly.ringLatLngs : null;
  if (ring && ring.length === 4) {
    const pts = ring.map((pair) => L.latLng(Number(pair?.[0]), Number(pair?.[1])));
    if (pts.every((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))) {
      const mid = (a, b) => [(a.lat + b.lat) / 2, (a.lng + b.lng) / 2];
      const midAsLatLng = (a, b) => L.latLng(mid(a, b)[0], mid(a, b)[1]);
      const pairA = [midAsLatLng(pts[0], pts[1]), midAsLatLng(pts[2], pts[3])];
      const pairB = [midAsLatLng(pts[1], pts[2]), midAsLatLng(pts[3], pts[0])];
      const lenA = pairA[0].distanceTo(pairA[1]);
      const lenB = pairB[0].distanceTo(pairB[1]);
      // Assign length/width to whichever pair's on-map distance is closer to the stored
      // measured_linear_feet, not just "whichever pair is longer" -- a slip whose length/width
      // got recorded backwards (see swapLWForSelected / the Swap Length-Width lasso tool) needs
      // these lines to actually flip when swapped, and matching against the stored value does
      // that; magnitude-only picking is self-correcting the wrong way, always relabeling the
      // physically-longer edge "length" regardless of what the record says. Falls back to
      // magnitude when there's no usable stored length (uncommitted draft, no record yet, or
      // a legacy record with no measured_linear_feet).
      const storedLengthFt = Number(record?.measured_linear_feet);
      const hasStoredLength = Number.isFinite(storedLengthFt) && storedLengthFt > 0;
      let longPair, shortPair;
      if (hasStoredLength) {
        const storedLengthM = feetToMeters(storedLengthFt);
        const diffA = Math.abs(lenA - storedLengthM);
        const diffB = Math.abs(lenB - storedLengthM);
        [longPair, shortPair] = diffA <= diffB ? [pairA, pairB] : [pairB, pairA];
      } else {
        [longPair, shortPair] = lenA >= lenB ? [pairA, pairB] : [pairB, pairA];
      }
      const toPair = (p) => [[p[0].lat, p[0].lng], [p[1].lat, p[1].lng]];
      return { lengthLine: toPair(longPair), widthLine: toPair(shortPair) };
    }
  }
  if (ring && ring.length >= 3) {
    const fit = fitRingBoundingRect(ring);
    if (fit) {
      const storedLengthFt = Number(record?.measured_linear_feet);
      const hasStoredLength = Number.isFinite(storedLengthFt) && storedLengthFt > 0;
      let lengthLine, widthLine;
      if (hasStoredLength) {
        const storedLengthM = feetToMeters(storedLengthFt);
        const diffAlong = Math.abs(fit.alongM - storedLengthM);
        const diffAcross = Math.abs(fit.acrossM - storedLengthM);
        [lengthLine, widthLine] = diffAlong <= diffAcross ? [fit.alongLine, fit.acrossLine] : [fit.acrossLine, fit.alongLine];
      } else {
        [lengthLine, widthLine] = fit.alongM >= fit.acrossM ? [fit.alongLine, fit.acrossLine] : [fit.acrossLine, fit.alongLine];
      }
      return {
        lengthLine: (fit.alongM > 0 || fit.acrossM > 0) ? lengthLine : null,
        widthLine: (fit.alongM > 0 || fit.acrossM > 0) ? widthLine : null,
      };
    }
  }
  const center = { lat: Number(poly?.center?.lat), lng: Number(poly?.center?.lng) };
  if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng)) return null;
  const lengthM = feetToMeters(poly?.lengthFt || 0);
  const widthM = feetToMeters(poly?.widthFt || 0);
  const rad = (Number(poly?.headingDeg || 0) * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const hl = lengthM / 2;
  const hw = widthM / 2;
  const toLatLng = (x, y) => { const ll = offsetPoint(center, x, y); return [ll.lat, ll.lng]; };
  return {
    lengthLine: lengthM > 0 ? [toLatLng(-ux * hl, -uy * hl), toLatLng(ux * hl, uy * hl)] : null,
    widthLine: widthM > 0 ? [toLatLng(-vx * hw, -vy * hw), toLatLng(vx * hw, vy * hw)] : null,
  };
}

function computeParametricSlipPolygonLatLngs({
  center,
  lengthFt,
  widthFt,
  headingDeg,
}) {
  const c = { lat: Number(center?.lat), lng: Number(center?.lng) };
  const lengthM = feetToMeters(lengthFt || 0);
  const widthM = feetToMeters(widthFt || 0);
  const heading = Number(headingDeg || 0);
  const rad = (heading * Math.PI) / 180;
  const ux = Math.sin(rad);
  const uy = Math.cos(rad);
  const vx = Math.sin(rad + (Math.PI / 2));
  const vy = Math.cos(rad + (Math.PI / 2));
  const hl = lengthM / 2;
  const hw = widthM / 2;
  const localCorners = [
    { x: hl, y: hw },
    { x: hl, y: -hw },
    { x: -hl, y: -hw },
    { x: -hl, y: hw },
  ];
  const corners = localCorners.map((point) => ({
    x: (ux * point.x) + (vx * point.y),
    y: (uy * point.x) + (vy * point.y),
  }));
  return corners.map((corner) => {
    const ll = offsetPoint(c, corner.x, corner.y);
    return [ll.lat, ll.lng];
  });
}

function pointInPolygon(point, ring) {
  if (!point || !Array.isArray(ring) || ring.length < 3) return false;
  const x = Number(point.lng);
  const y = Number(point.lat);
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][1]);
    const yi = Number(ring[i][0]);
    const xj = Number(ring[j][1]);
    const yj = Number(ring[j][0]);
    // (yi > y) !== (yj > y) already guarantees yi !== yj whenever the division below runs (if
    // they were equal, both sides of the XOR would be identical, making the whole condition
    // false) -- so yj - yi can never legitimately be 0 here. The Math.max(1e-12, ...) that used
    // to wrap it was meant to guard exactly that impossible case, but since Math.max doesn't
    // know about sign, it silently replaced any *negative* yj-yi (roughly half of a polygon's
    // edges, depending on winding direction) with 1e-12, blowing the division up into an
    // effectively random xCross value -- which flipped the crossing count for points nowhere
    // near the polygon. Every prior caller only ever tested a point already known to be close
    // to its own polygon, which happened not to expose this; testing arbitrary points against
    // arbitrary polygons (lasso select) did.
    const intersects = ((yi > y) !== (yj > y))
      && (x < (((xj - xi) * (y - yi)) / (yj - yi) + xi));
    if (intersects) inside = !inside;
  }
  return inside;
}

function makeGeometryId() {
  return `GEOM-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
}

function bearingDegrees(start, end) {
  const lat1 = (Number(start?.lat || 0) * Math.PI) / 180;
  const lat2 = (Number(end?.lat || 0) * Math.PI) / 180;
  const dLon = ((Number(end?.lng || 0) - Number(start?.lng || 0)) * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = (Math.cos(lat1) * Math.sin(lat2))
    - (Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon));
  const brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
}

// Rectangle centroid for the click-drawn placement flow: midpoint(A,B) offset
// along the length axis (headingDeg) by half the length. lengthFt is SIGNED
// (from msPerpDistFt) so a click on either side of A-B lands the centroid on
// the correct side with no separate branch.
function computeDrawnRectCenter({ A, B, lengthFt, headingDeg }) {
  const mid = { lat: (Number(A.lat) + Number(B.lat)) / 2, lng: (Number(A.lng) + Number(B.lng)) / 2 };
  const rad = (Number(headingDeg || 0) * Math.PI) / 180;
  const halfM = feetToMeters(Number(lengthFt || 0)) / 2;
  return offsetPoint(mid, halfM * Math.sin(rad), halfM * Math.cos(rad));
}

// Same as computeDrawnRectCenter but the full offset (not half) -- the far edge's midpoint,
// i.e. where the adjust handle sits during the review stage after the second dimension locks
// in (mirrors msSlotOffshorePoint, which does the same job for Multislip's per-slot handles).
function computeDrawnFarEdgeMidpoint({ A, B, lengthFt, headingDeg }) {
  const mid = { lat: (Number(A.lat) + Number(B.lat)) / 2, lng: (Number(A.lng) + Number(B.lng)) / 2 };
  const rad = (Number(headingDeg || 0) * Math.PI) / 180;
  const fullM = feetToMeters(Number(lengthFt || 0));
  return offsetPoint(mid, fullM * Math.sin(rad), fullM * Math.cos(rad));
}

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function movingAverage(values, radius = 2) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length || radius <= 0) return source.slice();
  return source.map((_, index) => {
    let total = 0;
    let count = 0;
    const start = Math.max(0, index - radius);
    const end = Math.min(source.length - 1, index + radius);
    for (let cursor = start; cursor <= end; cursor += 1) {
      total += Number(source[cursor]) || 0;
      count += 1;
    }
    return count ? total / count : 0;
  });
}

function seriesStats(values) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length) return { mean: 0, stdDev: 0 };
  const mean = source.reduce((sum, value) => sum + (Number(value) || 0), 0) / source.length;
  const variance = source.reduce((sum, value) => {
    const delta = (Number(value) || 0) - mean;
    return sum + (delta * delta);
  }, 0) / source.length;
  return { mean, stdDev: Math.sqrt(Math.max(0, variance)) };
}

function medianValue(values) {
  const nums = (Array.isArray(values) ? values : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  if (!nums.length) return null;
  const mid = Math.floor(nums.length / 2);
  return nums.length % 2 ? nums[mid] : ((nums[mid - 1] + nums[mid]) / 2);
}

function detectSignalPeaks(signal, { minDistance = 6, minProminence = 0 } = {}) {
  const values = Array.isArray(signal) ? signal : [];
  const distance = Math.max(1, Math.round(Number(minDistance) || 1));
  const prominenceFloor = Math.max(0, Number(minProminence) || 0);
  const candidates = [];
  for (let index = 1; index < values.length - 1; index += 1) {
    const current = Number(values[index]) || 0;
    const prev = Number(values[index - 1]) || 0;
    const next = Number(values[index + 1]) || 0;
    if (!(current > prev && current >= next)) continue;
    const leftBase = Math.min(prev, Number(values[Math.max(0, index - distance)]) || prev);
    const rightBase = Math.min(next, Number(values[Math.min(values.length - 1, index + distance)]) || next);
    const prominence = current - Math.max(leftBase, rightBase);
    if (prominence < prominenceFloor) continue;
    candidates.push({ index, value: current, prominence });
  }
  candidates.sort((a, b) => b.prominence - a.prominence);
  const selected = [];
  for (const candidate of candidates) {
    if (selected.some((entry) => Math.abs(entry.index - candidate.index) < distance)) continue;
    selected.push(candidate);
  }
  return selected.sort((a, b) => a.index - b.index);
}

function sampleCanvasLuma(imageData, width, height, x, y) {
  const maxX = Math.max(0, width - 1);
  const maxY = Math.max(0, height - 1);
  const fx = clampNumber(Number(x) || 0, 0, maxX);
  const fy = clampNumber(Number(y) || 0, 0, maxY);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(maxX, x0 + 1);
  const y1 = Math.min(maxY, y0 + 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const lumaAt = (px, py) => {
    const offset = ((py * width) + px) * 4;
    const r = Number(imageData[offset] || 0);
    const g = Number(imageData[offset + 1] || 0);
    const b = Number(imageData[offset + 2] || 0);
    return (0.2126 * r) + (0.7152 * g) + (0.0722 * b);
  };
  const l00 = lumaAt(x0, y0);
  const l10 = lumaAt(x1, y0);
  const l01 = lumaAt(x0, y1);
  const l11 = lumaAt(x1, y1);
  const top = l00 + ((l10 - l00) * tx);
  const bottom = l01 + ((l11 - l01) * tx);
  return top + ((bottom - top) * ty);
}

function sampleCanvasAlpha(imageData, width, height, x, y) {
  const maxX = Math.max(0, width - 1);
  const maxY = Math.max(0, height - 1);
  const fx = clampNumber(Number(x) || 0, 0, maxX);
  const fy = clampNumber(Number(y) || 0, 0, maxY);
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const x1 = Math.min(maxX, x0 + 1);
  const y1 = Math.min(maxY, y0 + 1);
  const tx = fx - x0;
  const ty = fy - y0;
  const alphaAt = (px, py) => {
    const offset = ((py * width) + px) * 4;
    return Number(imageData[offset + 3] || 0);
  };
  const a00 = alphaAt(x0, y0);
  const a10 = alphaAt(x1, y0);
  const a01 = alphaAt(x0, y1);
  const a11 = alphaAt(x1, y1);
  const top = a00 + ((a10 - a00) * tx);
  const bottom = a01 + ((a11 - a01) * tx);
  return top + ((bottom - top) * ty);
}

function buildImagerySpacingSignal(imageData, width, height, startPoint, endPoint, { sampleCount, crossSampleCount, corridorHalfWidthPx }) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(5, Number(crossSampleCount) || 13);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    let edgeEnergy = 0;
    let previous = null;
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      const luma = sampleCanvasLuma(imageData, width, height, sampleX, sampleY);
      if (previous != null) edgeEnergy += Math.abs(luma - previous);
      previous = luma;
    }
    values.push(edgeEnergy);
  }
  return movingAverage(values, 2);
}

function percentile(values, pct) {
  const source = (Array.isArray(values) ? values : []).filter((v) => Number.isFinite(Number(v))).map(Number).sort((a, b) => a - b);
  if (!source.length) return null;
  const p = clampNumber(Number(pct) || 0.5, 0, 1);
  const idx = clampNumber(Math.round((source.length - 1) * p), 0, source.length - 1);
  return source[idx];
}

function buildImagerySpacingSignalRobust(imageData, width, height, startPoint, endPoint, {
  sampleCount,
  crossSampleCount,
  corridorHalfWidthPx,
  darkPercentile = 0.3,
} = {}) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(9, Number(crossSampleCount) || 25);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    const lumas = [];
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      lumas.push(sampleCanvasLuma(imageData, width, height, sampleX, sampleY));
    }
    const darkValue = percentile(lumas, darkPercentile);
    values.push(Number.isFinite(Number(darkValue)) ? Number(darkValue) : 0);
  }
  return movingAverage(values, 2);
}

function buildImagerySpacingSignalBinary(imageData, width, height, startPoint, endPoint, {
  sampleCount,
  crossSampleCount,
  corridorHalfWidthPx,
  alphaThreshold = 10,
  darkPercentile = 0.35,
} = {}) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return [];
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(11, Number(crossSampleCount) || 33);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  const values = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    const lumas = [];
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      const alpha = sampleCanvasAlpha(imageData, width, height, sampleX, sampleY);
      if (alpha < alphaThreshold) continue;
      lumas.push(sampleCanvasLuma(imageData, width, height, sampleX, sampleY));
    }
    if (!lumas.length) {
      values.push(0);
      continue;
    }
    const darkCutoff = percentile(lumas, darkPercentile);
    if (!Number.isFinite(Number(darkCutoff))) {
      values.push(0);
      continue;
    }
    const darkHits = lumas.filter((value) => Number(value) <= Number(darkCutoff)).length;
    values.push(darkHits / Math.max(1, lumas.length));
  }
  return movingAverage(values, 2);
}

function estimateSignalCoverage(imageData, width, height, startPoint, endPoint, {
  sampleCount,
  crossSampleCount,
  corridorHalfWidthPx,
  alphaThreshold = 10,
} = {}) {
  const dx = Number(endPoint.x) - Number(startPoint.x);
  const dy = Number(endPoint.y) - Number(startPoint.y);
  const lineLengthPx = Math.hypot(dx, dy);
  if (!Number.isFinite(lineLengthPx) || lineLengthPx <= 0) return { coverageRatio: 0, meanLuma: 0, lumaStdDev: 0 };
  const tangentX = dx / lineLengthPx;
  const tangentY = dy / lineLengthPx;
  const normalX = -tangentY;
  const normalY = tangentX;
  const crossCount = Math.max(9, Number(crossSampleCount) || 25);
  const halfWidth = Math.max(4, Number(corridorHalfWidthPx) || 16);
  let total = 0;
  let covered = 0;
  const lumas = [];
  for (let index = 0; index < sampleCount; index += 1) {
    const ratio = sampleCount <= 1 ? 0 : (index / (sampleCount - 1));
    const centerX = Number(startPoint.x) + (dx * ratio);
    const centerY = Number(startPoint.y) + (dy * ratio);
    for (let crossIndex = 0; crossIndex < crossCount; crossIndex += 1) {
      const crossRatio = crossCount <= 1 ? 0 : (crossIndex / (crossCount - 1));
      const offset = ((crossRatio * 2) - 1) * halfWidth;
      const sampleX = centerX + (normalX * offset);
      const sampleY = centerY + (normalY * offset);
      total += 1;
      const alpha = sampleCanvasAlpha(imageData, width, height, sampleX, sampleY);
      if (alpha < alphaThreshold) continue;
      covered += 1;
      lumas.push(sampleCanvasLuma(imageData, width, height, sampleX, sampleY));
    }
  }
  const lumaStats = seriesStats(lumas);
  return {
    coverageRatio: total > 0 ? (covered / total) : 0,
    meanLuma: Number.isFinite(lumaStats.mean) ? lumaStats.mean : 0,
    lumaStdDev: Number.isFinite(lumaStats.stdDev) ? lumaStats.stdDev : 0,
  };
}

function estimateSignalLag(signal, { minLag, maxLag }) {
  const source = Array.isArray(signal) ? signal : [];
  if (source.length < 8) return null;
  const { mean, stdDev } = seriesStats(source);
  if (!Number.isFinite(stdDev) || stdDev < 1e-6) return null;
  const normalized = source.map((value) => ((Number(value) || 0) - mean) / stdDev);
  const startLag = clampNumber(Math.round(Number(minLag) || 0), 1, Math.max(1, source.length - 2));
  const endLag = clampNumber(Math.round(Number(maxLag) || 0), startLag, Math.max(startLag, source.length - 2));
  let best = null;
  const scores = [];
  for (let lag = startLag; lag <= endLag; lag += 1) {
    let total = 0;
    let count = 0;
    for (let index = 0; index + lag < normalized.length; index += 1) {
      total += normalized[index] * normalized[index + lag];
      count += 1;
    }
    if (!count) continue;
    const score = total / count;
    scores.push(score);
    if (!best || score > best.score) best = { lag, score };
  }
  if (!best) return null;
  const sortedScores = scores.slice().sort((a, b) => b - a);
  const secondBest = sortedScores[1] ?? sortedScores[0] ?? 0;
  const cycles = source.length / Math.max(1, best.lag);
  const sharpness = Math.max(0, best.score - secondBest);
  const confidence = clampNumber(((best.score - 0.15) / 0.85), 0, 1)
    * clampNumber((cycles - 2) / 5, 0, 1)
    * clampNumber((sharpness + 0.04) / 0.22, 0, 1);
  return {
    lag: best.lag,
    score: best.score,
    confidence,
  };
}

function estimatePitchFromSignal(signal, { minLag, maxLag }) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 8) return null;
  const stats = seriesStats(values);
  const smoothed = movingAverage(values, 1);
  const peaks = detectSignalPeaks(smoothed, {
    minDistance: Math.max(3, Math.round((Number(minLag) || 6) * 0.65)),
    minProminence: Math.max(0.5, stats.stdDev * 0.22),
  });
  const peakIntervals = [];
  for (let index = 1; index < peaks.length; index += 1) {
    const delta = peaks[index].index - peaks[index - 1].index;
    if (delta >= minLag * 0.5 && delta <= maxLag * 1.5) peakIntervals.push(delta);
  }
  const peakMedian = medianValue(peakIntervals);
  const lagEstimate = estimateSignalLag(smoothed, { minLag, maxLag });
  const autocorrLag = lagEstimate?.lag || null;
  let chosenLag = null;
  let method = "";
  if (peakMedian && autocorrLag) {
    const ratio = autocorrLag / Math.max(1e-6, peakMedian);
    if (ratio > 1.75 && ratio < 2.25) {
      chosenLag = peakMedian;
      method = "peaks";
    } else if (ratio > 0.45 && ratio < 0.75) {
      chosenLag = autocorrLag;
      method = "autocorr";
    } else {
      chosenLag = Math.abs(peakMedian - autocorrLag) <= (minLag * 0.45) ? ((peakMedian + autocorrLag) / 2) : peakMedian;
      method = "hybrid";
    }
  } else if (peakMedian) {
    chosenLag = peakMedian;
    method = "peaks";
  } else if (autocorrLag) {
    chosenLag = autocorrLag;
    method = "autocorr";
  } else {
    return null;
  }
  chosenLag = clampNumber(chosenLag, minLag, maxLag);
  const intervalStats = seriesStats(peakIntervals);
  const peakConsistency = peakIntervals.length >= 2 && peakMedian
    ? clampNumber(1 - ((intervalStats.stdDev || 0) / Math.max(peakMedian, 1)), 0, 1)
    : 0;
  const confidence = clampNumber(Math.max(lagEstimate?.confidence || 0, peakConsistency * 0.9), 0, 1);
  return {
    lag: chosenLag,
    confidence,
    method,
    peakCount: peaks.length,
  };
}

function median(values) {
  const source = (Array.isArray(values) ? values : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (!source.length) return null;
  const mid = Math.floor(source.length / 2);
  if (source.length % 2) return source[mid];
  return (source[mid - 1] + source[mid]) / 2;
}

function estimatePitchFromPeaks(signal, { minLag, maxLag, seedLag = null }) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 20) return null;
  const stats = seriesStats(values);
  if (!Number.isFinite(stats.stdDev) || stats.stdDev < 1e-6) return null;
  const minDistance = Math.max(4, Math.round((Number(minLag) || 8) * 0.55));
  const minProminence = Math.max(0.015, stats.stdDev * 0.24);
  const peaks = detectSignalPeaks(values, { minDistance, minProminence });
  if (!peaks || peaks.length < 3) return null;
  const diffs = [];
  for (let i = 1; i < peaks.length; i += 1) {
    const delta = Number(peaks[i].index) - Number(peaks[i - 1].index);
    if (!Number.isFinite(delta)) continue;
    if (delta < Number(minLag) * 0.7 || delta > Number(maxLag) * 1.15) continue;
    diffs.push(delta);
  }
  if (diffs.length < 2) return null;
  const lagMedian = median(diffs);
  if (!Number.isFinite(lagMedian) || lagMedian <= 0) return null;
  let lag = lagMedian;
  if (Number.isFinite(Number(seedLag)) && Number(seedLag) > 0) {
    const sortedBySeed = diffs
      .slice()
      .sort((a, b) => Math.abs(a - Number(seedLag)) - Math.abs(b - Number(seedLag)));
    const nearestSeedLag = sortedBySeed[0];
    if (Number.isFinite(nearestSeedLag)) {
      const nearSeedConfidence = clampNumber(1 - (Math.abs(nearestSeedLag - Number(seedLag)) / Math.max(1, Number(seedLag))), 0, 1);
      const medianConfidence = clampNumber(1 - (Math.abs(lagMedian - Number(seedLag)) / Math.max(1, Number(seedLag))), 0, 1);
      if (nearSeedConfidence >= Math.max(0.45, medianConfidence - 0.1)) {
        lag = nearestSeedLag;
      }
    }
  }
  const spread = seriesStats(diffs).stdDev || 0;
  const relSpread = spread / Math.max(1e-6, lag);
  const confidence = clampNumber((1 - relSpread) * clampNumber(diffs.length / 8, 0, 1), 0, 1);
  return {
    lag,
    score: confidence,
    confidence,
    method: "peak-interval",
    samples: diffs.length,
  };
}

function deriveSlipCentersFromDockPeaks(signal, {
  minLag,
  maxLag,
  seedLag = null,
  sampleCount = 0,
} = {}) {
  const values = Array.isArray(signal) ? signal : [];
  if (values.length < 20 || sampleCount < 20) return null;
  const stats = seriesStats(values);
  if (!Number.isFinite(stats.stdDev) || stats.stdDev < 1e-6) return null;
  const peaks = detectSignalPeaks(values, {
    minDistance: Math.max(3, Math.round((Number(minLag) || 8) * 0.4)),
    minProminence: Math.max(0.006, stats.stdDev * 0.09),
  });
  if (!peaks || peaks.length < 3) return null;
  const intervals = [];
  for (let i = 1; i < peaks.length; i += 1) {
    const delta = Number(peaks[i].index) - Number(peaks[i - 1].index);
    if (!Number.isFinite(delta)) continue;
    if (delta < Number(minLag) * 0.55 || delta > Number(maxLag) * 1.2) continue;
    intervals.push({ delta, left: peaks[i - 1].index, right: peaks[i].index });
  }
  if (intervals.length < 2) return null;
  let selected = intervals;
  if (Number.isFinite(Number(seedLag)) && Number(seedLag) > 0) {
    const seed = Number(seedLag);
    const nearSeed = intervals.filter((entry) => Math.abs(entry.delta - seed) <= Math.max(3, seed * 0.45));
    if (nearSeed.length >= 4) selected = nearSeed;
  }
  const deltas = selected.map((entry) => entry.delta);
  const medianLag = medianValue(deltas);
  if (!Number.isFinite(medianLag) || medianLag <= 0) return null;
  const filtered = selected.filter((entry) => Math.abs(entry.delta - medianLag) <= Math.max(3, medianLag * 0.22));
  const stable = filtered.length >= Math.max(4, Math.round(selected.length * 0.55)) ? filtered : selected;
  const centerRatios = stable
    .map((entry) => ((Number(entry.left) + Number(entry.right)) / 2) / Math.max(1, sampleCount - 1))
    .filter((ratio) => Number.isFinite(ratio))
    .map((ratio) => clampNumber(ratio, 0, 1));
  if (centerRatios.length < 2) return null;
  const spread = seriesStats(stable.map((entry) => entry.delta)).stdDev || 0;
  const confidence = clampNumber((1 - (spread / Math.max(1e-6, medianLag))) * clampNumber(stable.length / 8, 0, 1), 0, 1);
  return {
    centerRatios,
    lag: medianLag,
    confidence,
    intervalsUsed: stable.length,
    peakCount: peaks.length,
  };
}

function chooseConsensusPitchEstimate(estimates = []) {
  const candidates = (Array.isArray(estimates) ? estimates : [])
    .filter((entry) => entry && Number.isFinite(entry.lag) && entry.lag > 0 && Number.isFinite(entry.confidence));
  if (!candidates.length) return null;
  const toleranceFor = (lag) => Math.max(3, Number(lag) * 0.18);
  let bestGroup = null;
  candidates.forEach((candidate) => {
    const group = candidates.filter((entry) => Math.abs(entry.lag - candidate.lag) <= toleranceFor(candidate.lag));
    const support = group.reduce((sum, entry) => sum + Math.max(0.05, Number(entry.confidence) || 0), 0);
    const weightedLag = group.reduce((sum, entry) => sum + (entry.lag * Math.max(0.05, Number(entry.confidence) || 0)), 0) / Math.max(1e-6, support);
    if (!bestGroup || support > bestGroup.support) {
      bestGroup = {
        support,
        weightedLag,
        members: group,
      };
    }
  });
  if (!bestGroup) return candidates[0];
  const bestConfidence = clampNumber(bestGroup.support / Math.max(1, bestGroup.members.length), 0, 1);
  const methods = Array.from(new Set(bestGroup.members.map((entry) => entry.method).filter(Boolean)));
  return {
    lag: bestGroup.weightedLag,
    confidence: bestConfidence,
    method: methods.join("+") || "consensus",
    samples: bestGroup.members.length,
  };
}

function buildSparklinePath(values, width, height, padding = 10) {
  const source = Array.isArray(values) ? values : [];
  if (!source.length) return "";
  const minValue = Math.min(...source);
  const maxValue = Math.max(...source);
  const range = Math.max(1e-6, maxValue - minValue);
  return source.map((value, index) => {
    const x = padding + ((index / Math.max(1, source.length - 1)) * (width - (padding * 2)));
    const y = height - padding - (((value - minValue) / range) * (height - (padding * 2)));
    return `${index === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function estimateWidthFromPerpendicularProfile(imageData, width, height, centerPoint, axisUnit, pixelsPerFoot, priorWidthFt = null) {
  const nx = -Number(axisUnit.y);
  const ny = Number(axisUnit.x);
  const maxHalfWidthPx = clampNumber(Math.round((Number(pixelsPerFoot) || 1) * 26), 18, 120);
  let minWidthPx = clampNumber(Math.round((Number(pixelsPerFoot) || 1) * 6), 6, 90);
  let maxWidthPx = clampNumber(Math.round((Number(pixelsPerFoot) || 1) * 28), minWidthPx + 6, 220);
  const priorPx = Number.isFinite(Number(priorWidthFt))
    ? Number(priorWidthFt) * Math.max(1e-6, Number(pixelsPerFoot) || 1)
    : null;
  if (Number.isFinite(priorPx) && priorPx > 0) {
    minWidthPx = clampNumber(Math.round(priorPx * 0.6), 6, maxHalfWidthPx * 2);
    maxWidthPx = clampNumber(Math.round(priorPx * 1.5), minWidthPx + 4, maxHalfWidthPx * 2);
  }
  const samples = [];
  for (let offset = -maxHalfWidthPx; offset <= maxHalfWidthPx; offset += 1) {
    const x = Number(centerPoint.x) + (nx * offset);
    const y = Number(centerPoint.y) + (ny * offset);
    const luma = sampleCanvasLuma(imageData, width, height, x, y);
    samples.push({ offset, luma });
  }
  if (samples.length < 8) return null;
  const smooth = samples.map((sample, idx) => {
    const prev = samples[Math.max(0, idx - 1)].luma;
    const curr = sample.luma;
    const next = samples[Math.min(samples.length - 1, idx + 1)].luma;
    return (prev + curr + next) / 3;
  });
  const gradients = [];
  for (let i = 1; i < smooth.length; i += 1) {
    gradients.push({
      leftOffset: samples[i - 1].offset,
      rightOffset: samples[i].offset,
      magnitude: Math.abs(smooth[i] - smooth[i - 1]),
    });
  }
  const leftEdges = gradients.filter((g) => g.rightOffset < 0).sort((a, b) => b.magnitude - a.magnitude).slice(0, 20);
  const rightEdges = gradients.filter((g) => g.leftOffset > 0).sort((a, b) => b.magnitude - a.magnitude).slice(0, 20);
  const centerIndex = samples.findIndex((entry) => Number(entry.offset) === 0);
  const sampleLumaAt = (offset) => {
    const idx = clampNumber(Math.round(offset + maxHalfWidthPx), 0, smooth.length - 1);
    return smooth[idx];
  };
  let best = null;
  leftEdges.forEach((left) => {
    rightEdges.forEach((right) => {
      const span = Math.abs(Number(right.rightOffset) - Number(left.leftOffset));
      if (!Number.isFinite(span) || span < minWidthPx || span > maxWidthPx) return;
      const leftIdx = clampNumber(Math.round(Number(left.leftOffset) + maxHalfWidthPx), 0, smooth.length - 1);
      const rightIdx = clampNumber(Math.round(Number(right.rightOffset) + maxHalfWidthPx), 0, smooth.length - 1);
      if (rightIdx <= leftIdx + 2) return;
      const interior = smooth.slice(leftIdx + 1, rightIdx);
      if (!interior.length) return;
      const interiorMean = interior.reduce((sum, value) => sum + Number(value || 0), 0) / interior.length;
      const edgeOuter = (sampleLumaAt(Number(left.leftOffset) - 3) + sampleLumaAt(Number(right.rightOffset) + 3)) / 2;
      const darknessContrast = edgeOuter - interiorMean;
      const centerBias = centerIndex >= 0
        ? 1 - (Math.abs(((leftIdx + rightIdx) / 2) - centerIndex) / Math.max(1, smooth.length * 0.5))
        : 0.5;
      const priorBias = Number.isFinite(priorPx) && priorPx > 0
        ? 1 - (Math.abs(span - priorPx) / Math.max(priorPx, 1))
        : 0.5;
      const score = (Number(left.magnitude || 0) + Number(right.magnitude || 0))
        + (Math.max(0, darknessContrast) * 1.8)
        + (Math.max(0, centerBias) * 4)
        + (Math.max(0, priorBias) * 3);
      if (!best || score > best.score) best = { spanPx: span, score };
    });
  });
  return best?.spanPx ? best : null;
}

function normalizeRecord(record) {
  const source = record && typeof record === "object" ? record : {};
  const rawMeasurementDate = String(source.measurement_date || "").trim();
  const measurementDate = rawMeasurementDate.includes("T")
    ? rawMeasurementDate.slice(0, 10)
    : rawMeasurementDate;
  const optText = (v) => (v == null ? "" : String(v));
  const optNum = (v) => (v == null || v === "" ? null : (Number.isFinite(Number(v)) ? Number(v) : null));
  return {
    measurement_id: String(source.measurement_id || ""),
    marina_id: String(source.marina_id || ""),
    marina_name: String(source.marina_name || ""),
    accepted_adjusted: optText(source.accepted_adjusted),
    dock_name: optText(source.dock_name),
    region: optText(source.region),
    adjusted_dock: optText(source.adjusted_dock),
    slip_id: String(source.slip_id || ""),
    suffix: optText(source.suffix),
    adjusted_slip_id: optText(source.adjusted_slip_id),
    multislip_yn: optText(source.multislip_yn),
    multislip_id: optText(source.multislip_id),
    adjusted_multislip_id: optText(source.adjusted_multislip_id),
    space_type: optText(source.space_type),
    adjusted_space_type: optText(source.adjusted_space_type),
    covered_yn: optText(source.covered_yn),
    adjusted_covered: optText(source.adjusted_covered),
    boatlift_yn: optText(source.boatlift_yn),
    fueldock_yn: optText(source.fueldock_yn),
    out_of_service: optText(source.out_of_service),
    out_of_service_reason: optText(source.out_of_service_reason),
    measured_linear_feet: source.measured_linear_feet == null || source.measured_linear_feet === "" ? null : roundToTenthFoot(source.measured_linear_feet),
    marketable_linear_feet: optNum(source.marketable_linear_feet) != null ? roundToTenthFoot(source.marketable_linear_feet) : null,
    measured_width_feet: source.measured_width_feet == null || source.measured_width_feet === "" ? null : roundToTenthFoot(source.measured_width_feet),
    marketable_width_feet: optNum(source.marketable_width_feet) != null ? roundToTenthFoot(source.marketable_width_feet) : null,
    marina_comments: optText(source.marina_comments),
    measured_depth_feet: source.measured_depth_feet == null || source.measured_depth_feet === "" ? null : roundToTenthFoot(source.measured_depth_feet),
    operational_status: optText(source.operational_status),
    notes: optText(source.notes),
    measurement_date: measurementDate,
    geometry_wkt: String(source.geometry_wkt || ""),
    confidence_score: String(source.confidence_score || "High"),
    record_created_timestamp: String(source.record_created_timestamp || ""),
  };
}

const DIMENSION_COLOR_CACHE = new Map();
const SPACE_TYPE_PALETTE = [
  "#ef4444", "#3b82f6", "#22c55e", "#06b6d4", "#a855f7",
  "#14b8a6", "#f97316", "#ec4899", "#84cc16", "#06b6d4",
  "#eab308", "#8b5cf6",
];
// Fixed, locked color assignments for known space types — adding new types never shifts these
const SPACE_TYPE_COLOR_MAP = {
  "Side Tie":    "#ef4444",
  "Pilings":     "#3b82f6",
  "Finger":      "#22c55e",
  "Med-Mooring": "#06b6d4",
  "Med Mooring": "#06b6d4",
  "Travel Lift": "#a855f7",
  "Boat Ramp":   "#14b8a6",
  "Dock":        "#f97316",
  "Other":       "#ec4899",
  "unknown":     "#84cc16",
};

function dimensionKey(lengthFt, widthFt, toleranceFt = 0.5) {
  const normalize = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const tol = [0.5, 1].includes(Number(toleranceFt)) ? Number(toleranceFt) : 0.5;
    // Group by configurable tolerance bins for color classes.
    return Math.round(n / tol) * tol;
  };
  const l = Number(lengthFt);
  const w = Number(widthFt);
  if (!Number.isFinite(l) || !Number.isFinite(w)) return "";
  const ln = normalize(l);
  const wn = normalize(w);
  if (!Number.isFinite(ln) || !Number.isFinite(wn)) return "";
  return `${ln.toFixed(1)}x${wn.toFixed(1)}`;
}

function roundToTenthFoot(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Number((Math.round(n * 10) / 10).toFixed(1));
}

function formatFeetTenth(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return n.toFixed(1);
}

function normalizeDisplayToleranceFt(value) {
  const n = Number(value);
  return [0.1, 0.25, 0.5, 1].includes(n) ? n : 0.1;
}

function roundFeetForDisplay(value, toleranceFt = 0.1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const tol = normalizeDisplayToleranceFt(toleranceFt);
  return Number((Math.round(n / tol) * tol).toFixed(2));
}

function formatFeetForDisplay(value, toleranceFt = 0.1) {
  const rounded = roundFeetForDisplay(value, toleranceFt);
  if (!Number.isFinite(rounded)) return "";
  const tol = normalizeDisplayToleranceFt(toleranceFt);
  return rounded.toFixed(tol === 0.25 ? 2 : 1);
}

function buildDisplayExportRecord(record, toleranceFt = 0.1) {
  const normalized = normalizeRecord(record);
  return {
    ...normalized,
    measured_linear_feet: normalized.measured_linear_feet == null ? null : roundFeetForDisplay(normalized.measured_linear_feet, toleranceFt),
    measured_width_feet: normalized.measured_width_feet == null ? null : roundFeetForDisplay(normalized.measured_width_feet, toleranceFt),
  };
}

function paletteColorForKey(key) {
  const cacheKey = String(key || "");
  if (!cacheKey) return "#38bdf8";
  if (DIMENSION_COLOR_CACHE.has(cacheKey)) return DIMENSION_COLOR_CACHE.get(cacheKey);
  let hash = 0;
  const text = cacheKey;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  const abs = Math.abs(hash);
  const hue = abs % 360;
  const sat = 68 + ((abs >> 8) % 18); // 68..85
  const light = 50 + ((abs >> 16) % 10); // 50..59
  const color = `hsl(${hue} ${sat}% ${light}%)`;
  DIMENSION_COLOR_CACHE.set(cacheKey, color);
  return color;
}

// paletteColorForKey hashes each string independently, which works fine for keys that vary
// throughout (dimension pairs, space types) but badly for a run of near-identical, sequentially
// numbered IDs like "MS-001"/"MS-002"/"MS-003" -- the hash is dominated by their shared prefix
// and barely perturbed by the last digit, so consecutive groups land within a degree or two of
// each other on the hue wheel and read as the same color. This instead spaces hues evenly by
// index using the golden angle (137.508deg), which keeps any two keys visually distinct
// regardless of how similar the strings themselves are -- the right choice whenever the full
// set of keys is known up front (as it is here: every distinct multislip_id for the marina).
function assignDistinctColorsByIndex(keys) {
  const map = new Map();
  keys.forEach((key, i) => {
    const hue = (i * 137.508) % 360;
    map.set(key, `hsl(${hue.toFixed(1)} 72% 56%)`);
  });
  return map;
}

const DOCK_X_COLOR = "hsl(0 72% 56%)"; // same style as the golden-angle sequence, hue pinned to red
const DOCK_RED_HUE_EXCLUSION_DEG = 18; // no other dock may land within this of 0/360

// Same golden-angle spread as assignDistinctColorsByIndex, except dock "X" is always pinned
// to red and no other dock is ever allowed a reddish hue either -- red on this map means "the
// X dock" and nothing else should read that way at a glance.
function assignDockColors(dockNames) {
  const map = new Map();
  let i = 0;
  for (const name of dockNames) {
    if (String(name).trim().toUpperCase() === "X") {
      map.set(name, DOCK_X_COLOR);
      continue;
    }
    let hue = (i * 137.508) % 360;
    while (Math.min(hue, 360 - hue) <= DOCK_RED_HUE_EXCLUSION_DEG) {
      i += 1;
      hue = (i * 137.508) % 360;
    }
    map.set(name, `hsl(${hue.toFixed(1)} 72% 56%)`);
    i += 1;
  }
  return map;
}

function confidenceColor(value) {
  const v = String(value || "").trim().toLowerCase();
  if (v === "high") return "#22c55e";
  if (v === "medium") return "#f59e0b";
  if (v === "low") return "#ef4444";
  const n = Number(value);
  if (Number.isFinite(n)) {
    if (n >= 0.8) return "#22c55e";
    if (n >= 0.5) return "#f59e0b";
    return "#ef4444";
  }
  return "#38bdf8";
}

function downloadText(filename, text, type = "text/plain") {
  const blob = new Blob([String(text || "")], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function sanitizeFilenamePart(value, fallback = "slip_inventory") {
  const text = String(value || "").trim().replace(/[^a-z0-9_-]+/gi, "_").replace(/^_+|_+$/g, "");
  return text || fallback;
}

function exportDateStamp() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
}

function currentDeliverableIds(app, code) {
  const project = app?.services?.context?.getAllProjects?.()?.find(
    (p) => String(p.code || "").toUpperCase() === String(code || "").toUpperCase()
  );
  return { projectNumber: project?.projectNumber || "", code };
}

function buildCsv(records, toleranceFt = 0.1) {
  const escapeCsv = (value) => {
    const text = value == null ? "" : String(value);
    return /[",\n]/.test(text) ? `"${text.replaceAll("\"", "\"\"")}"` : text;
  };
  const rows = records
    .map((record) => buildDisplayExportRecord(record, toleranceFt))
    .map((record) => CSV_EXPORT_COLUMNS.map((col) => escapeCsv(record[col.key])).join(","));
  return `${CSV_EXPORT_COLUMNS.map((col) => col.header).join(",")}\n${rows.join("\n")}`;
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

export function createSlipInventoryRuntime(app) {
  const TASK_SOURCE = "slip-inventory";
  function taskStatus(message, progress = null, running = true) {
    reportTaskStatus(app, TASK_SOURCE, {
      label: "Slip Inventory", message, progress,
      status: running ? "running" : "idle",
      projectCode: state.marinaId || null,
    });
  }
  function taskIdle() { taskStatus("", null, false); }
  // Generate Deliverables failures used to only land in the small status line at the bottom
  // of the panel -- easy to miss entirely once the picker dialog has already closed, so a
  // real failure (e.g. no acquisition date set) looked like the button just did nothing. A
  // modal puts the actionable message directly in front of the user instead.
  function reportGenerateDeliverablesFailure(err) {
    const message = String(err?.message || err || "Unknown error.");
    status = "Generate Deliverables failed: " + message;
    taskDone(status, false);
    render();
    app.ui.alertModal(message, { title: "Can't Generate Deliverables" });
  }
  // Distinct from taskIdle(): leaves a visible ok/error summary in the system tray for a
  // few seconds instead of silently reverting to "Idle" -- a plain taskIdle() after a long
  // multi-step run (e.g. Generate Deliverables) looked like nothing had happened.
  function taskDone(message, ok = true) {
    reportTaskStatus(app, TASK_SOURCE, {
      label: "Slip Inventory", message, progress: 100,
      status: ok ? "done" : "error",
      projectCode: state.marinaId || null,
    });
  }

  const map = app.map.instance;
  // Host for the Dock Builder tool (src/tools/dock-builder): it draws on this map, works on this
  // runtime's marina, reads the slips for "Fingers from slips", and takes turns with the slip draw
  // tools -- starting either kind stops the other.
  const slipToolStartListeners = new Set(), marinaChangeListeners = new Set();
  const dockHost = {
    map,
    getMarinaId: () => filterProjectId || state.marinaId || "",
    getSlipRings: () => records.filter((r) => isRecordVisible(r)).map((r) => parsePolygonWkt(r.geometry_wkt)).filter((ring) => Array.isArray(ring) && ring.length >= 3),
    stopSlipTools: () => {
      stopPointDrop("docks"); stopRotateAdjustMode(); lassoToolActive = false; disarmAllLassoToggles();
      stopLineLayout("replaced"); stopGeometryRunLayout("replaced"); stopGeometryPlaceMode(); stopMultislipMode();
      stopFullDockMode(); stopCombMode(); stopChainMode(); stopAutoDetectSlipsMode();
      if (containerEl) render();
    },
    // a slip draw tool is running: leave its clicks alone
    isSlipToolActive: () => pointDropActive || lineLayoutActive || !!activeGeometryPlaceMode || rotateAdjustActive || lassoActive || lassoToolActive
      || geometryRunLayoutActive || multislipActive || combActive || chainActive || autoDetectActive || autoRunLassoActive || !!autoRunSides || fullDockActive,
    onSlipToolStart: (cb) => { slipToolStartListeners.add(cb); return () => slipToolStartListeners.delete(cb); },
    onMarinaChange: (cb) => { marinaChangeListeners.add(cb); return () => marinaChangeListeners.delete(cb); },
    // the Dock Builder opened: pick up the active project and make sure the slips are loaded
    attach: () => {
      const activeProject = app.services?.context?.getActiveProject?.();
      if (activeProject?.marinaId) setProjectMarina(activeProject.marinaId);
      if (!initialFetchDone) {
        initialFetchDone = true;
        refreshRecords().catch((error) => { status = error?.message || "Slip inventory API is unavailable."; if (containerEl) render(); });
      }
    },
  };
  const slipIdPane = map.getPane("slip-id-badge-pane") || map.createPane("slip-id-badge-pane");
  slipIdPane.style.zIndex = "750";
  const root = L.featureGroup().addTo(map);
  // Shares the map's single default canvas renderer (preferCanvas: true in core/map.js)
  // instead of creating a private one. A private L.canvas() here creates its own <canvas>
  // DOM element, which — unlike SVG — intercepts pointer events across its whole bounding
  // box; whichever canvas-backed layer ends up later in the DOM (e.g. a tile-index layer
  // added by another tool) silently eats every click meant for the one underneath. See the
  // identical fix in core/services/layers.js for the full explanation.
  const canvasRenderer = map.getRenderer(L.polyline([], { pane: "overlayPane" }));
  const markersLayer = L.featureGroup().addTo(root);
  const slipIdMarkersLayer = L.featureGroup().addTo(root);
  const depthMarkersLayer = L.featureGroup().addTo(root);
  const previewLayer = L.featureGroup().addTo(root);
  const geometryLayer = L.featureGroup().addTo(root);
  const slipDimensionsLayer = L.featureGroup().addTo(root);
  // Scratch layer for the "Extend Edges" guide lines during a live handle drag -- kept separate
  // from slipDimensionsLayer (which only redraws on a full renderGeometry(), not mid-drag; see
  // liveUpdateRing/liveRedrawEdgeExtensions) so a drag-frame redraw never pays the cost of
  // re-rendering every slip's dimension/extension lines, just the one(s) being touched.
  const slipLiveExtensionLayer = L.featureGroup().addTo(root);
  const geometryHandlesLayer = L.featureGroup().addTo(root);
  const geometryPlacePreviewLayer = L.featureGroup().addTo(root);
  // Separate from geometryPlacePreviewLayer (which clears/rebuilds every render): a
  // draggable marker gets destroyed mid-gesture if its layer is cleared while Leaflet is
  // still tracking the drag, same reason Multislip/Comb keep their handles in their own
  // layer instead of the shared preview one.
  const geometryPlaceHandleLayer = L.featureGroup().addTo(root);
  // Uncommitted (locally drafted, no database record yet) polygons don't have a slip_id
  // badge -- those come from `records`, which this shape isn't part of until "Create Slip
  // Record" runs. Without this they sit on the map as a bare colored box once the ghost
  // preview layer clears on commit-to-draft; label them the same way the ghost did.
  const geometryDraftLabelLayer = L.featureGroup().addTo(root);
  const geometryRunPreviewLayer = L.featureGroup().addTo(root);
  const geometryRunHandleLayer = L.featureGroup().addTo(root);
  const multislipPreviewLayer = L.featureGroup().addTo(root);
  const multislipHandleLayer = L.featureGroup().addTo(root);
  const fullDockPreviewLayer = L.featureGroup().addTo(root);
  const combPreviewLayer = L.featureGroup().addTo(root);
  const combHandleLayer = L.featureGroup().addTo(root);
  const chainPreviewLayer = L.featureGroup().addTo(root);
  const autoDetectPreviewLayer = L.featureGroup().addTo(root);
  // Slip drafts: auto-detected slips awaiting review (GET /api/slip-drafts/:code). Kept apart from the
  // slip inventory -- only Accept turns one into a real slip, through the normal create path.
  const draftsLayer = L.featureGroup().addTo(root);
  const lassoLayer = L.featureGroup().addTo(root);
  const autoRunLayer = L.featureGroup().addTo(root);
  const autoRunHandleLayer = L.featureGroup().addTo(root);
  const geometryLayerCache = new Map(); // polyId → { path, interactive, renderKey, ringKey }
  let html2canvasLoader = null;
  let containerEl = null;
  let tablePanelEl = null;
  let tablePanelResizeObserver = null;

  // --- performance monitor (Alt+Shift+D to toggle) ---
  let _perfLastCaller = "—";
  let _perfLastPanelKey = "—";
  (() => {
    let visible = false;
    let el = null;
    let intervalId = null;
    let _prevRenderCount = 0;
    let _rendersPerSec = 0;
    const canvasLayers = () => Object.keys(canvasRenderer?._layers || {}).length;
    const featureLayers = (fg) => Object.keys(fg?._layers || {}).length;
    const mbStr = (bytes) => bytes ? (bytes / 1048576).toFixed(1) + "MB" : "?";
    const update = () => {
      if (!el) return;
      _rendersPerSec = _perfRenderCount - _prevRenderCount;
      _prevRenderCount = _perfRenderCount;
      const mem = performance.memory;
      const zoom = map?.getZoom?.()?.toFixed(1) ?? "?";
      const rpsColor = _rendersPerSec > 10 ? "#f87171" : _rendersPerSec > 2 ? "#fbbf24" : "#4ade80";
      el.innerHTML = [
        `<b style="color:#60a5fa">⚡ Perf (Alt+Shift+D)</b>`,
        `Heap: ${mbStr(mem?.usedJSHeapSize)} / ${mbStr(mem?.jsHeapSizeLimit)}`,
        `Records: ${records.length} | PolyState: ${state.slipGeometryPolygons.length}`,
        `GeoCache: ${geometryLayerCache.size} | CanvasLayers: ${canvasLayers()}`,
        `GeoLayer: ${featureLayers(geometryLayer)} | Markers: ${featureLayers(markersLayer)}`,
        `SlipBadges: ${featureLayers(slipIdMarkersLayer)} | RunPreview: ${featureLayers(geometryRunPreviewLayer)}`,
        `render() fired: ${_perfRenderCount} | panel rebuilt: ${_perfPanelRebuildCount}`,
        `<span style="color:${rpsColor}">renders/sec: ${_rendersPerSec}</span> | Zoom: ${zoom} | RecordsVer: ${recordsVersion}`,
        `Last caller: ${_perfLastCaller}`,
        `Last key: ${_perfLastPanelKey.slice(0, 80)}`,
      ].join("<br>");
    };
    const toggle = () => {
      visible = !visible;
      if (visible) {
        el = document.createElement("div");
        el.style.cssText = "position:fixed;top:60px;right:12px;z-index:99999;padding:8px 12px;background:rgba(2,6,23,0.94);border:1px solid rgba(56,189,248,0.35);border-radius:8px;font:11px/1.6 monospace;color:#e2e8f0;pointer-events:none;min-width:240px;";
        document.body.appendChild(el);
        update();
        intervalId = setInterval(update, 1000);
      } else {
        clearInterval(intervalId); intervalId = null;
        el?.remove(); el = null;
      }
    };
    document.addEventListener("keydown", (e) => {
      if (e.altKey && e.shiftKey && e.key === "D") toggle();
    });
  })();
  // --- end performance monitor ---

  let state = readState();
  if (!Array.isArray(state.slipGeometryTypes) || !state.slipGeometryTypes.length) {
    state.slipGeometryTypes = [{ id: makeGeometryId(), name: "Standard 40x14", lengthFt: 40, widthFt: 14 }];
    state.slipGeometryDraftTypeId = state.slipGeometryTypes[0].id;
    persistState(state);
  } else if (!state.slipGeometryDraftTypeId) {
    // readState() always resets this to "" on load (see merged.slipGeometryDraftTypeId
    // above); the "Active Type" selector that used to set it lives in a dead legacy
    // panel, so without this fallback Paste/Polygon-place silently no-op every reload.
    state.slipGeometryDraftTypeId = state.slipGeometryTypes[0].id;
    persistState(state);
  }
  let records = [];
  let recordsVersion = 0;
  // Ctrl+Z undo: a bounded stack of past mutations, each a full-record snapshot rather than a
  // diff -- undoing "update"/"delete" re-PATCHes/re-POSTs the previous full record (this app's
  // PATCH route already overwrites every column, never a partial patch, so that's exactly what
  // a normal edit does anyway); undoing "create" just DELETEs what was created. Session-only
  // (not persisted), and scoped to one marina at a time -- see pushUndoEntry.
  const UNDO_STACK_LIMIT = 10;
  let undoStack = []; // [{ label, changes: [{type,measurementId,before,after}], ts }], oldest first
  let undoInProgress = false;
  let undoStackMarinaId = ""; // marina the current stack was captured against

  function cloneRecord(r) { return r ? { ...r } : null; }
  function snapshotRecord(measurementId) {
    return cloneRecord(records.find((r) => String(r.measurement_id) === String(measurementId)));
  }
  // Called once per user-triggered action (a button press, a drag-end, a lasso pass) with
  // every record it touched -- one undo step covers the whole action, not each individual
  // record in a batch. Switching marinas invalidates the stack: `records` no longer contains
  // the touched rows, so there'd be nothing correct to undo against.
  function pushUndoEntry(label, changes) {
    if (!changes.length) return;
    if (undoStackMarinaId !== filterProjectId) { undoStack = []; undoStackMarinaId = filterProjectId; }
    undoStack.push({ label, changes, ts: Date.now() });
    if (undoStack.length > UNDO_STACK_LIMIT) undoStack.shift();
  }

  // Pops the most recent entry and reverses every change in it. Guards against clobbering a
  // concurrent edit from someone else (this app polls every 45s so multiple people can edit
  // the same marina, see startRemoteSync/REMOTE_SYNC_INTERVAL_MS): if a record's live state no
  // longer matches what we expected right after our own original action, something else has
  // touched it since -- skip just that one change (reported in the status line) rather than
  // silently overwrite a newer edit we don't know about; the rest of the batch still undoes.
  async function performUndo() {
    if (undoInProgress || !undoStack.length) return;
    if (undoStackMarinaId !== filterProjectId) { undoStack = []; status = "Nothing to undo."; render(); return; }
    undoInProgress = true;
    const entry = undoStack.pop();
    let appliedCount = 0;
    const skipped = [];
    try {
      for (const change of entry.changes) {
        try {
          if (change.type === "create") {
            const live = snapshotRecord(change.measurementId);
            if (!live) { continue; } // already gone -- nothing to do
            await requestJson(`${API_BASE}/${encodeURIComponent(change.measurementId)}`, { method: "DELETE" });
            appliedCount++;
          } else {
            const live = snapshotRecord(change.measurementId);
            const liveKey = live ? JSON.stringify(live) : null;
            const expectedKey = change.after ? JSON.stringify(change.after) : null;
            if (liveKey !== expectedKey) {
              skipped.push(change.measurementId);
              continue;
            }
            if (change.type === "update") {
              await requestJson(`${API_BASE}/${encodeURIComponent(change.measurementId)}`, { method: "PATCH", body: change.before });
            } else if (change.type === "delete") {
              await requestJson(`${API_BASE}`, { method: "POST", body: change.before });
            }
            appliedCount++;
          }
        } catch (err) {
          skipped.push(change.measurementId);
        }
      }
    } finally {
      undoInProgress = false;
    }
    await refreshRecords({ quiet: true }).catch(() => {});
    status = skipped.length
      ? `Undid "${entry.label}" (${skipped.length} slip${skipped.length > 1 ? "s" : ""} skipped -- changed since).`
      : appliedCount
        ? `Undid "${entry.label}".`
        : `Nothing to undo for "${entry.label}" -- already changed.`;
    render();
  }
  let tableSelectedMeasurementIds = new Set(); // tracks table multi-select directly, parallel to geometry selection
  let _perfRenderCount = 0;
  let _perfPanelRebuildCount = 0;
  let spaceTypeColorMap = new Map();
  let multislipColorMap = new Map();
  let regionColorMap = new Map();
  let marinaNameColorMap = new Map();
  let dockColorMap = new Map();
  // buildAttributeColoring() result, refreshed by renderGeometry() whenever "Color slips by:
  // Attribute" is active.
  let attributeColoring = null;
  function currentAttributeColoring() {
    if (!attributeColoring || attributeColoring.records !== records || attributeColoring.version !== recordsVersion) {
      attributeColoring = { ...buildAttributeColoring(records), records, version: recordsVersion };
    }
    return attributeColoring;
  }
  // measurement_id -> "orphan" | "disconnected" | "valid", populated by renderGeometry()
  // whenever "Color slips by: Multislip Integrity" is active -- see computeMultislipIntegrityStatus.
  let multislipIntegrityStatusMap = new Map();
  // measurement_id -> true, populated by renderGeometry() whenever "Color slips by:
  // Duplicate/Overlap" is active -- see computeOverlappingSlipIds.
  let overlappingSlipIds = new Set();
  // measurement_id -> true, populated by renderGeometry() whenever "Color slips by:
  // Duplicate Slip ID" is active -- see computeDuplicateSlipIdSet.
  let duplicateSlipIdSet = new Set();
  let initialFetchDone = false; // mount() fires on every renderSidebar(); only fetch once
  let remoteSyncTimer = null;
  let remoteSyncInFlight = false;
  let lastLocalSaveAt = 0;
  // Guards a periodic remote sync from rebuilding slipGeometryPolygons out from under an
  // in-progress committed-slip drag (see the ID-badge marker's drag/dragend handlers below) —
  // a sync landing mid-drag would rebuild geometry from the server's still-pre-drag records,
  // desyncing the polygon from the marker the user is actively moving.
  let activeSlipDragCount = 0;
  let remoteSyncFocusHandler = null;
  let remoteSyncVisibilityHandler = null;
  let filterProjectId = "";
  let showNoMarinaCode = false;
  let status = "Slip inventory is ready.";
  let imageryEstimateStatus = "";
  let imageryEstimateDebug = null;
  let geometryImageryEstimateStatus = "";
  let geometryImageryEstimateDebug = null;
  let pointDropActive = false;
  let pointDropOff = null;
  let lineLayoutActive = false;
  let lineLayoutOff = null;
  let marinaCatalog = [];
  let lineEndpoints = [];
  let lineLayoutMode = "endpoint";
  let lineHoverPoint = null;
  let lineAnchorPoint = null;
  let zoomHandler = null;
  let moveHandler = null;
  let mapSelectClickHandler = null;
  let activeGeometryPlaceMode = false;
  let geometryPlaceOff = null;
  // Single-polygon placement commits the instant the second dimension is clicked (no
  // multi-step review/Enter gate like Multislip/Comb/Chain have) -- that's deliberate, it's
  // meant to be the fast one-shot tool. But it left Escape with nothing to catch right after:
  // the other tools can still Esc-cancel at the equivalent point, this one couldn't undo at
  // all. This id, when set, is checked by globalRunHotkeyHandler's own Escape branch (not a
  // separately-registered listener -- that handler is on window/capture and always runs
  // first anyway, so a second listener could never win the race or see the selection it
  // hadn't already cleared).
  let postPlaceUndoPolyId = null;
  // On-demand rotation, activated by pressing "r" on a selected polygon (freshly placed or
  // already committed) instead of a mandatory third click in the draw sequence.
  let rotateAdjustActive = false;
  let rotateAdjustOff = null;
  // AutoCAD-style lasso select: hold Ctrl (or Cmd), freehand-drag across the map, any slip the
  // path touches gets added to the selection -- plus, if the drag closes a loop (ends back near
  // its start or crosses over itself), every slip fully inside that loop. Same set is what the
  // "Include in Lasso" edits apply to (see lassoClosedLoops). Always armed (not a toggled "mode" like the draw
  // tools below) -- gated per-event on Ctrl being held and no other draw mode being active, so
  // it never fights multislip/comb/chain/etc. for the same pointer gesture.
  let lassoCtrlHeld = false;
  let lassoActive = false;
  let lassoPoints = [];
  let lassoPointerDownXY = null;
  let lassoPointerId = null;
  let lassoPreviewRafPending = false;
  let lassoPointerDownHandler = null;
  let lassoPointerMoveHandler = null;
  let lassoPointerUpHandler = null;
  let lassoCtrlSyncHandler = null;
  // Point-and-shoot: a Ctrl/Cmd-click with no drag, while either lasso action is armed,
  // updates just whatever single slip is under the cursor instead of falling through to
  // plain ctrl+click toggle-select. Set right before firing that single-slip update so the
  // capture-phase click listener below can swallow the native click Leaflet's marker/polygon
  // would otherwise also receive (a real drag never has this problem -- browsers don't fire
  // a click after a drag -- but a stationary click always does).
  let suppressNextLassoTapClick = false;
  let lassoTapClickSuppressHandler = null;
  // Single master switch for the lasso-editing UI: reveals the per-field "Include in Lasso"
  // toggles below (each disabled/greyed out until this is on) and, turned off, disarms all of
  // them at once so nothing can silently fire on the next Ctrl/Cmd-drag. Purely a
  // UI-organization flag -- Ctrl/Cmd-drag select mode itself is unconditional and unaffected
  // by this (see isSelectMode below).
  let lassoToolActive = false;
  // Nine fully independent "Include in Lasso" toggles -- any combination can be armed
  // together, applied as one combined PATCH per touched slip in a single drag (see
  // applyLassoPathActions/buildAttrLassoFields). Off by default and never persisted (state,
  // not localStorage) so none can silently stay armed into a future session. Deliberately not
  // in anyOtherDrawModeActive()'s list so they can combine freely with each other.
  //
  // Dock and Slip Number are independent -- a user can rename just the dock (keep each
  // touched slip's own number) or just renumber (keep each slip's own dock), or both. Auto
  // Increment/ID Step only matter when lassoIncludeSlipNumber is on.
  let lassoIncludeDock = false;
  let lassoIncludeSlipNumber = false;
  let lassoIncludeSuffix = false;
  // Region isn't part of the Slip ID text -- the fix for multi-yard marinas where two
  // different yards legitimately reuse the same slip numbering (see
  // conflictingSlipIdsMessage, which scopes its collision check by region for this reason).
  let lassoIncludeRegion = false;
  // Slip Type, Multislip, Covered, Boatlift, Fuel Dock, and Out of Service are all independent -- e.g.
  // bulk-tagging a run of slips as covered shouldn't force-touch their existing (possibly
  // mixed) Slip Type or Multislip membership.
  let lassoIncludeSlipType = false;
  let lassoIncludeCovered = false;
  let lassoIncludeBoatlift = false;
  let lassoIncludeFuelDock = false;
  let lassoIncludeOutOfService = false;
  let lassoIncludeMultislip = false;
  // Swaps each touched slip's OWN measured_linear_feet/measured_width_feet (mirrors the
  // single-selection "Swap L/W" table button) -- fixes slips drawn with length/width
  // backwards, e.g. flagged yellow by the Orientation QC color mode. Per-slip, not a stamped
  // shared value, so it's applied directly in applyLassoPathActions rather than through
  // buildAttrLassoFields.
  let lassoIncludeSwapLw = false;
  let geometryPlaceHoverPoint = null;
  let geometryPlaceDraft = null;
  // Click-drawn placement sub-state: null (legacy single-click paste path) |
  // "width" | "length" | "rotate". See startGeometryPlaceMode.
  let geometryDrawStage = null;
  let geometryDrawA = null; // click 1 -- corner anchor, fixed pivot for the rotate stage
  let geometryDrawB = null; // click 2 -- sets the width edge A->B
  // Signed feet (from msPerpDistFt) -- which side of A-B the length was drawn
  // toward. Kept separately from geometryPlaceDraft.lengthFt (always a
  // positive magnitude, used for rendering/storage) so the centroid lands on
  // the correct side without ever flipping headingDeg.
  let geometryDrawSignedLengthFt = 0;
  // Guards geometryPlaceHandleLayer against being rebuilt mid-drag (see its declaration).
  let geometryPlaceHandleDragging = false;
  // Which dimension the first click-pair measures in the single-polygon and Multislip
  // draw sequences: false = width-first (red crosshair, original behavior), true =
  // length-first (green crosshair). Toggled live with Spacebar during that first stage
  // only; persists across placements within the session (not saved to localStorage) so
  // repeated placements in the same orientation don't require re-toggling each time.
  let firstDimensionIsLength = false;
  function applyFirstDimensionCursor(container) {
    if (!container) return;
    container.classList.remove("slip-dim-cursor-length", "slip-dim-cursor-width");
    container.classList.add(firstDimensionIsLength ? "slip-dim-cursor-length" : "slip-dim-cursor-width");
  }
  function clearFirstDimensionCursor(container) {
    container?.classList?.remove?.("slip-dim-cursor-length", "slip-dim-cursor-width");
  }
  let geometryRunLayoutActive = false;
  let geometryRunLayoutOff = null;
  let geometryRunEndpoints = [];
  let geometryRunHoverPoint = null;
  let multislipActive = false;
  let multislipMode = "length"; // "length" = dock-run first (default), "width" = per-slip width first
  let multislipP1 = null;
  let multislipP2 = null;
  let multislipWidthFt = 0;   // signed (positive=left, negative=right of dock line)
  let multislipPartitions = 1;
  // multislipSlotLengths[bayIdx][subIdx] = signed offshore length (ft) for that sub-slip --
  // same nested shape as Comb's combSlotLengths. With multislipSlotsPerBay===1 (the default)
  // each bay has exactly one sub-slip, unchanged from before this existed.
  let multislipSlotLengths = [];
  // Sub-slips per bay (1/2/3, cycled by T): splits each finger-bounded bay into this many
  // directly-adjacent slips with no gap between them, while the finger-width gap still only
  // applies at bay boundaries -- e.g. 2 gives paired/"double" slips per finger, matching
  // Comb's combSlotsPerBay concept but reusing Fingers' own finger-center/finger-width flow
  // instead of Comb's manually-clicked bay edges.
  let multislipSlotsPerBay = 1;
  // Tilt (T), same model as Comb's combShearDeg/combFreeform and the Polygon tool's
  // parallelogram mode: the dock-side edge stays put and the offshore edge shifts sideways,
  // so every slip becomes a parallelogram whose tilted side keeps the slip's own length.
  // While setting length/width (Phase 3), T toggles freeform -- the mouse sets tilt and
  // extrusion in one motion. Once slips exist (Phase 4), T toggles 0 <-> 10 deg and [ / ]
  // fine-tune it (Shift = 5 deg). Sub-slips per bay moved to Shift+T to make room.
  let multislipShearDeg = 0;
  let multislipFreeform = false;
  // N+1 along-dock finger-CENTER fractions (0..1; index 0 and N are the run's own
  // start/end, not fingers), even by default; dragging an orange dock-line handle
  // repositions one interior center to match the actual finger in the imagery. Reset to
  // even whenever width (re)locks or the partition count (W/X) changes -- edits aren't
  // preserved across a count change, see msEvenFracs.
  let multislipBoundaryFracs = [];
  // Two-step finish once N>1: place finger centers (multislipBoundaryFracs, above) then
  // Enter locks them and switches to dragging one shared finger-width handle
  // (multislipFingerWidthFt, mirrored onto every finger via msSlotFracsFromCenters) --
  // a second Enter commits. Esc while locked goes back to center-placement instead of
  // cancelling the whole tool. N===1 has no fingers and skips straight to commit.
  let multislipCentersLocked = false;
  let multislipFingerWidthFt = 0;
  let multislipDragging = false; // true while a handle drag is in progress
  // renderMultislipPreview() runs on every mousemove anywhere over the map (not just while
  // dragging a handle -- see onPtrMove's rAF loop), but handle markers themselves only need
  // rebuilding when something structural changed (partition count, lock/unlock, a drag just
  // finished). Without this flag the whole handle layer was being torn down and recreated on
  // every hover frame, racing mousedown against marker teardown and making the handles feel
  // unresponsive/flaky to grab. Starts true so Phase 4's first render builds them.
  let multislipHandlesDirty = true;
  // "Fingers" is the same click/W/X/finger-center/finger-width flow as Multislip, entered
  // via a separate button/state so the two stay unambiguous the way the old Multi-Slip
  // checkbox wasn't (see the isMultiCommit comment in commitMultislip): Multislip always
  // groups its N slips under one shared multislip_id; Fingers never does -- it's just an
  // efficient way to draw a row of ordinary, independent slips.
  let multislipAsFingers = false;
  let multislipHoverPoint = null;
  let multislipRotating = false;  // true after step-3 click until rotation is locked
  let multislipP2Base = null;     // original P2 before rotation
  let multislipP3 = null;         // step-3 click position (rotation angle reference)
  // Free-rotate-while-sizing, triggered by "r" between the P2 click and the width click:
  // lets the dock-run edge itself pivot around P1 while width is decided in the same
  // mouse motion, instead of locking P1-P2 and only ever measuring width perpendicular
  // to it. multislipP2AtLock freezes the as-clicked P2 so width keeps a stable baseline
  // to measure against while multislipP2 itself is live-rotated for rendering/commit.
  let multislipPreWidthRotating = false;
  let multislipP2AtLock = null;
  let multislipOff = null;
  // Comb tool: click bay-edge pairs along a dock (skipping fingers), then one
  // perpendicular click sets the length for every bay at once. Each bay is
  // split into combSlotsPerBay slips (default 2 = "double slip").
  let combActive = false;
  let combPhase = "bays"; // "bays" → "length" → "adjust"
  let combBays = []; // [{p1,p2}, ...] each pair = one bay's width along the dock
  let combPendingP1 = null; // first click of an in-progress bay, awaiting its second click
  let combWidthFt = 0; // 0 until the length-set click locks it in
  let combSlotLengths = []; // combSlotLengths[bayIdx][slotIdx] = signed offshore length (ft)
  let combSlotsPerBay = 2;
  // Shared across every bay/slot in this Comb pass. During the length phase, "t" toggles
  // combFreeform -- while on, both combShearDeg and the length being placed are derived
  // live from the raw mouse position (msDeriveShearedLength) instead of a plain
  // perpendicular click, so angle and length are set together in one motion. In the
  // adjust phase (after length is locked), [ and ] fine-tune combShearDeg by hand instead.
  // Tilts each slot's offshore edge instead of extruding straight perpendicular from the
  // dock line, for a fanning dock where a whole row needs the same consistent angle.
  let combShearDeg = 0;
  let combFreeform = false;
  let combDragging = false;
  let combHoverPoint = null;
  let combOff = null;
  // Chain tool: click a bent polyline along a dock edge, then one perpendicular
  // click sets a single global width for every segment. Each segment becomes
  // its own slip (length = that segment's click-to-click distance); unlike
  // Comb, the whole chain shares ONE multislip_id since it's one continuous run.
  let chainActive = false;
  let chainPhase = "points"; // "points" → "width"
  let chainPoints = [];
  let chainHoverPoint = null;
  let chainWidthFt = 0; // 0 until the width-set click locks it in
  let chainOff = null;
  // Auto-Detect: click two points for one cross-section line through a run of fingers, and
  // instead of clicking each bay edge by hand (Comb's "bays" phase), the fingers are found
  // automatically from the marina's surface raster's elevation profile along that line, then
  // handed straight to Comb's existing "length"/"adjust" phases for human review. See
  // detectSlipGapsFromProfile for the actual detection algorithm.
  let autoDetectActive = false;
  let draftsRun = null, draftsReview = {}, draftsVisible = false, draftsLoading = false, draftsEnabled = true, draftsError = "";
  let draftsStatusCache = { key: "", byId: new Map() };
  // On-demand runs: the marina's latest Create Drafts request + when the devbox worker last polled.
  let draftsRequest = null, draftsWorkerSeenAt = null, draftsPollTimer = null, draftsRequesting = false;
  // Auto Run (lasso a pier -> editable two-sided finger run); see the "Auto Run" section
  let autoRunLassoActive = false, autoRunLassoPts = [], autoRunLassoOff = null;
  let autoRunSides = null, autoRunActiveSide = 0, autoRunDragging = false, autoRunHandlesDirty = true, autoRunKeyOff = null;
  let autoDetectP1 = null;
  let autoDetectHoverPoint = null;
  let autoDetectBusy = false;
  let autoDetectOff = null;
  // Full Dock: a 6-click setup (centerline P1/P2, then outer "end of fingers" and inner
  // "start of slips" perpendicular distances for each side) that derives two independent
  // (p1, p2, widthFt) rectangles -- one per side of the centerline -- and hands each off in
  // turn to the existing Multislip/Fingers engine via startMultislipMode's preset* options,
  // rather than reimplementing the count/center/width flow a second time. Side 1 fully
  // completes (including commit) before side 2's setup begins, chained through
  // multislipOnDone. See startFullDockMode.
  let fullDockActive = false;
  let fullDockStage = null; // "p1" | "p2" | "outerA" | "outerB" | "innerA" | "innerB"
  let fullDockP1 = null;
  let fullDockP2 = null;
  let fullDockOuterA = null; // signed ft (msPerpDistFt convention) -- side A's fingers-end distance
  let fullDockOuterB = null; // signed ft, opposite side
  let fullDockInnerA = null; // signed ft -- side A's start-of-slips distance
  let fullDockInnerB = null; // signed ft, opposite side
  let fullDockHoverPoint = null;
  let fullDockOff = null;
  // Set by startMultislipMode's onDone option, captured fresh by each commitMultislip() call
  // before stopMultislipMode() would otherwise clear it -- lets Full Dock chain side 2's
  // setup after side 1 successfully commits, without commitMultislip needing to know
  // anything about Full Dock itself.
  let multislipOnDone = null;
  // Prefixes multislipToolLabel()'s output (e.g. "Fingers (side 1 of 2)") so every status
  // message already built around that helper reflects which side of a Full Dock is active
  // for free, with no changes needed at each individual call site.
  let multislipSideLabel = "";
  let globalRunHotkeyHandler = null;
  let globalWheelAdjustKeydownHandler = null;
  let globalWheelAdjustKeyupHandler = null;
  let globalWheelAdjustHandler = null;
  const wheelAdjustHeldSlots = new Set();
  let wheelAdjustSpaceHeld = false;
  let wheelAdjustBacktickHeld = false;
  let suppressMapClickUntil = 0;
  let bottomTableScrollTop = 0;
  let bottomTablePointerInside = false;
  let bottomTableScrollActiveUntil = 0;
  let _scrollTableToSelectionPending = false;
  let _selectModeHandler = null;
  let _filterPanelCloseHandler = null;
  let _filterPanelShouldBeOpen = false;
  let copiedSlipGeometryTemplate = null;
  let slipHandleDragging = false;
  let dimensionLegendControl = null;
  let draftCovered = false;
  let draftBoatlift = false;
  let draftFuelDock = false;
  // Attributes-by-Lasso only -- never read by any slip-creation path (Polygon/Comb/Multislip/
  // Chain always decide multislip membership on their own; see buildAttrLassoFields). On: every
  // slip the lasso touches is stamped with one new shared multislip_id (generated once per
  // lasso pass, same DEFAULT_MULTISLIP_ID/incrementMultislipId counter Multislip/Chain use).
  // Off: every touched slip has multislip_yn/multislip_id cleared, regardless of prior state.
  let draftMultislip = false;
  // Out of Service is optional on the record (blank or yes/no), and out_of_service_reason must
  // stay blank unless out_of_service is set -- so the reason is only ever written alongside a
  // "yes", and new slips leave both blank unless this is checked.
  let draftOutOfService = false;
  let draftOosReason = "";
  function draftOutOfServiceFields() {
    return draftOutOfService ? { out_of_service: "yes", out_of_service_reason: draftOosReason || null } : {};
  }
  let draftComments = "";

  function clearDimensionLegend() {
    if (!dimensionLegendControl) return;
    try { map.removeControl(dimensionLegendControl); } catch {}
    dimensionLegendControl = null;
  }

  const LEGEND_TITLE_BY_MODE = {
    dimension: "Dimension Legend",
    space_type: "Slip Type Legend",
    multislip: "Multislip Legend",
    orientation_qc: "Orientation QC Legend",
    region: "Region Legend",
    dock: "Dock Legend",
    multislip_integrity: "Multislip Integrity Legend",
    duplicate_slips: "Duplicate/Overlap Legend",
    attribute: "Attribute Legend",
  };
  function renderDimensionLegend(entries = []) {
    clearDimensionLegend();
    const mode = String(state.colorSlipsBy || "none");
    if (!(mode in LEGEND_TITLE_BY_MODE)) return;
    if (state.showDimensionLegend === false) return;
    const items = Array.isArray(entries) ? entries : [];
    if (!items.length) return;
    const control = L.control({ position: "bottomleft" });
    control.onAdd = () => {
      const div = L.DomUtil.create("div", "slip-dimension-legend leaflet-bar");
      div.style.background = "rgba(2,6,23,0.88)";
      div.style.border = "1px solid rgba(56,189,248,0.38)";
      div.style.borderRadius = "10px";
      div.style.padding = "8px 10px";
      div.style.minWidth = "180px";
      div.style.maxHeight = "220px";
      div.style.overflow = "auto";
      div.style.color = "#e2e8f0";
      div.style.font = "600 11px/1.3 system-ui, sans-serif";
      const rows = items.map((entry) => `
        <div style="display:flex; align-items:center; gap:8px; margin:4px 0;">
          <span style="display:inline-block; width:12px; height:12px; border-radius:3px; background:${esc(entry.color)}; border:1px solid rgba(255,255,255,.35);"></span>
          <span>${esc(entry.label)}</span>
        </div>
      `).join("");
      div.innerHTML = `<div style="font-weight:700; margin-bottom:6px; color:#bae6fd;">${esc(LEGEND_TITLE_BY_MODE[mode] || "Legend")}</div>${rows}`;
      L.DomEvent.disableClickPropagation(div);
      L.DomEvent.disableScrollPropagation(div);
      return div;
    };
    control.addTo(map);
    dimensionLegendControl = control;
  }

  let _persistTimer = null;
  function persist() {
    clearTimeout(_persistTimer);
    _persistTimer = setTimeout(() => persistState(state), 300);
  }
  function persistNow() {
    clearTimeout(_persistTimer);
    _persistTimer = null;
    persistState(state);
  }

  function lengthWidthToleranceStep() {
    const raw = Number(state?.lengthWidthToleranceFt);
    return [0.1, 0.25, 0.5, 1].includes(raw) ? raw : 0.5;
  }

  function rotationToleranceStep() {
    const raw = Number(state?.rotationToleranceDeg);
    return [0.1, 0.5, 1].includes(raw) ? raw : 0.5;
  }

  function snapLengthWidth(value) {
    return roundToStep(value, lengthWidthToleranceStep(), 0.1);
  }

  function nudgeSelectedGeometry(directionKey) {
    const selected = selectedGeometryPolygon();
    if (!selected) return false;
    const stepFeet = lengthWidthToleranceStep();
    const stepMeters = feetToMeters(stepFeet);
    const key = String(directionKey || "").toLowerCase();
    let dx = 0;
    let dy = 0;
    if (key === "arrowup") dy = stepMeters;
    else if (key === "arrowdown") dy = -stepMeters;
    else if (key === "arrowleft") dx = -stepMeters;
    else if (key === "arrowright") dx = stepMeters;
    else return false;

    const center = selected?.center || { lat: 0, lng: 0 };
    const nextCenter = offsetPoint(center, dx, dy);
    updateSelectedGeometry({ center: nextCenter });
    status = `Nudged ${stepFeet} ft (${key.replace("arrow", "")}).`;
    render();
    return true;
  }

  function suppressMapClicks(ms = 400) {
    suppressMapClickUntil = Date.now() + Math.max(50, Number(ms) || 400);
  }

  function normalizeMarinaCatalog(items = []) {
    const seen = new Set();
    return (Array.isArray(items) ? items : [])
      .map((item) => {
        const marinaId = String(item?.portfolio?.code || item?.name || "").trim();
        const marinaName = String(item?.name || item?.portfolio?.name || "").trim();
        const lat = Number(item?.lat);
        const lng = Number(item?.lng);
        if (!marinaId || !marinaName || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        const key = marinaId.toLowerCase();
        if (seen.has(key)) return null;
        seen.add(key);
        return { marinaId, marinaName, lat, lng };
      })
      .filter(Boolean)
      .sort((a, b) => a.marinaId.localeCompare(b.marinaId));
  }

  function marinaOptionForId(marinaId, fallbackName = "") {
    const text = String(marinaId || "").trim();
    if (!text) return null;
    const existing = marinaCatalog.find((entry) => entry.marinaId === text);
    if (existing) return existing;
    return {
      marinaId: text,
      marinaName: String(fallbackName || text),
      lat: null,
      lng: null,
    };
  }

  function setMarinaDefaultsFromId(marinaId) {
    // No fallback to the still-stale state.marinaName here: this runs on every marina
    // switch, and falling back to the name of the marina being switched *away* from is what
    // let a marina not yet in marinaCatalog silently inherit the *previous* marina's name
    // into every record drawn afterward (see marina_name DB audit, 2026-08-26).
    //
    // marinaCatalog is never actually populated, though -- setMarinaItems (its only writer)
    // has no caller anywhere in the app -- so marinaOptionForId always fell through to its
    // own last resort, the raw marina *code*, and that's what got written to marina_name on
    // every slip drawn right after a marina switch (this is the flakiness: editing an
    // existing record correctly inherits its real marina_name via selectedRecord() below, so
    // it only broke right after switching marinas). Resolve the real name from the project
    // registry instead -- the same source exportPlotSvg's PDF title already trusts.
    const marina = marinaOptionForId(marinaId);
    const project = app?.services?.context?.getAllProjects?.()?.find(
      (p) => String(p.code || "").toUpperCase() === String(marinaId || "").toUpperCase()
    );
    state.marinaId = marina?.marinaId || "";
    state.marinaName = project?.name || marina?.marinaName || "";
    persist();
    if (draftsVisible) loadDrafts({ quiet: true });   // drafts are per marina
    if (autoRunBusy()) stopAutoRun("Auto Run cancelled: the marina changed.");
  }

  function selectedRecord() {
    return records.find((record) => record.measurement_id === state.selectedMeasurementId) || null;
  }

  function selectedIndex() {
    const index = records.findIndex((record) => record.measurement_id === state.selectedMeasurementId);
    return index >= 0 ? index : records.length;
  }

  function fitSelection() {
    const current = selectedRecord();
    const point = pointFromAnyWkt(current?.geometry_wkt);
    if (!point) return;
    map.setView([point.lat, point.lng], Math.max(18, Number(map.getZoom?.() || 18)));
  }

  function fitMarinaById(marinaId, fallbackName = "") {
    const marina = marinaOptionForId(marinaId, fallbackName);
    if (!marina || !Number.isFinite(marina.lat) || !Number.isFinite(marina.lng)) return false;
    map.setView([marina.lat, marina.lng], Math.max(16, Number(map.getZoom?.() || 16)));
    return true;
  }

  function interpolateLatLng(start, end, ratio) {
    return {
      lat: Number(start.lat) + ((Number(end.lat) - Number(start.lat)) * ratio),
      lng: Number(start.lng) + ((Number(end.lng) - Number(start.lng)) * ratio),
    };
  }

  function spacingRunPreviewPoints() {
    if (lineEndpoints.length < 2) return [];
    const first = lineEndpoints[0];
    const second = lineEndpoints[1];
    const target = lineEndpoints[2] || lineHoverPoint || second;
    const startPoint = map.latLngToLayerPoint([first.lat, first.lng]);
    const spacingPoint = map.latLngToLayerPoint([second.lat, second.lng]);
    const targetPoint = map.latLngToLayerPoint([target.lat, target.lng]);
    const vx = Number(spacingPoint.x) - Number(startPoint.x);
    const vy = Number(spacingPoint.y) - Number(startPoint.y);
    const spacingPx = Math.hypot(vx, vy);
    if (!Number.isFinite(spacingPx) || spacingPx <= 0) return [first, second];
    const ux = vx / spacingPx;
    const uy = vy / spacingPx;
    const tx = Number(targetPoint.x) - Number(startPoint.x);
    const ty = Number(targetPoint.y) - Number(startPoint.y);
    const projectedLength = Math.max(spacingPx, (tx * ux) + (ty * uy));
    const count = Math.max(1, Math.floor((projectedLength / spacingPx) + 0.35) + 1);
    return Array.from({ length: count }, (_, index) => {
      const point = L.point(startPoint.x + (ux * spacingPx * index), startPoint.y + (uy * spacingPx * index));
      const latlng = map.layerPointToLatLng(point);
      return { lat: Number(latlng.lat), lng: Number(latlng.lng) };
    });
  }

  function resolvedLineSlipCount() {
    return Math.max(1, Number(state.lineSlipCount || 0) || 1);
  }

  function linePreviewRatios() {
    const count = resolvedLineSlipCount();
    if (count <= 1) return [0.5];
    return Array.from({ length: count }, (_, index) => index / Math.max(1, count - 1));
  }

  function linePreviewPoints() {
    if (lineLayoutMode === "spacing") return spacingRunPreviewPoints();
    if (lineEndpoints.length < 2) return [];
    const [start, end] = lineEndpoints;
    const count = resolvedLineSlipCount();
    const anchor = lineAnchorPoint && Number.isFinite(Number(lineAnchorPoint.index))
      ? {
          index: Math.max(1, Math.min(Math.max(1, count - 2), Number(lineAnchorPoint.index))),
          lat: Number(lineAnchorPoint.lat),
          lng: Number(lineAnchorPoint.lng),
        }
      : null;
    if (anchor && count >= 3 && Number.isFinite(anchor.lat) && Number.isFinite(anchor.lng)) {
      return Array.from({ length: count }, (_, index) => {
        if (index <= anchor.index) {
          const leftRatio = anchor.index <= 0 ? 0 : (index / Math.max(1, anchor.index));
          return interpolateLatLng(start, { lat: anchor.lat, lng: anchor.lng }, leftRatio);
        }
        const rightSteps = Math.max(1, (count - 1 - anchor.index));
        const rightRatio = (index - anchor.index) / rightSteps;
        return interpolateLatLng({ lat: anchor.lat, lng: anchor.lng }, end, rightRatio);
      });
    }
    return linePreviewRatios().map((ratio) => interpolateLatLng(start, end, ratio));
  }

  function linePreviewSegments() {
    if (lineEndpoints.length < 2) return [];
    const start = lineEndpoints[0];
    const end = lineEndpoints[lineEndpoints.length - 1];
    const count = Math.max(1, linePreviewPoints().length);
    return Array.from({ length: count }, (_, index) => {
      const t0 = index / count;
      const t1 = (index + 1) / count;
      const tm = (t0 + t1) / 2;
      return {
        start: interpolateLatLng(start, end, t0),
        end: interpolateLatLng(start, end, t1),
        center: interpolateLatLng(start, end, tm),
      };
    });
  }

  function projectRatiosOnSegment(points, start, end) {
    const startPt = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPt = map.latLngToContainerPoint([end.lat, end.lng]);
    const vx = Number(endPt.x) - Number(startPt.x);
    const vy = Number(endPt.y) - Number(startPt.y);
    const len2 = (vx * vx) + (vy * vy);
    if (!Number.isFinite(len2) || len2 <= 1e-6) return [];
    return (Array.isArray(points) ? points : []).map((point) => {
      const p = map.latLngToContainerPoint([point.lat, point.lng]);
      const tx = Number(p.x) - Number(startPt.x);
      const ty = Number(p.y) - Number(startPt.y);
      const ratio = ((tx * vx) + (ty * vy)) / len2;
      return clampNumber(ratio, 0, 1);
    });
  }

  async function loadHtml2Canvas() {
    if (!html2canvasLoader) {
      html2canvasLoader = import("html2canvas").then((module) => module.default || module);
    }
    return html2canvasLoader;
  }

  function hasVisibleRasterImagery() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer) return false;
    return !!mapContainer.querySelector(".leaflet-tile-pane img, .leaflet-tile-pane canvas, .leaflet-overlay-pane img, .leaflet-overlay-pane canvas");
  }

  function isGeoTiffLikeLayer(layer) {
    if (!layer || typeof layer !== "object") return false;
    if (String(layer.type || "") === "geotiff-url") return true;
    const meta = layer.meta && typeof layer.meta === "object" ? layer.meta : {};
    if (String(meta.sourceType || "") === "raster-tiles") return true;
    if (String(meta.rasterJobId || "").trim()) return true;
    return false;
  }

  async function waitForRenderFrame() {
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  async function captureGeoTiffImageryCanvas() {
    const layersService = app?.services?.layers;
    if (!layersService?.listLayers) return captureVisibleImageryCanvas();
    const layers = layersService.listLayers();
    const visibleGeoTiffs = layers.filter((layer) => layer?.visible !== false && isGeoTiffLikeLayer(layer));
    if (!visibleGeoTiffs.length) return null;
    const nonGeoVisible = layers.filter((layer) => layer?.visible !== false && !isGeoTiffLikeLayer(layer));
    const muted = [];
    let rootDetached = false;
    const hiddenToolLayers = [];
    try {
      try {
        if (map?.hasLayer?.(root)) {
          map.removeLayer(root);
          rootDetached = true;
        }
      } catch {}
      const localOverlayGroups = [
        previewLayer,
        geometryLayer,
        geometryHandlesLayer,
        geometryRunPreviewLayer,
        geometryRunHandleLayer,
      ];
      localOverlayGroups.forEach((group) => {
        if (!group) return;
        if (!root?.hasLayer?.(group)) return;
        try {
          root.removeLayer(group);
          hiddenToolLayers.push(group);
        } catch {}
      });
      for (const layer of nonGeoVisible) {
        if (!layer?.id) continue;
        muted.push({ id: layer.id, opacity: Number(layer.opacity) });
        if (typeof layersService.setOpacity === "function") {
          layersService.setOpacity(layer.id, 0, { emit: false, persist: false });
        } else if (typeof layersService.updateLayer === "function") {
          layersService.updateLayer(layer.id, { opacity: 0 });
        }
      }
      await waitForRenderFrame();
      await waitForRenderFrame();
      return captureVisibleImageryCanvas();
    } finally {
      for (const layer of muted) {
        const restoreOpacity = Number.isFinite(layer.opacity) ? layer.opacity : 1;
        if (typeof layersService.setOpacity === "function") {
          layersService.setOpacity(layer.id, restoreOpacity, { emit: false, persist: false });
        } else if (typeof layersService.updateLayer === "function") {
          layersService.updateLayer(layer.id, { opacity: restoreOpacity });
        }
      }
      hiddenToolLayers.forEach((group) => {
        try { root.addLayer(group); } catch {}
      });
      if (rootDetached) {
        try { root.addTo(map); } catch {}
      }
      app?.events?.emit?.("layers:changed", layersService.listLayers?.() || []);
    }
  }

  async function captureVisibleImageryCanvas() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer) throw new Error("Map container is not available.");
    const html2canvas = await loadHtml2Canvas();
    return html2canvas(mapContainer, {
      backgroundColor: null,
      useCORS: true,
      logging: false,
      scale: 1,
      ignoreElements(element) {
        const className = String(element?.className?.baseVal || element?.className || "");
        if (className.includes("leaflet-control")) return true;
        if (className.includes("leaflet-marker-pane")) return true;
        if (className.includes("leaflet-tooltip-pane")) return true;
        if (className.includes("leaflet-popup-pane")) return true;
        return false;
      },
    });
  }

  async function estimateLineSlipCountFromImagery() {
    if (lineEndpoints.length < 2) {
      imageryEstimateStatus = "Draw a line first.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    imageryEstimateStatus = "Checking the visible imagery along this line...";
    render();
    const [start, end] = lineEndpoints;
    const lineLengthFeet = (Number(map.distance([start.lat, start.lng], [end.lat, end.lng])) || 0) * 3.280839895;
    if (!Number.isFinite(lineLengthFeet) || lineLengthFeet <= 0) {
      imageryEstimateStatus = "Could not determine line length from the current map.";
      render();
      return;
    }
    if (!hasVisibleRasterImagery()) {
      imageryEstimateStatus = "No readable aerial imagery layer is visible on the map.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const startPoint = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPoint = map.latLngToContainerPoint([end.lat, end.lng]);
    const lineLengthPx = Math.hypot(Number(endPoint.x) - Number(startPoint.x), Number(endPoint.y) - Number(startPoint.y));
    if (!Number.isFinite(lineLengthPx) || lineLengthPx < 40) {
      imageryEstimateStatus = "Zoom in a bit more before estimating from imagery.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    imageryEstimateStatus = "Analyzing repeating imagery pattern along the line...";
    render();
    const canvas = await captureGeoTiffImageryCanvas();
    if (!canvas) {
      geometryImageryEstimateStatus = "No visible GeoTIFF layer found. Import and enable a GeoTIFF first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const context = canvas?.getContext?.("2d", { willReadFrequently: true });
    if (!context) {
      imageryEstimateStatus = "Could not read the visible imagery layer.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const width = Number(canvas.width) || 0;
    const height = Number(canvas.height) || 0;
    if (!width || !height) {
      imageryEstimateStatus = "Visible imagery capture returned an empty canvas.";
      imageryEstimateDebug = null;
      render();
      return;
    }
    const pixels = context.getImageData(0, 0, width, height).data;
    const sampleCount = clampNumber(Math.round(lineLengthPx), 80, 420);
    const pixelsPerFoot = lineLengthPx / Math.max(1, lineLengthFeet);
    const minPitchFeet = 12;
    const maxPitchFeet = 30;
    const minLag = clampNumber(Math.round(minPitchFeet * pixelsPerFoot), 6, Math.max(12, Math.round(lineLengthPx / 2)));
    const maxLag = clampNumber(Math.round(maxPitchFeet * pixelsPerFoot), minLag + 2, Math.max(minLag + 2, Math.round(lineLengthPx / 2)));
    const corridorHalfWidths = [
      clampNumber(Math.round(lineLengthPx * 0.02), 7, 12),
      clampNumber(Math.round(lineLengthPx * 0.03), 9, 16),
      clampNumber(Math.round(lineLengthPx * 0.04), 11, 22),
      clampNumber(Math.round(lineLengthPx * 0.05), 13, 28),
    ];
    const signalRuns = corridorHalfWidths.map((corridorHalfWidthPx) => {
      const signal = buildImagerySpacingSignal(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 15,
        corridorHalfWidthPx,
      });
      const estimate = estimatePitchFromSignal(signal, { minLag, maxLag });
      return {
        corridorHalfWidthPx,
        signal,
        estimate: estimate ? { ...estimate, corridorHalfWidthPx } : null,
      };
    });
    const candidateEstimates = signalRuns.map((run) => run.estimate).filter(Boolean);
    const lagEstimate = chooseConsensusPitchEstimate(candidateEstimates);
    if (!lagEstimate?.lag) {
      imageryEstimateStatus = "Could not find a reliable repeating spacing in the imagery.";
      imageryEstimateDebug = {
        signalRuns,
        chosenCorridorHalfWidthPx: null,
        chosenSignal: [],
        suggestedSpacingSamples: null,
      };
      render();
      return;
    }
    const chosenRun = signalRuns.reduce((best, run) => {
      if (!run?.estimate) return best;
      if (Math.abs(run.estimate.lag - lagEstimate.lag) > Math.max(3, lagEstimate.lag * 0.2)) return best;
      if (!best) return run;
      return (run.estimate.confidence || 0) > (best.estimate?.confidence || 0) ? run : best;
    }, null);
    const currentCount = Math.max(2, Number(state.lineSlipCount || 2) || 2);
    const estimatedPitchFeet = lagEstimate.lag / Math.max(1e-6, pixelsPerFoot);
    const estimatedCount = Math.max(2, Math.round(lineLengthFeet / Math.max(1e-6, estimatedPitchFeet)) + 1);
    if (!Number.isFinite(estimatedCount) || estimatedCount < 2 || estimatedCount > 500) {
      imageryEstimateStatus = "Imagery estimate landed outside a safe slip-count range.";
      render();
      return;
    }
    const allowedDelta = Math.max(2, Math.round(currentCount * 0.35));
    if (lagEstimate.confidence < 0.28 && Math.abs(estimatedCount - currentCount) > allowedDelta) {
      imageryEstimateStatus = `Imagery pattern was too weak to safely move from ${currentCount} to ${estimatedCount} slips. Try zooming in more or draw the line tighter along one row.`;
      render();
      return;
    }
    state.lineSlipCount = estimatedCount;
    if (lineAnchorPoint && Number(lineAnchorPoint.index) >= estimatedCount - 1) lineAnchorPoint = null;
    persist();
    renderPreview();
    imageryEstimateDebug = {
      signalRuns,
      chosenCorridorHalfWidthPx: chosenRun?.corridorHalfWidthPx || null,
      chosenSignal: chosenRun?.signal || [],
      suggestedSpacingSamples: lagEstimate.lag,
    };
    const confidenceLabel = lagEstimate.confidence >= 0.66
      ? "high"
      : lagEstimate.confidence >= 0.4
        ? "medium"
        : "low";
    imageryEstimateStatus = `Estimated ${estimatedCount} slips from imagery at about ${estimatedPitchFeet.toFixed(1)} ft spacing (${confidenceLabel} confidence, ${lagEstimate.method || "signal"} method, ${lagEstimate.samples || candidateEstimates.length || 1} passes agreed).`;
    status = `Updated line preview to ${estimatedCount} slips from imagery.`;
    render();
  }

  async function estimateGeometryRunFromImagery() {
    if (!geometryRunLayoutActive || geometryRunEndpoints.length < 2) {
      geometryImageryEstimateStatus = "Set start and end run bookends first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const selected = selectedGeometryPolygon();
    if (!selected) {
      geometryImageryEstimateStatus = "Select a base slip polygon first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const start = geometryRunEndpoints[0];
    const end = geometryRunEndpoints[1];
    const startPoint = map.latLngToContainerPoint([start.lat, start.lng]);
    const endPoint = map.latLngToContainerPoint([end.lat, end.lng]);
    const vx = Number(endPoint.x) - Number(startPoint.x);
    const vy = Number(endPoint.y) - Number(startPoint.y);
    const lineLengthPx = Math.hypot(vx, vy);
    const lineLengthFeet = (Number(map.distance([start.lat, start.lng], [end.lat, end.lng])) || 0) * 3.280839895;
    if (!Number.isFinite(lineLengthPx) || lineLengthPx < 35 || !Number.isFinite(lineLengthFeet) || lineLengthFeet <= 0) {
      geometryImageryEstimateStatus = "Run line is too short. Extend the run and zoom in.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    if (!hasVisibleRasterImagery()) {
      geometryImageryEstimateStatus = "No readable imagery/raster layer is visible.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    geometryImageryEstimateStatus = "Analyzing GeoTIFF/imagery for spacing and width...";
    render();
    const canvas = await captureGeoTiffImageryCanvas();
    if (!canvas) {
      geometryImageryEstimateStatus = "No visible GeoTIFF layer found. Import and enable a GeoTIFF first.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const context = canvas?.getContext?.("2d", { willReadFrequently: true });
    if (!context) {
      geometryImageryEstimateStatus = "Could not read imagery pixels from the map.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const width = Number(canvas.width) || 0;
    const height = Number(canvas.height) || 0;
    if (!width || !height) {
      geometryImageryEstimateStatus = "Imagery capture was empty.";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    const pixels = context.getImageData(0, 0, width, height).data;
    const pixelsPerFoot = lineLengthPx / Math.max(1e-6, lineLengthFeet);
    const sampleCount = clampNumber(Math.round(lineLengthPx * 2.25), 240, 5200);
    const currentTotal = Math.max(2, geometryRunTotalCount());
    const currentPitchFeet = lineLengthFeet / Math.max(1, currentTotal - 1);
    const seedLag = currentPitchFeet * pixelsPerFoot;
    const minPitchFeet = clampNumber(currentPitchFeet * 0.7, 8, 40);
    const maxPitchFeet = clampNumber(currentPitchFeet * 1.35, minPitchFeet + 1, 50);
    const minLag = clampNumber(Math.round(minPitchFeet * pixelsPerFoot), 6, Math.max(12, Math.round(lineLengthPx / 2)));
    const maxLag = clampNumber(Math.round(maxPitchFeet * pixelsPerFoot), minLag + 2, Math.max(minLag + 2, Math.round(lineLengthPx / 2)));
    const corridorHalfWidths = [
      clampNumber(Math.round(lineLengthPx * 0.02), 7, 12),
      clampNumber(Math.round(lineLengthPx * 0.03), 9, 16),
      clampNumber(Math.round(lineLengthPx * 0.04), 11, 22),
    ];
    const coverageCheck = estimateSignalCoverage(pixels, width, height, startPoint, endPoint, {
      sampleCount: Math.max(64, Math.min(600, sampleCount)),
      crossSampleCount: 21,
      corridorHalfWidthPx: corridorHalfWidths[1] || corridorHalfWidths[0] || 12,
      alphaThreshold: 8,
    });
    if (coverageCheck.coverageRatio < 0.06 || coverageCheck.lumaStdDev < 2) {
      geometryImageryEstimateStatus = "Insufficient GeoTIFF coverage/signal under run line. Move run over raster data.";
      geometryImageryEstimateDebug = {
        chosenSignal: [],
        centerRatios: [],
      };
      render();
      return;
    }
    const signalRuns = corridorHalfWidths.flatMap((corridorHalfWidthPx) => {
      const robustSignal = buildImagerySpacingSignalRobust(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 31,
        corridorHalfWidthPx,
        darkPercentile: 0.28,
      });
      const binarySignal = buildImagerySpacingSignalBinary(pixels, width, height, startPoint, endPoint, {
        sampleCount,
        crossSampleCount: 35,
        corridorHalfWidthPx,
        alphaThreshold: 8,
        darkPercentile: 0.35,
      });
      const variants = [
        { signal: robustSignal, mode: "robust" },
        { signal: binarySignal, mode: "binary" },
      ];
      return variants.map((variant) => {
        const autoEstimate = estimatePitchFromSignal(variant.signal, { minLag, maxLag });
        const peakEstimate = estimatePitchFromPeaks(variant.signal, { minLag, maxLag, seedLag });
        let estimate = autoEstimate;
        if (peakEstimate?.lag && autoEstimate?.lag) {
          estimate = (Number(peakEstimate.confidence || 0) >= Number(autoEstimate.confidence || 0) * 0.85)
            ? peakEstimate
            : autoEstimate;
        } else if (peakEstimate?.lag) {
          estimate = peakEstimate;
        }
        const rawLag = estimateSignalLag(variant.signal, { minLag, maxLag });
        return {
          signal: variant.signal,
          mode: variant.mode,
          corridorHalfWidthPx,
          estimate,
          rawLag,
        };
      });
    });
    const candidateEstimates = signalRuns.map((run) => run.estimate).filter(Boolean);
    let lagEstimate = chooseConsensusPitchEstimate(candidateEstimates);
    if (!lagEstimate?.lag) {
      const fallbackRun = signalRuns.reduce((best, run) => {
        if (!run?.rawLag?.lag || !Number.isFinite(run.rawLag.score)) return best;
        if (!best) return run;
        return Number(run.rawLag.score) > Number(best.rawLag?.score || -Infinity) ? run : best;
      }, null);
      if (fallbackRun?.rawLag?.lag) {
        lagEstimate = {
          lag: Number(fallbackRun.rawLag.lag),
          confidence: clampNumber(Number(fallbackRun.rawLag.confidence || 0.12), 0, 1),
          method: `autocorr-fallback-${fallbackRun.mode || "unknown"}`,
          samples: 1,
        };
      }
    }
    if (!lagEstimate?.lag) {
      const bestSignalRun = signalRuns.reduce((best, run) => {
        const sig = Array.isArray(run?.signal) ? run.signal : [];
        const variance = seriesStats(sig).stdDev || 0;
        if (!best) return { run, variance };
        return variance > best.variance ? { run, variance } : best;
      }, null)?.run || null;
      geometryImageryEstimateStatus = "Could not detect repeating slip spacing from imagery.";
      geometryImageryEstimateDebug = {
        chosenSignal: Array.isArray(bestSignalRun?.signal) ? bestSignalRun.signal : [],
        centerRatios: [],
      };
      render();
      return;
    }
    const chosenRun = signalRuns.reduce((best, run) => {
      if (!run?.estimate) return best;
      if (Math.abs(run.estimate.lag - lagEstimate.lag) > Math.max(3, lagEstimate.lag * 0.2)) return best;
      if (!best) return run;
      return (run.estimate.confidence || 0) > (best.estimate?.confidence || 0) ? run : best;
    }, null);
    const peakDerived = deriveSlipCentersFromDockPeaks(chosenRun?.signal, {
      minLag,
      maxLag,
      seedLag,
      sampleCount,
    });
    const ux = vx / Math.max(1e-6, lineLengthPx);
    const uy = vy / Math.max(1e-6, lineLengthPx);
    const previewPoints = geometryRunPreviewPoints();
    const widthCandidatesFt = previewPoints
      .slice(0, Math.min(10, previewPoints.length))
      .map((point) => map.latLngToContainerPoint([point.lat, point.lng]))
      .map((centerPoint) => estimateWidthFromPerpendicularProfile(
        pixels,
        width,
        height,
        centerPoint,
        { x: ux, y: uy },
        pixelsPerFoot,
        Number(selected?.widthFt || 0)
      ))
      .filter((entry) => entry?.spanPx)
      .map((entry) => roundToHalfFoot(entry.spanPx / Math.max(1e-6, pixelsPerFoot)))
      .filter((value) => Number.isFinite(value) && value >= 4 && value <= 35)
      .sort((a, b) => a - b);
    const widthEstimateFt = widthCandidatesFt.length
      ? widthCandidatesFt[Math.floor(widthCandidatesFt.length / 2)]
      : null;
    const canTrustPeakDerived = Number(peakDerived?.intervalsUsed || 0) >= 4 && Number(peakDerived?.confidence || 0) >= 0.35;
    const usedLag = Number((canTrustPeakDerived ? peakDerived?.lag : null) || lagEstimate.lag || 0);
    const estimatedPitchFeet = usedLag / Math.max(1e-6, pixelsPerFoot);
    const peakDerivedTotal = canTrustPeakDerived ? Number(peakDerived?.centerRatios?.length || 0) : 0;
    const estimatedTotal = peakDerivedTotal >= 2
      ? Math.max(2, peakDerivedTotal)
      : Math.max(2, Math.round(lineLengthFeet / Math.max(1e-6, estimatedPitchFeet)) + 1);
    state.slipGeometryRunBetweenCount = Math.max(1, estimatedTotal);
    if (Number.isFinite(widthEstimateFt)) {
      updateSelectedGeometry({ widthFt: widthEstimateFt });
    }
    persist();
    renderGeometry();
    renderGeometryRunPreview();
    const newPreviewPoints = geometryRunPreviewPoints();
    const previewCenterRatios = projectRatiosOnSegment(newPreviewPoints, start, end);
    const centerRatios = peakDerivedTotal >= 2
      ? peakDerived.centerRatios
      : previewCenterRatios;
    geometryImageryEstimateDebug = {
      chosenSignal: Array.isArray(chosenRun?.signal) ? chosenRun.signal : [],
      centerRatios,
      spacingLagSamples: usedLag,
      sampleCount,
    };
    const widthText = Number.isFinite(widthEstimateFt)
      ? `${Number(widthEstimateFt).toFixed(1)} ft`
      : "unchanged";
    geometryImageryEstimateStatus = `Suggested run: ${estimatedTotal} slips, spacing ~${estimatedPitchFeet.toFixed(1)} ft (seed ${currentPitchFeet.toFixed(1)}), width ${widthText}.${canTrustPeakDerived ? ` Count from dock-peak intervals (${peakDerived.intervalsUsed} intervals).` : ""}`;
    status = "Applied optional imagery-based run suggestions.";
    render();
  }

  function renderPreview() {
    previewLayer.clearLayers();
    const previewPoints = linePreviewPoints();
    const previewSegments = linePreviewSegments();
    const guidePoints = lineLayoutMode === "spacing"
      ? [...lineEndpoints, ...(lineHoverPoint && lineEndpoints.length >= 2 && lineEndpoints.length < 3 ? [lineHoverPoint] : [])]
      : lineEndpoints;
    if (lineEndpoints.length === 1) {
      L.circleMarker([lineEndpoints[0].lat, lineEndpoints[0].lng], {
        radius: 6,
        color: "#f59e0b",
        weight: 2,
        fillColor: "#f59e0b",
        fillOpacity: 0.35,
        renderer: canvasRenderer,
      }).addTo(previewLayer);
      return;
    }
    if (guidePoints.length >= 2) {
      L.polyline(guidePoints.map((point) => [point.lat, point.lng]), {
        color: "rgba(217,70,239,0.35)",
        weight: 2,
        dashArray: "4 6",
        renderer: canvasRenderer,
      }).addTo(previewLayer);
      lineEndpoints.forEach((point, index) => {
        const handle = L.marker([point.lat, point.lng], {
          draggable: true,
          autoPan: true,
          zIndexOffset: 1000,
          icon: L.divIcon({
            className: "slip-inventory-endpoint-handle",
            iconSize: [18, 18],
            iconAnchor: [9, 9],
            html: `
              <div style="
                width:18px;
                height:18px;
                border-radius:999px;
                background:rgba(245,158,11,0.96);
                border:2px solid rgba(15,23,42,0.92);
                box-shadow:0 0 0 1px rgba(254,243,199,0.65), 0 6px 12px rgba(15,23,42,0.24);
              "></div>
            `,
          }),
        }).addTo(previewLayer);
        const tooltip = lineLayoutMode === "spacing"
          ? (index === 0 ? "Drag first slip center" : index === 1 ? "Drag spacing slip center" : "Drag run extent")
          : (index === 0 ? "Drag start point" : "Drag end point");
        handle.bindTooltip(tooltip, { direction: "top", offset: [0, -8] });
        handle.on("dragend", (event) => {
          const latlng = event?.target?.getLatLng?.();
          if (!latlng) return;
          lineEndpoints[index] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          status = lineLayoutMode === "spacing"
            ? "Adjusted spacing run point."
            : `Adjusted ${index === 0 ? "start" : "end"} point.`;
          renderPreview();
          render();
        });
      });
    }
    if (!previewPoints.length) return;
    if (previewSegments.length) {
      previewSegments.forEach((segment, index) => {
        L.polyline([[segment.start.lat, segment.start.lng], [segment.end.lat, segment.end.lng]], {
          color: "#d946ef",
          weight: 8,
          opacity: 0.92,
          lineCap: "round",
          renderer: canvasRenderer,
        }).addTo(previewLayer);
        if (index < previewSegments.length - 1) {
          const boundary = segment.end;
          const next = previewSegments[index + 1];
          const dx = Number(next.end.lng) - Number(next.start.lng);
          const dy = Number(next.end.lat) - Number(next.start.lat);
          const len = Math.hypot(dx, dy) || 1;
          const nx = -dy / len;
          const ny = dx / len;
          const tickHalf = 0.000012;
          L.polyline([
            [boundary.lat - (ny * tickHalf), boundary.lng - (nx * tickHalf)],
            [boundary.lat + (ny * tickHalf), boundary.lng + (nx * tickHalf)],
          ], {
            color: "rgba(250,232,255,0.9)",
            weight: 2,
            opacity: 0.95,
            dashArray: "2 4",
            renderer: canvasRenderer,
          }).addTo(previewLayer);
        }
      });
    }
    const ids = previewSlipIds(state, previewPoints.length);
    previewPoints.forEach((point, index) => {
      const marker = L.marker([point.lat, point.lng], {
        interactive: true,
        draggable: lineLayoutMode !== "spacing",
        icon: L.divIcon({
          className: "slip-inventory-preview-label-wrap",
          iconSize: null,
          iconAnchor: [0, 0],
          html: `
            <div style="transform:translate(-50%, -50%); display:flex; flex-direction:column; align-items:center; gap:4px;">
              <div style="width:16px; height:2px; background:rgba(245,158,11,0.95); box-shadow:0 0 0 1px rgba(15,23,42,0.28);"></div>
              <div style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                padding:2px 7px;
                border-radius:999px;
                background:rgba(15,23,42,0.78);
                color:#fde68a;
                border:1px solid rgba(245,158,11,0.55);
                font:700 11px/1.2 system-ui,sans-serif;
                white-space:nowrap;
              ">${esc(ids[index])}</div>
            </div>
          `,
        }),
      }).addTo(previewLayer);
      marker.bindTooltip(
        lineLayoutMode === "spacing"
          ? `Preview ${ids[index]}`
          : `Preview ${ids[index]}${index < previewPoints.length - 1 ? ` | click to set count ${index + 1}` : ""} | drag to re-space`,
        { direction: "top", offset: [0, -8] },
      );
      marker.on("click", () => {
        if (lineLayoutMode === "spacing") return;
        state.lineSlipCount = Math.max(1, index + 1);
        if (lineAnchorPoint && Number(lineAnchorPoint.index) >= state.lineSlipCount - 1) lineAnchorPoint = null;
        persist();
        renderPreview();
        render();
      });
      marker.on("dragend", (event) => {
        if (lineLayoutMode === "spacing") return;
        const latlng = event?.target?.getLatLng?.();
        if (!latlng) return;
        if (index <= 0) {
          lineEndpoints[0] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          lineAnchorPoint = null;
          status = "Adjusted line start point.";
        } else if (index >= previewPoints.length - 1) {
          lineEndpoints[1] = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          lineAnchorPoint = null;
          status = "Adjusted line end point.";
        } else {
          lineAnchorPoint = {
            index,
            lat: Number(latlng.lat),
            lng: Number(latlng.lng),
          };
          status = `Re-spaced line around ${ids[index] || `slip ${index + 1}`}.`;
        }
        renderPreview();
        render();
      });
    });
  }

  function syncSelection() {
    if (state.selectedMeasurementId && records.some((record) => record.measurement_id === state.selectedMeasurementId)) return;
    if (!state.selectedMeasurementId) return;
    state.selectedMeasurementId = "";
    persist();
  }

  function getSlipTypeById(typeId) {
    const id = String(typeId || "");
    return state.slipGeometryTypes.find((entry) => String(entry.id) === id) || null;
  }

  function getSelectedGeometryIds() {
    const ids = new Set((Array.isArray(state.slipGeometrySelectedIds) ? state.slipGeometrySelectedIds : []).map((value) => String(value || "")).filter(Boolean));
    const primary = String(state.slipGeometrySelectedId || "");
    if (primary) ids.add(primary);
    return [...ids];
  }

  function setSelectedGeometryIds(ids = [], primaryId = "") {
    const list = [...new Set((Array.isArray(ids) ? ids : []).map((value) => String(value || "")).filter(Boolean))];
    state.slipGeometrySelectedIds = list;
    state.slipGeometrySelectedId = String(primaryId || list[0] || "");
    persist();
  }

  function clearGeometrySelection() {
    state.slipGeometrySelectedIds = [];
    state.slipGeometrySelectedId = "";
    tableSelectedMeasurementIds.clear();
    persist();
  }

  function applyGeometrySelection(id, { toggle = false } = {}) {
    const targetId = String(id || "");
    if (!targetId) return;
    if (toggle) {
      const ids = getSelectedGeometryIds();
      const exists = ids.includes(targetId);
      const next = exists ? ids.filter((value) => value !== targetId) : [...ids, targetId];
      setSelectedGeometryIds(next, exists ? next[0] : targetId);
      const primaryId = String(exists ? next[0] : targetId);
      const primary = state.slipGeometryPolygons.find((poly) => String(poly?.id || "") === primaryId) || null;
      if (primary?.linkedMeasurementId) state.selectedMeasurementId = String(primary.linkedMeasurementId);
      return;
    }
    setSelectedGeometryIds([targetId], targetId);
    const selected = state.slipGeometryPolygons.find((poly) => String(poly?.id || "") === targetId) || null;
    if (selected?.linkedMeasurementId) state.selectedMeasurementId = String(selected.linkedMeasurementId);
  }

  function normalizeGeometrySelection() {
    const existing = new Set(state.slipGeometryPolygons.map((poly) => String(poly.id)));
    const current = getSelectedGeometryIds();
    const next = current.filter((id) => existing.has(id));
    const primaryId = String(state.slipGeometrySelectedId || "");
    // Only normalize when selection is actually invalid (stale IDs or primary not in set)
    if (next.length !== current.length || (next.length > 0 && !next.includes(primaryId))) {
      setSelectedGeometryIds(next, next[0] || "");
    }
  }

  function isGeometrySelected(polyId) {
    const id = String(polyId || "");
    return getSelectedGeometryIds().includes(id);
  }

  function selectedGeometryPolygon() {
    return state.slipGeometryPolygons.find((entry) => String(entry.id) === String(state.slipGeometrySelectedId || "")) || null;
  }

  function selectedGeometryPolygons() {
    const ids = new Set(getSelectedGeometryIds());
    return state.slipGeometryPolygons.filter((entry) => ids.has(String(entry.id)));
  }

  // Feeds the map's bottom status tray (core/map.js, alongside the zoom/rotation readouts)
  // so length/width/area of the current selection is visible while cycling through slips,
  // without having to open the table panel. Empty selection clears it back to just zoom/rotation.
  function updateSelectionStatusTray(selected) {
    if (!selected.length) { app.events.emit("map:status:extra", { items: [] }); return; }
    let totalLengthFt = 0, totalWidthFt = 0, totalAreaSf = 0;
    for (const poly of selected) {
      const l = Number(poly?.lengthFt) || 0;
      const w = Number(poly?.widthFt) || 0;
      totalLengthFt += l;
      totalWidthFt += w;
      totalAreaSf += l * w;
    }
    app.events.emit("map:status:extra", {
      items: [
        `${selected.length} slip${selected.length === 1 ? "" : "s"} selected`,
        `${totalLengthFt.toFixed(1)} ft len`,
        `${totalWidthFt.toFixed(1)} ft wid`,
        `${Math.round(totalAreaSf).toLocaleString()} sf area`,
      ],
    });
  }

  // Right-justified counterpart to updateSelectionStatusTray above: a live "Color Slips By"
  // control (select + prev/next cycle arrows) in the map's own status tray, so the mode can be
  // changed without opening the sidebar or table panel. Mirrors (and writes back to) the same
  // state.colorSlipsBy the sidebar/table selects use -- see the map:status:tray-right:action
  // listener for the write-back side. Skips re-emitting when the mode hasn't changed since the
  // last call so an open dropdown in the tray isn't yanked shut by an unrelated render() pass.
  let _lastColorByTrayValue = null;
  function updateColorByStatusTray() {
    if (_lastColorByTrayValue === state.colorSlipsBy) return;
    _lastColorByTrayValue = state.colorSlipsBy;
    const options = COLOR_SLIPS_BY_OPTIONS.map((o) => (
      `<option value="${esc(o.value)}"${o.value === state.colorSlipsBy ? " selected" : ""}>${esc(o.label)}</option>`
    )).join("");
    app.events.emit("map:status:tray-right", {
      html: `
        <button type="button" class="map-status-tray-cycle-btn" data-tray-action="color-by-prev" title="Previous color mode" aria-label="Previous color mode">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 4 7 12l8 8" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <select data-tray-action="color-by-select" title="Color slips by" aria-label="Color slips by">${options}</select>
        <button type="button" class="map-status-tray-cycle-btn" data-tray-action="color-by-next" title="Next color mode" aria-label="Next color mode">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4l8 8-8 8" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      `,
    });
  }

  function createSlipType({ name, lengthFt, widthFt }) {
    const next = {
      id: makeGeometryId(),
      name: String(name || `Type ${state.slipGeometryTypes.length + 1}`),
      lengthFt: roundToHalfFoot(lengthFt),
      widthFt: roundToHalfFoot(widthFt),
    };
    state.slipGeometryTypes = [...state.slipGeometryTypes, next];
    if (!state.slipGeometryDraftTypeId) state.slipGeometryDraftTypeId = next.id;
    persist();
  }

  function placeGeometryAt(latlng, typeId, overrides = null) {
    const slipType = getSlipTypeById(typeId);
    if (!slipType || !latlng) return null;
    const source = (overrides && typeof overrides === "object") ? overrides : slipType;
    const translatedRing = (
      Array.isArray(source?.ringLatLngs)
      && source.ringLatLngs.length >= 3
      && source?.sourceCenter
    )
      ? translateRingLatLngs(
        source.ringLatLngs,
        source.sourceCenter,
        { lat: Number(latlng.lat), lng: Number(latlng.lng) },
      )
      : null;
    const inferred = translatedRing ? inferPolyMetricsFromRing(translatedRing) : null;
    const poly = {
      id: makeGeometryId(),
      slipTypeId: slipType.id,
      lengthFt: Number(roundToTenthFoot(inferred?.lengthFt ?? source.lengthFt) ?? source.lengthFt ?? 40),
      widthFt: Number(roundToTenthFoot(inferred?.widthFt ?? source.widthFt) ?? source.widthFt ?? 14),
      headingDeg: Number((inferred?.headingDeg ?? source.headingDeg) || 0),
      locked: false,
      center: translatedRing
        ? { lat: Number(inferred?.center?.lat ?? latlng.lat), lng: Number(inferred?.center?.lng ?? latlng.lng) }
        : { lat: Number(latlng.lat), lng: Number(latlng.lng) },
      ringLatLngs: translatedRing || null,
    };
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, poly];
    setSelectedGeometryIds([poly.id], poly.id);
    // Explicitly ensure new placements are editable regardless of prior lock state.
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((entry) => (
      String(entry.id) === String(poly.id) ? { ...entry, locked: false } : entry
    ));
    persist();
    return poly;
  }

function updateSelectedGeometry(changes = {}, { relative = false } = {}) {
    let selectedIds = getSelectedGeometryIds();
    if (!selectedIds.length && state.slipGeometryPolygons.length) {
      const fallback = [...state.slipGeometryPolygons].reverse().find((poly) => !poly?.locked) || state.slipGeometryPolygons[state.slipGeometryPolygons.length - 1];
      if (fallback?.id) {
        setSelectedGeometryIds([String(fallback.id)], String(fallback.id));
        selectedIds = getSelectedGeometryIds();
      }
    }
    if (!selectedIds.length) return;
    const targetIds = new Set(selectedIds);
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => {
      if (!targetIds.has(String(poly.id))) return poly;
      if (poly.locked && !poly.committed && (changes.center || changes.lengthFt != null || changes.widthFt != null || changes.headingDeg != null)) return poly;
      const nextCenter = changes.center ? {
        lat: Number(changes.center.lat),
        lng: Number(changes.center.lng),
      } : poly.center;
      // relative: changes.lengthFt/widthFt/headingDeg (whichever is present) is a delta
      // added to THIS polygon's own current value instead of an absolute value applied to
      // every selected polygon alike -- lets a multi-selection of differently-sized slips
      // each grow/shrink by the same amount rather than all converging on one shared size.
      const nextHeadingDeg = changes.headingDeg == null
        ? Number(poly.headingDeg || 0)
        : (relative ? Number(poly.headingDeg || 0) + Number(changes.headingDeg || 0) : Number(changes.headingDeg || 0));
      const nextLengthFt = changes.lengthFt == null
        ? Number(poly.lengthFt || 0)
        : (relative ? Number(poly.lengthFt || 0) + Number(changes.lengthFt) : Number(changes.lengthFt));
      const nextWidthFt = changes.widthFt == null
        ? Number(poly.widthFt || 0)
        : (relative ? Number(poly.widthFt || 0) + Number(changes.widthFt) : Number(changes.widthFt));
      const canTransformRing = Array.isArray(poly.ringLatLngs) && poly.ringLatLngs.length >= 3
        && (
          changes.center
          || changes.lengthFt != null
          || changes.widthFt != null
          || changes.headingDeg != null
        );
      const transformedRing = canTransformRing
        ? transformRingLatLngsWithMetrics({
          ringLatLngs: poly.ringLatLngs,
          oldCenter: poly.center,
          newCenter: nextCenter,
          oldHeadingDeg: Number(poly.headingDeg || 0),
          newHeadingDeg: nextHeadingDeg,
          oldLengthFt: Number(poly.lengthFt || 0),
          newLengthFt: nextLengthFt,
          oldWidthFt: Number(poly.widthFt || 0),
          newWidthFt: nextWidthFt,
        })
        : null;
      return {
        ...poly,
        ...changes,
        center: nextCenter,
        headingDeg: nextHeadingDeg,
        lengthFt: nextLengthFt,
        widthFt: nextWidthFt,
        locked: (
          changes.center
          || changes.lengthFt != null
          || changes.widthFt != null
          || changes.headingDeg != null
        ) ? false : poly.locked,
        committed: poly.committed,
        ringLatLngs: transformedRing
          ? transformedRing
          : (
            changes.center
            || changes.lengthFt != null
            || changes.widthFt != null
            || changes.headingDeg != null
          ) ? null : poly.ringLatLngs,
      };
    });
    persist();
  }

  function duplicateSelectedGeometryRun({ copies = 1, spacingFt = 2 } = {}) {
    const base = selectedGeometryPolygon();
    if (!base) return 0;
    const total = Math.max(1, Math.floor(Number(copies) || 1));
    const gap = Math.max(0, Number(spacingFt) || 0);
    const headingRad = (Number(base.headingDeg || 0) * Math.PI) / 180;
    const ux = Math.sin(headingRad);
    const uy = Math.cos(headingRad);
    const stepMeters = feetToMeters((Number(base.widthFt) || 0) + gap);
    const next = [];
    for (let i = 1; i <= total; i += 1) {
      const c = offsetPoint(base.center, ux * stepMeters * i, uy * stepMeters * i);
      next.push({
        ...base,
        id: makeGeometryId(),
        center: { lat: c.lat, lng: c.lng },
        locked: false,
      });
    }
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, ...next];
    if (next.length) state.slipGeometrySelectedId = next[next.length - 1].id;
    persist();
    return next.length;
  }

  function geometryRunTotalCount() {
    return Math.max(1, Number(state.slipGeometryRunBetweenCount || 0) || 1);
  }

  function geometryRunPreviewPoints() {
    if (!geometryRunEndpoints.length) return [];
    const start = geometryRunEndpoints[0];
    const end = geometryRunEndpoints[1] || geometryRunHoverPoint;
    if (!end) return [];
    const total = geometryRunTotalCount();
    return Array.from({ length: total }, (_, index) => {
      const ratio = total <= 1 ? 0 : (index / Math.max(1, total - 1));
      return interpolateLatLng(start, end, ratio);
    });
  }

  function stopGeometryRunLayout(reason = "stopped") {
    if (geometryRunLayoutOff) {
      geometryRunLayoutOff();
      geometryRunLayoutOff = null;
    }
    geometryRunLayoutActive = false;
    geometryRunEndpoints = [];
    geometryRunHoverPoint = null;
    geometryRunPreviewLayer.clearLayers();
    geometryRunHandleLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
    if (reason === "cancelled") {
      status = "Slip geometry run layout cancelled.";
      render();
    }
  }

  function createGeometryRunFromPreview() {
    const selected = selectedGeometryPolygon();
    if (!selected) return null;
    const points = geometryRunPreviewPoints();
    if (points.length < 1) return null;
    const heading = Number(selected.headingDeg || 0);
    const selectedCenter = {
      lat: Number(selected?.center?.lat || 0),
      lng: Number(selected?.center?.lng || 0),
    };
    const hasSourceRing = Array.isArray(selected?.ringLatLngs) && selected.ringLatLngs.length >= 3;
    const firstPoint = points[0];
    const firstTargetCenter = { lat: Number(firstPoint.lat), lng: Number(firstPoint.lng) };
    const firstTranslatedRing = hasSourceRing
      ? translateRingLatLngs(selected.ringLatLngs, selectedCenter, firstTargetCenter)
      : null;
    const firstInferred = firstTranslatedRing ? inferPolyMetricsFromRing(firstTranslatedRing) : null;
    const updatedSelected = {
      ...selected,
      runGroupId: "",
      headingDeg: Number(firstInferred?.headingDeg ?? heading),
      center: firstTranslatedRing
        ? { lat: Number(firstInferred?.center?.lat ?? firstTargetCenter.lat), lng: Number(firstInferred?.center?.lng ?? firstTargetCenter.lng) }
        : firstTargetCenter,
      ringLatLngs: firstTranslatedRing || null,
      locked: false,
      committed: false,
    };
    const created = points.slice(1).map((point) => {
      const targetCenter = { lat: Number(point.lat), lng: Number(point.lng) };
      const translatedRing = hasSourceRing
        ? translateRingLatLngs(selected.ringLatLngs, selectedCenter, targetCenter)
        : null;
      const inferred = translatedRing ? inferPolyMetricsFromRing(translatedRing) : null;
      return {
        ...selected,
        id: makeGeometryId(),
        runGroupId: "",
        headingDeg: Number(inferred?.headingDeg ?? heading),
        center: translatedRing
          ? { lat: Number(inferred?.center?.lat ?? targetCenter.lat), lng: Number(inferred?.center?.lng ?? targetCenter.lng) }
          : targetCenter,
        ringLatLngs: translatedRing || null,
        locked: false,
        committed: false,
      };
    });
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      String(poly.id) === String(selected.id) ? updatedSelected : poly
    ));
    state.slipGeometryPolygons = [...state.slipGeometryPolygons, ...created];
    if (created.length) {
      const nextId = String(created[created.length - 1].id || "");
      setSelectedGeometryIds([nextId], nextId);
    }
    persist();
    return {
      createdCount: created.length,
      createdIds: created.map((entry) => String(entry.id)),
      sourceId: String(selected.id),
      points,
      basePoly: { ...selected },
    };
  }

  async function commitGeometryRunToRecords(runResult) {
    const points = Array.isArray(runResult?.points) ? runResult.points : [];
    if (!points.length) return 0;
    // points[0] is always the pre-existing selected slip's own location (ratio 0 of the
    // interpolation) -- it must be re-linked to its existing record, not minted a fresh
    // slip ID, or the run duplicates the source slip on top of itself (new ID, same spot)
    // and shifts every subsequent generated ID up by one.
    const sourceSlipId = String(runResult?.basePoly?.linkedSlipId || "").trim();
    const sourceMeasurementId = String(runResult?.basePoly?.linkedMeasurementId || "").trim();
    const hasSourceRecord = !!(sourceSlipId || sourceMeasurementId);
    const newPointCount = Math.max(0, points.length - (hasSourceRecord ? 1 : 0));
    const newIds = newPointCount > 0 ? previewSlipIds(state, newPointCount) : [];
    // Only the source point (re-linked below via its real linkedMeasurementId) is a
    // legitimate update-in-place; every other id here is about to mint a brand-new record,
    // so all of them -- plus the source's own id, on the rare uncommitted-draft path where
    // it has a typed slip_id but no measurementId link yet -- need the collision guard.
    const idsNeedingNewRecord = hasSourceRecord && !sourceMeasurementId
      ? [sourceSlipId, ...newIds]
      : newIds;
    const conflictMsg = conflictingSlipIdsMessage(idsNeedingNewRecord);
    if (conflictMsg) throw new Error(conflictMsg);
    const nowIso = new Date().toISOString();
    let upserts = 0;
    const links = [];
    const undoChanges = [];
    let newIdCursor = 0;
    for (let index = 0; index < points.length; index += 1) {
      const point = points[index];
      const isSourcePoint = index === 0 && hasSourceRecord;
      const slipId = isSourcePoint
        ? (sourceSlipId || `SLIP-${String(selectedIndex() + index + 1).padStart(4, "0")}`)
        : String(newIds[newIdCursor] || `SLIP-${String(selectedIndex() + index + 1).padStart(4, "0")}`);
      const polygonIds = [String(runResult?.sourceId || ""), ...((Array.isArray(runResult?.createdIds) ? runResult.createdIds : []).map((id) => String(id || "")))];
      const polygonId = String(polygonIds[index] || "");
      const polygon = state.slipGeometryPolygons.find((poly) => String(poly?.id || "") === polygonId) || null;
      const polygonRing = polygon ? computeSlipPolygonLatLngs(polygon) : [];
      const polygonWkt = buildPolygonWkt(polygonRing);
      const inferred = polygonRing.length >= 3 ? inferPolyMetricsFromRing(polygonRing) : null;
      const measuredLengthFt = roundToTenthFoot(inferred?.lengthFt ?? polygon?.lengthFt ?? null);
      const measuredWidthFt = roundToTenthFoot(inferred?.widthFt ?? polygon?.widthFt ?? null);
      // Only ever re-links by the polygon's own real linkedMeasurementId -- never by
      // matching slip_id text, which used to silently overwrite an unrelated existing slip
      // that happened to share the same (auto-generated) id. Every other point already
      // passed the collision guard above, so it's always safe to create fresh here.
      const existing = isSourcePoint && sourceMeasurementId
        ? records.find((record) => String(record?.measurement_id || "") === sourceMeasurementId) || null
        : null;
      if (existing?.measurement_id) {
        const measurementId = String(existing.measurement_id || "");
        const before = cloneRecord(existing);
        await requestJson(`${API_BASE}/${encodeURIComponent(existing.measurement_id)}`, {
          method: "PATCH",
          body: {
            ...existing,
            marina_id: state.marinaId,
            marina_name: state.marinaName,
            measurement_date: state.measurementDate,
            confidence_score: state.confidenceScore,
            operational_status: String(existing?.operational_status || "operable"),
            notes: String(existing?.notes || ""),
            geometry_wkt: polygonWkt || buildPointWkt(point.lng, point.lat),
            measured_linear_feet: measuredLengthFt,
            measured_width_feet: measuredWidthFt,
          },
        });
        undoChanges.push({ type: "update", measurementId, before, after: null });
        links.push({ polygonId, measurementId, slipId });
      } else {
        const measurementId = makeId("MEAS");
        await requestJson(`${API_BASE}`, {
          method: "POST",
          body: {
            measurement_id: measurementId,
            marina_id: state.marinaId,
            marina_name: state.marinaName,
            slip_id: slipId,
            dock_name: state.dockName || null,
            suffix: state.slipIdSuffix || null,
            region: state.region || null,
            space_type: state.spaceType || null,
            multislip_yn: "no",
            multislip_id: null,
            covered_yn: draftCovered ? "yes" : "no",
            boatlift_yn: draftBoatlift ? "yes" : "no",
            fueldock_yn: draftFuelDock ? "yes" : "no",
            ...draftOutOfServiceFields(),
            measured_linear_feet: measuredLengthFt,
            measured_width_feet: measuredWidthFt,
            measured_depth_feet: null,
            operational_status: "operable",
            notes: draftComments || "",
            measurement_date: state.measurementDate,
            confidence_score: state.confidenceScore,
            record_created_timestamp: nowIso,
            created_by_email: app.auth?.email || null,
            geometry_wkt: polygonWkt || buildPointWkt(point.lng, point.lat),
          },
        });
        undoChanges.push({ type: "create", measurementId });
        links.push({ polygonId, measurementId, slipId });
      }
      if (!isSourcePoint) {
        newIdCursor += 1;
        incrementSlipIdNumber(state);
        incrementSlipIdSuffix(state);
      }
      upserts += 1;
    }
    persist();
    await refreshRecords({ quiet: true });
    // "after" for update-type changes couldn't be captured until now -- records only reflects
    // the PATCHed state once refreshRecords() above has run.
    for (const change of undoChanges) {
      if (change.type === "update") change.after = snapshotRecord(change.measurementId);
    }
    pushUndoEntry(`Drew ${upserts} slip${upserts === 1 ? "" : "s"} (Run)`, undoChanges);
    return { upserts, links };
  }

  async function generateRunAndPersist() {
    const runResult = createGeometryRunFromPreview();
    if (!runResult) return { createdCount: 0, upserts: 0 };
    stopGeometryRunLayout("completed");
    const commitResult = await commitGeometryRunToRecords(runResult);
    const upserts = Number(commitResult?.upserts || 0);
    const links = Array.isArray(commitResult?.links) ? commitResult.links : [];
    const committedIds = new Set([
      String(runResult.sourceId || ""),
      ...((Array.isArray(runResult.createdIds) ? runResult.createdIds : []).map((id) => String(id || ""))),
    ]);
    const linkByPolyId = new Map(links.map((entry) => [String(entry.polygonId || ""), entry]));
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      committedIds.has(String(poly.id))
        ? {
          ...poly,
          committed: true,
          linkedMeasurementId: String(linkByPolyId.get(String(poly.id))?.measurementId || poly.linkedMeasurementId || ""),
          linkedSlipId: String(linkByPolyId.get(String(poly.id))?.slipId || poly.linkedSlipId || ""),
        }
        : poly
    ));
    // Remove local editable twins that can stack on top of the newly committed run.
    // We only remove non-committed polygons that match the committed record links.
    const committedMeasurementIds = new Set(
      links
        .map((entry) => String(entry?.measurementId || ""))
        .filter(Boolean),
    );
    const committedSlipIds = new Set(
      links
        .map((entry) => String(entry?.slipId || ""))
        .filter(Boolean),
    );
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => {
      const id = String(poly?.id || "");
      if (committedIds.has(id)) return true;
      if (poly?.committed) return true;
      const measurementId = String(poly?.linkedMeasurementId || "");
      const slipId = String(poly?.linkedSlipId || "");
      if (measurementId && committedMeasurementIds.has(measurementId)) return false;
      if (slipId && committedSlipIds.has(slipId)) return false;
      return true;
    });
    // Normalize to the same committed geometry shape/style that other clients receive
    // from API rehydration, so creator client does not need a manual refresh.
    synchronizeCommittedGeometryFromRecords();
    // After commit, leave no active geometry selection so controls don't
    // accidentally keep editing the just-generated run.
    clearGeometrySelection();
    persist();
    status = `Created ${runResult.createdCount} geometry polygons and upserted ${upserts} slip records from run layout.`;
    if (upserts > 0) app.log("slip.created", { count: upserts, tool: "geometry_run", marina: state.marinaId });
    render();
    return { createdCount: runResult.createdCount, upserts };
  }

  async function commitSelectedPolygonAndPersist() {
    const selected = selectedGeometryPolygon();
    if (!selected) return { upserts: 0 };
    disarmPostPlaceEscapeUndo();
    const runResult = {
      createdCount: 0,
      createdIds: [],
      sourceId: String(selected.id || ""),
      points: [{ lat: Number(selected.center?.lat || 0), lng: Number(selected.center?.lng || 0) }],
      basePoly: { ...selected },
    };
    const commitResult = await commitGeometryRunToRecords(runResult);
    const upserts = Number(commitResult?.upserts || 0);
    const links = Array.isArray(commitResult?.links) ? commitResult.links : [];
    const link = links[0] || null;
    state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
      String(poly.id || "") === String(selected.id || "")
        ? {
          ...poly,
          committed: true,
          locked: true,
          linkedMeasurementId: String(link?.measurementId || poly.linkedMeasurementId || ""),
          linkedSlipId: String(link?.slipId || poly.linkedSlipId || ""),
        }
        : poly
    ));
    synchronizeCommittedGeometryFromRecords();
    clearGeometrySelection();
    persist();
    status = `Committed 1 slip polygon and upserted ${upserts} slip record.`;
    render();
    return { upserts };
  }

  function buildLineWkt(points = []) {
    const clean = (Array.isArray(points) ? points : [])
      .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
      .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));
    if (clean.length < 2) return "";
    const body = clean.map(([lat, lng]) => `${Number(lng).toFixed(6)} ${Number(lat).toFixed(6)}`).join(", ");
    return `LINESTRING(${body})`;
  }

  async function commitLineFeatureFromCurrentLine() {
    if (lineEndpoints.length < 2) {
      status = "Draw a line first (start and end) before committing side-tie feature.";
      render();
      return;
    }
    const start = lineEndpoints[0];
    const end = lineEndpoints[lineEndpoints.length - 1];
    const lineWkt = buildLineWkt([[start.lat, start.lng], [end.lat, end.lng]]);
    if (!lineWkt) return;
    const linearFeet = roundToTenthFoot(toFeetDistance(L.latLng(start.lat, start.lng), L.latLng(end.lat, end.lng)));
    const slipId = String(buildSlipIdFromState(state) || "").trim() || `SIDE-${Date.now()}`;
    // Always creates a new record -- this is the only call site, always "commit whatever's
    // currently drawn," never a re-edit of an existing side-tie -- so any record already
    // holding this slip_id is a genuine name collision to guard against, not an update target
    // (see conflictingSlipIdsMessage; this used to look existing up by slip_id text and
    // silently overwrite it).
    const conflictMsg = conflictingSlipIdsMessage([slipId]);
    if (conflictMsg) throw new Error(conflictMsg);
    const payload = {
      measurement_id: makeId("MEAS"),
      marina_id: state.marinaId,
      marina_name: state.marinaName,
      slip_id: slipId,
      dock_name: state.dockName || null,
      suffix: state.slipIdSuffix || null,
      region: state.region || null,
      space_type: state.spaceType || null,
      multislip_yn: "no",
      multislip_id: null,
      covered_yn: draftCovered ? "yes" : "no",
      boatlift_yn: draftBoatlift ? "yes" : "no",
      fueldock_yn: draftFuelDock ? "yes" : "no",
      ...draftOutOfServiceFields(),
      marina_comments: null,
      measured_linear_feet: roundToTenthFoot(linearFeet),
      measured_width_feet: null,
      measured_depth_feet: null,
      operational_status: "operable",
      notes: draftComments || "",
      measurement_date: state.measurementDate,
      confidence_score: state.confidenceScore,
      record_created_timestamp: new Date().toISOString(),
      geometry_wkt: lineWkt,
    };
    await requestJson(API_BASE, { method: "POST", body: payload });
    incrementSlipIdNumber(state);
    incrementSlipIdSuffix(state);
    await refreshRecords({ quiet: true });
    pushUndoEntry(`Drew slip ${slipId} (Line)`, [{ type: "create", measurementId: payload.measurement_id }]);
    status = `Committed side-tie line feature ${slipId} (${linearFeet.toFixed(1)} ft).`;
    render();
  }

  // Status line for a length/width keyboard nudge, matching the pre-existing single-select
  // wording ("Run length: X ft") when there's only one selected slip, and switching to a
  // delta-worded message once 2+ are selected -- there's no single resulting "X ft" to
  // report there since updateSelectedGeometry({relative:true}) grew each one from its own
  // prior size, not a shared value.
  function multiSelectNudgeStatus(label, deltaFt, primaryOldValueFt, suffix = "") {
    const count = getSelectedGeometryIds().length;
    if (count > 1) {
      const sign = deltaFt >= 0 ? "+" : "";
      return `Run ${label.toLowerCase()}: ${sign}${deltaFt.toFixed(1)} ft on ${count} selected slips${suffix}.`;
    }
    const nextFt = Math.round((Number(primaryOldValueFt || 0) + deltaFt) * 10) / 10;
    return `Run ${label.toLowerCase()}: ${nextFt.toFixed(1)} ft${suffix}`;
  }

  function performRunControl(action) {
    const selected = selectedGeometryPolygon();
    const draft = activeGeometryPlaceMode ? geometryPlaceDraft : null;
    const rotateStep = rotationToleranceStep();
    const delta = 0.1;
    const key = String(action || "").trim().toLowerCase();
    const updateDraft = (changes = {}) => {
      if (!draft) return false;
      const draftRing = Array.isArray(draft?.ringLatLngs) ? draft.ringLatLngs : null;
      const draftInferred = draftRing && draftRing.length >= 3 ? inferPolyMetricsFromRing(draftRing) : null;
      const currentLengthFt = Number(draftInferred?.lengthFt || draft.lengthFt || 40);
      const currentWidthFt = Number(draftInferred?.widthFt || draft.widthFt || 14);
      const currentHeadingDeg = Number(draftInferred?.headingDeg ?? draft.headingDeg ?? 0);
      const nextLengthFt = changes.lengthFt == null ? currentLengthFt : Number(changes.lengthFt);
      const nextWidthFt = changes.widthFt == null ? currentWidthFt : Number(changes.widthFt);
      const nextHeadingDeg = changes.headingDeg == null ? currentHeadingDeg : Number(changes.headingDeg || 0);
      const ringSourceCenter = draft?.sourceCenter || null;
      const hasRing = Array.isArray(draft?.ringLatLngs) && draft.ringLatLngs.length >= 3 && ringSourceCenter;
      const changedShapeParams = (
        changes.lengthFt != null
        || changes.widthFt != null
        || changes.headingDeg != null
      );
      const transformedRing = (hasRing && changedShapeParams)
        ? transformRingLatLngsWithMetrics({
          ringLatLngs: draft.ringLatLngs,
          oldCenter: ringSourceCenter,
          newCenter: ringSourceCenter,
          oldHeadingDeg: currentHeadingDeg,
          newHeadingDeg: nextHeadingDeg,
          oldLengthFt: currentLengthFt,
          newLengthFt: nextLengthFt,
          oldWidthFt: currentWidthFt,
          newWidthFt: nextWidthFt,
        })
        : null;
      geometryPlaceDraft = {
        ...draft,
        ...changes,
        lengthFt: nextLengthFt,
        widthFt: nextWidthFt,
        headingDeg: nextHeadingDeg,
        ringLatLngs: transformedRing || draft.ringLatLngs || null,
        sourceCenter: (transformedRing || draft.ringLatLngs) ? (ringSourceCenter || null) : null,
      };
      render();
      return true;
    };
    if (!key) return false;
    const useDraftOnly = !!draft;
    if (key === "q") {
      if (useDraftOnly) {
        updateDraft({ headingDeg: Number(draft.headingDeg || 0) - rotateStep });
        status = `Draft rotation: ${(Number(draft.headingDeg || 0) - rotateStep).toFixed(1)}deg`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ headingDeg: Number(selected.headingDeg || 0) - rotateStep });
      status = `Run rotation: ${(Number(selected.headingDeg || 0) - rotateStep).toFixed(1)}deg`;
      render();
      return true;
    }
    if (key === "w") {
      state.slipGeometryRunBetweenCount = Math.max(1, Number(state.slipGeometryRunBetweenCount || 1) + 1);
      persist();
      status = `Run number of slips: ${geometryRunTotalCount()}.`;
      render();
      return true;
    }
    if (key === "e") {
      if (useDraftOnly) {
        updateDraft({ headingDeg: Number(draft.headingDeg || 0) + rotateStep });
        status = `Draft rotation: ${(Number(draft.headingDeg || 0) + rotateStep).toFixed(1)}deg`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ headingDeg: Number(selected.headingDeg || 0) + rotateStep });
      status = `Run rotation: ${(Number(selected.headingDeg || 0) + rotateStep).toFixed(1)}deg`;
      render();
      return true;
    }
    if (key === "a") {
      if (useDraftOnly) {
        updateDraft({ lengthFt: Number(draft.lengthFt || 0) - delta });
        status = `Draft length: ${(Math.round((Number(draft.lengthFt || 0) - delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ lengthFt: -delta }, { relative: true });
      status = multiSelectNudgeStatus("Length", -delta, selected.lengthFt);
      render();
      return true;
    }
    if (key === "s") {
      if (useDraftOnly) return false;
      if (!selected) return false;
      if (!geometryRunLayoutActive) {
        startGeometryRunLayout();
        return true;
      }
      if (geometryRunEndpoints.length >= 2) {
        generateRunAndPersist().catch((error) => {
          status = error?.message || "Failed to generate run records.";
          render();
        });
        return true;
      }
      status = "Run layout active: click end slip location first.";
      render();
      return true;
    }
    if (key === "d") {
      if (useDraftOnly) {
        updateDraft({ lengthFt: Number(draft.lengthFt || 0) + delta });
        status = `Draft length: ${(Math.round((Number(draft.lengthFt || 0) + delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ lengthFt: delta }, { relative: true });
      status = multiSelectNudgeStatus("Length", delta, selected.lengthFt);
      render();
      return true;
    }
    if (key === "z") {
      if (useDraftOnly) {
        updateDraft({ widthFt: Number(draft.widthFt || 0) - delta });
        status = `Draft width: ${(Math.round((Number(draft.widthFt || 0) - delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ widthFt: -delta }, { relative: true });
      status = multiSelectNudgeStatus("Width", -delta, selected.widthFt);
      render();
      return true;
    }
    if (key === "x") {
      if (useDraftOnly) return false;
      state.slipGeometryRunBetweenCount = Math.max(1, Number(state.slipGeometryRunBetweenCount || 1) - 1);
      persist();
      status = `Run number of slips: ${geometryRunTotalCount()}.`;
      render();
      return true;
    }
    if (key === "c") {
      if (useDraftOnly) {
        updateDraft({ widthFt: Number(draft.widthFt || 0) + delta });
        status = `Draft width: ${(Math.round((Number(draft.widthFt || 0) + delta) * 10) / 10).toFixed(1)} ft`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ widthFt: delta }, { relative: true });
      status = multiSelectNudgeStatus("Width", delta, selected.widthFt);
      render();
      return true;
    }
    return false;
  }

  function isTypingTarget(target) {
    const el = target && target.nodeType === 1 ? target : null;
    if (!el) return false;
    const tag = String(el.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || tag === "select") return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function performWheelAdjust(slot, direction, { fine = false, coarse = false } = {}) {
    const s = Number(slot);
    const dir = Number(direction);
    if (!Number.isFinite(s) || !Number.isFinite(dir) || dir === 0) return false;
    const selected = selectedGeometryPolygon();
    const draft = activeGeometryPlaceMode ? geometryPlaceDraft : null;
    // When the draft was pasted from a copied polygon it carries ringLatLngs/sourceCenter
    // whose inferred metrics override the draft's stored lengthFt/widthFt/headingDeg in
    // renderGeometryPlacePreview — making wheel adjustments invisible.  Resolve the ring
    // to effective parametric values up-front and drop the ring on first adjustment so the
    // preview switches to parametric rendering where changes are visible immediately.
    const draftRingInferred = draft && Array.isArray(draft.ringLatLngs) && draft.ringLatLngs.length >= 3
      ? inferPolyMetricsFromRing(draft.ringLatLngs)
      : null;
    const effectiveLength = Number(draftRingInferred?.lengthFt ?? draft?.lengthFt ?? 40);
    const effectiveWidth = Number(draftRingInferred?.widthFt ?? draft?.widthFt ?? 14);
    const effectiveHeading = Number(draftRingInferred?.headingDeg ?? draft?.headingDeg ?? 0);
    const updateDraft = (changes = {}) => {
      if (!draft) return false;
      // Drop the ring so parametric values are used by the preview going forward
      const { ringLatLngs: _r, sourceCenter: _s, ...draftBase } = draft;
      geometryPlaceDraft = {
        ...draftBase,
        lengthFt: effectiveLength,
        widthFt: effectiveWidth,
        headingDeg: effectiveHeading,
        ...changes,
      };
      render();
      return true;
    };
    const useDraftOnly = !!draft;
    const lengthStep = coarse ? 1 : fine ? 0.01 : 0.1;
    const widthStep = coarse ? 1 : fine ? 0.01 : 0.1;
    const rotationStep = coarse ? 5 : fine ? rotationToleranceStep() : 2;
    const modeTag = coarse ? " [coarse]" : fine ? " [fine]" : "";

    if (s === 1) {
      const delta = dir > 0 ? lengthStep : -lengthStep;
      if (useDraftOnly) {
        updateDraft({ lengthFt: effectiveLength + delta });
        status = `Draft length: ${(Math.round((effectiveLength + delta) * 10) / 10).toFixed(1)} ft${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ lengthFt: delta }, { relative: true });
      status = multiSelectNudgeStatus("Length", delta, selected.lengthFt, modeTag);
      render();
      return true;
    }
    if (s === 2) {
      const delta = dir > 0 ? widthStep : -widthStep;
      if (useDraftOnly) {
        updateDraft({ widthFt: effectiveWidth + delta });
        status = `Draft width: ${(Math.round((effectiveWidth + delta) * 10) / 10).toFixed(1)} ft${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ widthFt: delta }, { relative: true });
      status = multiSelectNudgeStatus("Width", delta, selected.widthFt, modeTag);
      render();
      return true;
    }
    if (s === 3) {
      const delta = dir > 0 ? rotationStep : -rotationStep;
      if (useDraftOnly) {
        updateDraft({ headingDeg: effectiveHeading + delta });
        status = `Draft rotation: ${(effectiveHeading + delta).toFixed(1)}deg${modeTag}`;
        return true;
      }
      if (!selected) return false;
      updateSelectedGeometry({ headingDeg: Number(selected.headingDeg || 0) + delta });
      status = `Run rotation: ${(Number(selected.headingDeg || 0) + delta).toFixed(1)}deg${modeTag}`;
      render();
      return true;
    }
    return false;
  }

  function shouldIgnoreGeometryHotkeys(event) {
    if (isTypingTarget(event?.target)) return true;
    if (isTypingTarget(document?.activeElement)) return true;
    return false;
  }

  function startGeometryRunLayout() {
    const selected = selectedGeometryPolygon();
    if (!selected) return;
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryPlaceMode();
    stopGeometryRunLayout("replaced");
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    geometryRunLayoutActive = true;
    geometryRunEndpoints = [{ lat: Number(selected.center?.lat || 0), lng: Number(selected.center?.lng || 0) }];
    geometryRunHoverPoint = { ...geometryRunEndpoints[0] };
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    let runPreviewRafPending = false;
    const onPointerMove = (event) => {
      if (geometryRunLayoutActive && geometryRunEndpoints.length === 1) {
        const latlng = map?.mouseEventToLatLng?.(event);
        if (latlng) {
          geometryRunHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          if (!runPreviewRafPending) {
            runPreviewRafPending = true;
            requestAnimationFrame(() => { runPreviewRafPending = false; renderGeometryRunPreview(); });
          }
        }
      }
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCapture = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCapture) return;
      if (geometryRunEndpoints.length === 1) {
        geometryRunEndpoints = [geometryRunEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
        geometryRunHoverPoint = null;
        status = `Run preview ready with ${geometryRunTotalCount()} slips.`;
      }
      render();
    };
    const onKey = (event) => {
      if (geometryRunLayoutActive) {
        const handled = performRunControl(event.key);
        if (handled) {
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
      }
      if (event.key === "Enter" && geometryRunEndpoints.length >= 2) {
        event.preventDefault?.();
        generateRunAndPersist().catch((error) => {
          status = error?.message || "Failed to generate run records.";
          render();
        });
        return;
      }
      if (event.key === "Escape") {
        stopGeometryRunLayout("cancelled");
      }
    };
    geometryRunLayoutOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      window.removeEventListener("keydown", onKey, true);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    window.addEventListener("keydown", onKey, true);
    status = "Run layout active: existing selected polygon is slip #1. Click end slip location. Press Enter to generate or Esc to cancel.";
    render();
  }

  async function applyGeometryToSlipRecords() {
    let matched = 0;
    let unmatched = 0;
    const polygons = state.slipGeometryPolygons.map((poly) => {
      const ring = computeSlipPolygonLatLngs(poly);
      const inferred = ring.length >= 3 ? inferPolyMetricsFromRing(ring) : null;
      return {
        poly,
        ring,
        centerLL: L.latLng(Number(poly.center?.lat || 0), Number(poly.center?.lng || 0)),
        lengthFt: inferred?.lengthFt ?? poly.lengthFt ?? null,
        widthFt: inferred?.widthFt ?? poly.widthFt ?? null,
      };
    });
    // Direct link lookup by measurementId (set on polygons at commit time)
    const directByMeasurementId = new Map();
    for (const entry of polygons) {
      const mid = String(entry.poly?.linkedMeasurementId || "");
      if (mid) directByMeasurementId.set(mid, entry);
    }
    for (const record of records) {
      const measurementId = String(record?.measurement_id || "");
      // Primary: use the direct polygon↔record link established at commit time
      let hit = directByMeasurementId.get(measurementId) || null;
      if (!hit) {
        // Geometric fallback for records without a direct link
        const point = pointFromAnyWkt(record.geometry_wkt);
        if (point) {
          hit = polygons.find((entry) => pointInPolygon(point, entry.ring)) || null;
          if (!hit && polygons.length) {
            let nearest = null;
            for (const candidate of polygons) {
              const d = toFeetDistance(L.latLng(point.lat, point.lng), candidate.centerLL);
              if (!nearest || d < nearest.distanceFt) nearest = { entry: candidate, distanceFt: d };
            }
            if (nearest && nearest.distanceFt <= 8) hit = nearest.entry;
          }
        }
      }
      if (!hit) {
        unmatched += 1;
        await requestJson(`${API_BASE}/${encodeURIComponent(record.measurement_id)}`, {
          method: "PATCH",
          body: { ...record, measured_linear_feet: null, measured_width_feet: null },
        });
        continue;
      }
      matched += 1;
      await requestJson(`${API_BASE}/${encodeURIComponent(record.measurement_id)}`, {
        method: "PATCH",
        body: {
          ...record,
          measured_linear_feet: roundToTenthFoot(hit.lengthFt),
          measured_width_feet: roundToTenthFoot(hit.widthFt),
        },
      });
    }
    await refreshRecords({ quiet: true });
    status = `Geometry assignment complete. Matched ${matched}, unmatched ${unmatched}.`;
    render();
  }

  async function removeSelectedGeometryPolygon({ deleteLinkedRecords = true } = {}) {
    const selectedIds = new Set(getSelectedGeometryIds());
    if (!selectedIds.size) return 0;
    const removedPolys = state.slipGeometryPolygons.filter((poly) => selectedIds.has(String(poly.id)));
    if (deleteLinkedRecords) {
      const deletedMeasurementIds = new Set();
      const undoChanges = [];
      for (const poly of removedPolys) {
        const measurementId = String(poly?.linkedMeasurementId || "");
        if (!measurementId || deletedMeasurementIds.has(measurementId)) continue;
        const deletedBefore = snapshotRecord(measurementId);
        await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "DELETE" });
        deletedMeasurementIds.add(measurementId);
        if (deletedBefore) undoChanges.push({ type: "delete", measurementId, before: deletedBefore });
      }
      if (deletedMeasurementIds.size) {
        app.log("slip.deleted", { count: deletedMeasurementIds.size, marina: state.marinaId });
        records = records.filter((record) => !deletedMeasurementIds.has(String(record?.measurement_id || ""))); recordsVersion++;
        pushUndoEntry(`Deleted ${deletedMeasurementIds.size} slip${deletedMeasurementIds.size === 1 ? "" : "s"}`, undoChanges);
      }
    }
    const before = state.slipGeometryPolygons.length;
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => !selectedIds.has(String(poly.id)));
    const removed = before - state.slipGeometryPolygons.length;
    if (removed > 0) {
      clearGeometrySelection();
      synchronizeCommittedGeometryFromRecords();
      persist();
    }
    return removed;
  }

  function disarmPostPlaceEscapeUndo() {
    postPlaceUndoPolyId = null;
  }

  function armPostPlaceEscapeUndo(polyId) {
    postPlaceUndoPolyId = String(polyId || "") || null;
  }

  function isGeometryPolygonCommitted(poly) {
    const linkedMeasurementId = String(poly?.linkedMeasurementId || "");
    if (linkedMeasurementId) {
      return records.some((record) => String(record?.measurement_id || "") === linkedMeasurementId);
    }
    const linkedSlipId = String(poly?.linkedSlipId || "");
    if (linkedSlipId) {
      const linkedSlipRecords = records.filter((record) => String(record?.slip_id || "") === linkedSlipId);
      if (linkedSlipRecords.length === 1) return true;
    }
    return false;
  }

  // Cycles through ALL uncommitted polygons on repeated clicks (not just the
  // last one) so every orphaned draft can be found and reviewed, not just
  // whichever happens to be newest -- and pans the map to whichever gets
  // selected, since an uncommitted polygon has no badge/table row to click
  // and so is otherwise invisible if it's outside the current viewport.
  let _uncommittedCycleIndex = -1;
  function selectLatestUncommittedGeometry() {
    const uncommitted = state.slipGeometryPolygons.filter((poly) => !isGeometryPolygonCommitted(poly));
    if (!uncommitted.length) { _uncommittedCycleIndex = -1; return false; }
    _uncommittedCycleIndex = (_uncommittedCycleIndex + 1) % uncommitted.length;
    const candidate = uncommitted[_uncommittedCycleIndex];
    setSelectedGeometryIds([String(candidate.id)], String(candidate.id));
    status = `Selected uncommitted polygon ${_uncommittedCycleIndex + 1} of ${uncommitted.length} (${candidate.id}).`;
    const ring = computeSlipPolygonLatLngs(candidate);
    if (ring.length) {
      const lat = ring.reduce((sum, p) => sum + p[0], 0) / ring.length;
      const lng = ring.reduce((sum, p) => sum + p[1], 0) / ring.length;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        map.setView([lat, lng], Math.max(19, Number(map.getZoom?.() || 19)));
      }
    }
    persist();
    render();
    return true;
  }

  function clearUncommittedGeometryPolygons() {
    const before = state.slipGeometryPolygons.length;
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => isGeometryPolygonCommitted(poly));
    const removed = before - state.slipGeometryPolygons.length;
    normalizeGeometrySelection();
    persist();
    render();
    return removed;
  }

  function purgeStaleGeometryPolygons() {
    const before = state.slipGeometryPolygons.length;
    state.slipGeometryPolygons = state.slipGeometryPolygons.filter((poly) => isGeometryPolygonCommitted(poly));
    const removed = before - state.slipGeometryPolygons.length;
    clearGeometrySelection();
    stopGeometryPlaceMode();
    stopGeometryRunLayout("purged");
    persist();
    render();
    return removed;
  }

  // Lookup caches: record⇄polygon matching used to linear-scan on every call,
  // which is O(records × polygons) per render pass once both grow into the
  // hundreds. Both arrays are only ever reassigned (never mutated in place),
  // so a reference/version check is a sound invalidation key.
  let _recordLookupCache = null;
  function getRecordLookup() {
    if (_recordLookupCache && _recordLookupCache.version === recordsVersion && _recordLookupCache.ref === records) {
      return _recordLookupCache;
    }
    const byMeasurementId = new Map();
    const bySlipId = new Map();
    for (const record of records) {
      const mid = String(record?.measurement_id || "");
      if (mid && !byMeasurementId.has(mid)) byMeasurementId.set(mid, record);
      const sid = String(record?.slip_id || "").trim();
      if (sid) {
        const bucket = bySlipId.get(sid);
        if (bucket) bucket.push(record); else bySlipId.set(sid, [record]);
      }
    }
    _recordLookupCache = { version: recordsVersion, ref: records, byMeasurementId, bySlipId };
    return _recordLookupCache;
  }

  let _polyLookupCache = null;
  function getPolyLookup() {
    const polys = state.slipGeometryPolygons;
    if (_polyLookupCache && _polyLookupCache.ref === polys) return _polyLookupCache;
    const byMeasurementId = new Map();
    const bySlipId = new Map();
    for (const poly of polys) {
      const mid = String(poly?.linkedMeasurementId || "").trim();
      if (mid && !byMeasurementId.has(mid)) byMeasurementId.set(mid, poly);
      const sid = String(poly?.linkedSlipId || "").trim();
      if (sid) {
        const bucket = bySlipId.get(sid);
        if (bucket) bucket.push(poly); else bySlipId.set(sid, [poly]);
      }
    }
    _polyLookupCache = { ref: polys, byMeasurementId, bySlipId, rings: null };
    return _polyLookupCache;
  }
  function getPolyRings(lookup) {
    if (!lookup.rings) {
      lookup.rings = state.slipGeometryPolygons.map((poly) => ({ poly, ring: computeSlipPolygonLatLngs(poly) }));
    }
    return lookup.rings;
  }

  function findRecordForPolygon(poly) {
    const lookup = getRecordLookup();
    const measurementId = String(poly?.linkedMeasurementId || "").trim();
    if (measurementId) {
      const direct = lookup.byMeasurementId.get(measurementId) || null;
      if (direct) return direct;
    }
    const slipId = String(poly?.linkedSlipId || "").trim();
    const marinaId = String(state.marinaId || "").trim();
    if (!slipId) return null;
    const candidates = lookup.bySlipId.get(slipId) || [];
    return candidates.find((record) => (!marinaId || String(record?.marina_id || "").trim() === marinaId)) || null;
  }

  function isRecordVisible(record) {
    const mode = String(state.colorSlipsBy || "none");
    const hidden = state.filterHiddenValues?.[mode];
    if (!hidden || !Array.isArray(hidden) || hidden.length === 0) return true;
    const hiddenSet = new Set(hidden);
    if (mode === "attribute") return !hiddenSet.has(currentAttributeColoring().keyFor(record));
    if (mode === "confidence") return !hiddenSet.has(String(record?.confidence_score || "High"));
    if (mode === "space_type") return !hiddenSet.has(String(record?.space_type || "").trim() || "unknown");
    if (mode === "multislip") return !hiddenSet.has(String(record?.multislip_id || "").trim() || "none");
    if (mode === "region") return !hiddenSet.has(String(record?.region || "").trim() || "unassigned");
    if (mode === "marina_name") return !hiddenSet.has(String(record?.marina_name || "").trim() || "unassigned");
    if (mode === "dock") return !hiddenSet.has(String(record?.dock_name || "").trim() || "unassigned");
    if (mode === "multislip_integrity") return !hiddenSet.has(multislipIntegrityKeyForRecord(record, multislipIntegrityStatusMap));
    if (mode === "duplicate_slips") return !hiddenSet.has(duplicateSlipKeyForRecord(record, overlappingSlipIds));
    if (mode === "duplicate_slip_id") return !hiddenSet.has(duplicateSlipIdKeyForRecord(record, duplicateSlipIdSet));
    if (mode === "dimension") {
      const tol = Number(state?.displayMeasurementToleranceFt) >= 1 ? 1 : 0.5;
      const l = Number(record?.measured_linear_feet);
      const w = Number(record?.measured_width_feet);
      if (!Number.isFinite(l) || !Number.isFinite(w)) return true;
      const key = dimensionKey(l, w, tol);
      return !key || !hiddenSet.has(key);
    }
    return true;
  }

  function dimensionInfoForPolygon(poly) {
    const tol = Number(state?.displayMeasurementToleranceFt) >= 1 ? 1 : 0.5;
    const record = findRecordForPolygon(poly);
    const lengthForColor = Number(record?.measured_linear_feet);
    const widthForColor = Number(record?.measured_width_feet);
    const key = Number.isFinite(lengthForColor) && Number.isFinite(widthForColor)
      ? dimensionKey(lengthForColor, widthForColor, tol)
      : dimensionKey(poly?.lengthFt, poly?.widthFt, tol);
    const color = key ? paletteColorForKey(key) : "#38bdf8";
    return {
      key,
      color,
      label: key ? `${key} ft` : "Unknown",
    };
  }

  // QC view: flags slips whose footprint is wider than it is long -- usually a sign length/
  // width got swapped or measured wrong, since a real slip is normally length-dominant.
  // Falls back to the polygon's own drawn dimensions (poly.lengthFt/widthFt) for an
  // uncommitted draft with no record yet, same fallback dimensionInfoForPolygon uses.
  function qcOrientationInfoForPolygon(poly) {
    const record = findRecordForPolygon(poly);
    const l = Number(record?.measured_linear_feet ?? poly?.lengthFt);
    const w = Number(record?.measured_width_feet ?? poly?.widthFt);
    if (!Number.isFinite(l) || !Number.isFinite(w)) {
      return { key: "unknown", color: "#64748b", label: "Missing length/width" };
    }
    if (l > w) return { key: "length_gt_width", color: "#38bdf8", label: "Length > Width" };
    if (w > l) return { key: "width_gt_length", color: "#eab308", label: "Width > Length" };
    return { key: "equal", color: "#64748b", label: "Length = Width" };
  }

  // Local-planar (tangent-plane) conversion of a polygon's ring to feet, anchored at a shared
  // reference point -- same 111320-m-per-degree-latitude approximation used everywhere else in
  // this file for small-scale distances (see e.g. mitreChainRing/rotatePointAround above).
  function polygonRingLocalFt(poly, anchor) {
    const ring = computeSlipPolygonLatLngs(poly);
    const cosLat = Math.max(0.000001, Math.cos((anchor.lat * Math.PI) / 180));
    return (ring || []).map(([lat, lng]) => ({
      x: metersToFeet((lng - anchor.lng) * cosLat * 111320),
      y: metersToFeet((lat - anchor.lat) * 111320),
    }));
  }

  function pointToSegmentDistFt(p, a, b) {
    const abx = b.x - a.x, aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby;
    const t = lenSq > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / lenSq)) : 0;
    const cx = a.x + t * abx, cy = a.y + t * aby;
    return Math.hypot(p.x - cx, p.y - cy);
  }

  function ringsMinDistanceFt(ringA, ringB) {
    let min = Infinity;
    for (const p of ringA) {
      for (let i = 0; i < ringB.length; i++) {
        min = Math.min(min, pointToSegmentDistFt(p, ringB[i], ringB[(i + 1) % ringB.length]));
      }
    }
    for (const p of ringB) {
      for (let i = 0; i < ringA.length; i++) {
        min = Math.min(min, pointToSegmentDistFt(p, ringA[i], ringA[(i + 1) % ringA.length]));
      }
    }
    return min;
  }

  // Two slip footprints count as physically adjacent (a shared dock wall -- legitimately one
  // multislip unit) when the closest point between their boundaries is within this gap. Covers
  // ordinary survey/drawing slack between touching rectangles without treating two merely
  // nearby-but-separate slips as connected.
  const MULTISLIP_ADJACENT_GAP_FT = 3;

  function polygonsAdjacent(polyA, polyB, anchor) {
    const ringA = polygonRingLocalFt(polyA, anchor);
    const ringB = polygonRingLocalFt(polyB, anchor);
    if (!ringA.length || !ringB.length) return false;
    return ringsMinDistanceFt(ringA, ringB) <= MULTISLIP_ADJACENT_GAP_FT;
  }

  // "Multislip Integrity" coloring: every committed slip with a multislip_id must have at
  // least one partner sharing that id (a lone multislip_id is almost certainly a mistake --
  // same "orphan" concept consolidateMultislipIds already flags), and every slip in a
  // multislip_id group must be reachable from every other member through a chain of
  // physically adjacent slips in that same group -- two slips sharing an id despite sitting in
  // disconnected parts of the marina is also almost certainly a mistake (two unrelated groups
  // that collided on the same id, or a typo). Always computed from the full committed set,
  // independent of the on-screen visibility filter or (for exportPlotSvg) a region scope --
  // this is a structural QC check, not a display concern.
  function computeMultislipIntegrityStatus() {
    const statusByMeasurementId = new Map();
    const withRecord = (state.slipGeometryPolygons || [])
      .filter((poly) => poly.committed)
      .map((poly) => ({ poly, record: findRecordForPolygon(poly) }))
      .filter(({ record }) => record);

    const groups = new Map();
    for (const item of withRecord) {
      const mid = String(item.record?.multislip_id || "").trim();
      if (!mid) continue;
      if (!groups.has(mid)) groups.set(mid, []);
      groups.get(mid).push(item);
    }

    for (const items of groups.values()) {
      if (items.length === 1) {
        statusByMeasurementId.set(String(items[0].record.measurement_id), "orphan");
        continue;
      }
      const anchorLL = computeSlipPolygonLatLngs(items[0].poly)?.[0];
      const anchor = anchorLL ? { lat: anchorLL[0], lng: anchorLL[1] } : { lat: 0, lng: 0 };
      const n = items.length;
      const parent = Array.from({ length: n }, (_, i) => i);
      const find = (i) => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
      const union = (a, b) => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra] = rb; };
      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          if (polygonsAdjacent(items[i].poly, items[j].poly, anchor)) union(i, j);
        }
      }
      const connected = new Set(items.map((_, i) => find(i))).size === 1;
      for (const item of items) {
        statusByMeasurementId.set(String(item.record.measurement_id), connected ? "valid" : "disconnected");
      }
    }
    return statusByMeasurementId;
  }

  const MULTISLIP_INTEGRITY_INFO = {
    none:         { color: "#64748b", label: "No Multislip" },
    valid:        { color: "#22c55e", label: "Valid" },
    orphan:       { color: "#ef4444", label: "Orphan (no partner)" },
    disconnected: { color: "#f97316", label: "Disconnected" },
    missing_id:   { color: "#eab308", label: "Flagged, no Multi-slip ID" },
  };

  function multislipIntegrityKeyForRecord(record, statusMap) {
    const mid = String(record?.multislip_id || "").trim();
    if (!mid) {
      // multislip_yn checked but no multislip_id set -- e.g. via manual edit of the Multi-slip
      // checkbox in the inventory table -- is its own inconsistency, distinct from a slip that
      // was never marked as multi-slip at all (see "Clear Multislip Tag" at line ~7363, which
      // already treats this same combination as needing attention).
      return String(record?.multislip_yn || "no").trim().toLowerCase() === "yes" ? "missing_id" : "none";
    }
    return statusMap.get(String(record?.measurement_id)) || "valid";
  }

  function multislipIntegrityInfoForPolygon(poly) {
    const record = findRecordForPolygon(poly);
    const key = multislipIntegrityKeyForRecord(record, multislipIntegrityStatusMap);
    return { key, ...MULTISLIP_INTEGRITY_INFO[key] };
  }

  // "Duplicate/Overlap" coloring: two slips drawn on top of (or nearly on top of) each other
  // -- e.g. an accidental double-place -- stack their semi-transparent fills into a slightly
  // darker/off color that's easy to miss at normal zoom, but hard to see reliably. This finds
  // it geometrically (real polygon intersection area, not just "close together" -- two
  // legitimately adjacent slips only ever touch along a shared edge, which has zero area) so
  // it flags true overlap regardless of how subtle the visual stacking looks.

  function ringAreaFt2(ring) {
    let area = 0;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      area += (ring[j].x + ring[i].x) * (ring[i].y - ring[j].y);
    }
    return area / 2;
  }

  function ensureCCW(ring) {
    return ringAreaFt2(ring) < 0 ? [...ring].reverse() : ring;
  }

  function segmentIntersectFt(p1, p2, p3, p4) {
    const d1x = p2.x - p1.x, d1y = p2.y - p1.y;
    const d3x = p4.x - p3.x, d3y = p4.y - p3.y;
    const denom = d1x * d3y - d1y * d3x;
    if (Math.abs(denom) < 1e-9) return p2;
    const t = ((p3.x - p1.x) * d3y - (p3.y - p1.y) * d3x) / denom;
    return { x: p1.x + t * d1x, y: p1.y + t * d1y };
  }

  // Sutherland-Hodgman: clips convex `subject` against convex `clip` (both CCW). Works
  // unmodified for rotated rectangles/freeform quads since slip footprints are always convex.
  function clipConvexPolygonFt(subject, clip) {
    let output = subject;
    const n = clip.length;
    for (let i = 0; i < n && output.length; i++) {
      const a = clip[i], b = clip[(i + 1) % n];
      const input = output;
      output = [];
      const cross = (p) => (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
      for (let k = 0; k < input.length; k++) {
        const curr = input[k];
        const prev = input[(k - 1 + input.length) % input.length];
        const currInside = cross(curr) >= -1e-9;
        const prevInside = cross(prev) >= -1e-9;
        if (currInside) {
          if (!prevInside) output.push(segmentIntersectFt(prev, curr, a, b));
          output.push(curr);
        } else if (prevInside) {
          output.push(segmentIntersectFt(prev, curr, a, b));
        }
      }
    }
    return output;
  }

  function polygonIntersectionAreaFt2(ringA, ringB) {
    const clipped = clipConvexPolygonFt(ensureCCW(ringA), ensureCCW(ringB));
    return clipped.length < 3 ? 0 : Math.abs(ringAreaFt2(clipped));
  }

  function ringBoundsFt(ring) {
    const xs = ring.map((p) => p.x), ys = ring.map((p) => p.y);
    return { minX: Math.min(...xs), maxX: Math.max(...xs), minY: Math.min(...ys), maxY: Math.max(...ys) };
  }

  // Two slips count as duplicates/overlapping once their footprints share this much of the
  // smaller one's area -- high enough that two merely-adjacent slips (which share zero area,
  // only a boundary line) never trip it, low enough to catch a duplicate placed slightly off
  // from the original rather than only an exact on-top-of-each-other double.
  const OVERLAP_AREA_RATIO_THRESHOLD = 0.5;

  // Marina-wide: every committed slip pair whose footprints overlap by more than the
  // threshold gets both measurement_ids added to the result set. O(n^2) pairs but each
  // comparison is a cheap bounding-box reject before the real polygon clip, and slip counts
  // per marina are small enough (hundreds, occasionally low thousands) for this to be fine as
  // a manual, human-triggered color mode rather than a hot per-frame path.
  function computeOverlappingSlipIds() {
    const overlapping = new Set();
    const withRecord = (state.slipGeometryPolygons || [])
      .filter((poly) => poly.committed)
      .map((poly) => ({ poly, record: findRecordForPolygon(poly) }))
      .filter(({ record }) => record);
    if (withRecord.length < 2) return overlapping;

    const anchor = { lat: computeSlipPolygonLatLngs(withRecord[0].poly)?.[0]?.[0] ?? 0, lng: computeSlipPolygonLatLngs(withRecord[0].poly)?.[0]?.[1] ?? 0 };
    const prepared = withRecord.map(({ poly, record }) => {
      const ring = polygonRingLocalFt(poly, anchor);
      return { measurementId: String(record.measurement_id), ring, area: Math.abs(ringAreaFt2(ring)), bounds: ringBoundsFt(ring) };
    }).filter((p) => p.ring.length >= 3 && p.area > 0);

    for (let i = 0; i < prepared.length; i++) {
      const a = prepared[i];
      for (let j = i + 1; j < prepared.length; j++) {
        const b = prepared[j];
        if (a.bounds.maxX < b.bounds.minX || b.bounds.maxX < a.bounds.minX
          || a.bounds.maxY < b.bounds.minY || b.bounds.maxY < a.bounds.minY) continue;
        const inter = polygonIntersectionAreaFt2(a.ring, b.ring);
        if (inter / Math.min(a.area, b.area) >= OVERLAP_AREA_RATIO_THRESHOLD) {
          overlapping.add(a.measurementId);
          overlapping.add(b.measurementId);
        }
      }
    }
    return overlapping;
  }

  const DUPLICATE_SLIP_INFO = {
    clear:       { color: "#38bdf8", label: "Clear" },
    overlapping: { color: "#ef4444", label: "Overlapping" },
  };

  function duplicateSlipKeyForRecord(record, overlapSet) {
    return overlapSet.has(String(record?.measurement_id)) ? "overlapping" : "clear";
  }

  function duplicateSlipInfoForPolygon(poly) {
    const record = findRecordForPolygon(poly);
    const key = duplicateSlipKeyForRecord(record, overlappingSlipIds);
    return { key, ...DUPLICATE_SLIP_INFO[key] };
  }

  // Text match on slip_id, scoped by marina/region the same way conflictingSlipIdsMessage's
  // creation-time guard is -- the system allows two slips to share a slip_id when they're in
  // different regions/docks (see conflictingSlipIdsMessage), so this only flags a collision
  // when the slip_id AND marina/region all match, not a same-named slip in a different region.
  function computeDuplicateSlipIdSet() {
    const duplicate = new Set();
    const idsByKey = new Map();
    for (const poly of state.slipGeometryPolygons || []) {
      if (!poly.committed) continue;
      const record = findRecordForPolygon(poly);
      const slipId = String(record?.slip_id || "").trim();
      if (!slipId || !record) continue;
      const marina = String(record?.marina_id || "");
      const region = String(record?.region || "").trim();
      const key = `${marina}::${region}::${slipId}`;
      const list = idsByKey.get(key) || [];
      list.push(String(record.measurement_id));
      idsByKey.set(key, list);
    }
    for (const ids of idsByKey.values()) {
      if (ids.length > 1) ids.forEach((id) => duplicate.add(id));
    }
    return duplicate;
  }

  const DUPLICATE_SLIP_ID_INFO = {
    clear:     { color: "#38bdf8", label: "Clear" },
    duplicate: { color: "#ef4444", label: "Duplicate Slip ID" },
  };

  function duplicateSlipIdKeyForRecord(record, dupSet) {
    return dupSet.has(String(record?.measurement_id)) ? "duplicate" : "clear";
  }

  function duplicateSlipIdInfoForPolygon(poly) {
    const record = findRecordForPolygon(poly);
    const key = duplicateSlipIdKeyForRecord(record, duplicateSlipIdSet);
    return { key, ...DUPLICATE_SLIP_ID_INFO[key] };
  }

  function colorForPolygon(poly) {
    const mode = String(state.colorSlipsBy || "none");
    if (mode === "dimension") {
      return dimensionInfoForPolygon(poly).color;
    }
    if (mode === "orientation_qc") {
      return qcOrientationInfoForPolygon(poly).color;
    }
    if (mode === "confidence") {
      const record = findRecordForPolygon(poly);
      return confidenceColor(record?.confidence_score);
    }
    if (mode === "space_type") {
      const record = findRecordForPolygon(poly);
      const spaceType = String(record?.space_type || "").trim() || "unknown";
      return spaceTypeColorMap.get(spaceType) ?? "#38bdf8";
    }
    if (mode === "multislip") {
      const record = findRecordForPolygon(poly);
      const multislipId = String(record?.multislip_id || "").trim();
      // Ungrouped slips (no multislip_id) get a neutral color so grouped ones stand out.
      return multislipId ? (multislipColorMap.get(multislipId) ?? "#38bdf8") : "#64748b";
    }
    if (mode === "region") {
      const record = findRecordForPolygon(poly);
      const region = String(record?.region || "").trim() || "unassigned";
      return regionColorMap.get(region) ?? "#38bdf8";
    }
    if (mode === "marina_name") {
      const record = findRecordForPolygon(poly);
      const name = String(record?.marina_name || "").trim() || "unassigned";
      return marinaNameColorMap.get(name) ?? "#38bdf8";
    }
    if (mode === "dock") {
      const record = findRecordForPolygon(poly);
      const dock = String(record?.dock_name || "").trim() || "unassigned";
      return dockColorMap.get(dock) ?? "#38bdf8";
    }
    if (mode === "multislip_integrity") {
      return multislipIntegrityInfoForPolygon(poly).color;
    }
    if (mode === "duplicate_slips") {
      return duplicateSlipInfoForPolygon(poly).color;
    }
    if (mode === "duplicate_slip_id") {
      return duplicateSlipIdInfoForPolygon(poly).color;
    }
    if (mode === "attribute") {
      return currentAttributeColoring().colorFor(findRecordForPolygon(poly));
    }
    return "#38bdf8";
  }

  // Removes just the given polys' committed "Extend Edges" lines from slipDimensionsLayer
  // (tagged with _extPolyId when drawn, above) -- called once at dragstart so the stale,
  // pre-drag lines don't sit underneath slipLiveExtensionLayer's live-updated ones for the
  // whole drag. Leaves every other poly's dimension/extension lines alone.
  function hideCommittedExtensionLines(polyIds) {
    const idSet = new Set(polyIds.filter(Boolean).map(String));
    if (!idSet.size) return;
    slipDimensionsLayer.eachLayer((layer) => {
      if (idSet.has(String(layer._extPolyId || ""))) slipDimensionsLayer.removeLayer(layer);
    });
  }

  function renderGeometry() {
    normalizeGeometrySelection();
    if (!slipHandleDragging) geometryHandlesLayer.clearLayers();
    slipDimensionsLayer.clearLayers();
    slipLiveExtensionLayer.clearLayers();
    const showLength = !!state.showSlipLength;
    const showWidth = !!state.showSlipWidth;
    const showEdgeExtensions = !!state.showEdgeExtensions;
    const mode = String(state.colorSlipsBy || "none");
    if (mode === "space_type") {
      const allTypes = [...new Set(records.map((r) => String(r?.space_type || "").trim() || "unknown"))];
      spaceTypeColorMap = new Map(allTypes.map((t) => [t, SPACE_TYPE_COLOR_MAP[t] ?? paletteColorForKey(t)]));
    }
    if (mode === "multislip") {
      const allIds = [...new Set(records.map((r) => String(r?.multislip_id || "").trim()).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
      multislipColorMap = assignDistinctColorsByIndex(allIds);
    }
    if (mode === "region") {
      const allRegions = [...new Set(records.map((r) => String(r?.region || "").trim() || "unassigned"))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
      regionColorMap = assignDistinctColorsByIndex(allRegions);
    }
    if (mode === "marina_name") {
      const allNames = [...new Set(records.map((r) => String(r?.marina_name || "").trim() || "unassigned"))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
      marinaNameColorMap = assignDistinctColorsByIndex(allNames);
    }
    if (mode === "dock") {
      const allDocks = [...new Set(records.map((r) => String(r?.dock_name || "").trim() || "unassigned"))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }));
      dockColorMap = assignDockColors(allDocks);
    }
    if (mode === "multislip_integrity") {
      multislipIntegrityStatusMap = computeMultislipIntegrityStatus();
    }
    if (mode === "duplicate_slips") {
      overlappingSlipIds = computeOverlappingSlipIds();
    }
    if (mode === "duplicate_slip_id") {
      duplicateSlipIdSet = computeDuplicateSlipIdSet();
    }
    if (mode === "attribute") {
      attributeColoring = null;
      currentAttributeColoring();
    }
    const attributeLegendKeys = new Set();
    const dimensionLegendMap = new Map();
    const spaceTypeLegendMap = new Map();
    const multislipLegendMap = new Map();
    const qcLegendMap = new Map();
    const regionLegendMap = new Map();
    const marinaNameLegendMap = new Map();
    const dockLegendMap = new Map();
    const multislipIntegrityLegendMap = new Map();
    const duplicateSlipLegendMap = new Map();
    const duplicateSlipIdLegendMap = new Map();
    const activePolyIds = new Set();
    let createdNewPath = false;
    const _hiddenForMode = state.filterHiddenValues?.[mode];
    const _hasHiddenFilters = Array.isArray(_hiddenForMode) && _hiddenForMode.length > 0;

    state.slipGeometryPolygons.forEach((poly) => {
      if (poly.committed && _hasHiddenFilters) {
        const record = findRecordForPolygon(poly);
        if (record && !isRecordVisible(record)) return;
      }
      activePolyIds.add(poly.id);
      if (showLength || showWidth) {
        const dims = computeSlipDimensionLines(poly, findRecordForPolygon(poly));
        if (showLength && dims?.lengthLine) {
          L.polyline(dims.lengthLine, {
            color: SLIP_DIMENSION_LINE_COLORS.length, weight: 2, opacity: 0.85,
            dashArray: "6 5", lineCap: "round", interactive: false, renderer: canvasRenderer,
          }).addTo(slipDimensionsLayer);
        }
        if (showWidth && dims?.widthLine) {
          L.polyline(dims.widthLine, {
            color: SLIP_DIMENSION_LINE_COLORS.width, weight: 2, opacity: 0.85,
            dashArray: "6 5", lineCap: "round", interactive: false, renderer: canvasRenderer,
          }).addTo(slipDimensionsLayer);
        }
      }
      const selected = isGeometrySelected(poly.id);
      if (showEdgeExtensions && selected) {
        const extensionLines = computeSlipEdgeExtensionLines(poly, state.edgeExtensionFt);
        extensionLines.forEach((line) => {
          const extLayer = L.polyline(line, {
            color: SLIP_EDGE_EXTENSION_COLOR, weight: 1, opacity: 0.75,
            dashArray: "2 4", lineCap: "butt", interactive: false, renderer: canvasRenderer,
          }).addTo(slipDimensionsLayer);
          // Tagged so a drag's dragstart can pull just this poly's committed extension lines
          // back out of slipDimensionsLayer -- otherwise they'd sit frozen at the pre-drag
          // geometry underneath/alongside slipLiveExtensionLayer's live-updated ones for the
          // whole drag, doubling the guide line instead of it cleanly following the cursor.
          extLayer._extPolyId = poly.id;
        });
      }
      const dimInfo = mode === "dimension" ? dimensionInfoForPolygon(poly) : null;
      const qcInfo = mode === "orientation_qc" ? qcOrientationInfoForPolygon(poly) : null;
      const miInfo = mode === "multislip_integrity" ? multislipIntegrityInfoForPolygon(poly) : null;
      const dupInfo = mode === "duplicate_slips" ? duplicateSlipInfoForPolygon(poly) : null;
      const dupIdInfo = mode === "duplicate_slip_id" ? duplicateSlipIdInfoForPolygon(poly) : null;
      const baseColor = dimInfo ? dimInfo.color : qcInfo ? qcInfo.color : miInfo ? miInfo.color : dupInfo ? dupInfo.color : dupIdInfo ? dupIdInfo.color : colorForPolygon(poly);
      if (dimInfo?.key && !dimensionLegendMap.has(dimInfo.key)) {
        dimensionLegendMap.set(dimInfo.key, { key: dimInfo.key, color: dimInfo.color, label: dimInfo.label });
      }
      if (qcInfo?.key && !qcLegendMap.has(qcInfo.key)) {
        qcLegendMap.set(qcInfo.key, { key: qcInfo.key, color: qcInfo.color, label: qcInfo.label });
      }
      if (miInfo?.key && !multislipIntegrityLegendMap.has(miInfo.key)) {
        multislipIntegrityLegendMap.set(miInfo.key, { key: miInfo.key, color: miInfo.color, label: miInfo.label });
      }
      // Only "overlapping" gets a legend entry -- like multislip's ungrouped-gray, "everything
      // is clear" isn't worth a legend row.
      if (dupInfo?.key === "overlapping" && !duplicateSlipLegendMap.has(dupInfo.key)) {
        duplicateSlipLegendMap.set(dupInfo.key, { key: dupInfo.key, color: dupInfo.color, label: dupInfo.label });
      }
      // Only "duplicate" gets a legend entry -- same reasoning as duplicate_slips above.
      if (dupIdInfo?.key === "duplicate" && !duplicateSlipIdLegendMap.has(dupIdInfo.key)) {
        duplicateSlipIdLegendMap.set(dupIdInfo.key, { key: dupIdInfo.key, color: dupIdInfo.color, label: dupIdInfo.label });
      }
      if (mode === "attribute") {
        attributeLegendKeys.add(attributeColoring.keyFor(findRecordForPolygon(poly)));
      }
      if (mode === "space_type") {
        const record = findRecordForPolygon(poly);
        const spaceType = String(record?.space_type || "").trim() || "unknown";
        if (!spaceTypeLegendMap.has(spaceType)) {
          spaceTypeLegendMap.set(spaceType, { key: spaceType, color: spaceTypeColorMap.get(spaceType) ?? "#38bdf8", label: spaceType });
        }
      }
      if (mode === "multislip") {
        const record = findRecordForPolygon(poly);
        const multislipId = String(record?.multislip_id || "").trim();
        // Ungrouped slips don't get a legend entry -- the legend is for identifying groups,
        // and "everything gray" isn't a group.
        if (multislipId && !multislipLegendMap.has(multislipId)) {
          multislipLegendMap.set(multislipId, { key: multislipId, color: multislipColorMap.get(multislipId) ?? "#38bdf8", label: multislipId });
        }
      }
      if (mode === "region") {
        const record = findRecordForPolygon(poly);
        const region = String(record?.region || "").trim() || "unassigned";
        if (!regionLegendMap.has(region)) {
          regionLegendMap.set(region, { key: region, color: regionColorMap.get(region) ?? "#38bdf8", label: region });
        }
      }
      if (mode === "marina_name") {
        const record = findRecordForPolygon(poly);
        const name = String(record?.marina_name || "").trim() || "unassigned";
        if (!marinaNameLegendMap.has(name)) {
          marinaNameLegendMap.set(name, { key: name, color: marinaNameColorMap.get(name) ?? "#38bdf8", label: name });
        }
      }
      if (mode === "dock") {
        const record = findRecordForPolygon(poly);
        const dock = String(record?.dock_name || "").trim() || "unassigned";
        if (!dockLegendMap.has(dock)) {
          dockLegendMap.set(dock, { key: dock, color: dockColorMap.get(dock) ?? "#38bdf8", label: dock });
        }
      }
      const needsInteraction = selected || !poly.committed;
      // Uncommitted polygons (no database record yet) always render the same way the
      // pre-commit ghost preview did -- green, light fill -- regardless of selection.
      // "Selected" orange is reserved for real, committed records, where it actually means
      // something (this is the one you've picked from the inventory/table to edit).
      // Otherwise a freshly-placed draft (auto-selected by placeGeometryAt) looked like an
      // unrelated "selected" item instead of a continuation of the green draw preview.
      const isDraft = !poly.committed;
      const styleColor = isDraft ? "#10b981" : (selected ? "#f59e0b" : baseColor);
      const styleWeight = selected ? 3 : (poly.committed ? 1.5 : 2);
      const styleFillOpacity = isDraft ? 0.18 : (selected ? 0.35 : (poly.committed ? 0.28 : 0.22));
      const styleDash = poly.locked ? "5 4" : "";
      const ringKey = poly.freeform
        ? `f|${(poly.ringLatLngs || []).length}|${poly.ringLatLngs?.[0]?.[0] ?? ""}|${poly.ringLatLngs?.[0]?.[1] ?? ""}`
        : `r|${poly.lengthFt}|${poly.widthFt}|${poly.headingDeg}|${poly.center?.lat}|${poly.center?.lng}`;
      const renderKey = `${needsInteraction ? 1 : 0}|${styleColor}|${styleWeight}|${styleFillOpacity}|${styleDash}|${ringKey}`;

      const cached = geometryLayerCache.get(poly.id);

      if (cached && cached.renderKey === renderKey) {
        return; // nothing changed — skip entirely
      }

      if (cached && cached.interactive === needsInteraction) {
        // update in-place — no layer destroy/create, no GC pressure
        cached.path.setStyle({ color: styleColor, weight: styleWeight, fillColor: styleColor, fillOpacity: styleFillOpacity, dashArray: styleDash });
        if (cached.ringKey !== ringKey) {
          cached.path.setLatLngs(computeSlipPolygonLatLngs(poly));
        }
        if (needsInteraction) {
          const record = findRecordForPolygon(poly);
          const operational = String(record?.operational_status || "operable");
          cached.path.setTooltipContent(`${Number(poly.lengthFt).toFixed(1)} x ${Number(poly.widthFt).toFixed(1)} ft | ${operational}`);
        }
        cached.renderKey = renderKey;
        cached.ringKey = ringKey;
      } else {
        // new polygon or interactivity changed — must recreate
        if (cached) geometryLayer.removeLayer(cached.path);
        const ring = computeSlipPolygonLatLngs(poly);
        const path = L.polygon(ring, {
          color: styleColor, weight: styleWeight, fillColor: styleColor,
          fillOpacity: styleFillOpacity, dashArray: styleDash,
          interactive: needsInteraction, renderer: canvasRenderer,
        }).addTo(geometryLayer);
        if (needsInteraction) {
          const record = findRecordForPolygon(poly);
          const operational = String(record?.operational_status || "operable");
          path.bindTooltip(`${Number(poly.lengthFt).toFixed(1)} x ${Number(poly.widthFt).toFixed(1)} ft | ${operational}`);
        }
        geometryLayerCache.set(poly.id, { path, interactive: needsInteraction, renderKey, ringKey });
        createdNewPath = true;
      }
    });

    // Remove polygons that no longer exist in state
    geometryLayerCache.forEach((cached, polyId) => {
      if (!activePolyIds.has(polyId)) {
        geometryLayer.removeLayer(cached.path);
        geometryLayerCache.delete(polyId);
      }
    });

    // Newly created canvas paths always land on top of the shared renderer's
    // draw order (see core/services/layers.js), which can silently bury
    // click targets on other tools' tracked overlays (e.g. the tile-index in
    // Point Cloud Workspace). Reassert their stacking now that we've drawn.
    if (createdNewPath) {
      try { app.services?.layers?.bringOverlaysToFront?.(); } catch {}
    }

    if (mode === "dimension") {
      const entries = [...dimensionLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true, sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "space_type") {
      const entries = [...spaceTypeLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "multislip") {
      const entries = [...multislipLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true, sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "region") {
      const entries = [...regionLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true, sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "marina_name") {
      const entries = [...marinaNameLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true, sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "dock") {
      const entries = [...dockLegendMap.values()]
        .sort((a, b) => String(a.key || "").localeCompare(String(b.key || ""), undefined, { numeric: true, sensitivity: "base" }));
      renderDimensionLegend(entries);
    } else if (mode === "orientation_qc") {
      const order = ["length_gt_width", "width_gt_length", "equal", "unknown"];
      const entries = [...qcLegendMap.values()]
        .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
      renderDimensionLegend(entries);
    } else if (mode === "multislip_integrity") {
      const order = ["orphan", "missing_id", "disconnected", "valid", "none"];
      const entries = [...multislipIntegrityLegendMap.values()]
        .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
      renderDimensionLegend(entries);
    } else if (mode === "duplicate_slips") {
      renderDimensionLegend([...duplicateSlipLegendMap.values()]);
    } else if (mode === "duplicate_slip_id") {
      renderDimensionLegend([...duplicateSlipIdLegendMap.values()]);
    } else if (mode === "attribute") {
      // Scheme order (sorted values / ascending bins), limited to what's actually drawn.
      renderDimensionLegend(attributeColoring.entries.filter((e) => attributeLegendKeys.has(e.key)));
    } else {
      clearDimensionLegend();
    }
    renderUncommittedDraftLabels();
    renderSlipEditHandles();
  }

  // Uncommitted (no database record yet) polygons don't get a slip_id badge from the
  // records-keyed marker rebuild in renderMap(), so they'd otherwise sit on the map as a
  // bare colored box with no label. Kept out of the per-polygon loop above and keyed/skipped
  // on its own here (same idea as geometryLayerCache's renderKey check) rather than
  // unconditionally clearing and recreating a marker per uncommitted polygon on every single
  // render() call -- render() fires very often across this tool (after nearly every action,
  // not just map interactions), and testing had left enough uncommitted drafts sitting in
  // state that rebuilding all of their markers every time was a real, visible source of lag.
  let _lastDraftLabelKey = "";
  function renderUncommittedDraftLabels() {
    const uncommitted = state.slipGeometryPolygons.filter((poly) => !poly.committed && poly.center);
    const key = uncommitted.length
      ? `${state.dockName}|${state.slipIdSeparator}|${state.slipIdPart3}|${state.slipIdAutoIncrement}|${state.slipIdIncrementBy}|${state.slipIdSuffix}|${state.slipIdAutoIncrementSuffix}|${state.slipIdIncrementBySuffix}|`
        + uncommitted.map((poly) => `${poly.id}:${poly.lengthFt}:${poly.widthFt}:${poly.center.lat}:${poly.center.lng}`).join(";")
      : "";
    if (key === _lastDraftLabelKey) return;
    _lastDraftLabelKey = key;
    geometryDraftLabelLayer.clearLayers();
    if (!uncommitted.length) return;
    const draftIds = previewSlipIds(state, uncommitted.length);
    uncommitted.forEach((poly, index) => {
      const draftId = draftIds[index] || "SLIP";
      const dimsText = `${Number(poly.lengthFt || 0).toFixed(1)}×${Number(poly.widthFt || 0).toFixed(1)} ft`;
      L.marker([poly.center.lat, poly.center.lng], {
        interactive: false,
        icon: L.divIcon({
          className: "",
          iconSize: null,
          iconAnchor: [0, 0],
          html: `<div style="color:#d1fae5;font:700 11px/1.2 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%);text-align:center;">${esc(draftId)}<br><span style="font-size:9px;font-weight:400;opacity:.8;">${dimsText}</span></div>`,
        }),
      }).addTo(geometryDraftLabelLayer);
    });
  }

  function renderSlipEditHandles() {
    if (slipHandleDragging) return;
    geometryHandlesLayer.clearLayers();

    const selIds = getSelectedMeasurementIds();
    if (!selIds.length) return;

    // Meters per degree of latitude (matches the 111320 constant used everywhere else in this
    // file, e.g. offsetPoint/inferPolyMetricsFromRing) -- NOT Earth's radius. Every use below
    // is the flat-plane approximation `Δdeg * R` (or `Δdeg * cos(lat) * R` for longitude), which
    // needs meters-per-degree, not meters-per-radian. This was previously 6371000 (Earth's
    // radius in meters), missing the degree-to-radian factor of π/180 -- every edge-handle drag
    // was computing distances ~57x (6371000/111320) larger than the actual physical mouse
    // movement, e.g. dragging a real 1ft snapped the geometry by ~57ft. Inherited by the
    // corner handles below when they were added, since they copied this same (buggy) pattern.
    const R = 111320;

    // One entry per selected slip: just its current ring (as {lat,lng} objects) -- reused,
    // when 2+ slips are selected, so a different slip's dragged handle can find its own
    // matching edge to propagate onto (see msFindMatchingRingEdge below). Deliberately NOT
    // each slip's inferred heading axis (see the comment above msFindMatchingRingEdge's call
    // site) -- that axis has an arbitrary sign per polygon, which used to invert the
    // propagated push direction for whichever slip(s) happened to land on the other sign.
    const slipInfos = selIds.map((measurementId) => {
      const record = records.find((r) => r.measurement_id === measurementId);
      if (!record) return null;
      const rawRing = parsePolygonWkt(record.geometry_wkt);
      if (!rawRing || rawRing.length < 3) return null;
      const ring = rawRing.map((p) => ({ lat: p[0], lng: p[1] }));
      return { measurementId, slipId: record.slip_id, ring };
    }).filter(Boolean);
    if (!slipInfos.length) return;
    const isMulti = slipInfos.length > 1;

    // Applies newRing to whichever on-map polygon path is linked to measurementId, for live
    // feedback during a drag -- the authoritative record/WKT update only happens on dragend.
    const liveUpdateRing = (measurementId, newRing) => {
      const poly = state.slipGeometryPolygons.find((p) =>
        String(p?.linkedMeasurementId || "") === String(measurementId));
      if (!poly) return;
      const cached = geometryLayerCache.get(poly.id);
      if (cached) cached.path.setLatLngs(newRing.map((p) => [p.lat, p.lng]));
    };

    // Keeps the "Extend Edges" guide lines glued to the polygon during a live drag. Normally
    // they're only (re)drawn inside renderGeometry(), which liveUpdateRing above deliberately
    // doesn't call on every drag frame (too expensive -- it redraws every slip, not just the
    // one being dragged); without this they'd sit frozen at the pre-drag geometry until
    // dragend. `touches` is every {measurementId, ring} touched by this one drag frame (the
    // dragged slip plus any cross-slip propagation targets) so a multi-slip edge drag updates
    // all of their extension lines together, same as the static render would.
    const liveRedrawEdgeExtensions = (touches) => {
      slipLiveExtensionLayer.clearLayers();
      if (!state.showEdgeExtensions) return;
      touches.forEach(({ measurementId, ring }) => {
        const poly = state.slipGeometryPolygons.find((p) =>
          String(p?.linkedMeasurementId || "") === String(measurementId));
        if (!poly || !isGeometrySelected(poly.id)) return;
        const ringPairs = ring.map((p) => [p.lat, p.lng]);
        const lines = computeEdgeExtensionLinesFromRing(ringPairs, state.edgeExtensionFt);
        lines.forEach((line) => {
          L.polyline(line, {
            color: SLIP_EDGE_EXTENSION_COLOR, weight: 1, opacity: 0.75,
            dashArray: "2 4", lineCap: "butt", interactive: false, renderer: canvasRenderer,
          }).addTo(slipLiveExtensionLayer);
        });
      });
    };

    // Writes newRing into `records` (WKT + re-inferred length/width) for one slip; the
    // caller batches these and PATCHes/persists once after every touched slip is updated.
    const applyRingToRecords = (measurementId, newRing) => {
      const newLatLngPairs = newRing.map((p) => [p.lat, p.lng]);
      const newWkt = buildPolygonWkt(newLatLngPairs);
      const inferred = inferPolyMetricsFromRing(newLatLngPairs);
      records = records.map((r) => (r.measurement_id === measurementId ? {
        ...r,
        geometry_wkt: newWkt,
        ...(inferred ? {
          measured_linear_feet: roundToTenthFoot(inferred.lengthFt),
          measured_width_feet: roundToTenthFoot(inferred.widthFt),
        } : {}),
      } : r));
    };

    // PATCHes every touched slip's now-updated record, one at a time (consistent with how
    // e.g. commitMultislip persists a batch), then a single combined status + re-render.
    // undoBefore (Map<measurementId, record-before-this-drag>) is optional -- callers that
    // don't pass one (there are none left in this file, but keep the default) just skip
    // pushing an undo entry.
    const persistTouched = async (measurementIds, statusVerb, undoBefore = null) => {
      recordsVersion++;
      synchronizeCommittedGeometryFromRecords();
      renderGeometry();
      const savedIds = [];
      let lastError = null;
      for (const measurementId of measurementIds) {
        try {
          const updatedRecord = records.find((r) => r.measurement_id === measurementId);
          await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, {
            method: "PATCH",
            body: updatedRecord,
          });
          app.log("slip.geometry_edited", { slip_id: updatedRecord?.slip_id, marina: state.marinaId });
          savedIds.push(measurementId);
        } catch (error) {
          lastError = error;
        }
      }
      if (lastError && !savedIds.length) {
        status = lastError?.message || "Failed to save resized geometry.";
      } else {
        const rec = records.find((r) => r.measurement_id === measurementIds[0]);
        const label = measurementIds.length > 1
          ? `${measurementIds.length} slips`
          : `slip ${rec?.slip_id || measurementIds[0]}`;
        status = lastError
          ? `${statusVerb} ${label} (${savedIds.length}/${measurementIds.length} saved -- ${lastError?.message || "one failed"}).`
          : `${statusVerb} ${label}.`;
        if (undoBefore && savedIds.length) {
          pushUndoEntry(`${statusVerb} ${label}`, savedIds.map((measurementId) => ({
            type: "update",
            measurementId,
            before: undoBefore.get(measurementId) || null,
            after: snapshotRecord(measurementId),
          })).filter((c) => c.before));
        }
      }
      render();
    };

    slipInfos.forEach((info) => {
      const { ring, measurementId } = info;
      const n = ring.length;
      const otherInfos = isMulti ? slipInfos.filter((other) => other.measurementId !== measurementId) : [];

      for (let i = 0; i < n; i++) {
        const edgeInfo = msRingEdgeNormal(ring, i);
        // Skip the midpoint handle entirely for a near-degenerate edge (e.g. the short cut
        // between two corners at a dock-following polygon's pointed tip) -- at that length
        // there's nothing meaningful left to resize, and the handle would just sit on top of
        // the two corner handles already at each end of it. Those corner handles are the
        // correct tool for reshaping a tip like that, one vertex at a time.
        if (!edgeInfo || edgeInfo.edgeLenFt < 2) continue;
        const { mid, nx, ny } = edgeInfo;

        // For cross-slip propagation, find each other selected slip's own edge that pushes
        // in the same REAL-WORLD direction as the one being dragged here (nx,ny -- this
        // edge's actual outward normal, straight off the ring's own geometry). Only
        // meaningful for a 4-point ring with 2+ slips selected; every other case just resizes
        // the one slip being dragged, exactly like before this feature existed.
        //
        // Deliberately NOT routed through either slip's own inferred heading axis (used to
        // read `sign * (isLengthEdge ? other.ux : other.vx)` etc. here): inferPolyMetricsFromRing
        // derives that axis from a tied bounding-box-area comparison for any true rectangle,
        // broken by whichever perpendicular edge direction the ring happens to hit first --
        // which depends on that polygon's own vertex winding/start point, not on real-world
        // orientation. Two slips drawn side by side with identical physical orientation can
        // end up with opposite-signed heading axes, which silently inverted the push direction
        // for whichever slip(s) in a multi-select happened to have the flipped sign -- they'd
        // shrink/grow opposite to the rest of the row even though everything was drawn
        // together. Matching directly on nx/ny sidesteps that ambiguity entirely: it's an
        // unambiguous real-world direction, not an axis with an arbitrary sign.
        let propagationTargets = [];

        const iCopy = i;
        const nCopy = n;
        const midCopy = { lat: mid.lat, lng: mid.lng };
        const nxCopy = nx;
        const nyCopy = ny;
        const origRing = ring;

        if (isMulti && nCopy === 4 && otherInfos.length) {
          propagationTargets = otherInfos.map((other) => {
            const match = msFindMatchingRingEdge(other.ring, nxCopy, nyCopy);
            return match ? { measurementId: other.measurementId, ring: other.ring, edgeIndex: match.i, nx: match.nx, ny: match.ny } : null;
          }).filter(Boolean);
        }

        const measurementIdCopy = measurementId;

        const handle = L.marker([mid.lat, mid.lng], {
          icon: L.divIcon({
            className: "",
            html: `<div style="width:20px;height:20px;display:flex;align-items:center;justify-content:center;cursor:move;"><div style="width:11px;height:11px;background:#f59e0b;border:2px solid #1e293b;border-radius:2px;transform:rotate(45deg);box-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;"></div></div>`,
            iconSize: [20, 20],
            iconAnchor: [10, 10],
          }),
          draggable: true,
          interactive: true,
          bubblingMouseEvents: false,
          zIndexOffset: 1000,
        });

        handle.on("dragstart", () => {
          slipHandleDragging = true;
          const touchedMeasurementIds = [measurementIdCopy, ...propagationTargets.map((t) => t.measurementId)];
          const polyIds = touchedMeasurementIds.map((mid) => {
            const p = state.slipGeometryPolygons.find((pp) => String(pp?.linkedMeasurementId || "") === String(mid));
            return p ? p.id : null;
          });
          hideCommittedExtensionLines(polyIds);
        });

        handle.on("drag", (ev) => {
          const pos = ev.latlng;
          const dx = (pos.lng - midCopy.lng) * Math.cos((midCopy.lat * Math.PI) / 180) * R;
          const dy = (pos.lat - midCopy.lat) * R;
          const perpM = (dx * nxCopy) + (dy * nyCopy);
          const snappedFt = Math.round((perpM * 3.280839895) / 0.1) * 0.1;
          const snappedM = snappedFt / 3.280839895;

          const newRing = msPushRingEdge(origRing, iCopy, nxCopy, nyCopy, snappedM);
          liveUpdateRing(measurementIdCopy, newRing);
          const touches = [{ measurementId: measurementIdCopy, ring: newRing }];
          propagationTargets.forEach((t) => {
            const targetRing = msPushRingEdge(t.ring, t.edgeIndex, t.nx, t.ny, snappedM);
            liveUpdateRing(t.measurementId, targetRing);
            touches.push({ measurementId: t.measurementId, ring: targetRing });
          });
          liveRedrawEdgeExtensions(touches);

          const snappedLat = midCopy.lat + ((snappedM * nyCopy) / R);
          const snappedLng = midCopy.lng + ((snappedM * nxCopy) / (R * Math.cos((midCopy.lat * Math.PI) / 180)));
          handle.setLatLng([snappedLat, snappedLng]);
        });

        handle.on("dragend", async () => {
          slipHandleDragging = false;
          const pos = handle.getLatLng();
          const dx = (pos.lng - midCopy.lng) * Math.cos((midCopy.lat * Math.PI) / 180) * R;
          const dy = (pos.lat - midCopy.lat) * R;
          const perpM = (dx * nxCopy) + (dy * nyCopy);
          const snappedFt = Math.round((perpM * 3.280839895) / 0.1) * 0.1;
          const snappedM = snappedFt / 3.280839895;

          const touchedIds = [measurementIdCopy, ...propagationTargets.map((t) => t.measurementId)];
          // Snapshot before any of them are mutated below -- applyRingToRecords overwrites
          // `records` in place, so this is the only point "before" is still recoverable.
          const undoBefore = new Map(touchedIds.map((id) => [id, snapshotRecord(id)]));
          applyRingToRecords(measurementIdCopy, msPushRingEdge(origRing, iCopy, nxCopy, nyCopy, snappedM));
          propagationTargets.forEach((t) => {
            applyRingToRecords(t.measurementId, msPushRingEdge(t.ring, t.edgeIndex, t.nx, t.ny, snappedM));
          });

          await persistTouched(touchedIds, "Resized", undoBefore);
        });

        geometryHandlesLayer.addLayer(handle);
        const el = handle.getElement();
        if (el) el.style.cursor = "move";
      }

      // Corner handles — move a single vertex freely, independent of every other vertex, and
      // (unlike the edge handles above) never propagated to other selected slips: a free
      // corner reshape has no principled "corresponding corner" on a differently-oriented or
      // differently-shaped slip the way a length/width end does. Deliberately NOT
      // parallelogram-preserving like the edge handles above: those exist to resize
      // length/width while keeping the slip's shape (rectangle or sheared parallelogram)
      // intact, whereas these are for reshaping a corner into something the edge handles
      // can't produce at all (an angled dock corner, a non-rectangular slip, etc.) -- for a
      // 4-point ring, dragging one corner without moving its neighbors generally stops the
      // result from being a parallelogram, and that's the point.
      for (let i = 0; i < n; i++) {
        const vertex = ring[i];
        const vertexLatRad = (vertex.lat * Math.PI) / 180;
        const iCopy = i;
        const origRing = ring;
        const measurementIdCopy = measurementId;

        // Snapped to the same 0.1ft grid as the edge handles, independently per axis (a corner
        // drag is a free 2D move, not a single perpendicular distance like an edge push).
        const snapCornerLatLng = (latlng) => {
          const dx = (latlng.lng - vertex.lng) * Math.cos(vertexLatRad) * R;
          const dy = (latlng.lat - vertex.lat) * R;
          const snappedXM = (Math.round((dx * 3.280839895) / 0.1) * 0.1) / 3.280839895;
          const snappedYM = (Math.round((dy * 3.280839895) / 0.1) * 0.1) / 3.280839895;
          return {
            lat: vertex.lat + (snappedYM / R),
            lng: vertex.lng + (snappedXM / (R * Math.cos(vertexLatRad))),
          };
        };

        const cornerHandle = L.marker([vertex.lat, vertex.lng], {
          icon: L.divIcon({
            className: "",
            html: `<div style="width:18px;height:18px;display:flex;align-items:center;justify-content:center;cursor:pointer;"><div style="width:10px;height:10px;background:#38bdf8;border:2px solid #1e293b;border-radius:50%;box-shadow:0 1px 4px rgba(0,0,0,0.6);pointer-events:none;"></div></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          }),
          draggable: true,
          interactive: true,
          bubblingMouseEvents: false,
          zIndexOffset: 1001,
        });

        cornerHandle.on("dragstart", () => {
          slipHandleDragging = true;
          const p = state.slipGeometryPolygons.find((pp) => String(pp?.linkedMeasurementId || "") === String(measurementIdCopy));
          if (p) hideCommittedExtensionLines([p.id]);
        });

        cornerHandle.on("drag", (ev) => {
          const snapped = snapCornerLatLng(ev.latlng);
          const newRing = origRing.map((pt, j) => (j === iCopy ? snapped : pt));
          liveUpdateRing(measurementIdCopy, newRing);
          liveRedrawEdgeExtensions([{ measurementId: measurementIdCopy, ring: newRing }]);
          cornerHandle.setLatLng([snapped.lat, snapped.lng]);
        });

        cornerHandle.on("dragend", async () => {
          slipHandleDragging = false;
          const snapped = snapCornerLatLng(cornerHandle.getLatLng());
          const newRing = origRing.map((pt, j) => (j === iCopy ? snapped : pt));
          const undoBefore = new Map([[measurementIdCopy, snapshotRecord(measurementIdCopy)]]);
          applyRingToRecords(measurementIdCopy, newRing);
          await persistTouched([measurementIdCopy], "Reshaped corner of", undoBefore);
        });

        geometryHandlesLayer.addLayer(cornerHandle);
        const cornerEl = cornerHandle.getElement();
        if (cornerEl) cornerEl.style.cursor = "pointer";
      }
    });
  }

  function drawGeometryRunGhost() {
    geometryRunPreviewLayer.clearLayers();
    if (!geometryRunLayoutActive) return;
    const guidePoints = [
      ...geometryRunEndpoints,
      ...(geometryRunEndpoints.length === 1 && geometryRunHoverPoint ? [geometryRunHoverPoint] : []),
    ];
    const points = geometryRunPreviewPoints();
    const selected = selectedGeometryPolygon();
    if (!selected || !points.length) return;
    if (guidePoints.length >= 2) {
      L.polyline(guidePoints.map((point) => [point.lat, point.lng]), {
        color: "#f59e0b",
        weight: 2,
        dashArray: "6 6",
        interactive: false,
        renderer: canvasRenderer,
      }).addTo(geometryRunPreviewLayer);
    }
    const heading = Number(selected.headingDeg || 0);
    const previewIds = previewSlipIds(state, points.length);
    const selectedCenter = {
      lat: Number(selected.center?.lat || 0),
      lng: Number(selected.center?.lng || 0),
    };
    points.forEach((point, index) => {
      if (index === 0) return;
      const targetCenter = { lat: Number(point.lat), lng: Number(point.lng) };
      const translatedRing = (Array.isArray(selected.ringLatLngs) && selected.ringLatLngs.length >= 3)
        ? translateRingLatLngs(selected.ringLatLngs, selectedCenter, targetCenter)
        : null;
      const previewPoly = {
        ...selected,
        center: targetCenter,
        headingDeg: heading,
        ringLatLngs: translatedRing || null,
      };
      const ring = computeSlipPolygonLatLngs(previewPoly);
      L.polygon(ring, {
        color: "rgba(245,158,11,0.95)",
        weight: 2,
        fillColor: "rgba(245,158,11,0.35)",
        fillOpacity: 0.12,
        dashArray: "4 4",
        interactive: false,
        renderer: canvasRenderer,
      }).addTo(geometryRunPreviewLayer);
      L.circleMarker([point.lat, point.lng], {
        radius: index === points.length - 1 ? 4 : 3,
        color: "#f59e0b",
        weight: 1.5,
        fillColor: "#fbbf24",
        fillOpacity: 0.85,
        interactive: false,
        renderer: canvasRenderer,
      }).addTo(geometryRunPreviewLayer);
      const idLabel = previewIds[index] || "";
      if (idLabel && geometryRunEndpoints.length >= 2) {
        L.marker([point.lat, point.lng], {
          interactive: false,
          icon: L.divIcon({
            className: "slip-geom-run-id-wrap",
            iconSize: null,
            iconAnchor: [0, 0],
            html: `<div style="transform:translate(-50%,-135%);padding:2px 8px;border-radius:999px;background:rgba(15,23,42,0.86);border:1px solid rgba(56,189,248,0.45);color:#e2e8f0;font:700 11px/1.1 system-ui,sans-serif;white-space:nowrap;">${esc(idLabel)}</div>`,
          }),
        }).addTo(geometryRunPreviewLayer);
      }
    });
  }

  function renderGeometryPlacePreview() {
    geometryPlacePreviewLayer.clearLayers();
    if (!activeGeometryPlaceMode || !geometryPlaceHoverPoint || !geometryPlaceDraft) return;
    if (geometryDrawStage === "width") {
      // Before click 1, there's no anchor yet -- nothing to preview; the
      // "slip-free-draw-cursor" class (same one Multislip/Comb use) already
      // gives a real crosshair cursor as the indicator that placement mode is
      // live. After click 1, draw the width edge being drawn (A -> hover)
      // with a live length label; the full-rectangle ghost only starts once
      // width is frozen.
      if (!geometryDrawA) return;
      const dim1Color = firstDimensionIsLength ? "rgba(16,185,129,0.95)" : "rgba(239,68,68,0.95)";
      const dim1TextColor = firstDimensionIsLength ? "#d1fae5" : "#fee2e2";
      L.polyline([geometryDrawA, geometryPlaceHoverPoint], {
        color: dim1Color, weight: 2, dashArray: "6 4", interactive: false, renderer: canvasRenderer,
      }).addTo(geometryPlacePreviewLayer);
      const dim1Ft = metersToFeet(map.distance(geometryDrawA, geometryPlaceHoverPoint));
      const mid = { lat: (geometryDrawA.lat + geometryPlaceHoverPoint.lat) / 2, lng: (geometryDrawA.lng + geometryPlaceHoverPoint.lng) / 2 };
      L.marker([mid.lat, mid.lng], {
        interactive: false,
        icon: L.divIcon({
          className: "",
          iconSize: null,
          iconAnchor: [0, 0],
          html: `<div style="color:${dim1TextColor};font:700 11px/1 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%)">${firstDimensionIsLength ? "L" : "W"}: ${dim1Ft.toFixed(1)} ft</div>`,
        }),
      }).addTo(geometryPlacePreviewLayer);
      return;
    }
    const translatedRing = (
      Array.isArray(geometryPlaceDraft.ringLatLngs)
      && geometryPlaceDraft.ringLatLngs.length >= 3
      && geometryPlaceDraft.sourceCenter
    )
      ? translateRingLatLngs(
        geometryPlaceDraft.ringLatLngs,
        geometryPlaceDraft.sourceCenter,
        { lat: Number(geometryPlaceHoverPoint.lat), lng: Number(geometryPlaceHoverPoint.lng) },
      )
      : null;
    const inferred = translatedRing ? inferPolyMetricsFromRing(translatedRing) : null;
    const previewPoly = {
      id: "draft",
      center: translatedRing
        ? { lat: Number(inferred?.center?.lat ?? geometryPlaceHoverPoint.lat), lng: Number(inferred?.center?.lng ?? geometryPlaceHoverPoint.lng) }
        : { lat: Number(geometryPlaceHoverPoint.lat), lng: Number(geometryPlaceHoverPoint.lng) },
      lengthFt: Number((inferred?.lengthFt ?? geometryPlaceDraft.lengthFt) || 40),
      widthFt: Number((inferred?.widthFt ?? geometryPlaceDraft.widthFt) || 14),
      headingDeg: Number((inferred?.headingDeg ?? geometryPlaceDraft.headingDeg) || 0),
      ringLatLngs: translatedRing || null,
    };
    const ring = computeSlipPolygonLatLngs(previewPoly);
    L.polygon(ring, {
      color: "rgba(16,185,129,0.95)",
      weight: 2,
      fillColor: "rgba(16,185,129,0.35)",
      fillOpacity: 0.15,
      dashArray: "6 4",
      interactive: false,
      renderer: canvasRenderer,
    }).addTo(geometryPlacePreviewLayer);
    const previewId = previewSlipIds(state, 1)[0] || "SLIP";
    const previewDimsText = `${previewPoly.lengthFt.toFixed(1)}×${previewPoly.widthFt.toFixed(1)} ft`;
    L.marker([previewPoly.center.lat, previewPoly.center.lng], {
      interactive: false,
      icon: L.divIcon({
        className: "",
        iconSize: null,
        iconAnchor: [0, 0],
        html: `<div style="color:#d1fae5;font:700 11px/1.2 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%);text-align:center;">${esc(previewId)}<br><span style="font-size:9px;font-weight:400;opacity:.8;">${previewDimsText}</span></div>`,
      }),
    }).addTo(geometryPlacePreviewLayer);
    // Review stage: second dimension is locked in but not yet committed -- a drag handle on
    // the far edge lets it be fine-tuned, same job msSlotOffshorePoint's handle does for
    // Multislip. Kept in its own layer (see geometryPlaceHandleLayer) so mid-drag rebuilds of
    // the preview layer above don't cancel the gesture.
    if (geometryDrawStage === "review" && geometryDrawA && geometryDrawB) {
      if (!geometryPlaceHandleDragging) {
        geometryPlaceHandleLayer.clearLayers();
        const offsetHeadingDeg = Number(geometryPlaceDraft.headingDeg || 0) - (firstDimensionIsLength ? 90 : 0);
        const farPt = computeDrawnFarEdgeMidpoint({
          A: geometryDrawA, B: geometryDrawB, lengthFt: geometryDrawSignedLengthFt, headingDeg: offsetHeadingDeg,
        });
        const handle = L.marker([farPt.lat, farPt.lng], {
          icon: L.divIcon({
            className: "",
            html: '<div style="width:12px;height:12px;background:#38bdf8;border:2px solid #fff;border-radius:50%;cursor:grab;margin-left:-6px;margin-top:-6px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
            iconSize: [0, 0], iconAnchor: [0, 0],
          }),
          draggable: true,
          zIndexOffset: 1000,
        });
        handle.on("dragstart", () => { geometryPlaceHandleDragging = true; });
        handle.on("drag", (e) => {
          const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
          const rawFt = msPerpDistFt(geometryDrawA, geometryDrawB, newPos);
          const sign = geometryDrawSignedLengthFt < 0 ? -1 : 1;
          const snapFt = (Math.round(Math.abs(rawFt) / 0.1) * 0.1) || 0.1;
          geometryDrawSignedLengthFt = sign * snapFt;
          const measuredFt = Math.max(0.1, snapFt);
          if (firstDimensionIsLength) geometryPlaceDraft.widthFt = measuredFt;
          else geometryPlaceDraft.lengthFt = measuredFt;
          geometryPlaceHoverPoint = computeDrawnRectCenter({
            A: geometryDrawA, B: geometryDrawB, lengthFt: geometryDrawSignedLengthFt, headingDeg: offsetHeadingDeg,
          });
          renderGeometryPlacePreview();
        });
        handle.on("dragend", () => { geometryPlaceHandleDragging = false; renderGeometryPlacePreview(); });
        geometryPlaceHandleLayer.addLayer(handle);
      }
    } else if (!geometryPlaceHandleDragging) {
      geometryPlaceHandleLayer.clearLayers();
    }
  }

  function renderGeometryRunPreview() {
    geometryRunHandleLayer.clearLayers();
    drawGeometryRunGhost();
    if (!geometryRunLayoutActive) return;
    const selected = selectedGeometryPolygon();
    if (!selected) return;
    if (geometryRunEndpoints.length >= 1) {
      const startHandle = L.marker([geometryRunEndpoints[0].lat, geometryRunEndpoints[0].lng], {
        draggable: true,
        autoPan: true,
        zIndexOffset: 1200,
        icon: L.divIcon({
          className: "slip-geom-run-start",
          iconSize: [18, 18],
          iconAnchor: [9, 9],
          html: `<div style="width:18px;height:18px;border-radius:999px;background:rgba(34,197,94,.95);border:2px solid rgba(15,23,42,.9);box-shadow:0 0 0 1px rgba(187,247,208,.55),0 6px 12px rgba(15,23,42,.22);"></div>`,
        }),
      }).addTo(geometryRunHandleLayer);
      startHandle.bindTooltip("Drag run start", { direction: "top", offset: [0, -8] });
      startHandle.on("mousedown", (event) => {
        event?.originalEvent?.preventDefault?.();
        event?.originalEvent?.stopPropagation?.();
      });
      startHandle.on("dragstart", () => {
        suppressMapClicks(700);
        try { map.dragging.disable(); } catch {}
      });
      startHandle.on("drag", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        geometryRunEndpoints[0] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        if (geometryRunEndpoints.length === 1) geometryRunHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
        // Keep the actual selected polygon anchored to the run start point.
        updateSelectedGeometry({ center: { lat: Number(ll.lat), lng: Number(ll.lng) } });
        // Repaint live polygon position while dragging, like the run ghost.
        renderGeometry();
        drawGeometryRunGhost();
      });
      startHandle.on("dragend", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        suppressMapClicks(700);
        try { map.dragging.enable(); } catch {}
        geometryRunEndpoints[0] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        updateSelectedGeometry({ center: { lat: Number(ll.lat), lng: Number(ll.lng) } });
        status = "Adjusted run start point.";
        render();
      });
    }
    if (geometryRunEndpoints.length >= 2) {
      const endHandle = L.marker([geometryRunEndpoints[1].lat, geometryRunEndpoints[1].lng], {
        draggable: true,
        autoPan: true,
        zIndexOffset: 1200,
        icon: L.divIcon({
          className: "slip-geom-run-end",
          iconSize: [18, 18],
          iconAnchor: [9, 9],
          html: `<div style="width:18px;height:18px;border-radius:999px;background:rgba(245,158,11,.95);border:2px solid rgba(15,23,42,.9);box-shadow:0 0 0 1px rgba(254,243,199,.55),0 6px 12px rgba(15,23,42,.22);"></div>`,
        }),
      }).addTo(geometryRunHandleLayer);
      endHandle.bindTooltip("Drag run end", { direction: "top", offset: [0, -8] });
      endHandle.on("mousedown", (event) => {
        event?.originalEvent?.preventDefault?.();
        event?.originalEvent?.stopPropagation?.();
      });
      endHandle.on("dragstart", () => {
        suppressMapClicks(700);
        try { map.dragging.disable(); } catch {}
      });
      endHandle.on("drag", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        geometryRunEndpoints[1] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        drawGeometryRunGhost();
      });
      endHandle.on("dragend", (event) => {
        const ll = event?.target?.getLatLng?.();
        if (!ll) return;
        suppressMapClicks(700);
        try { map.dragging.enable(); } catch {}
        geometryRunEndpoints[1] = { lat: Number(ll.lat), lng: Number(ll.lng) };
        status = "Adjusted run end point.";
        render();
      });
    }
  }

  function slipLabelScale() {
    const z = Number(map?.getZoom?.() ?? 14);
    // Shrink labels while zooming out to reduce overlap.
    return clampNumber(0.55 + ((z - 12) * 0.075), 0.45, 1);
  }

  let _lastMapKey = "";
  let _lastMapViewBounds = null;
  function renderMap() {
    const zoom = Number(map?.getZoom?.() ?? 14);
    const _mapKey = `${recordsVersion}|${state.selectedMeasurementId}|${zoom}|${state.showSlipIds ? 1 : 0}|${state.slipIdLabelSizePx}|${state.colorSlipsBy}|${filterProjectId}`;
    // _mapKey only covers what the expensive per-record marker rebuild below needs (records
    // count, selection, zoom, label settings) — it has no term for slipGeometryPolygons, the
    // in-progress draft, or multislip/chain/comb state. renderGeometry/renderGeometryPlacePreview/
    // renderGeometryRunPreview do their own internal per-shape keyed diffing, so calling them on
    // every render() is cheap; they used to be skipped along with the marker rebuild whenever
    // _mapKey was unchanged, so finishing a draw sequence or wheel-adjusting L/W/rotation left
    // the map showing nothing until an unrelated zoom change happened to bust this cache.
    // Always run them; only skip the marker rebuild when idle.
    //
    // The marker rebuild below used to run over every record in the marina regardless
    // of what's actually on screen -- fine for a few dozen slips, but a large marina
    // paid the full per-record cost (badge divIcon HTML + 5 listeners each) on every
    // zoom step even though only a handful of markers are ever visible at once (badges
    // only show at zoom>=20, where the viewport covers a tiny slice of a real marina).
    // currentBounds/_lastMapViewBounds bound the rebuild to what's near the viewport:
    // rebuild only when the key changed OR the view has panned somewhere the last
    // rebuild's (padded) bounds didn't cover.
    const currentBounds = map?.getBounds?.() || null;
    const boundsStale = !!currentBounds && (!_lastMapViewBounds || !_lastMapViewBounds.contains(currentBounds));
    const markerKeyChanged = _mapKey !== _lastMapKey || boundsStale;
    if (!markerKeyChanged) {
      renderGeometry();
      renderGeometryPlacePreview();
      renderGeometryRunPreview();
      return;
    }
    _lastMapKey = _mapKey;
    // Padded well past the visible edge so a moderate pan doesn't immediately go stale
    // again and re-trigger a rebuild -- trades a wider (still bounded) marker set for
    // far fewer rebuilds while panning around one area.
    const viewBounds = currentBounds ? currentBounds.pad(1) : null;
    _lastMapViewBounds = viewBounds;
    markersLayer.clearLayers();
    slipIdMarkersLayer.clearLayers();
    depthMarkersLayer.clearLayers();
    const showSlipIdBadges = zoom >= 20;
    const labelScale = slipLabelScale();
    const labelBasePx = clampNumber(Number(state.slipIdLabelSizePx || 13), 8, 28);
    const labelFontPx = Math.max(8, Math.round(labelBasePx * labelScale));
    const labelPadY = Math.max(2, Math.round((labelBasePx / 3) * labelScale));
    const labelPadX = Math.max(4, Math.round((labelBasePx * 0.72) * labelScale));
    records.forEach((record) => {
      const point = pointFromAnyWkt(record.geometry_wkt);
      if (!point) return;
      // viewBounds already has generous padding (see above) -- anything outside it isn't
      // visible now and won't be reachable by a small pan before the next rebuild anyway.
      if (viewBounds && !viewBounds.contains([point.lat, point.lng])) return;
      const selected = record.measurement_id === state.selectedMeasurementId;
      const lineCoords = parseLineStringWkt(record.geometry_wkt);
      if (Array.isArray(lineCoords) && lineCoords.length >= 2) {
        const line = L.polyline(lineCoords.map((p) => [p.lat, p.lng]), {
          color: selected ? "#f59e0b" : "#d946ef",
          weight: selected ? 9 : 7,
          opacity: 0.94,
          lineCap: "round",
          renderer: canvasRenderer,
        }).addTo(markersLayer);
        line.on("click", (event) => {
          if (event?.originalEvent) event.originalEvent.__htHandled = "slip-inventory";
          event?.originalEvent?.stopPropagation?.();
          state.selectedMeasurementId = record.measurement_id;
          const geom = findGeometryForRecord(record);
          const toggle = !!(event?.originalEvent?.ctrlKey || event?.originalEvent?.metaKey || event?.originalEvent?.shiftKey);
          if (geom?.id) applyGeometrySelection(geom.id, { toggle });
          _scrollTableToSelectionPending = true;
          persist();
          render();
        });
      }
      if (state.showSlipIds && showSlipIdBadges) {
        const label = record.slip_id || record.measurement_id;
        const marker = L.marker([point.lat, point.lng], {
          interactive: true,
          draggable: selected,
          pane: "slip-id-badge-pane",
          zIndexOffset: selected ? 1500 : 1000,
          icon: L.divIcon({
            className: "slip-inventory-map-label-wrap",
            iconSize: null,
            iconAnchor: [0, 0],
            html: `
              <div style="
                display:inline-flex;
                align-items:center;
                justify-content:center;
                padding:${labelPadY}px ${labelPadX}px;
                border-radius:999px;
                background:${selected ? "rgba(245,158,11,0.92)" : "rgba(15,23,42,0.88)"};
                color:${selected ? "#111827" : "#e2e8f0"};
                border:1px solid ${selected ? "rgba(254,243,199,0.95)" : "rgba(56,189,248,0.55)"};
                box-shadow:${selected ? "0 0 0 1px rgba(245,158,11,0.28)" : "0 6px 14px rgba(15,23,42,0.22)"};
                font:700 ${labelFontPx}px/1.2 system-ui,sans-serif;
                white-space:nowrap;
                transform:translate(-50%, -50%);
                pointer-events:auto;
                cursor:pointer;
              ">${esc(label)}</div>
            `,
          }),
        }).addTo(slipIdMarkersLayer);
        marker.on("click", (event) => {
          if (event?.originalEvent) event.originalEvent.__htHandled = "slip-inventory";
          event?.originalEvent?.preventDefault?.();
          event?.originalEvent?.stopPropagation?.();
          event?.originalEvent?.stopImmediatePropagation?.();
          state.selectedMeasurementId = record.measurement_id;
          const geom = findGeometryForRecord(record);
          const toggle = !!(event?.originalEvent?.ctrlKey || event?.originalEvent?.metaKey || event?.originalEvent?.shiftKey);
          if (geom?.id) applyGeometrySelection(geom.id, { toggle });
          _scrollTableToSelectionPending = true;
          persist();
          render();
        });
        marker.on("dragstart", (event) => {
          if (event?.originalEvent) event.originalEvent.__htHandled = "slip-inventory";
          activeSlipDragCount += 1;
          state.selectedMeasurementId = record.measurement_id;
          const geom = findGeometryForRecord(record);
          if (geom?.id) applyGeometrySelection(geom.id, { toggle: false });
          persist();
        });
        marker.on("drag", (event) => {
          const ll = event?.target?.getLatLng?.();
          const lat = Number(ll?.lat);
          const lng = Number(ll?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          const geom = findGeometryForRecord(record);
          if (!geom?.id) return;
          const selectedIds = getSelectedGeometryIds();
          if (!selectedIds.includes(String(geom.id))) setSelectedGeometryIds([String(geom.id)], String(geom.id));
          updateSelectedGeometry({ center: { lat, lng } });
          renderGeometry();
          renderGeometryRunPreview();
        });
        marker.on("dragend", async (event) => {
          activeSlipDragCount = Math.max(0, activeSlipDragCount - 1);
          const ll = event?.target?.getLatLng?.();
          const lat = Number(ll?.lat);
          const lng = Number(ll?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
          const geom = findGeometryForRecord(record);
          if (!geom?.id) return;
          const selectedIds = getSelectedGeometryIds();
          if (!selectedIds.includes(String(geom.id))) setSelectedGeometryIds([String(geom.id)], String(geom.id));
          updateSelectedGeometry({ center: { lat, lng } });
          const moved = selectedGeometryPolygon();
          const movedRing = moved ? computeSlipPolygonLatLngs(moved) : null;
          const nextWkt = movedRing && movedRing.length >= 3
            ? buildPolygonWkt(movedRing.map((pair) => [pair.lat, pair.lng]))
            : buildPointWkt(lng, lat);
          // Look up the live record instead of trusting the marker's closure-captured `record`
          // (frozen at the last full renderMap() pass) -- any field changed elsewhere (table
          // edit, another user's remote sync) between marker creation and this drag ending
          // would otherwise get silently reverted by this PATCH body.
          const liveRecord = records.find((entry) => String(entry?.measurement_id || "") === String(record?.measurement_id || "")) || record;
          const undoBefore = cloneRecord(liveRecord);
          const nextRecord = {
            ...liveRecord,
            geometry_wkt: nextWkt,
          };
          records = records.map((entry) => (
            String(entry?.measurement_id || "") === String(record?.measurement_id || "")
              ? { ...entry, geometry_wkt: nextWkt }
              : entry
          )); recordsVersion++;
          lastLocalSaveAt = Date.now();
          try {
            await requestJson(`${API_BASE}/${encodeURIComponent(record.measurement_id)}`, {
              method: "PATCH",
              body: nextRecord,
            });
            app.log("slip.geometry_moved", { slip_id: record.slip_id, marina: state.marinaId });
            status = `Moved ${record.slip_id || record.measurement_id}.`;
            pushUndoEntry(`Moved slip ${record.slip_id || record.measurement_id}`, [{
              type: "update",
              measurementId: String(record.measurement_id),
              before: undoBefore,
              after: snapshotRecord(record.measurement_id),
            }]);
          } catch (error) {
            status = error?.message || "Failed to persist moved slip geometry.";
          }
          persist();
          render();
        });
      }
      if (state.showDepths && zoom >= 17) {
        const dv = Number.isFinite(Number(record.measured_depth_feet)) ? Number(record.measured_depth_feet) : null;
        if (dv !== null) {
          const offset = state.showSlipIds && showSlipIdBadges ? "12px" : "-50%";
          L.marker([point.lat, point.lng], {
            interactive: false,
            pane: "slip-id-badge-pane",
            icon: L.divIcon({
              className: "slip-inventory-map-label-wrap",
              iconSize: null,
              iconAnchor: [0, 0],
              html: `<div style="font:600 ${labelFontPx}px/1.2 system-ui,sans-serif;white-space:nowrap;color:#e2e8f0;text-shadow:0 1px 3px rgba(0,0,0,0.9),0 0 6px rgba(0,0,0,0.7);transform:translate(-50%,${offset});pointer-events:none;">${dv.toFixed(1)} ft</div>`,
            }),
          }).addTo(depthMarkersLayer);
        }
      }
    });
    renderPreview();
    renderGeometry();
    renderGeometryPlacePreview();
    renderGeometryRunPreview();
  }

  function findGeometryForRecord(record) {
    const lookup = getPolyLookup();
    const measurementId = String(record?.measurement_id || "");
    if (measurementId) {
      const direct = lookup.byMeasurementId.get(measurementId);
      if (direct) return direct;
    }
    const slipId = String(record?.slip_id || "").trim();
    if (slipId) {
      const sameSlip = lookup.bySlipId.get(slipId) || [];
      if (sameSlip.length === 1) return sameSlip[0];
    }
    const point = pointFromAnyWkt(record?.geometry_wkt);
    if (!point) return null;
    // Rings are computed once per polygon-array version and shared across all
    // callers — previously every unmatched record recomputed every ring.
    const hit = getPolyRings(lookup).find((entry) => pointInPolygon(point, entry.ring)) || null;
    return hit?.poly || null;
  }

  function sortedRecordGeometryRows(recordGeometry = []) {
    const sortKey = String(state.tableSortKey || "slip_id");
    const dir = String(state.tableSortDir || "asc") === "desc" ? -1 : 1;
    const toText = (value) => String(value ?? "").trim().toLowerCase();
    return [...recordGeometry].sort((a, b) => {
      if (sortKey === "slip_id") {
        const av = toText(a?.record?.slip_id);
        const bv = toText(b?.record?.slip_id);
        // Tolerates an optional trailing "-Suffix" segment so this same-dock numeric tiebreak
        // still fires for a 3-part slip_id ("x-011-a") -- any suffix-only difference among
        // same dock+number slips still resolves via the full-string localeCompare below.
        const an = /^(\D*)(\d+)(?:[-.\s_]\S*)?$/.exec(av);
        const bn = /^(\D*)(\d+)(?:[-.\s_]\S*)?$/.exec(bv);
        if (an && bn && an[1] === bn[1]) {
          const diff = Number(an[2]) - Number(bn[2]);
          if (diff !== 0) return diff * dir;
        }
        return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * dir;
      }
      const av = toText(a?.record?.[sortKey]);
      const bv = toText(b?.record?.[sortKey]);
      return av.localeCompare(bv, undefined, { numeric: true, sensitivity: "base" }) * dir;
    });
  }

  function formatCellValue(key, value, displayToleranceFt) {
    if (value == null || value === "") return "";
    if (key === "measured_linear_feet" || key === "measured_width_feet" || key === "marketable_linear_feet" || key === "marketable_width_feet") {
      return formatFeetForDisplay(value, displayToleranceFt);
    }
    if (key === "measured_depth_feet") return Number.isFinite(Number(value)) ? Number(value).toFixed(1) : String(value);
    if (key === "geometry_wkt") {
      const s = String(value);
      return s.length > 60 ? s.slice(0, 57) + "…" : s;
    }
    return String(value);
  }

  function renderCellContent(col, record, displayToleranceFt) {
    const mid = esc(record.measurement_id);
    if (BINARY_COLS.has(col.key)) {
      const checked = String(record[col.key] || "").toLowerCase() === "yes";
      return `<input type="checkbox" data-slip-binary="${esc(col.key)}" data-slip-meas="${mid}" ${checked ? "checked" : ""} style="width:16px;height:16px;cursor:pointer;accent-color:#60a5fa;" />`;
    }
    if (col.key === "space_type") {
      const v = String(record.space_type || "");
      const opts = spaceTypeOptionsFor(v).map((o) => `<option value="${esc(o)}"${v === o ? " selected" : ""}>${esc(o)}</option>`).join("");
      return `<select data-slip-select="${esc(col.key)}" data-slip-meas="${mid}" style="width:100%;background:rgba(2,6,23,.5);border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 2px;font-size:11px;font-family:inherit;text-align:center;"><option value=""></option>${opts}</select>`;
    }
    if (col.key === "out_of_service_reason") {
      const v = String(record.out_of_service_reason || "");
      const oos = String(record.out_of_service || "").toLowerCase() === "yes";
      const opts = OOS_REASON_OPTIONS.map((o) => `<option value="${esc(o)}"${v.toLowerCase() === o.toLowerCase() ? " selected" : ""}>${esc(o)}</option>`).join("");
      return `<select data-slip-select="${esc(col.key)}" data-slip-meas="${mid}" ${oos ? "" : "disabled"} style="width:100%;background:rgba(2,6,23,.5);border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 2px;font-size:11px;font-family:inherit;text-align:center;opacity:${oos ? 1 : 0.4};"><option value=""></option>${opts}</select>`;
    }
    if (col.key === "confidence_score") {
      const v = String(record.confidence_score || "High");
      const opts = ["High", "Medium", "Low"].map((o) => `<option value="${o}"${v === o ? " selected" : ""}>${o}</option>`).join("");
      return `<select data-slip-select="${esc(col.key)}" data-slip-meas="${mid}" style="width:100%;background:rgba(2,6,23,.5);border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 2px;font-size:11px;font-family:inherit;text-align:center;">${opts}</select>`;
    }
    if (!NON_EDITABLE_COLS.has(col.key)) {
      const isNum = NUMERIC_COLS.has(col.key);
      const isDate = col.key === "measurement_date";
      const isComment = col.key === "notes";
      const rawVal = record[col.key];
      let displayVal = "";
      if (rawVal != null && rawVal !== "") {
        if (isNum) { const n = Number(rawVal); displayVal = Number.isFinite(n) ? formatFeetForDisplay(n, displayToleranceFt) : ""; }
        else { displayVal = String(rawVal); }
      }
      return `<input type="${isDate ? "date" : isNum ? "number" : "text"}" data-slip-text="${esc(col.key)}" data-slip-meas="${mid}"
        value="${esc(displayVal)}"${isNum ? ' step="0.1" min="0"' : ""}
        style="width:calc(100% - 4px);background:transparent;border:none;outline:none;border-radius:0;color:#e2e8f0;padding:1px 3px;font-size:11px;font-family:inherit;text-align:${isComment ? "left" : "center"};" />`;
    }
    return esc(formatCellValue(col.key, record[col.key], displayToleranceFt));
  }

  function getSelectedMeasurementIds() {
    const selGeomIds = new Set(getSelectedGeometryIds());
    return records
      .filter((r) => {
        if (r.measurement_id === state.selectedMeasurementId) return true;
        if (tableSelectedMeasurementIds.has(r.measurement_id)) return true;
        const geom = findGeometryForRecord(r);
        const gid = String(geom?.id || "");
        return gid && selGeomIds.has(gid);
      })
      .map((r) => r.measurement_id);
  }

  async function saveRecordField(measurementId, fieldKey, newValue) {
    const idx = records.findIndex((r) => r.measurement_id === measurementId);
    if (idx < 0) return;
    const updated = normalizeRecord({ ...records[idx], [fieldKey]: newValue });
    records[idx] = updated;
    lastLocalSaveAt = Date.now();
    await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "PATCH", body: updated });
  }

  async function saveFieldForSelected(triggerMeasurementId, fieldKey, newValue) {
    const selectedIds = getSelectedMeasurementIds();
    const idsToSave = selectedIds.length > 0 && selectedIds.includes(triggerMeasurementId)
      ? selectedIds
      : [triggerMeasurementId];
    const slipIdChanges = [];
    const undoChanges = [];

    // Optimistic update all matching records
    for (const mid of idsToSave) {
      const idx = records.findIndex((r) => r.measurement_id === mid);
      if (idx < 0) continue;
      const current = records[idx];
      const before = cloneRecord(current);
      const nextPatch = { ...current, [fieldKey]: newValue };
      if (fieldKey === "dock_name") {
        const nextSlipId = slipIdForDockNameChange(current, newValue, state.slipIdSeparator);
        nextPatch.slip_id = nextSlipId;
        if (String(current.slip_id || "") !== String(nextSlipId || "")) {
          slipIdChanges.push({ measurementId: String(mid), previousSlipId: String(current.slip_id || ""), nextSlipId: String(nextSlipId || "") });
        }
      }
      if (fieldKey === "suffix") {
        const nextSlipId = slipIdForSuffixChange(current, newValue, state.slipIdSeparator);
        nextPatch.slip_id = nextSlipId;
        if (String(current.slip_id || "") !== String(nextSlipId || "")) {
          slipIdChanges.push({ measurementId: String(mid), previousSlipId: String(current.slip_id || ""), nextSlipId: String(nextSlipId || "") });
        }
      }
      records[idx] = normalizeRecord(nextPatch);
      undoChanges.push({ type: "update", measurementId: String(mid), before, after: cloneRecord(records[idx]) });
    }

    if (slipIdChanges.length) {
      state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => {
        const linkedMeasurementId = String(poly?.linkedMeasurementId || "");
        const byMeasurement = slipIdChanges.find((change) => change.measurementId === linkedMeasurementId);
        if (byMeasurement) return { ...poly, linkedSlipId: byMeasurement.nextSlipId };
        if (linkedMeasurementId) return poly;
        const linkedSlipId = String(poly?.linkedSlipId || "");
        const bySlipId = slipIdChanges.find((change) => change.previousSlipId && change.previousSlipId === linkedSlipId);
        return bySlipId ? { ...poly, linkedSlipId: bySlipId.nextSlipId } : poly;
      });
      persist();
    }

    lastLocalSaveAt = Date.now();
    recordsVersion++;
    renderBottomPanel();
    renderMap();
    renderGeometry();
    // Fire PATCH for all in parallel, report first error
    const errors = (await Promise.all(
      idsToSave.map((mid) => {
        const rec = records.find((r) => r.measurement_id === mid);
        if (!rec) return Promise.resolve(null);
        return requestJson(`${API_BASE}/${encodeURIComponent(mid)}`, { method: "PATCH", body: rec })
          .then(() => null)
          .catch((err) => err?.message || String(err));
      })
    )).filter(Boolean);
    if (!errors.length) {
      pushUndoEntry(`Edited ${fieldKey} on ${undoChanges.length} slip${undoChanges.length === 1 ? "" : "s"}`, undoChanges);
      app.log("slip.field_edited", { field: fieldKey, count: idsToSave.length, marina: state.marinaId });
      renderBottomPanel();
    }
    if (errors.length) { status = `${errors.length} save error(s): ${errors[0]}`; render(); }
  }

  async function swapLWForSelected() {
    const idsToSave = getSelectedMeasurementIds();
    if (!idsToSave.length) return;
    const undoChanges = [];
    for (const mid of idsToSave) {
      const idx = records.findIndex((r) => r.measurement_id === mid);
      if (idx < 0) continue;
      const cur = records[idx];
      const before = cloneRecord(cur);
      records[idx] = normalizeRecord({ ...cur, measured_linear_feet: cur.measured_width_feet, measured_width_feet: cur.measured_linear_feet });
      undoChanges.push({ type: "update", measurementId: String(mid), before, after: cloneRecord(records[idx]) });
    }
    lastLocalSaveAt = Date.now();
    recordsVersion++;
    renderBottomPanel();
    renderMap();
    renderGeometry();
    const errors = (await Promise.all(
      idsToSave.map((mid) => {
        const rec = records.find((r) => r.measurement_id === mid);
        if (!rec) return Promise.resolve(null);
        return requestJson(`${API_BASE}/${encodeURIComponent(mid)}`, { method: "PATCH", body: rec })
          .then(() => null)
          .catch((err) => err?.message || String(err));
      })
    )).filter(Boolean);
    if (!errors.length) {
      pushUndoEntry(`Swapped L/W on ${undoChanges.length} slip${undoChanges.length === 1 ? "" : "s"}`, undoChanges);
      app.log("slip.lw_swapped", { count: idsToSave.length, marina: state.marinaId });
      renderBottomPanel();
    }
    if (errors.length) { status = `${errors.length} save error(s): ${errors[0]}`; render(); }
  }

  function renderColCfgPanel(cfg) {
    const rows = cfg.order.map((key) => {
      const col = ALL_COLUMNS.find((c) => c.key === key);
      if (!col) return "";
      const checked = !cfg.hidden.has(key);
      return `<div class="slip-col-cfg-row" data-cfg-key="${esc(key)}" draggable="true" style="display:flex;align-items:center;gap:8px;padding:4px 6px;border-radius:4px;cursor:grab;user-select:none;background:transparent;border:1px solid transparent;">
        <span style="color:rgba(148,163,184,.5);font-size:14px;line-height:1;">⠿</span>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;flex:1;min-width:0;">
          <input type="checkbox" data-cfg-toggle="${esc(key)}" ${checked ? "checked" : ""} />
          <span style="font-size:12px;white-space:nowrap;">${esc(col.label)}</span>
          <span class="small" style="color:rgba(148,163,184,.5);font-size:10px;">${esc(col.key)}</span>
        </label>
        <button class="btn" data-cfg-reset-width="${esc(key)}" style="padding:1px 5px;font-size:10px;" title="Reset width">↺</button>
      </div>`;
    }).join("");
    return `
      <div id="slip-col-cfg-panel" style="position:absolute;top:0;right:0;bottom:0;width:300px;background:rgba(15,23,42,.97);border-left:1px solid rgba(148,163,184,.2);z-index:10;display:flex;flex-direction:column;padding:10px;overflow:hidden;">
        <div class="row" style="justify-content:space-between;align-items:center;margin-bottom:8px;">
          <div class="lm-title" style="margin:0;">Column Config</div>
          <div class="row" style="gap:6px;">
            <button id="slip-col-cfg-reset-all" class="btn" style="font-size:11px;">Reset All</button>
            <button id="slip-col-cfg-close" class="btn" style="font-size:11px;">Done</button>
          </div>
        </div>
        <div class="small" style="color:rgba(148,163,184,.6);margin-bottom:6px;">Drag to reorder · Toggle visibility · ↺ reset width</div>
        <div id="slip-col-cfg-list" style="flex:1;overflow-y:auto;display:flex;flex-direction:column;gap:2px;">
          ${rows}
        </div>
      </div>`;
  }

  let _lastPanelKey = "";
  // Row virtualization state for the bottom table. A large marina's table used to build
  // one <tr> string per record on every render (selection, sort, filter, edit all rebuild
  // it) and rebind listeners per-row -- cost scaled with marina size, not what's actually
  // scrolled into view. Now renderBottomPanel only materializes a windowed slice around
  // the current scroll position (see buildTbodyHtml/computeTableWindow below), with two
  // spacer rows preserving correct total scroll height, and pure scrolling updates just
  // that slice via updateTableWindowFromScroll() without rebuilding the whole panel.
  // Row-level interactivity (edit, select, bulk-edit) is delegated to the stable
  // #slip-bottom-table-scroll container (see bindBottomPanelControls) rather than bound
  // per-row, so swapping which rows exist never needs to rebind anything.
  let _visibleRecordGeometryCache = [];
  let _tableRowRenderCtx = null;
  let _tableWindowStart = 0;
  let _tableWindowEnd = 0;
  let _measuredRowHeightPx = 0;
  const TABLE_ROW_HEIGHT_ESTIMATE = 22;
  const TABLE_ROW_BUFFER = 20;

  function computeTableWindow(totalRows, scrollTop, viewportHeight, rowHeight) {
    if (!totalRows) return { start: 0, end: 0 };
    const rh = rowHeight > 0 ? rowHeight : TABLE_ROW_HEIGHT_ESTIMATE;
    const firstVisible = Math.floor(Math.max(0, scrollTop) / rh);
    const visibleCount = Math.ceil((viewportHeight || 400) / rh) + 1;
    const start = Math.max(0, firstVisible - TABLE_ROW_BUFFER);
    const end = Math.min(totalRows, firstVisible + visibleCount + TABLE_ROW_BUFFER);
    return { start, end };
  }

  function buildSlipRowHtml(record, geom, ctx) {
    const { cols, cfg, displayToleranceFt, selectedGeomIds } = ctx;
    const geomId = String(geom?.id || "");
    const rowActive = record.measurement_id === state.selectedMeasurementId || (geomId && selectedGeomIds.has(geomId));
    const cells = cols.map((col) => {
      const w = getColWidth(cfg, col.key);
      if (col.key === "slip_id") {
        return `<td style="padding:2px 5px;width:${w}px;max-width:${w}px;text-align:center;color:rgba(148,163,184,.55);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" title="Slip ID is generated from Dock + Slip Number -- edit those fields instead.">${esc(record.slip_id || "")}</td>`;
      }
      const isBin = BINARY_COLS.has(col.key);
      if (!NON_EDITABLE_COLS.has(col.key)) {
        return `<td style="padding:2px 5px;width:${w}px;max-width:${w}px;text-align:${isBin || col.key !== "notes" ? "center" : "left"};">${renderCellContent(col, record, displayToleranceFt)}</td>`;
      }
      const isLong = col.key === "geometry_wkt" || col.key === "record_created_timestamp";
      return `<td style="padding:2px 5px;width:${w}px;max-width:${w}px;text-align:${col.key === "notes" ? "left" : "center"};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;"${isLong ? ` title="${esc(String(record[col.key] || ""))}"` : ""}>${esc(formatCellValue(col.key, record[col.key], displayToleranceFt))}</td>`;
    }).join("");
    return `<tr data-slip-row="${esc(record.measurement_id)}" data-geom-id="${esc(geomId)}"
      style="cursor:pointer;background:${rowActive ? "rgba(245,158,11,0.12)" : "transparent"};">${cells}</tr>`;
  }

  function buildTbodySpacerRow(colCount, heightPx) {
    if (heightPx <= 0) return "";
    return `<tr aria-hidden="true" style="height:${heightPx}px;"><td colspan="${colCount}" style="padding:0;border:none;"></td></tr>`;
  }

  function buildTbodyHtml(entries, ctx, win) {
    if (!entries.length) {
      return `<tr><td colspan="${ctx.cols.length}" class="small" style="padding:8px;">No slip inventory rows found.</td></tr>`;
    }
    const rowHeight = _measuredRowHeightPx || TABLE_ROW_HEIGHT_ESTIMATE;
    const topSpacer = buildTbodySpacerRow(ctx.cols.length, win.start * rowHeight);
    const bottomSpacer = buildTbodySpacerRow(ctx.cols.length, (entries.length - win.end) * rowHeight);
    const rowsHtml = entries.slice(win.start, win.end).map(({ record, geom }) => buildSlipRowHtml(record, geom, ctx)).join("");
    return topSpacer + rowsHtml + bottomSpacer;
  }

  function measureTableRowHeight(tbody) {
    if (_measuredRowHeightPx) return;
    const sampleRow = tbody?.querySelector?.("tr[data-slip-row]");
    const h = sampleRow?.getBoundingClientRect?.().height;
    if (h > 0) _measuredRowHeightPx = h;
  }

  // Pure-scroll path: recompute the window from the cached (already sorted/filtered) record
  // list and swap just the tbody, skipping the full renderBottomPanel/bindBottomPanelControls
  // pass entirely -- no re-sort, no header rebuild, no listener rebinding (delegation on
  // #slip-bottom-table-scroll covers whatever rows land in the new tbody automatically).
  function updateTableWindowFromScroll() {
    const scrollEl = document.getElementById("slip-bottom-table-scroll");
    const tbody = document.getElementById("slip-bottom-tbody");
    if (!scrollEl || !tbody || !_tableRowRenderCtx) return;
    if (slipTableHasFocusedEditor()) return;
    const entries = _visibleRecordGeometryCache;
    const win = computeTableWindow(entries.length, scrollEl.scrollTop, scrollEl.clientHeight, _measuredRowHeightPx);
    if (win.start === _tableWindowStart && win.end === _tableWindowEnd) return;
    _tableWindowStart = win.start;
    _tableWindowEnd = win.end;
    tbody.innerHTML = buildTbodyHtml(entries, _tableRowRenderCtx, win);
    measureTableRowHeight(tbody);
  }

  // Scrolls the (virtualized) bottom table so the given record's row is in view, centering it
  // when a jump is needed. Called after a map-originated selection (badge/line click) so picking
  // a slip on the map brings its table row along -- table-originated selections don't call this,
  // since the user is already looking at the table in that case.
  function scrollTableRowIntoView(measurementId) {
    const scrollEl = document.getElementById("slip-bottom-table-scroll");
    if (!scrollEl || !measurementId) return;
    const entries = _visibleRecordGeometryCache;
    const index = entries.findIndex(({ record }) => record?.measurement_id === measurementId);
    if (index < 0) return;
    const rowHeight = _measuredRowHeightPx || TABLE_ROW_HEIGHT_ESTIMATE;
    const rowTop = index * rowHeight;
    const rowBottom = rowTop + rowHeight;
    const viewTop = scrollEl.scrollTop;
    const viewBottom = viewTop + scrollEl.clientHeight;
    if (rowTop >= viewTop && rowBottom <= viewBottom) return; // already visible -- don't jump the scroll position needlessly
    const maxScrollTop = Math.max(0, entries.length * rowHeight - scrollEl.clientHeight);
    const target = Math.min(maxScrollTop, Math.max(0, rowTop - (scrollEl.clientHeight / 2) + (rowHeight / 2)));
    scrollEl.scrollTop = target;
    bottomTableScrollTop = target;
    updateTableWindowFromScroll();
  }

  function renderBottomPanel({ force = false } = {}) {
    const _selectedGeomIds = getSelectedGeometryIds();
    const _modeKey = `${geometryRunLayoutActive ? 1 : 0}${activeGeometryPlaceMode ? 1 : 0}${lineLayoutActive ? 1 : 0}`;
    const _filterKey = [...(state.filterHiddenValues?.[state.colorSlipsBy] || [])].sort().join(",");
    // `status` is deliberately NOT part of this key: the table never renders it,
    // and every hotkey/nudge writes a new status string — keying on it forced a
    // full 900+ row rebuild per keystroke.
    const _panelKey = `${recordsVersion}|${state.selectedMeasurementId}|${_selectedGeomIds.join(",")}|${state.tableSortDir}|${state.colorSlipsBy}|${state.slipIdLabelSizePx}|${_modeKey}|${_filterKey}`;
    if (!force && _panelKey === _lastPanelKey) return;
    _perfLastPanelKey = _panelKey;
    _lastPanelKey = _panelKey;
    _perfPanelRebuildCount++;
    const current = selectedRecord();
    const selectedGeomIds = new Set(_selectedGeomIds);
    const recordGeometry = sortedRecordGeometryRows(records.map((record) => ({ record, geom: findGeometryForRecord(record) })));
    const filterMode = String(state.colorSlipsBy || "none");
    const filterHiddenArr = Array.isArray(state.filterHiddenValues?.[filterMode]) ? state.filterHiddenValues[filterMode] : [];
    const filterHiddenSet = new Set(filterHiddenArr);
    const filterOptions = (() => {
      if (filterMode === "attribute") {
        return currentAttributeColoring().entries.map((e) => ({ value: e.key, label: e.label, color: e.color }));
      }
      if (filterMode === "confidence") {
        return [
          { value: "High", label: "High", color: "#22c55e" },
          { value: "Medium", label: "Medium", color: "#f59e0b" },
          { value: "Low", label: "Low", color: "#ef4444" },
        ];
      }
      if (filterMode === "space_type") {
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const t = String(r?.space_type || "").trim() || "unknown";
          if (!seen.has(t)) { seen.add(t); opts.push({ value: t, label: t, color: spaceTypeColorMap.get(t) ?? "#38bdf8" }); }
        });
        return opts.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
      }
      if (filterMode === "dimension") {
        const tol = Number(state.displayMeasurementToleranceFt) >= 1 ? 1 : 0.5;
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const l = Number(r?.measured_linear_feet);
          const w = Number(r?.measured_width_feet);
          if (!Number.isFinite(l) || !Number.isFinite(w)) return;
          const key = dimensionKey(l, w, tol);
          if (key && !seen.has(key)) { seen.add(key); opts.push({ value: key, label: `${key} ft`, color: paletteColorForKey(key) }); }
        });
        return opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" }));
      }
      if (filterMode === "multislip") {
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const mid = String(r?.multislip_id || "").trim();
          if (!mid || seen.has(mid)) return;
          seen.add(mid);
          opts.push({ value: mid, label: mid, color: multislipColorMap.get(mid) ?? "#38bdf8" });
        });
        return opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" }));
      }
      if (filterMode === "region") {
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const region = String(r?.region || "").trim() || "unassigned";
          if (seen.has(region)) return;
          seen.add(region);
          opts.push({ value: region, label: region, color: regionColorMap.get(region) ?? "#38bdf8" });
        });
        return opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" }));
      }
      if (filterMode === "marina_name") {
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const name = String(r?.marina_name || "").trim() || "unassigned";
          if (seen.has(name)) return;
          seen.add(name);
          opts.push({ value: name, label: name, color: marinaNameColorMap.get(name) ?? "#38bdf8" });
        });
        return opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" }));
      }
      if (filterMode === "dock") {
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const dock = String(r?.dock_name || "").trim() || "unassigned";
          if (seen.has(dock)) return;
          seen.add(dock);
          opts.push({ value: dock, label: dock, color: dockColorMap.get(dock) ?? "#38bdf8" });
        });
        return opts.sort((a, b) => a.value.localeCompare(b.value, undefined, { numeric: true, sensitivity: "base" }));
      }
      if (filterMode === "multislip_integrity") {
        const order = ["orphan", "missing_id", "disconnected", "valid", "none"];
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const key = multislipIntegrityKeyForRecord(r, multislipIntegrityStatusMap);
          if (seen.has(key)) return;
          seen.add(key);
          opts.push({ value: key, label: MULTISLIP_INTEGRITY_INFO[key].label, color: MULTISLIP_INTEGRITY_INFO[key].color });
        });
        return opts.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
      }
      if (filterMode === "duplicate_slips") {
        const order = ["overlapping", "clear"];
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const key = duplicateSlipKeyForRecord(r, overlappingSlipIds);
          if (seen.has(key)) return;
          seen.add(key);
          opts.push({ value: key, label: DUPLICATE_SLIP_INFO[key].label, color: DUPLICATE_SLIP_INFO[key].color });
        });
        return opts.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
      }
      if (filterMode === "duplicate_slip_id") {
        const order = ["duplicate", "clear"];
        const opts = [];
        const seen = new Set();
        records.forEach((r) => {
          const key = duplicateSlipIdKeyForRecord(r, duplicateSlipIdSet);
          if (seen.has(key)) return;
          seen.add(key);
          opts.push({ value: key, label: DUPLICATE_SLIP_ID_INFO[key].label, color: DUPLICATE_SLIP_ID_INFO[key].color });
        });
        return opts.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
      }
      return [];
    })();
    const filterHiddenCount = filterOptions.filter((o) => filterHiddenSet.has(o.value)).length;
    const filterLabel = filterHiddenCount > 0 ? ` (${filterOptions.length - filterHiddenCount}/${filterOptions.length})` : "";
    const visibleRecordGeometry = filterHiddenCount > 0
      ? recordGeometry.filter(({ record }) => isRecordVisible(record))
      : recordGeometry;
    const activeSortKey = String(state.tableSortKey || "slip_id");
    const sortArrow = String(state.tableSortDir || "asc") === "desc" ? "▼" : "▲";
    const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);
    const cfg = readColCfg();
    const cols = visibleCols(cfg);
    const swapLWCount = getSelectedMeasurementIds().length;

    const headerCells = cols.map((col) => {
      const w = getColWidth(cfg, col.key);
      const isActiveSort = col.key === activeSortKey;
      return `<th data-col-key="${esc(col.key)}" draggable="true"
          style="position:relative;width:${w}px;min-width:40px;text-align:${col.key === "notes" ? "left" : "center"};padding:2px 18px 2px 5px;white-space:nowrap;cursor:grab;user-select:none;background:rgba(15,23,42,.95);">
        <button data-sort-key="${esc(col.key)}" type="button" class="btn" style="padding:1px 5px;font-size:11px;">${esc(col.label)}${isActiveSort ? ` ${sortArrow}` : ""}</button>
        <div class="slip-col-resize" data-resize-key="${esc(col.key)}"
          style="position:absolute;right:0;top:0;width:6px;height:100%;cursor:col-resize;background:transparent;z-index:2;"></div>
      </th>`;
    }).join("");

    _visibleRecordGeometryCache = visibleRecordGeometry;
    const rowRenderCtx = { cols, cfg, displayToleranceFt, selectedGeomIds };
    _tableRowRenderCtx = rowRenderCtx;
    const existingScrollEl = document.getElementById("slip-bottom-table-scroll");
    const viewportHeight = existingScrollEl?.clientHeight || 400;
    // Preserve whatever window the previous render already had if the scroll position
    // hasn't actually moved (e.g. a selection/sort/edit rebuild) -- computeTableWindow is
    // cheap either way, this just keeps the same math as the pure-scroll path.
    const win = computeTableWindow(visibleRecordGeometry.length, bottomTableScrollTop, viewportHeight, _measuredRowHeightPx);
    _tableWindowStart = win.start;
    _tableWindowEnd = win.end;
    const rows = buildTbodyHtml(visibleRecordGeometry, rowRenderCtx, win);

    const html = `
      <style id="slip-inv-styles">
        #slip-inventory-bottom { display:flex; flex-direction:column; padding:8px; box-sizing:border-box; overflow:hidden; }
        #slip-inventory-bottom .card { flex:1; min-height:0; display:flex; flex-direction:column; padding:8px; position:relative; }
        #slip-inv-controls { flex-shrink:0; }
        #slip-bottom-table-scroll { flex:1 1 0; min-height:220px; overflow:auto; border:1px solid rgba(148,163,184,.18); border-radius:8px; position:relative; }
        #slip-bottom-table-scroll table { border-collapse:collapse; font-size:11px; table-layout:fixed; }
        #slip-bottom-table-scroll th { border:1px solid rgba(148,163,184,.12); border-bottom:1px solid rgba(148,163,184,.22); }
        #slip-bottom-table-scroll td { border:1px solid rgba(148,163,184,.08); }
        #slip-bottom-table-scroll tr:hover td { background:rgba(148,163,184,.04); }
        .slip-cell-input { width:calc(100% - 4px); background:transparent; border:none; outline:none; color:#e2e8f0; padding:1px 3px; font-size:11px; font-family:inherit; }
        .slip-cell-input:focus { background:rgba(96,165,250,.08); border-radius:2px; }
        .slip-cell-select { width:100%; background:rgba(2,6,23,.5); border:none; outline:none; color:#e2e8f0; padding:1px 2px; font-size:11px; font-family:inherit; }
        .slip-cell-select:focus { background:rgba(96,165,250,.1); }
        #slip-bottom-table-scroll.select-mode { cursor:pointer; }
        #slip-bottom-table-scroll.select-mode tr:hover td { background:rgba(96,165,250,.08); }
        #slip-bottom-table-scroll.select-mode input,
        #slip-bottom-table-scroll.select-mode select { cursor:pointer; }
      </style>
      <div id="slip-inventory-bottom">
        <div class="card">
          <div id="slip-inv-controls" class="row" style="justify-content:space-between;align-items:center;gap:8px;margin-bottom:6px;">
            <div>
              <div class="lm-title">Slip Inventory</div>
              <div class="small">${esc(records.length ? `${records.length} record${records.length === 1 ? "" : "s"}` : "No records loaded.")}${filterProjectId ? esc(` — filtered to ${filterProjectId}`) : " — all marinas"}</div>
            </div>
            <div class="row" style="gap:8px;align-items:center;flex-wrap:wrap;">
              <label class="small" style="display:flex;align-items:center;gap:4px;white-space:nowrap;">Color By
                <select id="slip-color-by-table" style="min-width:110px;margin-left:4px;">
                  ${renderColorSlipsByOptions(filterMode)}
                </select>
              </label>
              ${filterOptions.length > 0 ? `<div style="position:relative;">
                <button id="slip-filter-toggle" type="button" class="btn" style="font-size:11px;padding:3px 10px;">Filter${filterLabel} ▾</button>
                <div id="slip-filter-panel" style="display:none;position:absolute;top:calc(100% + 3px);right:0;z-index:200;background:rgba(15,23,42,.97);border:1px solid rgba(148,163,184,.22);border-radius:6px;padding:8px;min-width:170px;max-height:260px;overflow-y:auto;box-shadow:0 4px 16px rgba(0,0,0,.45);">
                  <div style="display:flex;gap:6px;margin-bottom:6px;">
                    <button id="slip-filter-select-all" type="button" class="btn" style="font-size:10px;padding:2px 8px;">Select All</button>
                    <button id="slip-filter-deselect-all" type="button" class="btn" style="font-size:10px;padding:2px 8px;">Deselect All</button>
                  </div>
                  ${filterOptions.map((opt) => `<label style="display:flex;align-items:center;gap:6px;padding:3px 0;font-size:11px;cursor:pointer;white-space:nowrap;"><input type="checkbox" data-slip-filter-val="${esc(opt.value)}"${filterHiddenSet.has(opt.value) ? "" : " checked"} /><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${esc(opt.color)};flex-shrink:0;"></span>${esc(opt.label)}</label>`).join("")}
                </div>
              </div>` : ""}
              ${filterHiddenCount > 0 ? `<span class="small" style="color:rgba(251,191,36,.8);">${visibleRecordGeometry.length}/${recordGeometry.length}</span>` : ""}
              ${filterProjectId ? `<label class="small" style="display:flex;align-items:center;gap:4px;"><input type="checkbox" id="slip-show-no-marina" ${showNoMarinaCode ? "checked" : ""} /> Include unassigned</label>` : ""}
              <label class="small">L/W Tol
                <select id="slip-display-measurement-tol" style="min-width:70px;">
                  <option value="0.1"${displayToleranceFt === 0.1 ? " selected" : ""}>0.1 ft</option>
                  <option value="0.25"${displayToleranceFt === 0.25 ? " selected" : ""}>0.25 ft</option>
                  <option value="0.5"${displayToleranceFt === 0.5 ? " selected" : ""}>0.5 ft</option>
                  <option value="1"${displayToleranceFt === 1 ? " selected" : ""}>1.0 ft</option>
                </select>
              </label>
              <label class="small" title="Map slip ID badge font size" style="display:flex;align-items:center;gap:5px;white-space:nowrap;">ID Size
                <input id="slip-id-label-size" type="range" min="8" max="28" step="1" value="${esc(state.slipIdLabelSizePx || 13)}" style="width:82px;" />
                <input id="slip-id-label-size-value" type="number" min="8" max="28" step="1" value="${esc(state.slipIdLabelSizePx || 13)}" style="width:48px;" />
              </label>
              <button id="slip-bottom-refresh" class="btn">Refresh</button>
              <button id="slip-bottom-show-selected" class="btn"${current ? "" : " disabled"}>Show Selected</button>
              <button id="slip-table-swap-lw" class="btn" title="Swap measured length and width for selected rows"${swapLWCount ? "" : " disabled"}>${swapLWCount > 1 ? `⇄ Swap L/W (${swapLWCount})` : "⇄ Swap L/W"}</button>
              <button id="slip-consolidate-xdock" class="btn" title="Renumber every X-dock slip in this marina to X-001, X-002, ... in their current order, closing any gaps">Consolidate X Dock</button>
              <button id="slip-consolidate-multislip" class="btn" title="Renumber every multislip group (2+ slips) in this marina to MS-001, MS-002, ... in their current order. Groups with no partner slip get selected for review instead of being touched.">Consolidate Multislip IDs</button>
              <button id="slip-clear-multislip-tag" class="btn" title="Remove multislip_yn/multislip_id from the currently selected row(s)"${swapLWCount ? "" : " disabled"}>Clear Multislip Tag${swapLWCount > 1 ? ` (${swapLWCount})` : ""}</button>
              <button id="slip-col-cfg-open" class="btn" title="Configure columns" style="padding:4px 8px;">⚙ Columns</button> ${renderSlipExportMenu({ disabled: !records.length })}
            </div>
          </div>
          <div id="slip-bottom-table-scroll">
            <table>
              <thead style="position:sticky;top:0;z-index:1;">
                <tr>${headerCells}</tr>
              </thead>
              <tbody id="slip-bottom-tbody">
                ${rows}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
    if (!tablePanelEl) return;
    tablePanelEl.innerHTML = html;
    measureTableRowHeight(document.getElementById("slip-bottom-tbody"));
    bindBottomPanelControls();
  }

  function synchronizeCommittedGeometryFromRecords() {
    const existing = Array.isArray(state.slipGeometryPolygons) ? state.slipGeometryPolygons : [];
    const uncommitted = existing.filter((poly) => !poly?.committed);
    const existingCommittedByMeasurementId = new Map(
      existing
        .filter((poly) => poly?.committed && poly?.linkedMeasurementId)
        .map((poly) => [String(poly.linkedMeasurementId), poly])
    );
    const rebuiltCommitted = [];
    for (const record of records) {
      const measurementId = String(record?.measurement_id || "");
      if (!measurementId) continue;
      const ring = parsePolygonWkt(record?.geometry_wkt);
      if (!Array.isArray(ring) || ring.length < 3) continue;
      const inferred = inferPolyMetricsFromRing(ring);
      if (!inferred) continue;
      const prev = existingCommittedByMeasurementId.get(measurementId) || null;
      const slipId = String(record?.slip_id || "");
      // Prefer the stored measured_linear_feet/measured_width_feet over the ring's inferred
      // bounding-box fit: inferPolyMetricsFromRing() is exact for a true rectangle, but a
      // mitred chain segment at a dock bend is a trapezoid, and the min-area bounding box of a
      // trapezoid doesn't equal the actual dock-edge length that was measured when the slip
      // was drawn (commitChain's slipAlongFt) -- it can end up matching the mitred offshore
      // edge instead, whichever is longer. The stored field is the exact value computed at
      // commit time by whichever tool drew it; only fall back to inference for legacy/imported
      // records that never had it.
      const storedLengthFt = Number(record?.measured_linear_feet);
      const storedWidthFt = Number(record?.measured_width_feet);
      rebuiltCommitted.push({
        id: String(prev?.id || makeId("GEOM")),
        typeId: String(prev?.typeId || state.slipGeometryDraftTypeId || ""),
        center: { lat: Number(inferred.center.lat), lng: Number(inferred.center.lng) },
        lengthFt: storedLengthFt > 0 ? storedLengthFt : Number(inferred.lengthFt || 0),
        widthFt: storedWidthFt > 0 ? storedWidthFt : Number(inferred.widthFt || 0),
        headingDeg: Number(inferred.headingDeg || 0),
        ringLatLngs: ring.map((pair) => [Number(pair[0]), Number(pair[1])]),
        freeform: !!(prev?.freeform) || ring.length >= 6,
        locked: true,
        committed: true,
        linkedMeasurementId: measurementId,
        linkedSlipId: slipId,
      });
    }
    state.slipGeometryPolygons = [...uncommitted, ...rebuiltCommitted];
    const selectedIds = getSelectedGeometryIds().filter((id) => state.slipGeometryPolygons.some((poly) => String(poly?.id || "") === String(id)));
    if (selectedIds.length !== getSelectedGeometryIds().length) {
      setSelectedGeometryIds(selectedIds, selectedIds[0] || "");
    }
  }

  async function refreshRecords({ quiet = false } = {}) {
    if (!quiet) taskStatus(filterProjectId ? `Fetching ${filterProjectId} records…` : "Fetching records…", 15);
    try {
      const params = new URLSearchParams({ limit: "5000" });
      if (filterProjectId) {
        params.set("marina_id", filterProjectId);
        if (showNoMarinaCode) params.set("include_unassigned", "1");
      }
      const payload = await requestJson(`${API_BASE}?${params.toString()}`);
      const allItems = Array.isArray(payload?.items) ? payload.items : [];
      if (!quiet) taskStatus("Building table…", 60);
      records = allItems.map((item) => normalizeRecord(item)); recordsVersion++;
      if (draftsVisible) { draftsStatusCache.key = ""; renderDrafts(); }
      synchronizeCommittedGeometryFromRecords();
      syncSelection();
      renderMap();
      if (!quiet) taskStatus("Rendering…", 85);
      renderBottomPanel();
      if (!quiet) status = `Loaded ${records.length} slip inventory record${records.length === 1 ? "" : "s"}.`;
      render();
      if (!quiet) taskIdle();
    } catch (error) {
      if (!quiet) taskIdle();
      status = error?.message || "Failed to load slip inventory records.";
      render();
      throw error;
    }
  }

  function slipTableHasFocusedEditor() {
    const active = document.activeElement;
    if (!active) return false;
    const host = document.getElementById("slip-inventory-bottom");
    if (!host || !host.contains(active)) return false;
    const tag = String(active.tagName || "").toLowerCase();
    return tag === "input" || tag === "select" || tag === "textarea" || active.isContentEditable === true;
  }

  function shouldSkipRemoteSync() {
    if (!initialFetchDone) return true;
    if (remoteSyncInFlight) return true;
    // A backgrounded/minimized tab has no one watching for other users' edits — don't burn
    // a full unfiltered-then-client-filtered fetch every interval while it's hidden.
    if (document.visibilityState === "hidden") return true;
    if (Date.now() - lastLocalSaveAt < LOCAL_SAVE_GRACE_MS) return true;
    if (slipTableHasFocusedEditor()) return true;
    if (bottomTablePointerInside) return true;
    if (Date.now() < bottomTableScrollActiveUntil) return true;
    if (activeSlipDragCount > 0) return true;
    return false;
  }

  async function syncRemoteRecords(reason = "interval") {
    if (shouldSkipRemoteSync()) return;
    remoteSyncInFlight = true;
    try {
      await refreshRecords({ quiet: true });
      app.log?.("slip.remote_sync", { reason, marina: filterProjectId || state.marinaId || "" });
    } catch (error) {
      console.warn("[Slip Inventory] remote sync failed", error);
    } finally {
      remoteSyncInFlight = false;
    }
  }

  function startRemoteSync() {
    if (!remoteSyncTimer) {
      remoteSyncTimer = setInterval(() => {
        if (typeof requestIdleCallback !== "undefined") {
          requestIdleCallback(() => syncRemoteRecords("interval").catch(() => {}), { timeout: 4000 });
        } else {
          syncRemoteRecords("interval").catch(() => {});
        }
      }, REMOTE_SYNC_INTERVAL_MS);
    }
    if (!remoteSyncFocusHandler) {
      remoteSyncFocusHandler = () => {
        syncRemoteRecords("focus").catch(() => {});
      };
      window.addEventListener("focus", remoteSyncFocusHandler);
    }
    if (!remoteSyncVisibilityHandler) {
      remoteSyncVisibilityHandler = () => {
        if (document.visibilityState === "visible") {
          syncRemoteRecords("visible").catch(() => {});
        }
      };
      document.addEventListener("visibilitychange", remoteSyncVisibilityHandler);
    }
  }

  function stopRemoteSync() {
    if (remoteSyncTimer) {
      clearInterval(remoteSyncTimer);
      remoteSyncTimer = null;
    }
    if (remoteSyncFocusHandler) {
      window.removeEventListener("focus", remoteSyncFocusHandler);
      remoteSyncFocusHandler = null;
    }
    if (remoteSyncVisibilityHandler) {
      document.removeEventListener("visibilitychange", remoteSyncVisibilityHandler);
      remoteSyncVisibilityHandler = null;
    }
    remoteSyncInFlight = false;
  }

  function stopPointDrop(reason = "stopped") {
    if (reason === "replaced") for (const cb of slipToolStartListeners) cb();
    if (pointDropOff) {
      pointDropOff();
      pointDropOff = null;
    }
    pointDropActive = false;
    try { map.getContainer().style.cursor = ""; } catch {}
    if (reason === "cancelled") {
      status = "Point drop cancelled.";
      render();
    }
  }

  function stopLineLayout(reason = "stopped") {
    if (lineLayoutOff) {
      lineLayoutOff();
      lineLayoutOff = null;
    }
    lineLayoutActive = false;
    lineEndpoints = [];
    lineLayoutMode = "endpoint";
    lineHoverPoint = null;
    lineAnchorPoint = null;
    imageryEstimateDebug = null;
    try { map.getContainer().style.cursor = ""; } catch {}
    renderPreview();
    if (reason === "cancelled") {
      status = "Line layout cancelled.";
      render();
    }
  }

  // Guards every slip-creation/duplication path against silently colliding with a slip_id
  // already used by a DIFFERENT record in this marina+region. previewSlipIds/
  // buildSlipIdFromState are pure string generation (Dock + Slip Number + increment) with
  // zero awareness of what already exists in the database -- without this check, a
  // coincidentally-reused ID either silently overwrote the unrelated existing slip (the old
  // upsert-by-slip_id-text behavior in commitGeometryRunToRecords/
  // commitLineFeatureFromCurrentLine) or minted an invisible duplicate slip_id (every other
  // creation path, which never checked at all).
  // Scoped by region as well as marina_id: some marinas have multiple yards that legitimately
  // reuse the same slip numbering per yard, and Region is exactly the field that
  // disambiguates them -- two slips sharing an id is only a real conflict when they'd also
  // land in the same region.
  // excludeMeasurementId lets a genuine update-in-place (re-linked via a real
  // linkedMeasurementId, not by matching slip_id text) skip flagging itself as a conflict.
  // Returns a ready-to-display message, or null if every id is safe to use.
  function conflictingSlipIdsMessage(slipIds, { excludeMeasurementId = "" } = {}) {
    const marina = String(state.marinaId || "");
    const region = String(state.region || "").trim();
    const exclude = String(excludeMeasurementId || "").trim();
    const list = (Array.isArray(slipIds) ? slipIds : [slipIds]).map((id) => String(id || "").trim()).filter(Boolean);
    if (!list.length) return null;
    const counts = new Map();
    for (const id of list) counts.set(id, (counts.get(id) || 0) + 1);
    const conflicts = [];
    for (const id of counts.keys()) {
      const hitsExisting = records.some((record) => (
        String(record?.slip_id || "").trim() === id
        && String(record?.marina_id || "") === marina
        && String(record?.region || "").trim() === region
        && (!exclude || String(record?.measurement_id || "") !== exclude)
      ));
      if (hitsExisting) conflicts.push(id);
    }
    const repeats = [...counts.entries()].filter(([, count]) => count > 1).map(([id]) => id);
    if (!conflicts.length && !repeats.length) return null;
    const parts = [];
    if (conflicts.length) parts.push(`already used in this marina/region: ${conflicts.join(", ")}`);
    if (repeats.length) parts.push(`repeated within this batch: ${repeats.join(", ")}`);
    const suggestion = conflicts.length
      ? "If these slips are actually in different yards, set a distinct Region for each — otherwise adjust Dock/Slip Number (or ID Step)"
      : "Adjust Dock/Slip Number (or ID Step)";
    const message = `Slip ID ${parts.join("; ")}. ${suggestion} and try again — nothing was created.`;
    // The quiet status line (set by every call site) is easy to miss entirely -- this is a
    // blocked action, not routine feedback, so it also needs something the user can't scroll
    // past without noticing.
    app.ui?.alertModal?.(message, { title: "Slip ID Conflict" });
    return message;
  }

  async function createRecordFromPoint(latlng) {
    const measurementId = makeId("MEAS");
    const slipId = buildSlipIdFromState(state) || `SLIP-${String(selectedIndex() + 1).padStart(4, "0")}`;
    const conflictMsg = conflictingSlipIdsMessage([slipId]);
    if (conflictMsg) throw new Error(conflictMsg);
    const payload = {
      measurement_id: measurementId,
      marina_id: state.marinaId,
      marina_name: state.marinaName,
      slip_id: slipId,
      dock_name: state.dockName || null,
      suffix: state.slipIdSuffix || null,
      region: state.region || null,
      space_type: state.spaceType || null,
      multislip_yn: "no",
      multislip_id: null,
      covered_yn: draftCovered ? "yes" : "no",
      boatlift_yn: draftBoatlift ? "yes" : "no",
      fueldock_yn: draftFuelDock ? "yes" : "no",
      ...draftOutOfServiceFields(),
      measured_linear_feet: null,
      measured_width_feet: null,
      measured_depth_feet: null,
      operational_status: "operable",
      notes: draftComments || "",
      measurement_date: state.measurementDate,
      confidence_score: state.confidenceScore,
      record_created_timestamp: new Date().toISOString(),
      created_by_email: app.auth?.email || null,
      lat: Number(latlng.lat),
      lng: Number(latlng.lng),
    };
    const response = await requestJson(`${API_BASE}/from-point`, {
      method: "POST",
      body: payload,
    });
    state.selectedMeasurementId = response?.item?.measurement_id || measurementId;
    incrementSlipIdNumber(state);
    incrementSlipIdSuffix(state);
    persist();
    await refreshRecords({ quiet: true });
    pushUndoEntry(`Created slip ${slipId}`, [{ type: "create", measurementId }]);
    app.log("slip.created", { slip_id: slipId, tool: "point_drop", marina: state.marinaId });
    status = `Created ${slipId} from map point.`;
    render();
  }

  async function createRecordsFromPoints(points = []) {
    const segments = linePreviewSegments();
    const ids = previewSlipIds(state, segments.length || points.length);
    const conflictMsg = conflictingSlipIdsMessage(ids);
    if (conflictMsg) throw new Error(conflictMsg);
    const createdIds = [];
    for (let index = 0; index < Math.max(segments.length, points.length); index += 1) {
      const segment = segments[index] || null;
      const point = points[index];
      const measurementId = makeId("MEAS");
      const slipId = ids[index] || `SLIP-${String(selectedIndex() + index + 1).padStart(4, "0")}`;
      const linearFeet = segment
        ? roundToTenthFoot(toFeetDistance(L.latLng(segment.start.lat, segment.start.lng), L.latLng(segment.end.lat, segment.end.lng)))
        : null;
      const geometryWkt = segment
        ? buildLineWkt([[segment.start.lat, segment.start.lng], [segment.end.lat, segment.end.lng]])
        : buildPointWkt(Number(point?.lng), Number(point?.lat));
      await requestJson(`${API_BASE}`, {
        method: "POST",
        body: {
          measurement_id: measurementId,
          marina_id: state.marinaId,
          marina_name: state.marinaName,
          slip_id: slipId,
          dock_name: state.dockName || null,
          suffix: state.slipIdSuffix || null,
          region: state.region || null,
          space_type: state.spaceType || null,
              multislip_yn: "no",
          multislip_id: null,
          covered_yn: draftCovered ? "yes" : "no",
          boatlift_yn: draftBoatlift ? "yes" : "no",
          fueldock_yn: draftFuelDock ? "yes" : "no",
          ...draftOutOfServiceFields(),
          measured_linear_feet: Number.isFinite(Number(linearFeet)) ? roundToTenthFoot(linearFeet) : null,
          measured_width_feet: null,
          measured_depth_feet: null,
          operational_status: "operable",
          notes: draftComments || "",
          measurement_date: state.measurementDate,
          confidence_score: state.confidenceScore,
          record_created_timestamp: new Date().toISOString(),
          created_by_email: app.auth?.email || null,
          geometry_wkt: geometryWkt,
        },
      });
      createdIds.push(measurementId);
      incrementSlipIdNumber(state);
      incrementSlipIdSuffix(state);
    }
    persist();
    await refreshRecords({ quiet: true });
    pushUndoEntry(`Created ${createdIds.length} slip${createdIds.length === 1 ? "" : "s"} (Line preview)`,
      createdIds.map((measurementId) => ({ type: "create", measurementId })));
    app.log("slip.created", { count: Math.max(segments.length, points.length), tool: "line_layout", marina: state.marinaId });
    status = `Created ${Math.max(segments.length, points.length)} slips from line preview.`;
    render();
  }

  function startPointDrop() {
    if (pointDropActive) return;
    stopGeometryPlaceMode();
    stopGeometryRunLayout("replaced");
    stopLineLayout("replaced");
    pointDropActive = true;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;

    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = {
        x: Number(event.clientX),
        y: Number(event.clientY),
      };
      pointerMoved = false;
    };

    const onPointerMove = (event) => {
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) {
        pointerMoved = true;
      }
    };

    const onPointerUp = async (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCreate = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCreate) return;
      try {
        await createRecordFromPoint(latlng);
        app.ui.toast("Slip inventory point created. Click again to continue.");
      } catch (error) {
        status = error?.message || "Failed to create slip inventory record.";
        app.ui.toast(status);
        render();
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        document.removeEventListener("keydown", onKey);
        mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
        mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
        mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
        mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
        stopPointDrop("cancelled");
      }
    };
    pointDropOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    document.addEventListener("keydown", onKey);
    status = "Click to create slips. Click and drag pans the map. Press Esc to cancel.";
    render();
  }

  function stopGeometryPlaceMode() {
    activeGeometryPlaceMode = false;
    geometryPlaceHoverPoint = null;
    geometryPlaceDraft = null;
    geometryDrawStage = null;
    geometryDrawA = null;
    geometryDrawB = null;
    geometryDrawSignedLengthFt = 0;
    geometryPlaceHandleDragging = false;
    if (geometryPlaceOff) {
      try { geometryPlaceOff(); } catch {}
      geometryPlaceOff = null;
    }
    geometryPlacePreviewLayer.clearLayers();
    geometryPlaceHandleLayer.clearLayers();
    try { map.getContainer().classList.remove("slip-free-draw-cursor", "slip-dim-cursor-length", "slip-dim-cursor-width"); } catch {}
  }

  function stopRotateAdjustMode() {
    if (!rotateAdjustActive && !rotateAdjustOff) return;
    rotateAdjustActive = false;
    if (rotateAdjustOff) {
      try { rotateAdjustOff(); } catch {}
      rotateAdjustOff = null;
    }
    try { map.getContainer().classList.remove("slip-free-draw-cursor"); } catch {}
  }

  // On-demand rotate: move mouse to spin the selected polygon around its own center, click
  // (or press Enter/r again) to lock it in, Escape to revert to the heading it had on entry.
  // Replaces what used to be a mandatory third click in the width/length draw sequence.
  function startRotateAdjustMode() {
    const selected = selectedGeometryPolygon();
    if (!selected?.center) return;
    stopPointDrop("replaced");
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopMultislipMode();
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    const polyId = String(selected.id);
    const center = { lat: Number(selected.center.lat), lng: Number(selected.center.lng) };
    const startHeadingDeg = Number(selected.headingDeg || 0);
    // Set on first move (not now) so the shape doesn't jump to face the cursor the instant
    // rotate mode activates -- rotation tracks cursor movement relative to wherever it
    // started, same feel as the old in-sequence rotate step.
    let baseBearingDeg = null;
    rotateAdjustActive = true;
    try { map.getContainer().classList.add("slip-free-draw-cursor"); } catch {}
    const mapContainer = map?.getContainer?.() || null;
    const applyHeading = (latlng) => {
      const bearing = bearingDegrees(center, latlng);
      if (baseBearingDeg == null) baseBearingDeg = bearing;
      const current = selectedGeometryPolygon();
      if (!current || String(current.id) !== polyId) { stopRotateAdjustMode(); return; }
      updateSelectedGeometry({ headingDeg: startHeadingDeg + (bearing - baseBearingDeg) });
      render();
    };
    const onMove = (event) => {
      const latlng = map?.mouseEventToLatLng?.(event);
      if (latlng) applyHeading({ lat: Number(latlng.lat), lng: Number(latlng.lng) });
    };
    const lockAndStop = () => { stopRotateAdjustMode(); status = "Rotation set."; render(); };
    const onUp = (event) => {
      event.__htHandled = "slip-inventory";
      lockAndStop();
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        updateSelectedGeometry({ headingDeg: startHeadingDeg });
        stopRotateAdjustMode();
        status = "Rotation cancelled.";
        render();
        return;
      }
      if (event.key === "Enter" || event.key?.toLowerCase?.() === "r") {
        event.preventDefault?.();
        lockAndStop();
      }
    };
    rotateAdjustOff = () => {
      mapContainer?.removeEventListener?.("pointermove", onMove, true);
      mapContainer?.removeEventListener?.("pointerup", onUp, true);
      document.removeEventListener("keydown", onKey, true);
    };
    mapContainer?.addEventListener?.("pointermove", onMove, true);
    mapContainer?.addEventListener?.("pointerup", onUp, true);
    document.addEventListener("keydown", onKey, true);
    status = "Move mouse to rotate, click (or r/Enter) to lock, Esc to cancel.";
    render();
  }

  // ── Lasso select (Ctrl/Cmd + drag) ───────────────────────────────────────────

  function anyOtherDrawModeActive() {
    return !!(
      activeGeometryPlaceMode || geometryRunLayoutActive || lineLayoutActive || pointDropActive
      || multislipActive || combActive || chainActive || rotateAdjustActive || fullDockActive || autoRunBusy()
    );
  }

  // All ten "Include in Lasso" toggles off -- shared by every other draw mode's cleanup
  // (arming this tool always disarms Comb/Chain/Multislip/etc, and vice versa, so a lasso
  // drag never fires while the map is mid-way through an unrelated tool) and by the Clear
  // button. Doesn't touch lassoToolActive itself -- callers that mean "turn the whole tool
  // off" do that separately.
  function disarmAllLassoToggles() {
    lassoIncludeDock = false;
    lassoIncludeSlipNumber = false;
    lassoIncludeSuffix = false;
    lassoIncludeRegion = false;
    lassoIncludeSlipType = false;
    lassoIncludeCovered = false;
    lassoIncludeBoatlift = false;
    lassoIncludeFuelDock = false;
    lassoIncludeOutOfService = false;
    lassoIncludeMultislip = false;
    lassoIncludeSwapLw = false;
  }

  function anyLassoToggleArmed() {
    return !!(
      lassoIncludeDock || lassoIncludeSlipNumber || lassoIncludeSuffix || lassoIncludeRegion
      || lassoIncludeSlipType || lassoIncludeCovered || lassoIncludeBoatlift || lassoIncludeFuelDock
      || lassoIncludeOutOfService || lassoIncludeMultislip || lassoIncludeSwapLw
    );
  }

  // One grid cell in the toggle column, row-aligned with its field by sharing the same grid
  // as the fields themselves (see the New Slip Defaults template) -- always rendered so the
  // column never shifts, just disabled/greyed until lassoToolActive.
  function lassoToggleCell(id, checked) {
    const disabled = !lassoToolActive;
    return `<div style="display:flex; align-items:center; justify-content:center;">
      <input id="${id}" type="checkbox" ${checked ? "checked" : ""}${disabled ? " disabled" : ""} style="width:15px; height:15px; margin:0;${disabled ? " opacity:.35; cursor:not-allowed;" : " cursor:pointer;"}" />
    </div>`;
  }

  // Only ever touches the shared crosshair class while lasso itself owns the interaction --
  // never steals it out from under another mode that happens to be active while Ctrl is held.
  function updateLassoCursor() {
    const mapContainer = map?.getContainer?.();
    if (!mapContainer || anyOtherDrawModeActive()) return;
    if (lassoCtrlHeld || lassoActive) mapContainer.classList.add("slip-free-draw-cursor");
    else mapContainer.classList.remove("slip-free-draw-cursor");
  }

  function renderLassoPreview() {
    lassoLayer.clearLayers();
    if (!lassoActive || lassoPoints.length < 2) return;
    // Warm amber while armed to rename/re-attribute/swap L-W (a real, immediate data mutation
    // on release) instead of the neutral white used for plain select -- a visible reminder
    // of which gesture this drag actually is, without adding any friction to either one.
    const isSelectMode = !(anyLassoToggleArmed());
    const color = isSelectMode ? "#f8fafc" : "#f59e0b";
    // Every closed loop (back near the start, or the trail crossing itself) is drawn filled --
    // otherwise there's no way to tell a closed loop (which will also grab fully-enclosed
    // slips) from one that isn't closed yet (touch-only) until after releasing.
    const fill = isSelectMode ? "#38bdf8" : "#f59e0b";
    for (const loop of lassoClosedLoops(lassoPoints)) {
      L.polygon(loop.map((p) => [p.lat, p.lng]), {
        stroke: false, fillColor: fill, fillOpacity: 0.16, interactive: false, renderer: canvasRenderer,
      }).addTo(lassoLayer);
    }
    L.polyline(lassoPoints.map((p) => [p.lat, p.lng]), {
      color, weight: 2, opacity: 0.9, dashArray: "5 4",
      lineCap: "round", lineJoin: "round", interactive: false, renderer: canvasRenderer,
    }).addTo(lassoLayer);
  }

  // Segment-vs-segment intersection, same approach as mitreChainRing's lineIntersect but
  // working directly in lat/lng -- fine at slip scale, and pointInPolygon already does the same.
  function lassoSegmentsIntersect(p1, p2, p3, p4) {
    const d1x = p2.lng - p1.lng, d1y = p2.lat - p1.lat;
    const d2x = p4.lng - p3.lng, d2y = p4.lat - p3.lat;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-15) return false;
    const t = ((p3.lng - p1.lng) * d2y - (p3.lat - p1.lat) * d2x) / denom;
    const u = ((p3.lng - p1.lng) * d1y - (p3.lat - p1.lat) * d1x) / denom;
    return t >= 0 && t <= 1 && u >= 0 && u <= 1;
  }

  // Closing tolerance scales with how big the loop actually is (a fixed 24px turned out
  // to be an unreasonably tight target to freehand back to -- reported as "closed loop
  // selection doesn't seem to work" because it essentially never fired in practice), with
  // a floor and ceiling so a tiny loop still needs to roughly close and a huge sweeping
  // one doesn't demand pixel-perfect precision either.
  const LASSO_CLOSE_MIN_PX = 32;
  const LASSO_CLOSE_MAX_PX = 100;
  const LASSO_CLOSE_PCT = 0.15; // of the loop's own screen-space bounding-box diagonal
  function lassoScreenPoint(p) {
    try { return map.latLngToContainerPoint([p.lat, p.lng]); } catch { return null; }
  }
  function lassoCloseRadiusPx(path) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, any = false;
    for (const p of path) {
      const pt = lassoScreenPoint(p);
      if (!pt) continue;
      any = true;
      if (pt.x < minX) minX = pt.x; if (pt.x > maxX) maxX = pt.x;
      if (pt.y < minY) minY = pt.y; if (pt.y > maxY) maxY = pt.y;
    }
    if (!any) return LASSO_CLOSE_MIN_PX;
    const diag = Math.hypot(maxX - minX, maxY - minY);
    return Math.min(LASSO_CLOSE_MAX_PX, Math.max(LASSO_CLOSE_MIN_PX, diag * LASSO_CLOSE_PCT));
  }
  function lassoPathIsClosed(path) {
    if (path.length < 4) return false;
    const first = lassoScreenPoint(path[0]);
    const last = lassoScreenPoint(path[path.length - 1]);
    if (!first || !last) return false;
    return Math.hypot(first.x - last.x, first.y - last.y) <= lassoCloseRadiusPx(path);
  }

  function lassoSegmentIntersection(p1, p2, p3, p4) {
    const d1x = p2.lng - p1.lng, d1y = p2.lat - p1.lat;
    const d2x = p4.lng - p3.lng, d2y = p4.lat - p3.lat;
    const denom = d1x * d2y - d1y * d2x;
    if (Math.abs(denom) < 1e-15) return null;
    const t = ((p3.lng - p1.lng) * d2y - (p3.lat - p1.lat) * d2x) / denom;
    const u = ((p3.lng - p1.lng) * d1y - (p3.lat - p1.lat) * d1x) / denom;
    if (t < 0 || t > 1 || u < 0 || u > 1) return null;
    return { lat: p1.lat + t * d1y, lng: p1.lng + t * d1x };
  }

  // Every closed area the drag encloses, as rings of {lat,lng}: the whole path when it ends
  // back near its start (lassoPathIsClosed), plus each loop formed where the trail crosses
  // over itself -- e.g. circling a dock and overshooting past the start, which never ends
  // within closing range but obviously completes a loop. Self-crossings are scanned
  // greedily (first crossing for each segment, then resume after it), so a scribble of
  // several loops yields several rings. Open drags return [].
  function lassoClosedLoops(path) {
    const loops = [];
    if (!Array.isArray(path) || path.length < 4) return loops;
    if (lassoPathIsClosed(path)) loops.push(path);
    const n = path.length;
    for (let i = 0; i < n - 3; i++) {
      for (let j = i + 2; j < n - 1; j++) {
        const x = lassoSegmentIntersection(path[i], path[i + 1], path[j], path[j + 1]);
        if (!x) continue;
        const ring = [x, ...path.slice(i + 1, j + 1)];
        if (ring.length >= 3) loops.push(ring);
        i = j; // outer loop's i++ resumes on the segment after the crossing
        break;
      }
    }
    return loops;
  }

  // True when every vertex of the slip's ring ([lat,lng] pairs) sits inside one of the loops.
  // Partially-enclosed slips don't need this -- their outline crosses the trail, so the
  // touch test already catches them.
  function slipRingInsideLassoLoops(ring, loopRings) {
    return loopRings.some((loopRing) => (
      ring.every(([lat, lng]) => pointInPolygon({ lat: Number(lat), lng: Number(lng) }, loopRing))
    ));
  }
  function lassoLoopRings(path) {
    return lassoClosedLoops(path).map((loop) => loop.map((p) => [p.lat, p.lng]));
  }

  function polygonsTouchedByLasso(path) {
    const mode = String(state.colorSlipsBy || "none");
    const hiddenForMode = state.filterHiddenValues?.[mode];
    const hasHiddenFilters = Array.isArray(hiddenForMode) && hiddenForMode.length > 0;
    // Only a genuinely closed loop also grabs slips the drag never actually crossed --
    // an open drag keeps the existing "touched" behavior only, since there's no enclosed
    // area to test containment against.
    const loopRings = lassoLoopRings(path);
    const hitIds = [];
    for (const poly of state.slipGeometryPolygons) {
      if (poly.committed && hasHiddenFilters) {
        const record = findRecordForPolygon(poly);
        if (record && !isRecordVisible(record)) continue;
      }
      const ring = computeSlipPolygonLatLngs(poly);
      if (!Array.isArray(ring) || ring.length < 3) continue;
      let hit = path.some((pt) => pointInPolygon(pt, ring));
      if (!hit) {
        outer: for (let i = 0; i < path.length - 1; i++) {
          for (let j = 0; j < ring.length; j++) {
            const a = { lat: Number(ring[j][0]), lng: Number(ring[j][1]) };
            const b = { lat: Number(ring[(j + 1) % ring.length][0]), lng: Number(ring[(j + 1) % ring.length][1]) };
            if (lassoSegmentsIntersect(path[i], path[i + 1], a, b)) { hit = true; break outer; }
          }
        }
      }
      // Closed-loop bonus: a slip the drag never touched at all, but that sits fully
      // inside a closed loop, still counts.
      if (!hit && loopRings.length) hit = slipRingInsideLassoLoops(ring, loopRings);
      if (hit) hitIds.push(String(poly.id));
    }
    return hitIds;
  }

  // Same hit test as polygonsTouchedByLasso, but tracks the earliest path index (point test or
  // segment test, whichever comes first along the drag) each polygon was touched at, instead of
  // stopping at the first hit -- needed so lasso path-rename can number slips in the order the
  // path actually reached them, not just "was this one touched at all."
  function firstLassoTouchIndex(path, ring) {
    const ptIdx = path.findIndex((pt) => pointInPolygon(pt, ring));
    let segIdx = -1;
    outer: for (let i = 0; i < path.length - 1; i++) {
      for (let j = 0; j < ring.length; j++) {
        const a = { lat: Number(ring[j][0]), lng: Number(ring[j][1]) };
        const b = { lat: Number(ring[(j + 1) % ring.length][0]), lng: Number(ring[(j + 1) % ring.length][1]) };
        if (lassoSegmentsIntersect(path[i], path[i + 1], a, b)) { segIdx = i; break outer; }
      }
    }
    if (ptIdx < 0) return segIdx;
    if (segIdx < 0) return ptIdx;
    return Math.min(ptIdx, segIdx);
  }

  function nearestLassoPathIndex(path, ring) {
    let cLat = 0, cLng = 0;
    for (const [lat, lng] of ring) { cLat += Number(lat); cLng += Number(lng); }
    cLat /= ring.length; cLng /= ring.length;
    let best = 0, bestD = Infinity;
    path.forEach((p, i) => {
      const d = (p.lat - cLat) ** 2 + (p.lng - cLng) ** 2;
      if (d < bestD) { bestD = d; best = i; }
    });
    return best;
  }

  // Only committed (real, already-saved) slips are eligible -- an uncommitted draft has no
  // slip_id/measurement_id of its own to rename yet, and renaming by path is fundamentally an
  // "existing inventory" operation, not a drawing one.
  function orderedCommittedSlipsTouchedByLasso(path) {
    const mode = String(state.colorSlipsBy || "none");
    const hiddenForMode = state.filterHiddenValues?.[mode];
    const hasHiddenFilters = Array.isArray(hiddenForMode) && hiddenForMode.length > 0;
    const loopRings = lassoLoopRings(path);
    const touched = [];
    for (const poly of state.slipGeometryPolygons) {
      if (!poly.committed) continue;
      if (hasHiddenFilters) {
        const record = findRecordForPolygon(poly);
        if (record && !isRecordVisible(record)) continue;
      }
      const ring = computeSlipPolygonLatLngs(poly);
      if (!Array.isArray(ring) || ring.length < 3) continue;
      let idx = firstLassoTouchIndex(path, ring);
      // Enclosed by a closed loop but never touched: slot it into the path order at the
      // path point nearest its centre, so renumbering still runs roughly along the drag.
      if (idx < 0 && loopRings.length && slipRingInsideLassoLoops(ring, loopRings)) {
        idx = nearestLassoPathIndex(path, ring);
      }
      if (idx >= 0) touched.push({ poly, idx });
    }
    touched.sort((a, b) => a.idx - b.idx);
    return touched.map((entry) => entry.poly);
  }

  // space_type is only included when the Slip Type dropdown is actually set to something AND
  // lassoIncludeSlipType is on -- unlike the checkboxes (always yes/no), a blank Slip Type is
  // a legitimate "don't touch this field" rather than a third value.
  // Each of the six attribute fields is fully independent -- only stamps the ones actually
  // checked, so e.g. bulk-tagging Covered doesn't force-touch Slip Type or Multislip
  // membership on slips that weren't meant to change.
  function buildAttrLassoFields() {
    const fields = {};
    if (lassoIncludeCovered) fields.covered_yn = draftCovered ? "yes" : "no";
    if (lassoIncludeBoatlift) fields.boatlift_yn = draftBoatlift ? "yes" : "no";
    if (lassoIncludeFuelDock) fields.fueldock_yn = draftFuelDock ? "yes" : "no";
    // Off writes an explicit "no" and clears the reason (a reason without OOS breaks the rule).
    if (lassoIncludeOutOfService) {
      fields.out_of_service = draftOutOfService ? "yes" : "no";
      fields.out_of_service_reason = draftOutOfService ? draftOosReason : "";
    }
    if (lassoIncludeSlipType && state.spaceType) fields.space_type = state.spaceType;
    // Called once per lasso pass (applyLassoPathActions), so when Multislip is on, every slip
    // touched by this single drag shares the ONE id generated right here -- not a per-slip id.
    // Off clears multislip_yn/multislip_id on every touched slip regardless of prior state.
    // "" (not null) for the off-case id so the generic target-vs-value diff below (which does
    // String(target[key] ?? "") on the target side but plain String(value) on this side) still
    // treats an already-cleared slip as unchanged instead of re-PATCHing it every time.
    if (lassoIncludeMultislip) {
      if (draftMultislip) {
        const id = state.multislipId || DEFAULT_MULTISLIP_ID;
        state.multislipId = incrementMultislipId(id);
        fields.multislip_yn = "yes";
        fields.multislip_id = id;
      } else {
        fields.multislip_yn = "no";
        fields.multislip_id = "";
      }
    }
    return fields;
  }

  // Combines every armed lasso-drag action into a single pass over the touched slips, one
  // PATCH per slip carrying whichever change(s) are currently checked under the Lasso Tool --
  // lassoIncludeDock (rename just the dock, keeping each slip's own number),
  // lassoIncludeSlipNumber (renumber, using Slip Number/ID Step, keeping each slip's own
  // dock), lassoIncludeRegion (stamp Region), the six buildAttrLassoFields toggles (Slip
  // Type/Multislip/Covered/Boatlift/Fuel Dock/Out of Service), and/or lassoIncludeSwapLw. Any combination can
  // run together in one drag; finishLasso() only calls this when at least one is armed.
  async function applyLassoPathActions(path, releaseClientXY) {
    const ordered = orderedCommittedSlipsTouchedByLasso(path);
    if (!ordered.length) {
      status = "Lasso path touched or enclosed 0 committed slips.";
      render();
      return;
    }
    if (lassoIncludeSlipNumber) {
      if (ordered.length > 1 && state.slipIdAutoIncrement !== true) {
        status = "Turn on Auto increment (Slip ID) before renumbering multiple slips by path -- otherwise they'd all get the same number.";
        render();
        return;
      }
      if (!String(state.slipIdPart3 || "").trim()) {
        status = "Set a Slip Number before renumbering by path.";
        render();
        return;
      }
    }
    if (lassoIncludeDock && !String(state.dockName || "").trim()) {
      status = "Set a Dock before renaming by path.";
      render();
      return;
    }
    const attrFields = buildAttrLassoFields();
    const hasAttrFields = Object.keys(attrFields).length > 0;
    const doSwapLw = lassoIncludeSwapLw;
    const doRegion = lassoIncludeRegion;
    const nextRegion = String(state.region || "");
    const sep = String(state.slipIdSeparator || "-");

    const actionNames = [];
    if (lassoIncludeDock) actionNames.push("dock");
    if (lassoIncludeSlipNumber) actionNames.push("slip number");
    if (lassoIncludeSuffix) actionNames.push("suffix");
    if (doRegion) actionNames.push("region");
    if (hasAttrFields) actionNames.push("attributes");
    if (doSwapLw) actionNames.push("L/W swap");
    const actionLabel = actionNames.length ? `Applying ${actionNames.join(", ")} to` : "Updating";
    status = `${actionLabel} ${ordered.length} slip${ordered.length > 1 ? "s" : ""}...`;
    render();
    const renamed = [];
    let reattributedCount = 0;
    let regionChangedCount = 0;
    let swappedCount = 0;
    const undoChanges = [];
    try {
      for (const poly of ordered) {
        const measurementId = String(poly.linkedMeasurementId || "");
        const target = measurementId ? records.find((r) => String(r.measurement_id) === measurementId) : null;
        if (!target) continue;
        const undoBefore = cloneRecord(target);
        const patch = {};
        if (lassoIncludeDock || lassoIncludeSlipNumber || lassoIncludeSuffix) {
          // Derive from slip_id (not dock_name directly) when they've drifted apart -- a
          // slip can be "mis-docked" in the dock_name column while its Slip ID still reads
          // correctly, and rebuilding around the stale column would duplicate ("K-K-059") or
          // silently drop the real dock prefix. See slipDockFromRecord/slipNumberFromRecord.
          const currentDock = slipDockFromRecord(target);
          const currentNumber = slipNumberFromRecord(target);
          const currentSuffix = slipSuffixFromRecord(target);
          const nextDock = lassoIncludeDock ? String(state.dockName || "").trim() : currentDock;
          const nextNumber = lassoIncludeSlipNumber ? String(state.slipIdPart3 || "").trim() : currentNumber;
          const nextSuffix = lassoIncludeSuffix ? String(state.slipIdSuffix || "").trim() : currentSuffix;
          const nextSlipId = [nextDock, nextNumber, nextSuffix].filter(Boolean).join(sep);
          if (nextSlipId && String(target.slip_id || "") !== nextSlipId) patch.slip_id = nextSlipId;
          // slip_id embeds Dock as its prefix, but dock_name is a separate stored column --
          // keep it in sync so renaming onto a new dock doesn't leave the Dock column showing
          // the old one while Slip ID shows the new prefix. Same for suffix.
          if (lassoIncludeDock && nextDock && String(target.dock_name || "") !== nextDock) patch.dock_name = nextDock;
          if (lassoIncludeSuffix && String(target.suffix || "") !== nextSuffix) patch.suffix = nextSuffix || null;
          if (lassoIncludeSlipNumber) incrementSlipIdNumber(state);
          if (lassoIncludeSuffix) incrementSlipIdSuffix(state);
        }
        let attrChanged = false;
        if (hasAttrFields) {
          for (const [key, value] of Object.entries(attrFields)) {
            if (String(target[key] ?? "") !== String(value)) { patch[key] = value; attrChanged = true; }
          }
        }
        let regionChanged = false;
        if (doRegion && String(target.region ?? "") !== nextRegion) {
          patch.region = nextRegion;
          regionChanged = true;
        }
        // Per-slip, unlike attrFields -- reads THIS slip's own length/width and swaps them,
        // rather than stamping a shared value onto every touched slip (mirrors the single-
        // selection "Swap L/W" table button, see swapLWForSelected).
        let lwSwapped = false;
        if (doSwapLw) {
          const l = target.measured_linear_feet;
          const w = target.measured_width_feet;
          if (String(l ?? "") !== String(w ?? "")) {
            patch.measured_linear_feet = w;
            patch.measured_width_feet = l;
            lwSwapped = true;
          }
        }
        if (Object.keys(patch).length) {
          const nextRecord = { ...target, ...patch };
          await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "PATCH", body: nextRecord });
          undoChanges.push({ type: "update", measurementId, before: undoBefore, after: null });
          if (patch.slip_id) {
            state.slipGeometryPolygons = state.slipGeometryPolygons.map((p) => (
              String(p?.linkedMeasurementId || "") === measurementId ? { ...p, linkedSlipId: patch.slip_id } : p
            ));
            renamed.push(patch.slip_id);
          }
          if (attrChanged) reattributedCount++;
          if (regionChanged) regionChangedCount++;
          if (lwSwapped) swappedCount++;
        }
      }
    } catch (err) {
      lastLocalSaveAt = Date.now();
      persist();
      status = `Lasso update stopped after ${renamed.length + reattributedCount + regionChangedCount + swappedCount} change${(renamed.length + reattributedCount + regionChangedCount + swappedCount) === 1 ? "" : "s"} (${err?.message || err}).`;
      await refreshRecords({ quiet: true }).catch(() => {});
      render();
      return;
    }
    lastLocalSaveAt = Date.now();
    persist();
    const parts = [];
    if (lassoIncludeDock || lassoIncludeSlipNumber || lassoIncludeSuffix) {
      parts.push(renamed.length
        ? `renamed ${renamed.length} slip${renamed.length > 1 ? "s" : ""}: ${renamed.join(", ")}`
        : "every touched slip already had that ID");
    }
    if (hasAttrFields) {
      parts.push(reattributedCount
        ? `updated attributes on ${reattributedCount} slip${reattributedCount > 1 ? "s" : ""}`
        : "every touched slip already matched those attributes");
    }
    if (doRegion) {
      parts.push(regionChangedCount
        ? `set region on ${regionChangedCount} slip${regionChangedCount > 1 ? "s" : ""}`
        : "every touched slip already had that region");
    }
    if (doSwapLw) {
      parts.push(swappedCount
        ? `swapped length/width on ${swappedCount} slip${swappedCount > 1 ? "s" : ""}`
        : "no touched slip needed a length/width swap");
    }
    status = parts.length ? `${parts.join("; ")}.` : "No changes.";
    if (renamed.length || reattributedCount || regionChangedCount || swappedCount) spawnConfettiBurst(releaseClientXY?.x, releaseClientXY?.y);
    await refreshRecords({ quiet: true }).catch(() => {});
    if (undoChanges.length) {
      for (const change of undoChanges) change.after = snapshotRecord(change.measurementId);
      pushUndoEntry(`Lasso: ${actionNames.join(", ") || "update"} on ${undoChanges.length} slip${undoChanges.length === 1 ? "" : "s"}`, undoChanges);
    }
    render();
  }

  // Highest existing X-dock number (+1), zero-padded to match whatever width is already in
  // use -- records is already scoped to the active marina (see refreshRecords/filterProjectId),
  // so no separate marina filter is needed here. Uses slipDockFromRecord, not dock_name
  // directly, so a mis-docked X slip (dock_name drifted out of sync with its own Slip ID --
  // see slipDockFromRecord) still counts toward the max and doesn't hand out a number that
  // collides with it.
  function nextXDockSlipIdPart3() {
    let maxNum = 0;
    let width = 3;
    for (const r of records) {
      if (slipDockFromRecord(r).trim().toUpperCase() !== "X") continue;
      // slipNumberFromRecord, not a trailing-digit regex against the raw slip_id -- once a
      // Suffix can follow the number ("X-011-A"), the number is no longer the last thing in
      // the string.
      const num = slipNumberFromRecord(r);
      if (!/^\d+$/.test(num)) continue;
      const n = Number(num);
      if (n > maxNum) maxNum = n;
      width = Math.max(width, num.length);
    }
    return String(maxNum + 1).padStart(width, "0");
  }

  // Renumbers every "X" dock slip in the active marina to X-001, X-002, ... in their current
  // relative order (by existing numeric suffix) -- closes gaps left by deletions without
  // reordering anything. Same PATCH-loop / partial-failure handling as applyLassoPathActions.
  async function consolidateXDockSlipIds() {
    // slipDockFromRecord, not dock_name directly -- a slip's Slip ID can still correctly read
    // "X-011" while its dock_name column has drifted onto something else entirely (confirmed
    // on WBM: a duplicate X-011/X-009 pair each had one twin stuck on dock_name "5"/"4").
    // Filtering by dock_name alone silently excludes that mis-docked twin from consolidation,
    // so its duplicate Slip ID never gets renumbered and the duplicate persists.
    const candidates = records.filter((r) => slipDockFromRecord(r).trim().toUpperCase() === "X");
    if (!candidates.length) {
      status = "No X dock slips found in this marina.";
      render();
      return;
    }
    // slipNumberFromRecord, not a trailing-digit regex -- suffix-aware (see
    // nextXDockSlipIdPart3), so a slip with a Suffix still sorts/pads by its real number.
    const withNum = candidates.map((record) => {
      const num = slipNumberFromRecord(record);
      return { record, num: /^\d+$/.test(num) ? Number(num) : Number.POSITIVE_INFINITY };
    });
    withNum.sort((a, b) => (
      a.num - b.num || String(a.record.slip_id || "").localeCompare(String(b.record.slip_id || ""), undefined, { numeric: true, sensitivity: "base" })
    ));
    const width = Math.max(3, ...withNum.map(({ record }) => {
      const num = slipNumberFromRecord(record);
      return /^\d+$/.test(num) ? num.length : 0;
    }));
    const separator = String(state.slipIdSeparator || "-") || "-";
    status = `Consolidating ${withNum.length} X dock slip${withNum.length > 1 ? "s" : ""}...`;
    render();
    let renamed = 0;
    const undoChanges = [];
    try {
      for (let i = 0; i < withNum.length; i++) {
        const { record } = withNum[i];
        const measurementId = String(record.measurement_id);
        // Preserve this slip's existing Suffix -- consolidation renumbers, it doesn't touch
        // other attributes, and the suffix column (spread in via ...record below) would
        // otherwise silently desync from the rebuilt slip_id text.
        const nextSlipId = ["X", String(i + 1).padStart(width, "0"), slipSuffixFromRecord(record)]
          .filter(Boolean).join(separator);
        if (String(record.slip_id || "") === nextSlipId && String(record.dock_name || "") === "X") continue;
        const nextRecord = { ...record, dock_name: "X", slip_id: nextSlipId };
        await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "PATCH", body: nextRecord });
        undoChanges.push({ type: "update", measurementId, before: cloneRecord(record), after: null });
        state.slipGeometryPolygons = state.slipGeometryPolygons.map((p) => (
          String(p?.linkedMeasurementId || "") === measurementId ? { ...p, linkedSlipId: nextSlipId } : p
        ));
        renamed++;
      }
    } catch (err) {
      persist();
      status = `X dock consolidation stopped after ${renamed} (${err?.message || err}).`;
      await refreshRecords({ quiet: true }).catch(() => {});
      render();
      return;
    }
    persist();
    status = renamed
      ? `Consolidated ${withNum.length} X dock slip${withNum.length > 1 ? "s" : ""} (${renamed} renumbered).`
      : "X dock slips already consolidated -- no changes needed.";
    await refreshRecords({ quiet: true }).catch(() => {});
    if (undoChanges.length) {
      for (const change of undoChanges) change.after = snapshotRecord(change.measurementId);
      pushUndoEntry(`Consolidated ${undoChanges.length} X dock slip${undoChanges.length === 1 ? "" : "s"}`, undoChanges);
    }
    render();
  }

  // Renumbers every real multislip group (2+ slips sharing a multislip_id) in the active
  // marina to MS-001, MS-002, ... in their current relative order (groups sorted by their
  // existing id, gaps closed, nothing reordered) -- same shape as consolidateXDockSlipIds.
  // Groups of exactly 1 (a multislip_id with no partner -- almost certainly accidental) are
  // left untouched but selected on the map/table so the user can review and explicitly clear
  // them with clearMultislipTagForSelected, rather than this silently guessing.
  async function consolidateMultislipIds() {
    const groups = new Map();
    for (const r of records) {
      const mid = String(r?.multislip_id || "").trim();
      if (!mid) continue;
      if (!groups.has(mid)) groups.set(mid, []);
      groups.get(mid).push(r);
    }
    if (!groups.size) {
      status = "No multislip IDs found in this marina.";
      render();
      return;
    }
    const entries = [...groups.entries()].sort((a, b) => (
      a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: "base" })
    ));
    const realGroups = entries.filter(([, recs]) => recs.length >= 2);
    const orphanGroups = entries.filter(([, recs]) => recs.length === 1);
    const width = Math.max(3, ...entries.map(([id]) => (/(\d+)\s*$/.exec(id)?.[1] || "").length));

    status = realGroups.length
      ? `Consolidating ${realGroups.length} multislip group${realGroups.length === 1 ? "" : "s"}...`
      : "No multislip groups of 2+ slips to consolidate.";
    render();

    let nextNum = 1;
    let renamedGroups = 0;
    let renamedRecords = 0;
    const undoChanges = [];
    try {
      for (const [oldId, recs] of realGroups) {
        const newId = `MS-${String(nextNum).padStart(width, "0")}`;
        nextNum += 1;
        if (oldId !== newId) {
          for (const record of recs) {
            const measurementId = String(record.measurement_id);
            const nextRecord = { ...record, multislip_id: newId };
            await requestJson(`${API_BASE}/${encodeURIComponent(measurementId)}`, { method: "PATCH", body: nextRecord });
            undoChanges.push({ type: "update", measurementId, before: cloneRecord(record), after: null });
            renamedRecords++;
          }
          renamedGroups++;
        }
      }
    } catch (err) {
      persist();
      status = `Multislip consolidation stopped after ${renamedGroups} group${renamedGroups === 1 ? "" : "s"} (${renamedRecords} slips) (${err?.message || err}).`;
      await refreshRecords({ quiet: true }).catch(() => {});
      render();
      return;
    }

    // Select the orphans so the user can see exactly which slips are affected before deciding
    // whether to clear their tag -- reuses the same table/geometry selection mechanism the
    // rest of the table's bulk-edit already relies on.
    if (orphanGroups.length) {
      const orphanMeasurementIds = orphanGroups.map(([, recs]) => String(recs[0].measurement_id));
      tableSelectedMeasurementIds = new Set(orphanMeasurementIds);
      state.selectedMeasurementId = orphanMeasurementIds[0] || "";
      const orphanGeomIds = orphanMeasurementIds
        .map((mid) => state.slipGeometryPolygons.find((p) => String(p?.linkedMeasurementId || "") === mid)?.id)
        .filter(Boolean)
        .map(String);
      if (orphanGeomIds.length) setSelectedGeometryIds(orphanGeomIds, orphanGeomIds[0]);
    }
    persist();

    const parts = [];
    parts.push(renamedGroups
      ? `Consolidated ${renamedGroups} multislip group${renamedGroups === 1 ? "" : "s"} (${renamedRecords} slips renumbered).`
      : (realGroups.length ? "Multislip groups already consolidated." : ""));
    if (orphanGroups.length) {
      const ids = orphanGroups.map(([id]) => id).join(", ");
      parts.push(`Found ${orphanGroups.length} orphaned multislip ID${orphanGroups.length === 1 ? "" : "s"} (${ids}) with no partner slip -- selected below. Click "Clear Multislip Tag" to remove multislip_yn/multislip_id from the selection, or adjust it first.`);
    }
    status = parts.filter(Boolean).join(" ") || "No changes needed.";
    await refreshRecords({ quiet: true }).catch(() => {});
    if (undoChanges.length) {
      for (const change of undoChanges) change.after = snapshotRecord(change.measurementId);
      pushUndoEntry(`Consolidated ${renamedGroups} multislip group${renamedGroups === 1 ? "" : "s"}`, undoChanges);
    }
    render();
  }

  // Clears multislip_yn/multislip_id from whatever's currently selected -- generic (not tied
  // to the orphan flow above), reusing the same getSelectedMeasurementIds() the rest of the
  // table's bulk-edit already relies on.
  async function clearMultislipTagForSelected() {
    const selectedIds = getSelectedMeasurementIds();
    if (!selectedIds.length) {
      status = "No rows selected to clear a multislip tag from.";
      render();
      return;
    }
    status = `Clearing multislip tag from ${selectedIds.length} slip${selectedIds.length === 1 ? "" : "s"}...`;
    render();
    let cleared = 0;
    const undoChanges = [];
    try {
      for (const mid of selectedIds) {
        const record = records.find((r) => r.measurement_id === mid);
        if (!record) continue;
        if (!String(record.multislip_id || "").trim() && String(record.multislip_yn || "no") !== "yes") continue;
        const nextRecord = { ...record, multislip_yn: "no", multislip_id: null };
        await requestJson(`${API_BASE}/${encodeURIComponent(mid)}`, { method: "PATCH", body: nextRecord });
        undoChanges.push({ type: "update", measurementId: String(mid), before: cloneRecord(record), after: null });
        cleared++;
      }
    } catch (err) {
      status = `Clear multislip tag stopped after ${cleared} (${err?.message || err}).`;
      await refreshRecords({ quiet: true }).catch(() => {});
      render();
      return;
    }
    status = cleared
      ? `Cleared multislip tag from ${cleared} slip${cleared === 1 ? "" : "s"}.`
      : "Selected slips had no multislip tag to clear.";
    await refreshRecords({ quiet: true }).catch(() => {});
    if (undoChanges.length) {
      for (const change of undoChanges) change.after = snapshotRecord(change.measurementId);
      pushUndoEntry(`Cleared multislip tag on ${undoChanges.length} slip${undoChanges.length === 1 ? "" : "s"}`, undoChanges);
    }
    render();
  }

  function cancelLasso() {
    lassoActive = false;
    lassoPoints = [];
    lassoPointerDownXY = null;
    // Browsers release pointer capture implicitly on pointerup/pointercancel anyway; this
    // covers the early-cancel paths (another mode taking over, Ctrl released mid-drag) where
    // no such event ever fires. Harmless no-op if this element doesn't hold that capture.
    if (lassoPointerId != null) {
      try { map.getContainer()?.releasePointerCapture?.(lassoPointerId); } catch {}
      lassoPointerId = null;
    }
    lassoLayer.clearLayers();
    try { map.dragging.enable(); } catch {}
    updateLassoCursor();
  }

  function finishLasso(releaseClientXY) {
    const path = lassoPoints.slice();
    cancelLasso();
    if (path.length < 2) return;
    if (anyLassoToggleArmed()) {
      applyLassoPathActions(path, releaseClientXY).catch((err) => {
        status = `Lasso update failed: ${err?.message || err}`;
        render();
      });
      return;
    }
    const hitIds = polygonsTouchedByLasso(path);
    if (!hitIds.length) {
      // Always additive now (see the merge below) -- an empty drag that hits nothing simply
      // adds nothing, leaving whatever was already selected untouched. Only Escape (the
      // global hotkey handler) clears the selection.
      status = "Lasso selected 0 slips.";
      render();
      return;
    }
    // Always adds to the existing selection -- each Ctrl-drag accumulates onto the last one,
    // same as repeatedly Ctrl-clicking individual slips would. Escape is the only way to
    // clear/start over.
    const merged = [...new Set([...getSelectedGeometryIds(), ...hitIds])];
    setSelectedGeometryIds(merged, hitIds[0]);
    const firstPoly = state.slipGeometryPolygons.find((p) => String(p.id) === hitIds[0]);
    const record = firstPoly ? findRecordForPolygon(firstPoly) : null;
    if (record?.measurement_id) {
      state.selectedMeasurementId = record.measurement_id;
      _scrollTableToSelectionPending = true;
    }
    suppressMapClicks(300);
    status = `Lassoed ${hitIds.length} slip${hitIds.length > 1 ? "s" : ""}.`;
    spawnConfettiBurst(releaseClientXY?.x, releaseClientXY?.y);
    persist();
    render();
  }

  // Lightweight, self-contained, on-brand confetti burst -- no external library, matches the
  // palette already used elsewhere on this map (dimension lines, selection, multislip preview).
  let _confettiStyleInjected = false;
  const CONFETTI_COLORS = ["#38bdf8", "#a78bfa", "#f59e0b", "#10b981", "#f472b6"];
  function ensureConfettiStyle() {
    if (_confettiStyleInjected) return;
    _confettiStyleInjected = true;
    const style = document.createElement("style");
    style.textContent = `
@keyframes slip-confetti-fall {
  0% { transform: translate(0,0) rotate(0deg); opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) rotate(var(--rot)); opacity: 0; }
}
.slip-confetti-piece {
  position: fixed; top: 0; left: 0; width: 7px; height: 7px; border-radius: 1px;
  pointer-events: none; z-index: 99999;
  animation: slip-confetti-fall 900ms cubic-bezier(.15,.7,.3,1) forwards;
}`;
    document.head.appendChild(style);
  }
  function spawnConfettiBurst(clientX, clientY) {
    ensureConfettiStyle();
    const x = Number.isFinite(clientX) ? clientX : window.innerWidth / 2;
    const y = Number.isFinite(clientY) ? clientY : window.innerHeight / 2;
    for (let i = 0; i < 26; i++) {
      const el = document.createElement("div");
      el.className = "slip-confetti-piece";
      const angle = Math.random() * Math.PI * 2;
      const dist = 40 + Math.random() * 70;
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      el.style.setProperty("--dx", `${Math.cos(angle) * dist}px`);
      el.style.setProperty("--dy", `${(Math.sin(angle) * dist - 30) + 90}px`);
      el.style.setProperty("--rot", `${(Math.random() * 480 - 240).toFixed(0)}deg`);
      document.body.appendChild(el);
      el.addEventListener("animationend", () => el.remove(), { once: true });
    }
  }

  // ── Place Multislip ──────────────────────────────────────────────────────────

  // Median measured_width_feet across this marina's own already-committed slips -- `records`
  // is already scoped to the active marina, so this reflects real local construction (beam
  // spacing, typical boat size) rather than a generic constant. Null with fewer than 5
  // samples: too little local signal yet to trust a guess over just starting at N=1.
  function typicalSlipWidthFtForMarina() {
    const widths = records
      .map((r) => Number(r?.measured_width_feet))
      .filter((w) => Number.isFinite(w) && w > 0)
      .sort((a, b) => a - b);
    if (widths.length < 5) return null;
    const mid = Math.floor(widths.length / 2);
    return widths.length % 2 ? widths[mid] : (widths[mid - 1] + widths[mid]) / 2;
  }

  // First-guess slip count for a run of runLenFt, from the marina's own typical slip width
  // -- replaces always starting a fresh Multislip/Fingers run at N=1 and W-mashing up to the
  // real count. Falls back to 1 (today's behavior) when there's no local width history yet.
  function guessSlipCountForRun(runLenFt) {
    const typicalWidthFt = typicalSlipWidthFtForMarina();
    if (!typicalWidthFt || !(runLenFt > 0)) return 1;
    return Math.max(1, Math.min(75, Math.round(runLenFt / typicalWidthFt)));
  }

  function multislipToolLabel() {
    const base = multislipAsFingers ? "Fingers" : "Multislip";
    return multislipSideLabel ? `${base} (${multislipSideLabel})` : base;
  }
  // The recurring "ready to adjust/commit" status line, shared by every place that
  // returns to this phase (initial width lock, rotation cancel, W/X count change) so the
  // Enter hint (lock centers vs. commit outright for a single slip) never drifts out of
  // sync between them.
  function multislipPhaseStatus() {
    const totalCount = multislipPartitions * multislipSlotsPerBay;
    const bayPart = multislipSlotsPerBay > 1 ? ` (${multislipPartitions} bay${multislipPartitions > 1 ? "s" : ""} × ${multislipSlotsPerBay})` : "";
    return `${multislipToolLabel()}: ${totalCount} slip${totalCount > 1 ? "s" : ""}${bayPart}.${multislipTiltPart()} W=add, X=remove, T=tilt, [/]=adjust tilt, Shift+T=slips/bay (${multislipSlotsPerBay}), drag orange handles to finger centers, R=rotate, Enter=${multislipPartitions > 1 ? "lock centers" : "commit"}, Esc=cancel.`;
  }
  function multislipTiltPart() {
    return multislipShearDeg ? ` Tilt: ${multislipShearDeg}°.` : "";
  }
  // Prompt shown before P1 (click 1) and while awaiting P2 (click 2) -- identical wording
  // between Multislip and Fingers apart from the tool name, and between length-first and
  // width-first apart from which click sets which dimension.
  function multislipP1PromptStatus() {
    return multislipMode === "width"
      ? `${multislipToolLabel()} (width-first): click one end of a single slip width along the dock. Space: toggle Length/Width first.`
      : `${multislipToolLabel()}: click the start of the dock run. Space: toggle Length/Width first.`;
  }
  function multislipP2PromptStatus() {
    return multislipMode === "width"
      ? `${multislipToolLabel()} (width-first): click the other end of one slip width. Space: toggle Length/Width first.`
      : `${multislipToolLabel()}: click the end of the dock run. Space: toggle Length/Width first.`;
  }
  function multislipWidthPromptStatus() {
    return multislipMode === "width"
      ? `${multislipToolLabel()} (width-first): move offshore and click to set slip length (snaps 1 ft). W/X to add/remove slips. T=freeform tilt${multislipFreeform ? " (on)" : ""}.`
      : `${multislipToolLabel()}: move perpendicular and click to set width (snaps 1 ft). W/X to add/remove slips after. T=freeform tilt${multislipFreeform ? " (on)" : ""}.`;
  }
  function multislipFingerWidthPromptStatus() {
    const totalCount = multislipPartitions * multislipSlotsPerBay;
    const bayPart = multislipSlotsPerBay > 1 ? ` — ${totalCount} slip${totalCount > 1 ? "s" : ""} (${multislipPartitions}×${multislipSlotsPerBay})` : "";
    return `${multislipToolLabel()}: drag the violet handle to set finger width (${multislipFingerWidthFt.toFixed(1)} ft)${bayPart}.${multislipTiltPart()} W/X=slips per bay (${multislipSlotsPerBay}), T=tilt, [/]=adjust tilt, Enter=commit, Esc=back to finger centers.`;
  }

  function stopMultislipMode() {
    if (!multislipActive && !multislipOff) return;
    multislipActive = false;
    multislipMode = "length";
    multislipOnDone = null;
    multislipSideLabel = "";
    multislipP1 = null; multislipP2 = null; multislipWidthFt = 0; multislipPartitions = 1;
    multislipSlotLengths = []; multislipBoundaryFracs = []; multislipDragging = false;
    multislipSlotsPerBay = 1;
    multislipShearDeg = 0; multislipFreeform = false;
    multislipCentersLocked = false; multislipFingerWidthFt = 0; multislipHandlesDirty = true;
    multislipHoverPoint = null;
    multislipRotating = false; multislipP2Base = null; multislipP3 = null;
    multislipPreWidthRotating = false; multislipP2AtLock = null;
    if (multislipOff) { try { multislipOff(); } catch {} multislipOff = null; }
    multislipPreviewLayer.clearLayers();
    multislipHandleLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  function renderMultislipPreview() {
    multislipPreviewLayer.clearLayers();
    if (!multislipActive) return;
    const p1 = multislipP1;
    const hover = multislipHoverPoint;
    const green = "rgba(16,185,129,0.95)";
    const greenFill = "#10b981";

    // Phase 1: awaiting P1 — show nothing (cursor is crosshair)
    if (!p1) return;

    // Phase 2: P1 set, tracking P2 -- color matches the green/red first-dimension crosshair
    // (green = length-first, red = width-first) until this dimension locks in at P2.
    if (!multislipP2) {
      if (!hover) return;
      const dim1Color = multislipMode === "width" ? "rgba(239,68,68,0.95)" : green;
      const dim1Fill = multislipMode === "width" ? "#ef4444" : greenFill;
      L.circleMarker([p1.lat, p1.lng], { radius: 5, color: dim1Color, fillColor: dim1Fill, fillOpacity: 1, weight: 2, interactive: false }).addTo(multislipPreviewLayer);
      L.polyline([[p1.lat, p1.lng], [hover.lat, hover.lng]], { color: dim1Color, weight: 2, dashArray: "6 4", interactive: false }).addTo(multislipPreviewLayer);
      return;
    }

    const p2 = multislipP2;

    // Phase 3: P2 set, tracking extrusion
    if (multislipWidthFt === 0) {
      L.polyline([[p1.lat, p1.lng], [p2.lat, p2.lng]], { color: green, weight: 3, interactive: false }).addTo(multislipPreviewLayer);
      if (!hover) return;
      // While free-rotating (see multislipPreWidthRotating), p2 itself is being live-rotated
      // around p1 to track the cursor, so measuring width perpendicular to p1-p2 would always
      // read ~0 (the cursor sits on that line by construction). Measure against the frozen
      // as-clicked edge instead -- width and rotation are then two independent reads of the
      // same cursor position, decided together in one motion.
      const widthRefP2 = (multislipPreWidthRotating && multislipP2AtLock) ? multislipP2AtLock : p2;
      let rawFt;
      if (multislipFreeform) {
        const derived = msDeriveShearedLength(p1, p2, hover);
        multislipShearDeg = derived.shearDeg;
        rawFt = derived.widthFt;
      } else {
        rawFt = msPerpDistFt(p1, widthRefP2, hover);
      }
      const snapFt = Math.round(rawFt / 0.1) * 0.1;
      if (snapFt === 0) return;
      const corners = msRectCornersSheared(p1, p2, snapFt, multislipShearDeg);
      L.polygon(corners.map(c => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.15, dashArray: "6 4", interactive: false }).addTo(multislipPreviewLayer);
      const midAlong = { lat: (p1.lat + p2.lat) / 2, lng: (p1.lng + p2.lng) / 2 };
      const wM = snapFt / 3.280839895;
      const { tiltX, tiltY } = msTiltVec(p1, p2, multislipShearDeg);
      const labelPt = offsetPoint(midAlong, tiltX * wM, tiltY * wM);
      const tiltSuffix = multislipShearDeg ? ` (tilt ${multislipShearDeg}°)` : "";
      const labelText = (multislipMode === "width" ? `L: ${Math.abs(snapFt)} ft` : `W: ${Math.abs(snapFt)} ft`) + tiltSuffix;
      L.marker([labelPt.lat, labelPt.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:#d1fae5;font:700 11px/1 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%)">${labelText}</div>` }),
      }).addTo(multislipPreviewLayer);
      return;
    }

    // Phase 4: Width/length set — per-bay rectangles, each optionally split into
    // multislipSlotsPerBay directly-adjacent sub-slips, with drag handles.
    // Both modes: P1→P2 holds N+1 finger-center fractions (multislipBoundaryFracs, evenly
    // spaced by default); each bay's actual span trims a shared finger-width gap off
    // either side of its two bounding centers (msSlotFracsFromCenters) -- zero gap (the
    // pre-lock default) means bays still touch edge-to-edge like before. Sub-slips within
    // one bay always touch edge-to-edge with each other regardless of finger width -- only
    // bay boundaries ever get that gap, matching Comb's own bay/sub-slot split.
    // Length-first: P1→P2 = dock run (L), slotLen = offshore (W).
    // Width-first:  P1→P2 = offshore (W), slotLen = dock-along (L).
    const N = multislipPartitions;
    const slotsPerBay = multislipSlotsPerBay;
    const totalCount = N * slotsPerBay;
    const wFt = multislipWidthFt;
    const centerFracs = multislipBoundaryFracs.length === N + 1 ? multislipBoundaryFracs : msEvenFracs(N);
    const { lenM: runLenM } = msDockvec(p1, p2);
    const runLenFt = runLenM * 3.280839895;
    const bayFracs = msSlotFracsFromCenters(centerFracs, multislipFingerWidthFt, runLenFt);
    const bayEndpoints = Array.from({ length: N }, (_, i) => ({
      p1: msPointAtFrac(p1, p2, bayFracs[i][0]),
      p2: msPointAtFrac(p1, p2, bayFracs[i][1]),
      alongFt: runLenFt * (bayFracs[i][1] - bayFracs[i][0]),
    }));

    const ids = previewSlipIds(state, totalCount);
    let previewIdx = 0;
    for (let i = 0; i < N; i++) {
      const bay = bayEndpoints[i];
      const subAlongFt = bay.alongFt / slotsPerBay;
      for (let s = 0; s < slotsPerBay; s++) {
        const slotLen = multislipSlotLengths[i]?.[s] != null ? multislipSlotLengths[i][s] : wFt;
        const corners = msSlotCornersPerLenSheared(bay.p1, bay.p2, slotLen, s, slotsPerBay, multislipShearDeg);
        L.polygon(corners.map(c => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.12, interactive: false }).addTo(multislipPreviewLayer);
        const cen = { lat: corners.reduce((a, c) => a + c.lat, 0) / 4, lng: corners.reduce((a, c) => a + c.lng, 0) / 4 };
        L.marker([cen.lat, cen.lng], {
          interactive: false,
          icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
            html: `<div style="color:#d1fae5;font:700 11px/1.2 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%);text-align:center;">${esc(ids[previewIdx])}<br><span style="font-size:9px;font-weight:400;opacity:.8;">${subAlongFt.toFixed(1)}×${Math.abs(slotLen).toFixed(1)} ft</span></div>` }),
        }).addTo(multislipPreviewLayer);
        previewIdx++;
      }
    }

    // Prominent running count, on the opposite side of the run from the extrusion (so it
    // never sits on top of a slot regardless of N or width) -- the whole point is answering
    // "how many slips right now" at a glance while pressing W/X (or Shift+T for slips/bay), without
    // hunting for it in the small status-bar text.
    const { ux: badgeUx, uy: badgeUy } = msDockvec(p1, p2);
    const badgeMidpoint = { lat: (p1.lat + p2.lat) / 2, lng: (p1.lng + p2.lng) / 2 };
    const badgeOffsetM = (-Math.sign(wFt || 1) * 10) / 3.280839895;
    const badgePt = offsetPoint(badgeMidpoint, -badgeUy * badgeOffsetM, badgeUx * badgeOffsetM);
    const badgeBayPart = slotsPerBay > 1 ? ` <span style="font-size:11px;font-weight:400;opacity:.85;">(${N}×${slotsPerBay})</span>` : "";
    L.marker([badgePt.lat, badgePt.lng], {
      interactive: false,
      icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
        html: `<div style="background:rgba(15,23,42,.92);border:2px solid ${green};border-radius:8px;padding:4px 12px;color:#d1fae5;font:800 16px/1.2 system-ui,sans-serif;white-space:nowrap;transform:translate(-50%,-50%);box-shadow:0 2px 10px rgba(0,0,0,.55);">${totalCount} Slip${totalCount === 1 ? "" : "s"}${badgeBayPart}</div>` }),
    }).addTo(multislipPreviewLayer);

    // Drag handles, rebuilt only when multislipHandlesDirty (see its declaration) --
    // never mid-drag (so the handle being dragged doesn't jump) and never on a plain
    // hover-triggered render (so the markers stay put and grabbable):
    //  - blue circles on each sub-slip's offshore edge (perpendicular extrusion, one per
    //    sub-slip -- two boats sharing a bay can still have different lengths)
    //  - while placing centers: amber squares on the dock line at each finger center
    //    (unconstrained relative to slot width -- only kept from crossing a neighbor;
    //    double-click resets one to even)
    //  - once centers are locked: a single violet handle that sets the shared finger
    //    width, mirrored onto every finger live as it's dragged
    if (!multislipDragging && multislipHandlesDirty) {
      multislipHandlesDirty = false;
      multislipHandleLayer.clearLayers();
      for (let i = 0; i < N; i++) {
        const bay = bayEndpoints[i];
        for (let s = 0; s < slotsPerBay; s++) {
          const slotLen = multislipSlotLengths[i]?.[s] != null ? multislipSlotLengths[i][s] : wFt;
          const offPt = msSlotOffshorePointSheared(bay.p1, bay.p2, slotLen, s, slotsPerBay, multislipShearDeg);
          const iCopy = i, sCopy = s;
          const handle = L.marker([offPt.lat, offPt.lng], {
            icon: L.divIcon({
              className: "",
              html: '<div style="width:12px;height:12px;background:#38bdf8;border:2px solid #fff;border-radius:50%;cursor:grab;margin-left:-6px;margin-top:-6px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
              iconSize: [0, 0], iconAnchor: [0, 0],
            }),
            draggable: true,
            zIndexOffset: 1000,
          });
          handle.on("dragstart", () => { multislipDragging = true; });
          handle.on("drag", (e) => {
            const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
            // Tilted slips: the handle rides the slanted side, whose length is the
            // perpendicular reach divided by cos(tilt).
            const cosTilt = Math.cos((multislipShearDeg * Math.PI) / 180) || 1;
            const rawFt = msPerpDistFt(p1, p2, newPos) / cosTilt;
            const sign = wFt < 0 ? -1 : 1;
            const snapFt = (Math.round(Math.abs(rawFt) / 0.1) * 0.1) || 0.1;
            if (!multislipSlotLengths[iCopy]) multislipSlotLengths[iCopy] = [];
            multislipSlotLengths[iCopy][sCopy] = sign * snapFt;
            renderMultislipPreview();
          });
          handle.on("dragend", () => { multislipDragging = false; multislipHandlesDirty = true; renderMultislipPreview(); });
          multislipHandleLayer.addLayer(handle);
        }
      }
      // Run-end handles (green bars, one per end line): slide that end of the whole run along
      // the dock line to sit on the end finger / end dock. The slip's visible end edge
      // follows the cursor (P1/P2 are finger centers, so the edge sits half a finger width
      // inside them); interior finger centers keep their positions on the map rather than
      // their fractions, so dragging an end never shifts the dividers already placed.
      const endSlotLen = (bayIdx, subIdx) => (multislipSlotLengths[bayIdx]?.[subIdx] != null ? multislipSlotLengths[bayIdx][subIdx] : wFt);
      for (const end of ["start", "end"]) {
        const isStart = end === "start";
        const edgeBase = isStart ? bayEndpoints[0].p1 : bayEndpoints[N - 1].p2;
        const lenFt = isStart ? endSlotLen(0, 0) : endSlotLen(N - 1, slotsPerBay - 1);
        const { tiltX, tiltY } = msTiltVec(p1, p2, multislipShearDeg);
        const halfM = lenFt / 2 / 3.280839895;
        const handlePt = offsetPoint(edgeBase, tiltX * halfM, tiltY * halfM);
        const handle = L.marker([handlePt.lat, handlePt.lng], {
          icon: L.divIcon({
            className: "",
            html: '<div title="Drag to move this end of the run" style="width:8px;height:18px;background:#10b981;border:2px solid #fff;border-radius:3px;cursor:move;margin-left:-4px;margin-top:-9px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
            iconSize: [0, 0], iconAnchor: [0, 0],
          }),
          draggable: true,
          zIndexOffset: 1001,
        });
        let dragStart = null;
        handle.on("dragstart", () => {
          multislipDragging = true;
          dragStart = { p1: { ...multislipP1 }, p2: { ...multislipP2 }, fracs: centerFracs.slice() };
        });
        handle.on("drag", (e) => {
          if (!dragStart) return;
          const a = dragStart.p1, b = dragStart.p2, fr = dragStart.fracs;
          const startLenFt = msDockvec(a, b).lenM * 3.280839895;
          const halfOld = startLenFt > 0.01 ? (multislipFingerWidthFt / 2) / startLenFt : 0;
          const minSpanFrac = startLenFt > 0.01 ? Math.max(1 / startLenFt, halfOld * 2) : 0.01; // >=1 ft past the neighbour edge
          // Cursor marks the visible edge; the run's end (a finger center) sits half a
          // finger width outside it.
          const cursorT = msProjectFrac(a, b, { lat: e.latlng.lat, lng: e.latlng.lng });
          if (isStart) {
            const newT0 = Math.min(cursorT - halfOld, fr[1] - minSpanFrac);
            const span = 1 - newT0;
            if (span <= 0) return;
            multislipP1 = msPointAtFrac(a, b, newT0);
            multislipBoundaryFracs = fr.map((t, k) => (k === 0 ? 0 : k === N ? 1 : (t - newT0) / span));
          } else {
            const newT1 = Math.max(cursorT + halfOld, fr[N - 1] + minSpanFrac);
            if (newT1 <= 0) return;
            multislipP2 = msPointAtFrac(a, b, newT1);
            multislipBoundaryFracs = fr.map((t, k) => (k === 0 ? 0 : k === N ? 1 : t / newT1));
          }
          renderMultislipPreview();
        });
        handle.on("dragend", () => {
          multislipDragging = false;
          multislipHandlesDirty = true;
          // Keep a later R (rotate) anchored to the moved run instead of snapping back to
          // the pre-drag P2 / reference direction.
          if (dragStart && multislipP3) {
            multislipP3 = {
              lat: multislipP3.lat + (multislipP1.lat - dragStart.p1.lat),
              lng: multislipP3.lng + (multislipP1.lng - dragStart.p1.lng),
            };
          }
          multislipP2Base = { lat: multislipP2.lat, lng: multislipP2.lng };
          dragStart = null;
          const newLenFt = msDockvec(multislipP1, multislipP2).lenM * 3.280839895;
          status = `${multislipCentersLocked ? multislipFingerWidthPromptStatus() : multislipPhaseStatus()} Run: ${newLenFt.toFixed(1)} ft.`;
          renderMultislipPreview(); render();
        });
        multislipHandleLayer.addLayer(handle);
      }
      // Tilt handle (rose diamond) on the start end line's offshore corner: drag it sideways
      // to set the tilt directly, pivoting on that end's dock-side corner. Slip lengths
      // don't change -- same as T / [ ] (the tilted side keeps each slip's length).
      {
        const pivot = bayEndpoints[0].p1;
        const lenFt = endSlotLen(0, 0);
        const { tiltX, tiltY } = msTiltVec(p1, p2, multislipShearDeg);
        const lenM = lenFt / 3.280839895;
        const cornerPt = offsetPoint(pivot, tiltX * lenM, tiltY * lenM);
        const handle = L.marker([cornerPt.lat, cornerPt.lng], {
          icon: L.divIcon({
            className: "",
            html: '<div title="Drag to tilt the slips" style="width:11px;height:11px;background:#f43f5e;border:2px solid #fff;transform:rotate(45deg);cursor:grab;margin-left:-6px;margin-top:-6px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
            iconSize: [0, 0], iconAnchor: [0, 0],
          }),
          draggable: true,
          zIndexOffset: 1002,
        });
        handle.on("dragstart", () => { multislipDragging = true; });
        handle.on("drag", (e) => {
          const derived = msDeriveShearedLength(p1, p2, { lat: e.latlng.lat, lng: e.latlng.lng }, pivot);
          if (multislipShearDeg === derived.shearDeg) return;
          multislipShearDeg = derived.shearDeg;
          status = `${multislipToolLabel()}: tilt ${multislipShearDeg}°.`;
          renderMultislipPreview(); render();
        });
        handle.on("dragend", () => {
          multislipDragging = false;
          multislipHandlesDirty = true;
          status = multislipCentersLocked ? multislipFingerWidthPromptStatus() : multislipPhaseStatus();
          renderMultislipPreview(); render();
        });
        multislipHandleLayer.addLayer(handle);
      }
      if (!multislipCentersLocked) {
        for (let k = 1; k < N; k++) {
          const kCopy = k;
          const centerPt = msPointAtFrac(p1, p2, centerFracs[k]);
          const handle = L.marker([centerPt.lat, centerPt.lng], {
            icon: L.divIcon({
              className: "",
              html: '<div style="width:10px;height:14px;background:#f59e0b;border:2px solid #fff;border-radius:3px;cursor:ew-resize;margin-left:-5px;margin-top:-7px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
              iconSize: [0, 0], iconAnchor: [0, 0],
            }),
            draggable: true,
            zIndexOffset: 1000,
          });
          handle.on("dragstart", () => { multislipDragging = true; });
          handle.on("drag", (e) => {
            const rawT = msProjectFrac(p1, p2, { lat: e.latlng.lat, lng: e.latlng.lng });
            const epsFrac = runLenFt > 0.01 ? Math.min(0.1, 0.1 / runLenFt) : 0.001;
            const lo = multislipBoundaryFracs[kCopy - 1] + epsFrac;
            const hi = multislipBoundaryFracs[kCopy + 1] - epsFrac;
            if (lo >= hi) return;
            multislipBoundaryFracs[kCopy] = Math.min(hi, Math.max(lo, rawT));
            renderMultislipPreview();
          });
          handle.on("dragend", () => { multislipDragging = false; multislipHandlesDirty = true; renderMultislipPreview(); });
          handle.on("dblclick", () => { multislipBoundaryFracs[kCopy] = kCopy / N; multislipHandlesDirty = true; renderMultislipPreview(); });
          multislipHandleLayer.addLayer(handle);
        }
      } else if (N > 1) {
        // Reference finger for the shared-width handle is always index 1 (the first
        // interior center) -- its distance from that one center is mirrored as the
        // half-width applied to every finger in msSlotFracsFromCenters.
        const halfFrac = runLenFt > 0.01 ? (multislipFingerWidthFt / 2) / runLenFt : 0;
        const handlePt = msPointAtFrac(p1, p2, centerFracs[1] + halfFrac);
        const handle = L.marker([handlePt.lat, handlePt.lng], {
          icon: L.divIcon({
            className: "",
            html: '<div style="width:14px;height:14px;background:#a855f7;border:2px solid #fff;border-radius:50%;cursor:ew-resize;margin-left:-7px;margin-top:-7px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
            iconSize: [0, 0], iconAnchor: [0, 0],
          }),
          draggable: true,
          zIndexOffset: 1000,
        });
        handle.on("dragstart", () => { multislipDragging = true; });
        handle.on("drag", (e) => {
          const rawT = msProjectFrac(p1, p2, { lat: e.latlng.lat, lng: e.latlng.lng });
          const offsetFt = (rawT - centerFracs[1]) * runLenFt;
          multislipFingerWidthFt = Math.max(0, roundToTenthFoot(Math.abs(offsetFt) * 2));
          renderMultislipPreview();
        });
        handle.on("dragend", () => {
          multislipDragging = false;
          multislipHandlesDirty = true;
          status = multislipFingerWidthPromptStatus();
          renderMultislipPreview(); render();
        });
        multislipHandleLayer.addLayer(handle);
      }
    }
  }

  async function commitMultislip() {
    if (!multislipP1 || !multislipP2 || multislipWidthFt === 0) return;
    const p1 = multislipP1, p2 = multislipP2;
    const N = multislipPartitions;
    const slotsPerBay = multislipSlotsPerBay;
    const totalCount = N * slotsPerBay;
    const wFt = multislipWidthFt;
    const mode = multislipMode;
    // Nested [bayIdx][subIdx], mirroring Comb's combSlotLengths -- captured before
    // stopMultislipMode clears it.
    const slotLengths = multislipSlotLengths.map((bay) => (Array.isArray(bay) ? bay.slice() : []));
    // Finger centers + shared width likewise captured before stopMultislipMode clears
    // them; each bay's along-dock span is its own trimmed slice, not a uniform N-way
    // split, once centers have been repositioned and/or a finger width dialed in.
    const centerFracs = multislipBoundaryFracs.length === N + 1 ? multislipBoundaryFracs.slice() : msEvenFracs(N);
    const fingerWidthFt = multislipFingerWidthFt;
    const shearDeg = multislipShearDeg;
    // Both modes: P1→P2 is split into N bay slices; slotLen = perpendicular extrusion.
    // Width-first: P1→P2 = offshore (W axis); slotLen = dock-along (L). L/W fields swapped.
    // Length-first: P1→P2 = dock run (L axis); slotLen = offshore (W). Normal field order.
    const runLenFt = msDockvec(p1, p2).lenM * 3.280839895;
    const bayFracs = msSlotFracsFromCenters(centerFracs, fingerWidthFt, runLenFt);
    // Each bay's own endpoints (at its trimmed fraction range along P1→P2), reused both for
    // that bay's sub-slip geometry and for its own along-dock length -- sub-slips within a
    // bay split that length evenly and touch edge-to-edge, with no finger gap between them
    // (only bay boundaries get the finger-width trim, already baked into bayFracs above).
    const bayEndpoints = Array.from({ length: N }, (_, i) => ({
      p1: msPointAtFrac(p1, p2, bayFracs[i][0]),
      p2: msPointAtFrac(p1, p2, bayFracs[i][1]),
      alongFt: runLenFt * (bayFracs[i][1] - bayFracs[i][0]),
    }));
    const subAlongFtFor = (i) => roundToTenthFoot(bayEndpoints[i].alongFt / slotsPerBay);
    const ids = previewSlipIds(state, totalCount);
    const conflictMsg = conflictingSlipIdsMessage(ids);
    if (conflictMsg) { status = conflictMsg; render(); return; }
    // Splitting a dock run into more than one slip total is, by definition, generating a
    // multislip — tag every slip (bay AND sub-slip) with the shared group ID. A single slip
    // (N===1, slotsPerBay===1) is never grouped; there's no manual override for that
    // anymore (removed along with the Multi-Slip checkbox). Fingers is the one deliberate
    // exception: it's the same draw flow used purely to digitize a row of ordinary,
    // independent slips efficiently, so it never tags a shared multislip_id regardless of
    // count -- that's the whole reason it's a separate tool from Multislip rather than a
    // checkbox on it (see multislipAsFingers's declaration).
    const isMultiCommit = totalCount > 1 && !multislipAsFingers;
    const sharedGroupId = isMultiCommit ? (state.multislipId || DEFAULT_MULTISLIP_ID) : null;
    if (isMultiCommit) state.multislipId = incrementMultislipId(sharedGroupId);
    // Captured before stopMultislipMode() clears multislipSideLabel/multislipOnDone below --
    // toolLabel keeps any "(side 1 of 2)" context in this commit's own status messages, and
    // onDone (only invoked on the success path, never on a failed commit) is how a Full Dock
    // in progress finds out this side is done and moves on to the next.
    const toolLabel = multislipToolLabel();
    const onDone = multislipOnDone;
    stopMultislipMode();
    status = `Committing ${totalCount} slip${totalCount > 1 ? "s" : ""}...`;
    render();
    const createdIds = [];
    try {
      let idx = 0;
      for (let i = 0; i < N; i++) {
        const bay = bayEndpoints[i];
        const subAlongFt = subAlongFtFor(i);
        for (let s = 0; s < slotsPerBay; s++) {
          const slotWFt = slotLengths[i]?.[s] != null ? slotLengths[i][s] : wFt;
          const corners = msSlotCornersPerLenSheared(bay.p1, bay.p2, slotWFt, s, slotsPerBay, shearDeg);
          const ring = corners.map(c => [c.lat, c.lng]);
          const wkt = buildPolygonWkt(ring);
          const measurementId = makeId("MEAS");
          createdIds.push(measurementId);
          await requestJson(API_BASE, {
            method: "POST",
            body: {
              measurement_id: measurementId,
              marina_id: state.marinaId,
              marina_name: state.marinaName,
              slip_id: ids[idx],
              dock_name: state.dockName || null,
              suffix: state.slipIdSuffix || null,
              region: state.region || null,
              space_type: state.spaceType || null,
              multislip_yn: sharedGroupId ? "yes" : "no",
              multislip_id: sharedGroupId,
              covered_yn: draftCovered ? "yes" : "no",
              boatlift_yn: draftBoatlift ? "yes" : "no",
              fueldock_yn: draftFuelDock ? "yes" : "no",
              ...draftOutOfServiceFields(),
              measured_linear_feet: mode === "width" ? Math.abs(slotWFt) : subAlongFt,
              measured_width_feet:  mode === "width" ? subAlongFt : Math.abs(slotWFt),
              measured_depth_feet: null,
              operational_status: "operable",
              notes: draftComments || "",
              measurement_date: state.measurementDate,
              confidence_score: state.confidenceScore,
              record_created_timestamp: new Date().toISOString(),
              created_by_email: app.auth?.email || null,
              geometry_wkt: wkt,
            },
          });
          incrementSlipIdNumber(state);
          incrementSlipIdSuffix(state);
          idx++;
        }
      }
      await refreshRecords({ quiet: true });
      const allSlotLens = [];
      for (let i = 0; i < N; i++) {
        for (let s = 0; s < slotsPerBay; s++) {
          allSlotLens.push(roundToTenthFoot(Math.abs(slotLengths[i]?.[s] != null ? slotLengths[i][s] : wFt)));
        }
      }
      const uniqueW = [...new Set(allSlotLens)];
      const wDesc = uniqueW.length === 1 ? `${uniqueW[0]} ft W` : "varied W";
      const uniqueL = [...new Set(Array.from({ length: N }, (_, i) => subAlongFtFor(i)))];
      const lDesc = uniqueL.length === 1 ? `${uniqueL[0]} ft L` : "varied L";
      const bayDesc = slotsPerBay > 1 ? ` (${N} bay${N > 1 ? "s" : ""} × ${slotsPerBay})` : "";
      pushUndoEntry(`Created ${totalCount} slip${totalCount === 1 ? "" : "s"} (${multislipAsFingers ? "Fingers" : "Multislip"})`,
        createdIds.map((measurementId) => ({ type: "create", measurementId })));
      app.log("slip.created", { count: totalCount, tool: multislipAsFingers ? "fingers" : "multislip", mode, marina: state.marinaId });
      status = `Committed ${totalCount} side-tie slip${totalCount > 1 ? "s" : ""}${bayDesc} — ${lDesc} × ${wDesc}.`;
      render();
      onDone?.(N, fingerWidthFt, slotsPerBay);
      return;
    } catch (err) {
      status = `${toolLabel} commit failed: ${err?.message || "Unknown error"}`;
    }
    render();
  }

  function startMultislipMode(widthFirst = false, {
    asFingers = false,
    // Full Dock's mechanism for skipping straight to the "ready to add slips" phase: when
    // all three are given, P1/P2/width are seeded directly instead of captured via the
    // usual three clicks (see the hasPreset branch below). onDone fires once this side
    // successfully commits (not on cancel), letting a caller chain another step; sideLabel
    // flows into multislipToolLabel() so status text can say which side/step is active.
    presetP1 = null,
    presetP2 = null,
    presetWidthFt = null,
    // Full Dock side 2's inheritance from side 1's finished numbers (see commitMultislip's
    // onDone call): when given alongside the preset geometry above, these seed the starting
    // slip count and finger width directly instead of guessing them, since a dock's two
    // sides are usually built to the same count/finger-width even when individual finger
    // positions aren't identical (those still need their own per-side handle drags).
    presetPartitions = null,
    presetFingerWidthFt = null,
    presetSlotsPerBay = null,
    onDone = null,
    sideLabel = "",
  } = {}) {
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    multislipActive = true;
    multislipAsFingers = !!asFingers;
    multislipOnDone = typeof onDone === "function" ? onDone : null;
    multislipSideLabel = String(sideLabel || "");
    // Shift+click on the button is a one-off convenience that forces width-first for this
    // session; otherwise derive from the same shared toggle the single-polygon tool uses,
    // so the green/red crosshair and Spacebar mean the same thing in both tools.
    if (widthFirst) firstDimensionIsLength = false;
    multislipMode = firstDimensionIsLength ? "length" : "width";
    const hasPreset = !!(presetP1 && presetP2 && Number.isFinite(presetWidthFt) && presetWidthFt !== 0);
    const hasPresetCount = hasPreset && Number.isFinite(presetPartitions) && presetPartitions >= 1;
    multislipP1 = hasPreset ? presetP1 : null;
    multislipP2 = hasPreset ? presetP2 : null;
    multislipWidthFt = hasPreset ? presetWidthFt : 0;
    multislipShearDeg = 0;
    multislipFreeform = false;
    // Side 2 of a Full Dock inherits side 1's finished count/width directly (see
    // presetPartitions above); a bare Fingers preset (Full Dock side 1) still gets the same
    // typical-width guess a click-driven Fingers start gets, instead of always landing on
    // N=1. Multislip (asFingers false) never guesses, preset or not -- it's a deliberately
    // grouped shared-ID run, so it always starts at one slip and builds up with W/X.
    // Plain (non-preset) starts keep N=1 here regardless -- their own guess happens later,
    // once the width click actually sets a real runLenFt (see onPtrUp).
    multislipPartitions = hasPresetCount
      ? presetPartitions
      : hasPreset && asFingers
        ? guessSlipCountForRun(msDockvec(presetP1, presetP2).lenM * 3.280839895)
        : 1;
    multislipSlotsPerBay = hasPresetCount && Number.isFinite(presetSlotsPerBay) && presetSlotsPerBay >= 1
      ? presetSlotsPerBay
      : 1;
    multislipSlotLengths = hasPreset
      ? Array.from({ length: multislipPartitions }, () => Array(multislipSlotsPerBay).fill(presetWidthFt))
      : [];
    multislipBoundaryFracs = hasPreset ? msEvenFracs(multislipPartitions) : [];
    multislipDragging = false;
    multislipCentersLocked = false;
    multislipFingerWidthFt = hasPresetCount ? Number(presetFingerWidthFt) || 0 : 0;
    multislipHandlesDirty = true;
    multislipHoverPoint = null;
    multislipRotating = false; multislipP2Base = null; multislipP3 = null;
    multislipPreWidthRotating = false; multislipP2AtLock = null;
    status = hasPreset ? multislipPhaseStatus() : multislipP1PromptStatus();
    const mapContainer = map?.getContainer?.() || null;
    try {
      if (mapContainer) {
        if (hasPreset) mapContainer.classList.add("slip-free-draw-cursor");
        else applyFirstDimensionCursor(mapContainer);
      }
    } catch {}

    let ptrDown = null, ptrMoved = false, rafPending = false;
    const THRESH = 6;

    const onPtrDown = (e) => { if (e.button != null && e.button !== 0) return; ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; };
    const onPtrMove = (e) => {
      const ll = map?.mouseEventToLatLng?.(e);
      if (ll) {
        multislipHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
        if (multislipRotating && multislipP1 && multislipP2Base && multislipP3) {
          multislipP2 = msRotateAroundPivot(multislipP2Base, multislipP1, multislipP3, multislipHoverPoint);
          // P2 moving means every handle's lat/lng (all derived from P1->P2) is now stale --
          // unlike a plain hover, this needs a rebuild each frame to track the rotation.
          multislipHandlesDirty = true;
        } else if (multislipPreWidthRotating && multislipP1 && multislipP2AtLock) {
          multislipP2 = msRotateAroundPivot(multislipP2AtLock, multislipP1, multislipP2AtLock, multislipHoverPoint);
        }
      }
      if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; renderMultislipPreview(); }); }
      if (ptrDown) { const dx = Math.abs(e.clientX - ptrDown.x), dy = Math.abs(e.clientY - ptrDown.y); if (dx > THRESH || dy > THRESH) ptrMoved = true; }
    };
    const onPtrUp = (e) => {
      if (!ptrDown) return;
      const ll = map?.mouseEventToLatLng?.(e);
      const clicked = !ptrMoved && ll;
      ptrDown = null; ptrMoved = false;
      if (!clicked) return;
      const pt = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!multislipP1) {
        multislipP1 = pt;
        status = multislipP2PromptStatus();
      } else if (!multislipP2) {
        multislipP2 = pt;
        try { clearFirstDimensionCursor(mapContainer); mapContainer?.classList?.add?.("slip-free-draw-cursor"); } catch {}
        status = multislipWidthPromptStatus();
      } else if (multislipWidthFt === 0) {
        const widthRefP2 = (multislipPreWidthRotating && multislipP2AtLock) ? multislipP2AtLock : multislipP2;
        let rawFt;
        if (multislipFreeform) {
          const derived = msDeriveShearedLength(multislipP1, multislipP2, pt);
          multislipShearDeg = derived.shearDeg;
          rawFt = derived.widthFt;
          multislipFreeform = false; // tilt is locked in now; T/[ ] keep adjusting it in Phase 4
        } else {
          rawFt = msPerpDistFt(multislipP1, widthRefP2, pt);
        }
        const snapFt = Math.round(rawFt / 0.1) * 0.1;
        multislipWidthFt = snapFt !== 0 ? snapFt : 0.1;
        // First-guess N from this marina's own typical slip width, instead of always
        // starting at 1 and W-mashing up to the real count -- see guessSlipCountForRun.
        // Fingers-only: Multislip's whole point is one deliberately-grouped shared-ID run,
        // so it keeps starting at a single slip and building up from there with W/X, same
        // as before this guess existed.
        multislipPartitions = multislipAsFingers
          ? guessSlipCountForRun(msDockvec(multislipP1, multislipP2).lenM * 3.280839895)
          : 1;
        // multislipSlotsPerBay is always 1 here (Shift+T only applies once Phase 4 is reached) --
        // still nested [bayIdx][subIdx] like everywhere else that reads this array.
        multislipSlotLengths = Array.from({ length: multislipPartitions }, () => Array(multislipSlotsPerBay).fill(multislipWidthFt));
        multislipBoundaryFracs = msEvenFracs(multislipPartitions);
        multislipCentersLocked = false;
        multislipFingerWidthFt = 0;
        multislipHandlesDirty = true;
        multislipP3 = pt;
        multislipP2Base = { lat: multislipP2.lat, lng: multislipP2.lng };
        multislipPreWidthRotating = false;
        multislipP2AtLock = null;
        // Rotation used to be mandatory here (a forced 4th click). It's now optional --
        // ready to commit immediately; press "r" to enter the same rotate-around-P1
        // preview on demand, click (or click again) to lock it.
        status = multislipPhaseStatus();
      } else if (multislipRotating) {
        multislipRotating = false;
        status = multislipPhaseStatus();
      }
      renderMultislipPreview();
      render();
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (multislipCentersLocked) {
          // Step back to finger-center placement instead of cancelling the whole run --
          // the width value is discarded (re-dragging the handle re-derives it), but the
          // centers themselves are left as the user placed them.
          multislipCentersLocked = false;
          multislipFingerWidthFt = 0;
          multislipHandlesDirty = true;
          status = multislipPhaseStatus();
          renderMultislipPreview(); render();
          return;
        }
        if (multislipRotating) {
          // Cancel just the on-demand rotation, not the whole multislip -- revert to the
          // pivot position it had before "r" was pressed.
          multislipP2 = multislipP2Base ? { lat: multislipP2Base.lat, lng: multislipP2Base.lng } : multislipP2;
          multislipRotating = false;
          multislipHandlesDirty = true;
          status = multislipPhaseStatus();
          renderMultislipPreview(); render();
          return;
        }
        if (multislipPreWidthRotating) {
          // Same cancel-just-this-mode pattern as the multislipRotating branch above --
          // revert P2 to where it was when "r" was pressed, keep the multislip itself alive.
          multislipP2 = multislipP2AtLock ? { lat: multislipP2AtLock.lat, lng: multislipP2AtLock.lng } : multislipP2;
          multislipPreWidthRotating = false;
          multislipP2AtLock = null;
          status = multislipWidthPromptStatus();
          renderMultislipPreview(); render();
          return;
        }
        stopMultislipMode(); status = `${multislipToolLabel()} placement cancelled.`; render(); return;
      }
      if ((e.key === " " || e.code === "Space") && !multislipP2 && !shouldIgnoreGeometryHotkeys(e)) {
        e.preventDefault();
        firstDimensionIsLength = !firstDimensionIsLength;
        multislipMode = firstDimensionIsLength ? "length" : "width";
        applyFirstDimensionCursor(mapContainer);
        status = !multislipP1 ? multislipP1PromptStatus() : multislipP2PromptStatus();
        renderMultislipPreview(); render();
        return;
      }
      // "t" between the P2 click and the width click: freeform tilt, like the Polygon tool's
      // parallelogram mode -- mouse sets tilt + extrusion together. Mutually exclusive with
      // the "r" pre-width rotate below (both reinterpret the same mouse position).
      if (multislipP2 && multislipWidthFt === 0 && (e.key === "t" || e.key === "T") && !shouldIgnoreGeometryHotkeys(e)) {
        multislipFreeform = !multislipFreeform;
        if (!multislipFreeform) multislipShearDeg = 0;
        if (multislipFreeform && multislipPreWidthRotating) {
          multislipP2 = multislipP2AtLock ? { lat: multislipP2AtLock.lat, lng: multislipP2AtLock.lng } : multislipP2;
          multislipPreWidthRotating = false;
          multislipP2AtLock = null;
        }
        status = multislipWidthPromptStatus();
        renderMultislipPreview(); render();
        return;
      }
      // "r" between the P2 click and the width click: free-rotate the dock-run edge around
      // P1 while width is decided in the same mouse motion (see multislipPreWidthRotating).
      // Distinct from the multislipRotating case below, which only runs after width is
      // already locked and just re-angles the finished shape.
      if (multislipP2 && multislipWidthFt === 0 && (e.key === "r" || e.key === "R") && !shouldIgnoreGeometryHotkeys(e)) {
        if (!multislipPreWidthRotating) {
          multislipFreeform = false; multislipShearDeg = 0;
          multislipP2AtLock = { lat: multislipP2.lat, lng: multislipP2.lng };
          multislipPreWidthRotating = true;
          status = `${multislipToolLabel()}: move mouse to rotate about the start point and set width together, click to lock. Esc to cancel.`;
        } else {
          multislipP2 = multislipP2AtLock ? { lat: multislipP2AtLock.lat, lng: multislipP2AtLock.lng } : multislipP2;
          multislipPreWidthRotating = false;
          multislipP2AtLock = null;
          status = multislipWidthPromptStatus();
        }
        renderMultislipPreview(); render();
        return;
      }
      if (multislipWidthFt !== 0 && !multislipRotating && !shouldIgnoreGeometryHotkeys(e)
          && (((e.key === "t" || e.key === "T") && !e.shiftKey) || ["[", "]", "{", "}"].includes(e.key))) {
        if (e.key === "t" || e.key === "T") {
          // Toggle like Comb: 0 -> a visible starting tilt, nonzero -> back to rectangles.
          multislipShearDeg = multislipShearDeg === 0 ? 10 : 0;
        } else {
          e.preventDefault();
          const step = (e.shiftKey || e.key === "{" || e.key === "}") ? 5 : 1;
          const delta = (e.key === "]" || e.key === "}") ? step : -step;
          multislipShearDeg = Math.max(-80, Math.min(80, multislipShearDeg + delta));
        }
        multislipHandlesDirty = true;
        status = multislipCentersLocked ? multislipFingerWidthPromptStatus() : multislipPhaseStatus();
        renderMultislipPreview(); render();
        return;
      }
      if (multislipWidthFt !== 0 && !multislipRotating) {
        // Once centers are locked, this phase is the finger-width handle drag (see
        // renderMultislipPreview) plus Enter to commit -- bay count and rotation are both
        // decided before locking, so W/X/R don't apply to those anymore. W/X do still apply
        // here to slots-per-bay, though: the real finger width is only known once it's been
        // dragged in from the imagery, so this is the natural point to decide how many boats
        // actually fit side-by-side in that width -- uncapped like Comb's own
        // combSlotsPerBay, not the 1-2-3 Shift+T cycle below (which only runs pre-lock).
        if (multislipCentersLocked) {
          if (e.key === "w" || e.key === "W") {
            const prev = multislipSlotsPerBay;
            multislipSlotsPerBay = Math.min(20, multislipSlotsPerBay + 1);
            if (multislipSlotsPerBay > prev) {
              for (let b = 0; b < multislipPartitions; b++) {
                if (!multislipSlotLengths[b]) multislipSlotLengths[b] = [];
                const lastLen = multislipSlotLengths[b][multislipSlotLengths[b].length - 1] ?? multislipWidthFt;
                multislipSlotLengths[b].push(lastLen);
              }
            }
            multislipHandlesDirty = true;
            status = multislipFingerWidthPromptStatus();
            renderMultislipPreview(); render();
            return;
          }
          if (e.key === "x" || e.key === "X") {
            const prev = multislipSlotsPerBay;
            multislipSlotsPerBay = Math.max(1, multislipSlotsPerBay - 1);
            if (multislipSlotsPerBay < prev) {
              for (let b = 0; b < multislipPartitions; b++) { if (multislipSlotLengths[b]) multislipSlotLengths[b].pop(); }
            }
            multislipHandlesDirty = true;
            status = multislipFingerWidthPromptStatus();
            renderMultislipPreview(); render();
            return;
          }
          if (e.key === "Enter") { e.preventDefault(); commitMultislip().catch(console.error); }
          return;
        }
        if (e.key === "w" || e.key === "W") {
          const prev = multislipPartitions;
          multislipPartitions = Math.min(75, multislipPartitions + 1);
          if (multislipPartitions > prev) {
            // New bay's sub-slips default to the previous last bay's own lengths (or the
            // shared width, if this is the very first bay beyond the original one).
            const lastBay = multislipSlotLengths[multislipSlotLengths.length - 1];
            multislipSlotLengths.push(
              lastBay ? lastBay.slice() : Array(multislipSlotsPerBay).fill(multislipWidthFt),
            );
          }
          // Center edits don't survive a count change -- re-splitting N unevenly-placed
          // centers into N+1 has no unambiguous mapping, so just go back to even.
          multislipBoundaryFracs = msEvenFracs(multislipPartitions);
          multislipHandlesDirty = true;
          status = multislipPhaseStatus();
          renderMultislipPreview(); render();
        } else if (e.key === "x" || e.key === "X") {
          const prev = multislipPartitions;
          multislipPartitions = Math.max(1, multislipPartitions - 1);
          if (multislipPartitions < prev) multislipSlotLengths.pop();
          multislipBoundaryFracs = msEvenFracs(multislipPartitions);
          multislipHandlesDirty = true;
          status = multislipPhaseStatus();
          renderMultislipPreview(); render();
        } else if ((e.key === "t" || e.key === "T") && e.shiftKey) {
          // Shift+T (plain T is tilt). Cycles 1 -> 2 -> 3 -> 1: sub-slips per bay, packed directly adjacent with no
          // finger gap between them (only bay boundaries get the finger-width trim). Every
          // bay's sub-array is resized to match, duplicating its own last length outward
          // (or falling back to the shared width) same as Comb's combSlotsPerBay does.
          multislipSlotsPerBay = multislipSlotsPerBay >= 3 ? 1 : multislipSlotsPerBay + 1;
          for (let b = 0; b < multislipPartitions; b++) {
            if (!multislipSlotLengths[b]) multislipSlotLengths[b] = [];
            while (multislipSlotLengths[b].length < multislipSlotsPerBay) {
              const lastLen = multislipSlotLengths[b][multislipSlotLengths[b].length - 1] ?? multislipWidthFt;
              multislipSlotLengths[b].push(lastLen);
            }
            while (multislipSlotLengths[b].length > multislipSlotsPerBay) multislipSlotLengths[b].pop();
          }
          multislipHandlesDirty = true;
          status = multislipPhaseStatus();
          renderMultislipPreview(); render();
        } else if (e.key === "r" || e.key === "R") {
          multislipRotating = true;
          status = `${multislipToolLabel()}: move cursor to rotate around P1, click to lock. Esc=cancel rotation.`;
          renderMultislipPreview(); render();
        } else if (e.key === "Enter") {
          e.preventDefault();
          if (multislipPartitions > 1) {
            multislipCentersLocked = true;
            multislipHandlesDirty = true;
            status = multislipFingerWidthPromptStatus();
            renderMultislipPreview(); render();
          } else {
            commitMultislip().catch(console.error);
          }
        }
      }
    };

    mapContainer?.addEventListener("pointerdown", onPtrDown, true);
    mapContainer?.addEventListener("pointermove", onPtrMove, true);
    mapContainer?.addEventListener("pointerup", onPtrUp, true);
    mapContainer?.addEventListener("pointercancel", onPtrUp, true);
    document.addEventListener("keydown", onKey);
    multislipOff = () => {
      mapContainer?.removeEventListener("pointerdown", onPtrDown, true);
      mapContainer?.removeEventListener("pointermove", onPtrMove, true);
      mapContainer?.removeEventListener("pointerup", onPtrUp, true);
      mapContainer?.removeEventListener("pointercancel", onPtrUp, true);
      document.removeEventListener("keydown", onKey);
      try { if (mapContainer) mapContainer.classList.remove("slip-free-draw-cursor", "slip-dim-cursor-length", "slip-dim-cursor-width"); } catch {}
    };
    // A preset start (Full Dock's side hand-off) lands directly on Phase 4 with real
    // geometry to show -- render it now rather than waiting for the first mouse move that
    // would otherwise trigger it via onPtrMove's RAF loop.
    renderMultislipPreview();
    render();
  }

  // ── Full Dock: a shared centerline + two independent Fingers rectangles, one on each
  // side ──────────────────────────────────────────────────────────────────────────────

  function stopFullDockMode() {
    if (!fullDockActive && !fullDockOff) return;
    fullDockActive = false;
    fullDockStage = null;
    fullDockP1 = null; fullDockP2 = null;
    fullDockOuterA = null; fullDockOuterB = null; fullDockInnerA = null; fullDockInnerB = null;
    fullDockHoverPoint = null;
    if (fullDockOff) { try { fullDockOff(); } catch {} fullDockOff = null; }
    fullDockPreviewLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  // Given the shared centerline and one side's two signed perpendicular distances (same
  // sign convention as msPerpDistFt: positive = left of p1->p2), returns that side's own
  // (p1, p2, widthFt) -- the inset baseline at innerFt, extruded the remaining distance out
  // to outerFt -- ready to feed straight into startMultislipMode's preset* options.
  function fullDockSideGeometry(p1, p2, innerFt, outerFt) {
    const { ux, uy } = msDockvec(p1, p2);
    const insetM = innerFt / 3.280839895;
    const sideP1 = offsetPoint(p1, -uy * insetM, ux * insetM);
    const sideP2 = offsetPoint(p2, -uy * insetM, ux * insetM);
    const rawWidthFt = outerFt - innerFt;
    const widthFt = Math.sign(rawWidthFt || outerFt || 1) * Math.max(0.1, Math.abs(rawWidthFt));
    return { p1: sideP1, p2: sideP2, widthFt };
  }

  function renderFullDockPreview() {
    fullDockPreviewLayer.clearLayers();
    if (!fullDockActive || !fullDockP1) return;
    const p1 = fullDockP1;
    const neutral = "rgba(148,163,184,0.9)";
    if (!fullDockP2) {
      if (!fullDockHoverPoint) return;
      L.polyline([[p1.lat, p1.lng], [fullDockHoverPoint.lat, fullDockHoverPoint.lng]], {
        color: neutral, weight: 2, dashArray: "6 4", interactive: false,
      }).addTo(fullDockPreviewLayer);
      return;
    }
    const p2 = fullDockP2;
    L.polyline([[p1.lat, p1.lng], [p2.lat, p2.lng]], { color: neutral, weight: 3, interactive: false }).addTo(fullDockPreviewLayer);

    const rawHoverFt = fullDockHoverPoint ? msPerpDistFt(p1, p2, fullDockHoverPoint) : null;
    const snapHoverFt = rawHoverFt == null ? null : Math.round(rawHoverFt / 0.1) * 0.1;
    const drawRect = (color, innerFt, outerFt, dashed) => {
      const { p1: sp1, p2: sp2, widthFt } = fullDockSideGeometry(p1, p2, innerFt, outerFt);
      const corners = msRectCorners(sp1, sp2, widthFt);
      L.polygon(corners.map((c) => [c.lat, c.lng]), {
        color, weight: 2, fillColor: color, fillOpacity: dashed ? 0.08 : 0.15,
        dashArray: dashed ? "6 4" : null, interactive: false,
      }).addTo(fullDockPreviewLayer);
    };
    const green = "rgba(16,185,129,0.95)";
    const amber = "rgba(245,158,11,0.95)";

    // Each side draws exactly one rectangle at a time (never both the envelope and the
    // final rect at once, which would just overlap): the final inner->outer rect once both
    // are known (solid), otherwise the outer-only envelope as a dashed reference guide, or
    // a live dashed preview tracking the cursor while that side's next click is pending.
    if (fullDockOuterA != null && fullDockInnerA != null) drawRect(green, fullDockInnerA, fullDockOuterA, false);
    else if (fullDockOuterA != null) drawRect(green, 0, fullDockOuterA, true);
    else if (fullDockStage === "outerA" && snapHoverFt != null && snapHoverFt !== 0) drawRect(green, 0, snapHoverFt, true);
    else if (fullDockStage === "innerA" && fullDockOuterA != null && snapHoverFt != null) drawRect(green, snapHoverFt, fullDockOuterA, true);

    if (fullDockOuterB != null && fullDockInnerB != null) drawRect(amber, fullDockInnerB, fullDockOuterB, false);
    else if (fullDockOuterB != null) drawRect(amber, 0, fullDockOuterB, true);
    else if (fullDockStage === "outerB" && snapHoverFt != null && snapHoverFt !== 0) drawRect(amber, 0, snapHoverFt, true);
    else if (fullDockStage === "innerB" && fullDockOuterB != null && snapHoverFt != null) drawRect(amber, snapHoverFt, fullDockOuterB, true);

    if (snapHoverFt != null && ["outerA", "outerB", "innerA", "innerB"].includes(fullDockStage)) {
      L.marker([fullDockHoverPoint.lat, fullDockHoverPoint.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:#e2e8f0;font:700 11px/1 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(12px,-50%)">${Math.abs(snapHoverFt).toFixed(1)} ft</div>` }),
      }).addTo(fullDockPreviewLayer);
    }
  }

  function fullDockStageStatus(stage) {
    // innerA/innerB read state.fullDockWalkwayFt live (not baked in at load time) so the
    // Enter-to-accept hint always matches whatever the current default actually is.
    const walkwayFt = Number(state.fullDockWalkwayFt) || 15;
    const map = {
      p1: "Full Dock: click the start of the main dock centerline.",
      p2: "Full Dock: click the end of the centerline.",
      outerA: "Full Dock: click how far out the fingers extend on one side (the far/offshore end).",
      outerB: "Full Dock: click how far out the fingers extend on the other side.",
      innerA: `Full Dock: click where the slips start on the first side (past the walkway), or press Enter to accept the ${walkwayFt.toFixed(1)} ft default.`,
      innerB: `Full Dock: click where the slips start on the other side, or press Enter to accept the ${walkwayFt.toFixed(1)} ft default.`,
    };
    return map[stage] || "";
  }

  function startFullDockMode() {
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopMultislipMode();
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    fullDockActive = true;
    fullDockStage = "p1";
    fullDockP1 = null; fullDockP2 = null;
    fullDockOuterA = null; fullDockOuterB = null; fullDockInnerA = null; fullDockInnerB = null;
    fullDockHoverPoint = null;
    status = fullDockStageStatus("p1");
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.classList.add("slip-free-draw-cursor"); } catch {}

    let ptrDown = null, ptrMoved = false, rafPending = false;
    const THRESH = 6;

    // Applies a signed perpendicular-ft value to whichever outer/inner stage is currently
    // pending and advances fullDockStage -- shared by the click handler (a real measured
    // click) and the Enter-key shortcut on the inner stages (accepts state.fullDockWalkwayFt
    // as-is). Returns true once the 6th value lands and both sides have been handed off to
    // the Multislip/Fingers engine, so the caller knows to skip its own trailing
    // status/render (stopFullDockMode + startMultislipMode already render).
    const applyFullDockPerpValue = (snapFt) => {
      if (fullDockStage === "outerA") { fullDockOuterA = snapFt; fullDockStage = "outerB"; return false; }
      if (fullDockStage === "outerB") { fullDockOuterB = snapFt; fullDockStage = "innerA"; return false; }
      if (fullDockStage === "innerA") { fullDockInnerA = snapFt; fullDockStage = "innerB"; return false; }
      if (fullDockStage !== "innerB") return false;
      fullDockInnerB = snapFt;
      const p1 = fullDockP1, p2 = fullDockP2;
      const sideA = fullDockSideGeometry(p1, p2, fullDockInnerA, fullDockOuterA);
      const sideB = fullDockSideGeometry(p1, p2, fullDockInnerB, fullDockOuterB);
      stopFullDockMode();
      startMultislipMode(false, {
        asFingers: true,
        presetP1: sideA.p1,
        presetP2: sideA.p2,
        presetWidthFt: sideA.widthFt,
        sideLabel: "side 1 of 2",
        // Side 2 inherits side 1's just-committed slip count and finger width as its own
        // starting point (see presetPartitions/presetFingerWidthFt's declaration) -- most
        // docks are built to the same count/finger-width on both sides even when the
        // individual fingers themselves land in different spots, so this saves the
        // W-mashing and width-dragging in the common case while leaving every value still
        // adjustable per side.
        onDone: (sideACount, sideAFingerWidthFt, sideASlotsPerBay) => {
          startMultislipMode(false, {
            asFingers: true,
            presetP1: sideB.p1,
            presetP2: sideB.p2,
            presetWidthFt: sideB.widthFt,
            presetPartitions: sideACount,
            presetFingerWidthFt: sideAFingerWidthFt,
            presetSlotsPerBay: sideASlotsPerBay,
            sideLabel: "side 2 of 2",
            onDone: () => { status = "Full Dock: both sides committed."; render(); },
          });
        },
      });
      return true;
    };

    const onPtrDown = (e) => { if (e.button != null && e.button !== 0) return; ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; };
    const onPtrMove = (e) => {
      const ll = map?.mouseEventToLatLng?.(e);
      if (ll) fullDockHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; renderFullDockPreview(); }); }
      if (ptrDown) { const dx = Math.abs(e.clientX - ptrDown.x), dy = Math.abs(e.clientY - ptrDown.y); if (dx > THRESH || dy > THRESH) ptrMoved = true; }
    };
    const onPtrUp = (e) => {
      if (!ptrDown) return;
      const ll = map?.mouseEventToLatLng?.(e);
      const clicked = !ptrMoved && ll;
      ptrDown = null; ptrMoved = false;
      if (!clicked) return;
      const pt = { lat: Number(ll.lat), lng: Number(ll.lng) };

      if (fullDockStage === "p1") {
        fullDockP1 = pt;
        fullDockStage = "p2";
      } else if (fullDockStage === "p2") {
        fullDockP2 = pt;
        fullDockStage = "outerA";
      } else if (fullDockStage === "outerA" || fullDockStage === "outerB" || fullDockStage === "innerA" || fullDockStage === "innerB") {
        const rawFt = msPerpDistFt(fullDockP1, fullDockP2, pt);
        const snapFt = Math.round(rawFt / 0.1) * 0.1;
        if (snapFt === 0) { render(); return; } // degenerate click on the centerline itself -- ignore, wait for a real click
        if (applyFullDockPerpValue(snapFt)) return; // finished -- side 1 already rendered its own status
      }
      status = fullDockStageStatus(fullDockStage);
      renderFullDockPreview();
      render();
    };
    const onKey = (e) => {
      if (e.key === "Escape") { stopFullDockMode(); status = "Full Dock cancelled."; render(); return; }
      if (e.key === "Enter" && (fullDockStage === "innerA" || fullDockStage === "innerB") && !shouldIgnoreGeometryHotkeys(e)) {
        e.preventDefault?.();
        const outerFt = fullDockStage === "innerA" ? fullDockOuterA : fullDockOuterB;
        const magnitude = Math.min(Math.abs(Number(state.fullDockWalkwayFt) || 15), Math.abs(outerFt) - 0.5);
        const defaultInnerFt = Math.sign(outerFt || 1) * Math.max(0.5, magnitude);
        if (applyFullDockPerpValue(defaultInnerFt)) return;
        status = fullDockStageStatus(fullDockStage);
        renderFullDockPreview();
        render();
      }
    };

    mapContainer?.addEventListener("pointerdown", onPtrDown, true);
    mapContainer?.addEventListener("pointermove", onPtrMove, true);
    mapContainer?.addEventListener("pointerup", onPtrUp, true);
    mapContainer?.addEventListener("pointercancel", onPtrUp, true);
    document.addEventListener("keydown", onKey);
    fullDockOff = () => {
      mapContainer?.removeEventListener("pointerdown", onPtrDown, true);
      mapContainer?.removeEventListener("pointermove", onPtrMove, true);
      mapContainer?.removeEventListener("pointerup", onPtrUp, true);
      mapContainer?.removeEventListener("pointercancel", onPtrUp, true);
      document.removeEventListener("keydown", onKey);
      try { if (mapContainer) mapContainer.classList.remove("slip-free-draw-cursor"); } catch {}
    };
    render();
  }

  // ── Comb tool: click bay-edge pairs along a dock (skip fingers), then one
  // perpendicular click sets the length for every bay at once. Each bay then
  // splits into combSlotsPerBay slips (default 2 = "double slip"). ──────────

  function stopCombMode() {
    if (!combActive && !combOff) return;
    combActive = false;
    combPhase = "bays";
    combBays = [];
    combPendingP1 = null;
    combWidthFt = 0;
    combSlotLengths = [];
    combSlotsPerBay = 2;
    combShearDeg = 0;
    combFreeform = false;
    combDragging = false;
    combHoverPoint = null;
    if (combOff) { try { combOff(); } catch {} combOff = null; }
    combPreviewLayer.clearLayers();
    combHandleLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  function renderCombPreview() {
    combPreviewLayer.clearLayers();
    if (!combActive) return;
    const amber = "rgba(245,158,11,0.95)";
    const amberFill = "#f59e0b";
    const green = "rgba(16,185,129,0.95)";
    const greenFill = "#10b981";

    for (const bay of combBays) {
      L.polyline([[bay.p1.lat, bay.p1.lng], [bay.p2.lat, bay.p2.lng]], { color: amber, weight: 3, interactive: false }).addTo(combPreviewLayer);
      L.circleMarker([bay.p1.lat, bay.p1.lng], { radius: 4, color: amber, fillColor: amberFill, fillOpacity: 1, weight: 2, interactive: false }).addTo(combPreviewLayer);
      L.circleMarker([bay.p2.lat, bay.p2.lng], { radius: 4, color: amber, fillColor: amberFill, fillOpacity: 1, weight: 2, interactive: false }).addTo(combPreviewLayer);
    }

    if (combPhase === "bays") {
      if (combPendingP1 && combHoverPoint) {
        L.circleMarker([combPendingP1.lat, combPendingP1.lng], { radius: 5, color: amber, fillColor: amberFill, fillOpacity: 1, weight: 2, interactive: false }).addTo(combPreviewLayer);
        L.polyline([[combPendingP1.lat, combPendingP1.lng], [combHoverPoint.lat, combHoverPoint.lng]], { color: amber, weight: 2, dashArray: "6 4", interactive: false }).addTo(combPreviewLayer);
      }
      return;
    }

    if (combPhase === "length") {
      if (!combHoverPoint || !combBays.length) return;
      const ref = combBays[0];
      let rawFt;
      if (combFreeform) {
        const derived = msDeriveShearedLength(ref.p1, ref.p2, combHoverPoint);
        combShearDeg = derived.shearDeg;
        rawFt = derived.widthFt;
      } else {
        rawFt = msPerpDistFt(ref.p1, ref.p2, combHoverPoint);
      }
      const snapFt = Math.round(rawFt / 0.1) * 0.1;
      if (snapFt === 0) return;
      for (const bay of combBays) {
        const corners = msRectCornersSheared(bay.p1, bay.p2, snapFt, combShearDeg);
        L.polygon(corners.map(c => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.15, dashArray: "6 4", interactive: false }).addTo(combPreviewLayer);
      }
      const midAlong = { lat: (ref.p1.lat + ref.p2.lat) / 2, lng: (ref.p1.lng + ref.p2.lng) / 2 };
      const wM = snapFt / 3.280839895;
      const { tiltX, tiltY } = msTiltVec(ref.p1, ref.p2, combShearDeg);
      const labelPt = offsetPoint(midAlong, tiltX * wM, tiltY * wM);
      const shearSuffix = combShearDeg ? ` (shear ${combShearDeg}°)` : "";
      L.marker([labelPt.lat, labelPt.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:#d1fae5;font:700 11px/1 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%)">L: ${Math.abs(snapFt)} ft${shearSuffix}</div>` }),
      }).addTo(combPreviewLayer);
      return;
    }

    // Phase "adjust": per-bay, per-slot rectangles + drag handles
    const totalCount = combBays.length * combSlotsPerBay;
    const ids = previewSlipIds(state, totalCount);
    let idx = 0;
    for (let b = 0; b < combBays.length; b++) {
      const bay = combBays[b];
      const slipAlongFt = (msDockvec(bay.p1, bay.p2).lenM * 3.280839895) / combSlotsPerBay;
      for (let s = 0; s < combSlotsPerBay; s++) {
        const slotLen = combSlotLengths[b]?.[s] != null ? combSlotLengths[b][s] : combWidthFt;
        const corners = msSlotCornersPerLenSheared(bay.p1, bay.p2, slotLen, s, combSlotsPerBay, combShearDeg);
        L.polygon(corners.map(c => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.12, interactive: false }).addTo(combPreviewLayer);
        const cen = msSlotCentroid(bay.p1, bay.p2, slotLen, s, combSlotsPerBay);
        L.marker([cen.lat, cen.lng], {
          interactive: false,
          icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
            html: `<div style="color:#d1fae5;font:700 11px/1.2 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%);text-align:center;">${esc(ids[idx])}<br><span style="font-size:9px;font-weight:400;opacity:.8;">${slipAlongFt.toFixed(1)}×${Math.abs(slotLen).toFixed(1)} ft</span></div>` }),
        }).addTo(combPreviewLayer);
        idx++;
      }
    }

    if (!combDragging) {
      combHandleLayer.clearLayers();
      for (let b = 0; b < combBays.length; b++) {
        const bay = combBays[b];
        for (let s = 0; s < combSlotsPerBay; s++) {
          const slotLen = combSlotLengths[b]?.[s] != null ? combSlotLengths[b][s] : combWidthFt;
          const offPt = msSlotOffshorePointSheared(bay.p1, bay.p2, slotLen, s, combSlotsPerBay, combShearDeg);
          const bCopy = b, sCopy = s;
          const handle = L.marker([offPt.lat, offPt.lng], {
            icon: L.divIcon({
              className: "",
              html: '<div style="width:12px;height:12px;background:#38bdf8;border:2px solid #fff;border-radius:50%;cursor:grab;margin-left:-6px;margin-top:-6px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>',
              iconSize: [0, 0], iconAnchor: [0, 0],
            }),
            draggable: true,
            zIndexOffset: 1000,
          });
          handle.on("dragstart", () => { combDragging = true; });
          handle.on("drag", (e) => {
            const newPos = { lat: e.latlng.lat, lng: e.latlng.lng };
            const rawFt = msPerpDistFt(bay.p1, bay.p2, newPos);
            const sign = combWidthFt < 0 ? -1 : 1;
            const snapFt = (Math.round(Math.abs(rawFt) / 0.1) * 0.1) || 0.1;
            if (!combSlotLengths[bCopy]) combSlotLengths[bCopy] = [];
            combSlotLengths[bCopy][sCopy] = sign * snapFt;
            renderCombPreview();
          });
          handle.on("dragend", () => { combDragging = false; renderCombPreview(); });
          combHandleLayer.addLayer(handle);
        }
      }
    }
  }

  async function commitComb() {
    if (!combBays.length || combWidthFt === 0) return;
    const bays = combBays.slice();
    const slotsPerBay = combSlotsPerBay;
    const slotLengths = combBays.map((_, b) => (combSlotLengths[b] || []).slice());
    const shearDeg = combShearDeg;
    const totalCount = bays.length * slotsPerBay;
    const ids = previewSlipIds(state, totalCount);
    const conflictMsg = conflictingSlipIdsMessage(ids);
    if (conflictMsg) { status = conflictMsg; render(); return; }
    stopCombMode();
    status = `Committing ${totalCount} slip${totalCount > 1 ? "s" : ""}...`;
    render();
    const createdIds = [];
    try {
      let idx = 0;
      for (let b = 0; b < bays.length; b++) {
        const bay = bays[b];
        const slipAlongFt = roundToTenthFoot((msDockvec(bay.p1, bay.p2).lenM * 3.280839895) / slotsPerBay);
        for (let s = 0; s < slotsPerBay; s++) {
          const slotWFt = slotLengths[b]?.[s] != null ? slotLengths[b][s] : combWidthFt;
          const corners = msSlotCornersPerLenSheared(bay.p1, bay.p2, slotWFt, s, slotsPerBay, shearDeg);
          const ring = corners.map(c => [c.lat, c.lng]);
          const wkt = buildPolygonWkt(ring);
          const measurementId = makeId("MEAS");
          createdIds.push(measurementId);
          await requestJson(API_BASE, {
            method: "POST",
            body: {
              measurement_id: measurementId,
              marina_id: state.marinaId,
              marina_name: state.marinaName,
              slip_id: ids[idx],
              dock_name: state.dockName || null,
              suffix: state.slipIdSuffix || null,
              region: state.region || null,
              space_type: state.spaceType || null,
              // Comb splits a bay into adjacent slots for footprint purposes only -- it must
              // never create a multislip group (multislip_id is the dedicated Multislip
              // tool's exclusive job, see startMultislipMode/commitMultislip below).
              multislip_yn: "no",
              multislip_id: null,
              covered_yn: draftCovered ? "yes" : "no",
              boatlift_yn: draftBoatlift ? "yes" : "no",
              fueldock_yn: draftFuelDock ? "yes" : "no",
              ...draftOutOfServiceFields(),
              measured_linear_feet: Math.abs(slotWFt),
              measured_width_feet: slipAlongFt,
              measured_depth_feet: null,
              operational_status: "operable",
              notes: draftComments || "",
              measurement_date: state.measurementDate,
              confidence_score: state.confidenceScore,
              record_created_timestamp: new Date().toISOString(),
              created_by_email: app.auth?.email || null,
              geometry_wkt: wkt,
            },
          });
          incrementSlipIdNumber(state);
          incrementSlipIdSuffix(state);
          idx++;
        }
      }
      await refreshRecords({ quiet: true });
      pushUndoEntry(`Created ${totalCount} slip${totalCount === 1 ? "" : "s"} (Comb)`,
        createdIds.map((measurementId) => ({ type: "create", measurementId })));
      app.log("slip.created", { count: totalCount, tool: "comb", bays: bays.length, slotsPerBay, marina: state.marinaId });
      status = `Committed ${totalCount} slip${totalCount > 1 ? "s" : ""} across ${bays.length} bay${bays.length > 1 ? "s" : ""}.`;
    } catch (err) {
      status = `Comb commit failed: ${err?.message || "Unknown error"}`;
    }
    render();
  }

  function startCombMode() {
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopMultislipMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    combActive = true;
    combPhase = "bays";
    combBays = [];
    combPendingP1 = null;
    combWidthFt = 0;
    combSlotLengths = [];
    combSlotsPerBay = 2;
    combShearDeg = 0;
    combFreeform = false;
    combDragging = false;
    combHoverPoint = null;
    status = "Comb: click one edge of the first bay (double-slip width). Skip fingers by clicking the next bay's edge directly.";
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.classList.add("slip-free-draw-cursor"); } catch {}

    let ptrDown = null, ptrMoved = false, rafPending = false;
    const THRESH = 6;

    const onPtrDown = (e) => { if (e.button != null && e.button !== 0) return; ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; };
    const onPtrMove = (e) => {
      const ll = map?.mouseEventToLatLng?.(e);
      if (ll) combHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; renderCombPreview(); }); }
      if (ptrDown) { const dx = Math.abs(e.clientX - ptrDown.x), dy = Math.abs(e.clientY - ptrDown.y); if (dx > THRESH || dy > THRESH) ptrMoved = true; }
    };
    const onPtrUp = (e) => {
      if (!ptrDown) return;
      const ll = map?.mouseEventToLatLng?.(e);
      const clicked = !ptrMoved && ll;
      ptrDown = null; ptrMoved = false;
      if (!clicked) return;
      const pt = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (combPhase === "bays") {
        if (!combPendingP1) {
          combPendingP1 = pt;
          status = "Comb: click the other edge of this bay.";
        } else {
          combBays.push({ p1: combPendingP1, p2: pt });
          combPendingP1 = null;
          status = `Comb: ${combBays.length} bay${combBays.length > 1 ? "s" : ""}. Click next bay's edge (skip fingers), Backspace=undo, Enter=set length.`;
        }
      } else if (combPhase === "length") {
        const ref = combBays[0];
        let rawFt;
        if (combFreeform) {
          const derived = msDeriveShearedLength(ref.p1, ref.p2, pt);
          combShearDeg = derived.shearDeg;
          rawFt = derived.widthFt;
        } else {
          rawFt = msPerpDistFt(ref.p1, ref.p2, pt);
        }
        const snapFt = Math.round(rawFt / 0.1) * 0.1;
        combWidthFt = snapFt !== 0 ? snapFt : 0.1;
        combSlotLengths = combBays.map(() => Array(combSlotsPerBay).fill(combWidthFt));
        combPhase = "adjust";
        status = `Comb: ${combBays.length * combSlotsPerBay} slip${combBays.length * combSlotsPerBay > 1 ? "s" : ""}. W/X=slips per bay, t=shear, drag handles to adjust, Enter=commit, Esc=cancel.`;
      }
      renderCombPreview();
      render();
    };
    const onKey = (e) => {
      if (e.key === "Escape") { stopCombMode(); status = "Comb placement cancelled."; render(); return; }
      if (combPhase === "bays") {
        if (e.key === "Backspace") {
          e.preventDefault();
          if (combPendingP1) { combPendingP1 = null; status = "Comb: click one edge of the bay."; }
          else if (combBays.length) { combBays.pop(); status = `Comb: ${combBays.length} bay${combBays.length === 1 ? "" : "s"}. Click next bay's edge, Backspace=undo, Enter=set length.`; }
          renderCombPreview(); render();
        } else if (e.key === "Enter" && !combPendingP1 && combBays.length >= 1) {
          e.preventDefault();
          combPhase = "length";
          status = "Comb: move offshore and click to set length for all bays at once. t=freeform angle.";
          renderCombPreview(); render();
        }
      } else if (combPhase === "length") {
        if (e.key === "t" || e.key === "T") {
          combFreeform = !combFreeform;
          if (!combFreeform) combShearDeg = 0;
          const shearPart = combFreeform ? ` Freeform: move to set angle + length together (currently ${combShearDeg}°).` : "";
          status = `Comb: move offshore and click to set length for all bays at once.${shearPart} t=freeform angle.`;
          renderCombPreview(); render();
        }
      } else if (combPhase === "adjust") {
        const combAdjustStatus = () => {
          const count = combBays.length * combSlotsPerBay;
          const shearPart = combShearDeg ? ` Shear: ${combShearDeg}°.` : "";
          return `Comb: ${count} slip${count > 1 ? "s" : ""}.${shearPart} W/X=slips per bay, t=shear, [/]=adjust shear, Enter=commit, Esc=cancel.`;
        };
        if (e.key === "w" || e.key === "W") {
          const prev = combSlotsPerBay;
          combSlotsPerBay = Math.min(20, combSlotsPerBay + 1);
          if (combSlotsPerBay > prev) {
            for (let b = 0; b < combBays.length; b++) {
              if (!combSlotLengths[b]) combSlotLengths[b] = [];
              const lastLen = combSlotLengths[b][combSlotLengths[b].length - 1] ?? combWidthFt;
              combSlotLengths[b].push(lastLen);
            }
          }
          status = combAdjustStatus();
          renderCombPreview(); render();
        } else if (e.key === "x" || e.key === "X") {
          const prev = combSlotsPerBay;
          combSlotsPerBay = Math.max(1, combSlotsPerBay - 1);
          if (combSlotsPerBay < prev) {
            for (let b = 0; b < combBays.length; b++) { if (combSlotLengths[b]) combSlotLengths[b].pop(); }
          }
          status = combAdjustStatus();
          renderCombPreview(); render();
        } else if (e.key === "t" || e.key === "T") {
          // Toggle: 0 -> a modest starting angle (immediately visible, like the Polygon
          // tool's parallelogram mode snapping on), nonzero -> back to a plain rectangle.
          combShearDeg = combShearDeg === 0 ? 10 : 0;
          status = combAdjustStatus();
          renderCombPreview(); render();
        } else if (e.key === "[" || e.key === "]") {
          e.preventDefault();
          const step = e.shiftKey ? 5 : 1;
          const delta = e.key === "]" ? step : -step;
          combShearDeg = Math.max(-80, Math.min(80, combShearDeg + delta));
          status = combAdjustStatus();
          renderCombPreview(); render();
        } else if (e.key === "Enter") { e.preventDefault(); commitComb().catch(console.error); }
      }
    };

    mapContainer?.addEventListener("pointerdown", onPtrDown, true);
    mapContainer?.addEventListener("pointermove", onPtrMove, true);
    mapContainer?.addEventListener("pointerup", onPtrUp, true);
    mapContainer?.addEventListener("pointercancel", onPtrUp, true);
    document.addEventListener("keydown", onKey);
    combOff = () => {
      mapContainer?.removeEventListener("pointerdown", onPtrDown, true);
      mapContainer?.removeEventListener("pointermove", onPtrMove, true);
      mapContainer?.removeEventListener("pointerup", onPtrUp, true);
      mapContainer?.removeEventListener("pointercancel", onPtrUp, true);
      document.removeEventListener("keydown", onKey);
      try { if (mapContainer) mapContainer.classList.remove("slip-free-draw-cursor"); } catch {}
    };
    render();
  }

  // ── Chain tool: click a bent polyline along a dock edge (one point per corner,
  // Backspace to undo), then one perpendicular click sets a single global width
  // for every segment at once. Each segment becomes its own slip, all sharing
  // one multislip_id since it's one continuous run — unlike Comb's per-bay
  // grouping. Interior corners are mitered (mitreChainRing); any corner whose
  // miter would blow past the limit falls back to a bevel and is flagged in the
  // review preview so the operator can fix it by repositioning that vertex. ──

  function stopChainMode() {
    if (!chainActive && !chainOff) return;
    chainActive = false;
    chainPhase = "points";
    chainPoints = [];
    chainHoverPoint = null;
    chainWidthFt = 0;
    if (chainOff) { try { chainOff(); } catch {} chainOff = null; }
    chainPreviewLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  function renderChainPreview() {
    chainPreviewLayer.clearLayers();
    if (!chainActive) return;
    const amber = "rgba(245,158,11,0.95)";
    const amberFill = "#f59e0b";
    const green = "rgba(16,185,129,0.95)";
    const greenFill = "#10b981";
    const flagColor = "rgba(248,113,113,0.95)";

    if (chainPoints.length) {
      L.polyline(chainPoints.map((p) => [p.lat, p.lng]), { color: amber, weight: 3, interactive: false }).addTo(chainPreviewLayer);
      for (const p of chainPoints) {
        L.circleMarker([p.lat, p.lng], { radius: 4, color: amber, fillColor: amberFill, fillOpacity: 1, weight: 2, interactive: false }).addTo(chainPreviewLayer);
      }
    }

    if (chainPhase === "points") {
      if (chainPoints.length && chainHoverPoint) {
        const last = chainPoints[chainPoints.length - 1];
        L.polyline([[last.lat, last.lng], [chainHoverPoint.lat, chainHoverPoint.lng]], { color: amber, weight: 2, dashArray: "6 4", interactive: false }).addTo(chainPreviewLayer);
      }
      return;
    }

    if (chainPoints.length < 2) return;

    if (chainPhase === "width") {
      if (!chainHoverPoint) return;
      const rawFt = msPerpDistFt(chainPoints[0], chainPoints[1], chainHoverPoint);
      const snapFt = Math.round(rawFt / 0.1) * 0.1;
      if (snapFt === 0) return;
      const cappedFt = clampChainWidthFt(chainPoints, snapFt);
      const atMax = Math.abs(cappedFt) < Math.abs(snapFt) - 1e-6;
      const { rings } = mitreChainRing(chainPoints, cappedFt);
      for (const ring of rings) {
        L.polygon(ring.map((c) => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.15, dashArray: "6 4", interactive: false }).addTo(chainPreviewLayer);
      }
      const { ux, uy } = msDockvec(chainPoints[0], chainPoints[1]);
      const midAlong = { lat: (chainPoints[0].lat + chainPoints[1].lat) / 2, lng: (chainPoints[0].lng + chainPoints[1].lng) / 2 };
      const wM = cappedFt / 3.280839895;
      const labelPt = offsetPoint(midAlong, -uy * wM, ux * wM);
      L.marker([labelPt.lat, labelPt.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:${atMax ? "#fca5a5" : "#d1fae5"};font:700 11px/1 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%)">W: ${Math.abs(cappedFt)} ft${atMax ? " (max)" : ""}</div>` }),
      }).addTo(chainPreviewLayer);
      return;
    }

    // Phase "review": final mitred rings, slip-id badges, flagged corners.
    const { rings, flaggedVertices } = mitreChainRing(chainPoints, chainWidthFt);
    const ids = previewSlipIds(state, rings.length);
    rings.forEach((ring, i) => {
      L.polygon(ring.map((c) => [c.lat, c.lng]), { color: green, weight: 2, fillColor: greenFill, fillOpacity: 0.15, interactive: false }).addTo(chainPreviewLayer);
      const cen = { lat: ring.reduce((s, c) => s + c.lat, 0) / ring.length, lng: ring.reduce((s, c) => s + c.lng, 0) / ring.length };
      // Segment length is the dock-edge distance for this slip (points[i]->points[i+1]),
      // same value commitChain() stores as measured_linear_feet -- every segment shares
      // chainWidthFt for the offshore dimension.
      const slipAlongFt = msDockvec(chainPoints[i], chainPoints[i + 1]).lenM * 3.280839895;
      L.marker([cen.lat, cen.lng], {
        interactive: false,
        icon: L.divIcon({ className: "", iconSize: null, iconAnchor: [0, 0],
          html: `<div style="color:#d1fae5;font:700 10px/1.2 system-ui,sans-serif;text-shadow:0 1px 3px rgba(0,0,0,.9);white-space:nowrap;transform:translate(-50%,-50%);text-align:center;">${esc(ids[i] || "")}<br><span style="font-size:9px;font-weight:400;opacity:.8;">${slipAlongFt.toFixed(1)}×${Math.abs(chainWidthFt).toFixed(1)} ft</span></div>` }),
      }).addTo(chainPreviewLayer);
    });
    for (const vIdx of flaggedVertices) {
      const v = chainPoints[vIdx];
      L.circleMarker([v.lat, v.lng], { radius: 8, color: flagColor, fillColor: flagColor, fillOpacity: 0.2, weight: 2, dashArray: "3 3", interactive: false }).addTo(chainPreviewLayer);
    }
  }

  async function commitChain() {
    if (chainPoints.length < 3 || !chainWidthFt) return;
    const points = chainPoints.slice();
    const widthFt = chainWidthFt;
    const { rings } = mitreChainRing(points, widthFt);
    const totalCount = rings.length;
    const ids = previewSlipIds(state, totalCount);
    const conflictMsg = conflictingSlipIdsMessage(ids);
    if (conflictMsg) { status = conflictMsg; render(); return; }
    const groupId = state.multislipId || DEFAULT_MULTISLIP_ID;
    state.multislipId = incrementMultislipId(groupId);
    stopChainMode();
    stopAutoDetectSlipsMode();
    status = `Committing ${totalCount} slip${totalCount > 1 ? "s" : ""}...`;
    render();
    const createdIds = [];
    try {
      for (let i = 0; i < rings.length; i++) {
        const ring = rings[i];
        const wkt = buildPolygonWkt(ring.map((c) => [c.lat, c.lng]));
        const slipAlongFt = roundToTenthFoot(msDockvec(points[i], points[i + 1]).lenM * 3.280839895);
        const measurementId = makeId("MEAS");
        createdIds.push(measurementId);
        await requestJson(API_BASE, {
          method: "POST",
          body: {
            measurement_id: measurementId,
            marina_id: state.marinaId,
            marina_name: state.marinaName,
            slip_id: ids[i],
            dock_name: state.dockName || null,
            suffix: state.slipIdSuffix || null,
            region: state.region || null,
            space_type: state.spaceType || null,
            multislip_yn: "yes",
            multislip_id: groupId,
            covered_yn: draftCovered ? "yes" : "no",
            boatlift_yn: draftBoatlift ? "yes" : "no",
            fueldock_yn: draftFuelDock ? "yes" : "no",
            ...draftOutOfServiceFields(),
            measured_linear_feet: slipAlongFt,
            measured_width_feet: Math.abs(widthFt),
            measured_depth_feet: null,
            operational_status: "operable",
            notes: draftComments || "",
            measurement_date: state.measurementDate,
            confidence_score: state.confidenceScore,
            record_created_timestamp: new Date().toISOString(),
            created_by_email: app.auth?.email || null,
            geometry_wkt: wkt,
          },
        });
        incrementSlipIdNumber(state);
        incrementSlipIdSuffix(state);
      }
      await refreshRecords({ quiet: true });
      pushUndoEntry(`Created ${totalCount} slip${totalCount === 1 ? "" : "s"} (Chain)`,
        createdIds.map((measurementId) => ({ type: "create", measurementId })));
      app.log("slip.created", { count: totalCount, tool: "chain", marina: state.marinaId });
      status = `Committed ${totalCount} slip${totalCount > 1 ? "s" : ""} in one chain.`;
    } catch (err) {
      status = `Chain commit failed: ${err?.message || "Unknown error"}`;
    }
    render();
  }

  function startChainMode() {
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopMultislipMode();
    stopCombMode();
    stopFullDockMode();
    stopAutoRun();
    chainActive = true;
    chainPhase = "points";
    chainPoints = [];
    chainHoverPoint = null;
    chainWidthFt = 0;
    status = "Chain: click each point along the dock edge, Backspace=undo, Enter=set width (min 3 points).";
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.classList.add("slip-free-draw-cursor"); } catch {}

    let ptrDown = null, ptrMoved = false, rafPending = false;
    const THRESH = 6;

    const onPtrDown = (e) => { if (e.button != null && e.button !== 0) return; ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; };
    const onPtrMove = (e) => {
      const ll = map?.mouseEventToLatLng?.(e);
      if (ll) chainHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; renderChainPreview(); }); }
      if (ptrDown) { const dx = Math.abs(e.clientX - ptrDown.x), dy = Math.abs(e.clientY - ptrDown.y); if (dx > THRESH || dy > THRESH) ptrMoved = true; }
    };
    const onPtrUp = (e) => {
      if (!ptrDown) return;
      const ll = map?.mouseEventToLatLng?.(e);
      const clicked = !ptrMoved && ll;
      ptrDown = null; ptrMoved = false;
      if (!clicked) return;
      const pt = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (chainPhase === "points") {
        chainPoints.push(pt);
        const slipCount = Math.max(0, chainPoints.length - 1);
        status = `Chain: ${chainPoints.length} point${chainPoints.length > 1 ? "s" : ""} (${slipCount} slip${slipCount === 1 ? "" : "s"}). Click next point, Backspace=undo${chainPoints.length >= 3 ? ", Enter=set width" : ""}.`;
      } else if (chainPhase === "width") {
        const rawFt = msPerpDistFt(chainPoints[0], chainPoints[1], pt);
        const snapFt = Math.round(rawFt / 0.1) * 0.1;
        const clickedFt = snapFt !== 0 ? snapFt : 0.1;
        const cappedFt = clampChainWidthFt(chainPoints, clickedFt);
        const wasCapped = Math.abs(cappedFt) < Math.abs(clickedFt) - 1e-6;
        chainWidthFt = cappedFt;
        chainPhase = "review";
        const { flaggedVertices } = mitreChainRing(chainPoints, chainWidthFt);
        const slipCount = chainPoints.length - 1;
        status = `Chain: ${slipCount} slip${slipCount === 1 ? "" : "s"} at ${Math.abs(chainWidthFt)} ft${wasCapped ? " (capped — wider would overlap at a corner)" : ""}.` +
          (flaggedVertices.length ? ` ${flaggedVertices.length} corner${flaggedVertices.length > 1 ? "s" : ""} flagged — review before committing.` : "") +
          " Enter=commit, Backspace=change width, Esc=cancel.";
      }
      renderChainPreview();
      render();
    };
    const onKey = (e) => {
      if (e.key === "Escape") { stopChainMode(); status = "Chain placement cancelled."; render(); return; }
      if (chainPhase === "points") {
        if (e.key === "Backspace") {
          e.preventDefault();
          if (chainPoints.length) chainPoints.pop();
          const slipCount = Math.max(0, chainPoints.length - 1);
          status = chainPoints.length
            ? `Chain: ${chainPoints.length} point${chainPoints.length > 1 ? "s" : ""} (${slipCount} slip${slipCount === 1 ? "" : "s"}). Click next point, Backspace=undo${chainPoints.length >= 3 ? ", Enter=set width" : ""}.`
            : "Chain: click each point along the dock edge, Backspace=undo, Enter=set width (min 3 points).";
          renderChainPreview(); render();
        } else if (e.key === "Enter" && chainPoints.length >= 3) {
          e.preventDefault();
          chainPhase = "width";
          status = "Chain: move offshore and click to set width for all slips.";
          renderChainPreview(); render();
        }
      } else if (chainPhase === "width") {
        if (e.key === "Backspace") {
          e.preventDefault();
          chainPhase = "points";
          status = `Chain: ${chainPoints.length} points. Click next point, Backspace=undo, Enter=set width.`;
          renderChainPreview(); render();
        }
      } else if (chainPhase === "review") {
        if (e.key === "Backspace") {
          e.preventDefault();
          chainPhase = "width";
          chainWidthFt = 0;
          status = "Chain: move offshore and click to set width for all slips.";
          renderChainPreview(); render();
        } else if (e.key === "Enter") {
          e.preventDefault();
          commitChain().catch(console.error);
        }
      }
    };

    mapContainer?.addEventListener("pointerdown", onPtrDown, true);
    mapContainer?.addEventListener("pointermove", onPtrMove, true);
    mapContainer?.addEventListener("pointerup", onPtrUp, true);
    mapContainer?.addEventListener("pointercancel", onPtrUp, true);
    document.addEventListener("keydown", onKey);
    chainOff = () => {
      mapContainer?.removeEventListener("pointerdown", onPtrDown, true);
      mapContainer?.removeEventListener("pointermove", onPtrMove, true);
      mapContainer?.removeEventListener("pointerup", onPtrUp, true);
      mapContainer?.removeEventListener("pointercancel", onPtrUp, true);
      document.removeEventListener("keydown", onKey);
      try { if (mapContainer) mapContainer.classList.remove("slip-free-draw-cursor"); } catch {}
    };
    render();
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-Detect: click two points for one cross-section line through a run of fingers,
  // and instead of clicking each bay edge by hand (Comb's own "bays" phase), the fingers
  // are found automatically from the marina's surface raster's elevation profile along
  // that line, then handed straight into Comb's existing "length"/"adjust" phases for
  // human review -- no new commit path, just an automated way to fill in Comb's own
  // first phase.

  function pointAtDistFt(points, distFt) {
    if (!points.length) return null;
    if (distFt <= points[0].distFt) return { lat: points[0].lat, lng: points[0].lng };
    const last = points[points.length - 1];
    if (distFt >= last.distFt) return { lat: last.lat, lng: last.lng };
    for (let i = 0; i < points.length - 1; i += 1) {
      const a = points[i], b = points[i + 1];
      if (distFt >= a.distFt && distFt <= b.distFt) {
        const span = b.distFt - a.distFt;
        const t = span > 0 ? (distFt - a.distFt) / span : 0;
        return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t };
      }
    }
    return { lat: last.lat, lng: last.lng };
  }

  // Given the per-point elevation profile from /api/surfaces/sample-line, finds the dock
  // decking elevation from the profile's OWN data (the modal 0.1ft-binned elevation --
  // validated against 5 real adjacent slips at VIM: this matches the same reading
  // /api/surfaces/dockline-stats' "majority" statistic gives for a hand-drawn sample on one of
  // those same fingers), then finds runs of consecutive samples within that band. Runs
  // separated by a short out-of-band gap get merged first -- a cleat, a shadow, a boat's rail
  // grazing the line -- since those are data dropouts within one physical finger, not real
  // breaks in it. The water gaps between the surviving fingers are the candidate slips.
  function detectSlipGapsFromProfile(points, { toleranceFt = 0.5, mergeGapFt = 3, minFingerFt = 2 } = {}) {
    const valid = (points || []).filter((p) => p.elevation != null);
    if (valid.length < 4) return { fingers: [], gaps: [], modeElevation: null };

    const counts = new Map();
    for (const p of valid) {
      const bin = Math.round(p.elevation * 10) / 10;
      counts.set(bin, (counts.get(bin) || 0) + 1);
    }
    let modeBin = null, modeCount = -1;
    for (const [bin, count] of counts) {
      if (count > modeCount) { modeBin = bin; modeCount = count; }
    }
    const bandLo = modeBin - toleranceFt;
    const bandHi = modeBin + toleranceFt;
    const inBand = points.map((p) => p.elevation != null && p.elevation >= bandLo && p.elevation <= bandHi);

    // Interpolate the true elevation-crossing point between two samples, rather than
    // snapping to whichever raw ~1ft-spaced sample happened to land inside the band --
    // that snapping was making real finger widths jitter by a foot or more, and a
    // narrow finger could shrink enough to fall under minFingerFt entirely.
    const crossingDistFt = (fromIdx, toIdx, threshold) => {
      const from = points[fromIdx], to = points[toIdx];
      if (!from || !to || from.elevation == null || to.elevation == null) return null;
      const span = to.elevation - from.elevation;
      if (span === 0) return null;
      const t = (threshold - from.elevation) / span;
      if (!(t >= 0 && t <= 1)) return null;
      return from.distFt + t * (to.distFt - from.distFt);
    };
    const refineEdge = (idx, dir) => {
      // dir=-1 refines the entering edge (looks one sample back), dir=+1 the exiting edge.
      const neighborIdx = idx + dir;
      const neighbor = points[neighborIdx];
      const self = points[idx];
      if (!neighbor || neighbor.elevation == null || !self) return self?.distFt ?? null;
      const threshold = neighbor.elevation < bandLo ? bandLo : neighbor.elevation > bandHi ? bandHi : null;
      if (threshold == null) return self.distFt;
      const fromIdx = dir < 0 ? neighborIdx : idx;
      const toIdx = dir < 0 ? idx : neighborIdx;
      const crossed = crossingDistFt(fromIdx, toIdx, threshold);
      return crossed ?? self.distFt;
    };

    const rawRuns = [];
    let i = 0;
    while (i < points.length) {
      if (inBand[i]) {
        let j = i;
        while (j < points.length && inBand[j]) j += 1;
        const startDistFt = refineEdge(i, -1);
        const endDistFt = refineEdge(j - 1, 1);
        rawRuns.push([startDistFt, endDistFt]);
        i = j;
      } else {
        i += 1;
      }
    }

    const EPS = 1e-6; // guards against float noise on distFt values already rounded to 2dp upstream
    const merged = [];
    for (const run of rawRuns) {
      if (merged.length && (run[0] - merged[merged.length - 1][1]) <= mergeGapFt + EPS) {
        merged[merged.length - 1][1] = run[1];
      } else {
        merged.push([...run]);
      }
    }

    const fingers = merged.filter((run) => (run[1] - run[0]) >= minFingerFt - EPS);
    const gaps = [];
    for (let k = 0; k < fingers.length - 1; k += 1) gaps.push([fingers[k][1], fingers[k + 1][0]]);
    return { fingers, gaps, modeElevation: modeBin };
  }

  function stopAutoDetectSlipsMode() {
    if (!autoDetectActive && !autoDetectOff) return;
    autoDetectActive = false;
    autoDetectP1 = null;
    if (autoDetectOff) { try { autoDetectOff(); } catch {} autoDetectOff = null; }
    autoDetectPreviewLayer.clearLayers();
    try { map.getContainer().style.cursor = ""; } catch {}
  }

  function renderAutoDetectPreview() {
    autoDetectPreviewLayer.clearLayers();
    if (!autoDetectActive || !autoDetectP1 || !autoDetectHoverPoint) return;
    L.polyline([[autoDetectP1.lat, autoDetectP1.lng], [autoDetectHoverPoint.lat, autoDetectHoverPoint.lng]], {
      color: "#38bdf8", weight: 2, dashArray: "6 4", interactive: false,
    }).addTo(autoDetectPreviewLayer);
  }

  async function runAutoDetectSlips(surface, p1, p2) {
    autoDetectBusy = true;
    status = "Auto-Detect: sampling surface elevation along the line…";
    render();
    try {
      const midLatRad = ((p1.lat + p2.lat) / 2) * Math.PI / 180;
      const dx = (p2.lng - p1.lng) * Math.cos(midLatRad) * 111320;
      const dy = (p2.lat - p1.lat) * 111320;
      const lengthFt = Math.hypot(dx, dy) * 3.280839895;
      const samples = Math.max(20, Math.min(1500, Math.round(lengthFt)));
      // A single Qimera COG samples straight off its file; the mosaic has no one file, so its
      // route (mirroring dockline-stats' mosaic variant) collects just the tiles the line
      // crosses into a scratch VRT server-side and samples that instead.
      const url = surface.kind === "mosaic2d"
        ? `/api/point-cloud/mosaic2d/${encodeURIComponent(surface.marinaCode || "")}/sample-line`
        : "/api/surfaces/sample-line";
      const body = surface.kind === "mosaic2d"
        ? { attribute: surface.attr || "elevation", p1, p2, samples }
        : { rasterPath: surface.rasterPath, p1, p2, samples };
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || `HTTP ${resp.status}`);
      const { fingers, gaps, modeElevation } = detectSlipGapsFromProfile(data.points);
      console.log("[Auto-Detect] " + JSON.stringify({ lengthFt: +lengthFt.toFixed(1), modeElevation, fingers, gaps }));
      console.log("[Auto-Detect] profile=" + JSON.stringify(data.points.map(p => [p.distFt, p.elevation])));
      if (!gaps.length) {
        status = fingers.length
          ? `Auto-Detect: found ${fingers.length} finger${fingers.length === 1 ? "" : "s"} but no full slip between two of them along this line — try a longer line, or use Comb mode to click bays by hand.`
          : "Auto-Detect: couldn't find a consistent dock elevation along that line — try crossing more fingers, or a spot with less boat clutter.";
        render();
        return;
      }
      startCombMode();
      combPhase = "length";
      combSlotsPerBay = 1;
      combBays = gaps.map(([d0, d1]) => ({ p1: pointAtDistFt(data.points, d0), p2: pointAtDistFt(data.points, d1) }));
      status = `Auto-Detect: found ${gaps.length} slip${gaps.length === 1 ? "" : "s"} (dock elevation ~${modeElevation.toFixed(1)}ft). Click offshore to set a length for all of them, then review/adjust each one.`;
      renderCombPreview();
      render();
    } catch (err) {
      status = `Auto-Detect failed: ${err?.message || err}`;
      render();
    } finally {
      autoDetectBusy = false;
    }
  }

  function startAutoDetectSlipsMode(surface) {
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopGeometryPlaceMode();
    stopMultislipMode();
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    autoDetectActive = true;
    autoDetectP1 = null;
    status = "Auto-Detect: click the first point of a line crossing every finger in the run.";
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.classList.add("slip-free-draw-cursor"); } catch {}

    let ptrDown = null, ptrMoved = false, rafPending = false;
    const THRESH = 6;

    const onPtrDown = (e) => { if (e.button != null && e.button !== 0) return; ptrDown = { x: e.clientX, y: e.clientY }; ptrMoved = false; };
    const onPtrMove = (e) => {
      const ll = map?.mouseEventToLatLng?.(e);
      if (ll) autoDetectHoverPoint = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!rafPending) { rafPending = true; requestAnimationFrame(() => { rafPending = false; renderAutoDetectPreview(); }); }
      if (ptrDown) { const dx = Math.abs(e.clientX - ptrDown.x), dy = Math.abs(e.clientY - ptrDown.y); if (dx > THRESH || dy > THRESH) ptrMoved = true; }
    };
    const onPtrUp = (e) => {
      if (!ptrDown) return;
      const ll = map?.mouseEventToLatLng?.(e);
      const clicked = !ptrMoved && ll;
      ptrDown = null; ptrMoved = false;
      if (!clicked) return;
      const pt = { lat: Number(ll.lat), lng: Number(ll.lng) };
      if (!autoDetectP1) {
        autoDetectP1 = pt;
        status = "Auto-Detect: click the second point, on the far side of the run.";
        render();
        return;
      }
      const p1 = autoDetectP1;
      stopAutoDetectSlipsMode();
      runAutoDetectSlips(surface, p1, pt).catch((err) => {
        status = `Auto-Detect failed: ${err?.message || err}`;
        render();
      });
    };
    const onKey = (e) => {
      if (e.key === "Escape") { stopAutoDetectSlipsMode(); status = "Auto-Detect cancelled."; render(); return; }
    };

    mapContainer?.addEventListener("pointerdown", onPtrDown, true);
    mapContainer?.addEventListener("pointermove", onPtrMove, true);
    mapContainer?.addEventListener("pointerup", onPtrUp, true);
    mapContainer?.addEventListener("pointercancel", onPtrUp, true);
    document.addEventListener("keydown", onKey);
    autoDetectOff = () => {
      mapContainer?.removeEventListener("pointerdown", onPtrDown, true);
      mapContainer?.removeEventListener("pointermove", onPtrMove, true);
      mapContainer?.removeEventListener("pointerup", onPtrUp, true);
      mapContainer?.removeEventListener("pointercancel", onPtrUp, true);
      document.removeEventListener("keydown", onKey);
      try { if (mapContainer) mapContainer.classList.remove("slip-free-draw-cursor"); } catch {}
    };
    render();
  }

  // ─────────────────────────────────────────────────────────────────────────────

  function startGeometryPlaceMode({ template = null } = {}) {
    if (!state.slipGeometryDraftTypeId) return;
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopLineLayout("replaced");
    stopGeometryRunLayout("replaced");
    stopMultislipMode();
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    disarmPostPlaceEscapeUndo();
    activeGeometryPlaceMode = true;
    const selected = selectedGeometryPolygon();
    const type = getSlipTypeById(state.slipGeometryDraftTypeId);
    geometryPlaceDraft = template && typeof template === "object"
      ? {
        lengthFt: Number(template.lengthFt || 40),
        widthFt: Number(template.widthFt || 14),
        headingDeg: Number(template.headingDeg || 0),
        ringLatLngs: Array.isArray(template.ringLatLngs) ? template.ringLatLngs : null,
        sourceCenter: template.sourceCenter || null,
      }
      : selected
      ? {
        lengthFt: Number(selected.lengthFt || 40),
        widthFt: Number(selected.widthFt || 14),
        headingDeg: Number(selected.headingDeg || 0),
      }
      : {
        lengthFt: Number(type?.lengthFt || 40),
        widthFt: Number(type?.widthFt || 14),
        headingDeg: 0,
      };
    // A template/paste carries an exact ring to stamp on one click (Ctrl+V) --
    // everything else goes through the click-drawn width/length/rotate flow.
    const ringBacked = Array.isArray(geometryPlaceDraft?.ringLatLngs) && geometryPlaceDraft.ringLatLngs.length >= 3;
    geometryDrawStage = ringBacked ? null : "width";
    geometryDrawA = null;
    geometryDrawB = null;
    geometryDrawSignedLengthFt = 0;
    // A plain inline style.cursor is beaten by Leaflet's own .leaflet-interactive
    // { cursor: pointer } rule on every existing slip polygon layer, so hovering
    // over any already-drawn shape showed the pointer/hand instead of crosshair.
    // Multislip/Comb avoid this with a CSS class carrying !important and an
    // explicit .leaflet-interactive override (dashboard.css) -- match that
    // exact mechanism instead of reinventing it here.
    const mapContainer = map?.getContainer?.() || null;
    // Ring-backed (paste) placements skip the width/length draw stages entirely, so the
    // length-vs-width ambiguity the colored crosshair communicates doesn't apply there --
    // plain crosshair for that path, colored for the draw-from-scratch path.
    try {
      if (ringBacked) mapContainer?.classList?.add?.("slip-free-draw-cursor");
      else applyFirstDimensionCursor(mapContainer);
    } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    // "t" during the second-dimension stage: freeform-shear the locked-90deg second edge
    // into a parallelogram instead. Per-placement only -- always starts off, unlike
    // firstDimensionIsLength which persists across placements.
    let parallelogramMode = false;
    // "r" during the second-dimension stage: instead of locking the edge perpendicular to
    // A-B, let the whole rectangle pivot around A (the very first click) while its second
    // dimension is measured, so angle and size are decided together in one motion. Mutually
    // exclusive with parallelogramMode -- both reinterpret the same second click differently.
    let freeRotateMode = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    let placePreviewRafPending = false;
    const onPointerMove = (event) => {
      const latlng = map?.mouseEventToLatLng?.(event);
      if (latlng) {
        if (geometryDrawStage === "length" && geometryDrawA && geometryDrawB) {
          geometryDrawSignedLengthFt = msPerpDistFt(geometryDrawA, geometryDrawB, latlng);
          if (parallelogramMode) {
            const point = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
            geometryPlaceDraft.ringLatLngs = buildParallelogramRing(geometryDrawA, geometryDrawB, point);
            geometryPlaceDraft.sourceCenter = { lat: point.lat, lng: point.lng };
            geometryPlaceHoverPoint = point;
          } else if (freeRotateMode) {
            const measuredFt = Math.max(0.1, Math.abs(geometryDrawSignedLengthFt));
            if (firstDimensionIsLength) geometryPlaceDraft.widthFt = measuredFt;
            else geometryPlaceDraft.lengthFt = measuredFt;
            // Recompute the square-to-A-B heading fresh from A/B every move rather than
            // reading it back off geometryPlaceDraft.headingDeg, which this branch itself
            // overwrites below -- using it as an input too would compound drift frame over
            // frame (same reasoning as the old mandatory rotate stage this restores).
            const squareHeadingDeg = bearingDegrees(geometryDrawA, geometryDrawB) - (firstDimensionIsLength ? 0 : 90);
            const squareOffsetHeadingDeg = squareHeadingDeg - (firstDimensionIsLength ? 90 : 0);
            const baseCenter = computeDrawnRectCenter({
              A: geometryDrawA, B: geometryDrawB, lengthFt: geometryDrawSignedLengthFt, headingDeg: squareOffsetHeadingDeg,
            });
            geometryPlaceHoverPoint = msRotateAroundPivot(baseCenter, geometryDrawA, geometryDrawB, latlng);
            geometryPlaceDraft.headingDeg = squareHeadingDeg + (bearingDegrees(geometryDrawA, latlng) - bearingDegrees(geometryDrawA, geometryDrawB));
          } else {
            const measuredFt = Math.max(0.1, Math.abs(geometryDrawSignedLengthFt));
            if (firstDimensionIsLength) geometryPlaceDraft.widthFt = measuredFt;
            else geometryPlaceDraft.lengthFt = measuredFt;
            // The offset that locates the rectangle's center must run perpendicular to the
            // A-B edge just drawn, on the same side msPerpDistFt calls positive (so the
            // near edge stays pinned to A-B and the far edge follows the cursor). When A-B
            // was the width edge (default), that perpendicular is headingDeg (the length
            // axis) itself. When A-B was the length edge (firstDimensionIsLength), headingDeg
            // IS A-B's own direction, so the matching perpendicular is headingDeg-90 --
            // headingDeg+90 points the opposite way and grows the box away from the cursor.
            const offsetHeadingDeg = Number(geometryPlaceDraft.headingDeg || 0) - (firstDimensionIsLength ? 90 : 0);
            geometryPlaceHoverPoint = computeDrawnRectCenter({
              A: geometryDrawA, B: geometryDrawB, lengthFt: geometryDrawSignedLengthFt, headingDeg: offsetHeadingDeg,
            });
          }
        } else if (geometryDrawStage !== "review") {
          // "review" keeps geometryPlaceHoverPoint frozen at the locked-in center -- only
          // the drag handle (a separate gesture, tracked by Leaflet's own marker drag) is
          // allowed to move it at this stage.
          geometryPlaceHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
        }
        if (!placePreviewRafPending) {
          placePreviewRafPending = true;
          requestAnimationFrame(() => { placePreviewRafPending = false; renderGeometryPlacePreview(); });
        }
      }
      if (!pointerDown) return;
      const dx = Math.abs(Number(event.clientX) - pointerDown.x);
      const dy = Math.abs(Number(event.clientY) - pointerDown.y);
      if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
    };
    const finishPlacement = (finalLatlng) => {
      const created = placeGeometryAt(finalLatlng, state.slipGeometryDraftTypeId, geometryPlaceDraft);
      if (!created) return null;
      suppressMapClicks(800);
      stopGeometryPlaceMode();
      armPostPlaceEscapeUndo(created.id);
      status = `Placed polygon ${created.id}. Press S to start run layout from this slip, Esc to undo.`;
      render();
      return created;
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldAdvance = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldAdvance || !geometryPlaceDraft) return;
      if (event) event.__htHandled = "slip-inventory";
      const point = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
      if (geometryDrawStage === "width") {
        if (!geometryDrawA) {
          geometryDrawA = point;
          status = firstDimensionIsLength ? "Click to set the length edge." : "Click to set the width edge.";
          render();
          return;
        }
        geometryDrawB = point;
        // geometryDrawA/B are plain {lat,lng} (from map.mouseEventToLatLng, spread
        // into a fresh object), not Leaflet LatLng instances -- toFeetDistance()
        // needs a real .distanceTo() method, which a plain object doesn't have and
        // silently returns 0 for. map.distance() accepts plain lat/lng-like objects.
        const measuredFt = Math.max(0.1, metersToFeet(map.distance(geometryDrawA, geometryDrawB)));
        if (firstDimensionIsLength) {
          geometryPlaceDraft.lengthFt = measuredFt;
          geometryPlaceDraft.headingDeg = bearingDegrees(geometryDrawA, geometryDrawB);
        } else {
          geometryPlaceDraft.widthFt = measuredFt;
          geometryPlaceDraft.headingDeg = bearingDegrees(geometryDrawA, geometryDrawB) - 90;
        }
        geometryDrawStage = "length";
        try { clearFirstDimensionCursor(mapContainer); mapContainer?.classList?.add?.("slip-free-draw-cursor"); } catch {}
        status = firstDimensionIsLength ? "Move mouse to set the width, then click." : "Move mouse to set the length, then click.";
        render();
        return;
      }
      if (geometryDrawStage === "length") {
        if (parallelogramMode) {
          const freeDistFt = metersToFeet(map.distance(geometryDrawB, point));
          if (freeDistFt < 0.1) return; // degenerate -- stay in mode, ignore the click
          geometryPlaceDraft.ringLatLngs = buildParallelogramRing(geometryDrawA, geometryDrawB, point);
          geometryPlaceDraft.sourceCenter = { lat: point.lat, lng: point.lng };
          finishPlacement(point);
          return;
        }
        if (freeRotateMode) {
          const signedLenFt = msPerpDistFt(geometryDrawA, geometryDrawB, point);
          if (Math.abs(signedLenFt) < 0.1) return; // degenerate -- stay in mode, ignore the click
          geometryDrawSignedLengthFt = signedLenFt;
          const measuredFt = Math.max(0.1, Math.abs(signedLenFt));
          if (firstDimensionIsLength) geometryPlaceDraft.widthFt = measuredFt;
          else geometryPlaceDraft.lengthFt = measuredFt;
          const squareHeadingDeg = bearingDegrees(geometryDrawA, geometryDrawB) - (firstDimensionIsLength ? 0 : 90);
          geometryPlaceDraft.headingDeg = squareHeadingDeg + (bearingDegrees(geometryDrawA, point) - bearingDegrees(geometryDrawA, geometryDrawB));
          const squareOffsetHeadingDeg = squareHeadingDeg - (firstDimensionIsLength ? 90 : 0);
          const baseCenter = computeDrawnRectCenter({
            A: geometryDrawA, B: geometryDrawB, lengthFt: signedLenFt, headingDeg: squareOffsetHeadingDeg,
          });
          geometryPlaceHoverPoint = msRotateAroundPivot(baseCenter, geometryDrawA, geometryDrawB, point);
          geometryDrawStage = "review";
          status = "Drag the handle to adjust. Enter to place, S to place + start run, Esc to cancel.";
          render();
          return;
        }
        // Recompute fresh from this click rather than trusting the last
        // pointermove -- touch input can deliver a pointerup with no
        // immediately-preceding move at the same coordinate.
        const signedLenFt = msPerpDistFt(geometryDrawA, geometryDrawB, point);
        if (Math.abs(signedLenFt) < 0.1) return; // degenerate click on the first edge's line -- ignore, stay in "length"
        geometryDrawSignedLengthFt = signedLenFt;
        const measuredFt = Math.max(0.1, Math.abs(signedLenFt));
        if (firstDimensionIsLength) geometryPlaceDraft.widthFt = measuredFt;
        else geometryPlaceDraft.lengthFt = measuredFt;
        // Rotation used to be a mandatory third click here. It's now optional: square to
        // the width/length edges just drawn (headingDeg was already set from A/B at the
        // width step above), let the user press "r" afterward to enter an on-demand rotate
        // mode (startRotateAdjustMode) if they want to angle it off that default. This click
        // no longer commits outright, though -- it locks the dimension and drops into a
        // review stage (drag handle to fine-tune, Enter to place, Esc to cancel), matching
        // Multislip/Comb/Chain's review step after their own last dimension.
        const baseHeadingDeg = Number(geometryPlaceDraft.headingDeg || 0);
        // See the matching comment in onPointerMove -- must be -90, not +90, or a
        // length-first placement grows the box on the opposite side from the cursor.
        const offsetHeadingDeg = baseHeadingDeg - (firstDimensionIsLength ? 90 : 0);
        geometryPlaceHoverPoint = computeDrawnRectCenter({
          A: geometryDrawA, B: geometryDrawB, lengthFt: geometryDrawSignedLengthFt, headingDeg: offsetHeadingDeg,
        });
        geometryDrawStage = "review";
        status = "Drag the handle to adjust. Enter to place, S to place + start run, Esc to cancel.";
        render();
        return;
      }
      if (geometryDrawStage === "review") {
        // Matches Multislip/Comb/Chain's own review stage: a plain click does nothing here,
        // only Enter (place) / Esc (cancel) / dragging the handle apply.
        return;
      }
      // geometryDrawStage === null: legacy single-click path, stamps an exact
      // ring copy (Ctrl+V paste) -- unchanged from before this redesign.
      finishPlacement(latlng);
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        stopGeometryPlaceMode();
        status = "Placement cancelled.";
        render();
        return;
      }
      if (event.key === "Enter" && geometryDrawStage === "review" && geometryPlaceHoverPoint && !shouldIgnoreGeometryHotkeys(event)) {
        event.preventDefault();
        finishPlacement(geometryPlaceHoverPoint);
        return;
      }
      // "S" during review both places (like Enter) and starts run layout in one press --
      // preserves the old one-shot "click, click, click, S" muscle memory from before this
      // shape gained a review stage, instead of forcing Enter-then-S as two separate steps.
      if ((event.key === "s" || event.key === "S") && geometryDrawStage === "review" && geometryPlaceHoverPoint && !shouldIgnoreGeometryHotkeys(event)) {
        event.preventDefault();
        const created = finishPlacement(geometryPlaceHoverPoint);
        if (created) startGeometryRunLayout();
        return;
      }
      if ((event.key === " " || event.code === "Space") && geometryDrawStage === "width" && !shouldIgnoreGeometryHotkeys(event)) {
        event.preventDefault();
        firstDimensionIsLength = !firstDimensionIsLength;
        applyFirstDimensionCursor(mapContainer);
        status = geometryDrawA
          ? (firstDimensionIsLength ? "Click to set the length edge." : "Click to set the width edge.")
          : (firstDimensionIsLength
            ? "Click to set the first corner (length start). Space: toggle Length/Width first."
            : "Click to set the first corner (width start). Space: toggle Length/Width first.");
        render();
        return;
      }
      if ((event.key === "t" || event.key === "T") && geometryDrawStage === "length" && geometryDrawA && geometryDrawB && !shouldIgnoreGeometryHotkeys(event)) {
        parallelogramMode = !parallelogramMode;
        if (parallelogramMode) freeRotateMode = false; // mutually exclusive -- both reinterpret this click differently
        if (parallelogramMode && geometryPlaceHoverPoint) {
          const point = { lat: Number(geometryPlaceHoverPoint.lat), lng: Number(geometryPlaceHoverPoint.lng) };
          geometryPlaceDraft.ringLatLngs = buildParallelogramRing(geometryDrawA, geometryDrawB, point);
          geometryPlaceDraft.sourceCenter = { lat: point.lat, lng: point.lng };
        } else if (!parallelogramMode) {
          delete geometryPlaceDraft.ringLatLngs;
          delete geometryPlaceDraft.sourceCenter;
        }
        status = parallelogramMode
          ? "Freeform: move mouse to shear into a parallelogram, click to place. \"t\" to return to a rectangle."
          : (firstDimensionIsLength ? "Move mouse to set the width, then click." : "Move mouse to set the length, then click.");
        if (geometryPlaceHoverPoint) renderGeometryPlacePreview();
        render();
        return;
      }
      // "r" during the second-dimension stage: pivot the whole rectangle around A (click 1)
      // while its second dimension is still being decided, instead of locking it square to
      // A-B. Restores the old free-rotate-while-sizing feel the width/length/review redesign
      // dropped in favor of a strictly-square draw + separate on-demand rotate afterward.
      if ((event.key === "r" || event.key === "R") && geometryDrawStage === "length" && geometryDrawA && geometryDrawB && !shouldIgnoreGeometryHotkeys(event)) {
        freeRotateMode = !freeRotateMode;
        if (freeRotateMode) {
          parallelogramMode = false; // mutually exclusive, same as above
          delete geometryPlaceDraft.ringLatLngs;
          delete geometryPlaceDraft.sourceCenter;
        }
        status = freeRotateMode
          ? "Move mouse to rotate about the first point and set the second dimension together, click to lock. \"r\" to return to square."
          : (firstDimensionIsLength ? "Move mouse to set the width, then click." : "Move mouse to set the length, then click.");
        if (geometryPlaceHoverPoint) renderGeometryPlacePreview();
        render();
        return;
      }
    };
    geometryPlaceOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    document.addEventListener("keydown", onKey);
    status = geometryDrawStage === "width"
      ? (firstDimensionIsLength
        ? "Click to set the first corner (length start). Space: toggle Length/Width first."
        : "Click to set the first corner (width start). Space: toggle Length/Width first.")
      : "Move mouse to position draft slip, shape with hotkeys, left-click to place, then press S to start run.";
    render();
  }

  function startLineLayout(mode = "endpoint") {
    if (lineLayoutActive) return;
    stopGeometryPlaceMode();
    stopGeometryRunLayout("replaced");
    stopPointDrop("replaced");
    stopRotateAdjustMode();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopMultislipMode();
    stopCombMode();
    stopChainMode();
    stopAutoDetectSlipsMode();
    stopFullDockMode();
    stopAutoRun();
    lineLayoutActive = true;
    lineLayoutMode = mode === "spacing" ? "spacing" : "endpoint";
    lineEndpoints = [];
    lineHoverPoint = null;
    lineAnchorPoint = null;
    imageryEstimateStatus = "";
    imageryEstimateDebug = null;
    const mapContainer = map?.getContainer?.() || null;
    try { if (mapContainer) mapContainer.style.cursor = "crosshair"; } catch {}
    let pointerDown = null;
    let pointerMoved = false;
    const moveThreshold = 6;
    const onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      pointerDown = { x: Number(event.clientX), y: Number(event.clientY) };
      pointerMoved = false;
    };
    const onPointerMove = (event) => {
      if (lineLayoutMode === "spacing" && lineEndpoints.length >= 2 && lineEndpoints.length < 3) {
        const latlng = map?.mouseEventToLatLng?.(event);
        if (latlng) {
          lineHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          renderPreview();
        }
      }
      if (pointerDown) {
        const dx = Math.abs(Number(event.clientX) - pointerDown.x);
        const dy = Math.abs(Number(event.clientY) - pointerDown.y);
        if (dx > moveThreshold || dy > moveThreshold) pointerMoved = true;
      }
    };
    const onWheel = (event) => {
      if (lineLayoutMode === "spacing") return;
      if (!event.ctrlKey || lineEndpoints.length < 2) return;
      if (!mapContainer?.contains?.(event.target)) return;
      event.preventDefault?.();
      event.stopPropagation?.();
      event.stopImmediatePropagation?.();
      const direction = Number(event.deltaY) > 0 ? -1 : 1;
      state.lineSlipCount = Math.max(1, Number(state.lineSlipCount || 1) + direction);
      persist();
      renderPreview();
      render();
    };
    const onPointerUp = (event) => {
      if (!pointerDown) return;
      const latlng = map?.mouseEventToLatLng?.(event);
      const shouldCapture = !pointerMoved && latlng;
      pointerDown = null;
      pointerMoved = false;
      if (!shouldCapture) return;
      if (lineLayoutMode === "spacing") {
        if (lineEndpoints.length === 0) {
          lineEndpoints = [{ lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          status = "First slip center captured. Click the next slip center to set spacing.";
        } else if (lineEndpoints.length === 1) {
          lineEndpoints = [lineEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineHoverPoint = { lat: Number(latlng.lat), lng: Number(latlng.lng) };
          status = "Spacing captured. Move along the pier to preview labels, then click the end of the run.";
        } else {
          lineEndpoints = [lineEndpoints[0], lineEndpoints[1], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineHoverPoint = null;
          state.lineSlipCount = linePreviewPoints().length;
          persist();
          status = `Spacing run ready for ${state.lineSlipCount} slips. Press Enter or Generate Line.`;
        }
      } else {
        if (lineEndpoints.length === 0) {
          lineEndpoints = [{ lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineAnchorPoint = null;
          status = "Line start captured. Click the end point.";
        } else {
          lineEndpoints = [lineEndpoints[0], { lat: Number(latlng.lat), lng: Number(latlng.lng) }];
          lineAnchorPoint = null;
          status = `Line preview ready for ${state.lineSlipCount} slips. Adjust the count, optionally use Auto Estimate From Imagery, or generate.`;
        }
      }
      renderPreview();
      render();
    };
    const onKey = (event) => {
      const canGenerate = lineLayoutMode === "spacing" ? lineEndpoints.length >= 3 : lineEndpoints.length >= 2;
      if (event.key === "Enter" && canGenerate) {
        event.preventDefault?.();
        const points = linePreviewPoints();
        if (points.length >= 2) {
          createRecordsFromPoints(points).then(() => {
            stopLineLayout("completed");
            app.ui.toast(`Generated ${points.length} slips from line preview.`);
          }).catch((error) => {
            status = error?.message || "Failed to generate line slips.";
            app.ui.toast(status);
            render();
          });
        }
        return;
      }
      if (event.key === "Escape") {
        mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
        mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
        mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
        mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
        window.removeEventListener("wheel", onWheel, { capture: true });
        stopLineLayout("cancelled");
      }
    };
    lineLayoutOff = () => {
      mapContainer?.removeEventListener?.("pointerdown", onPointerDown, true);
      mapContainer?.removeEventListener?.("pointermove", onPointerMove, true);
      mapContainer?.removeEventListener?.("pointerup", onPointerUp, true);
      mapContainer?.removeEventListener?.("pointercancel", onPointerUp, true);
      window.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("keydown", onKey);
    };
    mapContainer?.addEventListener?.("pointerdown", onPointerDown, true);
    mapContainer?.addEventListener?.("pointermove", onPointerMove, true);
    mapContainer?.addEventListener?.("pointerup", onPointerUp, true);
    mapContainer?.addEventListener?.("pointercancel", onPointerUp, true);
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    document.addEventListener("keydown", onKey);
    status = lineLayoutMode === "spacing"
      ? "Click the first slip center, then the next slip center to set spacing."
      : "Click the first slip location, then click the last slip location. Use Ctrl+mouse wheel to adjust slip count after the line is set. Drag any preview slip to re-space around that slip.";
    renderPreview();
    render();
  }

  async function saveSelectedRecord() {
    const current = selectedRecord();
    if (!current) return;
    const selectedProjectId = String(containerEl?.querySelector("#slip-marina-id")?.value || "");
    const selectedMarinaName = String(containerEl?.querySelector("#slip-marina-name")?.value || "");
    const nextSlipNumber = String(containerEl?.querySelector("#slip-number")?.value || "");
    const nextSuffix = String(containerEl?.querySelector("#slip-suffix")?.value || "").trim();
    const nextRecord = {
      ...current,
      marina_id: selectedProjectId,
      marina_name: selectedMarinaName,
      // Full rebuild (not the old slipIdForNumberChange, single-segment-only) since Number
      // and Suffix can both change in the same Save -- mirrors the pseudo-state pattern
      // previewSlipIds already uses. Dock comes from slipDockFromRecord (not dock_name
      // directly), since this panel doesn't edit Dock and shouldn't rebuild around it if it's
      // drifted out of sync with the slip's own Slip ID (see slipDockFromRecord).
      slip_id: buildSlipIdFromState({
        dockName: slipDockFromRecord(current),
        slipIdSeparator: state.slipIdSeparator,
        slipIdPart3: nextSlipNumber,
        slipIdSuffix: nextSuffix,
      }),
      suffix: nextSuffix || null,
      measured_linear_feet: containerEl?.querySelector("#slip-linear-feet")?.value === "" ? null : roundToTenthFoot(containerEl?.querySelector("#slip-linear-feet")?.value),
      measured_width_feet: containerEl?.querySelector("#slip-width-feet")?.value === "" ? null : roundToTenthFoot(containerEl?.querySelector("#slip-width-feet")?.value),
      measured_depth_feet: containerEl?.querySelector("#slip-depth-feet")?.value === "" ? null : Number(containerEl?.querySelector("#slip-depth-feet")?.value),
      space_type: String(containerEl?.querySelector("#slip-space-type")?.value || ""),
      operational_status: String(containerEl?.querySelector("#slip-operational-status")?.value || "operable"),
      notes: String(containerEl?.querySelector("#slip-notes")?.value || ""),
      measurement_date: String(containerEl?.querySelector("#slip-measurement-date")?.value || ""),
      confidence_score: String(containerEl?.querySelector("#slip-confidence")?.value || "High"),
      geometry_wkt: String(containerEl?.querySelector("#slip-geometry-wkt")?.value || current.geometry_wkt || buildPointWkt(0, 0)),
      record_created_timestamp: String(containerEl?.querySelector("#slip-record-created-timestamp")?.value || current.record_created_timestamp || new Date().toISOString()),
    };
    await requestJson(`${API_BASE}/${encodeURIComponent(current.measurement_id)}`, {
      method: "PATCH",
      body: nextRecord,
    });
    state.marinaId = nextRecord.marina_id;
    state.marinaName = nextRecord.marina_name;
    state.measurementDate = nextRecord.measurement_date;
    state.confidenceScore = nextRecord.confidence_score;
    if (nextRecord.slip_id !== current.slip_id) {
      state.slipGeometryPolygons = state.slipGeometryPolygons.map((poly) => (
        String(poly?.linkedMeasurementId || "") === current.measurement_id
          ? { ...poly, linkedSlipId: nextRecord.slip_id }
          : poly
      ));
    }
    persist();
    await refreshRecords({ quiet: true });
    pushUndoEntry(`Edited slip ${nextRecord.slip_id}`, [{
      type: "update",
      measurementId: String(current.measurement_id),
      before: cloneRecord(current),
      after: snapshotRecord(current.measurement_id),
    }]);
    status = `Saved ${nextRecord.slip_id}.`;
    render();
  }

  async function deleteSelectedRecord() {
    const current = selectedRecord();
    if (!current) return;
    const measurementId = String(current.measurement_id || "");
    const deletedSlipId = String(current.slip_id || "");
    const before = cloneRecord(current);
    await requestJson(`${API_BASE}/${encodeURIComponent(current.measurement_id)}`, {
      method: "DELETE",
    });
    // Keep geometry and labels coupled: remove committed polygons linked to deleted record.
    // Also clear stale slip-id links that may reference the deleted slip id.
    state.slipGeometryPolygons = state.slipGeometryPolygons
      .filter((poly) => String(poly?.linkedMeasurementId || "") !== measurementId)
      .map((poly) => (
        String(poly?.linkedSlipId || "") === deletedSlipId
          ? { ...poly, linkedSlipId: "" }
          : poly
      ));
    app.log("slip.deleted", { slip_id: current.slip_id, marina: state.marinaId });
    status = `Deleted ${current.slip_id || current.measurement_id}.`;
    state.selectedMeasurementId = "";
    persist();
    await refreshRecords({ quiet: true });
    pushUndoEntry(`Deleted slip ${deletedSlipId || measurementId}`, [{ type: "delete", measurementId, before }]);
    render();
  }

  function exportRecordsForTable() {     return sortedRecordGeometryRows(records.map((record) => ({ record, geom: findGeometryForRecord(record) })))       .filter(({ record }) => record && isRecordVisible(record))       .map(({ record }) => record);   }    function exportBaseName() {     return sanitizeFilenamePart(filterProjectId || state.marinaId || "slip_inventory");   }    function exportProjectCrs() {     const code = String(app?.services?.projections?.getProjectProfile?.()?.horizontalCRS || "EPSG:4326").trim().toUpperCase();     return code || "EPSG:4326";   }    function buildSlipFeatureCollection(sourceRecords = exportRecordsForTable()) {     const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);     const targetCrs = exportProjectCrs();     const featureCollection = {       type: "FeatureCollection",       name: `${exportBaseName()}_slip_inventory`,       crs: { type: "name", properties: { name: targetCrs } },       features: sourceRecords.map((record) => {         const normalized = buildDisplayExportRecord(record, displayToleranceFt);         const sourceGeometry = geometryFromWkt(normalized.geometry_wkt);         const geometry = sourceGeometry && targetCrs !== "EPSG:4326"           ? app.services.projections.transformGeometry(sourceGeometry, "EPSG:4326", targetCrs)           : sourceGeometry;         const properties = { ...normalized, export_crs: targetCrs };         delete properties.geometry_wkt;         return { type: "Feature", geometry, properties };       }),     };     return { featureCollection, targetCrs };   }    function dxfText(value) {     return String(value ?? "").replace(new RegExp("[\\r\\n]+", "g"), " ").slice(0, 255);   }    function dxfNumber(value) {     const n = Number(value);     return Number.isFinite(n) ? Number(n.toFixed(4)) : 0;   }    function dxfOrderedCoords(coords, closed = false) { const clean = (Array.isArray(coords) ? coords : []).map((pair) => [Number(pair?.[0]), Number(pair?.[1])]).filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y)); if (clean.length < 2) return []; const ring = closed && clean.length > 2 && clean[0][0] === clean[clean.length - 1][0] && clean[0][1] === clean[clean.length - 1][1] ? clean.slice(0, -1) : clean; const dedup = []; const seen = new Set(); ring.forEach(([x, y]) => { const key = String(dxfNumber(x)) + "," + String(dxfNumber(y)); if (!seen.has(key)) { seen.add(key); dedup.push([x, y]); } }); return dedup; }    function dxfPolyline(lines, coords, { closed = false, layer = "SLIPS" } = {}) { const ring = dxfOrderedCoords(coords, closed); if (ring.length < 2) return; lines.push("0", "POLYLINE", "8", layer, "66", "1", "70", closed ? "1" : "0"); for (const [x, y] of ring) { lines.push("0", "VERTEX", "8", layer, "10", String(dxfNumber(x)), "20", String(dxfNumber(y)), "30", "0"); } lines.push("0", "SEQEND", "8", layer); }    function dxfFeatureLabelPoint(geometry) {     if (!geometry) return null;     if (geometry.type === "Point") return geometry.coordinates;     if (geometry.type === "LineString" && Array.isArray(geometry.coordinates) && geometry.coordinates.length) {       return geometry.coordinates[Math.floor(geometry.coordinates.length / 2)];     }     if (geometry.type === "Polygon" && Array.isArray(geometry.coordinates?.[0])) {       const ring = geometry.coordinates[0];       const sum = ring.reduce((acc, pair) => [acc[0] + Number(pair?.[0] || 0), acc[1] + Number(pair?.[1] || 0)], [0, 0]);       return [sum[0] / Math.max(1, ring.length), sum[1] / Math.max(1, ring.length)];     }     return null;   }    function buildDxfFromFeatureCollection(featureCollection, targetCrs) {     const lines = [       "0", "SECTION", "2", "HEADER", "9", "$ACADVER", "1", "AC1009", "0", "ENDSEC",       "0", "SECTION", "2", "TABLES", "0", "TABLE", "2", "LAYER", "70", "2",       "0", "LAYER", "2", "SLIPS", "70", "0", "62", "3", "6", "CONTINUOUS",       "0", "ENDTAB", "0", "ENDSEC", "0", "SECTION", "2", "ENTITIES",     ];     lines.push("999", "HYDROTOOLS_DXF_V5");     lines.push("999", "Hydrotools slip inventory export CRS: " + targetCrs);     for (const feature of featureCollection.features || []) {       const geometry = feature?.geometry;       if (!geometry) continue;       if (geometry.type === "Point") {         const [x, y] = geometry.coordinates || [];         lines.push("0", "POINT", "8", "SLIPS", "10", String(dxfNumber(x)), "20", String(dxfNumber(y)), "30", "0");       } else if (geometry.type === "LineString") {         dxfPolyline(lines, geometry.coordinates, { closed: false });       } else if (geometry.type === "Polygon") {         dxfPolyline(lines, geometry.coordinates?.[0], { closed: true });       }     }     lines.push("0", "ENDSEC", "0", "EOF");     const nl = String.fromCharCode(10); return lines.join(nl) + nl;   }    function renderSlipExportMenu({ disabled = false, includePlot = true } = {}) {     const d = disabled ? " disabled" : "";     const exportCrs = exportProjectCrs();     const crsIsGeo = exportCrs === "EPSG:4326";     const crsNote = crsIsGeo       ? `<div style="padding:4px 6px 2px;font-size:10px;color:rgba(251,191,36,.8);border-top:1px solid rgba(148,163,184,.12);margin-top:2px;">DXF/GeoJSON: ${exportCrs} — set CRS in Projection Manager</div>`       : `<div style="padding:4px 6px 2px;font-size:10px;color:rgba(148,163,184,.55);border-top:1px solid rgba(148,163,184,.12);margin-top:2px;">DXF/GeoJSON: ${exportCrs}</div>`;     return `<details class="slip-export-menu" style="position:relative;display:inline-block;">       <summary class="btn" style="list-style:none;cursor:${disabled ? "not-allowed" : "pointer"};opacity:${disabled ? 0.55 : 1};"${d}>Export ▾</summary>       <div style="position:absolute;right:0;top:calc(100% + 4px);z-index:240;min-width:154px;background:rgba(15,23,42,.98);border:1px solid rgba(148,163,184,.24);border-radius:6px;padding:4px;box-shadow:0 8px 22px rgba(0,0,0,.45);display:grid;gap:2px;">         <button type="button" class="btn" data-slip-export="csv"${d}>CSV</button>         <button type="button" class="btn" data-slip-export="xls"${d}>XLSX</button>         <button type="button" class="btn" data-slip-export="json"${d}>JSON</button>         <button type="button" class="btn" data-slip-export="geojson"${d}>GeoJSON</button>         <button type="button" class="btn" data-slip-export="dxf"${d}>DXF</button>         ${includePlot ? `<button type="button" class="btn" data-slip-export="plot"${d}>PDF Plot</button><button type="button" class="btn" data-slip-export="report"${d}>PDF Report</button>` : ""}         ${crsNote}       </div>     </details>`;   }    function renderSlipDisplayToggles() {
    const toggle = (id, checked, label, color) => `<label class="small" style="display:flex; align-items:center; gap:6px;${color ? ` color:${color};` : ""}"><input id="${id}" type="checkbox"${checked ? " checked" : ""} /> ${label}</label>`;
    return `<div style="display:flex; flex-direction:column; gap:4px;">
      ${toggle("slip-toggle-ids", state.showSlipIds, "Show IDs")}
      ${toggle("slip-toggle-depths", state.showDepths, "Show Depths")}
      ${toggle("slip-toggle-length", state.showSlipLength, "Show Length", SLIP_DIMENSION_LINE_COLORS.length)}
      ${toggle("slip-toggle-width", state.showSlipWidth, "Show Width", SLIP_DIMENSION_LINE_COLORS.width)}
      <label class="small" style="display:flex; align-items:center; gap:6px;" title="When a slip is selected, extend its edges past their corners with dotted lines to help line them up with adjacent slips and dock geometry.">
        <input id="slip-toggle-edge-extensions"${state.showEdgeExtensions ? " checked" : ""} type="checkbox" /> Extend Edges
        <input id="slip-edge-extension-ft" type="number" min="0" step="1" value="${esc(state.edgeExtensionFt)}" style="width:46px;" /> ft
      </label>
    </div>`;
  }
  async function runSlipExport(kind) {     const action = String(kind || "").toLowerCase();     const label = { csv: "CSV", xls: "XLSX", xlsx: "XLSX", json: "JSON", geojson: "GeoJSON", dxf: "DXF", plot: "PDF Plot", report: "PDF Report" }[action] || action.toUpperCase();     taskStatus(`Exporting ${label}…`);     const exportStart = Date.now();     try {       let result = null;       if (action === "csv") result = exportCsv();       else if (action === "xls" || action === "xlsx") result = await exportXls();       else if (action === "json") result = exportJson();       else if (action === "geojson") result = exportGeoJson();       else if (action === "dxf") result = exportDxf();       else if (action === "plot") result = await exportPlotSvg();       else if (action === "report") result = await exportSlipReportPdf();       app.log("export.complete", { format: action, durationMs: Date.now() - exportStart, count: exportRecordsForTable().length, marina: state.marinaId });       return result;     } catch (err) {       app.log("export.error", { format: action, durationMs: Date.now() - exportStart, error: String(err?.message || err), marina: state.marinaId });       throw err;     } finally {       taskIdle();     }   }    function exportJson() {     const outRecords = exportRecordsForTable();     downloadText(`${exportBaseName()}_slip_inventory_${exportDateStamp()}.json`, JSON.stringify(outRecords, null, 2), "application/json;charset=utf-8");   }    function exportGeoJson() {     const { featureCollection } = buildSlipFeatureCollection(exportRecordsForTable());     const { projectNumber, code } = currentDeliverableIds(app, filterProjectId || state.marinaId);     downloadText(deliverableFilename({ projectNumber, code, kind: "slip_inventory_geojson" }), JSON.stringify(featureCollection, null, 2), "application/geo+json;charset=utf-8");   }    function dxfProjectLngLat(lng, lat, targetCrs) {     const coord = [Number(lng), Number(lat)];     if (!Number.isFinite(coord[0]) || !Number.isFinite(coord[1])) return null;     const projected = targetCrs === "EPSG:4326" ? coord : app.services.projections.transformCoord(coord, "EPSG:4326", targetCrs);     return Array.isArray(projected) && Number.isFinite(Number(projected[0])) && Number.isFinite(Number(projected[1])) ? [Number(projected[0]), Number(projected[1])] : null;   }    function dxfParametricRingForPoly(poly, targetCrs) {     const center = { lat: Number(poly?.center?.lat), lng: Number(poly?.center?.lng) };     const lengthFt = Number(poly?.lengthFt || 0);     const widthFt = Number(poly?.widthFt || 0);     if (!Number.isFinite(center.lat) || !Number.isFinite(center.lng) || lengthFt <= 0 || widthFt <= 0) return null;     const ringLatLngs = computeParametricSlipPolygonLatLngs({       center,       lengthFt,       widthFt,       headingDeg: Number(poly?.headingDeg || 0),     });     const ring = (Array.isArray(ringLatLngs) ? ringLatLngs : []).map(([lat, lng]) => dxfProjectLngLat(lng, lat, targetCrs)).filter(Boolean);     return ring.length >= 3 ? ring : null;   }    function dxfParametricRingForRecord(record, targetCrs) {     const sourceRing = parsePolygonWkt(record?.geometry_wkt);     const inferred = Array.isArray(sourceRing) && sourceRing.length >= 3 ? inferPolyMetricsFromRing(sourceRing) : null;     if (!inferred?.center) return null;     const ringLatLngs = computeParametricSlipPolygonLatLngs({       center: inferred.center,       lengthFt: Number(inferred.lengthFt || record?.measured_linear_feet || 0),       widthFt: Number(inferred.widthFt || record?.measured_width_feet || 0),       headingDeg: Number(inferred.headingDeg || 0),     });     const ring = (Array.isArray(ringLatLngs) ? ringLatLngs : []).map(([lat, lng]) => dxfProjectLngLat(lng, lat, targetCrs)).filter(Boolean);     return ring.length >= 3 ? ring : null;   }    function buildDxfFeatureCollectionForRecords(sourceRecords = exportRecordsForTable()) {     const { featureCollection, targetCrs } = buildSlipFeatureCollection(sourceRecords);     const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);     const features = sourceRecords.map((record, index) => {       const poly = findGeometryForRecord(record);       const ring = (poly ? dxfParametricRingForPoly(poly, targetCrs) : null) || dxfParametricRingForRecord(record, targetCrs);       if (ring) {         const closed = [...ring, ring[0]];         const normalized = buildDisplayExportRecord(record, displayToleranceFt);         const properties = { ...normalized, export_crs: targetCrs, export_geometry_source: poly ? "parametric_map_outline" : "inferred_record_outline" };         delete properties.geometry_wkt;         return { type: "Feature", geometry: { type: "Polygon", coordinates: [closed] }, properties };       }       return featureCollection.features?.[index] || null;     }).filter(Boolean);     return { featureCollection: { ...featureCollection, features }, targetCrs };   }    function exportDxf() {     const { featureCollection, targetCrs } = buildDxfFeatureCollectionForRecords(exportRecordsForTable());     if (targetCrs === "EPSG:4326") {       app.ui?.toast?.("DXF exported in geographic coordinates (decimal degrees). Open Projection Manager and set a projected CRS for CAD use.", { level: "warn" });     }     const dxf = buildDxfFromFeatureCollection(featureCollection, targetCrs);     downloadText(exportBaseName() + "_slip_inventory_" + targetCrs.replace(/[^A-Z0-9]+/g, "_") + "_" + exportDateStamp() + ".dxf", dxf, "application/dxf;charset=utf-8");   }    function exportCsv() {     const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);     const { projectNumber, code } = currentDeliverableIds(app, filterProjectId || state.marinaId);     downloadText(deliverableFilename({ projectNumber, code, kind: "slip_inventory_csv" }), buildCsv(exportRecordsForTable(), displayToleranceFt), "text/csv;charset=utf-8");   }    async function exportXls() {
    const toleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);
    const [{ utils, write }, { default: JSZip }] = await Promise.all([
      import("xlsx"),
      import("jszip"),
    ]);

    const rows = exportRecordsForTable().map((r) => buildDisplayExportRecord(r, toleranceFt));
    const ws = utils.json_to_sheet(rows, { header: RECORD_HEADERS });
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Slip Inventory");

    // SheetJS 0.18.x does not write <dataValidations> natively.
    // Generate base XLSX, re-open with JSZip, inject validation XML, re-zip.
    const xlsxBuf = write(wb, { type: "array", bookType: "xlsx" });
    const zip = await JSZip.loadAsync(xlsxBuf);
    const sheetXml = await zip.file("xl/worksheets/sheet1.xml").async("string");

    const nRows = rows.length;
    const cl = (key) => utils.encode_col(RECORD_HEADERS.indexOf(key));
    const dv = (key, opts) =>
      `<dataValidation type="list" allowBlank="1" showInputMessage="1" showErrorMessage="0" sqref="${cl(key)}2:${cl(key)}${nRows + 1}"><formula1>"${opts.join(",")}"</formula1></dataValidation>`;

    const dvEntries = [
      ...[...BINARY_COLS].map((k) => dv(k, ["Yes", "No"])),
      dv("space_type",            SPACE_TYPE_OPTIONS),
      dv("out_of_service_reason", OOS_REASON_OPTIONS),
      dv("confidence_score",      ["High", "Medium", "Low"]),
    ];
    const dvXml = `<dataValidations count="${dvEntries.length}">${dvEntries.join("")}</dataValidations>`;
    zip.file("xl/worksheets/sheet1.xml", sheetXml.replace("</worksheet>", dvXml + "</worksheet>"));

    const outBuf = await zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
    const blob = new Blob([outBuf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportBaseName() + "_slip_inventory_" + exportDateStamp() + ".xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  // Blob-producing counterparts to exportCsv()/exportGeoJson() (which trigger a browser
  // download) -- reuse the exact same data-building calls, just skip the <a download> click
  // and hand back {filename, blob} instead, for generateAllDeliverables() to upload.
  function buildSlipCsvBlob(date) {
    const displayToleranceFt = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);
    const { projectNumber, code } = currentDeliverableIds(app, filterProjectId || state.marinaId);
    const filename = deliverableFilename({ projectNumber, code, kind: "slip_inventory_csv", date });
    const blob = new Blob([buildCsv(exportRecordsForTable(), displayToleranceFt)], { type: "text/csv;charset=utf-8" });
    return { filename, blob };
  }

  function buildSlipGeoJsonBlob(date) {
    const { featureCollection } = buildSlipFeatureCollection(exportRecordsForTable());
    const { projectNumber, code } = currentDeliverableIds(app, filterProjectId || state.marinaId);
    const filename = deliverableFilename({ projectNumber, code, kind: "slip_inventory_geojson", date });
    const blob = new Blob([JSON.stringify(featureCollection, null, 2)], { type: "application/geo+json;charset=utf-8" });
    return { filename, blob };
  }

  const ESRI_WORLD_IMAGERY_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

  // The one source for both the plot-export background raster and the SLAM GeoTIFF
  // deliverable (see generateAllDeliverables) -- Tile Pyramids retired in favor of the
  // Surfaces tool's mosaic2d raster. Unlike Tile Pyramids (which could have several
  // re-flights/builds per marina, hence a picker), there's at most one mosaic per marina, so
  // this just finds it: the mosaic2d tile endpoint (dynamic TiTiler tiles, no static XYZ
  // pyramid to build/store), plus its active Dock Centerline mask if one's been set in the
  // Surfaces tool -- same {rescaleMin,rescaleMax} the surfaces tool's own export uses.
  // A surface's rescaleMin/Max only count as a real value mask (not just a display stretch)
  // once both under/overflow are set transparent -- same condition the surfaces tool's own
  // "Export → DAM" checkbox uses.
  // Mirrors the Surfaces tool's activeMaskRange() -- has to read the mask from whichever
  // representation is actually visible/editable for the surface's colormap: the Custom
  // (colormapName "solid") bins editor's dividers/per-bin transparency, or the interval
  // ramp's rescale range + underflow/overflow transparency otherwise. Reading
  // rescaleMin/rescaleMax unconditionally was stale for a "solid" surface, since Dock
  // Centerline (and any hand-editing of the visible bars afterward) writes to
  // customDividers/customTransparent there instead.
  function activeMaskFromSettings(st) {
    if (!st) return { maskMin: null, maskMax: null };
    if (st.colormapName === "solid") {
      const bins = Math.max(1, Number(st.customBins) || 0);
      const divs = Array.isArray(st.customDividers) ? [...st.customDividers].sort((a, b) => a - b) : [];
      if (divs.length !== bins - 1) return { maskMin: null, maskMax: null };
      const transparent = st.customTransparent || [];
      const opaqueIdx = [];
      for (let i = 0; i < bins; i++) if (!transparent[i]) opaqueIdx.push(i);
      if (!opaqueIdx.length) return { maskMin: null, maskMax: null };
      const first = opaqueIdx[0], last = opaqueIdx[opaqueIdx.length - 1];
      if (last - first !== opaqueIdx.length - 1 || first === 0 || last === bins - 1) return { maskMin: null, maskMax: null };
      return { maskMin: divs[first - 1], maskMax: divs[last] };
    }
    const masked = !!(st.underflowTransparent && st.overflowTransparent && Number.isFinite(st.rescaleMin) && Number.isFinite(st.rescaleMax));
    return { maskMin: masked ? st.rescaleMin : null, maskMax: masked ? st.rescaleMax : null };
  }

  // Low-res viridis approximation (5 anchor stops, linearly interpolated) -- used below to
  // build a real gradient inside a masked range for an interval-ramp-styled surface, so a
  // continuous-colormap surface still previews as a gradient (just clipped to what's kept),
  // not one arbitrary flat color.
  const VIRIDIS_STOPS = [
    [0.00, 68, 1, 84], [0.25, 59, 82, 139], [0.50, 33, 144, 140],
    [0.75, 93, 200, 99], [1.00, 253, 231, 37],
  ];
  function viridisColor(t) {
    const c = Math.max(0, Math.min(1, t));
    for (let i = 0; i < VIRIDIS_STOPS.length - 1; i++) {
      const [t0, r0, g0, b0] = VIRIDIS_STOPS[i];
      const [t1, r1, g1, b1] = VIRIDIS_STOPS[i + 1];
      if (c >= t0 && c <= t1) {
        const f = (c - t0) / (t1 - t0 || 1);
        return [Math.round(r0 + (r1 - r0) * f), Math.round(g0 + (g1 - g0) * f), Math.round(b0 + (b1 - b0) * f)];
      }
    }
    return VIRIDIS_STOPS[VIRIDIS_STOPS.length - 1].slice(1);
  }

  // Tile-styling query params for a raster preview. An interval-ramp surface gets a real
  // viridis gradient, clipped to [maskMin, maskMax] when the surface has an active mask
  // (otherwise just [lo, hi]) -- everything outside is an explicit transparent [0,0,0,0]
  // entry. Standardized on plain viridis so every PDF plot background looks the same
  // regardless of how the source surface happens to be styled for its own DAM export.
  //
  // Always an explicit interval array, never colormap_name+rescale: confirmed directly
  // against prod that colormap_name=viridis&rescale=lo,hi does NOT render nodata pixels
  // transparent here -- rio-tiler clamps them to the bottom of the named colormap instead
  // (opaque, alpha=255), so the whole raster's nodata area painted solid dark viridis-purple
  // over the plot -- exactly the "purple wash with blocky patches" a client saw, unrelated
  // to tile zoom/resolution. An explicit colormap array with [0,0,0,0] outside the real
  // range renders nodata genuinely transparent -- verified with the same tile both ways.
  function surfaceStyleParams(surface, lo, hi) {
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return "";
    const maskInfo = activeMaskFromSettings(surface?.settings);
    const rangeMin = maskInfo.maskMin != null ? maskInfo.maskMin : lo;
    const rangeMax = maskInfo.maskMax != null ? maskInfo.maskMax : hi;
    const pad = (Math.abs(hi - lo) + 1) * 1000;
    const steps = 32;
    const entries = [[[lo - pad, rangeMin], [0, 0, 0, 0]]];
    for (let i = 0; i < steps; i++) {
      const t0 = i / steps, t1 = (i + 1) / steps;
      const [r, g, b] = viridisColor((t0 + t1) / 2);
      entries.push([[rangeMin + t0 * (rangeMax - rangeMin), rangeMin + t1 * (rangeMax - rangeMin)], [r, g, b, 255]]);
    }
    entries.push([[rangeMax, hi + pad], [0, 0, 0, 0]]);
    return `&colormap=${encodeURIComponent(JSON.stringify(entries))}`;
  }

  // Every Surfaces-tool raster usable as a SLAM GeoTIFF source for this marina -- the 2D
  // mosaic (if built) plus each ready single-COG surface, most-recent first. Restores a
  // per-candidate list (Tile Pyramids had one too, before "Retire Tile Pyramids" collapsed
  // this down to "the" single mosaic/fallback surface) so a multi-region marina like one with
  // genuinely distinct built surfaces per region can link the correct raster to each region
  // instead of every region silently sharing whichever one surface this used to auto-pick.
  async function listSurfaceDeliverableCandidates(code) {
    const [summaryData, catalogData] = await Promise.all([
      requestJson(`/api/point-cloud/mosaic2d/${encodeURIComponent(code)}`).catch(() => null),
      requestJson(`/api/surfaces/catalog?marina=${encodeURIComponent(code)}`).catch(() => null),
    ]);
    const catalog = catalogData?.catalog || [];
    const mosaic = summaryData?.mosaic;
    const candidates = [];

    if (mosaic?.tileCount) {
      const surface = catalog.find((s) => s.kind === "mosaic2d" && !s.attr);
      const mosaicStyleParams = surfaceStyleParams(surface, mosaic.stretchMin, mosaic.stretchMax);
      candidates.push({
        key: "mosaic2d",
        kind: "mosaic2d",
        name: surface?.name || "2D LiDAR Mosaic",
        marinaCode: code,
        tileUrl: `/api/point-cloud/mosaic2d/${encodeURIComponent(code)}/tiles/{z}/{x}/{y}.png?v=${encodeURIComponent(mosaic.updatedAt || "")}${mosaicStyleParams}`,
        wgs84Bounds: mosaic.bounds || null,
        zoom: mosaic.maxzoom || 22,
        minNativeZoom: mosaic.minzoom || 12,
        createdAt: mosaic.updatedAt || surface?.createdAt || null,
        ...activeMaskFromSettings(surface?.settings),
      });
    }

    const cogSurfaces = catalog.filter((s) => s.rasterReady && s.rasterPath && s.kind !== "mosaic2d");
    const cogCandidates = await Promise.all(cogSurfaces.map(async (surface) => {
      // A multi-band surface (see the Surfaces tool's per-cell statistic bands) needs an
      // explicit &bidx= or TiTiler has to encode every band into one PNG at once -- which
      // fails outright past 4 bands ("Could not encode array of shape (6,256,256)... using
      // PNG driver", a hard 500 on every tile) and silently misrenders as bogus RGB(A) for
      // 2-4 bands. Same index convention as surfaceBandIndex in src/tools/surfaces/index.js:
      // displayBand's position in the canonical PDAL band order, defaulting to band 1.
      const bandIdx = Array.isArray(surface.bands) && surface.bands.length >= 2
        ? (Math.max(0, surface.bands.indexOf(surface.settings?.displayBand)) + 1)
        : null;
      const bidxParam = bandIdx ? `&bidx=${bandIdx}` : "";
      // /api/qimera/cog-tiles is a bare TiTiler proxy with no default styling (unlike the
      // mosaic2d tile route, which applies its own colormap_name/rescale for a bare request) --
      // an unstyled request renders grayscale with each tile auto-stretching its own local
      // min/max, which on a masked (mostly-nodata) surface is the same salt-and-pepper noise
      // /api/qimera/cogify used to produce. Attach a global, mask-aware colormap here too.
      let styleParams = "";
      try {
        const stats = await requestJson(`/api/qimera/cog-tiles/statistics?url=${encodeURIComponent(surface.rasterPath)}${bidxParam}`);
        // With bidx set, TiTiler keys the response by the *requested* band number (e.g. "b3"),
        // not "b1" -- read whichever key actually matches what was asked for.
        const band = stats?.[`b${bandIdx || 1}`];
        const lo = Number(band?.percentile_2 ?? band?.min);
        const hi = Number(band?.percentile_98 ?? band?.max);
        styleParams = surfaceStyleParams(surface, lo, hi);
      } catch {}
      return {
        key: surface.id,
        kind: "qimera-cog",
        name: surface.name || "Built surface",
        marinaCode: code,
        rasterPath: surface.rasterPath,
        tileUrl: `/api/qimera/cog-tiles/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${encodeURIComponent(surface.rasterPath)}${bidxParam}${styleParams}`,
        wgs84Bounds: null,
        zoom: 22,
        minNativeZoom: 12,
        createdAt: surface.createdAt || null,
        ...activeMaskFromSettings(surface.settings),
      };
    }));
    candidates.push(...cogCandidates);

    candidates.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    return candidates;
  }

  // Single "best" source for callers that just need one raster and don't offer a picker: the
  // plot-export background default, and the single-region Generate Deliverables auto-resolve.
  async function getMosaicDeliverableSource(code) {
    const candidates = await listSurfaceDeliverableCandidates(code);
    return candidates[0] || null;
  }

  // Slip plot exports (the standalone "PDF Plot" export and the automated Generate
  // Deliverables flow both funnel through exportPlotSvg) need a consistent look regardless
  // of whatever the user currently has on screen: Hydrotools light mode, Esri World Imagery
  // as a subtle 35% base, and the marina's 2D mosaic raster forced fully opaque -- auto-
  // loading it from the DAM if nothing's already on the map, rather than plotting without
  // it, since the whole point is the plot always carries the survey raster alongside the
  // slips. Snapshots whatever was there so it can be restored after capture -- this must
  // never leave the user's live map altered. mapModeEl/the "map:basemap:mode" event mirror
  // exactly what the shell's theme toggle does (see core/ui/shell.js applyMapMode), since
  // that function itself isn't exposed to tool modules.
  async function standardizePlotBaseLayers(mosaicSource, { autoResolveMissing = true } = {}) {
    const mapModeEl = document.querySelector("[data-map-mode]");
    const originalMapMode = mapModeEl?.dataset?.mapMode === "light" ? "light" : "dark";
    if (mapModeEl) mapModeEl.dataset.mapMode = "light";
    app.events.emit("map:basemap:mode", { mode: "light" });

    const snapshot = { originalMapMode, esri: null, esriAddedId: null, mosaics: [], mosaicAddedId: null, mosaicUrl: null };
    const layersService = app?.services?.layers;
    if (!layersService) return snapshot;

    const layers = layersService.listLayers();
    const esriLayer = layers.find((l) => String(l.id || "").includes("esri-imagery"));
    if (esriLayer) {
      snapshot.esri = { id: esriLayer.id, opacity: esriLayer.opacity, visible: esriLayer.visible !== false };
      layersService.setVisible(esriLayer.id, true);
      layersService.setOpacity(esriLayer.id, 0.35);
    } else {
      snapshot.esriAddedId = layersService.addLayer("overlay-manager", {
        id: `esri-imagery-plotexport-${Math.random().toString(16).slice(2, 6)}`,
        name: "Esri World Imagery",
        kind: "basemap",
        type: "xyz",
        url: ESRI_WORLD_IMAGERY_URL,
        options: { maxNativeZoom: 19, maxZoom: 24, attribution: "Tiles © Esri" },
        opacity: 0.35,
        volatile: true,
      });
    }

    // Only this function ever creates a mosaic2d-export layer, so any that's already on the
    // map is a leftover from an earlier export that never reached restorePlotBaseLayers (the
    // tab reloaded mid tile-fetch, say) and came back through the layers service's
    // localStorage restore. Matching leftovers on marinaCode alone used to treat any of them
    // as "the requested raster" and silently plot every page against it -- on a multi-region
    // marina whose regions link different surfaces (COW, 2026-09-04), the tile server 404s
    // outside the leftover surface's footprint, so every other region's page came out with
    // no raster at all even though the Generate Deliverables links were right. A leftover
    // is only reused when its tile URL is exactly the raster being asked for; every other
    // leftover is removed here (they're this export's own orphaned temp layers, never
    // user content), and the fresh layer below is volatile so it can never be persisted.
    const code = String(filterProjectId || state.marinaId || "").trim().toUpperCase();
    let candidate = mosaicSource;
    // Only auto-resolve a fallback raster when nobody upstream already made a surface
    // decision (the standalone Export > PDF Plot button has no picker context). The
    // Generate Deliverables flow always resolves its own surface per assignment -- including
    // explicitly "none" when a region's GeoTIFF dropdown is left at its default Skip, or the
    // single-region picker's GeoTIFF is skipped -- and passes that through as mosaicSource
    // (keepDoc: true is that flow's own signal, see exportPlotSvg's call below). Auto-picking
    // "whichever surface in this marina has the newest createdAt" here used to silently
    // override that choice: a multi-region marina's un-touched (default-Skip) region rows
    // would each grab the same marina-wide "most recent" surface regardless of which region
    // it actually belongs to, and an explicit single-region Skip would still get a random
    // raster baked into the plot background it was told not to carry.
    if (!candidate && code && autoResolveMissing) {
      try { candidate = await getMosaicDeliverableSource(code); } catch { candidate = null; }
    }
    const requestedUrl = candidate?.tileUrl ? String(candidate.tileUrl) : null;
    const leftovers = layers.filter((l) => l.meta?.rasterKind === "mosaic2d-export");
    const reusable = requestedUrl ? leftovers.find((l) => String(l.url || "") === requestedUrl) : null;
    for (const l of leftovers) {
      if (reusable && l.id === reusable.id) continue;
      layersService.removeLayer(l.id);
      app.log("export.plot.leftover_raster_removed", { marina: code, layer: l.name, url: String(l.url || "").slice(0, 160) });
    }
    if (reusable) {
      snapshot.mosaics.push({ id: reusable.id, opacity: reusable.opacity, visible: reusable.visible !== false });
      layersService.setVisible(reusable.id, true);
      layersService.setOpacity(reusable.id, 1);
      snapshot.mosaicUrl = requestedUrl;
    } else if (requestedUrl) {
      snapshot.mosaicAddedId = layersService.addLayer("point-cloud-workspace", {
        type: "tile-url",
        kind: "overlay",
        name: candidate.kind === "mosaic2d" ? "2D LiDAR Mosaic (plot export)" : "Surface (plot export)",
        url: requestedUrl,
        opacity: 1,
        visible: true,
        volatile: true,
        meta: { sourceType: "surfaces", rasterKind: "mosaic2d-export", marinaCode: candidate.marinaCode },
        options: {
          bounds: candidate.wgs84Bounds || null,
          maxNativeZoom: Number(candidate.zoom || 22),
          minNativeZoom: Number(candidate.minNativeZoom || 12),
          maxZoom: 26,
        },
      });
      snapshot.mosaicUrl = requestedUrl;
    }
    return snapshot;
  }

  function restorePlotBaseLayers(snapshot) {
    if (!snapshot) return;
    const mapModeEl = document.querySelector("[data-map-mode]");
    if (mapModeEl) mapModeEl.dataset.mapMode = snapshot.originalMapMode;
    app.events.emit("map:basemap:mode", { mode: snapshot.originalMapMode });

    const layersService = app?.services?.layers;
    if (!layersService) return;
    if (snapshot.esriAddedId) {
      layersService.removeLayer(snapshot.esriAddedId);
    } else if (snapshot.esri) {
      layersService.setOpacity(snapshot.esri.id, snapshot.esri.opacity);
      layersService.setVisible(snapshot.esri.id, snapshot.esri.visible);
    }
    if (snapshot.mosaicAddedId) layersService.removeLayer(snapshot.mosaicAddedId);
    for (const p of snapshot.mosaics) {
      layersService.setOpacity(p.id, p.opacity);
      layersService.setVisible(p.id, p.visible);
    }
  }

  async function exportPlotSvg({ returnBlob = false, date, tileset: mosaicSource, colorMode = null, deliverableRegion = null, pdfDoc = null, keepDoc = false } = {}) {
    const setStatus = (msg) => taskStatus(msg);

    const mode = colorMode || String(state.colorSlipsBy || "none");
    const marinaLabel = filterProjectId || "Marina";

    const polysWithRecords = (state.slipGeometryPolygons || [])
      .filter((poly) => poly.committed)
      .map((poly) => ({ poly, record: findRecordForPolygon(poly) }))
      .filter(({ record }) => record && isRecordVisible(record))
      // Generate Deliverables scopes a multi-region marina's plot to just that region's slips
      // (see enumerateDeliverableRegions/generateAllDeliverables) -- the standalone Export >
      // PDF Plot menu item never passes deliverableRegion, so it's unaffected.
      .filter(({ record }) => !deliverableRegion || deliverableRegionKeyForRecord(record) === deliverableRegion.key);

    if (!polysWithRecords.length) {
      // A blocking dialog is fine for the interactive Export-menu path, but would freeze
      // generateAllDeliverables() waiting on a click nobody's there to make -- let that
      // caller turn the empty result into its own non-blocking status message instead.
      if (!returnBlob) alert("No committed slip polygons to export. Draw and commit polygons first.");
      return null;
    }

    // Web Mercator projection (same CRS as XYZ tiles — no distortion at marina scale)
    const toMx = (lng) => lng * Math.PI / 180;
    const toMy = (lat) => Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));

    // Build polygon data (centroid in Mercator radians, pts in Mercator radians)
    const polyDataRaw = polysWithRecords.map(({ poly, record }) => {
      const ring = computeSlipPolygonLatLngs(poly);
      const pts = ring.map(([lat, lng]) => ({ mx: toMx(lng), my: toMy(lat), lat, lng }));
      // Shoelace centroid in local coords to avoid floating-point cancellation
      // (Mercator coords are large absolute values; translate to near-zero first)
      const omx = pts.reduce((s, p) => s + p.mx, 0) / pts.length;
      const omy = pts.reduce((s, p) => s + p.my, 0) / pts.length;
      const lp = pts.map(p => ({ mx: p.mx - omx, my: p.my - omy }));
      let area = 0, lcx = 0, lcy = 0;
      for (let i = 0, j = lp.length - 1; i < lp.length; j = i++) {
        const cross = lp[j].mx * lp[i].my - lp[i].mx * lp[j].my;
        area += cross; lcx += (lp[j].mx + lp[i].mx) * cross; lcy += (lp[j].my + lp[i].my) * cross;
      }
      area /= 2;
      const cx = Math.abs(area) > 1e-20 ? lcx / (6 * area) + omx : omx;
      const cy = Math.abs(area) > 1e-20 ? lcy / (6 * area) + omy : omy;
      const centerLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
      const centerLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
      return { poly, record, pts, cx, cy, centerLat, centerLng };
    });

    // Reject outlier polygons whose centroid is more than 1° from the median centroid.
    // Prevents a single bad/null-island coordinate from blowing up the bounding box.
    const sortedLats = [...polyDataRaw.map((d) => d.centerLat)].sort((a, b) => a - b);
    const sortedLngs = [...polyDataRaw.map((d) => d.centerLng)].sort((a, b) => a - b);
    const medianLat = sortedLats[Math.floor(sortedLats.length / 2)];
    const medianLng = sortedLngs[Math.floor(sortedLngs.length / 2)];
    const polyData = polyDataRaw.filter((d) =>
      Math.abs(d.centerLat - medianLat) <= 1 && Math.abs(d.centerLng - medianLng) <= 1
    );
    if (polyData.length === 0) {
      alert("All polygon centroids appear to be at invalid coordinates. Check geometry data.");
      return;
    }

    // Mercator bounds from outlier-filtered polygon set
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const d of polyData) {
      for (const p of d.pts) {
        if (p.lat < minLat) minLat = p.lat;
        if (p.lat > maxLat) maxLat = p.lat;
        if (p.lng < minLng) minLng = p.lng;
        if (p.lng > maxLng) maxLng = p.lng;
      }
    }
    const allPts = polyData.flatMap((d) => d.pts);
    const minMx = Math.min(...allPts.map((p) => p.mx));
    const maxMx = Math.max(...allPts.map((p) => p.mx));
    const minMy = Math.min(...allPts.map((p) => p.my));
    const maxMy = Math.max(...allPts.map((p) => p.my));

    const SVG_W = 1800;
    const PAD = 80;
    const rawBearing = Number(map?.getBearing?.());
    const mapBearingDeg = Number.isFinite(rawBearing) ? ((rawBearing % 360) + 360) % 360 : 0;
    const mapBearingRad = mapBearingDeg * Math.PI / 180;
    const cosBearing = Math.cos(mapBearingRad);
    const sinBearing = Math.sin(mapBearingRad);
    const centerMx = (minMx + maxMx) / 2;
    const centerMy = (minMy + maxMy) / 2;
    const rotatedDeltas = allPts.map((p) => {
      const dx = p.mx - centerMx;
      const dy = -(p.my - centerMy); // screen-space Y: north is up
      return {
        x: (dx * cosBearing) - (dy * sinBearing),
        y: (dx * sinBearing) + (dy * cosBearing),
      };
    });
    const minRotX = Math.min(...rotatedDeltas.map((p) => p.x));
    const maxRotX = Math.max(...rotatedDeltas.map((p) => p.x));
    const minRotY = Math.min(...rotatedDeltas.map((p) => p.y));
    const maxRotY = Math.max(...rotatedDeltas.map((p) => p.y));
    const rangeX = maxRotX - minRotX || 1e-9;
    const rangeY = maxRotY - minRotY || 1e-9;
    const scale = Math.min((SVG_W - 2 * PAD) / rangeX, (SVG_W - 2 * PAD) / rangeY);
    const contentH = rangeY * scale;
    const SVG_H = contentH + 2 * PAD;
    const mapCenterX = (SVG_W - ((minRotX + maxRotX) * scale)) / 2;
    const mapCenterY = PAD - (minRotY * scale);

    const sx = (mx) => mapCenterX + (mx - centerMx) * scale;
    const sy = (my) => mapCenterY - (my - centerMy) * scale; // Y flip (Mercator Y increases northward, SVG Y increases downward)

    // keepDoc is only ever passed true by generateAllDeliverables (see its exportPlotSvg call)
    // -- that flow always resolves its own surface per assignment, so a falsy mosaicSource
    // there means "explicitly no surface for this region," not "nobody decided yet."
    const _plotLayerSnapshot = await standardizePlotBaseLayers(mosaicSource, { autoResolveMissing: !keepDoc });
    let rasterWarning = null;
    try {

    // ---- Tile raster background ----
    // Walk Leaflet map._layers to find all currently-visible TileLayer instances
    const tileLayerOrder = (layer, fallback) => {
      const paneName = layer.options?.pane || "tilePane";
      const pane = typeof map?.getPane === "function" ? map.getPane(paneName) : null;
      const paneZ = Number.parseInt(pane?.style?.zIndex || "", 10);
      const optionZ = Number.parseInt(layer.options?.zIndex ?? "", 10);
      const zIndex = Number.isFinite(optionZ)
        ? optionZ
        : Number.isFinite(paneZ)
          ? paneZ
          : paneName === "tilePane"
            ? 200
            : 0;
      return { zIndex, id: Number(layer._leaflet_id || fallback) };
    };
    const tileLayerDefs = Object.values(map._layers || {})
      .filter((l) => typeof l._url === "string" && l._url)
      .filter((l) => typeof map?.hasLayer !== "function" || map.hasLayer(l))
      .map((l, index) => {
        const opacity = Number(l.options?.opacity ?? 1);
        return {
          urlTemplate: l._url,
          maxNativeZoom: l.options?.maxNativeZoom ?? l.options?.maxZoom ?? 18,
          opacity: Math.max(0, Math.min(1, Number.isFinite(opacity) ? opacity : 1)),
          ...tileLayerOrder(l, index),
        };
      })
      .filter((l) => l.opacity > 0.01)
      .sort((a, b) => (a.zIndex - b.zIndex) || (a.id - b.id));

    // Standard XYZ tile coordinate helpers
    const latLngToTileXY = (lat, lng, z) => {
      const n = 1 << z;
      const lr = lat * Math.PI / 180;
      return [
        Math.floor((lng + 180) / 360 * n),
        Math.max(0, Math.min(n - 1, Math.floor((1 - Math.log(Math.tan(lr) + 1 / Math.cos(lr)) / Math.PI) / 2 * n))),
      ];
    };
    const tileTopLeft = (tx, ty, z) => {
      const n = 1 << z;
      return [
        Math.atan(Math.sinh(Math.PI * (1 - 2 * ty / n))) * 180 / Math.PI,
        tx / n * 360 - 180,
      ];
    };
    const buildTileUrl = (tmpl, x, y, z) =>
      tmpl.replace("{z}", z).replace("{x}", x).replace("{y}", y)
        .replace("{s}", ["a", "b", "c"][Math.abs(x + y) % 3]).replace("{r}", "");
    const fetchTileB64 = async (url) => {
      try {
        const r = await fetch(url, { mode: "cors" });
        if (!r.ok) return null;
        const blob = await r.blob();
        return await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result.split(",")[1]);
          reader.onerror = rej;
          reader.readAsDataURL(blob);
        });
      } catch { return null; }
    };

    // Auto-pick zoom: target ~12 tiles across the LARGER of the lon/lat extents, capped
    // 14-22. Sizing off lon alone (as this used to) breaks down for a marina whose selection
    // is much taller than it is wide (a long north-south seawall/channel, common along a
    // coastline) -- it'd pick a zoom fine enough for the short lon axis, then blow the tile
    // budget on the long lat axis with perfectly valid geometry (AIM: a correct, tight
    // ~110m x ~520m bounding box needed 1088 tiles at the lon-only zoom, well past the
    // 1000-tile abort below -- whose message wrongly points at bad/null-island geometry for
    // this failure mode; nothing was actually wrong with the data). The high end used to cap
    // at 20, which was well short of what a compact, high-resolution surface (e.g. a 0.1ft-
    // cell multi-band surface -- see the Surfaces tool) needs to render each cell crisply on
    // a small marina's tight extent -- the formula wants zoom 23 for a ~120ft-wide marina,
    // and got clamped to 20, producing visibly blocky tiles. 22 matches the `zoom: 22`
    // maxNativeZoom already declared for qimera-cog candidates (see
    // listSurfaceDeliverableCandidates) -- z below already gets Math.min'd against that per
    // layer, so a higher cap here would just be clamped there anyway.
    const lngRange = maxLng - minLng || 0.001;
    const latRange = maxLat - minLat || 0.001;
    const spanDeg = Math.max(lngRange, latRange);
    const tileZoom = Math.max(14, Math.min(22, Math.round(Math.log2(12 * 360 / spanDeg))));

    const tileImages = [];
    if (tileLayerDefs.length) {
      for (let layerIndex = 0; layerIndex < tileLayerDefs.length; layerIndex++) {
        const { urlTemplate, maxNativeZoom, opacity } = tileLayerDefs[layerIndex];
        let fetched = 0;
        let okCount = 0;
        const z = Math.min(tileZoom, maxNativeZoom);
        const tileCount = 1 << z;
        const rotationPad = Math.abs(Math.sin(mapBearingRad)) > 0.05 ? 3 : 1;
        const [rawTxMin, rawTyMin] = latLngToTileXY(maxLat, minLng, z);
        const [rawTxMax, rawTyMax] = latLngToTileXY(minLat, maxLng, z);
        const txMin = rawTxMin - rotationPad;
        const txMax = rawTxMax + rotationPad;
        const tyMin = Math.max(0, rawTyMin - rotationPad);
        const tyMax = Math.min(tileCount - 1, rawTyMax + rotationPad);
        const totalTiles = (txMax - txMin + 1) * (tyMax - tyMin + 1);
        if (totalTiles > 1000) {
          alert(`PDF plot aborted: tile fetch would require ${totalTiles} tiles (limit 1000). The polygon bounds may include bad geometry. Check for stray or null-island coordinates.`);
          return;
        }
        const layerStatus = tileLayerDefs.length > 1 ? ` layer ${layerIndex + 1}/${tileLayerDefs.length}` : "";
        setStatus(`Fetching tiles${layerStatus}... 0 / ${totalTiles}`);

        for (let tx = txMin; tx <= txMax; tx++) {
          for (let ty = tyMin; ty <= tyMax; ty++) {
            const url = buildTileUrl(urlTemplate, tx, ty, z);
            const b64 = await fetchTileB64(url);
            fetched++;
            setStatus(`Fetching tiles${layerStatus}... ${fetched} / ${totalTiles}`);
            if (!b64) continue;
            okCount++;

            const [tlLat, tlLng] = tileTopLeft(tx, ty, z);
            const [brLat, brLng] = tileTopLeft(tx + 1, ty + 1, z);
            const ix = sx(toMx(tlLng));
            const iy = sy(toMy(tlLat));
            const iw = sx(toMx(brLng)) - ix;
            const ih = sy(toMy(brLat)) - iy;
            const opacityAttr = opacity < 0.995 ? ` opacity="${opacity.toFixed(3)}"` : "";
            tileImages.push(`<image x="${ix.toFixed(1)}" y="${iy.toFixed(1)}" width="${Math.max(0, iw).toFixed(1)}" height="${Math.max(0, ih).toFixed(1)}" href="data:image/png;base64,${b64}" preserveAspectRatio="none"${opacityAttr}/>`);
          }
        }
        // The tile proxy answers 404 for tiles outside a COG's footprint and fetchTileB64
        // swallows every non-OK response, so a raster that doesn't cover this plot's extent
        // (or whose tiles are failing outright) used to vanish from the page without a word.
        // Surface it: the caller folds this into the deliverables status / a toast.
        if (_plotLayerSnapshot.mosaicUrl && urlTemplate === _plotLayerSnapshot.mosaicUrl && okCount === 0) {
          rasterWarning = `survey raster returned no tiles over this plot's extent${deliverableRegion ? ` (${deliverableRegion.label})` : ""} -- check that the linked surface actually covers these slips`;
          app.log("export.plot.raster_missing", { marina: marinaLabel, region: deliverableRegion?.label || null, tiles: totalTiles, zoom: z, url: urlTemplate.slice(0, 160) });
        }
      }
    }

    setStatus("Building SVG…");

    // Built fresh here (not reused from multislipColorMap) so the export doesn't depend on
    // renderGeometry() having last run in multislip mode -- same golden-angle spacing so
    // sequential IDs like MS-001/MS-002 stay visually distinct (see assignDistinctColorsByIndex).
    const exportMultislipColorMap = mode === "multislip"
      ? assignDistinctColorsByIndex(
        [...new Set(polysWithRecords.map(({ record }) => String(record?.multislip_id || "").trim()).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })),
      )
      : null;
    // Built fresh here (not reused from regionColorMap) for the same reason as
    // exportMultislipColorMap above -- the export shouldn't depend on renderGeometry()
    // having last run in region mode.
    const exportRegionColorMap = mode === "region"
      ? assignDistinctColorsByIndex(
        [...new Set(polysWithRecords.map(({ record }) => String(record?.region || "").trim() || "unassigned"))]
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })),
      )
      : null;
    // Same independence rationale as exportRegionColorMap above.
    const exportMarinaNameColorMap = mode === "marina_name"
      ? assignDistinctColorsByIndex(
        [...new Set(polysWithRecords.map(({ record }) => String(record?.marina_name || "").trim() || "unassigned"))]
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })),
      )
      : null;
    // Same independence rationale as exportRegionColorMap above. assignDockColors (not
    // assignDistinctColorsByIndex) so the X dock prints red here too, same as on the map.
    const exportDockColorMap = mode === "dock"
      ? assignDockColors(
        [...new Set(polysWithRecords.map(({ record }) => String(record?.dock_name || "").trim() || "unassigned"))]
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })),
      )
      : null;
    // Computed from the full committed set (not polysWithRecords) -- this is a structural QC
    // check, so it must not depend on this export's region scope or on-screen visibility filter.
    const exportMultislipIntegrityStatusMap = mode === "multislip_integrity" ? computeMultislipIntegrityStatus() : null;
    // Same full-committed-set independence as exportMultislipIntegrityStatusMap above.
    const exportOverlappingSlipIds = mode === "duplicate_slips" ? computeOverlappingSlipIds() : null;
    // Same full-committed-set independence -- a slip_id collision with a slip outside this
    // export's region/visibility scope still prints as a duplicate on the plot, so it has to
    // be checked against everything, not just what's on this particular page.
    const exportDuplicateSlipIdSet = mode === "duplicate_slip_id" ? computeDuplicateSlipIdSet() : null;
    // Built from the full record set (not polysWithRecords) so numeric bins and value colors
    // match the map exactly, whatever this export's region scope.
    const exportAttributeColoring = mode === "attribute" ? buildAttributeColoring(records) : null;

    // Color lookup (mirrors renderGeometry)
    const getColor = (poly, record) => {
      if (mode === "confidence") return confidenceColor(record?.confidence_score);
      if (mode === "space_type") {
        const st = String(record?.space_type || "").trim() || "unknown";
        return SPACE_TYPE_COLOR_MAP[st] ?? paletteColorForKey(st);
      }
      if (mode === "dimension") return dimensionInfoForPolygon(poly).color;
      if (mode === "orientation_qc") return qcOrientationInfoForPolygon(poly).color;
      if (mode === "multislip") {
        const mid = String(record?.multislip_id || "").trim();
        return mid ? (exportMultislipColorMap.get(mid) ?? "#38bdf8") : "#64748b";
      }
      if (mode === "region") {
        const region = String(record?.region || "").trim() || "unassigned";
        return exportRegionColorMap.get(region) ?? "#38bdf8";
      }
      if (mode === "marina_name") {
        const name = String(record?.marina_name || "").trim() || "unassigned";
        return exportMarinaNameColorMap.get(name) ?? "#38bdf8";
      }
      if (mode === "dock") {
        const dock = String(record?.dock_name || "").trim() || "unassigned";
        return exportDockColorMap.get(dock) ?? "#38bdf8";
      }
      if (mode === "multislip_integrity") {
        return MULTISLIP_INTEGRITY_INFO[multislipIntegrityKeyForRecord(record, exportMultislipIntegrityStatusMap)].color;
      }
      if (mode === "duplicate_slips") {
        return DUPLICATE_SLIP_INFO[duplicateSlipKeyForRecord(record, exportOverlappingSlipIds)].color;
      }
      if (mode === "duplicate_slip_id") {
        return DUPLICATE_SLIP_ID_INFO[duplicateSlipIdKeyForRecord(record, exportDuplicateSlipIdSet)].color;
      }
      if (mode === "attribute") return exportAttributeColoring.colorFor(record);
      return "#38bdf8";
    };

    // Legend
    const legendMap = new Map();
    for (const { poly, record } of polysWithRecords) {
      const color = getColor(poly, record);
      const key = mode === "space_type" ? (String(record?.space_type || "").trim() || "unknown")
        : mode === "dimension" ? dimensionInfoForPolygon(poly).label
        : mode === "orientation_qc" ? qcOrientationInfoForPolygon(poly).label
        : mode === "confidence" ? String(record?.confidence_score || "—")
        : mode === "multislip" ? (String(record?.multislip_id || "").trim() || null)
        : mode === "region" ? (String(record?.region || "").trim() || "unassigned")
        : mode === "marina_name" ? (String(record?.marina_name || "").trim() || "unassigned")
        : mode === "dock" ? (String(record?.dock_name || "").trim() || "unassigned")
        : mode === "multislip_integrity" ? MULTISLIP_INTEGRITY_INFO[multislipIntegrityKeyForRecord(record, exportMultislipIntegrityStatusMap)].label
        : mode === "duplicate_slips" ? (duplicateSlipKeyForRecord(record, exportOverlappingSlipIds) === "overlapping" ? DUPLICATE_SLIP_INFO.overlapping.label : null)
        : mode === "duplicate_slip_id" ? (duplicateSlipIdKeyForRecord(record, exportDuplicateSlipIdSet) === "duplicate" ? DUPLICATE_SLIP_ID_INFO.duplicate.label : null)
        : mode === "attribute" ? exportAttributeColoring.keyFor(record)
        : null;
      if (key && !legendMap.has(key)) legendMap.set(key, color);
    }
    const legendEntries = [...legendMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));

    // Detect basemap mode for panel theming
    const isDark = document.querySelector('[data-map-mode="light"]') === null;
    const panelBg     = isDark ? "#0f172a" : "#f8fafc";
    const panelBorder = isDark ? "#334155" : "#cbd5e1";
    const panelText   = isDark ? "#e2e8f0" : "#1e293b";
    const panelSub    = isDark ? "#94a3b8" : "#64748b";
    const panelAccent = isDark ? "#60a5fa" : "#2563eb";
    const PF = "Arial,sans-serif";      // panel font family
    const PL = 16;                       // panel line height
    const PP = 10;                       // panel padding

    // Metadata — collected before layout so we know panel height
    const marinaCode = String(filterProjectId || records[0]?.marina_id || "—");
    const projectName = app?.services?.context?.getActiveProject?.()?.name
      || records.find((r) => String(r.marina_id || "") === marinaCode)?.marina_name
      || records[0]?.marina_name
      || state.marinaName
      || marinaCode;
    const clientName = app?.services?.context?.getActiveClient?.()?.name || "";
    const exportDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    const oosCount = polysWithRecords.filter(({ record }) =>
      record?.out_of_service === true || String(record?.out_of_service ?? "") === "1").length;
    const typeCountMap = new Map();
    for (const { record } of polysWithRecords) {
      const st = String(record?.space_type || "").trim() || "Unknown";
      typeCountMap.set(st, (typeCountMap.get(st) || 0) + 1);
    }
    const typeCounts = [...typeCountMap.entries()].sort((a, b) => b[1] - a[1]);

    // Fixed-height title block below the map — nothing in this band can collide with map elements.
    const TITLE_H = 100;
    const TOTAL_H = SVG_H + TITLE_H;

    const xmlEsc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const rotateSvgPoint = (x, y) => {
      const dx = Number(x) - mapCenterX;
      const dy = Number(y) - mapCenterY;
      return {
        x: mapCenterX + (dx * cosBearing) - (dy * sinBearing),
        y: mapCenterY + (dx * sinBearing) + (dy * cosBearing),
      };
    };
    const lines = [];
    lines.push(`<?xml version="1.0" encoding="UTF-8"?>`);
    lines.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${SVG_W}" height="${TOTAL_H}" viewBox="0 0 ${SVG_W} ${TOTAL_H}">`);
    lines.push(`<rect width="${SVG_W}" height="${TOTAL_H}" fill="${isDark ? "#0f172a" : "#f8fafc"}"/>`);

    lines.push(`<g>`);
    lines.push(`<rect width="${SVG_W}" height="${SVG_H}" fill="${isDark ? "#0f172a" : "#f8fafc"}"/>`);
    lines.push(`<g transform="rotate(${mapBearingDeg.toFixed(3)} ${mapCenterX.toFixed(3)} ${mapCenterY.toFixed(3)})">`);

    // Raster tile images (bottom layer)
    lines.push(...tileImages);
    lines.push(`</g>`);

    // Wash the basemap back as an unrotated sheet overlay; rotating this creates large diagonal panels.
    if (tileImages.length) lines.push(`<rect width="${SVG_W}" height="${SVG_H}" fill="${isDark ? "#000000" : "#ffffff"}" fill-opacity="${isDark ? "0.12" : "0.34"}"/>`);
    lines.push(`<g transform="rotate(${mapBearingDeg.toFixed(3)} ${mapCenterX.toFixed(3)} ${mapCenterY.toFixed(3)})">`);

    // Polygon fills
    for (const { poly, record, pts } of polyData) {
      const color = getColor(poly, record);
      const pointsStr = pts.map((p) => `${sx(p.mx).toFixed(2)},${sy(p.my).toFixed(2)}`).join(" ");
      lines.push(`<polygon points="${pointsStr}" fill="${xmlEsc(color)}" fill-opacity="0.38" stroke="${xmlEsc(color)}" stroke-width="1.5" stroke-linejoin="round"/>`);
    }

    // Slip labels (outside the bearing-rotation group so they stay screen-aligned)
    {
      lines.push(`</g>`);
      for (const { record, pts, cx, cy } of polyData) {
        const slipId = String(record?.slip_id || record?.measurement_id || "");
        if (!slipId) continue;
        const rotatedPts = pts.map((p) => rotateSvgPoint(sx(p.mx), sy(p.my)));
        // Find longest edge — its angle drives label rotation so text runs parallel to the slip length
        let longestLen = -1;
        let longestEdgeDeg = 0;
        for (let i = 0; i < rotatedPts.length; i++) {
          const a = rotatedPts[i];
          const b = rotatedPts[(i + 1) % rotatedPts.length];
          const edgeLen = Math.hypot(b.x - a.x, b.y - a.y);
          if (edgeLen > longestLen) {
            longestLen = edgeLen;
            longestEdgeDeg = Math.atan2(b.y - a.y, b.x - a.x) * 180 / Math.PI;
          }
        }
        if (longestEdgeDeg > 90) longestEdgeDeg -= 180;
        else if (longestEdgeDeg <= -90) longestEdgeDeg += 180;
        const edgeRad = longestEdgeDeg * Math.PI / 180;
        const ex = Math.cos(edgeRad), ey = Math.sin(edgeRad);
        const px = -ey, py = ex;
        const alongs = rotatedPts.map((p) => p.x * ex + p.y * ey);
        const perps  = rotatedPts.map((p) => p.x * px + p.y * py);
        const midAlong = (Math.min(...alongs) + Math.max(...alongs)) / 2;
        const midPerp  = (Math.min(...perps)  + Math.max(...perps))  / 2;
        const perpSpan = Math.max(...perps) - Math.min(...perps);
        // Font size must fit both the run direction (longestLen vs label width) and the
        // cross-axis (perpSpan, i.e. slip width) — otherwise narrow slips get oversized text.
        const fitByLength = (longestLen * 0.82) / Math.max(1, slipId.length * 0.58);
        const fitByWidth = perpSpan * 0.72;
        const fs = Math.max(6, Math.min(11, fitByLength, fitByWidth));
        const lx = (midAlong * ex + midPerp * px).toFixed(2);
        const ly = (midAlong * ey + midPerp * py).toFixed(2);
        // svg2pdf.js only honors the legacy "alignment-baseline" attribute (not the modern
        // "dominant-baseline"), so without it text renders alphabetic-baseline and sits
        // visibly above the intended center point — i.e. biased toward the top of the polygon.
        lines.push(`<text x="${lx}" y="${ly}" text-anchor="middle" alignment-baseline="middle" dominant-baseline="middle" font-family="${PF}" font-size="${fs.toFixed(1)}" font-weight="650" fill="#111827" fill-opacity="0.92" transform="rotate(${longestEdgeDeg.toFixed(2)},${lx},${ly})">${xmlEsc(slipId)}</text>`);
      }
    }

    // Top title band — frosted project name across the full width
    lines.push(`<rect x="0" y="0" width="${SVG_W}" height="44" fill="#0f172a" fill-opacity="${isDark ? "0.75" : "0.60"}"/>`);
    lines.push(`<text x="${SVG_W / 2}" y="28" text-anchor="middle" font-family="${PF}" font-size="18" font-weight="bold" fill="#e2e8f0">${xmlEsc(projectName)} — Slip Inventory Plot${deliverableRegion ? ` — ${deliverableRegion.label}` : ""}</text>`);

    lines.push(`</g>`); // end map area group

    // ── Bottom title block ──────────────────────────────────────────────────────
    // Solid band below the map viewport — nothing here can ever overlap map content.
    // Layout (left→right): Project info | Stats | Space-type legend | North arrow
    {
      const ty = SVG_H; // top of title block
      const th = TITLE_H;
      const MID = ty + th / 2;
      const tbBg = isDark ? "#0f172a" : "#1e293b";
      const tbText = "#e2e8f0";
      const tbMuted = "#94a3b8";
      const tbAccent = "#60a5fa";
      const tbBorder = "#334155";

      lines.push(`<rect x="0" y="${ty}" width="${SVG_W}" height="${th}" fill="${tbBg}"/>`);
      lines.push(`<line x1="0" y1="${ty}" x2="${SVG_W}" y2="${ty}" stroke="${tbAccent}" stroke-width="2"/>`);

      // ── Col 1: Project name + client + marina code (x: 0–520) ──
      const c1x = 24;
      lines.push(`<text x="${c1x}" y="${ty + 32}" font-family="${PF}" font-size="18" font-weight="bold" fill="${tbText}">${xmlEsc(projectName)}</text>`);
      const subLine = [clientName, marinaCode, deliverableRegion ? `Region: ${deliverableRegion.label}` : null].filter(Boolean).join("  ·  ");
      if (subLine) lines.push(`<text x="${c1x}" y="${ty + 56}" font-family="${PF}" font-size="11" fill="${tbMuted}">${xmlEsc(subLine)}</text>`);
      lines.push(`<text x="${c1x}" y="${ty + 76}" font-family="${PF}" font-size="10" fill="${tbMuted}">${xmlEsc(exportDate)}</text>`);

      // ── Divider ──
      lines.push(`<line x1="540" y1="${ty + 14}" x2="540" y2="${ty + th - 14}" stroke="${tbBorder}" stroke-width="1"/>`);

      // ── Col 2: Slip stats (x: 556–760) ──
      const c2x = 556;
      lines.push(`<text x="${c2x}" y="${ty + 34}" font-family="${PF}" font-size="28" font-weight="bold" fill="${tbText}">${polysWithRecords.length}</text>`);
      lines.push(`<text x="${c2x}" y="${ty + 56}" font-family="${PF}" font-size="10" fill="${tbMuted}">Total Slips</text>`);
      if (oosCount > 0) {
        lines.push(`<text x="${c2x + 100}" y="${ty + 34}" font-family="${PF}" font-size="22" font-weight="bold" fill="#f59e0b">${oosCount}</text>`);
        lines.push(`<text x="${c2x + 100}" y="${ty + 56}" font-family="${PF}" font-size="10" fill="${tbMuted}">Out of Service</text>`);
      }

      // ── Divider ──
      lines.push(`<line x1="760" y1="${ty + 14}" x2="760" y2="${ty + th - 14}" stroke="${tbBorder}" stroke-width="1"/>`);

      // ── Col 3: Space type legend (x: 776–1700), dots in two rows if many types ──
      {
        const lx0 = 776;
        const dotR = 5;
        const itemW = 130; // px per legend entry
        const cols = Math.ceil(typeCounts.length / 2);
        const rows = typeCounts.length > 1 ? 2 : 1;
        const rowH = rows > 1 ? (th - 28) / 2 : 0;
        typeCounts.forEach(([type, count], i) => {
          const col = i % cols;
          const row = Math.floor(i / cols);
          const ix = lx0 + col * itemW;
          const iy = ty + (rows > 1 ? 26 + row * rowH : th / 2);
          const dotColor = SPACE_TYPE_COLOR_MAP[type] ?? paletteColorForKey(type);
          lines.push(`<circle cx="${ix + dotR}" cy="${iy}" r="${dotR}" fill="${xmlEsc(dotColor)}"/>`);
          lines.push(`<text x="${ix + dotR * 2 + 4}" y="${iy}" font-family="${PF}" font-size="10" fill="${tbText}" dominant-baseline="middle">${xmlEsc(type)}</text>`);
          lines.push(`<text x="${ix + dotR * 2 + 4}" y="${iy + 13}" font-family="${PF}" font-size="9" fill="${tbMuted}" dominant-baseline="middle">${count}</text>`);
        });
      }

      // ── Divider ──
      lines.push(`<line x1="${SVG_W - 90}" y1="${ty + 14}" x2="${SVG_W - 90}" y2="${ty + th - 14}" stroke="${tbBorder}" stroke-width="1"/>`);

      // ── Col 4: North arrow (x: SVG_W-90 to SVG_W) ──
      {
        const ax = SVG_W - 45;
        const ay = ty + th / 2 - 22;
        lines.push(`<g transform="translate(${ax},${ay})" font-family="${PF}">`);
        lines.push(`<g transform="rotate(${mapBearingDeg.toFixed(3)} 0 18)">`);
        lines.push(`<path d="M0 0 L9 31 L0 25 L-9 31 Z" fill="#e2e8f0" stroke="#334155" stroke-width="1" stroke-linejoin="round"/>`);
        lines.push(`<path d="M0 0 L0 25" stroke="#94a3b8" stroke-width="0.8" stroke-linecap="round" opacity="0.8"/>`);
        lines.push(`</g>`);
        lines.push(`<text x="0" y="48" text-anchor="middle" font-family="${PF}" font-size="13" font-weight="700" fill="${tbText}">N</text>`);
        lines.push(`</g>`);
      }
    }

    lines.push(`</svg>`);

    const svgContent = lines.join("\n");
    setStatus("Generating PDF…");

    // Lazy-load jsPDF + svg2pdf only when the button is clicked (keeps initial bundle lean)
    const [{ jsPDF }, { svg2pdf }] = await Promise.all([
      import("jspdf"),
      import("svg2pdf.js"),
    ]);

    // svg2pdf needs the SVG element to be in the live DOM to resolve styles/dimensions
    const offscreen = document.createElement("div");
    offscreen.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;pointer-events:none;";
    offscreen.innerHTML = svgContent;
    const svgEl = offscreen.querySelector("svg");
    document.body.appendChild(offscreen);

    try {
      const orientation = SVG_W > TOTAL_H ? "landscape" : "portrait";
      // pdfDoc lets a caller (see generateAllDeliverables) fold several regions' plots into
      // one multi-page PDF instead of a separate file per region:
      // the first call creates the doc, every later call on the same doc adds a page sized to
      // its own region's SVG before rendering onto it -- addPage's format/orientation args are
      // per-page in jsPDF, so pages don't have to share dimensions.
      const pdf = pdfDoc || new jsPDF({
        unit: "px",
        format: [SVG_W, TOTAL_H],
        orientation,
        hotfixes: ["px_scaling"],
        compress: true,
      });
      if (pdfDoc) pdf.addPage([SVG_W, TOTAL_H], orientation);
      await svg2pdf(svgEl, pdf, { x: 0, y: 0, width: SVG_W, height: TOTAL_H });
      const { projectNumber, code } = currentDeliverableIds(app, filterProjectId);
      // Main region (or the ordinary single-region marina) keeps the plain filename; every
      // other region in a multi-region marina gets "-REGION-SLUG" appended to the code.
      const filenameRegion = deliverableRegion && !deliverableRegion.isMain ? deliverableRegion.label : null;
      const fname = deliverableFilename({ projectNumber, code: code || marinaLabel, kind: "slip_plot", date, region: filenameRegion });
      // keepDoc hands the live jsPDF instance back instead of finalizing it, so the caller can
      // pass it in as pdfDoc for the next region's page and only serialize/save once, at the end.
      if (keepDoc) return { filename: fname, pdf, rasterWarning };
      if (returnBlob) return { filename: fname, blob: pdf.output("blob"), rasterWarning };
      if (rasterWarning) app.ui?.toast?.(`PDF plot: ${rasterWarning}`, { level: "warn" });
      pdf.save(fname);
    } finally {
      document.body.removeChild(offscreen);
    }

    } finally {
      restorePlotBaseLayers(_plotLayerSnapshot);
    }
  }

  async function exportSlipReportPdf() {
    const sourceRows = exportRecordsForTable();
    if (!sourceRows.length) { alert("No slip records to export."); return; }

    const { jsPDF } = await import("jspdf");

    const PW = 792, PH = 612;     // letter landscape, points
    const MX = 36;                 // left/right margin
    const UW = PW - 2 * MX;       // usable width
    const FTR_H = 22;              // footer band
    const COL_H = 32;              // column header row (tall enough for 2-line wrapped labels)
    const ROW_H = 18;              // data row
    const TITLE1_H = 56;           // first-page title block
    const TITLE_N_H = 28;          // subsequent-page compact header
    const COMMENT_W = 220;         // reserved for the fillable Comments column

    const pName = app?.services?.context?.getActiveProject?.()?.name
      || state.marinaName || state.marinaId || "Marina";
    const cName = app?.services?.context?.getActiveClient?.()?.name || "";
    const mCode = String(filterProjectId || records[0]?.marina_id || "");
    const eDate = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    const displayTol = normalizeDisplayToleranceFt(state.displayMeasurementToleranceFt);

    // Create doc first so we can use getTextWidth() for content-driven column sizing
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });

    // Mirror the table's current visible column set (respects user show/hide + order)
    const tableCols = visibleCols(readColCfg()).filter((c) => c.key !== "geometry_wkt");
    const dataW = UW - COMMENT_W;

    // Pre-compute display values once — reused for measurement and rendering
    const displayRows = sourceRows.map((r) => buildDisplayExportRecord(r, displayTol));

    // Measure widest data value per column at 9pt — drives column widths so no cell overflows.
    doc.setFontSize(9);
    doc.setFont(undefined, "normal");
    const dataContentW = tableCols.map((col) => {
      if (BINARY_COLS.has(col.key)) return 16; // checkbox only
      let maxW = 0;
      for (const norm of displayRows) {
        const raw = norm[col.key];
        const v = raw != null && raw !== "null" && raw !== "undefined" ? String(raw) : "";
        if (v) maxW = Math.max(maxW, doc.getTextWidth(v));
      }
      return Math.ceil(maxW) + 10; // 5pt padding each side
    });

    // Measure minimum column width so the header label fits in ≤2 lines at 6pt bold.
    // min = ceil(fullLabelWidth / 2) + 8pt padding ensures each line ≤ half the label.
    doc.setFontSize(6);
    doc.setFont(undefined, "bold");
    const headerMinW = tableCols.map((col) => {
      const fullW = doc.getTextWidth(col.label.toUpperCase());
      return Math.ceil(fullW / 2) + 8;
    });

    // Each column floor = max(data content need, header 2-line need)
    const contentW = tableCols.map((_, i) => Math.max(dataContentW[i], headerMinW[i]));

    // Scale proportionally so all data columns together fill dataW exactly
    const contentTotal = contentW.reduce((s, w) => s + w, 0);
    const scaledCols = tableCols.map((col, i) => ({
      key: col.key,
      label: col.label,
      w: Math.max(16, Math.round(contentW[i] * dataW / contentTotal)),
      align: NUMERIC_COLS.has(col.key) ? "right" : "center",
      isBinary: BINARY_COLS.has(col.key),
    }));
    // Absorb rounding error into last column
    const scaledTotal = scaledCols.reduce((s, c) => s + c.w, 0);
    if (scaledCols.length) scaledCols[scaledCols.length - 1].w += dataW - scaledTotal;

    const cols = [
      ...scaledCols,
      { key: "_comments", label: "Comments", w: COMMENT_W, align: "left", isBinary: false },
    ];

    let pageNum = 0;
    let curY = 0;
    let rowOnPage = 0;

    function startPage() {
      const isFirst = pageNum === 0;
      if (!isFirst) doc.addPage("letter", "landscape");
      pageNum++;
      rowOnPage = 0;

      const TH = isFirst ? TITLE1_H : TITLE_N_H;

      // Title band
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, PW, TH, "F");
      doc.setFillColor(59, 130, 246);
      doc.rect(0, TH - 2, PW, 2, "F");

      doc.setTextColor(226, 232, 240);
      if (isFirst) {
        doc.setFontSize(15);
        doc.setFont(undefined, "bold");
        doc.text(`${pName} — Slip ID Report`, MX, 24);
        doc.setFont(undefined, "normal");
        doc.setFontSize(9);
        doc.setTextColor(148, 163, 184);
        doc.text([cName, mCode, eDate].filter(Boolean).join("  ·  "), MX, 43);
      } else {
        doc.setFontSize(10);
        doc.setFont(undefined, "bold");
        doc.text(`${pName} — Slip ID Report`, MX, 18);
      }

      // Column header row — labels wrap to 2 lines at 6pt to fit narrow columns
      doc.setFillColor(30, 41, 59);
      doc.rect(MX, TH, UW, COL_H, "F");
      doc.setFontSize(6);
      doc.setFont(undefined, "bold");
      doc.setTextColor(226, 232, 240);
      const HDR_LINE_H = 7.2;
      let cx = MX;
      for (const col of cols) {
        const lines = doc.splitTextToSize(col.label.toUpperCase(), col.w - 4);
        const blockH = lines.length * HDR_LINE_H;
        const ty = TH + (COL_H - blockH) / 2 + 6;
        const halign = col.align === "right" ? "right" : col.align === "center" ? "center" : "left";
        const tx = col.align === "right" ? cx + col.w - 4
                 : col.align === "center" ? cx + col.w / 2
                 : cx + 5;
        lines.forEach((line, i) => {
          doc.text(line, tx, ty + i * HDR_LINE_H, { align: halign });
        });
        cx += col.w;
      }
      curY = TH + COL_H;
    }

    startPage();

    for (let _ri = 0; _ri < sourceRows.length; _ri++) {
      const record = sourceRows[_ri];
      if (curY + ROW_H > PH - FTR_H) startPage();

      // Alternating row background
      if (rowOnPage % 2 === 1) {
        doc.setFillColor(248, 250, 252);
        doc.rect(MX, curY, UW, ROW_H, "F");
      }

      // Bottom border
      doc.setDrawColor(203, 213, 225);
      doc.setLineWidth(0.35);
      doc.line(MX, curY + ROW_H, MX + UW, curY + ROW_H);

      // Cell values
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.setTextColor(30, 41, 59);
      const norm = displayRows[_ri];
      let cx = MX;
      for (const col of cols) {
        if (col.key === "_comments") {
          const tf = new doc.AcroFormTextField();
          tf.fieldName = `c_${String(record.measurement_id || record.slip_id || rowOnPage).replace(/\W/g, "_")}`;
          tf.x = cx + 3;
          tf.y = curY + 2;
          tf.width = col.w - 6;
          tf.height = ROW_H - 4;
          tf.maxFontSize = 5;
          tf.doNotScroll = true;
          doc.addField(tf);
        } else {
          if (col.isBinary) {
            const raw = record[col.key];
            const _rawLower = String(raw || "").toLowerCase();
            const isTrue = _rawLower === "yes" || _rawLower === "true" || _rawLower === "1" || raw === true || raw === 1;
            const cbSize = Math.min(11, ROW_H - 5);
            const cb = new doc.AcroFormCheckBox();
            cb.fieldName = `cb_${col.key}_${String(record.measurement_id || record.slip_id || rowOnPage).replace(/\W/g, "_")}`;
            cb.x = cx + col.w / 2 - cbSize / 2;
            cb.y = curY + ROW_H / 2 - cbSize / 2;
            cb.width = cbSize;
            cb.height = cbSize;
            cb.appearanceState = isTrue ? "On" : "Off";
            doc.addField(cb);
          } else {
            const raw = norm[col.key] ?? record[col.key];
            const val = raw != null && raw !== "null" && raw !== "undefined" ? String(raw) : "";
            if (val) {
              const tx = col.align === "right" ? cx + col.w - 4
                       : col.align === "center" ? cx + col.w / 2
                       : cx + 5;
              doc.text(val, tx, curY + ROW_H / 2 + 3, {
                align: col.align === "right" ? "right" : col.align === "center" ? "center" : "left",
              });
            }
          }
        }
        cx += col.w;
      }

      curY += ROW_H;
      rowOnPage++;
    }

    // Footers (go back through all pages now that total is known)
    const totalP = doc.getNumberOfPages();
    for (let p = 1; p <= totalP; p++) {
      doc.setPage(p);
      doc.setFillColor(15, 23, 42);
      doc.rect(0, PH - FTR_H, PW, FTR_H, "F");
      doc.setFontSize(8);
      doc.setFont(undefined, "normal");
      doc.setTextColor(148, 163, 184);
      doc.text("Hydrotools  ·  Slip Inventory Report", MX, PH - 7);
      doc.text(`Page ${p} of ${totalP}`, PW - MX, PH - 7, { align: "right" });
    }

    const fname = `${exportBaseName()}_slip_report_${exportDateStamp()}.pdf`;
    doc.save(fname);
  }

  // ── Generate Deliverables: one click, all 5 standard kinds ──────────────────────────────
  // slip_plot/slip_inventory_geojson/slip_inventory_csv are genuinely generated here and now,
  // from live data -- built, uploaded to the project's DAM tree via a presigned PUT (same
  // mechanism the DAM's own "+ Upload" button uses), then registered in the delivery manifest
  // with the correct name and kind tag. slam_tif is generated too: the user picks a Tile
  // Pyramid (not a raw .tif), and this exports its underlying COG to GeoTIFF via the same
  // /api/qimera/cog-export endpoint the DAM's manual "Export raster" button uses, uploads the
  // result into geotiff-files, and registers it -- no more manual export-then-reupload round
  // trip. slam_las is the one kind that truly can't be generated -- it's raw survey data
  // uploaded by field crews -- so it only locates the most recently modified existing file in
  // the project's las-files DAM category and registers *that*, pointer-only, same as a manual
  // "Send to Deliverables" click would.

  async function presignAndUploadDeliverable({ code, category, filename, blob, contentType }) {
    const presign = await requestJson("/api/project/asset/presign-upload", {
      method: "POST",
      body: { code, category, filename, contentType },
    });
    const putResponse = await fetch(presign.url, { method: "PUT", headers: { "Content-Type": contentType }, body: blob });
    if (!putResponse.ok) throw new Error(`Upload failed (${putResponse.status})`);
    return presign.key;
  }

  async function registerDeliverableEntry({ code, category, sourceKey, filename, size, kind, folder }) {
    const result = await requestJson("/api/project/deliverables/link", {
      method: "POST",
      body: { code, folder, subfolder: category, filename, sourceKey, size, kind },
    });
    // The DAM (image-generator tool) renders the deliverables checklist and category trees
    // from its own cached state, which it has no other way to know just went stale -- this is
    // the cross-tool refresh signal, same pattern as hydrotools:project-changed.
    document.dispatchEvent(new CustomEvent("hydrotools:deliverable-registered", { detail: { code, category } }));
    return result;
  }

  // DAM files in a category matching an extension pattern, newest first -- used to populate
  // the Generate Deliverables picker so the user chooses which SLAM.tif/.las to register,
  // rather than silently auto-picking when a marina has more than one candidate on hand.
  async function listDamCandidates(code, category, extRegex) {
    const data = await requestJson(`/api/project/assets?code=${encodeURIComponent(code)}&category=${encodeURIComponent(category)}`);
    const files = (Array.isArray(data.files) ? data.files : []).filter((f) => extRegex.test(String(f.name || f.key || "")));
    files.sort((a, b) => new Date(b.lastModified || 0) - new Date(a.lastModified || 0));
    return files;
  }

  // Polls a mosaic2d export job (see getMosaicDeliverableSource / generateAllDeliverables)
  // until it lands on a terminal state -- same job queue/shape the surfaces tool's own
  // "Export → DAM" button polls, just awaited straight through instead of driving a
  // progress bar.
  async function pollMosaicExportJob(jobId, { intervalMs = 2500, timeoutMs = 20 * 60 * 1000 } = {}) {
    const start = Date.now();
    for (;;) {
      const data = await requestJson(`/api/point-cloud/generate-tiles/jobs/${encodeURIComponent(jobId)}`);
      const job = data?.job;
      if (job?.status === "completed" || job?.status === "failed") return job;
      if (Date.now() - start > timeoutMs) throw new Error("mosaic GeoTIFF export timed out");
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  // Same polling shape as pollMosaicExportJob, for /api/project/las/combine (see
  // ensureUncompressedLasFile below).
  async function pollLasCombineJob(jobId, { intervalMs = 2000, timeoutMs = 20 * 60 * 1000 } = {}) {
    const start = Date.now();
    for (;;) {
      const data = await requestJson(`/api/project/las/combine/${encodeURIComponent(jobId)}`);
      if (data?.status === "completed" || data?.status === "failed") return data;
      if (Date.now() - start > timeoutMs) throw new Error("LAZ → LAS conversion timed out");
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
  }

  // The slam_las deliverable must always be a genuine uncompressed .las, whatever the selected
  // DAM candidate's own encoding is -- a raw field .laz (or a pre-2026-09-22 Combine LAS
  // leftover, see safeharbor-combine-las-and-surface-fixes) gets run through the same
  // decompress-and-rewrite PDAL pipeline Combine LAS already uses (with a single sourceKey, so
  // no merge happens), rather than shipping compressed bytes under a name that claims otherwise.
  async function ensureUncompressedLasFile(code, lasFile) {
    if (!/\.laz$/i.test(lasFile.name || lasFile.key || "")) return { key: lasFile.key, size: lasFile.size };
    const started = await requestJson("/api/project/las/combine", { method: "POST", body: { code, sourceKeys: [lasFile.key] } });
    const job = await pollLasCombineJob(started.jobId);
    if (job?.status !== "completed") throw new Error(job?.error || "LAZ → LAS conversion failed");
    // Job status doesn't carry the output size -- look it up from the DAM listing now that it exists.
    const candidates = await listDamCandidates(code, "las-files", /\.las$/i);
    const converted = candidates.find((f) => f.key === job.targetKey);
    return { key: job.targetKey, size: converted?.size ?? lasFile.size };
  }

  function formatDeliverableBytes(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return "";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let size = n;
    let idx = 0;
    while (size >= 1024 && idx < units.length - 1) { size /= 1024; idx += 1; }
    return `${size.toFixed(idx === 0 || size >= 10 ? 0 : 1)} ${units[idx]}`;
  }

  function formatDeliverableDate(value) {
    const d = value ? new Date(value) : null;
    return d && Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
  }

  function projectEpsgCode(code) {
    const project = app?.services?.context?.getAllProjects?.()?.find(
      (p) => String(p.code || "").toUpperCase() === String(code || "").toUpperCase()
    );
    const match = /(\d+)/.exec(String(project?.horizontalCRS || ""));
    return match ? match[1] : "";
  }

  // The marina's acquisition date (set in the client portal / Project Portfolio) -- used to
  // date-stamp deliverable *filenames*, since those need to reflect when the survey data is
  // *from*. The deliverables *folder* is dated separately, by when the deliverables were
  // generated (see generateAllDeliverables) -- that's when someone actually packaged them,
  // which is what you'd look up a delivery run by. Throws if no acquisition date has been
  // recorded yet -- generateAllDeliverables calls this before anything is built/uploaded, so
  // the whole run stops with an actionable message instead of silently dating the files with
  // today (i.e. deliverable-creation date, not acquisition date -- the bug this replaces).
  function projectAcquisitionDate(code) {
    const project = app?.services?.context?.getAllProjects?.()?.find(
      (p) => String(p.code || "").toUpperCase() === String(code || "").toUpperCase()
    );
    const acquired = String(project?.acquisitionDate || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(acquired)) {
      throw new Error(`${code} has no acquisition date set -- mark the Acquisition milestone Complete (auto-fills it), or set the "Acquired" date directly in the client portal, before generating deliverables.`);
    }
    return acquired;
  }

  // deliverableFilename's date param needs an actual Date -- parsed via explicit
  // year/month/day components (not `new Date(ymd)`) so it lands on the intended calendar
  // day in local time regardless of the browser's timezone offset from UTC.
  function dateFromYmd(ymd) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
  }

  // A slip's region bucket for Generate Deliverables purposes -- blank/unset region text
  // falls into "unassigned", same convention already used by the on-screen "Color slips by:
  // Region" mode (see isRecordVisible/mode === "region" above) and the Lasso tool's region
  // toggle, so a marina that's never touched the Region field just has one bucket.
  function deliverableRegionKeyForRecord(record) {
    return String(record?.region || "").trim() || "unassigned";
  }

  function deliverableRegionLabelForKey(key) {
    return key === "unassigned" ? "Unassigned" : key;
  }

  function committedVisibleSlipRecords() {
    return (state.slipGeometryPolygons || [])
      .filter((poly) => poly.committed)
      .map((poly) => findRecordForPolygon(poly))
      .filter((record) => record != null && isRecordVisible(record));
  }

  // Regions present in this marina's committed/visible slips, largest first (the openDeliverablesPicker
  // default for "Main") with an alphabetical tiebreak for determinism. A marina that's never used the
  // Region field comes back as a single "Unassigned" bucket -- generateAllDeliverables treats a
  // single-region result as the plain, unsuffixed single-package case exactly as before.
  function enumerateDeliverableRegions() {
    const counts = new Map();
    for (const record of committedVisibleSlipRecords()) {
      const key = deliverableRegionKeyForRecord(record);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([key, count]) => ({ key, label: deliverableRegionLabelForKey(key), count }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  }

  async function generateAllDeliverables({ regionAssignments = null } = {}) {
    const code = String(filterProjectId || state.marinaId || "").trim().toUpperCase();
    if (!code) { status = "Select a marina before generating deliverables."; render(); return; }
    // Folder = when this delivery run was packaged (so a delivery run is easy to look up by
    // when someone actually clicked the button). Filenames = the marina's acquisition date
    // (so the files themselves reflect when the survey data is from), independent of the
    // folder they land in.
    const folder = new Date().toISOString().slice(0, 10);
    // projectAcquisitionDate throws if unset/invalid, so dateFromYmd here always succeeds --
    // no fallback date, since that would silently reintroduce the bug this replaces.
    const fileDate = dateFromYmd(projectAcquisitionDate(code));
    const { projectNumber } = currentDeliverableIds(app, code);
    const results = [];
    const generateStart = Date.now();

    // A marina with only one region (the ordinary case -- Region is a rarely-used field) gets
    // exactly the original single unsuffixed package; regionAssignments only carries more than
    // one entry when openDeliverablesPicker found multiple regions among the committed slips.
    // geotiffSurface is per-assignment (see openDeliverablesPicker's per-region picker) so
    // distinct regions can link distinct built surfaces -- the single-region call site below
    // fills it with the one auto-resolved candidate, same as before.
    const assignments = Array.isArray(regionAssignments) && regionAssignments.length
      ? regionAssignments
      : [{ key: null, label: null, isMain: true, lasFile: null, geotiffSurface: await getMosaicDeliverableSource(code).catch(() => null) }];
    const multiRegion = assignments.length > 1;

    // Cache of surface key -> { damKey, sizeBytes }, so two regions linking the *same* surface
    // export it once and both register a pointer at that one physical file (registerDeliverableEntry
    // links a sourceKey under a filename, it doesn't copy -- same as slam_las already does);
    // regions linking different surfaces each get their own export.
    const mosaicExports = new Map();
    // Same idea, keyed by the ORIGINAL selected lasFile.key: two regions sharing the same .laz
    // source only get decompressed once (see ensureUncompressedLasFile), not once per region.
    const lasConversions = new Map();

    taskStatus("Generating slip inventory (GeoJSON)…", 5);
    render();
    try {
      const built = buildSlipGeoJsonBlob(fileDate);
      const sourceKey = await presignAndUploadDeliverable({ code, category: "vector-files", filename: built.filename, blob: built.blob, contentType: "application/geo+json" });
      await registerDeliverableEntry({ code, category: "vector-files", sourceKey, filename: built.filename, size: built.blob.size, kind: "slip_inventory_geojson", folder });
      results.push({ kind: "slip_inventory_geojson", ok: true });
    } catch (err) {
      results.push({ kind: "slip_inventory_geojson", ok: false, error: err?.message || String(err) });
    }

    taskStatus("Generating slip inventory (CSV)…", 15);
    render();
    try {
      const built = buildSlipCsvBlob(fileDate);
      const sourceKey = await presignAndUploadDeliverable({ code, category: "project-files", filename: built.filename, blob: built.blob, contentType: "text/csv" });
      await registerDeliverableEntry({ code, category: "project-files", sourceKey, filename: built.filename, size: built.blob.size, kind: "slip_inventory_csv", folder });
      results.push({ kind: "slip_inventory_csv", ok: true });
    } catch (err) {
      results.push({ kind: "slip_inventory_csv", ok: false, error: err?.message || String(err) });
    }

    // Slip plot is always ONE deliverable, no matter how many regions: each region gets its
    // own page (zoomed to just that region's slips, labeled with the region name -- see
    // exportPlotSvg's deliverableRegion filter/title), rendered onto a shared jsPDF doc via
    // pdfDoc/keepDoc instead of a separate file per region. A single-region marina is just the
    // degenerate one-page case of the same loop. SLAM GeoTIFF and SLAM LAS stay genuinely
    // per-region though (each region can legitimately link a different point cloud/surface):
    // the main region's files keep the plain filename, every other region's get
    // "-REGION-SLUG" appended to the code (see deliverableFilename's region param).
    const totalSteps = 2 + assignments.length + assignments.length * 2;
    let stepsDone = 2;

    // Any single region's page failing aborts the combined plot the same way a single-region
    // marina's plot failure already aborted that deliverable -- there's no meaningful "partial" PDF.
    let sharedPdf = null;
    let plotError = null;
    const plotWarnings = [];
    for (const assignment of assignments) {
      const { key, label, isMain } = assignment;
      const regionTag = multiRegion ? ` (${label})` : "";
      stepsDone += 1;
      taskStatus(`Generating slip plot${regionTag}…`, Math.round((stepsDone / totalSteps) * 100));
      render();
      try {
        // Generate Deliverables always colors the plot by Slip Type, independent of whatever
        // "Color slips by" mode the user last had the map set to for their own editing work --
        // the manual Export > PDF Plot menu still honors state.colorSlipsBy as before. Plot
        // background uses this region's own linked surface when it has one, so the plot and
        // the SLAM GeoTIFF show the same raster.
        const built = await exportPlotSvg({
          date: fileDate, tileset: assignment.geotiffSurface, colorMode: "space_type",
          deliverableRegion: multiRegion ? { key, label, isMain } : null, pdfDoc: sharedPdf, keepDoc: true,
        });
        if (!built) throw new Error(`no committed slip polygons to plot${regionTag}`);
        sharedPdf = built.pdf;
        if (built.rasterWarning) plotWarnings.push(built.rasterWarning);
      } catch (err) {
        plotError = `${regionTag ? regionTag.trim() + ": " : ""}${err?.message || err}`;
        break;
      }
    }
    if (sharedPdf && !plotError) {
      try {
        const blob = sharedPdf.output("blob");
        const filename = deliverableFilename({ projectNumber, code, kind: "slip_plot", date: fileDate, region: null });
        const sourceKey = await presignAndUploadDeliverable({ code, category: "project-files", filename, blob, contentType: "application/pdf" });
        await registerDeliverableEntry({ code, category: "project-files", sourceKey, filename, size: blob.size, kind: "slip_plot", folder });
        results.push({ kind: "slip_plot", ok: true, warning: plotWarnings.length ? plotWarnings.join("; ") : null });
      } catch (err) {
        results.push({ kind: "slip_plot", ok: false, error: err?.message || String(err) });
      }
    } else {
      results.push({ kind: "slip_plot", ok: false, error: plotError || "plot generation failed" });
    }

    // Regions genuinely CAN link distinct point clouds/surfaces (see the docstring above), but
    // when a marina's regions all share the exact same LAS/surface -- the common case, "Region"
    // is a rarely-used field -- that's one deliverable file, not one copy per region. First
    // assignment to claim a given source's key wins the plain, unsuffixed filename (assignments
    // is sorted Main-first, see above), and every later region sharing that same source is
    // skipped entirely: no re-export, no second registerDeliverableEntry call, no extra file.
    const registeredGeotiffSurfaceKeys = new Set();
    const registeredLasFileKeys = new Set();

    for (const assignment of assignments) {
      const { key, label, isMain, lasFile, geotiffSurface } = assignment;
      const deliverableRegion = multiRegion ? { key, label, isMain } : null;
      const regionTag = multiRegion ? ` (${label})` : "";
      const filenameRegion = deliverableRegion && !deliverableRegion.isMain ? deliverableRegion.label : null;

      stepsDone += 1;
      taskStatus(`Exporting SLAM GeoTIFF${regionTag}…`, Math.round((stepsDone / totalSteps) * 100));
      render();
      if (geotiffSurface && registeredGeotiffSurfaceKeys.has(geotiffSurface.key)) {
        // Already registered (as the plain, unsuffixed file) for an earlier region sharing
        // this exact surface -- not a separate deliverable, nothing to do.
      } else if (geotiffSurface) {
        try {
          // The merge+warp only needs to run once per distinct surface -- every region linked
          // to the same surface registers a pointer at the same physical export (see
          // registerDeliverableEntry/deliverables/link: it links a sourceKey under a filename,
          // it doesn't copy), same as slam_las already does.
          let exported = mosaicExports.get(geotiffSurface.key);
          if (!exported) {
            const maskParams = geotiffSurface.maskMin != null && geotiffSurface.maskMax != null
              ? { maskMin: geotiffSurface.maskMin, maskMax: geotiffSurface.maskMax } : {};
            const isMosaic = geotiffSurface.kind === "mosaic2d";
            const exportUrl = isMosaic
              ? `/api/point-cloud/mosaic2d/${encodeURIComponent(code)}/export`
              : "/api/qimera/cog-export-dam";
            // name lets the physical DAM file keep the surface's own name -- without it every
            // surface's export collides onto the same standard-deliverable filename and each
            // overwrites the last one in S3 (see the surfaces tool's own "Export -> DAM" fix).
            const exportBody = isMosaic
              ? { attribute: "elevation", polygon: null, name: geotiffSurface.name, ...maskParams }
              : { rasterPath: geotiffSurface.rasterPath, marina: code, polygon: null, name: geotiffSurface.name, ...maskParams };
            const started = await requestJson(exportUrl, { method: "POST", body: exportBody });
            const job = await pollMosaicExportJob(started.jobId);
            if (job?.status !== "completed") throw new Error(job?.error || job?.message || "mosaic export failed");
            exported = { damKey: job.damKey, sizeBytes: job.sizeBytes };
            mosaicExports.set(geotiffSurface.key, exported);
          }
          const filename = deliverableFilename({ projectNumber, code, kind: "slam_tif", date: fileDate, region: filenameRegion });
          await registerDeliverableEntry({ code, category: "geotiff-files", sourceKey: exported.damKey, filename, size: exported.sizeBytes, kind: "slam_tif", folder });
          registeredGeotiffSurfaceKeys.add(geotiffSurface.key);
          results.push({ kind: "slam_tif", region: label, ok: true });
        } catch (err) {
          results.push({ kind: "slam_tif", region: label, ok: false, error: err?.message || String(err) });
        }
      } else {
        results.push({ kind: "slam_tif", region: label, ok: false, error: `no SLAM GeoTIFF surface linked${regionTag} -- pick one in Generate Deliverables, or build one in the Surfaces tool` });
      }

      stepsDone += 1;
      taskStatus(`Registering SLAM point cloud${regionTag}…`, Math.round((stepsDone / totalSteps) * 100));
      render();
      if (lasFile && registeredLasFileKeys.has(lasFile.key)) {
        // Already registered (as the plain, unsuffixed file) for an earlier region sharing
        // this exact point cloud -- not a separate deliverable, nothing to do.
      } else if (lasFile) {
        try {
          const epsgCode = projectEpsgCode(code);
          if (!epsgCode) throw new Error("this project has no numeric EPSG code set for its horizontal CRS");
          // The deliverable must always be a genuine uncompressed .las regardless of what the
          // selected candidate actually is -- ensureUncompressedLasFile runs a .laz candidate
          // through the same decompress-and-rewrite PDAL pipeline Combine LAS uses (see its
          // doc comment) and returns the resulting real .las's key/size; an already-.las
          // candidate passes through untouched.
          let source = lasConversions.get(lasFile.key);
          if (!source) {
            if (/\.laz$/i.test(lasFile.name || lasFile.key || "")) {
              taskStatus(`Converting LAZ point cloud to LAS${regionTag}…`, Math.round((stepsDone / totalSteps) * 100));
              render();
            }
            source = await ensureUncompressedLasFile(code, lasFile);
            lasConversions.set(lasFile.key, source);
          }
          const filename = deliverableFilename({ projectNumber, code, kind: "slam_las", epsgCode, date: fileDate, region: filenameRegion });
          await registerDeliverableEntry({ code, category: "las-files", sourceKey: source.key, filename, size: source.size, kind: "slam_las", folder });
          registeredLasFileKeys.add(lasFile.key);
          results.push({ kind: "slam_las", region: label, ok: true, located: true });
        } catch (err) {
          results.push({ kind: "slam_las", region: label, ok: false, error: err?.message || String(err) });
        }
      } else {
        results.push({ kind: "slam_las", region: label, ok: false, error: `no point cloud selected${regionTag}` });
      }
    }

    const okCount = results.filter((r) => r.ok).length;
    const failed = results.filter((r) => !r.ok);
    const warned = results.filter((r) => r.ok && r.warning);
    const totalCount = results.length;
    const regionSuffix = multiRegion ? ` across ${assignments.length} regions` : "";
    const warningSuffix = warned.length ? ` Warning: ${warned.map((w) => `${w.kind}: ${w.warning}`).join("; ")}` : "";
    status = failed.length
      ? `Generated ${okCount}/${totalCount} deliverables for ${code}${regionSuffix}. ${failed.map((f) => `${f.kind}${f.region ? ` (${f.region})` : ""}: ${f.error}`).join("; ")}${warningSuffix}`
      : `Generated all ${totalCount} deliverables for ${code}${regionSuffix} → DAM Deliverables › ${folder}.${warningSuffix}`;
    taskDone(`${okCount}/${totalCount} deliverables — ${code}`, failed.length === 0);
    // A plot page that silently lost its raster, or a whole kind (e.g. SLAM LAS) that failed
    // outright, is exactly the kind of thing a status line gets scrolled past once the picker
    // dialog has already closed -- reportGenerateDeliverablesFailure already popups a modal for
    // a *total* failure (the whole call throwing), but a *per-kind* failure inside the results
    // array never did, so "no point cloud selected" or similar looked like the button silently
    // did nothing instead of skipping just that one deliverable.
    if (failed.length || warned.length) {
      const lines = [
        ...failed.map((f) => `${f.kind}${f.region ? ` (${f.region})` : ""}: ${f.error}`),
        ...warned.map((w) => `${w.kind}: ${w.warning}`),
      ];
      app.ui?.alertModal?.(lines.join("\n"), {
        title: failed.length ? `Generated ${okCount}/${totalCount} deliverables — some failed` : "Deliverables generated with warnings",
      });
    }
    app.log("deliverables.generate_all", {
      marina: code, durationMs: Date.now() - generateStart, regions: assignments.length,
      results: results.map((r) => ({ kind: r.kind, region: r.region || null, ok: r.ok, located: !!r.located })),
    });
    render();
  }

  let deliverablesPickerEl = null;
  let deliverablesPickerKeydownHandler = null;

  function closeDeliverablesPicker() {
    if (deliverablesPickerKeydownHandler) {
      document.removeEventListener("keydown", deliverablesPickerKeydownHandler);
      deliverablesPickerKeydownHandler = null;
    }
    if (deliverablesPickerEl) {
      deliverablesPickerEl.remove();
      deliverablesPickerEl = null;
    }
  }

  // Popup shown by the "Generate Deliverables" button: the slip plot/GeoJSON/CSV are
  // generated automatically, and so is the SLAM GeoTIFF now (see generateAllDeliverables --
  // it resolves the marina's mosaic2d surface itself, no file to pick, Tile Pyramids
  // retired). A marina can still have more than one point cloud sitting in its DAM
  // (re-flights, multiple survey areas) though, so the user picks which one becomes the
  // registered SLAM LAS rather than the tool silently guessing "most recent".
  async function openDeliverablesPicker() {
    const code = String(filterProjectId || state.marinaId || "").trim().toUpperCase();
    if (!code) { alert("Select a marina before generating deliverables."); return; }
    closeDeliverablesPicker();

    const regions = enumerateDeliverableRegions();
    const multiRegion = regions.length > 1;

    const backdrop = document.createElement("div");
    backdrop.style.cssText = "position:fixed;inset:0;z-index:100000;background:rgba(2,6,23,0.72);display:flex;align-items:center;justify-content:center;font:12px/1.5 -apple-system,BlinkMacSystemFont,sans-serif;";
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) closeDeliverablesPicker(); });
    const dialog = document.createElement("div");
    dialog.style.cssText = `background:rgba(15,23,42,0.98);border:1px solid rgba(148,163,184,0.25);border-radius:10px;padding:18px 20px;width:${multiRegion ? "560px" : "420px"};max-width:calc(100vw - 32px);max-height:80vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,0.5);color:#e2e8f0;`;
    dialog.innerHTML = `
      <div style="font-size:13px;font-weight:700;margin-bottom:4px;">Generate Deliverables — ${esc(code)}</div>
      <div style="font-size:11px;color:#94a3b8;margin-bottom:14px;">${multiRegion
        ? `This marina's committed slips span ${regions.length} regions. Slip inventory (GeoJSON/CSV) is generated automatically; the slip plot, SLAM LAS, and SLAM GeoTIFF are per region -- choose which region is the main package, and link a point cloud / surface to each.`
        : `Slip plot, GeoJSON, and CSV are generated automatically from live slip data. Pick a point cloud for the SLAM LAS, and a surface for the SLAM GeoTIFF.`}</div>
      <div data-picker-body style="font-size:11px;color:#94a3b8;text-align:center;padding:24px 0;">Loading DAM files…</div>
    `;
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);
    deliverablesPickerEl = backdrop;
    deliverablesPickerKeydownHandler = (e) => { if (e.key === "Escape") closeDeliverablesPicker(); };
    document.addEventListener("keydown", deliverablesPickerKeydownHandler);

    let lasFiles = [];
    let geotiffCandidates = [];
    try {
      [lasFiles, geotiffCandidates] = await Promise.all([
        listDamCandidates(code, "las-files", /\.(las|laz)$/i),
        listSurfaceDeliverableCandidates(code),
      ]);
    } catch (err) {
      if (deliverablesPickerEl !== backdrop) return; // closed while loading
      const body = dialog.querySelector("[data-picker-body]");
      if (body) body.innerHTML = `<div style="color:#f87171;padding:12px 0;">Failed to load DAM files: ${esc(err?.message || err)}</div>`;
      return;
    }
    if (deliverablesPickerEl !== backdrop) return; // closed while loading

    const fileLabel = (f) => `${f.name}${f.size != null ? ` — ${formatDeliverableBytes(f.size)}` : ""}${f.lastModified ? ` — ${formatDeliverableDate(f.lastModified)}` : ""}`;
    const geotiffLabel = (c) => `${c.name}${c.maskMin != null ? ` — masked ${c.maskMin.toFixed(2)}–${c.maskMax.toFixed(2)}` : ""}`;
    // Exactly one candidate (the common case: one mosaic covering the whole marina) stays
    // automatic, same as before -- no picker needed. Two or more (e.g. a distinct built
    // surface per region) means the tool can no longer guess which belongs where, so it's
    // shown as a real picker instead of silently linking the same one everywhere.
    const geotiffAutoHtml = geotiffCandidates.length === 1
      ? `<div style="font-size:11px;color:#22c55e;background:rgba(34,197,94,0.08);border:1px solid rgba(34,197,94,0.25);border-radius:6px;padding:6px 8px;margin-bottom:14px;">
          ✓ SLAM GeoTIFF will export from ${esc(geotiffCandidates[0].name)}${geotiffCandidates[0].maskMin != null ? ` — masked to ${geotiffCandidates[0].maskMin.toFixed(2)}–${geotiffCandidates[0].maskMax.toFixed(2)}` : ""}.
        </div>`
      : !geotiffCandidates.length
      ? `<div style="font-size:11px;color:#f59e0b;background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:6px;padding:6px 8px;margin-bottom:14px;">
          No 2D mosaic or built surface found for this marina — SLAM GeoTIFF will be skipped. Build one in the Surfaces tool first.
        </div>`
      : "";

    const body = dialog.querySelector("[data-picker-body]");
    if (!body) return;

    if (!multiRegion) {
      const pickerSection = (label, files, category, { labelFn = fileLabel, emptyMessage } = {}) => {
        if (!files.length) {
          return `<div style="margin-bottom:14px;">
            <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px;">${esc(label)}</div>
            <div style="font-size:11px;color:#f59e0b;background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:6px;padding:6px 8px;">${esc(emptyMessage || `None found in ${category}.`)}</div>
          </div>`;
        }
        const options = [`<option value="__skip__">— Skip this deliverable —</option>`]
          .concat(files.map((f, i) => `<option value="${esc(f.key)}"${i === 0 ? " selected" : ""}>${esc(labelFn(f))}</option>`))
          .join("");
        return `<div style="margin-bottom:14px;">
          <div style="font-size:10px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;margin-bottom:4px;">${esc(label)} (${files.length} found)</div>
          <select data-picker-select="${esc(category)}" style="width:100%;font-size:11px;background:rgba(2,6,23,0.6);border:1px solid rgba(148,163,184,0.25);border-radius:4px;padding:5px 7px;color:#e2e8f0;">${options}</select>
        </div>`;
      };
      body.outerHTML = `<div data-picker-body>
        ${geotiffAutoHtml}
        ${pickerSection("SLAM point cloud (LAS/LAZ)", lasFiles, "las-files")}
        ${geotiffCandidates.length > 1 ? pickerSection("SLAM GeoTIFF surface", geotiffCandidates, "geotiff-surface", { labelFn: geotiffLabel }) : ""}
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:6px;">
          <button type="button" data-picker-cancel class="btn" style="font-size:11px;padding:4px 12px;">Cancel</button>
          <button type="button" data-picker-generate class="btn primary" style="font-size:11px;padding:4px 12px;">Generate</button>
        </div>
      </div>`;

      dialog.querySelector("[data-picker-cancel]")?.addEventListener("click", closeDeliverablesPicker);
      dialog.querySelector("[data-picker-generate]")?.addEventListener("click", () => {
        const lasSel = dialog.querySelector('select[data-picker-select="las-files"]');
        const lasKey = lasSel && lasSel.value !== "__skip__" ? lasSel.value : null;
        const lasFile = lasKey ? lasFiles.find((f) => f.key === lasKey) || null : null;
        let geotiffSurface = null;
        if (geotiffCandidates.length === 1) {
          geotiffSurface = geotiffCandidates[0];
        } else if (geotiffCandidates.length > 1) {
          const gtSel = dialog.querySelector('select[data-picker-select="geotiff-surface"]');
          const gtKey = gtSel && gtSel.value !== "__skip__" ? gtSel.value : null;
          geotiffSurface = gtKey ? geotiffCandidates.find((c) => c.key === gtKey) || null : null;
        }
        closeDeliverablesPicker();
        generateAllDeliverables({ regionAssignments: [{ key: null, label: null, isMain: true, lasFile, geotiffSurface }] }).catch(reportGenerateDeliverablesFailure);
      });
      return;
    }

    // Multi-region: one row per region -- Main radio, region name + slip count, LAS select,
    // GeoTIFF surface select. Both pools are shared across all rows since regions usually
    // correspond to different flights/areas within the same DAM tree -- each dropdown defaults
    // to "Skip" rather than auto-picking, since guessing the same file for every region would
    // usually be wrong. When there's only one candidate surface for the whole marina, though,
    // every row just shows its name (no picker needed -- same as the single-region case).
    const fileSelect = (files, category, regionKey, { labelFn = fileLabel } = {}) => {
      if (!files.length) {
        return `<select data-picker-select="${esc(category)}" data-region-key="${esc(regionKey)}" disabled style="width:100%;font-size:10px;background:rgba(2,6,23,0.4);border:1px solid rgba(148,163,184,0.15);border-radius:4px;padding:4px 6px;color:#64748b;"><option>— none found —</option></select>`;
      }
      const options = [`<option value="__skip__" selected>— Skip —</option>`]
        .concat(files.map((f) => `<option value="${esc(f.key)}">${esc(labelFn(f))}</option>`))
        .join("");
      return `<select data-picker-select="${esc(category)}" data-region-key="${esc(regionKey)}" style="width:100%;font-size:10px;background:rgba(2,6,23,0.6);border:1px solid rgba(148,163,184,0.25);border-radius:4px;padding:4px 6px;color:#e2e8f0;">${options}</select>`;
    };
    const geotiffCell = (regionKey) => {
      if (!geotiffCandidates.length) return `<div style="font-size:10px;color:#64748b;">— none found —</div>`;
      if (geotiffCandidates.length === 1) return `<div style="font-size:10px;color:#94a3b8;">${esc(geotiffCandidates[0].name)}</div>`;
      return fileSelect(geotiffCandidates, "geotiff-surface", regionKey, { labelFn: geotiffLabel });
    };

    const rows = regions.map((r, i) => `
      <tr data-region-row="${esc(r.key)}">
        <td style="padding:6px 8px 6px 2px;text-align:center;"><input type="radio" name="picker-main-region" value="${esc(r.key)}"${i === 0 ? " checked" : ""}/></td>
        <td style="padding:6px 8px;">
          <div style="font-weight:600;color:#e2e8f0;">${esc(r.label)}</div>
          <div style="font-size:9px;color:#94a3b8;">${r.count} slip${r.count === 1 ? "" : "s"}</div>
        </td>
        <td style="padding:6px 8px;min-width:140px;">${fileSelect(lasFiles, "las-files", r.key)}</td>
        <td style="padding:6px 2px 6px 8px;min-width:140px;">${geotiffCell(r.key)}</td>
      </tr>`).join("");

    const filesWarning = (!lasFiles.length || !geotiffCandidates.length)
      ? `<div style="font-size:10px;color:#f59e0b;background:rgba(245,158,11,0.08);border:1px solid rgba(245,158,11,0.25);border-radius:6px;padding:6px 8px;margin-bottom:10px;">${[
          !lasFiles.length ? "No LAS/LAZ point clouds found in las-files." : null,
          !geotiffCandidates.length ? "No 2D mosaic or built surface found -- build one in the Surfaces tool." : null,
        ].filter(Boolean).join(" ")}</div>`
      : "";

    body.outerHTML = `<div data-picker-body>
      ${filesWarning}
      <table style="width:100%;border-collapse:collapse;margin-bottom:10px;">
        <thead>
          <tr style="font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:0.05em;">
            <th style="padding:0 8px 4px 2px;text-align:center;">Main</th>
            <th style="padding:0 8px 4px;text-align:left;">Region</th>
            <th style="padding:0 8px 4px;text-align:left;">SLAM point cloud</th>
            <th style="padding:0 2px 4px 8px;text-align:left;">SLAM GeoTIFF surface</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="font-size:10px;color:#64748b;margin-bottom:10px;">The slip plot is always one PDF, with a page per region. The main region's LAS/GeoTIFF keep the plain "${esc(code)}" filename; every other region's LAS/GeoTIFF get "-REGION" appended to the marina code as a supplemental package.</div>
      <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:6px;">
        <button type="button" data-picker-cancel class="btn" style="font-size:11px;padding:4px 12px;">Cancel</button>
        <button type="button" data-picker-generate class="btn primary" style="font-size:11px;padding:4px 12px;">Generate</button>
      </div>
    </div>`;

    dialog.querySelector("[data-picker-cancel]")?.addEventListener("click", closeDeliverablesPicker);
    dialog.querySelector("[data-picker-generate]")?.addEventListener("click", () => {
      const mainKey = dialog.querySelector('input[name="picker-main-region"]:checked')?.value ?? regions[0].key;
      const regionAssignments = regions.map((r) => {
        const lasSel = dialog.querySelector(`select[data-picker-select="las-files"][data-region-key="${CSS.escape(r.key)}"]`);
        const lasKey = lasSel && lasSel.value !== "__skip__" ? lasSel.value : null;
        let geotiffSurface = null;
        if (geotiffCandidates.length === 1) {
          geotiffSurface = geotiffCandidates[0];
        } else if (geotiffCandidates.length > 1) {
          const gtSel = dialog.querySelector(`select[data-picker-select="geotiff-surface"][data-region-key="${CSS.escape(r.key)}"]`);
          const gtKey = gtSel && gtSel.value !== "__skip__" ? gtSel.value : null;
          geotiffSurface = gtKey ? geotiffCandidates.find((c) => c.key === gtKey) || null : null;
        }
        return {
          key: r.key,
          label: r.label,
          isMain: r.key === mainKey,
          lasFile: lasKey ? lasFiles.find((f) => f.key === lasKey) || null : null,
          geotiffSurface,
        };
      });
      // Main region first, so its plain-filename package leads the generation order/status messaging.
      regionAssignments.sort((a, b) => (b.isMain ? 1 : 0) - (a.isMain ? 1 : 0));
      closeDeliverablesPicker();
      generateAllDeliverables({ regionAssignments }).catch(reportGenerateDeliverablesFailure);
    });
  }

  // ── Slip drafts ──────────────────────────────────────────────────────────────
  async function loadDrafts({ quiet = false } = {}) {
    const code = String(state.marinaId || "").trim();
    if (!code) { draftsRun = null; draftsReview = {}; draftsError = "Set the marina first."; render(); return; }
    draftsLoading = true; draftsError = ""; render();
    try {
      const data = await requestJson(`/api/slip-drafts/${encodeURIComponent(code)}`);
      draftsEnabled = data?.enabled !== false;
      const prevRequestStatus = draftsRequest?.status;
      draftsRun = data?.run || null; draftsReview = data?.review || {};
      draftsRun && (draftsRun._marina = code);
      draftsRequest = data?.request || null; draftsWorkerSeenAt = data?.workerSeenAt || null;
      if (prevRequestStatus === "running" && draftsRequest?.status === "done") quiet = false;
      draftsStatusCache.key = "";
      if (!quiet) status = !draftsEnabled ? "Slip drafts are not enabled on this server." : draftsRun ? `Loaded ${draftsRun.drafts.length} draft slips (run ${draftsRun.runId}).` : `No draft slips for ${code} yet.`;
    } catch (err) { draftsError = err?.message || String(err); }
    finally { draftsLoading = false; scheduleDraftsPoll(); renderDrafts(); render(); }
  }
  function draftsRequestActive() { return draftsRequest?.status === "queued" || draftsRequest?.status === "running"; }
  // While a Create Drafts request is queued/running, re-check every 10 s so progress and the new run
  // show up without a manual reload.
  function scheduleDraftsPoll() {
    clearTimeout(draftsPollTimer); draftsPollTimer = null;
    if (!draftsVisible || !draftsRequestActive()) return;
    const code = String(state.marinaId || "").trim();
    draftsPollTimer = setTimeout(() => {
      draftsPollTimer = null;
      if (draftsVisible && String(state.marinaId || "").trim() === code) loadDrafts({ quiet: true });
    }, 10000);
  }
  async function requestDraftsRun() {
    const code = String(state.marinaId || "").trim();
    if (!code || draftsRequesting) return;
    if (draftsRun && !window.confirm(`Run detection again for ${code}? The new run replaces the drafts shown now (decisions on this run are kept with it).`)) return;
    draftsRequesting = true; render();
    try {
      const data = await requestJson(`/api/slip-drafts/${encodeURIComponent(code)}/request`, { method: "POST", body: { by: app.auth?.email || null } });
      draftsRequest = data?.request || null;
      status = data?.existing ? `A detection run for ${code} is already ${draftsRequest?.status}.` : `Detection requested for ${code}. It runs on the devbox; this panel updates as it goes.`;
    } catch (err) { status = `Create Drafts failed: ${err?.message || err}`; }
    finally { draftsRequesting = false; scheduleDraftsPoll(); render(); }
  }
  function draftsRequestHtml() {
    const r = draftsRequest;
    if (!r) return "";
    const ago = (iso) => { const m = Math.round((Date.now() - Date.parse(iso)) / 60000); return !Number.isFinite(m) ? "" : m < 1 ? "just now" : m < 60 ? `${m} min ago` : `${Math.round(m / 60)} h ago`; };
    if (r.status === "queued" || r.status === "running") {
      // Only for queued: a running request is itself proof the worker is alive, and it checks in only
      // between jobs and at each progress step (gridding a big cloud can take a while).
      const seen = draftsWorkerSeenAt ? Date.now() - Date.parse(draftsWorkerSeenAt) : Infinity;
      const offline = r.status === "queued" && seen > 10 * 60000
        ? `<br><span style="color:#f87171">The detection worker hasn't checked in ${draftsWorkerSeenAt ? `since ${esc(ago(draftsWorkerSeenAt))}` : "yet"} -- it may be offline.</span>` : "";
      // progress/etaSeconds come from the devbox worker; etaSeconds is as of updatedAt
      const pct = r.status === "running" && Number.isFinite(Number(r.progress)) ? Math.max(0, Math.min(100, Number(r.progress))) : null;
      const left = Number.isFinite(Number(r.etaSeconds)) && r.etaSeconds !== null && r.updatedAt
        ? Math.max(0, Number(r.etaSeconds) - (Date.now() - Date.parse(r.updatedAt)) / 1000) : null;
      const leftTxt = left == null ? "" : left < 90 ? " · about a minute left" : ` · ~${Math.round(left / 60)} min left`;
      const bar = pct == null ? "" : `<div style="margin:5px auto 0; height:6px; border-radius:3px; background:rgba(148,163,184,.25); overflow:hidden;" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}">
          <div style="height:100%; width:${pct.toFixed(1)}%; background:#f59e0b; transition:width .6s ease;"></div></div>
        <div style="font-size:10px; margin-top:2px;">${Math.round(pct)}%${leftTxt}</div>`;
      return `<div style="margin-top:4px; color:#f59e0b;">${r.status === "queued" ? "Queued" : "Running"} (requested ${esc(ago(r.requestedAt))}): ${esc(r.message || "")}${bar}${offline}</div>`;
    }
    if (r.status === "failed") return `<div style="margin-top:4px; color:#f87171;">Last run failed (${esc(ago(r.finishedAt || r.requestedAt))}): ${esc(r.message || "")}</div>`;
    return "";
  }
  function draftsScoreHtml() {
    const sc = draftsRun?.score;
    if (!sc || !sc.truth) return "";
    const pct = (v) => `${Math.round(Number(v) * 100)}%`;
    return `<br><span style="font-size:10px" title="Measured against the slips already drawn inside the point cloud when the run was made. Recall: share of drawn slips the detector found. Precision: share of drafts that match a drawn slip (drafts on undrawn docks count against it).">vs ${sc.truth} drawn slips: found ${pct(sc.recall)} · precision ${pct(sc.precision)}</span>`;
  }
  // ── Auto Run: lasso a pier, get both sides as a pre-filled, fully editable run of finger slips ──
  // Seeded from the drafts run's detected finger rows (detect.py "rows": finger centres on the walkway
  // edge, slip length, finger width, finger direction, slips per bay). Each side uses the same model
  // and geometry as the Fingers tool (msSlotFracsFromCenters / msSlotCornersPerLenSheared), so a
  // committed Auto Run slip is identical to one drawn by hand; both sides are edited together and
  // committed in one go.
  //   side = { p1, p2 (run ends = first/last finger centre, on the walkway edge), lenFt (signed,
  //            + = left of p1->p2), centerFracs (N+1), fingerWidthFt, slotsPerBay, shearDeg }
  const AUTO_RUN_COLORS = ["#10b981", "#f59e0b"];

  function autoRunAvailable() { return !!(draftsAllowed() && draftsRun && Array.isArray(draftsRun.rows) && draftsRun.rows.length); }
  function autoRunBusy() { return autoRunLassoActive || !!autoRunSides; }

  function stopAutoRun(reason = "") {
    if (autoRunLassoOff) { try { autoRunLassoOff(); } catch {} autoRunLassoOff = null; }
    if (autoRunKeyOff) { try { autoRunKeyOff(); } catch {} autoRunKeyOff = null; }
    autoRunLassoActive = false; autoRunLassoPts = []; autoRunSides = null; autoRunDragging = false;
    autoRunLayer.clearLayers(); autoRunHandleLayer.clearLayers();
    try { map.dragging.enable(); map.getContainer().style.cursor = ""; } catch {}
    if (reason) { status = reason; render(); }
  }

  async function startAutoRun() {
    if (!draftsAllowed()) return;
    if (!draftsRun || String(draftsRun._marina || "") !== String(state.marinaId || "").trim()) await loadDrafts({ quiet: true });
    if (!autoRunAvailable()) { status = "Auto Run needs a drafts run with finger rows for this marina -- click Create Drafts (or Re-run) first."; render(); return; }
    // same hand-off as every other draw tool: nothing else may own the map while this runs
    stopPointDrop("replaced"); stopRotateAdjustMode(); lassoToolActive = false; disarmAllLassoToggles();
    stopLineLayout("replaced"); stopGeometryRunLayout("replaced"); stopGeometryPlaceMode(); stopCombMode();
    stopChainMode(); stopAutoDetectSlipsMode(); stopFullDockMode(); stopMultislipMode();
    stopAutoRun();
    autoRunLassoActive = true;
    const el = map.getContainer();
    el.style.cursor = "crosshair";
    let drawing = false;
    const toLL = (e) => { const ll = map.mouseEventToLatLng(e); return { lat: ll.lat, lng: ll.lng }; };
    const down = (e) => {
      if (e.button !== 0) return;
      drawing = true; autoRunLassoPts = [toLL(e)];
      try { map.dragging.disable(); } catch {}
      try { e.target?.setPointerCapture?.(e.pointerId); } catch {}
      e.preventDefault(); e.stopPropagation();
    };
    const move = (e) => {
      if (!drawing) return;
      autoRunLassoPts.push(toLL(e));
      autoRunLayer.clearLayers();
      L.polygon(autoRunLassoPts.map((p) => [p.lat, p.lng]), { color: draftOutlineColor(), weight: 2, dashArray: "5 4", fillColor: "#38bdf8", fillOpacity: 0.08, interactive: false }).addTo(autoRunLayer);
      e.preventDefault(); e.stopPropagation();
    };
    const up = (e) => {
      if (!drawing) return;
      drawing = false;
      try { map.dragging.enable(); } catch {}
      e.preventDefault(); e.stopPropagation();
      suppressMapClicks(300);
      const pts = autoRunLassoPts.slice();
      if (autoRunLassoOff) { autoRunLassoOff(); autoRunLassoOff = null; }
      autoRunLassoActive = false; el.style.cursor = "";
      autoRunLayer.clearLayers();
      if (pts.length < 4) { status = "Auto Run: draw a loop around the pier (drag)."; startAutoRun(); return; }
      buildAutoRunFromLasso(pts);
    };
    el.addEventListener("pointerdown", down, true); el.addEventListener("pointermove", move, true); el.addEventListener("pointerup", up, true);
    const key = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopImmediatePropagation(); stopAutoRun("Auto Run cancelled."); } };
    document.addEventListener("keydown", key, true);
    autoRunLassoOff = () => {
      el.removeEventListener("pointerdown", down, true); el.removeEventListener("pointermove", move, true); el.removeEventListener("pointerup", up, true);
      document.removeEventListener("keydown", key, true);
    };
    status = "Auto Run: drag a loop around one pier (both sides of its walkway). Esc cancels.";
    render();
  }

  // Shear (deg) that tilts this side's offshore direction onto the detected finger direction, in the
  // same east/north frame and sign convention as msTiltVec (CCW rotation of the side's own outward normal).
  function autoRunShearDeg(p1, p2, sign, from, to, maxDeg = 15) {
    const { ux, uy } = msDockvec(p1, p2);
    const cosLat = Math.cos((from.lat * Math.PI) / 180);
    const vx = (to.lng - from.lng) * cosLat * 111320, vy = (to.lat - from.lat) * 111320;
    const bx = -uy * sign, by = ux * sign;
    const deg = (Math.atan2(bx * vy - by * vx, bx * vx + by * vy) * 180) / Math.PI;
    if (Math.abs(deg) > maxDeg) return maxDeg >= 80 ? Math.sign(deg) * 80 : 0;
    return Math.round(deg * 10) / 10;
  }

  function buildAutoRunFromLasso(lassoPts) {
    const ring = lassoPts.map((p) => [p.lat, p.lng]);
    const inside = (ll) => pointInPolygon({ lat: ll[1], lng: ll[0] }, ring);
    // every detected row with fingers inside the loop -- a walkway is often detected in pieces, so
    // rows from several walkways may make up one pier
    const cands = [];
    for (const row of draftsRun.rows || []) {
      const pts = (row.centres || []).filter(inside).map(([lng, lat]) => ({ lat, lng }));
      if (pts.length >= 2) cands.push({ row, pts, tip: { lat: row.tip[1], lng: row.tip[0] }, from: { lat: row.centres[0][1], lng: row.centres[0][0] } });
    }
    if (!cands.length) { status = "Auto Run: no detected finger row inside that loop -- loop around the fingers on at least one side of a walkway."; render(); return; }
    // the pier's axis: the row with the most fingers inside
    const main = cands.reduce((a, b) => (b.pts.length > a.pts.length ? b : a));
    const mp = main.pts.slice().sort((a, b) => msProjectFrac(main.pts[0], main.pts[main.pts.length - 1], a) - msProjectFrac(main.pts[0], main.pts[main.pts.length - 1], b));
    const A1 = mp[0], A2 = mp[mp.length - 1];
    const { ux, uy } = msDockvec(A1, A2);
    const cosLat = Math.cos((A1.lat * Math.PI) / 180);
    const toXY = (p) => { const e = (p.lng - A1.lng) * cosLat * 111320, n = (p.lat - A1.lat) * 111320; return { x: (e * ux + n * uy) * 3.280839895, y: (-e * uy + n * ux) * 3.280839895 }; };
    const fromXY = (x, y) => { const xm = x / 3.280839895, ym = y / 3.280839895; return offsetPoint(A1, ux * xm - uy * ym, uy * xm + ux * ym); };
    // group rows by side of the axis (by where their fingers point); only rows on this pier
    const groups = new Map();
    for (const c of cands) {
      const ys = c.pts.map((p) => toXY(p).y); const ymed = ys.sort((a, b) => a - b)[Math.floor(ys.length / 2)];
      if (Math.abs(ymed) > 45) continue;                                   // another pier inside the loop
      const s = msPerpDistFt(A1, A2, c.tip) - msPerpDistFt(A1, A2, c.from) >= 0 ? 1 : -1;
      groups.set(s, [...(groups.get(s) || []), c]);
    }
    const sides = [];
    for (const [sign, rows] of groups) {
      // all fingers on this side, along the axis, near-duplicates merged
      let P = rows.flatMap((c) => c.pts.map(toXY)).sort((a, b) => a.x - b.x);
      const merged = [];
      for (const p of P) { const q = merged[merged.length - 1]; if (q && p.x - q.x < 2) { q.x = (q.x + p.x) / 2; q.y = (q.y + p.y) / 2; } else merged.push({ ...p }); }
      if (merged.length < 2) continue;
      // spacing: fill fingers the detector missed at the row's usual pitch; a gap of more than 8
      // pitches is a real break (keep the longest stretch)
      const gaps = merged.slice(1).map((p, i) => p.x - merged[i].x);
      const plausible = gaps.filter((g) => g >= 8 && g <= 40).sort((a, b) => a - b);
      const pitch = plausible.length ? plausible[Math.floor(plausible.length / 2)] : 0;
      const segs = [[merged[0]]];
      for (let i = 1; i < merged.length; i++) {
        const g = merged[i].x - merged[i - 1].x;
        if (pitch && g > 8 * pitch) { segs.push([merged[i]]); continue; }
        const n = pitch && g > 1.6 * pitch ? Math.round(g / pitch) - 1 : 0;
        for (let k = 1; k <= n; k++) {
          const f = k / (n + 1); const a = merged[i - 1], b = merged[i];
          segs[segs.length - 1].push({ x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f, filled: true });
        }
        segs[segs.length - 1].push(merged[i]);
      }
      const seg = segs.reduce((a, b) => (b.length > a.length ? b : a));
      if (seg.length < 2) continue;
      // baseline: straight line fitted through this side's finger bases (rows from different
      // walkway pieces can disagree slightly)
      const n = seg.length, mx = seg.reduce((s, p) => s + p.x, 0) / n, my = seg.reduce((s, p) => s + p.y, 0) / n;
      const sxx = seg.reduce((s, p) => s + (p.x - mx) ** 2, 0), sxy = seg.reduce((s, p) => s + (p.x - mx) * (p.y - my), 0);
      const b = sxx > 1e-6 ? sxy / sxx : 0, a = my - b * mx;
      const x0 = seg[0].x, x1 = seg[n - 1].x;
      const p1 = fromXY(x0, a + b * x0), p2 = fromXY(x1, a + b * x1);
      const fr = seg.map((p) => (p.x - x0) / Math.max(1e-6, x1 - x0)); fr[0] = 0; fr[n - 1] = 1;
      const big = rows.reduce((p, q) => (q.pts.length > p.pts.length ? q : p));
      const wmed = (key) => { const v = rows.flatMap((c) => Array(c.pts.length).fill(Number(c.row[key]) || 0)).sort((p, q) => p - q); return v[Math.floor(v.length / 2)]; };
      sides.push({ p1, p2, lenFt: sign * Math.max(5, wmed("lengthFt") || 30), centerFracs: fr,
        fingerWidthFt: Math.max(0, wmed("fingerWidthFt")), slotsPerBay: Math.min(3, Math.max(1, Number(big.row.slotsPerBay) || 1)),
        shearDeg: autoRunShearDeg(p1, p2, sign, big.from, big.tip), removed: new Set(),
        filled: seg.filter((p) => p.filled).length });
    }
    if (!sides.length) { status = "Auto Run: couldn't build a run from the fingers in that loop."; render(); return; }
    sides.sort((p, q) => q.centerFracs.length - p.centerFracs.length);
    autoRunSides = sides; autoRunActiveSide = 0; autoRunHandlesDirty = true;
    const key = (e) => {
      if (!autoRunSides) return;
      const t = e.target; if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      const sd = autoRunSides[autoRunActiveSide];
      let used = true;
      if (e.key === "Escape") stopAutoRun("Auto Run cancelled -- nothing was saved.");
      else if (e.key === "Enter") commitAutoRun();
      else if (e.key === "Tab") { autoRunActiveSide = (autoRunActiveSide + 1) % autoRunSides.length; autoRunHandlesDirty = true; }
      else if (sd && (e.key === "[" || e.key === "]")) { sd.shearDeg = Math.round(Math.max(-80, Math.min(80, sd.shearDeg + (e.key === "]" ? 1 : -1) * (e.shiftKey ? 1 : 0.1))) * 10) / 10; autoRunHandlesDirty = true; }
      else if (sd && (e.key === "T" || (e.key === "t" && e.shiftKey))) { sd.slotsPerBay = (sd.slotsPerBay % 3) + 1; sd.removed = new Set(); autoRunHandlesDirty = true; }
      else used = false;
      if (used) { e.preventDefault(); e.stopImmediatePropagation(); if (autoRunSides) { renderAutoRun(); render(); } }
    };
    document.addEventListener("keydown", key, true);
    const mapEl = map.getContainer();
    mapEl.addEventListener("contextmenu", autoRunPointerHandler, true);
    mapEl.addEventListener("dblclick", autoRunPointerHandler, true);
    autoRunKeyOff = () => {
      document.removeEventListener("keydown", key, true);
      mapEl.removeEventListener("contextmenu", autoRunPointerHandler, true);
      mapEl.removeEventListener("dblclick", autoRunPointerHandler, true);
    };
    renderAutoRun();
    status = autoRunStatus();
    render();
  }

  function autoRunSlips(sd) {
    const runLenFt = msDockvec(sd.p1, sd.p2).lenM * 3.280839895;
    const bays = msSlotFracsFromCenters(sd.centerFracs, sd.fingerWidthFt, runLenFt);
    const out = [];
    bays.forEach(([t0, t1], i) => {
      const b1 = msPointAtFrac(sd.p1, sd.p2, t0), b2 = msPointAtFrac(sd.p1, sd.p2, t1);
      for (let s = 0; s < sd.slotsPerBay; s++) {
        const key = `${i}:${s}`;
        out.push({ bay: i, slot: s, key, removed: !!sd.removed?.has(key), corners: msSlotCornersPerLenSheared(b1, b2, sd.lenFt, s, sd.slotsPerBay, sd.shearDeg),
          alongFt: runLenFt * (t1 - t0) / sd.slotsPerBay });
      }
    });
    return { runLenFt, bays, slips: out };
  }

  // Removed slips are keyed "bay:slot"; adding/removing a finger renumbers the bays after it.
  function autoRunShiftRemoved(sd, fromBay, delta, dropBays = []) {
    if (!sd.removed?.size) return;
    const next = new Set();
    for (const k of sd.removed) {
      const [b, sl] = k.split(":").map(Number);
      if (dropBays.includes(b)) continue;
      next.add(`${b >= fromBay ? b + delta : b}:${sl}`);
    }
    sd.removed = next;
  }
  // One more slip at an end of the run, at the run's typical bay width (finger centre to centre).
  function autoRunAddBayAtEnd(sd, atStart) {
    const N = sd.centerFracs.length - 1; const runFt = msDockvec(sd.p1, sd.p2).lenM * 3.280839895;
    const spans = sd.centerFracs.slice(1).map((t, i) => (t - sd.centerFracs[i]) * runFt).sort((a, b) => a - b);
    const pitch = Math.max(8, spans[Math.floor(spans.length / 2)] || 15);
    const { ux, uy } = msDockvec(sd.p1, sd.p2); const m = pitch / 3.280839895; const newLen = runFt + pitch;
    if (atStart) {
      sd.centerFracs = [0, ...sd.centerFracs.map((t) => (t * runFt + pitch) / newLen)];
      sd.p1 = offsetPoint(sd.p1, -ux * m, -uy * m);
      autoRunShiftRemoved(sd, 0, 1);
    } else {
      sd.centerFracs = [...sd.centerFracs.map((t) => (t * runFt) / newLen), 1];
      sd.p2 = offsetPoint(sd.p2, ux * m, uy * m);
    }
    void N;
  }

  // Removing a slip takes it out of the run itself, so handles and guides follow the new layout.
  // An end slip: the end finger goes and the run's end moves in to the next finger (the reverse of
  // autoRunAddBayAtEnd). A mid-run slip: its two fingers merge into one -- the near one stays, and
  // everything past the slip closes up by its width, so the slips either side share that finger.
  // Only a bay with every slot removed goes (Shift+T); the last bay is never removed.
  function autoRunDropRemovedBays(sd) {
    const whollyRemoved = (b) => { for (let s = 0; s < sd.slotsPerBay; s++) if (!sd.removed?.has(`${b}:${s}`)) return false; return true; };
    for (;;) {
      const fr = sd.centerFracs, N = fr.length - 1;
      if (N <= 1) return;
      let k = 0; while (k < N && !whollyRemoved(k)) k++;
      if (k === N) return;
      if (k === 0) {
        const t = fr[1];
        sd.p1 = msPointAtFrac(sd.p1, sd.p2, t);
        sd.centerFracs = fr.slice(1).map((v) => (v - t) / (1 - t));
      } else if (k === N - 1) {
        const t = fr[N - 1];
        sd.p2 = msPointAtFrac(sd.p1, sd.p2, t);
        sd.centerFracs = fr.slice(0, -1).map((v) => v / t);
      } else {
        const w = fr[k + 1] - fr[k], keep = 1 - w;
        sd.p2 = msPointAtFrac(sd.p1, sd.p2, keep);
        sd.centerFracs = [...fr.slice(0, k + 1), ...fr.slice(k + 2).map((v) => v - w)].map((v) => v / keep);
      }
      autoRunShiftRemoved(sd, k + 1, -1, [k]);
    }
  }

  function autoRunStatus() {
    if (!autoRunSides) return "";
    const parts = autoRunSides.map((sd, i) => {
      const { slips } = autoRunSlips(sd); const kept = slips.filter((x) => !x.removed).length;
      return `${i === autoRunActiveSide ? "▶ " : ""}side ${i + 1}: ${kept} slips${kept < slips.length ? ` (${slips.length - kept} removed)` : ""}${sd.filled ? ` (${sd.filled} missed finger${sd.filled > 1 ? "s" : ""} filled in)` : ""}, ${Math.abs(sd.lenFt).toFixed(1)} ft long, fingers ${sd.fingerWidthFt.toFixed(1)} ft, tilt ${sd.shearDeg.toFixed(1)}°${sd.slotsPerBay > 1 ? `, ${sd.slotsPerBay}/bay` : ""}`;
    });
    return `Auto Run -- ${parts.join(" · ")}. Drag handles to adjust; right-click a slip to remove it (the run closes up around it), + at either end adds a slip, double-click a slip to split it with a finger, right-click a finger handle to remove the finger; Tab = switch side, [ ] tilt 0.1° (Shift 1°), Shift-drag the tilt diamond for fine tilt, Shift+T slips per bay; Enter saves both sides, Esc cancels.`;
  }

  function autoRunHandle(pt, html, title, z = 1000) {
    return L.marker([pt.lat, pt.lng], { icon: L.divIcon({ className: "", html: html.replace("<div ", `<div title="${title}" `), iconSize: [0, 0], iconAnchor: [0, 0] }), draggable: true, zIndexOffset: z });
  }
  const AR_DOT = (bg, size = 12, shape = "border-radius:50%;", cursor = "grab") => `<div style="width:${size}px;height:${size}px;background:${bg};border:2px solid #fff;${shape}cursor:${cursor};margin-left:-${size / 2}px;margin-top:-${size / 2}px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>`;

  // Auto Run slip under a mouse event (active side first, where the two sides overlap), or null.
  function autoRunSlipAt(e) {
    if (!autoRunSides) return null;
    const ll = map.mouseEventToLatLng(e);
    const order = autoRunSides.map((sd, si) => si).sort((a, b) => (b === autoRunActiveSide) - (a === autoRunActiveSide));
    for (const si of order) {
      const sd = autoRunSides[si];
      const sl = autoRunSlips(sd).slips.find((x) => pointInPolygon(ll, x.corners.map((c) => [c.lat, c.lng])));
      if (sl) return { sd, si, sl };
    }
    return null;
  }

  // Capture-phase on the map container while a run is open, so it wins over whatever canvas shape
  // is on top: right-click a slip removes/restores it, double-click splits it with a new finger.
  // Events on handles (markers) are left alone -- a finger handle's right-click removes the finger.
  function autoRunPointerHandler(e) {
    if (!autoRunSides || e.target?.closest?.(".leaflet-marker-icon")) return;
    const hit = autoRunSlipAt(e);
    if (!hit) return;
    e.preventDefault(); e.stopPropagation();
    const { sd, si, sl } = hit;
    if (e.type === "contextmenu") {
      if (!sd.removed) sd.removed = new Set();
      if (sd.removed.has(sl.key)) sd.removed.delete(sl.key); else sd.removed.add(sl.key);
      autoRunDropRemovedBays(sd);
    } else {
      const k = sl.bay, fr = sd.centerFracs;
      fr.splice(k + 1, 0, (fr[k] + fr[k + 1]) / 2);
      autoRunShiftRemoved(sd, k + 1, 1, [k]);
    }
    autoRunActiveSide = si; autoRunHandlesDirty = true; renderAutoRun(); status = autoRunStatus(); render();
  }

  function renderAutoRun() {
    autoRunLayer.clearLayers();
    if (!autoRunSides) { autoRunHandleLayer.clearLayers(); return; }
    const outline = draftOutlineColor();
    autoRunSides.forEach((sd, si) => {
      const col = AUTO_RUN_COLORS[si % 2];
      const { slips } = autoRunSlips(sd);
      slips.forEach((sl) => {
        const poly = L.polygon(sl.corners.map((c) => [c.lat, c.lng]), sl.removed
          ? { color: col, weight: 1, opacity: 0.7, dashArray: "3 5", fillOpacity: 0.03, bubblingMouseEvents: false }
          : { color: si === autoRunActiveSide ? outline : col, weight: si === autoRunActiveSide ? 2 : 1.5, fillColor: col, fillOpacity: 0.22, bubblingMouseEvents: false });
        // right-click / double-click are hit-tested in autoRunPointerHandler, not here: other
        // interactive shapes on the shared canvas (saved slips, drafts, tracked overlays) can sit
        // above these in the draw order and take the event, leaving the browser's own menu.
        poly.on("click", () => { if (autoRunActiveSide !== si) { autoRunActiveSide = si; autoRunHandlesDirty = true; renderAutoRun(); status = autoRunStatus(); render(); } });
        poly.addTo(autoRunLayer);
      });
      L.polyline([[sd.p1.lat, sd.p1.lng], [sd.p2.lat, sd.p2.lng]], { color: col, weight: 2, dashArray: "2 4", interactive: false }).addTo(autoRunLayer);
      if (si === autoRunActiveSide) drawAutoRunGuides(sd, autoRunLayer);
    });
    if (!autoRunDragging && autoRunHandlesDirty) buildAutoRunHandles();
  }

  // Unit offshore direction of this side's tilted fingers (east/north metres) and helpers for guides.
  function autoRunOffshore(sd) {
    const { tiltX, tiltY } = msTiltVec(sd.p1, sd.p2, sd.shearDeg); const sign = sd.lenFt < 0 ? -1 : 1;
    return { x: tiltX * sign, y: tiltY * sign };
  }
  const arAlong = (p, ux, uy, ft) => offsetPoint(p, ux * ft / 3.280839895, uy * ft / 3.280839895);

  // Dotted guides in each handle's colour, extending past the run, for the active side.
  function drawAutoRunGuides(sd, layer) {
    const ext = Math.max(15, Number(state.edgeExtensionFt) || 0);
    const len = Math.abs(sd.lenFt);
    const off = autoRunOffshore(sd); const { ux, uy } = msDockvec(sd.p1, sd.p2);
    const { runLenFt, bays } = autoRunSlips(sd); const N = sd.centerFracs.length - 1;
    const line = (a, b, color, dash = "2 5", weight = 1.5) => L.polyline([[a.lat, a.lng], [b.lat, b.lng]], { color, weight, opacity: 0.95, dashArray: dash, interactive: false }).addTo(layer);
    // behind the walkway: far enough to reach across to the other side's slips, to line the two sides up
    const other = (autoRunSides || []).find((o) => o !== sd);
    const back = other ? Math.abs(msPerpDistFt(sd.p1, sd.p2, other.p1)) + Math.abs(other.lenFt) * 0.5 : ext;
    const fingerLine = (base, color) => line(arAlong(base, off.x, off.y, -Math.max(ext, back)), arAlong(base, off.x, off.y, len + ext), color);
    // orange: every finger centreline (incl. the run ends' fingers), back across the walkway to past the slips
    sd.centerFracs.forEach((t) => fingerLine(msPointAtFrac(sd.p1, sd.p2, t), "#f59e0b"));
    // green: the two run-end edges
    fingerLine(msPointAtFrac(sd.p1, sd.p2, bays[0][0]), "#10b981");
    fingerLine(msPointAtFrac(sd.p1, sd.p2, bays[N - 1][1]), "#10b981");
    // white: inner edge; blue: offshore edge -- both extended past the run
    const a0 = arAlong(sd.p1, ux, uy, -ext), a1 = arAlong(sd.p2, ux, uy, ext);
    line(a0, a1, "#e2e8f0");
    line(arAlong(a0, off.x, off.y, len), arAlong(a1, off.x, off.y, len), "#38bdf8");
    // violet: both edges of the reference fingers the width handles sit on
    if (N > 1) {
      const half = sd.fingerWidthFt / 2;
      for (const k of new Set([1, N - 1])) {
        const c = msPointAtFrac(sd.p1, sd.p2, sd.centerFracs[k]);
        fingerLine(arAlong(c, ux, uy, -half), "#a855f7"); fingerLine(arAlong(c, ux, uy, half), "#a855f7");
      }
    }
    // rose: square (0 deg) reference at each end's dock corner, to read the tilt against
    if (Math.abs(sd.shearDeg) >= 0.05) {
      const sign = sd.lenFt < 0 ? -1 : 1; const px = -uy * sign, py = ux * sign;
      for (const t of [bays[0][0], bays[N - 1][1]]) { const b = msPointAtFrac(sd.p1, sd.p2, t); line(b, arAlong(b, px, py, len + ext), "#f43f5e", "1 4", 1.2); }
    }
  }

  function buildAutoRunHandles() {
    autoRunHandlesDirty = false;
    autoRunHandleLayer.clearLayers();
    const sd = autoRunSides?.[autoRunActiveSide];
    if (!sd) return;
    const done = () => { autoRunDragging = false; autoRunHandlesDirty = true; renderAutoRun(); status = autoRunStatus(); render(); };
    const live = () => { renderAutoRun(); status = autoRunStatus(); render(); };
    const N = sd.centerFracs.length - 1;
    const { runLenFt, bays } = autoRunSlips(sd);
    const sign = sd.lenFt < 0 ? -1 : 1;
    // Handles that act on the whole side sit at BOTH ends and the middle, so one is always in view
    // when zoomed into any part of the run.
    const spots = [...new Set([0, Math.floor((N - 1) / 2), N - 1])];
    // 1. slip length (blue): every slip on this side
    for (const b of spots) {
      const [t0, t1] = bays[b];
      const pt = msSlotOffshorePointSheared(msPointAtFrac(sd.p1, sd.p2, t0), msPointAtFrac(sd.p1, sd.p2, t1), sd.lenFt, 0, 1, sd.shearDeg);
      const h = autoRunHandle(pt, AR_DOT("#38bdf8", 14), "Drag: slip length for this whole side", 1003);
      h.on("dragstart", () => { autoRunDragging = true; });
      h.on("drag", (e) => {
        const cosT = Math.cos((sd.shearDeg * Math.PI) / 180) || 1;
        sd.lenFt = sign * Math.max(5, Math.round(Math.abs(msPerpDistFt(sd.p1, sd.p2, e.latlng) / cosT) * 10) / 10); live();
      });
      h.on("dragend", done); autoRunHandleLayer.addLayer(h);
    }
    // 2. inner edge (white squares): move the run toward/away from the walkway; the far end stays put
    for (const b of spots) {
      const [t0, t1] = bays[b]; const pt = msPointAtFrac(sd.p1, sd.p2, (t0 + t1) / 2);
      const h = autoRunHandle(pt, AR_DOT("#e2e8f0", 11, "border-radius:2px;", "move"), "Drag: where the slips start (distance from the walkway); the far end stays put", 1002);
      let st = null;
      h.on("dragstart", () => { autoRunDragging = true; st = { p1: { ...sd.p1 }, p2: { ...sd.p2 }, len: sd.lenFt, ref: msPerpDistFt(sd.p1, sd.p2, pt) }; });
      h.on("drag", (e) => {
        if (!st) return;
        const dFt = msPerpDistFt(st.p1, st.p2, e.latlng) - st.ref;
        const { ux, uy } = msDockvec(st.p1, st.p2); const m = dFt / 3.280839895;
        sd.p1 = offsetPoint(st.p1, -uy * m, ux * m); sd.p2 = offsetPoint(st.p2, -uy * m, ux * m);
        sd.lenFt = sign * Math.max(5, Math.abs(st.len - dFt)); live();
      });
      h.on("dragend", () => { st = null; done(); }); autoRunHandleLayer.addLayer(h);
    }
    // 3. run ends (green bars): slide an end along the walkway; interior fingers keep their map positions
    for (const isStart of [true, false]) {
      const [t0, t1] = isStart ? bays[0] : bays[N - 1];
      const pt = msPointAtFrac(sd.p1, sd.p2, isStart ? t0 : t1);
      const h = autoRunHandle(pt, '<div style="width:8px;height:18px;background:#10b981;border:2px solid #fff;border-radius:3px;cursor:move;margin-left:-4px;margin-top:-9px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>', "Drag: move this end of the run", 1001);
      let st = null;
      h.on("dragstart", () => { autoRunDragging = true; st = { p1: { ...sd.p1 }, p2: { ...sd.p2 }, fr: sd.centerFracs.slice() }; });
      h.on("drag", (e) => {
        if (!st) return;
        const L0 = msDockvec(st.p1, st.p2).lenM * 3.280839895; const half = L0 > 0.01 ? (sd.fingerWidthFt / 2) / L0 : 0;
        const minSpan = L0 > 0.01 ? Math.max(1 / L0, half * 2) : 0.01;
        const t = msProjectFrac(st.p1, st.p2, e.latlng); const fr = st.fr;
        if (isStart) {
          const n0 = Math.min(t - half, fr[1] - minSpan), span = 1 - n0; if (span <= 0) return;
          sd.p1 = msPointAtFrac(st.p1, st.p2, n0); sd.centerFracs = fr.map((v, k) => (k === 0 ? 0 : k === N ? 1 : (v - n0) / span));
        } else {
          const n1 = Math.max(t + half, fr[N - 1] + minSpan); if (n1 <= 0) return;
          sd.p2 = msPointAtFrac(st.p1, st.p2, n1); sd.centerFracs = fr.map((v, k) => (k === 0 ? 0 : k === N ? 1 : v / n1));
        }
        live();
      });
      h.on("dragend", () => { st = null; done(); }); autoRunHandleLayer.addLayer(h);
    }
    // 4. tilt (rose diamonds) at each end's offshore corner, pivoting on that end's dock corner; 0.1 deg steps
    for (const isStart of [true, false]) {
      const pivot = msPointAtFrac(sd.p1, sd.p2, isStart ? bays[0][0] : bays[N - 1][1]);
      const off = autoRunOffshore(sd);
      const h = autoRunHandle(arAlong(pivot, off.x, off.y, Math.abs(sd.lenFt)), AR_DOT("#f43f5e", 11, "transform:rotate(45deg);"), "Drag sideways: tilt the slips to follow the fingers. Hold Shift while dragging for fine control", 1002);
      // Shift while dragging = fine: a tenth of the mouse's angular movement since the drag (or the
      // Shift press) began, so the last fraction of a degree can be dialled in at any zoom
      let st = null;
      h.on("dragstart", () => { autoRunDragging = true; st = null; });
      h.on("drag", (e) => {
        const raw = autoRunShearDeg(sd.p1, sd.p2, sign, pivot, { lat: e.latlng.lat, lng: e.latlng.lng }, 80);
        const fine = !!e.originalEvent?.shiftKey;
        if (!st || st.fine !== fine) st = { fine, raw, base: sd.shearDeg };
        const shDeg = fine ? Math.round((st.base + (raw - st.raw) * 0.1) * 10) / 10 : raw;
        if (shDeg !== sd.shearDeg) { sd.shearDeg = shDeg; live(); }
      });
      h.on("dragend", done); autoRunHandleLayer.addLayer(h);
    }
    // 5. rotate (cyan) just past each end: swings the run around the OPPOSITE end, so a run that's
    //    right at one end can be fixed from the other while zoomed in there
    for (const isStart of [true, false]) {
      const { ux, uy } = msDockvec(sd.p1, sd.p2);
      const end = isStart ? sd.p1 : sd.p2; const pt = arAlong(end, ux, uy, isStart ? -10 : 10);
      const h = autoRunHandle(pt, AR_DOT("#22d3ee", 11), `Drag: rotate the run around its ${isStart ? "far" : "start"} end (that end stays put)`, 1002);
      let st = null;
      h.on("dragstart", () => { autoRunDragging = true; st = { p1: { ...sd.p1 }, p2: { ...sd.p2 }, ref: pt }; });
      h.on("drag", (e) => {
        if (!st) return;
        if (isStart) sd.p1 = msRotateAroundPivot(st.p1, st.p2, st.ref, e.latlng);
        else sd.p2 = msRotateAroundPivot(st.p2, st.p1, st.ref, e.latlng);
        live();
      });
      h.on("dragend", () => { st = null; done(); }); autoRunHandleLayer.addLayer(h);
    }
    // 5b. add a slip (+) just beyond each end of the run
    for (const isStart of [true, false]) {
      const { ux, uy } = msDockvec(sd.p1, sd.p2); const off = autoRunOffshore(sd);
      const base = arAlong(isStart ? sd.p1 : sd.p2, ux, uy, isStart ? -6 : 6);
      const pt = arAlong(base, off.x, off.y, Math.abs(sd.lenFt) / 2);
      const h = L.marker([pt.lat, pt.lng], { icon: L.divIcon({ className: "", iconSize: [0, 0], iconAnchor: [0, 0],
        html: '<div title="Click: add a slip at this end of the run" style="width:18px;height:18px;margin-left:-9px;margin-top:-9px;border-radius:50%;background:#10b981;border:2px solid #fff;color:#fff;font:800 14px/14px system-ui,sans-serif;text-align:center;cursor:pointer;box-shadow:0 1px 4px rgba(0,0,0,.7);">+</div>' }), zIndexOffset: 1004 });
      h.on("click", (e) => { L.DomEvent.stop(e.originalEvent || e); autoRunAddBayAtEnd(sd, isStart); autoRunHandlesDirty = true; renderAutoRun(); status = autoRunStatus(); render(); });
      autoRunHandleLayer.addLayer(h);
    }
    // 6. finger centres (orange): drag along the walkway; right-click removes the finger
    for (let k = 1; k < N; k++) {
      const h = autoRunHandle(msPointAtFrac(sd.p1, sd.p2, sd.centerFracs[k]), '<div style="width:10px;height:14px;background:#f59e0b;border:2px solid #fff;border-radius:3px;cursor:ew-resize;margin-left:-5px;margin-top:-7px;box-shadow:0 1px 4px rgba(0,0,0,.7);"></div>', "Drag: finger position. Right-click: remove this finger", 1000);
      h.on("dragstart", () => { autoRunDragging = true; });
      h.on("drag", (e) => {
        const eps = runLenFt > 0.01 ? Math.min(0.1, 0.5 / runLenFt) : 0.001;
        const lo = sd.centerFracs[k - 1] + eps, hi = sd.centerFracs[k + 1] - eps; if (lo >= hi) return;
        sd.centerFracs[k] = Math.min(hi, Math.max(lo, msProjectFrac(sd.p1, sd.p2, e.latlng))); live();
      });
      h.on("dragend", done);
      h.on("contextmenu", (e) => { L.DomEvent.stop(e.originalEvent || e); sd.centerFracs.splice(k, 1); autoRunShiftRemoved(sd, k + 1, -1, [k - 1, k]); autoRunHandlesDirty = true; renderAutoRun(); status = autoRunStatus(); render(); });
      autoRunHandleLayer.addLayer(h);
    }
    // 7. finger width (violet) beside the first AND last interior finger -- every finger on this side
    for (const k of N > 1 ? [...new Set([1, N - 1])] : []) {
      const half = runLenFt > 0.01 ? (sd.fingerWidthFt / 2) / runLenFt : 0;
      const dir = k === 1 ? 1 : -1;
      const h = autoRunHandle(msPointAtFrac(sd.p1, sd.p2, sd.centerFracs[k] + dir * half), AR_DOT("#a855f7", 13, "border-radius:50%;", "ew-resize"), "Drag: finger width (every finger on this side)", 1001);
      h.on("dragstart", () => { autoRunDragging = true; });
      h.on("drag", (e) => {
        const off = (msProjectFrac(sd.p1, sd.p2, e.latlng) - sd.centerFracs[k]) * runLenFt;
        sd.fingerWidthFt = Math.max(0, Math.round(Math.abs(off) * 2 * 10) / 10); live();
      });
      h.on("dragend", done); autoRunHandleLayer.addLayer(h);
    }
  }

  async function commitAutoRun() {
    if (!autoRunSides?.length) return;
    if (!String(state.dockName || "").trim()) { status = "Auto Run: set a Dock in New Slip Defaults before saving (the slips need a dock, same as drawing them)."; render(); return; }
    const plan = autoRunSides.flatMap((sd) => autoRunSlips(sd).slips.filter((sl) => !sl.removed).map((sl) => ({ sl, lenFt: Math.abs(sd.lenFt) })));
    if (!plan.length) { status = "Auto Run: every slip is removed -- nothing to save."; render(); return; }
    const ids = previewSlipIds(state, plan.length);
    const conflictMsg = conflictingSlipIdsMessage(ids);
    if (conflictMsg) { status = conflictMsg; render(); return; }
    const sidesSnapshot = autoRunSides;
    stopAutoRun();
    status = `Auto Run: saving ${plan.length} slips...`; render();
    const createdIds = [];
    try {
      for (let i = 0; i < plan.length; i++) {
        const { sl, lenFt } = plan[i];
        const measurementId = makeId("MEAS");
        await requestJson(API_BASE, {
          method: "POST",
          body: {
            measurement_id: measurementId, marina_id: state.marinaId, marina_name: state.marinaName, slip_id: ids[i],
            dock_name: state.dockName || null, suffix: state.slipIdSuffix || null, region: state.region || null,
            space_type: state.spaceType || "Finger", multislip_yn: "no", multislip_id: null,
            covered_yn: draftCovered ? "yes" : "no", boatlift_yn: draftBoatlift ? "yes" : "no", fueldock_yn: draftFuelDock ? "yes" : "no", ...draftOutOfServiceFields(),
            measured_linear_feet: roundToTenthFoot(lenFt), measured_width_feet: roundToTenthFoot(sl.alongFt), measured_depth_feet: null,
            operational_status: "operable", notes: draftComments || `Auto Run from drafts run ${draftsRun?.runId || ""}`,
            measurement_date: state.measurementDate, confidence_score: state.confidenceScore,
            record_created_timestamp: new Date().toISOString(), created_by_email: app.auth?.email || null,
            geometry_wkt: buildPolygonWkt(sl.corners.map((c) => [c.lat, c.lng])),
          },
        });
        createdIds.push(measurementId);
        incrementSlipIdNumber(state); incrementSlipIdSuffix(state);
      }
      persist();
      await refreshRecords({ quiet: true });
      pushUndoEntry(`Created ${createdIds.length} slips (Auto Run)`, createdIds.map((measurementId) => ({ type: "create", measurementId })));
      app.log("slip.created", { count: createdIds.length, tool: "auto-run", sides: sidesSnapshot.length, marina: state.marinaId });
      status = `Auto Run: saved ${createdIds.length} slips (${ids[0]} – ${ids[ids.length - 1]}).`;
    } catch (err) {
      persist();
      await refreshRecords({ quiet: true }).catch(() => {});
      if (createdIds.length) pushUndoEntry(`Created ${createdIds.length} slips (Auto Run, partial)`, createdIds.map((measurementId) => ({ type: "create", measurementId })));
      status = `Auto Run stopped after ${createdIds.length} of ${plan.length} slips: ${err?.message || err}`;
    }
    render();
  }

  function recordRings() {
    return (records || []).map((r) => ({ id: String(r?.measurement_id || ""), ring: parsePolygonWkt(r?.geometry_wkt) })).filter((r) => r.ring);
  }
  // pending | drawn (overlaps an existing slip -- can't be accepted) | accepted | rejected
  function draftStatuses() {
    const key = `${draftsRun?.runId}|${records.length}|${(records[records.length - 1] || {}).measurement_id}|${JSON.stringify(draftsReview).length}`;
    if (draftsStatusCache.key === key) return draftsStatusCache.byId;
    const rings = recordRings();
    const ids = new Set(rings.map((r) => r.id).concat((records || []).map((r) => String(r?.measurement_id || ""))));
    const bbox = (ring) => ring.reduce((b, [la, ln]) => [Math.min(b[0], la), Math.min(b[1], ln), Math.max(b[2], la), Math.max(b[3], ln)], [90, 180, -90, -180]);
    const rb = rings.map((r) => ({ ...r, b: bbox(r.ring) }));
    const byId = new Map();
    for (const d of draftsRun?.drafts || []) {
      const rv = draftsReview[d.id];
      if (rv?.status === "accepted" && rv.measurementId && ids.has(String(rv.measurementId))) { byId.set(d.id, "accepted"); continue; }
      if (rv?.status === "rejected") { byId.set(d.id, "rejected"); continue; }
      // share of a 5x5 sample grid inside the draft that falls in any existing slip
      const ring = d.ring.slice(0, 4).map(([lng, lat]) => [lat, lng]); const b = bbox(ring);
      const near = rb.filter((r) => r.b[0] <= b[2] && r.b[2] >= b[0] && r.b[1] <= b[3] && r.b[3] >= b[1]);
      let inside = 0, total = 0;
      if (near.length) {
        for (let i = 1; i <= 5; i++) for (let j = 1; j <= 5; j++) {
          const u = i / 6, v = j / 6;
          const lat = ring[0][0] + (ring[1][0] - ring[0][0]) * u + (ring[3][0] - ring[0][0]) * v;
          const lng = ring[0][1] + (ring[1][1] - ring[0][1]) * u + (ring[3][1] - ring[0][1]) * v;
          total++; if (near.some((r) => pointInPolygon({ lat, lng }, r.ring))) inside++;
        }
      }
      byId.set(d.id, total && inside / total >= 0.4 ? "drawn" : "pending");
    }
    draftsStatusCache = { key, byId };
    return byId;
  }
  function draftCounts() {
    const c = { pending: 0, drawn: 0, accepted: 0, rejected: 0 };
    if (draftsRun) for (const v of draftStatuses().values()) c[v] = (c[v] || 0) + 1;
    return c;
  }
  // Draft outlines in the strongest contrast with the current UI theme: black on the light themes,
  // white on the dark ones (read from the theme's --bg, so new themes just work). Status stays in the fill
  // (orange = to review) and the dash (already drawn).
  function draftOutlineColor() {
    const bg = getComputedStyle(document.getElementById("app") || document.documentElement).getPropertyValue("--bg").trim();
    const m = bg.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i);
    if (!m) return "#ffffff";
    const [r, g, b] = m.slice(1).map((h) => parseInt(h, 16) / 255);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5 ? "#000000" : "#ffffff";
  }
  function draftStyle(k) {
    const color = draftOutlineColor();
    return k === "pending"
      ? { color, weight: 2, opacity: 0.95, fillColor: "#f59e0b", fillOpacity: 0.14 }
      : { color, weight: 1, opacity: 0.55, dashArray: "4 4", fillOpacity: 0 };
  }
  let draftsThemeObserver = null;
  // Admin-only while drafts are evaluated on prod -- technicians never see the panel or the layer.
  function draftsAllowed() {
    return (app?.auth?.groups || []).some((g) => String(g || "").trim().toLowerCase() === "admin");
  }
  function renderDrafts() {
    draftsLayer.clearLayers();
    // re-draw on a UI theme switch so the outline colour flips with it
    if (!draftsThemeObserver && typeof MutationObserver !== "undefined" && document.getElementById("app")) {
      draftsThemeObserver = new MutationObserver(() => { if (draftsVisible) renderDrafts(); });
      draftsThemeObserver.observe(document.getElementById("app"), { attributes: true, attributeFilter: ["data-ui-theme"] });
    }
    if (!draftsAllowed() || !draftsVisible || !draftsRun || draftsRun._marina !== String(state.marinaId || "").trim()) return;
    const st = draftStatuses();
    for (const d of draftsRun.drafts) {
      const k = st.get(d.id);
      if (k !== "pending" && k !== "drawn") continue;
      const poly = L.polygon(d.ring.slice(0, 4).map(([lng, lat]) => [lat, lng]), { ...draftStyle(k), interactive: true, bubblingMouseEvents: false });
      const dims = d.type === "Side Tie" ? `${d.widthFt} ft along the dock` : `${d.lengthFt} × ${d.widthFt} ft`;
      poly.bindPopup(() => k === "drawn"
        ? `<div style="font-size:12px"><b>Draft ${esc(d.id)}</b> · ${esc(d.type)}<br>Already drawn -- an existing slip covers it.</div>`
        : `<div style="font-size:12px; min-width:190px"><b>Draft ${esc(d.id)}</b> · ${esc(d.type)}<br>${esc(dims)}<br>
            ${String(state.dockName || "").trim()
              ? `<span style="color:#64748b">Accept creates a new slip with the current New Slip Defaults: dock <b>${esc(state.dockName)}</b>, ID <b>${esc(String(buildSlipIdFromState(state) || ""))}</b>.</span>`
              : `<span style="color:#f87171">Set a dock name in New Slip Defaults to accept drafts (a slip needs a dock, same as drawing one).</span>`}
            <div style="display:flex; gap:6px; margin-top:6px">
              <button type="button" class="btn primary" data-draft-accept="${esc(d.id)}"${String(state.dockName || "").trim() ? "" : " disabled"}>Accept</button>
              <button type="button" class="btn" data-draft-reject="${esc(d.id)}">Reject</button>
            </div></div>`);
      poly.on("popupopen", (e) => {
        const el = e.popup.getElement();
        el?.querySelector("[data-draft-accept]")?.addEventListener("click", () => { map.closePopup(); acceptDraft(d).catch((err) => { status = `Accept failed: ${err?.message || err}`; render(); }); });
        el?.querySelector("[data-draft-reject]")?.addEventListener("click", () => { map.closePopup(); reviewDraft(d, "rejected").catch((err) => { status = `Reject failed: ${err?.message || err}`; render(); }); });
      });
      poly.on("click", (ev) => { if (ev?.originalEvent) ev.originalEvent.__htHandled = "slip-inventory"; });
      poly.addTo(draftsLayer);
    }
  }
  async function reviewDraft(d, decision, measurementId = null) {
    const data = await requestJson(`/api/slip-drafts/${encodeURIComponent(draftsRun._marina)}/review`, {
      method: "POST", body: { runId: draftsRun.runId, draftId: d.id, status: decision, measurementId, by: app.auth?.email || null },
    });
    draftsReview = data?.review || draftsReview;
    draftsStatusCache.key = "";
    if (decision === "rejected") status = `Rejected draft ${d.id}.`;
    renderDrafts(); render();
  }
  // Accept = create a NEW slip exactly as drawing one would (same payload, slip-id collision guard,
  // numbering, undo). Never updates or deletes an existing slip; drafts overlapping one can't be accepted.
  async function acceptDraft(d) {
    if (draftStatuses().get(d.id) !== "pending") throw new Error("this draft overlaps an existing slip");
    if (!String(state.dockName || "").trim()) throw new Error("set a dock name in New Slip Defaults first");
    const slipId = String(buildSlipIdFromState(state) || "").trim() || `AUTO-${d.id}`;
    const conflictMsg = conflictingSlipIdsMessage([slipId]);
    if (conflictMsg) throw new Error(conflictMsg);
    const ring = d.ring.slice(0, 4);
    const wkt = `POLYGON((${ring.concat([ring[0]]).map(([lng, lat]) => `${Number(lng).toFixed(7)} ${Number(lat).toFixed(7)}`).join(", ")}))`;
    const sideTie = d.type === "Side Tie";
    const measurementId = makeId("MEAS");
    await requestJson(API_BASE, {
      method: "POST",
      body: {
        measurement_id: measurementId,
        marina_id: state.marinaId,
        marina_name: state.marinaName,
        slip_id: slipId,
        dock_name: state.dockName || null,
        suffix: state.slipIdSuffix || null,
        region: state.region || null,
        space_type: d.type || state.spaceType || null,
        multislip_yn: "no",
        multislip_id: null,
        covered_yn: "no",
        boatlift_yn: "no",
        fueldock_yn: "no",
        measured_linear_feet: roundToTenthFoot(sideTie ? d.widthFt : d.lengthFt),
        measured_width_feet: roundToTenthFoot(sideTie ? d.lengthFt : d.widthFt),
        measured_depth_feet: null,
        operational_status: "operable",
        notes: `Accepted from auto-detected draft ${draftsRun.runId}/${d.id}`,
        measurement_date: state.measurementDate,
        confidence_score: state.confidenceScore,
        record_created_timestamp: new Date().toISOString(),
        created_by_email: app.auth?.email || null,
        geometry_wkt: wkt,
      },
    });
    incrementSlipIdNumber(state);
    incrementSlipIdSuffix(state);
    persist();
    await refreshRecords({ quiet: true });
    pushUndoEntry(`Accepted draft ${d.id} as ${slipId}`, [{ type: "create", measurementId }]);
    await reviewDraft(d, "accepted", measurementId);
    status = `Accepted draft ${d.id} as slip ${slipId}.`;
    render();
  }

  let _plotExporting = false;
  let _renderRafId = null;
  let _surfacesCatalog = null;
  let _surfacesCatalogLoading = false;
  // Usable for elevation sampling (Auto-Detect Slips): a built single-COG surface, or the
  // project-wide mosaic on a continuous attribute -- classification is categorical class
  // codes, not elevation, so it can't localize fingers/gaps.
  function isSampleableSurface(s) {
    return !!(s?.rasterReady && (s.rasterPath || s.kind === "mosaic2d") && s.attr !== "classification");
  }
  let _autoDetectSurfaceId = "";
  let _fillDepthsSurfaceId = "";
  function render() {
    if (_renderRafId !== null) return;
    try {
      const stack = new Error().stack || "";
      const lines = stack.split("\n").slice(1, 4).map((l) => l.replace(/.*?at\s+/, "").replace(/\s*\(.*\)/, "").trim());
      _perfLastCaller = lines.join(" → ").slice(0, 120);
    } catch {}
    _renderRafId = requestAnimationFrame(() => { _renderRafId = null; _renderNow(); });
  }
  function _renderNow() {
    _perfRenderCount++;
    const _activeMarina = filterProjectId || state.marinaId || "";
    if (_activeMarina && !_surfacesCatalog && !_surfacesCatalogLoading) {
      _surfacesCatalogLoading = true;
      fetch(`/api/surfaces/catalog?marina=${encodeURIComponent(_activeMarina)}`).then(r => r.json()).then(data => {
        _surfacesCatalog = (data.catalog || []).filter(s => s.rasterReady);
        render();
      }).catch(() => { _surfacesCatalog = []; }).finally(() => { _surfacesCatalogLoading = false; });
    }
    renderMap();
    updateSelectionStatusTray(selectedGeometryPolygons());
    updateColorByStatusTray();
    renderBottomPanel();
    if (_scrollTableToSelectionPending) {
      _scrollTableToSelectionPending = false;
      scrollTableRowIntoView(state.selectedMeasurementId);
    }
    if (!containerEl) return;
    if (slipTableHasFocusedEditor()) return;
    const _active = document.activeElement;
    if (_active && containerEl.contains(_active)) {
      const _tag = String(_active.tagName || "").toLowerCase();
      if (_tag === "input" || _tag === "select" || _tag === "textarea" || _active.isContentEditable) return;
    }
    const current = selectedRecord();
    const currentMarina = marinaOptionForId(current?.marina_id, current?.marina_name);
    const defaultMarina = marinaOptionForId(state.marinaId, state.marinaName);
    const currentOptions = [
      currentMarina,
      ...marinaCatalog,
    ]
      .filter((entry, index, arr) => entry && arr.findIndex((candidate) => candidate?.marinaId === entry.marinaId) === index)
      .sort((a, b) => String(a?.marinaId || "").localeCompare(String(b?.marinaId || ""), undefined, { numeric: true, sensitivity: "base" }));
    const previewPoints = linePreviewPoints();
    const previewIds = previewSlipIds(state, previewPoints.length);
    const canGenerateLine = lineLayoutMode === "spacing"
      ? lineEndpoints.length >= 3 && previewPoints.length >= 1
      : lineEndpoints.length >= 2 && previewPoints.length >= 1;
    const selectedGeom = selectedGeometryPolygon();
    const selectedGeomCount = selectedGeometryPolygons().length;
    const canEstimateGeometryImagery = geometryRunLayoutActive && geometryRunEndpoints.length >= 2 && !!selectedGeom;
    const canEstimateImagery = lineLayoutMode !== "spacing" && lineEndpoints.length >= 2;
    const chartSignal = Array.isArray(imageryEstimateDebug?.chosenSignal) ? imageryEstimateDebug.chosenSignal : [];
    const chartWidth = 640;
    const chartHeight = 180;
    const chartPadding = 12;
    const chartPath = buildSparklinePath(chartSignal, chartWidth, chartHeight, chartPadding);
    const chartMin = chartSignal.length ? Math.min(...chartSignal) : 0;
    const chartMax = chartSignal.length ? Math.max(...chartSignal) : 0;
    const chartRange = Math.max(1e-6, chartMax - chartMin);
    const liveCenterRatios = previewPoints.length >= 2 ? linePreviewRatios() : [];
    const chartCenterLines = liveCenterRatios.map((ratio) => {
      const x = chartPadding + (ratio * (chartWidth - (chartPadding * 2)));
      return `<line x1="${x.toFixed(1)}" y1="${chartPadding}" x2="${x.toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(245,158,11,0.55)" stroke-width="1.5" stroke-dasharray="4 4" />`;
    }).join("");
    const chartPeakDots = chartSignal.map((value, index) => {
      const x = chartPadding + ((index / Math.max(1, chartSignal.length - 1)) * (chartWidth - (chartPadding * 2)));
      const y = chartHeight - chartPadding - (((value - chartMin) / chartRange) * (chartHeight - (chartPadding * 2)));
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.6" fill="rgba(147,197,253,0.8)" />`;
    }).join("");
    const geomChartSignal = Array.isArray(geometryImageryEstimateDebug?.chosenSignal) ? geometryImageryEstimateDebug.chosenSignal : [];
    const geomChartPath = buildSparklinePath(geomChartSignal, chartWidth, chartHeight, chartPadding);
    const geomChartMin = geomChartSignal.length ? Math.min(...geomChartSignal) : 0;
    const geomChartMax = geomChartSignal.length ? Math.max(...geomChartSignal) : 0;
    const geomChartRange = Math.max(1e-6, geomChartMax - geomChartMin);
    const geomCenterRatios = Array.isArray(geometryImageryEstimateDebug?.centerRatios) ? geometryImageryEstimateDebug.centerRatios : [];
    const geomChartCenterLines = geomCenterRatios.map((ratio) => {
      const x = chartPadding + (ratio * (chartWidth - (chartPadding * 2)));
      return `<line x1="${x.toFixed(1)}" y1="${chartPadding}" x2="${x.toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(245,158,11,0.6)" stroke-width="1.5" stroke-dasharray="4 4" />`;
    }).join("");
    const geomChartDots = geomChartSignal.map((value, index) => {
      const x = chartPadding + ((index / Math.max(1, geomChartSignal.length - 1)) * (chartWidth - (chartPadding * 2)));
      const y = chartHeight - chartPadding - (((value - geomChartMin) / geomChartRange) * (chartHeight - (chartPadding * 2)));
      return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="1.5" fill="rgba(125,211,252,0.9)" />`;
    }).join("");
    containerEl.innerHTML = `
      <div class="tool-section" style="display:flex; flex-direction:column; gap:12px;">
        <div class="row" style="justify-content:space-between; align-items:flex-start; gap:12px;">
          <div>
            <div class="lm-title" style="margin:0;">Slip Inventory</div>
            <div class="small" style="margin-top:4px; color:var(--muted);">
              Draw slips, select a polygon, then edit its attributes in the inspector.
            </div>
          </div>
          <div class="small" style="text-align:right; color:var(--muted);">
            ${state.marinaName || state.marinaId
              ? `<strong style="color:#e2e8f0;">${esc(state.marinaName || state.marinaId)}</strong>${state.marinaName && state.marinaId && state.marinaName !== state.marinaId ? `<br>${esc(state.marinaId)}` : ""}`
              : `No marina selected`}
            <br>${esc(records.length)} records / ${esc(state.slipGeometryPolygons.length)} polygons
          </div>
        </div>

        <div style="display:flex; flex-direction:column; gap:8px;">
          <div style="padding:6px; border:1px solid rgba(148,163,184,.15); border-radius:8px;">
            <div style="color:var(--muted); margin-bottom:6px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; text-align:center;" title="Never creates a multislip group, regardless of the Multislip checkbox in Attributes below.">Single Slip</div>
            <div style="display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:6px;">
              <button id="slip-geom-place" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${activeGeometryPlaceMode ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}"${state.slipGeometryDraftTypeId ? "" : " disabled"}>
                <span style="display:block; font-weight:700;">Polygon</span>
              </button>
              <button id="slip-geom-place-comb" type="button" class="btn primary" title="Click each bay's two dock-edge points (skip fingers), then one offshore click sets length for all bays at once." style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${combActive ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
                <span style="display:block; font-weight:700;">Comb</span>
              </button>
              <button id="slip-geom-place-fingers" type="button" class="btn primary" title="Same as Multislip's draw flow (click run length, click width, W/X for bay count, T for slips per bay, drag finger centers, drag one shared finger-width handle, then W/X again for slips per bay once width is dialed in) but every slip commits independently -- never tagged with a shared multislip ID." style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${(multislipActive && multislipAsFingers) ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
                <span style="display:block; font-weight:700;">Fingers</span>
              </button>
            </div>
          </div>
          <div style="padding:6px; border:1px solid rgba(148,163,184,.15); border-radius:8px;">
            <div style="color:var(--muted); margin-bottom:6px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; text-align:center;" title="Always creates a shared multislip group, regardless of the Multislip checkbox in Attributes below.">Multislip</div>
            <div style="display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:6px;">
              <button id="slip-geom-place-multislip" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${(multislipActive && !multislipAsFingers) ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
                <span style="display:block; font-weight:700;">Multislip</span>
              </button>
              <button id="slip-geom-place-chain" type="button" class="btn primary" title="Click each point along a bent dock edge (min 3), then one offshore click sets one width for every segment. Segments become slips sharing one multislip ID; corners that can't miter cleanly are flagged for review." style="min-height:36px; padding:5px 8px; text-align:center; justify-content:center; border-radius:18px; ${chainActive ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
                <span style="display:block; font-weight:700;">Chain</span>
              </button>
            </div>
          </div>
          <div style="padding:6px; border:1px solid rgba(148,163,184,.15); border-radius:8px;">
            <div style="color:var(--muted); margin-bottom:6px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; text-align:center;" title="Never creates a multislip group. Click the main dock centerline (2 clicks), how far the fingers extend on each side (2 clicks), then where each side's slips start past the walkway (2 clicks, or Enter to accept the default at right) -- then run the Fingers count/center/width steps once per side.">Full Dock</div>
            <div style="display:flex; gap:6px; align-items:center;">
              <button id="slip-geom-place-full-dock" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; flex:1; text-align:center; justify-content:center; border-radius:18px; ${(fullDockActive || (multislipActive && multislipSideLabel)) ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}">
                <span style="display:block; font-weight:700;">Full Dock</span>
              </button>
              <label class="small" style="display:flex; flex-direction:column; align-items:center; gap:2px; white-space:nowrap;" title="Default distance from the centerline to where slips start on each side, past the main dock's own walkway -- Enter accepts this at the two 'start of slips' setup clicks; a click still overrides it per side.">
                Walkway ft
                <input id="slip-full-dock-walkway-ft" type="number" min="0.5" step="0.5" value="${esc(Number(state.fullDockWalkwayFt) || 15)}" style="width:56px; text-align:center;" />
              </label>
            </div>
          </div>
          <div style="padding:6px; border:1px solid rgba(148,163,184,.15); border-radius:8px;">
            <div style="color:var(--muted); margin-bottom:6px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; text-align:center;" title="Draw one line across a run of fingers -- the tool samples the chosen surface's elevation along it, finds the fingers, and hands the detected slips to Comb mode for you to review and set a length.">Auto-Detect</div>
            <div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap;">
              ${(_surfacesCatalog || []).filter(isSampleableSurface).length
                ? `<select id="slip-auto-detect-surface-select" class="btn" style="flex:1; min-width:110px; padding:3px 6px; font-size:11px;">
                    <option value=""${_autoDetectSurfaceId ? "" : " selected"}>Choose surface…</option>
                    ${(_surfacesCatalog || []).filter(isSampleableSurface).map(s => `<option value="${esc(s.id)}"${String(s.id) === _autoDetectSurfaceId ? " selected" : ""}>${esc(s.name || s.id)}${s.marinaCode ? ` (${s.marinaCode})` : ""}</option>`).join("")}
                  </select>`
                : `<span class="small" style="color:var(--muted);">${_surfacesCatalogLoading ? "Loading…" : "No built surfaces found"}</span>`
              }
              <button id="slip-auto-detect-run" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; flex:1; min-width:120px; text-align:center; justify-content:center; border-radius:18px; ${autoDetectActive ? "background:rgba(34,197,94,0.2);border-color:#22c55e;" : ""}"${(_surfacesCatalog||[]).some(isSampleableSurface) ? "" : " disabled"}>
                <span style="display:block; font-weight:700;">${autoDetectActive ? "Click 2 Points…" : "Auto-Detect"}</span>
              </button>
            </div>
          </div>
          ${draftsAllowed() ? `<div style="padding:6px; border:1px solid rgba(245,158,11,.35); border-radius:8px;">
            <div style="color:var(--muted); margin-bottom:6px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em; text-align:center;" title="Admin only while drafts are being evaluated. Slips auto-detected from the point cloud, kept apart from the slip inventory. Click a dashed orange draft to Accept it (creates a new slip with the New Slip Defaults) or Reject it. Grey drafts are already covered by an existing slip.">Drafts (auto-detected)</div>
            <div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap;">
              <button id="slip-drafts-toggle" type="button" class="btn primary" style="min-height:36px; padding:5px 8px; flex:1; min-width:110px; justify-content:center; border-radius:18px; ${draftsVisible ? "background:rgba(245,158,11,0.2);border-color:#f59e0b;" : ""}"${draftsLoading ? " disabled" : ""}>
                <span style="display:block; font-weight:700;">${draftsLoading ? "Loading…" : draftsVisible ? "Hide Drafts" : "Show Drafts"}</span>
              </button>
              ${draftsVisible ? `<button id="slip-drafts-reload" type="button" class="btn" title="Reload drafts and review decisions">↻</button>` : ""}
              ${draftsVisible && autoRunAvailable() ? `<button id="slip-auto-run" type="button" class="btn${autoRunBusy() ? " primary" : ""}" title="Lasso a pier: both sides come up as an editable run of finger slips, seeded from the detected fingers. Adjust with the handles, Enter saves.">${autoRunBusy() ? "Auto Run…" : "Auto Run"}</button>` : ""}
              ${draftsVisible && draftsEnabled && !draftsLoading && state.marinaId && !draftsRequestActive() ? `<button id="slip-drafts-create" type="button" class="btn" title="Run slip detection on this marina's SLAM point cloud (on the devbox, a few minutes or more) and publish the result here as drafts"${draftsRequesting ? " disabled" : ""}>${draftsRun ? "Re-run" : "Create Drafts"}</button>` : ""}
            </div>
            ${draftsVisible ? `<div class="small" style="margin-top:6px; color:var(--muted); text-align:center;">${
              draftsError ? `<span style="color:#f87171">${esc(draftsError)}</span>`
              : !draftsEnabled ? "Drafts are not enabled on this server."
              : (!draftsRun ? (draftsRequestActive() ? "" : `No drafts for ${esc(state.marinaId || "this marina")} yet -- click Create Drafts.`)
              : (() => { const c = draftCounts(); return `${c.pending} to review · ${c.drawn} already drawn · ${c.accepted} accepted · ${c.rejected} rejected<br><span style="font-size:10px">run ${esc(draftsRun.runId)}</span>${draftsScoreHtml()}`; })())
              + draftsRequestHtml()
            }</div>` : ""}
          </div>` : ""}
        </div>

        <div class="subtoolset-list" style="--subtoolset-bleed-top:0px;">
          <details class="toolset-group" open>
            <summary>New Slip Defaults</summary>
            <div class="toolset-content">
            <div class="row" style="gap:8px; align-items:center; margin-bottom:8px;">
              <div class="small">
                <div style="color:var(--muted); margin-bottom:4px; font-size:10px; text-transform:uppercase; letter-spacing:0.05em;">Marina (project settings)</div>
                <div class="row" style="gap:8px; align-items:center;">
                  ${state.marinaName || state.marinaId
                    ? `<span style="font-size:13px; color:#e2e8f0;">${esc(state.marinaName || state.marinaId)}${state.marinaName && state.marinaId && state.marinaName !== state.marinaId ? ` <span style="color:var(--muted); font-size:11px;">(${esc(state.marinaId)})</span>` : ""}</span>`
                    : `<span style="color:var(--muted); font-size:12px; font-style:italic;">Not set — select a marina in project settings</span>`}
                  <button id="slip-default-marina-zoom" type="button" class="btn"${defaultMarina && Number.isFinite(defaultMarina.lat) && Number.isFinite(defaultMarina.lng) ? "" : " disabled"}>Zoom To</button>
                </div>
              </div>
            </div>
            <div style="margin-bottom:10px; border:1px solid ${lassoToolActive ? "rgba(245,158,11,.4)" : "rgba(148,163,184,.15)"}; border-radius:6px; overflow:hidden;">
              <div style="padding:8px; display:flex; justify-content:space-between; align-items:center; gap:8px; border-bottom:1px solid rgba(148,163,184,.15);">
                <div style="color:var(--muted); font-size:10px; text-transform:uppercase; letter-spacing:0.05em;">Slip ID / Attributes</div>
                <div style="display:flex; gap:6px;">
                  ${lassoToolActive ? `<button id="slip-lasso-clear" type="button" class="btn" style="font-size:11px; padding:3px 8px;">Clear</button>` : ""}
                  <button id="slip-lasso-tool-toggle" type="button" class="btn${lassoToolActive ? " primary" : ""}"${lassoToolActive ? ' style="border-color:#f59e0b;color:#f59e0b;"' : ""}>${lassoToolActive ? "Lasso: ON" : "Lasso"}</button>
                </div>
              </div>
              <div class="small" style="padding:6px 8px; color:${lassoToolActive ? "#fbbf24" : "var(--muted)"}; border-bottom:1px solid rgba(148,163,184,.15);">
                ${lassoToolActive
                  ? "Ctrl/Cmd-drag through committed slips to apply every checked field on the right. Click Lasso again to turn off."
                  : "Turn on Lasso to arm the per-field toggles on the right, then Ctrl/Cmd-drag through committed slips."}
              </div>
              <div style="display:grid; grid-template-columns:${FIELD_LABEL_COL_WIDTH}px 1fr 60px; gap:8px 10px; align-items:center; padding:8px;${lassoToolActive ? " background:rgba(245,158,11,.06);" : ""}">
                <span></span><span></span>
                <div style="text-align:center; font-size:9px; color:var(--muted); text-transform:uppercase; letter-spacing:.04em;">Lasso</div>

                <div style="grid-column:1/-1; color:var(--muted); font-size:10px; text-transform:uppercase; letter-spacing:.05em;">Slip ID</div>

                <label class="small" for="slip-default-dock">Dock</label>
                <input id="slip-default-dock" type="text" value="${esc(state.dockName)}" placeholder="A" />
                ${lassoToggleCell("slip-lasso-include-dock", lassoIncludeDock)}

                <label class="small" for="slip-default-slipid-part3">Slip Number</label>
                <div style="display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
                  <input id="slip-default-slipid-part3" type="text" value="${esc(state.slipIdPart3)}" placeholder="001" style="flex:1; min-width:56px;" />
                  <label class="small" style="display:flex; align-items:center; gap:3px; font-weight:400; white-space:nowrap;">
                    <input id="slip-default-slipid-autoinc" type="checkbox" ${state.slipIdAutoIncrement ? "checked" : ""} /> Auto inc.
                  </label>
                  <label class="small" for="slip-default-slipid-step" style="display:flex; align-items:center; gap:3px; font-weight:400; white-space:nowrap;">
                    Step <input id="slip-default-slipid-step" type="number" min="1" step="1" value="${esc(state.slipIdIncrementBy)}" style="width:46px;" />
                  </label>
                </div>
                ${lassoToggleCell("slip-lasso-include-slipnumber", lassoIncludeSlipNumber)}

                <label class="small" for="slip-default-suffix">Suffix</label>
                <div style="display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
                  <input id="slip-default-suffix" type="text" value="${esc(state.slipIdSuffix)}" placeholder="A (optional)" style="flex:1; min-width:56px;" />
                  <label class="small" style="display:flex; align-items:center; gap:3px; font-weight:400; white-space:nowrap;">
                    <input id="slip-default-slipid-autoinc-suffix" type="checkbox" ${state.slipIdAutoIncrementSuffix ? "checked" : ""} /> Auto inc.
                  </label>
                  <label class="small" for="slip-default-slipid-step-suffix" style="display:flex; align-items:center; gap:3px; font-weight:400; white-space:nowrap;">
                    Step <input id="slip-default-slipid-step-suffix" type="number" min="1" step="1" value="${esc(state.slipIdIncrementBySuffix)}" style="width:46px;" />
                  </label>
                </div>
                ${lassoToggleCell("slip-lasso-include-suffix", lassoIncludeSuffix)}

                <label class="small" for="slip-default-region" title="Only needed for a marina with multiple same-numbered sub-areas (e.g. North Yard's B-001 vs South Yard's B-001). Not part of the Slip ID text -- set per-slip in the table, or by Lasso.">Region</label>
                <input id="slip-default-region" type="text" value="${esc(state.region)}" placeholder="e.g. North Yard (optional, rare)" title="Only needed for a marina with multiple same-numbered sub-areas (e.g. North Yard's B-001 vs South Yard's B-001). Not part of the Slip ID text -- set per-slip in the table, or by Lasso." />
                ${lassoToggleCell("slip-lasso-include-region", lassoIncludeRegion)}

                <label class="small" for="slip-default-space-type">Slip Type</label>
                <select id="slip-default-space-type">
                  <option value="">-</option>
                  ${SPACE_TYPE_OPTIONS.map((o) => `<option value="${esc(o)}"${state.spaceType === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
                </select>
                ${lassoToggleCell("slip-lasso-include-sliptype", lassoIncludeSlipType)}

                <span></span>
                <label class="small" style="display:flex; align-items:center; gap:6px;" title="New slips from Multislip/Chain always get a multislip ID and Polygon/Comb never do, regardless of this. By Lasso: on shares one new multislip ID across every touched slip; off clears multislip from every touched slip, regardless of its current membership.">
                  <input id="slip-default-multislip" type="checkbox" ${draftMultislip ? "checked" : ""} />
                  Multislip
                </label>
                ${lassoToggleCell("slip-lasso-include-multislip", lassoIncludeMultislip)}

                <div style="grid-column:1/-1; color:var(--muted); font-size:10px; text-transform:uppercase; letter-spacing:.05em; margin-top:4px; padding-top:6px; border-top:1px solid rgba(148,163,184,.1);">Attributes</div>

                <span></span>
                <label class="small" style="display:flex; align-items:center; gap:6px;">
                  <input id="slip-default-covered" type="checkbox" ${draftCovered ? "checked" : ""} />
                  Covered
                </label>
                ${lassoToggleCell("slip-lasso-include-covered", lassoIncludeCovered)}

                <span></span>
                <label class="small" style="display:flex; align-items:center; gap:6px;">
                  <input id="slip-default-boatlift" type="checkbox" ${draftBoatlift ? "checked" : ""} />
                  Boatlift
                </label>
                ${lassoToggleCell("slip-lasso-include-boatlift", lassoIncludeBoatlift)}

                <span></span>
                <label class="small" style="display:flex; align-items:center; gap:6px;">
                  <input id="slip-default-fueldock" type="checkbox" ${draftFuelDock ? "checked" : ""} />
                  Fuel dock
                </label>
                ${lassoToggleCell("slip-lasso-include-fueldock", lassoIncludeFuelDock)}

                <span></span>
                <div style="display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
                  <label class="small" style="display:flex; align-items:center; gap:6px;" title="New slips are left blank (in service) unless this is checked. By Lasso: on marks every touched slip out of service with the chosen reason; off sets them back to in service and clears the reason.">
                    <input id="slip-default-out-of-service" type="checkbox" ${draftOutOfService ? "checked" : ""} />
                    Out of service
                  </label>
                  ${draftOutOfService ? `<select id="slip-default-oos-reason" style="flex:1; min-width:100px;" aria-label="Out of service reason">
                    <option value=""${draftOosReason ? "" : " selected"}>Reason (optional)</option>
                    ${OOS_REASON_OPTIONS.map((o) => `<option value="${esc(o)}"${draftOosReason === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
                  </select>` : ""}
                </div>
                ${lassoToggleCell("slip-lasso-include-outofservice", lassoIncludeOutOfService)}

                <div style="grid-column:1/-1; color:var(--muted); font-size:10px; text-transform:uppercase; letter-spacing:.05em; margin-top:4px; padding-top:6px; border-top:1px solid rgba(148,163,184,.1);">Swap Length/Width</div>
                <div style="grid-column:1/span 2;" class="small">Fixes slips drawn with length/width backwards -- e.g. the yellow ones under Orientation QC coloring. Swaps each touched slip's own measured length and width; doesn't redraw its shape.</div>
                ${lassoToggleCell("slip-lasso-include-swaplw", lassoIncludeSwapLw)}
              </div>
            </div>

            </div>
          </details>

          <details class="toolset-group" open>
            <summary>Selected Slip Attributes <span style="font-size:10px; font-weight:400; text-transform:none; letter-spacing:normal; color:var(--muted);">${current ? esc(current.measurement_id) : (selectedGeom ? "Uncommitted polygon" : "No selection")}</span></summary>
            <div class="toolset-content">
            ${current ? `
              <table style="width:100%; border-collapse:collapse; margin-top:8px; font-size:11px; table-layout:fixed;">
                <tbody>
                  <tr>
                    <td style="width:92px; color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Slip Number</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-number" type="text" value="${esc(slipNumberFromRecord(current))}" placeholder="001" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" title="Combines with Dock (${esc(current.dock_name || "no dock set")}) and Suffix to form the Slip ID: ${esc(current.slip_id || "")}" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Suffix</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-suffix" type="text" value="${esc(slipSuffixFromRecord(current))}" placeholder="(optional)" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" title="Combines with Dock and Slip Number to form the Slip ID: ${esc(current.slip_id || "")}" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Slip Type</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-space-type" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value=""></option>
                        ${spaceTypeOptionsFor(current.space_type).map((o) => `<option value="${esc(o)}"${String(current.space_type || "") === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Marina</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-marina-id" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value="">Select marina</option>
                        ${currentOptions.map((entry) => `<option value="${esc(entry.marinaId)}"${entry.marinaId === current.marina_id ? " selected" : ""}>${esc(entry.marinaId)}</option>`).join("")}
                      </select>
                      <input id="slip-marina-name" type="hidden" value="${esc(currentMarina?.marinaName || current.marina_name)}" />
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Length ft</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-linear-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_linear_feet))}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Width ft</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-width-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_width_feet))}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Depth ft</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-depth-feet" type="number" step="0.01" value="${esc(current.measured_depth_feet ?? "")}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Confidence</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-confidence" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value="High"${current.confidence_score === "High" ? " selected" : ""}>High</option>
                        <option value="Medium"${current.confidence_score === "Medium" ? " selected" : ""}>Medium</option>
                        <option value="Low"${current.confidence_score === "Low" ? " selected" : ""}>Low</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Date</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);"><input id="slip-measurement-date" type="date" value="${esc(current.measurement_date)}" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;" /></td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Status</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <select id="slip-operational-status" style="width:100%; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">
                        <option value="operable"${String(current.operational_status || "operable") === "operable" ? " selected" : ""}>operable</option>
                        <option value="under_construction"${String(current.operational_status || "") === "under_construction" ? " selected" : ""}>under_construction</option>
                        <option value="maintenance"${String(current.operational_status || "") === "maintenance" ? " selected" : ""}>maintenance</option>
                        <option value="blocked"${String(current.operational_status || "") === "blocked" ? " selected" : ""}>blocked</option>
                        <option value="closed"${String(current.operational_status || "") === "closed" ? " selected" : ""}>closed</option>
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td style="color:var(--muted); vertical-align:top; padding:5px 6px; border-bottom:1px solid rgba(148,163,184,0.1);">Notes</td>
                    <td style="padding:3px 0; border-bottom:1px solid rgba(148,163,184,0.1);">
                      <textarea id="slip-notes" rows="2" style="width:100%; resize:vertical; border:0; border-radius:0; background:transparent; color:#e2e8f0; padding:3px 4px;">${esc(current.notes || "")}</textarea>
                      <textarea id="slip-geometry-wkt" style="display:none;">${esc(current.geometry_wkt || "")}</textarea>
                      <input id="slip-record-created-timestamp" type="hidden" value="${esc(current.record_created_timestamp || "")}" />
                    </td>
                  </tr>
                </tbody>
              </table>
              <div class="row" style="gap:8px; margin-top:8px;">
                <button id="slip-save" type="button" class="btn primary">Save Attributes</button>
                <button id="slip-swap-lw" type="button" class="btn" title="Swap measured length and width">⇄ Swap L/W</button>
                <button id="slip-show-selected" type="button" class="btn">Zoom To</button>
                <button id="slip-delete" type="button" class="btn">Delete</button>
              </div>
            ` : selectedGeom ? `
              <div class="small" style="margin-top:8px; color:var(--muted);">This polygon is selected but has not been committed to a slip record.</div>
              <div style="display:grid; grid-template-columns:repeat(2,minmax(150px,1fr)); gap:8px; margin-top:8px;">
                <label class="small">Length: ${esc(Number(selectedGeom?.lengthFt ?? 0).toFixed(1))} ft
                  <input id="slip-geom-slider-length" type="range" min="5" max="120" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.lengthFt ?? 40)}" />
                </label>
                <label class="small">Width: ${esc(Number(selectedGeom?.widthFt ?? 0).toFixed(1))} ft
                  <input id="slip-geom-slider-width" type="range" min="3" max="40" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.widthFt ?? 14)}" />
                </label>
                <label class="small">Rotation: ${esc(Number(selectedGeom?.headingDeg ?? 0).toFixed(1))} deg
                  <input id="slip-geom-slider-rotation" type="range" min="-180" max="180" step="${esc(rotationToleranceStep())}" value="${esc(selectedGeom?.headingDeg ?? 0)}" />
                </label>
              </div>
              <div class="row" style="gap:8px; margin-top:8px;">
                <button id="slip-geom-commit-selected" type="button" class="btn primary">Create Slip Record</button>
                <button id="slip-geom-delete-selected" type="button" class="btn">Delete Polygon</button>
                <button id="slip-geom-clear-selection" type="button" class="btn">Clear Selection</button>
              </div>
            ` : `
              <div style="margin-top:10px; padding:18px; border:1px dashed rgba(148,163,184,0.28); border-radius:8px; color:var(--muted); text-align:center;">
                Select a slip polygon or a row in the table to edit attributes.
                ${state.slipGeometryPolygons.length ? `
                  <div style="margin-top:10px;">
                    <button id="slip-geom-select-uncommitted" type="button" class="btn" title="Click repeatedly to cycle through every uncommitted polygon, panning the map to each -- use this to recover a draft that lost its selection (e.g. after a page reload) before it was saved.">Select Latest Uncommitted</button>
                  </div>
                ` : ""}
              </div>
            `}
            </div>
          </details>
        </div>

        <div class="row" style="gap:8px; flex-wrap:wrap; align-items:center; padding-top:2px;">
          ${renderSlipDisplayToggles()}
          <label class="small">Color
            <select id="slip-color-by" style="min-width:130px;">
              ${renderColorSlipsByOptions(state.colorSlipsBy)}
            </select>
          </label>
          <button id="slip-geom-apply-report" type="button" class="btn primary"${records.length && state.slipGeometryPolygons.length ? "" : " disabled"}>Update Report</button>
          <button id="slip-refresh" type="button" class="btn">Refresh</button> ${renderSlipExportMenu({ disabled: !records.length })}
        </div>
        <div class="small" style="color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
      </div>

      <details hidden style="display:none; margin-top:10px;">
        <summary class="small" style="cursor:pointer; color:#93c5fd; padding:8px 10px; border:1px solid rgba(148,163,184,0.18); border-radius:8px; background:rgba(15,23,42,0.22);">
          Advanced tools
        </summary>
        <div style="margin-top:8px;">
      <div class="tool-section">
        <div class="small">Create database records by dropping a point on the map, then edit the current export-schema fields here.</div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <label class="small">Dock
            <input id="slip-default-dock" type="text" value="${esc(state.dockName)}" style="width:70px;" placeholder="A" />
          </label>
          <label class="small">Slip ID
            <div class="row" style="gap:4px; align-items:center; margin-top:3px;">
              <span style="font-size:12px; color:var(--muted); min-width:18px;">${esc(state.dockName || "—")}</span>
              <select id="slip-default-slipid-separator" style="width:50px; padding:2px 2px;">
                <option value="-"${state.slipIdSeparator === "-" ? " selected" : ""}>-</option>
                <option value="."${state.slipIdSeparator === "." ? " selected" : ""}>.</option>
              </select>
              <input id="slip-default-slipid-part3" type="text" value="${esc(state.slipIdPart3)}" style="width:65px;" placeholder="001" />
            </div>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-slipid-autoinc" type="checkbox" ${state.slipIdAutoIncrement ? "checked" : ""} />
            Auto Inc
          </label>
          <label class="small">Step
            <input id="slip-default-slipid-step" type="number" min="1" step="1" value="${esc(state.slipIdIncrementBy)}" style="width:58px;" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-covered" type="checkbox" ${draftCovered ? "checked" : ""} />
            Covered
          </label>
          <label class="small">Slip Type
            <select id="slip-default-space-type">
              <option value="">—</option>
              ${SPACE_TYPE_OPTIONS.map((o) => `<option value="${esc(o)}"${state.spaceType === o ? " selected" : ""}>${esc(o)}</option>`).join("")}
            </select>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-boatlift" type="checkbox" ${draftBoatlift ? "checked" : ""} />
            Boatlift
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; padding-bottom:4px;">
            <input id="slip-default-fueldock" type="checkbox" ${draftFuelDock ? "checked" : ""} />
            Fuel Dock
          </label>
          <label class="small">Confidence
            <select id="slip-default-confidence">
              <option value="High"${state.confidenceScore === "High" ? " selected" : ""}>High</option>
              <option value="Medium"${state.confidenceScore === "Medium" ? " selected" : ""}>Medium</option>
              <option value="Low"${state.confidenceScore === "Low" ? " selected" : ""}>Low</option>
            </select>
          </label>
          <label class="small">Measurement Date
            <input id="slip-default-measurement-date" type="date" value="${esc(state.measurementDate)}" />
          </label>
          <label class="small">Number of Slips
            <input id="slip-default-line-count" type="number" min="1" step="1" value="${esc(state.lineSlipCount)}" style="width:90px;" />
          </label>
          <div class="small" style="padding-bottom:6px; min-width:160px;">Next Slip ID: <strong>${esc(buildSlipIdFromState(state) || "auto")}</strong></div>
          <label class="small" style="flex:1; min-width:180px;">Notes
            <textarea id="slip-default-comments" rows="2" style="width:100%; resize:vertical; font-family:inherit; font-size:11px; background:rgba(2,6,23,.8); border:1px solid rgba(148,163,184,0.2); border-radius:4px; color:#e2e8f0; padding:4px 6px; box-sizing:border-box;">${esc(draftComments)}</textarea>
          </label>
          <div class="row" style="margin-left:auto; gap:8px; align-items:flex-end;">
            <button id="slip-drop-point" type="button" class="btn primary">${pointDropActive ? "Click Map..." : "Drop Point"}</button>
            <button id="slip-stop-drop" type="button" class="btn"${pointDropActive ? "" : " disabled"}>Stop</button>
            <button id="slip-spacing-run-layout" type="button" class="btn primary"${lineLayoutActive ? " disabled" : ""}>Spacing Run</button>
            <button id="slip-line-clear" type="button" class="btn"${lineEndpoints.length ? "" : " disabled"}>Clear Line</button>
            <button id="slip-line-estimate-imagery" type="button" class="btn"${canEstimateImagery ? "" : " disabled"}>Auto Estimate From Imagery</button>
            <button id="slip-line-generate" type="button" class="btn"${canGenerateLine ? "" : " disabled"}>Generate Line</button>
            <button id="slip-line-commit-feature" type="button" class="btn"${lineEndpoints.length >= 2 ? "" : " disabled"}>Commit Line</button>
            <button id="slip-refresh" type="button" class="btn">Refresh</button> ${renderSlipExportMenu({ disabled: !records.length })}
          </div>
        </div>
        <div style="margin-top:10px;padding-top:10px;border-top:1px solid rgba(148,163,184,0.15);">
          <div class="small" style="font-weight:600;margin-bottom:6px;color:var(--muted);">FILL DEPTHS FROM SURFACE</div>
          <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap;">
            ${(_surfacesCatalog || []).length
              ? `<select id="slip-depth-surface-select" class="btn" style="flex:1;min-width:120px;padding:3px 6px;">
                  <option value=""${_fillDepthsSurfaceId ? "" : " selected"}>Choose surface…</option>
                  ${(_surfacesCatalog || []).filter(s=>s.rasterReady).map(s=>`<option value="${esc(s.id)}"${String(s.id) === _fillDepthsSurfaceId ? " selected" : ""}>${esc(s.name || s.id)}${s.marinaCode ? ` (${s.marinaCode})` : ""}</option>`).join("")}
                </select>`
              : `<span class="small" style="color:var(--muted);">${_surfacesCatalogLoading ? "Loading…" : "No built surfaces found"}</span>`
            }
            <button id="slip-fill-depths-run" type="button" class="btn primary"${filterProjectId && (_surfacesCatalog||[]).some(s=>s.rasterReady) ? "" : " disabled"}>Fill Depths</button>
          </div>
          <div id="slip-fill-depths-status" class="small" style="color:var(--muted);margin-top:4px;min-height:14px;"></div>
        </div>
        ${lineEndpoints.length ? `
          <div class="small" style="margin-top:8px;">
            ${lineLayoutMode === "spacing"
              ? (lineEndpoints.length === 1
                ? "First slip center set. Click the next slip center to measure spacing."
                : lineEndpoints.length === 2
                  ? `Spacing measured. Move along the pier to preview ${previewPoints.length} slips, then click the end of the run.`
                  : `Spacing run ready with ${previewPoints.length} slips. Press Enter or Generate Line.`)
              : (lineEndpoints.length === 1
                ? "Line start set. Click the end point on the map."
                : `Previewing ${previewPoints.length} slips along the line. Click a preview mark to snap the count to that position.`)}
          </div>
          ${previewIds.length ? `<div class="small" style="margin-top:4px;">Preview IDs: ${esc(previewIds.join(", "))}</div>` : ""}
        ` : ""}
        ${imageryEstimateStatus ? `<div class="small" style="margin-top:6px; color:#93c5fd;">${esc(imageryEstimateStatus)}</div>` : ""}
        ${chartSignal.length ? `
          <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,.18); border-radius:8px; background:rgba(15,23,42,.35);">
            <div class="small" style="margin-bottom:6px;">Imagery signal debug: blue line is sampled imagery intensity change, dashed orange lines are suggested slip centers.</div>
            <svg viewBox="0 0 ${chartWidth} ${chartHeight}" style="display:block; width:100%; height:auto;">
              <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" rx="8" fill="rgba(15,23,42,0.5)" />
              <line x1="${chartPadding}" y1="${(chartHeight - chartPadding).toFixed(1)}" x2="${(chartWidth - chartPadding).toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(148,163,184,0.35)" stroke-width="1" />
              ${chartCenterLines}
              <path d="${chartPath}" fill="none" stroke="rgba(147,197,253,0.95)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />
              ${chartPeakDots}
            </svg>
          </div>
        ` : ""}
      </div>
      <div class="tool-section" style="margin-top:10px;">
        <div class="row" style="justify-content:space-between; align-items:center; gap:8px;">
          <div class="lm-title">Slip Geometry (Phase 1)</div>
          <div class="small">${esc(state.slipGeometryPolygons.length)} polygon${state.slipGeometryPolygons.length === 1 ? "" : "s"} | selected: ${esc(selectedGeomCount)}</div>
        </div>
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <label class="small">Type Name
            <input id="slip-geom-type-name" type="text" value="Standard" style="width:120px;" />
          </label>
          <label class="small">Length (ft)
            <input id="slip-geom-type-length" type="number" min="0.5" step="0.5" value="40" style="width:90px;" />
          </label>
          <label class="small">Width (ft)
            <input id="slip-geom-type-width" type="number" min="0.5" step="0.5" value="14" style="width:90px;" />
          </label>
          <button id="slip-geom-type-add" type="button" class="btn">Add Type</button>
          <label class="small">Active Type
            <select id="slip-geom-active-type" style="min-width:180px;">
              <option value="">Select type</option>
              ${state.slipGeometryTypes.map((entry) => `<option value="${esc(entry.id)}"${String(entry.id) === String(state.slipGeometryDraftTypeId || "") ? " selected" : ""}>${esc(entry.name)} (${esc(Number(entry.lengthFt).toFixed(1))}x${esc(Number(entry.widthFt).toFixed(1))})</option>`).join("")}
            </select>
          </label>
          <button id="slip-geom-place" type="button" class="btn primary"${state.slipGeometryDraftTypeId ? "" : " disabled"}>${activeGeometryPlaceMode ? "Click Map..." : "Place Polygon"}</button>
          <button id="slip-geom-place-line" type="button" class="btn primary">${lineLayoutActive ? "Layout Active..." : "Place Line"}</button>
          <button id="slip-geom-place-multislip" type="button" class="btn primary" title="Place side-tie multislip. Shift+click for width-first mode.">${
            multislipActive
              ? (multislipWidthFt !== 0
                  ? `${multislipPartitions} Slip${multislipPartitions > 1 ? "s" : ""} — W/X/↵`
                  : multislipP2 ? (multislipMode === "width" ? "Click Length..." : "Click Width...") : multislipP1 ? (multislipMode === "width" ? "Click Width End..." : "Click Dock End...") : (multislipMode === "width" ? "Click Width Start..." : "Click Dock Start..."))
              : "Place Multislip"
          }</button>
          ${renderSlipDisplayToggles()}
          <label class="small">Color Slips By
            <select id="slip-color-by" style="min-width:150px;">
              ${renderColorSlipsByOptions(state.colorSlipsBy)}
            </select>
          </label>
          ${state.colorSlipsBy === "dimension" ? `
          <label class="small">Dimension Color Tolerance
            <select id="slip-dimension-color-tol" style="min-width:120px;">
              <option value="0.5"${Number(state.dimensionColorToleranceFt) === 0.5 ? " selected" : ""}>0.5 ft</option>
              <option value="1"${Number(state.dimensionColorToleranceFt) === 1 ? " selected" : ""}>1.0 ft</option>
            </select>
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px; min-width:160px;">
            <input id="slip-dimension-legend-toggle" type="checkbox" ${state.showDimensionLegend === false ? "" : "checked"} />
            Show Dimension Legend
          </label>
          ` : ""}
          <button id="slip-geom-delete-selected" type="button" class="btn"${selectedGeomCount ? "" : " disabled"}>Delete Selected</button>
          <button id="slip-geom-select-uncommitted" type="button" class="btn"${state.slipGeometryPolygons.length ? "" : " disabled"}>Select Latest Uncommitted</button>
          <button id="slip-geom-clear-selection" type="button" class="btn"${selectedGeomCount ? "" : " disabled"}>Clear Selection</button>
          <button id="slip-geom-clear-all" type="button" class="btn"${state.slipGeometryPolygons.length ? "" : " disabled"}>Clear All</button>
          <button id="slip-geom-purge-all" type="button" class="btn"${state.slipGeometryPolygons.length ? "" : " disabled"}>Purge Stale</button>
        </div>
        ${state.colorSlipsBy === "confidence" ? `
          <div class="small" style="margin-top:6px; display:flex; gap:12px; align-items:center;">
            <span>Legend:</span>
            <span><span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:#16a34a; margin-right:6px;"></span>High</span>
            <span><span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:#f59e0b; margin-right:6px;"></span>Medium</span>
            <span><span style="display:inline-block; width:10px; height:10px; border-radius:50%; background:#dc2626; margin-right:6px;"></span>Low</span>
          </div>
        ` : ""}
        <div class="row" style="margin-top:8px; gap:8px; flex-wrap:wrap; align-items:flex-end;">
          <button id="slip-geom-lock-toggle" type="button" class="btn"${selectedGeom ? "" : " disabled"}>${selectedGeom?.locked ? "Unlock" : "Lock"}</button>
          <label class="small">Number of Slips
            <input id="slip-geom-run-between" type="number" min="1" step="1" value="${esc(state.slipGeometryRunBetweenCount)}" style="width:90px;" />
          </label>
          <button id="slip-geom-layout-run" type="button" class="btn"${selectedGeom ? "" : " disabled"}>${geometryRunLayoutActive ? "Layout Active..." : "Layout Run"}</button>
          <button id="slip-geom-estimate-run-imagery" type="button" class="btn"${canEstimateGeometryImagery ? "" : " disabled"}>Detect Run From GeoTIFF</button>
          <button id="slip-geom-generate-run" type="button" class="btn"${geometryRunEndpoints.length >= 2 ? "" : " disabled"}>Generate Run</button>
          <button id="slip-geom-cancel-run" type="button" class="btn"${geometryRunLayoutActive ? "" : " disabled"}>Cancel Run</button>
          <button id="slip-geom-hotkey-map" type="button" class="btn">Hotkey Map</button>
          <button id="slip-geom-apply-report" type="button" class="btn primary"${records.length && state.slipGeometryPolygons.length ? "" : " disabled"}>Update Slip Report from Geometry</button>
          <label class="small">L/W Tolerance
            <select id="slip-geom-tolerance-lw">
              <option value="0.1"${Number(state.lengthWidthToleranceFt) === 0.1 ? " selected" : ""}>0.1 ft</option>
              <option value="0.25"${Number(state.lengthWidthToleranceFt) === 0.25 ? " selected" : ""}>0.25 ft</option>
              <option value="0.5"${Number(state.lengthWidthToleranceFt) === 0.5 ? " selected" : ""}>0.5 ft</option>
              <option value="1"${Number(state.lengthWidthToleranceFt) === 1 ? " selected" : ""}>1 ft</option>
            </select>
          </label>
          <label class="small">Rotation Tolerance
            <select id="slip-geom-tolerance-rot">
              <option value="0.1"${Number(state.rotationToleranceDeg) === 0.1 ? " selected" : ""}>0.1 deg</option>
              <option value="0.5"${Number(state.rotationToleranceDeg) === 0.5 ? " selected" : ""}>0.5 deg</option>
              <option value="1"${Number(state.rotationToleranceDeg) === 1 ? " selected" : ""}>1 deg</option>
            </select>
          </label>
        </div>
        <div style="margin-top:8px; display:grid; grid-template-columns:repeat(2,minmax(180px,1fr)); gap:8px;">
          <label class="small">Length: ${esc(Number(selectedGeom?.lengthFt ?? 0).toFixed(1))} ft
            <input id="slip-geom-slider-length" type="range" min="5" max="120" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.lengthFt ?? 40)}" ${selectedGeom ? "" : "disabled"} />
          </label>
          <label class="small">Width: ${esc(Number(selectedGeom?.widthFt ?? 0).toFixed(1))} ft
            <input id="slip-geom-slider-width" type="range" min="3" max="40" step="${esc(lengthWidthToleranceStep())}" value="${esc(selectedGeom?.widthFt ?? 14)}" ${selectedGeom ? "" : "disabled"} />
          </label>
          <label class="small">Rotation: ${esc(Number(selectedGeom?.headingDeg ?? 0).toFixed(1))} deg
            <input id="slip-geom-slider-rotation" type="range" min="-180" max="180" step="${esc(rotationToleranceStep())}" value="${esc(selectedGeom?.headingDeg ?? 0)}" ${selectedGeom ? "" : "disabled"} />
          </label>
        </div>
        <div class="small" style="margin-top:6px;">Selection is intentional: click a Slip ID badge (or bottom table row) to select. Polygon body clicks are disabled to avoid accidental selection.</div>
        ${geometryRunLayoutActive ? `<div class="small" style="margin-top:4px;">${geometryRunEndpoints.length <= 1 ? "Run layout: selected polygon is start; click end point." : `Run preview ready with ${geometryRunTotalCount()} slips.`} Controls: QWE / ASD / ZXC. Also: hold 1/2/3 and mouse-wheel (0.1ft/2°), add \` for coarse (1ft/5°), or Space for fine (0.01ft).</div>` : ""}
        ${geometryImageryEstimateStatus ? `<div class="small" style="margin-top:4px; color:#93c5fd;">${esc(geometryImageryEstimateStatus)}</div>` : ""}
        ${geomChartSignal.length ? `
          <div style="margin-top:8px; padding:8px; border:1px solid rgba(148,163,184,.18); border-radius:8px; background:rgba(15,23,42,.35);">
            <div class="small" style="margin-bottom:6px;">GeoTIFF run signal debug: blue line is sampled intensity along run centerline corridor; dashed orange lines are generated slip centers.</div>
            <svg viewBox="0 0 ${chartWidth} ${chartHeight}" style="display:block; width:100%; height:auto;">
              <rect x="0" y="0" width="${chartWidth}" height="${chartHeight}" rx="8" fill="rgba(15,23,42,0.5)" />
              <line x1="${chartPadding}" y1="${(chartHeight - chartPadding).toFixed(1)}" x2="${(chartWidth - chartPadding).toFixed(1)}" y2="${(chartHeight - chartPadding).toFixed(1)}" stroke="rgba(148,163,184,0.35)" stroke-width="1" />
              ${geomChartCenterLines}
              <path d="${geomChartPath}" fill="none" stroke="rgba(125,211,252,0.95)" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />
              ${geomChartDots}
            </svg>
          </div>
        ` : ""}
      </div>
      <div class="tool-section" style="margin-top:10px;">
        <div class="row" style="justify-content:space-between; align-items:center; gap:8px;">
          <div class="small">Selected Record</div>
          <div class="small">${esc(current ? current.measurement_id : "None selected")}</div>
        </div>
        ${current ? `
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Marina ID
              <div class="row" style="gap:8px; align-items:center;">
                <select id="slip-marina-id" style="flex:1;">
                  <option value="">Select marina</option>
                  ${currentOptions.map((entry) => `<option value="${esc(entry.marinaId)}"${entry.marinaId === current.marina_id ? " selected" : ""}>${esc(entry.marinaId)}</option>`).join("")}
                </select>
                <button id="slip-marina-zoom" type="button" class="btn"${currentMarina && Number.isFinite(currentMarina.lat) && Number.isFinite(currentMarina.lng) ? "" : " disabled"}>Zoom To</button>
              </div>
            </label>
            <label class="small" style="flex:1;">Marina Name
              <input id="slip-marina-name" type="text" value="${esc(currentMarina?.marinaName || current.marina_name)}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Slip ID
              <input id="slip-slip-id" type="text" value="${esc(current.slip_id)}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small" style="flex:1;">Linear Feet
              <input id="slip-linear-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_linear_feet))}" />
            </label>
            <label class="small" style="flex:1;">Width Feet
              <input id="slip-width-feet" type="number" step="0.1" value="${esc(formatFeetTenth(current.measured_width_feet))}" />
            </label>
            <label class="small" style="flex:1;">Depth Feet
              <input id="slip-depth-feet" type="number" step="0.01" value="${esc(current.measured_depth_feet ?? "")}" />
            </label>
          </div>
          <div class="row" style="margin-top:8px; gap:8px;">
            <label class="small">Measurement Date
              <input id="slip-measurement-date" type="date" value="${esc(current.measurement_date)}" />
            </label>
            <label class="small">Confidence
              <select id="slip-confidence">
                <option value="High"${current.confidence_score === "High" ? " selected" : ""}>High</option>
                <option value="Medium"${current.confidence_score === "Medium" ? " selected" : ""}>Medium</option>
                <option value="Low"${current.confidence_score === "Low" ? " selected" : ""}>Low</option>
              </select>
            </label>
            <label class="small">Operational Status
              <select id="slip-operational-status">
                <option value="operable"${String(current.operational_status || "operable") === "operable" ? " selected" : ""}>operable</option>
                <option value="under_construction"${String(current.operational_status || "") === "under_construction" ? " selected" : ""}>under_construction</option>
                <option value="maintenance"${String(current.operational_status || "") === "maintenance" ? " selected" : ""}>maintenance</option>
                <option value="blocked"${String(current.operational_status || "") === "blocked" ? " selected" : ""}>blocked</option>
                <option value="closed"${String(current.operational_status || "") === "closed" ? " selected" : ""}>closed</option>
              </select>
            </label>
          </div>
          <div class="small" style="margin-top:8px;">Notes</div>
          <textarea id="slip-notes" style="min-height:54px;" placeholder="Optional notes (construction, access, restrictions, etc.)">${esc(current.notes || "")}</textarea>
          <div class="small" style="margin-top:8px;">Geometry WKT</div>
          <textarea id="slip-geometry-wkt" style="min-height:64px;">${esc(current.geometry_wkt || "")}</textarea>
          <div class="small" style="margin-top:8px;">Record Created Timestamp</div>
          <input id="slip-record-created-timestamp" type="text" value="${esc(current.record_created_timestamp || "")}" />
          <div class="row" style="margin-top:10px; gap:8px;">
            <button id="slip-save" type="button" class="btn primary">Save Record</button>
            <button id="slip-delete" type="button" class="btn">Delete Record</button>
            <button id="slip-show-selected" type="button" class="btn">Show on Map</button>
          </div>
        ` : `<div class="small" style="margin-top:8px;">Pick a record from the bottom table or click an inventory point on the map.</div>`}
      </div>
      <div class="small" style="margin-top:10px; color:${status ? "#cbd5e1" : "#94a3b8"};">${esc(status || "Ready.")}</div>
        </div>
      </details>
    `;
  }

  function onClick(event) {
    const sortBtn = event.target?.closest?.("[data-sort-key]");
    if (sortBtn) {
      const key = String(sortBtn.getAttribute("data-sort-key") || "slip_id");
      if (String(state.tableSortKey || "slip_id") === key) {
        state.tableSortDir = String(state.tableSortDir || "asc") === "asc" ? "desc" : "asc";
      } else {
        state.tableSortKey = key;
        state.tableSortDir = "asc";
      }
      persist();
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-type-add")) {
      const name = String(containerEl?.querySelector("#slip-geom-type-name")?.value || "").trim() || "Standard";
      const lengthFt = Number(containerEl?.querySelector("#slip-geom-type-length")?.value || 40);
      const widthFt = Number(containerEl?.querySelector("#slip-geom-type-width")?.value || 14);
      createSlipType({ name, lengthFt, widthFt });
      status = `Added slip type ${name}.`;
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-place")) {
      if (activeGeometryPlaceMode) {
        stopGeometryPlaceMode();
        status = "Polygon placement stopped.";
      } else {
        startGeometryPlaceMode();
      }
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-line")) {
      startLineLayout("endpoint");
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-multislip")) {
      if (multislipActive && !multislipAsFingers) { stopMultislipMode(); status = "Multislip placement cancelled."; render(); }
      else { startMultislipMode(!!event.shiftKey); }
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-fingers")) {
      if (multislipActive && multislipAsFingers) { stopMultislipMode(); status = "Fingers placement cancelled."; render(); }
      else { startMultislipMode(!!event.shiftKey, { asFingers: true }); }
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-comb")) {
      if (combActive) { stopCombMode(); status = "Comb placement cancelled."; render(); }
      else { startCombMode(); }
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-full-dock")) {
      if (fullDockActive || (multislipActive && multislipSideLabel)) {
        stopFullDockMode();
        stopMultislipMode();
        status = "Full Dock cancelled.";
        render();
      } else {
        startFullDockMode();
      }
      return;
    }
    if (event.target?.closest?.("#slip-geom-place-chain")) {
      if (chainActive) { stopChainMode(); status = "Chain placement cancelled."; render(); }
      else { startChainMode(); }
      return;
    }
    if (event.target?.closest?.("#slip-lasso-tool-toggle")) {
      if (!lassoToolActive && anyOtherDrawModeActive()) {
        status = "Finish or cancel the active drawing tool before enabling the Lasso tool.";
        render();
        return;
      }
      lassoToolActive = !lassoToolActive;
      if (!lassoToolActive) {
        // Turning the master off fully disarms it -- no hidden toggle can silently fire on
        // the next Ctrl/Cmd-drag just because it was left checked from a prior session.
        disarmAllLassoToggles();
      }
      status = lassoToolActive
        ? "Lasso tool armed -- check which fields it should apply on the right, then Ctrl/Cmd-drag through slips."
        : "Lasso tool off.";
      updateLassoCursor();
      render();
      return;
    }
    if (event.target?.closest?.("#slip-lasso-clear")) {
      disarmAllLassoToggles();
      status = "Lasso toggles cleared.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-clear-all")) {
      const removed = clearUncommittedGeometryPolygons();
      status = removed
        ? `Cleared ${removed} uncommitted slip geometry polygon${removed === 1 ? "" : "s"}.`
        : "No uncommitted geometry polygons to clear.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-delete-selected")) {
      removeSelectedGeometryPolygon({ deleteLinkedRecords: true }).then((removed) => {
        status = removed ? `Deleted ${removed} selected slip geometry polygon${removed === 1 ? "" : "s"}.` : "No selected polygon to delete.";
        render();
      }).catch((error) => {
        status = error?.message || "Failed to delete selected polygon(s).";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-commit-selected")) {
      commitSelectedPolygonAndPersist().catch((error) => {
        status = error?.message || "Failed to create slip record from selected polygon.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-select-uncommitted")) {
      const ok = selectLatestUncommittedGeometry();
      if (!ok) {
        status = "No uncommitted polygons found.";
        render();
      }
      return;
    }
    if (event.target?.closest?.("#slip-geom-clear-selection")) {
      clearGeometrySelection();
      status = "Cleared geometry selection.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-purge-all")) {
      const removed = purgeStaleGeometryPolygons();
      status = removed
        ? `Purged ${removed} stale slip geometry polygon${removed === 1 ? "" : "s"}.`
        : "No stale slip geometry polygons found.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-hotkey-map")) {
      const msg = [
        "Slip Geometry Hotkeys",
        "",
        "Q / E: Rotate -/+",
        "A / D: Length -/+",
        "Z / C: Width -/+",
        "R: Activate rotate mode on selected slip (move mouse, click/Enter to lock, Esc to cancel)",
        "R (while drawing Polygon/Multislip's 2nd dimension): free-rotate about the first point clicked while setting that dimension, click to lock",
        "Arrow Keys: Nudge selected slip",
        "W / X: Number of slips + / -",
        "S: Start run layout / Generate run",
        "Enter: Commit selected polygon as a single slip",
        "Delete/Backspace: Delete selected polygon(s)",
        "Esc: Cancel active run layout",
        "",
        "Mouse Wheel (hold 1-3 + wheel): adjust length/width/rotation",
        "  + ` (backtick): coarse step (1ft / 5°)",
        "  + Space: fine step (0.01ft / tolerance step)",
        "  (default: 0.1ft / 2°)",
        "",
        "Ctrl/Cmd + drag: lasso-select every slip the path touches (adds to selection --",
        "  repeat to keep building it up; Esc clears)",
        "  Lasso Tool ON (New Slip Defaults): same drag instead applies whichever",
        "    fields are checked on the right -- Dock, Slip Number, Region, Slip Type,",
        "    Covered, Boatlift, Fuel Dock, Multislip, and/or Swap L/W. Any combination",
        "    can be armed together for one combined pass in path order.",
      ].join("\n");
      window.alert(msg);
      return;
    }
    if (event.target?.closest?.("#slip-geom-lock-toggle")) {
      const selected = selectedGeometryPolygon();
      if (selected) {
        updateSelectedGeometry({ locked: !selected.locked });
        status = selected.locked ? "Polygon unlocked." : "Polygon locked.";
      }
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-layout-run")) {
      startGeometryRunLayout();
      geometryImageryEstimateStatus = "";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-estimate-run-imagery")) {
      estimateGeometryRunFromImagery().catch((error) => {
        geometryImageryEstimateStatus = error?.message || "GeoTIFF run estimate failed.";
        app.ui.toast(geometryImageryEstimateStatus);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-generate-run")) {
      if (geometryRunEndpoints.length < 2) return;
      generateRunAndPersist().catch((error) => {
        status = error?.message || "Failed to generate run records.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-geom-cancel-run")) {
      stopGeometryRunLayout("cancelled");
      geometryImageryEstimateStatus = "";
      geometryImageryEstimateDebug = null;
      render();
      return;
    }
    if (event.target?.closest?.("#slip-geom-apply-report")) {
      applyGeometryToSlipRecords().catch((error) => {
        status = error?.message || "Failed to apply geometry to slip report.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-default-marina-zoom")) {
      fitMarinaById(state.marinaId, state.marinaName);
      return;
    }
    if (event.target?.closest?.("#slip-marina-zoom")) {
      const current = selectedRecord();
      if (!current) return;
      fitMarinaById(String(containerEl?.querySelector("#slip-marina-id")?.value || current.marina_id), current.marina_name);
      return;
    }
    if (event.target?.closest?.("#slip-drop-point")) {
      startPointDrop();
      return;
    }
    if (event.target?.closest?.("#slip-stop-drop")) {
      stopPointDrop("cancelled");
      return;
    }
    if (event.target?.closest?.("#slip-line-layout")) {
      startLineLayout("endpoint");
      return;
    }
    if (event.target?.closest?.("#slip-spacing-run-layout")) {
      startLineLayout("spacing");
      return;
    }
    if (event.target?.closest?.("#slip-line-clear")) {
      lineEndpoints = [];
      lineHoverPoint = null;
      lineLayoutMode = "endpoint";
      imageryEstimateStatus = "";
      imageryEstimateDebug = null;
      stopLineLayout("cleared");
      status = "Line preview cleared.";
      render();
      return;
    }
    if (event.target?.closest?.("#slip-line-estimate-imagery")) {
      if (lineLayoutMode === "spacing") return;
      estimateLineSlipCountFromImagery().catch((error) => {
        imageryEstimateStatus = error?.message || "Imagery estimate failed.";
        app.ui.toast(imageryEstimateStatus);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-line-generate")) {
      const canGenerate = lineLayoutMode === "spacing" ? lineEndpoints.length >= 3 : lineEndpoints.length >= 2;
      if (!canGenerate) return;
      const points = linePreviewPoints();
      createRecordsFromPoints(points).then(() => {
        imageryEstimateStatus = "";
        imageryEstimateDebug = null;
        stopLineLayout("completed");
        app.ui.toast(`Generated ${points.length} slips from line preview.`);
      }).catch((error) => {
        status = error?.message || "Failed to generate line slips.";
        app.ui.toast(status);
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-line-commit-feature")) {
      commitLineFeatureFromCurrentLine().catch((error) => {
        status = error?.message || "Failed to commit side-tie line feature.";
        render();
      });
      return;
    }
    const exportActionButton = event.target?.closest?.("[data-slip-export]");
    if (exportActionButton) {
      const details = exportActionButton.closest("details");
      if (details) details.open = false;
      runSlipExport(exportActionButton.getAttribute("data-slip-export")).catch((err) => alert("Export failed: " + (err?.message || err)));
      return;
    }
    if (event.target?.closest?.("#slip-refresh")) {
      refreshRecords().catch(() => {});
      return;
    }
    if (event.target?.closest?.("#slip-export-csv")) {
      exportCsv();
      return;
    }
    if (event.target?.closest?.("#slip-export-xls")) {
      exportXls().catch((err) => alert(`XLS export failed: ${err.message}`));
      return;
    }
    if (event.target?.closest?.("#slip-export-json")) {
      exportJson();
      return;
    }
    if (event.target?.closest?.("#slip-export-geojson")) {
      exportGeoJson();
      return;
    }
    if (event.target?.closest?.("#slip-fill-depths-run")) {
      const sel = containerEl?.querySelector("#slip-depth-surface-select");
      const surfaceId = sel?.value || "";
      if (!surfaceId || !filterProjectId) return;
      const btn = event.target.closest("#slip-fill-depths-run");
      const statusEl = containerEl?.querySelector("#slip-fill-depths-status");
      btn.disabled = true;
      if (statusEl) statusEl.textContent = "Running…";
      fetch("/api/surfaces/populate-slip-depths", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ surfaceId, marina: filterProjectId }),
      }).then(r => r.json()).then(data => {
        if (statusEl) statusEl.textContent = data.ok
          ? `Done: ${data.updated} updated, ${data.nodata} no data, ${data.errors} errors (${data.total} slips)`
          : `Error: ${data.error}`;
        if (data.ok && data.updated > 0) refreshRecords().catch(() => {});
      }).catch(err => {
        if (statusEl) statusEl.textContent = `Error: ${err.message}`;
      }).finally(() => { btn.disabled = false; });
      return;
    }
    if (event.target?.closest?.("#slip-drafts-toggle") && draftsAllowed()) {
      draftsVisible = !draftsVisible;
      // always re-fetch on open: a Create Drafts request may have finished while the panel was closed
      if (draftsVisible) loadDrafts();
      else { clearTimeout(draftsPollTimer); draftsPollTimer = null; renderDrafts(); render(); }
      return;
    }
    if (event.target?.closest?.("#slip-drafts-reload")) { loadDrafts(); return; }
    if (event.target?.closest?.("#slip-drafts-create") && draftsAllowed()) { requestDraftsRun(); return; }
    if (event.target?.closest?.("#slip-auto-run") && draftsAllowed()) { if (autoRunBusy()) stopAutoRun("Auto Run cancelled."); else startAutoRun(); return; }
    if (event.target?.closest?.("#slip-auto-detect-run")) {
      if (autoDetectActive) { stopAutoDetectSlipsMode(); status = "Auto-detect cancelled."; render(); return; }
      const sel = containerEl?.querySelector("#slip-auto-detect-surface-select");
      const surfaceId = sel?.value || "";
      const surface = (_surfacesCatalog || []).find((s) => String(s.id) === surfaceId);
      if (!isSampleableSurface(surface)) { status = "Choose a surface first."; render(); return; }
      startAutoDetectSlipsMode(surface);
      return;
    }
    if (event.target?.closest?.("#slip-save")) {
      saveSelectedRecord().catch((error) => {
        status = error?.message || "Failed to save record.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-swap-lw")) {
      const lInput = containerEl?.querySelector("#slip-linear-feet");
      const wInput = containerEl?.querySelector("#slip-width-feet");
      if (lInput && wInput) {
        const tmp = lInput.value;
        lInput.value = wInput.value;
        wInput.value = tmp;
      }
      saveSelectedRecord().catch((error) => {
        status = error?.message || "Failed to swap L/W.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-delete")) {
      deleteSelectedRecord().catch((error) => {
        status = error?.message || "Failed to delete record.";
        render();
      });
      return;
    }
    if (event.target?.closest?.("#slip-show-selected")) {
      fitSelection();
    }
  }

  function onInput(event) {
    const id = String(event.target?.id || "");
    if (id === "slip-auto-detect-surface-select") {
      _autoDetectSurfaceId = String(event.target?.value || "");
      return;
    }
    if (id === "slip-depth-surface-select") {
      _fillDepthsSurfaceId = String(event.target?.value || "");
      return;
    }
    if (id === "slip-marina-id") {
      const current = selectedRecord();
      if (!current) return;
      const marina = marinaOptionForId(String(event.target?.value || ""), current.marina_name);
      const nameInput = containerEl?.querySelector("#slip-marina-name");
      if (nameInput) nameInput.value = marina?.marinaName || "";
      return;
    }
    if (id === "slip-geom-active-type") {
      state.slipGeometryDraftTypeId = String(event.target?.value || "");
      persist();
      render();
      return;
    }
    if (id === "slip-color-by") {
      state.colorSlipsBy = normalizeColorSlipsBy(event.target?.value);
      persist();
      render();
      return;
    }
    if (id === "slip-display-measurement-tol") {
      state.displayMeasurementToleranceFt = normalizeDisplayToleranceFt(event.target?.value);
      if (state.filterHiddenValues?.dimension?.length) state.filterHiddenValues = { ...state.filterHiddenValues, dimension: [] };
      persist();
      renderGeometry();
      render();
      return;
    }
    if (id === "slip-confidence") {
      const current = selectedRecord();
      if (!current) return;
      const next = String(event.target?.value || "High");
      records = records.map((record) => (
        String(record?.measurement_id || "") === String(current.measurement_id || "")
          ? { ...record, confidence_score: next }
          : record
      )); recordsVersion++;
      renderMap();
      renderBottomPanel();
      return;
    }
    if (id === "slip-dimension-color-tol") {
      const next = Number(event.target?.value);
      state.dimensionColorToleranceFt = [0.5, 1].includes(next) ? next : 0.5;
      persist();
      render();
      return;
    }
    if (id === "slip-dimension-legend-toggle") {
      state.showDimensionLegend = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-toggle-ids") {
      state.showSlipIds = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-toggle-depths") {
      state.showDepths = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-toggle-length") {
      state.showSlipLength = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-toggle-width") {
      state.showSlipWidth = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-toggle-edge-extensions") {
      state.showEdgeExtensions = !!event.target?.checked;
      persist();
      render();
      return;
    }
    if (id === "slip-edge-extension-ft") {
      const ft = Number(event.target?.value);
      state.edgeExtensionFt = Number.isFinite(ft) && ft >= 0 ? ft : 10;
      persist();
      render();
      return;
    }
    if (id === "slip-geom-run-between") {
      state.slipGeometryRunBetweenCount = Math.max(1, Number(event.target?.value || 1) || 1);
      persist();
      render();
      return;
    }
    if (id === "slip-geom-tolerance-lw") {
      const next = Number(event.target?.value || 0.5);
      state.lengthWidthToleranceFt = [0.1, 0.25, 0.5, 1].includes(next) ? next : 0.5;
      persist();
      render();
      return;
    }
    if (id === "slip-geom-tolerance-rot") {
      const next = Number(event.target?.value || 0.5);
      state.rotationToleranceDeg = [0.1, 0.5, 1].includes(next) ? next : 0.5;
      persist();
      render();
      return;
    }
    if (id === "slip-geom-slider-length") {
      updateSelectedGeometry({ lengthFt: snapLengthWidth(event.target?.value) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-geom-slider-width") {
      updateSelectedGeometry({ widthFt: snapLengthWidth(event.target?.value) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-geom-slider-rotation") {
      updateSelectedGeometry({ headingDeg: roundToStep(event.target?.value, rotationToleranceStep(), -180) });
      renderGeometry();
      renderGeometryRunPreview();
      return;
    }
    if (id === "slip-default-measurement-date") state.measurementDate = String(event.target?.value || "");
    if (id === "slip-default-confidence") state.confidenceScore = String(event.target?.value || "High");
    if (id === "slip-default-dock") {
      state.dockName = String(event.target?.value || "");
      // "X" is this marina's convention for slips with no clear dock label from the marina --
      // typing it as the whole Dock value jumps Slip Number straight to one past whatever X-dock
      // slip already has the highest number, instead of leaving the user to go check.
      if (state.dockName.trim().toUpperCase() === "X") {
        state.slipIdPart3 = nextXDockSlipIdPart3();
        persist();
        render();
        return;
      }
    }
    if (id === "slip-full-dock-walkway-ft") {
      const ft = Number(event.target?.value);
      state.fullDockWalkwayFt = Number.isFinite(ft) && ft > 0 ? ft : 15;
    }
    if (id === "slip-default-suffix") state.slipIdSuffix = String(event.target?.value || "");
    if (id === "slip-default-region") state.region = String(event.target?.value || "");
    if (id === "slip-default-slipid-separator") state.slipIdSeparator = String(event.target?.value || "-");
    if (id === "slip-default-slipid-part3") state.slipIdPart3 = String(event.target?.value || "");
    if (id === "slip-default-slipid-autoinc") state.slipIdAutoIncrement = !!event.target?.checked;
    if (id === "slip-default-slipid-step") state.slipIdIncrementBy = Math.max(1, Number(event.target?.value || 1) || 1);
    if (id === "slip-default-slipid-autoinc-suffix") state.slipIdAutoIncrementSuffix = !!event.target?.checked;
    if (id === "slip-default-slipid-step-suffix") state.slipIdIncrementBySuffix = Math.max(1, Number(event.target?.value || 1) || 1);
    if (id === "slip-default-covered") draftCovered = !!event.target?.checked;
    if (id === "slip-default-space-type") state.spaceType = String(event.target?.value || "");
    if (id === "slip-lasso-include-dock") lassoIncludeDock = !!event.target?.checked;
    if (id === "slip-lasso-include-slipnumber") lassoIncludeSlipNumber = !!event.target?.checked;
    if (id === "slip-lasso-include-suffix") lassoIncludeSuffix = !!event.target?.checked;
    if (id === "slip-lasso-include-region") lassoIncludeRegion = !!event.target?.checked;
    if (id === "slip-lasso-include-sliptype") lassoIncludeSlipType = !!event.target?.checked;
    if (id === "slip-lasso-include-covered") lassoIncludeCovered = !!event.target?.checked;
    if (id === "slip-lasso-include-boatlift") lassoIncludeBoatlift = !!event.target?.checked;
    if (id === "slip-lasso-include-fueldock") lassoIncludeFuelDock = !!event.target?.checked;
    if (id === "slip-lasso-include-outofservice") lassoIncludeOutOfService = !!event.target?.checked;
    if (id === "slip-lasso-include-multislip") lassoIncludeMultislip = !!event.target?.checked;
    if (id === "slip-lasso-include-swaplw") lassoIncludeSwapLw = !!event.target?.checked;
    if (id === "slip-default-boatlift") draftBoatlift = !!event.target?.checked;
    if (id === "slip-default-fueldock") draftFuelDock = !!event.target?.checked;
    if (id === "slip-default-multislip") draftMultislip = !!event.target?.checked;
    if (id === "slip-default-out-of-service") {
      draftOutOfService = !!event.target?.checked;
      if (!draftOutOfService) draftOosReason = "";
      render(); // shows/hides the Reason select
      return;
    }
    if (id === "slip-default-oos-reason") draftOosReason = OOS_REASON_OPTIONS.includes(String(event.target?.value || "")) ? String(event.target.value) : "";
    if (id === "slip-default-comments") draftComments = String(event.target?.value || "");
    if (id === "slip-default-line-count") {
      state.lineSlipCount = Math.max(1, Number(event.target?.value || 1) || 1);
      if (lineAnchorPoint && Number(lineAnchorPoint.index) >= state.lineSlipCount - 1) lineAnchorPoint = null;
    }
    if (id.startsWith("slip-default-slipid-")) {
      persist();
      return;
    }
    if (id === "slip-default-line-count") {
      persist();
      renderPreview();
      render();
      return;
    }
    if (id) persist();
  }

  function bindBottomPanelControls() {
    const host = document.getElementById("slip-inventory-bottom");
    if (!host) return;
    const bottomTableAnchors = () => {
      const panelView = host.closest(".bottom-panel-view");
      const catDetails = tablePanelEl?.closest?.("details[data-dock-name]");
      const catContent = catDetails?.querySelector?.(".dock-category-content") || null;
      const toolDet = tablePanelEl?.closest?.("details[data-tool-id]");
      return { panelView, catDetails, catContent, toolDet };
    };
    const clearClosedTableLayout = () => {
      const { catDetails, catContent, toolDet } = bottomTableAnchors();
      [catDetails, catContent, toolDet, tablePanelEl, host].filter(Boolean).forEach((el) => {
        ["height", "flex", "minHeight", "overflow", "display", "flexDirection"].forEach((prop) => {
          el.style[prop] = "";
        });
      });
      const scrollEl = host.querySelector("#slip-bottom-table-scroll");
      if (scrollEl) {
        ["height", "maxHeight", "flex", "minHeight"].forEach((prop) => { scrollEl.style[prop] = ""; });
      }
    };
    const sizeBottomTableViewport = () => {
      const { panelView, catDetails, toolDet } = bottomTableAnchors();
      const bottomDockRoot = tablePanelEl?.closest?.('[data-dock-root="bottom"]') || null;
      const scrollEl = host.querySelector("#slip-bottom-table-scroll");
      if (!panelView || !bottomDockRoot || !scrollEl || (catDetails && !catDetails.open) || (toolDet && !toolDet.open)) return;
      const boundsHost = panelView.closest(".bottom-panel-body") || panelView.closest(".panel-content") || panelView;
      const bounds = boundsHost.getBoundingClientRect();
      const scrollTop = scrollEl.getBoundingClientRect().top;
      const nextHeight = Math.max(180, Math.floor(bounds.bottom - scrollTop - 10));
      Object.assign(scrollEl.style, {
        flex: "0 0 auto",
        minHeight: "0",
        height: `${nextHeight}px`,
        maxHeight: `${nextHeight}px`,
      });
      // A taller viewport can fit more rows than the current window covers -- top up
      // the windowed rows so a resize doesn't leave blank space at the bottom.
      updateTableWindowFromScroll();
    };
    const applyBottomTableLayout = () => {
      const { panelView, catDetails, catContent, toolDet } = bottomTableAnchors();
      if (!panelView) return;
      const bottomDockRoot = tablePanelEl?.closest?.('[data-dock-root="bottom"]') || null;
      if (bottomDockRoot) {
        if (catDetails && !catDetails.open) catDetails.open = true;
        if (toolDet && !toolDet.open) toolDet.open = true;
        bottomDockRoot.querySelectorAll(":scope > .tool-drop-end-zone").forEach((zone) => {
          Object.assign(zone.style, { flex:"0 0 0", minHeight:"0", height:"0", overflow:"hidden" });
        });
      }
      if ((catDetails && !catDetails.open) || (toolDet && !toolDet.open)) {
        clearClosedTableLayout();
        return;
      }
      // Build a flex chain from .bottom-panel-view down so #slip-inventory-bottom fills the panel.
      // height:100% on inner elements can't work when <details> ancestors are auto-height;
      // flex:1 at every level lets CSS handle resizing without JS measurement.
      const bottomBody = panelView.closest(".bottom-panel-body");
      const bottomStack = panelView.closest(".bottom-panel-stack");
      const panelContent = panelView.closest(".panel-content");
      if (panelContent) Object.assign(panelContent.style, { height:"100%", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      if (bottomStack) Object.assign(bottomStack.style, { height:"100%", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      if (bottomBody) Object.assign(bottomBody.style, { flex:"1", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      Object.assign(panelView.style, { height:"100%", overflow:"hidden", display:"flex", flexDirection:"column" });
      const sidebar = panelView.querySelector("#bottom-toolset-sidebar");
      if (sidebar) Object.assign(sidebar.style, { height:"100%", flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (catDetails) Object.assign(catDetails.style, { flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (catContent) Object.assign(catContent.style, { flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (toolDet) Object.assign(toolDet.style, { flex:"1", minHeight:"0", display:"flex", flexDirection:"column", overflow:"hidden" });
      if (tablePanelEl) Object.assign(tablePanelEl.style, { flex:"1", minHeight:"0", overflow:"hidden", display:"flex", flexDirection:"column" });
      Object.assign(host.style, { flex:"1", minHeight:"0", height:"100%", overflow:"hidden" });
      requestAnimationFrame(sizeBottomTableViewport);
    };
    // renderBottomPanelHost calls onShow() on every re-render (tab switches, etc.),
    // not only when content is replaced. Guard against stacking listeners.
    if (host.dataset.listenersBound === "1") {
      applyBottomTableLayout();
      return;
    }
    host.dataset.listenersBound = "1";
    applyBottomTableLayout();
    const { catDetails, toolDet } = bottomTableAnchors();
    [catDetails, toolDet].filter(Boolean).forEach((details) => {
      details.addEventListener("toggle", () => {
        requestAnimationFrame(applyBottomTableLayout);
      });
    });
    if (!tablePanelResizeObserver && typeof ResizeObserver !== "undefined") {
      tablePanelResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(sizeBottomTableViewport);
      });
      [host.closest(".bottom-panel-body"), host.closest(".panel-content"), tablePanelEl].filter(Boolean).forEach((el) => {
        tablePanelResizeObserver.observe(el);
      });
    }
    const scrollEl = host.querySelector("#slip-bottom-table-scroll");
    if (scrollEl) {
      const targetScrollTop = Number(bottomTableScrollTop || 0);
      if (Math.abs(Number(scrollEl.scrollTop || 0) - targetScrollTop) > 1) scrollEl.scrollTop = targetScrollTop;
      scrollEl.addEventListener("pointerenter", () => {
        bottomTablePointerInside = true;
      });
      scrollEl.addEventListener("pointerleave", () => {
        bottomTablePointerInside = false;
        bottomTableScrollActiveUntil = Date.now() + 1500;
      });
      let _scrollWindowRaf = null;
      scrollEl.addEventListener("scroll", () => {
        bottomTableScrollTop = Number(scrollEl.scrollTop || 0);
        bottomTableScrollActiveUntil = Date.now() + 2500;
        if (_scrollWindowRaf !== null) return;
        _scrollWindowRaf = requestAnimationFrame(() => {
          _scrollWindowRaf = null;
          updateTableWindowFromScroll();
        });
      }, { passive: true });
    }

    host.querySelector("#slip-show-no-marina")?.addEventListener("change", (e) => {
      showNoMarinaCode = e.target.checked;
      refreshRecords({ quiet: true }).catch(() => {});
    });
    host.querySelector("#slip-bottom-refresh")?.addEventListener("click", () => { refreshRecords().catch(() => {}); });
    host.querySelectorAll("[data-slip-export]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const kind = btn.getAttribute("data-slip-export");
        const details = btn.closest("details");
        if (details) details.open = false;
        if (kind === "plot") {
          if (_plotExporting) return;
          _plotExporting = true;
          const summary = details?.querySelector("summary");
          if (summary) { summary.disabled = true; summary.textContent = "Exporting…"; }
          exportPlotSvg()
            .catch((err) => alert("PDF export failed: " + (err?.message || err)))
            .finally(() => {
              _plotExporting = false;
              taskIdle();
              if (summary) { summary.disabled = false; summary.textContent = "Export ▾"; }
            });
        } else {
          runSlipExport(kind).catch((err) => alert("Export failed: " + (err?.message || err)));
        }
      });
    });
    host.querySelector("#slip-bottom-export-csv")?.addEventListener("click", () => { runSlipExport("csv").catch((err) => alert(`CSV export failed: ${err.message}`)); });
    host.querySelector("#slip-bottom-export-xls")?.addEventListener("click", () => { runSlipExport("xls").catch((err) => alert(`XLS export failed: ${err.message}`)); });
    host.querySelector("#slip-bottom-export-plot")?.addEventListener("click", async (e) => {
      if (_plotExporting) return;
      _plotExporting = true;
      const btn = e.currentTarget;
      btn.disabled = true;
      btn.textContent = "Exporting…";
      try { await exportPlotSvg(); } catch (err) { alert(`Plot export failed: ${err.message}`); } finally {
        _plotExporting = false;
        taskIdle();
        btn.disabled = false;
        btn.textContent = "PDF Plot";
      }
    });
    host.querySelector("#slip-display-measurement-tol")?.addEventListener("change", (event) => {
      state.displayMeasurementToleranceFt = normalizeDisplayToleranceFt(event.target?.value);
      if (state.filterHiddenValues?.dimension?.length) state.filterHiddenValues = { ...state.filterHiddenValues, dimension: [] };
      persist(); renderGeometry(); render();
    });
    const setSlipIdLabelSize = (value) => {
      const next = clampNumber(Math.round(Number(value || 13)), 8, 28);
      if (Number(state.slipIdLabelSizePx) === next) return;
      state.slipIdLabelSizePx = next;
      const slider = host.querySelector("#slip-id-label-size");
      const number = host.querySelector("#slip-id-label-size-value");
      if (slider) slider.value = String(next);
      if (number) number.value = String(next);
      persist();
      renderMap();
    };
    host.querySelector("#slip-id-label-size")?.addEventListener("input", (event) => {
      setSlipIdLabelSize(event.target?.value);
    });
    host.querySelector("#slip-id-label-size-value")?.addEventListener("change", (event) => {
      setSlipIdLabelSize(event.target?.value);
      renderBottomPanel({ force: true });
    });
    host.querySelector("#slip-bottom-show-selected")?.addEventListener("click", () => { fitSelection(); });
    host.querySelector("#slip-table-swap-lw")?.addEventListener("click", () => {
      swapLWForSelected().catch((err) => { status = err?.message || "Failed to swap L/W."; render(); });
    });
    host.querySelector("#slip-consolidate-xdock")?.addEventListener("click", () => {
      consolidateXDockSlipIds().catch((err) => { status = err?.message || "Failed to consolidate X dock."; render(); });
    });
    host.querySelector("#slip-consolidate-multislip")?.addEventListener("click", () => {
      consolidateMultislipIds().catch((err) => { status = err?.message || "Failed to consolidate multislip IDs."; render(); });
    });
    host.querySelector("#slip-clear-multislip-tag")?.addEventListener("click", () => {
      clearMultislipTagForSelected().catch((err) => { status = err?.message || "Failed to clear multislip tag."; render(); });
    });
    // Row selection — plain click on non-interactive area, OR modifier+click anywhere in the row.
    // Capture-phase mousedown intercepts Ctrl/Shift+click before any cell handler fires,
    // allowing row selection without activating the cell underneath.
    function applyRowSelection(event, row) {
      const clickedMeasurementId = String(row.getAttribute("data-slip-row") || "");
      const clickedGeomId = String(row.getAttribute("data-geom-id") || "");
      const ctrl = !!(event.ctrlKey || event.metaKey);
      const shift = !!event.shiftKey;
      // Sourced from the full sorted/filtered record list, not the DOM -- under table
      // windowing, host.querySelectorAll("[data-slip-row]") would only see whatever rows
      // are currently rendered near the scroll position, silently truncating any shift-click
      // range that reaches outside that window.
      const allEntries = _visibleRecordGeometryCache;
      const previousMeasurementId = String(state.selectedMeasurementId || "");
      state.selectedMeasurementId = clickedMeasurementId;

      if (shift && previousMeasurementId) {
        const from = allEntries.findIndex(({ record }) => String(record?.measurement_id || "") === previousMeasurementId);
        const to = allEntries.findIndex(({ record }) => String(record?.measurement_id || "") === clickedMeasurementId);
        if (from >= 0 && to >= 0) {
          const [a, b] = from <= to ? [from, to] : [to, from];
          const rangeSlice = allEntries.slice(a, b + 1);
          const rangeMids = rangeSlice.map(({ record }) => String(record?.measurement_id || "")).filter(Boolean);
          const rangeGeomIds = rangeSlice.map(({ geom }) => String(geom?.id || "")).filter(Boolean);
          if (ctrl) {
            rangeMids.forEach((mid) => tableSelectedMeasurementIds.add(mid));
          } else {
            tableSelectedMeasurementIds = new Set(rangeMids);
          }
          if (rangeGeomIds.length) {
            if (ctrl) {
              const merged = [...new Set([...getSelectedGeometryIds(), ...rangeGeomIds])];
              setSelectedGeometryIds(merged, clickedGeomId || merged[0] || "");
            } else {
              setSelectedGeometryIds(rangeGeomIds, clickedGeomId || rangeGeomIds[0] || "");
            }
          } else if (!ctrl) { clearGeometrySelection(); }
        }
      } else if (ctrl) {
        // Ctrl+click: toggle this row in the table selection set
        if (tableSelectedMeasurementIds.has(clickedMeasurementId)) {
          tableSelectedMeasurementIds.delete(clickedMeasurementId);
        } else {
          tableSelectedMeasurementIds.add(clickedMeasurementId);
        }
        if (clickedGeomId) applyGeometrySelection(clickedGeomId, { toggle: true });
      } else {
        // Plain click: single selection
        tableSelectedMeasurementIds = new Set([clickedMeasurementId]);
        if (clickedGeomId) applyGeometrySelection(clickedGeomId, { toggle: false });
        else clearGeometrySelection();
      }
      persist(); render();
    }

    // Capture-phase: runs before cell handlers. When modifier held, handle selection
    // and block cell interaction (preventDefault stops input focus / checkbox toggle).
    const tableScroll = host.querySelector("#slip-bottom-table-scroll");
    if (tableScroll) {
      tableScroll.addEventListener("mousedown", (event) => {
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return;
        const row = event.target?.closest?.("[data-slip-row]");
        if (!row) return;
        event.preventDefault();
        applyRowSelection(event, row);
      }, true);
      tableScroll.addEventListener("click", (event) => {
        if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return;
        const row = event.target?.closest?.("[data-slip-row]");
        if (!row) return;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      // Toggle select-mode class for visual cursor feedback — remove old handler first to prevent accumulation
      if (_selectModeHandler) {
        document.removeEventListener("keydown", _selectModeHandler);
        document.removeEventListener("keyup", _selectModeHandler);
      }
      _selectModeHandler = (e) => tableScroll.classList.toggle("select-mode", !!(e.ctrlKey || e.metaKey || e.shiftKey));
      document.addEventListener("keydown", _selectModeHandler);
      document.addEventListener("keyup", _selectModeHandler);
    }

    // Everything below is delegated onto tableScroll (stable across scroll-driven tbody
    // swaps) instead of bound per-row/per-cell -- table windowing means which row/cell
    // elements exist changes on every scroll, so anything bound directly to them would need
    // constant rebinding. event.target is the actual leaf control for click/change/focus*
    // on form elements (they have no children), so .closest() reliably finds it either way.
    if (tableScroll) {
      // Plain click on non-interactive row area (no modifier needed)
      tableScroll.addEventListener("click", (event) => {
        if (event.ctrlKey || event.metaKey || event.shiftKey) return; // handled by capture above
        if (event.target?.closest?.("[data-slip-binary],[data-slip-select],[data-slip-text]")) return;
        const row = event.target?.closest?.("[data-slip-row]");
        if (!row) return;
        applyRowSelection(event, row);
      });

      // Binary checkbox changes — applies to all selected rows when multi-selected
      tableScroll.addEventListener("click", (event) => {
        if (event.target?.closest?.("[data-slip-binary]")) event.stopPropagation();
      });
      tableScroll.addEventListener("change", (event) => {
        const cb = event.target?.closest?.("[data-slip-binary]");
        if (!cb) return;
        const fieldKey = String(cb.getAttribute("data-slip-binary") || "");
        const measurementId = String(cb.getAttribute("data-slip-meas") || "");
        if (!fieldKey || !measurementId) return;
        saveFieldForSelected(measurementId, fieldKey, cb.checked ? "yes" : "no").catch(() => {});
      });

      // Dropdown select changes — applies to all selected rows when multi-selected
      tableScroll.addEventListener("click", (event) => {
        if (event.target?.closest?.("[data-slip-select]")) event.stopPropagation();
      });
      tableScroll.addEventListener("change", (event) => {
        const sel = event.target?.closest?.("[data-slip-select]");
        if (!sel) return;
        const fieldKey = String(sel.getAttribute("data-slip-select") || "");
        const measurementId = String(sel.getAttribute("data-slip-meas") || "");
        if (!fieldKey || !measurementId) return;
        saveFieldForSelected(measurementId, fieldKey, sel.value || "").catch(() => {});
      });

      // Text / number / date inputs — applies to all selected rows when multi-selected
      tableScroll.addEventListener("click", (event) => {
        if (event.target?.closest?.("[data-slip-text]")) event.stopPropagation();
      });
      tableScroll.addEventListener("focusin", (event) => {
        const input = event.target?.closest?.("[data-slip-text]");
        if (!input) return;
        // Sync left-panel + row highlight to the record being edited, without disrupting the
        // focused input. We defer the render until after this focus event completes so the
        // browser doesn't lose the caret position.
        const measurementId = String(input.getAttribute("data-slip-meas") || "");
        if (!measurementId || state.selectedMeasurementId === measurementId) return;
        // Focusing a field in a row that's already part of the current multi-selection must
        // not collapse that selection down to just this row -- that's what broke bulk-edit
        // for every row except whichever one happened to be state.selectedMeasurementId
        // (the last shift/ctrl-clicked row). Only single-select-ify when the focused row is
        // genuinely outside the existing selection.
        const partOfExistingSelection = tableSelectedMeasurementIds.has(measurementId);
        state.selectedMeasurementId = measurementId;
        if (!partOfExistingSelection) tableSelectedMeasurementIds = new Set([measurementId]);
        if (!partOfExistingSelection) {
          const row = input.closest("[data-slip-row]");
          const geomId = String(row?.getAttribute("data-geom-id") || "");
          if (geomId) setSelectedGeometryIds([geomId], geomId);
          else clearGeometrySelection();
        }
        // partOfExistingSelection: leave both tableSelectedMeasurementIds and the geometry
        // selection exactly as they were -- only the "which record is primary" bookkeeping
        // above (state.selectedMeasurementId) moves to follow focus.
        persist();
        // Defer render so focus is not disrupted by DOM replacement
        requestAnimationFrame(() => render());
      });
      tableScroll.addEventListener("keydown", (event) => {
        const input = event.target?.closest?.("[data-slip-text]");
        if (!input) return;
        event.stopPropagation();
        if (event.key === "Enter") { event.preventDefault(); input.blur(); }
      });
      tableScroll.addEventListener("focusout", (event) => {
        const input = event.target?.closest?.("[data-slip-text]");
        if (!input) return;
        const fieldKey = String(input.getAttribute("data-slip-text") || "");
        const measurementId = String(input.getAttribute("data-slip-meas") || "");
        if (!fieldKey || !measurementId) return;
        const current = records.find((r) => r.measurement_id === measurementId);
        if (!current) return;
        const newRaw = String(input.value ?? "").trim();
        const prevRaw = String(current[fieldKey] ?? "").trim();
        if (newRaw === prevRaw) return;
        saveFieldForSelected(measurementId, fieldKey, newRaw || null).catch(() => {});
      });
    }

    // Column config panel open/close
    host.querySelector("#slip-col-cfg-open")?.addEventListener("click", () => {
      const card = host.querySelector(".card");
      if (!card) return;
      const existing = card.querySelector("#slip-col-cfg-panel");
      if (existing) { existing.remove(); return; }
      const cfg = readColCfg();
      card.insertAdjacentHTML("beforeend", renderColCfgPanel(cfg));
      bindColCfgPanel(card, cfg);
    });

    // Color By select in table header — synced with sidebar
    host.querySelector("#slip-color-by-table")?.addEventListener("change", (e) => {
      state.colorSlipsBy = normalizeColorSlipsBy(e.target?.value);
      persist();
      renderGeometry();
      render();
    });

    // Filter panel toggle + checkbox handlers
    if (_filterPanelCloseHandler) {
      document.removeEventListener("click", _filterPanelCloseHandler);
      _filterPanelCloseHandler = null;
    }
    const filterToggleBtn = host.querySelector("#slip-filter-toggle");
    const filterPanelEl = host.querySelector("#slip-filter-panel");
    if (filterToggleBtn && filterPanelEl) {
      // Restore open state if panel was open before a re-render triggered by a filter change
      if (_filterPanelShouldBeOpen) filterPanelEl.style.display = "block";
      filterToggleBtn.addEventListener("click", (e) => {
        e.stopPropagation();
        const opening = filterPanelEl.style.display === "none";
        filterPanelEl.style.display = opening ? "block" : "none";
        _filterPanelShouldBeOpen = opening;
      });
      _filterPanelCloseHandler = (e) => {
        if (!filterPanelEl.isConnected) { document.removeEventListener("click", _filterPanelCloseHandler); _filterPanelCloseHandler = null; return; }
        if (!filterPanelEl.contains(e.target) && e.target !== filterToggleBtn) { filterPanelEl.style.display = "none"; _filterPanelShouldBeOpen = false; }
      };
      document.addEventListener("click", _filterPanelCloseHandler);
      filterPanelEl.querySelector("#slip-filter-select-all")?.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!state.filterHiddenValues) state.filterHiddenValues = {};
        state.filterHiddenValues[state.colorSlipsBy] = [];
        _filterPanelShouldBeOpen = true;
        persist(); renderGeometry(); render();
      });
      filterPanelEl.querySelector("#slip-filter-deselect-all")?.addEventListener("click", (e) => {
        e.stopPropagation();
        const allVals = [...filterPanelEl.querySelectorAll("[data-slip-filter-val]")].map((cb) => String(cb.getAttribute("data-slip-filter-val") || "")).filter(Boolean);
        if (!state.filterHiddenValues) state.filterHiddenValues = {};
        state.filterHiddenValues[state.colorSlipsBy] = allVals;
        _filterPanelShouldBeOpen = true;
        persist(); renderGeometry(); render();
      });
      filterPanelEl.querySelectorAll("[data-slip-filter-val]").forEach((cb) => {
        cb.addEventListener("change", (e) => {
          e.stopPropagation();
          const val = String(cb.getAttribute("data-slip-filter-val") || "");
          if (!val) return;
          if (!state.filterHiddenValues) state.filterHiddenValues = {};
          const arr = Array.isArray(state.filterHiddenValues[state.colorSlipsBy]) ? [...state.filterHiddenValues[state.colorSlipsBy]] : [];
          if (cb.checked) {
            state.filterHiddenValues[state.colorSlipsBy] = arr.filter((v) => v !== val);
          } else {
            if (!arr.includes(val)) arr.push(val);
            state.filterHiddenValues[state.colorSlipsBy] = arr;
          }
          _filterPanelShouldBeOpen = true;
          persist(); renderGeometry(); render();
        });
      });
    }

    // Column header resize
    host.querySelectorAll(".slip-col-resize").forEach((grip) => {
      grip.addEventListener("mousedown", (startEvt) => {
        startEvt.preventDefault(); startEvt.stopPropagation();
        const key = String(grip.getAttribute("data-resize-key") || "");
        if (!key) return;
        const th = grip.closest("th");
        if (!th) return;
        const startX = startEvt.clientX;
        const startW = th.offsetWidth;
        const onMove = (moveEvt) => {
          const newW = Math.max(40, startW + (moveEvt.clientX - startX));
          th.style.width = newW + "px";
        };
        const onUp = (upEvt) => {
          document.removeEventListener("mousemove", onMove);
          document.removeEventListener("mouseup", onUp);
          const newW = Math.max(40, startW + (upEvt.clientX - startX));
          const cfg = readColCfg();
          cfg.widths[key] = newW;
          writeColCfg(cfg);
        };
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
      });
    });

    // Column header drag-to-reorder
    let dragSrcKey = null;
    host.querySelectorAll("th[data-col-key]").forEach((th) => {
      th.addEventListener("dragstart", (e) => {
        dragSrcKey = th.getAttribute("data-col-key");
        e.dataTransfer.effectAllowed = "move";
        th.style.opacity = "0.5";
      });
      th.addEventListener("dragend", () => { th.style.opacity = ""; dragSrcKey = null; });
      th.addEventListener("dragover", (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; });
      th.addEventListener("drop", (e) => {
        e.preventDefault();
        const dropKey = th.getAttribute("data-col-key");
        if (!dragSrcKey || dragSrcKey === dropKey) return;
        const cfg = readColCfg();
        const fromIdx = cfg.order.indexOf(dragSrcKey);
        const toIdx = cfg.order.indexOf(dropKey);
        if (fromIdx < 0 || toIdx < 0) return;
        cfg.order.splice(fromIdx, 1);
        cfg.order.splice(toIdx, 0, dragSrcKey);
        writeColCfg(cfg);
        renderBottomPanel();
      });
    });
  }

  function bindColCfgPanel(card, cfg) {
    const panel = card.querySelector("#slip-col-cfg-panel");
    if (!panel) return;

    function reopenColCfgPanel() {
      const newCard = document.querySelector("#slip-inventory-bottom .card");
      if (!newCard) return;
      newCard.querySelector("#slip-col-cfg-panel")?.remove();
      const newCfg = readColCfg();
      newCard.insertAdjacentHTML("beforeend", renderColCfgPanel(newCfg));
      bindColCfgPanel(newCard, newCfg);
    }

    // Close — force table re-render so col cfg changes are applied
    panel.querySelector("#slip-col-cfg-close")?.addEventListener("click", () => {
      panel.remove();
      _lastPanelKey = "";
      renderBottomPanel();
    });

    // Reset all to defaults
    panel.querySelector("#slip-col-cfg-reset-all")?.addEventListener("click", () => {
      try { localStorage.removeItem(COL_CFG_KEY); } catch {}
      panel.remove();
      _lastPanelKey = "";
      renderBottomPanel();
    });

    // Toggle visibility
    panel.querySelectorAll("[data-cfg-toggle]").forEach((cb) => {
      cb.addEventListener("change", () => {
        const key = String(cb.getAttribute("data-cfg-toggle") || "");
        if (!key) return;
        const fresh = readColCfg();
        if (cb.checked) fresh.hidden.delete(key); else fresh.hidden.add(key);
        writeColCfg(fresh);
        reopenColCfgPanel();
      });
    });

    // Reset individual column width
    panel.querySelectorAll("[data-cfg-reset-width]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const key = String(btn.getAttribute("data-cfg-reset-width") || "");
        if (!key) return;
        const fresh = readColCfg();
        delete fresh.widths[key];
        writeColCfg(fresh);
        reopenColCfgPanel();
      });
    });

    // Drag-to-reorder rows in config panel
    let cfgDragSrc = null;
    panel.querySelectorAll(".slip-col-cfg-row").forEach((row) => {
      row.addEventListener("dragstart", (e) => {
        cfgDragSrc = row.getAttribute("data-cfg-key");
        e.dataTransfer.effectAllowed = "move";
        row.style.background = "rgba(148,163,184,.1)";
      });
      row.addEventListener("dragend", () => { row.style.background = ""; cfgDragSrc = null; });
      row.addEventListener("dragover", (e) => { e.preventDefault(); row.style.background = "rgba(96,165,250,.15)"; });
      row.addEventListener("dragleave", () => { row.style.background = ""; });
      row.addEventListener("drop", (e) => {
        e.preventDefault();
        row.style.background = "";
        const dropKey = row.getAttribute("data-cfg-key");
        if (!cfgDragSrc || cfgDragSrc === dropKey) return;
        const fresh = readColCfg();
        const fromIdx = fresh.order.indexOf(cfgDragSrc);
        const toIdx = fresh.order.indexOf(dropKey);
        if (fromIdx < 0 || toIdx < 0) return;
        fresh.order.splice(fromIdx, 1);
        fresh.order.splice(toIdx, 0, cfgDragSrc);
        writeColCfg(fresh);
        reopenColCfgPanel();
      });
    });
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
    }
    containerEl = el;
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("input", onInput);
    // Sync marina from the globally active project on every mount — don't rely solely on
    // the hydrotools:project-changed event, which is missed if this tool wasn't mounted yet
    // when the project was activated (leaves state.marinaId stale/blank until the user
    // re-picks a project).
    const activeProject = app.services?.context?.getActiveProject?.();
    if (activeProject?.marinaId) setProjectMarina(activeProject.marinaId);
    if (!zoomHandler) {
      zoomHandler = () => renderMap();
      map.on("zoomend", zoomHandler);
    }
    if (!moveHandler) {
      // Marker rebuild is now viewport-bounded (see renderMap) -- panning into a
      // previously-unrendered area needs a rebuild the same way zooming does.
      moveHandler = () => renderMap();
      map.on("moveend", moveHandler);
    }
    if (!mapSelectClickHandler) {
      mapSelectClickHandler = (event) => {
        if (activeGeometryPlaceMode || geometryRunLayoutActive || lineLayoutActive || pointDropActive) return;
        if (Date.now() < Number(suppressMapClickUntil || 0)) return;
        const handledBy = String(event?.originalEvent?.__htHandled || "");
        if (event?.originalEvent?.defaultPrevented || handledBy.startsWith("sv360") || handledBy.startsWith("slip-inventory")) return;
        const hadSelection = getSelectedGeometryIds().length > 0 || !!state.selectedMeasurementId;
        clearGeometrySelection();
        state.selectedMeasurementId = "";
        if (!hadSelection) return;
        persist();
        render();
      };
      map.on("click", mapSelectClickHandler);
    }
    if (!globalRunHotkeyHandler) {
      globalRunHotkeyHandler = (event) => {
        if (shouldIgnoreGeometryHotkeys(event)) return;
        if (!event.ctrlKey && !event.metaKey && !event.altKey) {
          // Arm-tool hotkeys: P=Polygon, M=Multislip, B=Comb, F=Fingers (Chain/Auto-Detect
          // have no single-letter hotkey since their names don't yield a free, unambiguous
          // one). "C" is already the run-control width nudge below, so Comb uses "B" instead.
          // Each start*Mode() already tears down whichever other tool is active, same as
          // clicking a different tool button mid-draw does, so no extra guard is needed here.
          const armKey = String(event.key || "").toLowerCase();
          if (armKey === "p" || armKey === "m" || armKey === "b" || armKey === "f") {
            if (armKey === "p") {
              if (activeGeometryPlaceMode) { stopGeometryPlaceMode(); status = "Polygon placement stopped."; }
              else if (state.slipGeometryDraftTypeId) startGeometryPlaceMode();
              else status = "Pick a slip type before arming Polygon.";
            } else if (armKey === "m") {
              if (multislipActive && !multislipAsFingers) { stopMultislipMode(); status = "Multislip placement cancelled."; }
              else startMultislipMode(!!event.shiftKey);
            } else if (armKey === "b") {
              if (combActive) { stopCombMode(); status = "Comb placement cancelled."; }
              else startCombMode();
            } else if (armKey === "f") {
              if (multislipActive && multislipAsFingers) { stopMultislipMode(); status = "Fingers placement cancelled."; }
              else startMultislipMode(!!event.shiftKey, { asFingers: true });
            }
            render();
            event.preventDefault?.();
            event.stopPropagation?.();
            event.stopImmediatePropagation?.();
            return;
          }
        }
        if (!activeGeometryPlaceMode && !geometryRunLayoutActive && !lineLayoutActive && !pointDropActive) {
          const nudgeHandled = nudgeSelectedGeometry(event.key);
          if (nudgeHandled) {
            event.preventDefault?.();
            event.stopPropagation?.();
            event.stopImmediatePropagation?.();
            return;
          }
        }
        if (event.key === "Escape") {
          if (activeGeometryPlaceMode || geometryRunLayoutActive || lineLayoutActive || pointDropActive || rotateAdjustActive) return;
          // A single-polygon placement just finished (armPostPlaceEscapeUndo) -- undo that
          // shape instead of just clearing the selection, same as this global handler would
          // otherwise do below. Must be checked here, ahead of the generic clear-selection
          // path: this is the same Escape keydown that path would also handle, and it wins
          // the race (window-capture) over any separately-registered listener.
          if (postPlaceUndoPolyId) {
            const polyId = postPlaceUndoPolyId;
            postPlaceUndoPolyId = null;
            const stillDraft = state.slipGeometryPolygons.some((p) => String(p.id) === String(polyId) && !p.committed);
            const stillSelected = getSelectedGeometryIds().includes(String(polyId));
            if (stillDraft && stillSelected) {
              event.preventDefault?.();
              event.stopPropagation?.();
              event.stopImmediatePropagation?.();
              removeSelectedGeometryPolygon({ deleteLinkedRecords: false }).then((removed) => {
                status = removed ? "Placement undone." : status;
                render();
              });
              return;
            }
          }
          const hadSelection = getSelectedGeometryIds().length > 0 || !!state.selectedMeasurementId;
          clearGeometrySelection();
          state.selectedMeasurementId = "";
          if (!hadSelection) return;
          persist();
          render();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        const ctrlOrMeta = !!(event.ctrlKey || event.metaKey);
        if (ctrlOrMeta && String(event.key || "").toLowerCase() === "c") {
          const selected = selectedGeometryPolygon();
          if (!selected) return;
          const selectedRing = computeSlipPolygonLatLngs(selected);
          const inferred = inferPolyMetricsFromRing(selectedRing);
          const ringBacked = Array.isArray(selectedRing) && selectedRing.length >= 3 && !!inferred;
          copiedSlipGeometryTemplate = {
            lengthFt: Number(ringBacked ? inferred.lengthFt : (selected.lengthFt || 40)),
            widthFt: Number(ringBacked ? inferred.widthFt : (selected.widthFt || 14)),
            headingDeg: Number(ringBacked ? inferred.headingDeg : (selected.headingDeg || 0)),
            ringLatLngs: Array.isArray(selectedRing) && selectedRing.length >= 3 ? selectedRing : null,
            sourceCenter: ringBacked
              ? { lat: Number(inferred.center.lat), lng: Number(inferred.center.lng) }
              : (selected?.center ? { lat: Number(selected.center.lat), lng: Number(selected.center.lng) } : null),
          };
          status = `Copied slip geometry ${Number(copiedSlipGeometryTemplate.lengthFt).toFixed(1)} x ${Number(copiedSlipGeometryTemplate.widthFt).toFixed(1)} ft.`;
          render();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (ctrlOrMeta && String(event.key || "").toLowerCase() === "v") {
          if (!copiedSlipGeometryTemplate) return;
          startGeometryPlaceMode({ template: copiedSlipGeometryTemplate });
          status = "Paste mode active: move mouse and click to place copied slip.";
          render();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (ctrlOrMeta && String(event.key || "").toLowerCase() === "z" && !event.shiftKey) {
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          if (undoInProgress) return;
          if (!undoStack.length || undoStackMarinaId !== filterProjectId) {
            status = "Nothing to undo.";
            render();
            return;
          }
          performUndo().catch((err) => { status = err?.message || "Undo failed."; render(); });
          return;
        }
        const deletePressed = event.key === "Delete" || event.key === "Backspace";
        if (deletePressed) {
          const selectedCount = getSelectedGeometryIds().length;
          if (!selectedCount) return;
          const ok = window.confirm(`Delete ${selectedCount} selected slip geometry polygon${selectedCount === 1 ? "" : "s"}?`);
          if (!ok) return;
          removeSelectedGeometryPolygon({ deleteLinkedRecords: true }).then((removed) => {
            status = removed
              ? `Deleted ${removed} selected slip geometry polygon${removed === 1 ? "" : "s"}.`
              : "No selected polygon to delete.";
            render();
          }).catch((error) => {
            status = error?.message || "Failed to delete selected polygon(s).";
            render();
          });
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (event.key === "Enter") {
          // Any in-progress draw tool (Polygon/Multislip/Comb/Chain/...) must own Enter
          // itself -- each has its own onKey that handles Enter at the right stage. Without
          // this guard, this global handler fires first (capture phase beats each tool's
          // bubble-phase listener) and, if some unrelated polygon was selected before the
          // tool started, re-commits THAT one and stopImmediatePropagation()s the event --
          // the tool's own draft never gets placed, and Enter has to be pressed a second time
          // (after clearGeometrySelection() drops the stale selection) before this handler
          // backs off and lets the draft through.
          if (anyOtherDrawModeActive()) return;
          const selected = selectedGeometryPolygon();
          if (!selected) return;
          commitSelectedPolygonAndPersist().catch((error) => {
            status = error?.message || "Failed to commit selected polygon.";
            render();
          });
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        const key = String(event.key || "").toLowerCase();
        if (key === "r" && !rotateAdjustActive && !activeGeometryPlaceMode) {
          const selected = selectedGeometryPolygon();
          if (!selected) return;
          startRotateAdjustMode();
          event.preventDefault?.();
          event.stopPropagation?.();
          event.stopImmediatePropagation?.();
          return;
        }
        if (!["q", "w", "e", "a", "s", "d", "z", "x", "c", "r"].includes(key)) return;
        if (key === "r") return; // rotateAdjustActive: let the mode's own document-level listener lock/cancel it
        if ((multislipActive || combActive) && (key === "w" || key === "x")) return;
        // Click-drawn placement (width/length stages): length/width/heading come from the
        // clicks themselves, not these nudges -- letting A/D/Z/C (or Q/E) through would
        // desync geometryPlaceDraft from the frozen A/B corner positions the whole flow
        // depends on. "r" (optional rotate-mode) is handled separately below, before this
        // guard, since it's meaningful mid-placement.
        if (activeGeometryPlaceMode && (geometryDrawStage === "width" || geometryDrawStage === "length" || geometryDrawStage === "review") && ["q", "e", "a", "d", "z", "c"].includes(key)) return;
        const handled = performRunControl(key);
        if (!handled) return;
        // performRunControl() only calls the generic panel render() -- it never
        // refreshes the run-layout ghost preview on the map itself (that's a
        // separate call, normally only made from the run's own pointermove
        // handler). Left alone, W/X (slip count) and Q/E/A/D/Z/C (rotating/
        // resizing the source polygon while a run is active) all silently update
        // state.slipGeometryRunBetweenCount / the selected polygon but leave the
        // on-map dashed preview showing the old count/shape, which reads as the
        // hotkey doing nothing.
        if (geometryRunLayoutActive) renderGeometryRunPreview();
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };
      window.addEventListener("keydown", globalRunHotkeyHandler, true);
    }
    if (!globalWheelAdjustKeydownHandler) {
      globalWheelAdjustKeydownHandler = (event) => {
        if (shouldIgnoreGeometryHotkeys(event)) return;
        const code = String(event?.code || "");
        const key = String(event?.key || "");
        if (code === "Space" || key === " ") {
          wheelAdjustSpaceHeld = true;
          return;
        }
        if (code === "Backquote" || key === "`") {
          wheelAdjustBacktickHeld = true;
          return;
        }
        const slot = (
          code === "Digit1" || key === "1" ? 1
            : code === "Digit2" || key === "2" ? 2
              : code === "Digit3" || key === "3" ? 3
                : code === "Digit4" || key === "4" ? 4
                  : code === "Digit5" || key === "5" ? 5
                    : null
        );
        if (!slot) return;
        wheelAdjustHeldSlots.add(slot);
      };
      window.addEventListener("keydown", globalWheelAdjustKeydownHandler, true);
    }
    if (!globalWheelAdjustKeyupHandler) {
      globalWheelAdjustKeyupHandler = (event) => {
        const code = String(event?.code || "");
        const key = String(event?.key || "");
        const slot = (
          code === "Digit1" || key === "1" ? 1
            : code === "Digit2" || key === "2" ? 2
              : code === "Digit3" || key === "3" ? 3
                : code === "Digit4" || key === "4" ? 4
                  : code === "Digit5" || key === "5" ? 5
                    : null
        );
        if (slot) wheelAdjustHeldSlots.delete(slot);
        if (code === "Space" || key === " ") wheelAdjustSpaceHeld = false;
        if (code === "Backquote" || key === "`") wheelAdjustBacktickHeld = false;
      };
      window.addEventListener("keyup", globalWheelAdjustKeyupHandler, true);
    }
    if (!globalWheelAdjustHandler) {
      globalWheelAdjustHandler = (event) => {
        if (!wheelAdjustHeldSlots.size) return;
        if (shouldIgnoreGeometryHotkeys(event)) return;
        if (!activeGeometryPlaceMode && !selectedGeometryPolygon()) return;
        const mapContainer = map?.getContainer?.();
        if (!mapContainer) return;
        const targetNode = event?.target;
        if (!targetNode || (typeof Node !== "undefined" && !(targetNode instanceof Node)) || !mapContainer.contains(targetNode)) return;
        const held = [1, 2, 3, 4, 5].filter((slot) => wheelAdjustHeldSlots.has(slot));
        if (!held.length) return;
        const deltaY = Number(event?.deltaY || 0);
        if (!Number.isFinite(deltaY) || deltaY === 0) return;
        const direction = deltaY < 0 ? 1 : -1;
        let handledAny = false;
        for (const slot of held) {
          const handled = performWheelAdjust(slot, direction, { fine: wheelAdjustSpaceHeld, coarse: wheelAdjustBacktickHeld });
          if (handled) handledAny = true;
        }
        if (!handledAny) return;
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };
      window.addEventListener("wheel", globalWheelAdjustHandler, { capture: true, passive: false });
    }
    if (!lassoCtrlSyncHandler) {
      // One handler for both keydown and keyup: browsers keep ctrlKey/metaKey accurate on
      // every keyboard event while either is held, including the keyup that releases it, so
      // there's no need to special-case the "Control"/"Meta" key names themselves.
      lassoCtrlSyncHandler = (event) => {
        const held = !!(event.ctrlKey || event.metaKey);
        if (held === lassoCtrlHeld) return;
        lassoCtrlHeld = held;
        updateLassoCursor();
        if (!held && lassoActive) finishLasso(null);
      };
      window.addEventListener("keydown", lassoCtrlSyncHandler, true);
      window.addEventListener("keyup", lassoCtrlSyncHandler, true);
    }
    if (!lassoPointerDownHandler) {
      const LASSO_MOVE_THRESHOLD = 4;
      lassoPointerDownHandler = (event) => {
        if (event.button != null && event.button !== 0) return;
        if (!(event.ctrlKey || event.metaKey)) return;
        if (anyOtherDrawModeActive()) return;
        // Defensive, independent of pointer capture: never start a new gesture on top of a
        // leftover path from one that didn't get a clean pointerup (belt-and-suspenders next
        // to the capture fix above -- don't rely on that alone across every browser/version).
        if (lassoActive || lassoPoints.length) cancelLasso();
        lassoPointerDownXY = { x: event.clientX, y: event.clientY };
        lassoPointerId = event.pointerId;
        // Every other draw tool in this file is click-click-click (down+up almost
        // immediately), so the cursor has no real chance to leave the map container's
        // bounds mid-gesture. A lasso is a sustained freehand drag -- without capturing
        // the pointer, straying outside the container even briefly (e.g. near an edge)
        // stops move/up events from ever reaching these listeners, leaving lassoActive
        // stuck true with a half-built path. The next Ctrl-drag would then append its
        // points onto that stale, far-away leftover path instead of starting clean --
        // producing one phantom segment that spans the whole map and "selects" everything
        // it happens to cross, which is exactly the reported symptom.
        try { event.target?.setPointerCapture?.(event.pointerId); } catch {}
        // Disable Leaflet's map-drag-to-pan right now, before its own "mousedown" listener
        // even runs. mousedown is a SEPARATE event from pointerdown -- the browser dispatches
        // both for the same physical click -- so stopPropagation() on this pointerdown event
        // never reaches Leaflet's own handler and never stopped its pan from starting. Disabling
        // reactively later (once movement crosses the threshold below) was too late: Leaflet's
        // own drag had often already begun panning the map by then, so the world was shifting
        // under a fixed screen position while the user dragged -- the live preview line looked
        // fine (drawn in current screen space) while the recorded lat/lngs silently swept across
        // a much larger real area than the actual on-screen mouse movement. This is what was
        // actually causing unrelated slips far from the drawn line to get selected.
        try { map.dragging.disable(); } catch {}
        event.stopPropagation?.();
      };
      lassoPointerMoveHandler = (event) => {
        if (!lassoPointerDownXY) return;
        if (!(event.ctrlKey || event.metaKey) || anyOtherDrawModeActive()) { cancelLasso(); return; }
        if (!lassoActive) {
          const dx = Math.abs(event.clientX - lassoPointerDownXY.x);
          const dy = Math.abs(event.clientY - lassoPointerDownXY.y);
          if (dx <= LASSO_MOVE_THRESHOLD && dy <= LASSO_MOVE_THRESHOLD) return;
          lassoActive = true;
          updateLassoCursor();
        }
        const latlng = map?.mouseEventToLatLng?.(event);
        if (latlng) lassoPoints.push({ lat: Number(latlng.lat), lng: Number(latlng.lng) });
        if (!lassoPreviewRafPending) {
          lassoPreviewRafPending = true;
          requestAnimationFrame(() => { lassoPreviewRafPending = false; renderLassoPreview(); });
        }
        event.preventDefault?.();
        event.stopPropagation?.();
      };
      lassoPointerUpHandler = (event) => {
        if (!lassoPointerDownXY) return;
        lassoPointerDownXY = null;
        if (lassoActive) {
          // The actual release position was never recorded -- lassoPoints' last entry was
          // whatever the last pointermove happened to sample, which can sit a real distance
          // short of where the mouse actually stopped. That gap alone could keep a
          // deliberately-closed loop from ever measuring as closed, so add the true release
          // point before finishing.
          const latlng = map?.mouseEventToLatLng?.(event);
          if (latlng) lassoPoints.push({ lat: Number(latlng.lat), lng: Number(latlng.lng) });
          finishLasso({ x: event.clientX, y: event.clientY });
          return;
        }
        // No real drag happened -- re-enable map dragging (disabled at pointerdown above;
        // cancelLasso/finishLasso never ran to do it).
        try { map.dragging.enable(); } catch {}
        if (anyLassoToggleArmed()) {
          // Point-and-shoot: same update a drag would apply, just to whatever single slip is
          // under this click. Swallow the native click that's about to follow so it doesn't
          // also reach the marker/polygon's own click-to-select handler underneath.
          suppressNextLassoTapClick = true;
          const latlng = map?.mouseEventToLatLng?.(event);
          if (latlng) {
            applyLassoPathActions([{ lat: Number(latlng.lat), lng: Number(latlng.lng) }], { x: event.clientX, y: event.clientY }).catch((err) => {
              status = `Lasso update failed: ${err?.message || err}`;
              render();
            });
          }
          return;
        }
        // Let the click propagate normally so the existing Ctrl+click toggle-select on a
        // slip ID badge or polygon still works.
      };
      const mapContainerEl = map?.getContainer?.();
      mapContainerEl?.addEventListener("pointerdown", lassoPointerDownHandler, true);
      mapContainerEl?.addEventListener("pointermove", lassoPointerMoveHandler, true);
      mapContainerEl?.addEventListener("pointerup", lassoPointerUpHandler, true);
      mapContainerEl?.addEventListener("pointercancel", lassoPointerUpHandler, true);
      // Safety net: if pointer capture ever gets revoked out from under an active drag (rare,
      // e.g. another element stealing capture for the same pointerId) without a normal
      // pointerup/pointercancel following, this still finalizes instead of leaving a stuck
      // half-built path for the next Ctrl-drag to accidentally append onto.
      mapContainerEl?.addEventListener("lostpointercapture", lassoPointerUpHandler, true);
      // Capture phase, ahead of any marker/polygon's own bubble-phase click listener --
      // guaranteed to run first regardless of Leaflet's internal event wiring, so this
      // reliably swallows the native click a point-and-shoot tap is about to produce.
      lassoTapClickSuppressHandler = (event) => {
        if (!suppressNextLassoTapClick) return;
        suppressNextLassoTapClick = false;
        event.preventDefault?.();
        event.stopPropagation?.();
        event.stopImmediatePropagation?.();
      };
      mapContainerEl?.addEventListener("click", lassoTapClickSuppressHandler, true);
    }
    render();
    startRemoteSync();
    if (!initialFetchDone) {
      initialFetchDone = true;
      // Only auto-fetch if a marina is already known -- refreshRecords() with no
      // filterProjectId returns every slip across every Safe Harbor marina with no filter at
      // all, and this used to fire unconditionally on first mount. That was effectively
      // masked before: context.js always silently restored a marina before, so
      // filterProjectId was rarely empty in practice. Now that nothing is active until the
      // user explicitly activates a project (see landing.js), skip this fetch when
      // unscoped -- setProjectMarina's own refreshRecords() call (fired by the real
      // hydrotools:project-changed activation) fetches correctly-scoped data once one is.
      if (filterProjectId) {
        refreshRecords().catch((error) => {
          status = error?.message || "Slip inventory API is unavailable.";
          render();
        });
      }
    } else {
      // renderSidebar() re-mounts the panel — re-render with existing data, no re-fetch
      renderBottomPanel();
    }
  }

  function destroy() {
    stopAutoRun();
    document.removeEventListener("hydrotools:project-changed", handleProjectChanged);
    persistNow();
    app.events.emit("map:status:extra", { items: [] });
    app.events.emit("map:status:tray-right", { html: "" });
    _lastColorByTrayValue = null;
    stopRemoteSync();
    closeDeliverablesPicker();
    clearDimensionLegend();
    lassoToolActive = false;
    disarmAllLassoToggles();
    stopPointDrop("destroyed");
    stopLineLayout("destroyed");
    stopGeometryPlaceMode();
    stopGeometryRunLayout("destroyed");
    stopCombMode();
    stopChainMode("destroyed");
    try { root.remove(); } catch {}
    tablePanelEl = null;
    if (containerEl) {
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("input", onInput);
    }
    if (zoomHandler) {
      try { map.off("zoomend", zoomHandler); } catch {}
      zoomHandler = null;
    }
    if (moveHandler) {
      try { map.off("moveend", moveHandler); } catch {}
      moveHandler = null;
    }
    if (mapSelectClickHandler) {
      try { map.off("click", mapSelectClickHandler); } catch {}
      mapSelectClickHandler = null;
    }
    if (globalRunHotkeyHandler) {
      window.removeEventListener("keydown", globalRunHotkeyHandler, true);
      globalRunHotkeyHandler = null;
    }
    if (globalWheelAdjustKeydownHandler) {
      window.removeEventListener("keydown", globalWheelAdjustKeydownHandler, true);
      globalWheelAdjustKeydownHandler = null;
    }
    if (globalWheelAdjustKeyupHandler) {
      window.removeEventListener("keyup", globalWheelAdjustKeyupHandler, true);
      globalWheelAdjustKeyupHandler = null;
    }
    if (globalWheelAdjustHandler) {
      window.removeEventListener("wheel", globalWheelAdjustHandler, { capture: true });
      globalWheelAdjustHandler = null;
    }
    wheelAdjustHeldSlots.clear();
    wheelAdjustSpaceHeld = false;
    wheelAdjustBacktickHeld = false;
    if (lassoCtrlSyncHandler) {
      window.removeEventListener("keydown", lassoCtrlSyncHandler, true);
      window.removeEventListener("keyup", lassoCtrlSyncHandler, true);
      lassoCtrlSyncHandler = null;
    }
    if (lassoPointerDownHandler) {
      const mapContainerEl = map?.getContainer?.();
      mapContainerEl?.removeEventListener?.("pointerdown", lassoPointerDownHandler, true);
      mapContainerEl?.removeEventListener?.("pointermove", lassoPointerMoveHandler, true);
      mapContainerEl?.removeEventListener?.("pointerup", lassoPointerUpHandler, true);
      mapContainerEl?.removeEventListener?.("pointercancel", lassoPointerUpHandler, true);
      mapContainerEl?.removeEventListener?.("lostpointercapture", lassoPointerUpHandler, true);
      mapContainerEl?.removeEventListener?.("click", lassoTapClickSuppressHandler, true);
      lassoPointerDownHandler = null;
      lassoPointerMoveHandler = null;
      lassoPointerUpHandler = null;
      lassoTapClickSuppressHandler = null;
    }
    cancelLasso();
    lassoCtrlHeld = false;
    if (_selectModeHandler) {
      document.removeEventListener("keydown", _selectModeHandler);
      document.removeEventListener("keyup", _selectModeHandler);
      _selectModeHandler = null;
    }
    if (_filterPanelCloseHandler) {
      document.removeEventListener("click", _filterPanelCloseHandler);
      _filterPanelCloseHandler = null;
    }
    _filterPanelShouldBeOpen = false;
    containerEl = null;
  }

  function mountTablePanel(el) {
    tablePanelEl = el;
    if (tablePanelResizeObserver) { tablePanelResizeObserver.disconnect(); tablePanelResizeObserver = null; }
    renderBottomPanel({ force: true });
    if (!initialFetchDone) {
      initialFetchDone = true;
      refreshRecords().catch((error) => {
        status = error?.message || "Slip inventory API is unavailable.";
        render();
      });
    }
  }

  function unmountTablePanel() {
    if (tablePanelResizeObserver) {
      tablePanelResizeObserver.disconnect();
      tablePanelResizeObserver = null;
    }
    if (tablePanelEl) tablePanelEl.innerHTML = "";
    tablePanelEl = null;
    bottomTablePointerInside = false;
    bottomTableScrollActiveUntil = 0;
  }

  function snapshot() {
    return { version: 2, state };
  }

  function restore(data) {
    if (!data?.state || typeof data.state !== "object") return;
    state = { ...defaultState(), ...data.state };
    state.measurementDate = todayDate();
    state.multislipId = String(state.multislipId || "").trim() || DEFAULT_MULTISLIP_ID;
    state.lineSlipCount = Math.max(1, Number(state.lineSlipCount || 6) || 6);
    state.slipGeometryTypes = Array.isArray(state.slipGeometryTypes) ? state.slipGeometryTypes : [];
    state.slipGeometryPolygons = Array.isArray(state.slipGeometryPolygons) ? state.slipGeometryPolygons : [];
    state.slipGeometrySelectedId = String(state.slipGeometrySelectedId || "");
    state.slipGeometryDraftTypeId = String(state.slipGeometryDraftTypeId || "") || (state.slipGeometryTypes[0]?.id || "");
    state.slipGeometryRunBetweenCount = Math.max(1, Number(state.slipGeometryRunBetweenCount || 5) || 5);
    persist();
    render();
  }

  function setMarinaItems(items) {
    marinaCatalog = normalizeMarinaCatalog(items);
    const marinaToResolve = state.marinaId || filterProjectId;
    if (marinaToResolve) {
      setMarinaDefaultsFromId(marinaToResolve);
    }
    if (containerEl) render();
  }

  function setProjectMarina(code) {
    const newCode = String(code || "");
    const changed = newCode !== filterProjectId;
    filterProjectId = newCode;
    // Only reset "Include unassigned" on an actual marina switch — this is called on every
    // tool mount now (re-syncing to whatever marina is already active), and resetting it on
    // every idempotent re-sync silently hid unassigned records the user had just opted in to see.
    if (changed) showNoMarinaCode = false;
    if (newCode) {
      setMarinaDefaultsFromId(newCode);
    }
    if (changed) {
      if (containerEl) render();
      refreshRecords({ quiet: true }).catch(() => {});
      for (const cb of marinaChangeListeners) cb(newCode);
    }
  }

  // Project activation must work before the optional table has mounted. Register
  // once for the shared runtime so remounting either panel cannot add listeners.
  function handleProjectChanged(event) {
    const projectId = String(event.detail?.projectId || "");
    _surfacesCatalog = null;
    _autoDetectSurfaceId = "";
    _fillDepthsSurfaceId = "";
    setProjectMarina(projectId);
  }
  document.addEventListener("hydrotools:project-changed", handleProjectChanged);

  // The "Generate Deliverables" entry point now lives on the DAM's Deliverables panel
  // (image-generator tool) instead of on this tool's own UI, since registering the 5
  // standard files is fundamentally a DAM operation. That panel has no direct reference to
  // this runtime, so it asks for the picker via a DOM event -- same cross-tool pattern as
  // hydrotools:project-changed. Bound once here (this factory is a singleton per app), not
  // inside mount(), so the DAM button works even before this tool's own panel is opened.
  document.addEventListener("hydrotools:generate-deliverables", () => {
    openDeliverablesPicker();
  });

  // Interaction on the color-by control this tool injects into the map's status tray (see
  // updateColorByStatusTray) comes back through this event rather than a direct DOM listener,
  // since the control lives inside core/map.js's tray element, not this tool's own container --
  // same singleton-registration reasoning as hydrotools:generate-deliverables above.
  app.events.on("map:status:tray-right:action", ({ action, value } = {}) => {
    if (action === "color-by-select") {
      state.colorSlipsBy = normalizeColorSlipsBy(value);
    } else if (action === "color-by-prev" || action === "color-by-next") {
      const idx = COLOR_SLIPS_BY_VALUES.indexOf(state.colorSlipsBy);
      const delta = action === "color-by-prev" ? -1 : 1;
      state.colorSlipsBy = COLOR_SLIPS_BY_VALUES[(idx + delta + COLOR_SLIPS_BY_VALUES.length) % COLOR_SLIPS_BY_VALUES.length];
    } else {
      return;
    }
    persist();
    render();
  });

  return { mount, destroy, mountTablePanel, unmountTablePanel, snapshot, restore, setMarinaItems, setProjectMarina, dockHost };
}

let sharedSlipInventoryRuntime = null;

export function getSlipInventoryRuntime(app) {
  sharedSlipInventoryRuntime = sharedSlipInventoryRuntime ?? createSlipInventoryRuntime(app);
  return sharedSlipInventoryRuntime;
}

export const slipDeliveryTool = {
  id: "slip-delivery",
  name: "Slip Inventory",
  init(app) { this._rt = getSlipInventoryRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; sharedSlipInventoryRuntime = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = getSlipInventoryRuntime(app);
    this._rt.mount(el);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = getSlipInventoryRuntime(app);
    this._rt.restore?.(data);
  },
};

export const slipInventoryTableTool = {
  id: "slip-inventory-table",
  name: "Slip Inventory Table",
  init(app) { this._rt = getSlipInventoryRuntime(app); },
  destroy() { this._rt?.unmountTablePanel?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = getSlipInventoryRuntime(app);
    this._rt.mountTablePanel?.(el);
  },
};
